// TheBurntPeanut Simulator 9000 — Core Game Engine (Room-Based Map)
// =====================================================================
// Orchestrator: game loop, camera, player movement, world rendering.
// Uses the new room-based cluster map system (roomMapgen.ts) with
// shrines, chests, border walls, and tiled floor textures.
//
// COLLISION: Wall rects + barricade rects from each PlacedRoom.
// Player can walk anywhere in the 18000×18000 world EXCEPT inside
// wall rects, barricade rects, or outside world bounds.
// =====================================================================

// BETA 14.50 — Side-effect import. Patches CanvasRenderingContext2D.font setter
// to skip identical-value writes. ~7.8% CPU savings observed in profile data.
// Must run before any rendering code touches a canvas context.
import './canvasFontCache';

export const GAME_VERSION = 'BETA 23.76';

// ── Extraction port (BETA 22.21) — the Tarkov-style escape hatch. Opens at
// EXTRACT_APPEAR_SEC; stand within EXTRACT_RADIUS + press X to ARM a
// EXTRACT_COUNTDOWN_SEC timer that then runs anywhere (kite freely). Survive to
// 0 → extract and keep your scrap / cases / modded guns.
const EXTRACT_APPEAR_SEC = 120;
const EXTRACT_COUNTDOWN_SEC = 60;
const EXTRACT_RADIUS = 100; // arm-proximity (world units) — sized with the ~84-radius pad so ~4 players fit + you arm by standing on it

// ── BETA 21.11 — Rechargeable dash tuning (single source of truth) ──
// Tap Shift / right-bumper to blink a short burst in the move direction.
// Two charges, each refills on its own timer; brief i-frames let you dash
// THROUGH a one-shot throw. Movement reuses the existing sub-step wall
// collision so a dash can never phase through walls.
const DASH_MAX_CHARGES = 2;       // charges you can bank
// BETA 21.17 — Dylan: "i dash really far... dashes come back way too quick.
// feels OP." Shorter hop + much slower recharge. Recharge 3.5 → 6.0s.
const DASH_RECHARGE_SEC = 6.0;    // seconds to refill one charge
const DASH_DURATION_SEC = 0.16;   // length of the speed burst
const DASH_SPEED_MULT = 4.5;      // move-speed multiplier during the burst (was 7.5)
// Distance = base speed (100 u/s) × MULT × DURATION = 100 × 4.5 × 0.16 = ~72
// world-units (~1.1 peanut body-lengths) — a tight hop, not a teleport. Still
// clears a thrown projectile thanks to the 0.30s i-frames.
const DASH_IFRAME_SEC = 0.30;     // i-frames granted on dash (reuses invincibleTimer)
// BETA 21.18 — shrine-buffable dash. Recharge buff divides the cooldown; duration
// buff multiplies the burst length (= longer dash). Computed identically at the
// recharge-arm sites AND the HUD pip so the meter stays in sync.
function _dashRechargeFor(p: { dashRechargeBonus?: number }): number {
  return DASH_RECHARGE_SEC / (1 + Math.max(0, p.dashRechargeBonus ?? 0) / 100);
}
function _dashDurationFor(p: { dashDurationBonus?: number }): number {
  return DASH_DURATION_SEC * (1 + Math.max(0, p.dashDurationBonus ?? 0) / 100);
}

// BETA 20.91 — DEV-ONLY data sanity check (zod). Catches typo'd / NaN / negative
// balance values + dup ids at load instead of letting them break a run. Dynamic
// import behind the DEV guard so neither the validator nor zod's schemas ship in
// the production bundle, and the call is dead-code-eliminated from prod builds.
// Pure logging — no gameplay effect.
if (import.meta.env.DEV) {
  void import('./dataValidation').then(m => m.logDataValidation()).catch(() => {});
}

import { CHANGELOG } from './CHANGELOG';
import { seedRolls } from './rng'; // BETA 20.92 — seeded balance rolls
// BETA 16.58 — Wave 3: gamepad polling
import { tickGamepad, getConnectedControllerName } from './gamepadInput';
// BETA 18.17 — Controller anomaly detector.
import { tickControllerAnomalies } from './controllerAnomalies';
import { getPauseSelectedIndex } from './pauseMenu';
// BETA 16.80 — controller-aware interact prompt detection
function _hasGamepadConnected(): boolean {
  return getConnectedControllerName() !== null;
}

// BETA 19.68 — PSBOT cycle-30ba Bug #2: Dylan reported white focus box
// stuck on first card during PC/mouse play. Root cause: focus-box render
// gated on `_hasGamepadConnected()` which is TRUE if any gamepad is
// plugged in — even when Dylan is using mouse only. New gate: track the
// LAST input source the user actually used. Focus box renders only when
// last input was gamepad. Mouse handlers + gamepad input both call
// noteInputSource() with their kind. State lives in inputSource.ts to
// avoid circular dep with gamepadFocus (which engine already imports).
import { noteInputSource, getLastInputSource } from './inputSource';
export { noteInputSource, getLastInputSource };
function _gamepadIsActiveInput(): boolean {
  // True only when a gamepad is connected AND was the last input the user used.
  return _hasGamepadConnected() && getLastInputSource() === 'gamepad';
}
// BETA 16.59 — Wave 4: graphics quality knobs (particle density, screen shake,
// glow, screen flash, resolution scale). Registered with types.ts so
// spawnParticles can read the live density multiplier.
import { getParticleDensityMult, getScreenShakeEnabled, getGlowEffectsEnabled, getScreenFlashEnabled } from './graphicsSettings';
import { setParticleDensityMultRef } from './types';
setParticleDensityMultRef(getParticleDensityMult);
// ⚠️  Safety check: GAME_VERSION must always match the top CHANGELOG entry.
// If this fires, add a CHANGELOG entry before shipping.
if (CHANGELOG[0]?.version !== GAME_VERSION) {
  console.warn(`[VERSION MISMATCH] engine.ts says "${GAME_VERSION}" but CHANGELOG top entry is "${CHANGELOG[0]?.version}". Add a CHANGELOG entry!`);
}

import { type CharacterDef, CHARACTERS } from './assets';
import { playGoopCollect, playPortalActivate, playVictory, playFootstep, duckBgm, startMuffledDuck, startSustainedDuck, endSustainedDuck, playDeathEulogy, stopDeathEulogy, stopBgm, resetGameBgmChain, setBgmPlaybackRate } from './audio';
import { generateRoomMap, type RoomMap, type PlacedRoom, WORLD_SIZE, ROOM_SIZE } from './roomMapgen';
import {
  ROOM_COLLISION_GRID_DISPLAY, ROOM_COLLISION_GRID_RES, ROOM_COLLISION_GRID_SIZE,
  classifyPoint, ROOM_SIZE_PX as _ROOM_PX,
  WALL as _WALL, OUTER as _OUTER, CLIP as _CLIP,
  N_OUT_L, N_OUT_R, N_IN_L, N_IN_R,
  S_OUT_L, S_OUT_R, S_IN_L, S_IN_R,
  E_OUT_T, E_OUT_B, E_IN_T, E_IN_B,
  W_OUT_T, W_OUT_B, W_IN_T, W_IN_B,
  CROSS_LEFT, CROSS_RIGHT, CROSS_TOP, CROSS_BOTTOM,
  DOORWAY_INNER_MIDPOINTS,
} from './roomCollisionGrid';
import { getFloorTileUrls, OUTSIDE_TEXTURE_URLS } from './roomTiles';
import {
  OUTSIDE_TEXTURE_EXTENT, BORDER_COLLISION_MARGIN,
  BORDER_CORNER_SIZE, BORDER_WALL_DEPTH, BORDER_WALL_TILE_LENGTH,
  WALL_HORIZ_ASPECT,
  BORDER_WALL_ASSETS,
} from './borderWalls';
import {
  updateShrines, markShrineSpent, getShrineSprite, drawShrineProgressArc,
  drawShrineChargeSegments, SHRINE_ASSETS, SHRINE_ACTIVATION_RADIUS, SHRINE_SPRITE_SIZE,
} from './shrines';
import {
  getMachineSprite, isPlayerNearMachine, openMachine, markMachineGone, createEliteMachine, type WildCardMachine,
  MACHINE_SPRITE_SIZE, MACHINE_INTERACT_RADIUS, MACHINE_BASE_COST,
} from './wildCardMachines';
import {
  type GameState, type Player, type GoopBox, type Portal, type Particle,
  type ViewBounds, type WeaponsModule, type EnemiesModule, type AudioModule, type ItemsModule,
  canMoveTo, spawnParticles,
} from './types';
import { getWeaponReloadProgress, getWeaponAmmoDisplay, getWeaponSystemState, getWeaponIsThrowable, renderWeaponArcSlashOverlay, renderWeaponArcIdleOverlay, renderAuraWeaponsBelow, getRunDamageDealt, getLastFacingAngle, renderOrbitWeaponSpritesPass, updateWeaponOcclusionZones, addWeaponById } from './weaponAdapter';
import { spawnGroundEffectAt, renderGroundEffects, ORBIT_RADIUS, BUNGULATOR_ORBIT_RADIUS, spawnDamageNumber } from './weaponSystem';
// BETA 14.05 / 14.07 / 14.09 / 14.17 — Lab gates + time-lock + death log
import { isLabMode, isLabSkipLevelUp, isLabCalibratorOpen, getLabFrozenTimeSec, pushLabDeath, setCurrentCombatStats, setCurrentStatSnapshot, setCurrentSweepCounters, setCurrentFramePerf, setFirstBloodTime, setFirstDamageTakenTime, setLongestStretchBetweenHitsMs, getRetreatSimZoom, getRetreatSimShowFovBox, consumePerBuildStateReset, isPerBuildStateResetPending } from './weaponLab';
// BETA 14.12 — Snapshot player stats into enemySystem each frame so item-
// on-hit hooks (Big Mac burn DoT, Stay Frosty slow) can scale with perks.
import { setCurrentPlayerStats, preloadAllEnemySprites } from './enemySystem';
import { preloadAllWeaponSprites } from './weaponSystem';
import { renderPauseMenu, updatePauseMousePos, registerPauseMinimapRenderer } from './pauseMenu';
import {
  renderBalancePanel,
  handleBalancePanelClick,
  handleBalancePanelKey,
  isBalancePanelVisible,
  captureRunSnapshot,
} from './balancePanel';
// BETA 15.91 — Live-play telemetry. Submits a bot_run-shape object to
// Supabase under category='live_play' so PSBOT RUN can analyze human play
// the same way it analyzes bot runs. See livePlayTracker.ts for the field
// list + finalize trigger logic.
import { finalizeLiveRun, resetLivePlayTracker, registerLivePlayerStatsProvider, captureLiveHpAtDeath } from './livePlayTracker';
// BETA 16.22 — Per Dylan: enemy death floor sprite (under enemies, 2s
// fade, exact original-sprite size). Plus gas damage overlay (25%
// opacity, sized to enemy hitbox, with damage-tick text above).
import {
  spawnDeathDecal as _spawnDeathDecal,
  tickDeathDecals,
  clearDeathDecals,
  renderDeathDecals,
  getDeathDecalUrls,
} from './deathDecals';
// BETA 22.70 — weapon aftermath floor decals (scorch/splat/foam), stamped-canvas system
import { renderGroundDecals, clearGroundDecals } from './groundDecals';
import { hasWalkSet, listWalkFrameUrls, resolveWalkFrame } from './playerWalkAnim';
void _spawnDeathDecal; // re-exported via enemySystem hook (avoid unused-import)
import {
  tickGasOverlay,
  clearGasOverlay,
  renderGasOverlayBelow,
  renderGasOverlayAbove,
  getGasOverlayUrls,
  registerGasDamageNumberPusher,
} from './gasOverlay';
void renderGasOverlayAbove;
// BETA 15.93 — RIP PEANUT death screen with grade + trash talk + eulogy.
// Replaces the legacy renderGameOverScreen flow on state.phase='gameover'.
// See deathScreen.ts for the renderer; deathGrade.ts + deathTrashTalk.ts
// for the supporting modules.
import { renderDeathScreen, hitTestDeathScreenButton, resetDeathScreenState, getDeathScreenButtonRects } from './deathScreen';
// BETA 15.99 — Bot mode skips the death screen + eulogy entirely. Bot
// runs cycle every ~1.5s after death (watchdog in GameCanvas) so a
// 2:18 eulogy would waste 2 minutes per run. Bot mode is for fast
// cycle testing, not for experiencing the death moment. isBotEnabled
// is already imported below from './autoPlaytester' alongside the bot
// runtime hooks — no duplicate import needed.
import { getWeaponDef } from './weapons';
import { isArmed } from './weaponArmed';
import { equippedCodes, loseSocketedModsOnDeath } from './weaponModLoadout';
import { modComboImage } from './weaponMods';
// BETA 16.69 — Ugly Kittens monkey-bomb lure mechanic
import { tickKittenLure, registerKittenLureLookups, getCurrentLureRadius } from './kittenLure';
import { tickTrapLure } from './trapLure';   // BETA 20.10 — banana/whoopie monkey-bomb lure
// BETA 22.95 — lightning bg replaced by LIQUID NEON (Dylan: "the electric behind
// the cards when they pop up looks lame… think liquid neon").
import { drawLiquidNeon, NEON_BLUE, NEON_GOLD, NEON_PURPLE } from './liquidNeon';
import { BOUNDLESS_MAP, MAP_BUILD_NO_ENEMIES, MAP_PROPS, drawChunkedGround, clearChunkCache, chunkAssetUrls, chunkCacheSize, setGroundPois, getGroundPois } from './mapChunks';   // BETA 22.96 — boundless map M1 · 23.56 validator
import { propsInRect, propAssetUrls, setPropExclusions, notePropAspect, type WorldProp } from './worldProps';   // BETA 23.13 — world props
import { setForestAnchors, addForestAnchor, forestClearance, MAP_FOREST } from './forest';   // 23.41 G1 / 23.56 fix-pack tangent slide
import { renderLighting, type LightSource , flickerMul } from './lighting';   // BETA 23.19 — the lighting pass
import { renderSurgeVeinGlow, veinsForChunk, corruptionInfluence } from './surgeVeins';   // BETA 23.26 · 23.54 corruption cracks
import { resetSurgeCracks, renderDynamicCracks, createSurgeCrack, queryCracksNear, canCrackAt, dynamicCrackCount } from './surgeCracks';   // G8 — dynamic cracks (locked §G8 API)
import { biomeAt, biomeWeightsAt, biomeCarveMulAt, biomeLampSpacingMulAt } from './biomes';   // G2 — biome layer (debug probe + [t] borders)
import { groundKitDisplayName } from './groundKits';   // 23.60 — ground-kit debug labels
import { getEnemyDef } from './enemies';   // 23.55 — brawler-tag checks (Timmy deleted; items target the tag)
import { getParkSites, setSpurAnchors, addSpurAnchor, getPlaces } from './paths';   // BETA 23.35 dev teleport · 23.56 anchor spurs · 23.57 places
import { MAP_SURGE_VEINS } from './mapChunks';
import { MAP_LIGHTING } from './mapChunks';
import { MAPDEV, mapDevRequested, mapDevTick, drawHorizonPreview, drawMapDevOverlays, drawPerfOverlay, takeMapDevScreenshot } from './mapDevViewer';   // BETA 23.00 P0 + 23.06 DT-A
// BETA 22.87 — PERK CARD SYSTEM v1: baked 4-tier card art + zones + styles.
import { cardArtPath, cardShellPath, weaponCardArtPath, CARD_RARITY_STYLE, getCardTierBoxes, type CardTierBoxes } from './perkCards';
import { shrineTilePath, TILE_VALUE_ZONE } from './shrineTiles';   // BETA 22.92 — Dylan's shrine tiles
import { WILD_CARD_IMG_BODY } from './wildCards';   // BETA 22.89 — machine vend screen
import { registerKittenLureRadiusGetter } from './weaponSystem';
// BETA 16.77 — Controller-driven nav for level-up / shrine / chest choice screens.
import { registerFocusScreen, setActiveScreen, unregisterFocusScreen, getFocusedTarget, getActiveScreen } from './gamepadFocus';
// BETA 20.64 — ultra-chest picks are now single-sourced in engine (controller +
// keyboard + mouse all route through pickUltraChestCard) so the tracker event
// fires no matter which input picked the card.
import { logEvent as _logSessionEvent } from './sessionTracker';
// BETA 16.70 — touch-device detection (zoom out on phones)
import { isTouchDevice, isHandheldGamingDevice } from './deviceDetect';
// BETA 16.71 — per-run input mix (keyboard / touch / gamepad / mouse)
import { tickInputTracker, resetInputTracker } from './inputTracker';
// Register lookups so kittenLure.ts can read weapon def + level without
// circular imports. Module-load-time registration is safe because both
// getWeaponDef + getWeaponSystemState are themselves module-level
// functions that don't depend on game-state existing yet.
registerKittenLureLookups(
  (id: string) => getWeaponDef(id),
  (id: string) => {
    const ws = getWeaponSystemState();
    const inst = ws?.weapons.find(w => w.definitionId === id);
    return inst?.level ?? 1;
  },
);
// Wire the renderer's lure-radius getter so weaponSystem can draw the
// suck-radius ring around each kitten in render passes.
registerKittenLureRadiusGetter(() => getCurrentLureRadius());
import { WEAPONS } from './loadoutSelection';
import {
  handleChestOpen, handleShrineActivation,
  getLevelUpOptionDisplayData, getPercSlotDisplayData, getAllPercSlotDisplayData,
  getItemInventoryDisplayData, isPendingLevelUp,
  getLevelUpSelectedIndex, setLevelUpSelectedIndex,
  getLevelUpHoverIndex, setLevelUpHoverIndex,
  handleLevelUpSelection,
  generateShrineOptions, getShrineOptionDisplayData,
  getShrineSelectedIndex, setShrineSelectedIndex, getShrineHoverIndex, setShrineHoverIndex,
  handleShrineSelection,
  getShrineBuffDisplayOptions, type ShrineBuffOption, getShrineIsGolden,
  getChestOptionDisplayData, type ChestItemDisplay, handleChestItemSelection,
  getChestHoverIndex, setChestHoverIndex,
  rerollLevelUpOptions, rerollShrineOptions, rerollChestOptions,
  skipShrineChoice, skipChestChoice,
  startChestSpin, getChestSpinDisplay, isChestSpinDone, getChestSpinType, clearChestSpin,
  type ChestSpinDisplay,
  registerShrineSelectTracker,
} from './itemsAdapter';
import {
  createLootOrbState, updateLootOrbs, renderLootOrbs, spawnDrops, vacuumAllOrbs, spawnSingleOrb,
  vacuumOrbsNear,   // BETA 20.10 — banana/whoopie trap loot auto-collect
  clearLootOrbs,    // BETA 21.52 — bulletproof orb wipe on floor advance
} from './lootOrbs';
import { playLevelUp, playPlayerDeath, playGoldPickup, playXPPickup, playHealthPickup, playChestOpenFree, playMachineVend, playTokenSpend, playMachineDenied, playHeartThrobPulse, playBarrelBreak, playLowHealthWarning, playCardHover, playCardSlamSfx, playShrineTileSlamSfx, playCardSelectSfx, playCardFlySfx, playShrineActivate, playShrineIgnite, playTBagSummon, playBungulatorPunch, playSheeeshActivate, playLiarDeceiverActivate, playAnusRocket, playShieldActivate, playLiarNukeBlast, playBananaThrow, playWhoopieThrow, playBananaBombBurst, playWhoopiePopBurst, startKarenDronesLoop, stopKarenDronesLoop, stopAllWeaponLoops, playIranianBirdFire, playExtractAppear, playExtractArm, playExtractSuccess } from './gameSfx';
// BETA 22.98 — Cloakzy DELETED (Dylan: real streamer; no streamer likeness in the game)
import {
  isPlayerNearBarrel, BARREL_INTERACT_RADIUS,
  BARREL_SPRITE_SMALL, BARREL_SPRITE_LARGE,
  BARREL_SPRITE_URL_XP, BARREL_SPRITE_URL_GOLD, BARREL_SPRITE_URL_BOTH,
} from './barrels';
// BETA 23.28 — chickMagnet imports removed (statues deleted; module tombstoned for PSBOT compat)
import { RARITY_COLORS as UPGRADE_RARITY_COLORS } from './types';
import { drawCardFireGlow, getStatEmoji, injectStatEmojis } from './cardVisuals';
// BETA 16.38 — bottom-left active-items tray.
// BETA 16.99 — Q-press items eliminated per Dylan ("remove all q items.
// make them all passives"). Set is now empty so the tray subsystem
// (still imported for potential future re-enable) doesn't track any
// items. VooGoo Rager auto-triggers at HP < 50% via the apply path
// in updateGame — no keyboard / tray involvement.
import { syncActiveItemSlots, flashActiveItem, getActiveItemSlots, getTraySlotBbox, resetActiveItemTray, TRAY_SLOT_SIZE } from './activeItemTray';
const Q_ACTIVATED_ITEM_IDS = new Set<string>();
import { RARITY_COLORS as ITEM_RARITY_COLORS, BUNCHA_BULLSHIIII, tuneOf } from './bunchaBullshiiii';
import { PEANUT_PERCS } from './peanutPercs';
import { ultraChestPool, ULTRA_ITEMS, DONATION_MESSAGES, type UltraItem } from './ultraItems';
// BETA 22.49 — live-chat feature removed (chatSystem.ts deleted).
// BETA 16.33 — wire stats.difficulty into waveState.difficultyMultiplier
// each tick (Dylan's "increase difficulty = more enemies, not stronger" audit).
import { setDifficultyMultiplier as _setWaveDifficulty, setGodModeDensityMult } from './waveSystem';
import { debugNavOverlay, debugShowWaypoints, debugFlowField, debugFlowFieldMode, isVfxEnabled, debugDynamicLighting } from './debugState';
import { maintainTestDummies, spawnFinalBoss, getEnemySystemState, _resolveKillerFromSource } from './enemyAdapter';
import { ROOM_SIZE as _RS, GAP_DEPTH as _GD, GAP_WIDTH as _GW, GAP_OFFSET as _GO } from './roomMapgen';
import { initBot, updateBot, exportBotData, getCurrentRunLog, isBotEnabled, noteTrailTeleport, notePerf } from './autoPlaytester';
import { initBotV2, updateBotV2, downloadFullCSV, isBotV2Active } from './autoPlaytester_v2';
import { initFlowField, updateFlowField, forceRebuild as forceFlowFieldRebuild, resetFlowField, getFlowDirection, getCostField, getIntegrationField, getFlowVectors, isFlowFieldReady, CELL_SIZE as FF_CELL, GRID_W as FF_GW, GRID_H as FF_GH } from './flowField';
// generateCorridorTriangles / setOutsideCornerTris / setPinchTris removed in BETA 13.25 — replaced by 3-zone collision (getGreenZoneRedirect).
import {
  updateItemEffectVisuals,
  renderItemEffects,
  renderItemEffectsBelow,
  resetItemEffects,
  preloadItemSprites,
  setActiveEnemies,
  updateTBagPosition,
  wasTBagJustSpawned,
} from './itemEffectSystem';
import { getItemState, getPercState, getShrineDifficultyPct, setShrineGatingContext } from './itemsAdapter';
// BETA 15.26 — character passive per-frame tick + per-run reset + ID setter
// (so weaponSystem and other systems without GameState can call hooks).
import { tickCharacterPassiveRunState, resetCharacterPassiveRunState, setCurrentCharacterId, attributeSignatureFrameDamage, CHARACTER_PASSIVES } from './characterPassives';

// Re-export GameState so GameCanvas.tsx doesn't need to change its import
export type { GameState } from './types';

// ─── Registered Subsystem Modules ───────────────────────────────────
let weaponsModule: WeaponsModule | null = null;
let enemiesModule: EnemiesModule | null = null;
let audioModuleExt: AudioModule | null = null;
let itemsModule: ItemsModule | null = null;

export function registerWeapons(mod: WeaponsModule) { weaponsModule = mod; }
export function registerEnemies(mod: EnemiesModule) { enemiesModule = mod; }
export function registerAudio(mod: AudioModule) { audioModuleExt = mod; }
export function registerItems(mod: ItemsModule) { itemsModule = mod; }

// BETA 14.47 — Wire per-(weapon × enemy) hit tracker. weaponSystem can't
// import from weaponLab (circular dep), so engine.ts (which already imports
// from weaponLab) acts as the bridge: registers weaponLab.trackWeaponHit
// at module load. Every weapon damage application call site in weaponSystem
// now fires this with (weaponId, enemy.type, dmg, isCrit) → populates
// _localWeaponEnemyMatrix → emitted in WL CSV's weaponEnemyMatrix col +
// 5 top-line cols (per-enemy top weapons + weakest; Caseoh col kept as an
// empty PSBOT-compat column — 23.39 purge, handoff sent).
import { registerWeaponHitTracker, registerKittenEventTracker, registerFloorEffectTracker, registerSynergyTrigger, clearAllGroundEffects, resetKarenDroneTrace } from './weaponSystem';
// BETA 17.08 — PSBOT cycle-30t Tier C counters (closes the batch).
import { resetAnusRocketTrace as _resetAnusRocketTrace, spawnBigBrainSparkle } from './itemEffectSystem';
import { resetSillyJuicePeakStack as _resetSillyJuicePeakStack } from './itemsAdapter';
import {
  trackWeaponHit, trackKittenPlace, trackKittenAttack, trackKittenHit,
  trackKittenProjectileDeath,
  trackFloorEffectSpawn, trackFloorEffectTick, trackFloorEffectFirstContact,
  pushEventLog, pushNearMissEvent, setDeathContext,
  trackPercProc, trackPercCondition, trackPercEffectiveBonus, trackPercApply,
  trackItemProc, trackItemDmg, trackItemQPress, trackItemQReady, trackItemStatusFrames,
  trackItemStackChange, trackShrineApply,
  trackSynergyTrigger, consumeLastDamageSource,
  setCurrentRunTime, getCurrentRunTime,
} from './weaponLab';
// BETA 14.73 — BUG-5/6 fix: register tracker callbacks for the real-game
// perc + item application paths so percTracker.applies + itemTracker populate
// for bot/real-game runs (not just lab applyBuild path). Same registration
// pattern as registerShrineSelectTracker.
import { registerPercApplyTracker, registerPercPickAuditor, xpRequiredForLevel } from './peanutPercSystem';
// BETA 15.34 — pick audit log (shared module for all pick events)
import { recordPick, snapshotStats } from './pickAudit';
import { registerItemStackChangeTracker } from './bunchaBullshiiiiSystem';
import { clearEnemyProjectiles, registerSlamImpactShake, registerDeathDustSpawner, registerProjectileImpactSpawner, getActiveEnemyProjectiles, setGodModeScaling } from './enemySystem';
// BETA 20.72 — namespace handle on weaponSystem for the impact-feedback VFX
// hooks (damage-number sparks / beam impact spray). The hook exports land in
// a parallel weaponSystem change; guarded property access below means this
// file compiles + runs either way and the bursts light up the moment the
// hooks exist.
import * as _weaponSystemHooks from './weaponSystem';

// BETA 16.40 — register the minimap renderer with the pause menu so it
// can draw a copy inside the chalkboard.
registerPauseMinimapRenderer((ctx, state, w, h, x, y, size) => {
  renderMinimap(ctx, state, w, h, x, y, size);
});

// BETA 16.33 — boss slam impact shake. enemySystem can't reach GameState
// directly (would create an import cycle) so it pokes via this callback.
// BETA 16.39 — Dylan: "i dont think [the boss] causes screen shake when he
// jumps. all of them." Bumped duration + intensity so the slam jolt is
// unmissable. Boss variants slam HARD; non-boss slammers still chunky.
let _engineStateForShake: GameState | null = null;
registerSlamImpactShake((isBoss, isMega, impactX, impactY, aoeRadius) => {
  if (!_engineStateForShake) return;
  // BETA 16.52 — a mega-tier final boss gets EXTREME shake (1.0s @ 80px)
  // per Dylan: "obviously camera shake heavy every time he lands."
  // Regular boss slams stay at 0.65s / 44px; non-boss at 0.65s / 30px.
  if (isMega) {
    // BETA 21.51 — Dylan: "[the mega boss] screen shake is way too heavy." Was an
    // EXTREME 1.0s @ 80px; eased to 0.55s @ 42px (still clearly the biggest slam
    // — a normal boss is 0.65s @ 44px — just not nauseating).
    _engineStateForShake.screenShakeTimer = 0.55;
    _engineStateForShake.screenShakeIntensity = 42;
    // BETA 16.53 — Per Dylan: rooms within mega's belly-flop radius get
    // CRUSHED. Crushed rooms lose collision (walls + barricades) and
    // render with a crack overlay. Iterate the room map at impact time
    // and mark any room whose center is inside the AoE.
    if (impactX !== undefined && impactY !== undefined && aoeRadius !== undefined) {
      // BETA 21.28 — widen the crush reach. The slam DAMAGE radius (~140) is smaller
      // than a room, so a belly-flop landing dead-center on a room often crushed
      // NOTHING (its center was >140 from impact) — which is why it felt like the
      // feature didn't fire. Now: crush if the impact lands INSIDE a room, or the
      // room center is within a generous radius, so a flop reliably flattens the room
      // it lands on (+ any close neighbors) — "belly-flops your map into rubble."
      const crushR = Math.max(aoeRadius, 300);
      const cr2 = crushR * crushR;
      for (const room of _engineStateForShake.map.roomMap.rooms) {
        if (room.crushed) continue;
        const rcx = room.x + room.size / 2;
        const rcy = room.y + room.size / 2;
        const insideBbox = impactX >= room.x && impactX < room.x + room.size &&
                           impactY >= room.y && impactY < room.y + room.size;
        const dx = rcx - impactX;
        const dy = rcy - impactY;
        if (insideBbox || dx * dx + dy * dy <= cr2) {
          room.crushed = true;
          // POOF — dust plume (rises) + heavy debris shards (fall) at the room center
          // so the destruction reads as a real impact moment, not a silent state flip.
          spawnParticles(_engineStateForShake.particles, rcx, rcy, '#6b5a48', 30, { shape: 'mote', gravity: -40, speedMin: 40, speedMax: 210, lifeMin: 0.7, lifeMax: 1.5, sizeMin: 3, sizeMax: 9 });
          spawnParticles(_engineStateForShake.particles, rcx, rcy, '#3a3a3a', 16, { shape: 'shard', gravity: 260, speedMin: 80, speedMax: 320, spin: 8, spinRandomSign: true, lifeMin: 0.5, lifeMax: 1.0, sizeMin: 4, sizeMax: 9 });
          // Notification so the player sees what happened
          _engineStateForShake.pickupNotifications?.push({
            text: `⚠ ${room.displayName.toUpperCase()} CRUSHED`,
            color: '#ff6666',
            timer: 3.0,
          });
        }
      }
    }
  } else {
    _engineStateForShake.screenShakeTimer = 0.65;
    _engineStateForShake.screenShakeIntensity = isBoss ? 44 : 30;
  }
  // BETA 20.72 — slam landing debris (visuals only — slam sprite + 60s crack
  // ground-effect in enemySystem stay untouched). Identity: angular gray
  // crack-line shards that skid FLAT along the ground (gravity 0 = no rise,
  // tumbling spin) + a brief two-tone brown/gray dust explosion that settles
  // back down. 7 shards + 32 motes per landing — slams are event-driven, not
  // per-frame, so the burst stays cheap.
  if (impactX !== undefined && impactY !== undefined) {
    const _slamP = _engineStateForShake.particles;
    spawnParticles(_slamP, impactX, impactY, '#6e6e6e', 7, { shape: 'shard', speedMin: 60, speedMax: 180, gravity: 0, spin: 6, spinRandomSign: true, lifeMin: 0.35, lifeMax: 0.6, sizeMin: 3, sizeMax: 6 });
    spawnParticles(_slamP, impactX, impactY, '#8b6f47', 20, { shape: 'mote', gravity: 160, speedMin: 40, speedMax: 200, lifeMin: 0.3, lifeMax: 0.6 });
    spawnParticles(_slamP, impactX, impactY, '#9a9a9a', 12, { shape: 'mote', gravity: 160, speedMin: 40, speedMax: 200, lifeMin: 0.3, lifeMax: 0.6 });
  }
});
registerWeaponHitTracker(trackWeaponHit);
// BETA 20.72 — hoisted opts for the impact-feedback bursts. Deaths and
// damage popups are the hottest EVENT paths in the game, so these option
// objects are built once at module load, never per event.
const _DEATH_DUST_OPTS = { shape: 'mote', gravity: 240, speedMin: 20, speedMax: 90, lifeMin: 0.4, lifeMax: 0.8, sizeMin: 1.5, sizeMax: 3 } as const;
const _DN_SPARK_OPTS = { shape: 'spark', speedMin: 110, speedMax: 240, lifeMin: 0.12, lifeMax: 0.22, stretch: 3, sizeMin: 1, sizeMax: 2 } as const;
const _DN_CRIT_RING_OPTS = { shape: 'ring', speedMin: 0, speedMax: 0, sizeMin: 7, sizeMax: 9, lifeMin: 0.25, lifeMax: 0.25 } as const;
// BETA 20.72 — Death-dust: every corpse decal lands with 16 settling motes
// (12 dirt-brown + 4 ash-gray, positive gravity = sink + drift, short life).
// Identity vs the slam dust above: this dust SINKS slowly and stays small/
// dim — a body crumpling, not a blast. Enemy deaths are one of the hottest
// event paths in the game, so the count is capped low and it's two
// spawnParticles calls total, no per-frame work.
registerDeathDustSpawner((x, y, _spriteSize) => {
  if (!_engineStateForShake) return;
  const _ddP = _engineStateForShake.particles;
  spawnParticles(_ddP, x, y, '#8b6f47', 12, _DEATH_DUST_OPTS);
  spawnParticles(_ddP, x, y, '#9a9a9a', 4, _DEATH_DUST_OPTS);
});
// BETA 20.78 — enemy projectile IMPACT splats (audit: thrown bottles/debris
// vanished with zero feedback on wall/player hit). Two distinct identities:
// Timmy-class bottle = pale glass shards spraying + falling; boss throw =
// fewer, heavier dark debris wedges. Opts hoisted — projectiles hit often.
const _PROJ_GLASS_OPTS = { shape: 'shard', spin: 14, spinRandomSign: true, gravity: 320, speedMin: 60, speedMax: 190, lifeMin: 0.22, lifeMax: 0.42, sizeMin: 1.8, sizeMax: 3.2 } as const;
const _PROJ_DEBRIS_OPTS = { shape: 'shard', spin: 6, spinRandomSign: true, gravity: 400, speedMin: 40, speedMax: 150, lifeMin: 0.3, lifeMax: 0.5, sizeMin: 3, sizeMax: 5 } as const;
registerProjectileImpactSpawner((x, y, isBoss) => {
  if (!_engineStateForShake) return;
  const _piP = _engineStateForShake.particles;
  if (isBoss) {
    spawnParticles(_piP, x, y, '#3a3026', 6, _PROJ_DEBRIS_OPTS);
  } else {
    spawnParticles(_piP, x, y, '#cfeefb', 5, _PROJ_GLASS_OPTS);
  }
});
// BETA 20.72 — Impact-feedback hooks whose TRIGGER sites live in
// weaponSystem.ts (spawnDamageNumber fresh-spawn branch + the beam-projectile
// collision HIT block). Those hook exports ship in a parallel weaponSystem
// change, so this uses guarded property access — a harmless no-op until the
// exports exist, then the bursts wire up automatically. Same registration
// pattern as registerWeaponHitTracker above.
{
  const _wsHooks = _weaponSystemHooks as unknown as {
    registerDamageNumberVfx?: (cb: (x: number, y: number, isCrit: boolean, isBuff: boolean, color?: string) => void) => void;
    registerBeamImpactVfx?: (cb: (x: number, y: number, travelAngle: number, color: string) => void) => void;
    registerWeaponParticleSpawner?: (cb: (x: number, y: number, color: string, count: number, opts?: import('./types').ParticleOpts) => void) => void;
  };
  // Damage-number spark bursts: 4-8 tiny streaks punched through the popup,
  // dying in 0.12-0.22s — faster than the text so the number stays readable.
  // WHITE for normal hits, GOLD for crits (+ one shimmer-halo ring particle),
  // GREEN for heals/buffs. Popups spawn constantly → counts capped small and
  // the coalesce branch in spawnDamageNumber never fires this (fresh spawns
  // only), so multi-hit spam can't multiply sparks.
  if (_wsHooks.registerDamageNumberVfx) {
    _wsHooks.registerDamageNumberVfx((x, y, isCrit, isBuff, _color) => {
      if (!_engineStateForShake) return;
      const _dnP = _engineStateForShake.particles;
      if (isCrit) {
        spawnParticles(_dnP, x, y, '#FFD700', 7, _DN_SPARK_OPTS);
        spawnParticles(_dnP, x, y, '#FFD700', 1, _DN_CRIT_RING_OPTS);
      } else if (isBuff) {
        spawnParticles(_dnP, x, y, '#7ee090', 4, _DN_SPARK_OPTS);
      } else {
        spawnParticles(_dnP, x, y, '#ffffff', 4, _DN_SPARK_OPTS);
      }
    });
  }
  // Beam impact spray: beams finally show impact AT the hit point — a small
  // forward cone of fast stretched sparks continuing the beam's travel
  // direction, in the beam's own color. Identity vs popup sparks: these are
  // DIRECTIONAL (cone, not omni), faster, and longer-streaked.
  if (_wsHooks.registerBeamImpactVfx) {
    _wsHooks.registerBeamImpactVfx((x, y, travelAngle, color) => {
      if (!_engineStateForShake) return;
      spawnParticles(_engineStateForShake.particles, x, y, color, 6, { shape: 'spark', dir: travelAngle, spread: 0.6, speedMin: 140, speedMax: 320, lifeMin: 0.15, lifeMax: 0.3, stretch: 4, sizeMin: 1.5, sizeMax: 2.5 });
    });
  }
  // BETA 20.72 — weapon-side particle bridge (the other direction): fire
  // signatures (revolver speckle / RPK cascade / blunderbuss smoke / Karen
  // star-glint / Ex-GF sparkle), boomerang flip pops, Frozen Darts freeze
  // rings, basketball bounce stars all spawn through this. weaponSystem
  // can't reach engine state, so it exposes a registered spawner — wired
  // here exactly like registerWeaponHitTracker above.
  if (_wsHooks.registerWeaponParticleSpawner) {
    _wsHooks.registerWeaponParticleSpawner((x, y, color, count, opts) => {
      if (!_engineStateForShake) return;
      spawnParticles(_engineStateForShake.particles, x, y, color, count, opts);
    });
  }
}
// BETA 14.54 — Kitten event tracker registration (Ugly Kittens telemetry).
// Same pattern as weapon-hit tracker: wire weaponSystem to weaponLab via
// engine.ts which can import both.
registerKittenEventTracker({
  onPlace:           trackKittenPlace,
  onAttack:          trackKittenAttack,
  onHit:             trackKittenHit,
  onProjectileDeath: trackKittenProjectileDeath,
});
// BETA 14.55 — Floor-effect tracker registration. Wires weaponSystem floor
// effect lifecycle (spawn / first-contact / per-enemy DoT tick) into
// weaponLab's _localFloorEffectTracker. Was previously empty {} in CSV.
registerFloorEffectTracker({
  onSpawn:        trackFloorEffectSpawn,
  onEnemyTick:    trackFloorEffectTick,
  onFirstContact: trackFloorEffectFirstContact,
});
// BETA 14.58 — Synergy trigger registration. Fires from weaponSystem when
// an actual synergy boost is applied (vs just bothEquipped). Currently wired
// for dracs_fang_gas_mask (lifesteal 8% → 20%). More synergies will be
// instrumented as their boost code paths get hooks.
registerSynergyTrigger((key, valueDelta) => trackSynergyTrigger(key, valueDelta));
// BETA 14.56 — Real-game shrine-select tracker. itemsAdapter handleShrineSelection
// fires this when player picks a shrine buff in actual gameplay (lab path
// already covered via trackShrineApply at applyBuild time). Both the
// shrineTracker col AND the eventLog get populated.
registerShrineSelectTracker((label, _stat, value, _pct) => {
  // BETA 14.63 — uses the module-level _currentRunTime cache (engine pushes
  // state.time each frame in updateGame). Shrine events now timestamped with
  // the actual game-clock second, not 0.
  pushEventLog({ t: getCurrentRunTime(), type: 'shrine', data: { label, value } });
  // BETA 16.90 — PSBOT cycle-30f Bug 6: increment shrinesActivatedThisRun
  // counter so live_play / bot CSV shrinesActivated column reflects reality.
  // Was: always 0 even though pickEvents had 587 shrine-source picks.
  if (_liveState) _liveState.shrinesActivatedThisRun = (_liveState.shrinesActivatedThisRun ?? 0) + 1;
  // BETA 14.73 — BUG-7 fix: ALSO call trackShrineApply so the shrineTracker col
  // populates for real-game / bot picks. Previously only WeaponLab's applyBuild()
  // path called trackShrineApply, leaving shrineTracker={} in BotFarm CSV runs.
  trackShrineApply(label, value);
  // BETA 15.34 — Pick audit: log the shrine selection with value + player
  // stat snapshot. Rarity isn't passed in the callback signature today, but
  // the label often encodes the magnitude (e.g. "+12% Damage" vs "+40% Damage")
  // and the SHRINE_BUFF_POOL definitions in itemsAdapter have rarity per
  // entry — that mapping happens at offer-generation time.
  try {
    if (!_liveState) return;
    const p = _liveState.player;
    recordPick({
      t: getCurrentRunTime(),
      source: 'shrine',
      pickedId: label,
      pickedName: label,
      pickedValue: value,
      pickedIsPercent: _pct,
      before: snapshotStats({
        level: p.level, hp: p.hp, maxHp: p.maxHp,
        damageBonus: p.damageBonus, attackSpeedBonus: p.attackSpeedBonus,
        critChance: p.critChance, critDamage: p.critDamage,
        luck: p.luck, armor: p.armor, gold: p.gold,
      }),
    });
  } catch { /* never crash on audit */ }
});

// BETA 14.73 — BUG-5 fix: percTracker for real-game/bot picks. peanutPercSystem
// fires this callback after selectLevelUpOption() actually applies a perc level
// (the path real-game UI + bot use). Was previously only fired by WeaponLab's
// incrementPercLevel(), leaving percTracker.applies=0 in BotFarm CSV runs.
registerPercApplyTracker((percId, newStack) => {
  trackPercApply(percId, newStack);
});

// BETA 15.34 — Pick audit hook: fires on every level-up perc pick with the
// option + the full offered set. Engine has access to live state.player so
// we can capture the BEFORE-pick stat snapshot. Reads getCurrentRunTime()
// for the timestamp (set every frame by updateGame).
registerPercPickAuditor((option, allOptions) => {
  try {
    if (!_liveState) return;
    const p = _liveState.player;
    recordPick({
      t: getCurrentRunTime(),
      source: 'levelup_perc',
      pickedId: option.perc?.id ?? '?',
      pickedName: option.perc?.name,
      pickedRarity: option.rarity,
      pickedValue: option.bonusAmount,
      pickedIsPercent: option.perc?.isPercent ?? false,
      offered: allOptions.map(o => ({
        id: o.perc?.id ?? '?',
        name: o.perc?.name,
        rarity: o.rarity,
        value: o.bonusAmount,
        isPercent: o.perc?.isPercent ?? false,
      })),
      before: snapshotStats({
        level: p.level, hp: p.hp, maxHp: p.maxHp,
        damageBonus: p.damageBonus, attackSpeedBonus: p.attackSpeedBonus,
        critChance: p.critChance, critDamage: p.critDamage,
        luck: p.luck, armor: p.armor, gold: p.gold,
      }),
    });
  } catch { /* never crash on audit */ }
});

// BETA 14.73 — BUG-6 fix: itemTracker for real-game/bot picks. bunchaBullshiiii
// fires this callback after addBullshiiii() pushes/stacks an item (the path real
// game chest pickups + bot picks use). Was previously only fired by WeaponLab's
// setItemStacks(), leaving itemTracker={} in BotFarm CSV runs.
registerItemStackChangeTracker((itemId, newStack) => {
  trackItemStackChange(itemId, newStack);
  // BETA 15.34/15.54 — Pick audit: log the item pick (chest contents).
  // BETA 15.54 — paid/free distinction now read from `_lastChestPaid` flag set
  // by handleInteract at the chest-open site. Resolves the BETA 15.34 TODO.
  // Per PSBOT cycle-20 audit: bots HAD been buying paid chests (runGoldSpent
  // matched 3^N exactly across 98/145 runs) but tracker hardcoded chest_free.
  try {
    if (!_liveState) return;
    const p = _liveState.player;
    const isPaid = (_liveState as unknown as { _lastChestPaid?: boolean })._lastChestPaid === true;
    recordPick({
      t: getCurrentRunTime(),
      source: isPaid ? 'chest_paid' : 'chest_free',
      pickedId: itemId,
      pickedValue: newStack,  // stack-count as proxy for "buff" magnitude
      before: snapshotStats({
        level: p.level, hp: p.hp, maxHp: p.maxHp,
        damageBonus: p.damageBonus, attackSpeedBonus: p.attackSpeedBonus,
        critChance: p.critChance, critDamage: p.critDamage,
        luck: p.luck, armor: p.armor, gold: p.gold,
      }),
    });
  } catch { /* never crash on audit */ }
});

// ─── Chat System Trackers ───────────────────────────────────────────────
// BETA 15.34 — Live state pointer for event callbacks. Set at the top of
// updateGame each frame, read by RARE callbacks like the perc pick auditor
// (fires only on level-up, ~once every 10-30 seconds of play). Safe — does
// NOT touch the per-frame hot path (the 15.24/15.37 freeze pattern was a
// 14-field state-sync block running every frame; this is just a pointer).
let _liveState: GameState | null = null;

// BETA 16.21 — PSBOT cycle-24 P1.1+P1.3: register a player-stats provider so
// recordLivePickEvent (in livePlayTracker.ts) auto-snapshots player stats at
// pick time. Lets PSBOT diff consecutive pickEvents to verify whether T02 picks
// actually move attackSpeedBonus, or T03 moves maxHp, etc.
// BETA 16.22 — Per Dylan: gas damage tick spawns a floating "+N" damage
// number above the enemy. Bridge gasOverlay into the existing weaponSystem
// damageNumbers pipeline (avoids a circular import).
registerGasDamageNumberPusher((x, y, value, isCrit) => {
  try {
    const ws = getWeaponSystemState();
    if (ws) {
      spawnDamageNumber(ws, {
        x,
        y,
        value,
        isCrit,
        timer: 0.9,
        velocityY: -32,
      });
    }
  } catch { /* never crash on tick */ }
});

registerLivePlayerStatsProvider(() => {
  if (!_liveState) return null;
  const p = _liveState.player;
  // Defensive cast: some fields aren't on the strictly-typed Player but
  // are present at runtime (set by per-frame apply). Type the access.
  const px = p as unknown as Record<string, number | undefined>;
  const r2 = (v: number | undefined) => Math.round((v ?? 0) * 100) / 100;
  const r0 = (v: number | undefined) => Math.round(v ?? 0);
  return {
    maxHp: r0(p.maxHp),
    hp: r0(p.hp),
    damageBonus: r2(p.damageBonus),
    attackSpeedBonus: r2(p.attackSpeedBonus),
    critChance: r2(p.critChance),
    critDamage: r2(p.critDamage),
    lifesteal: r2(p.lifesteal),
    eliteDamageBonus: r2(p.eliteDamageBonus),
    armor: r0(p.armor),
    luck: r2(p.luck),
    // BETA 19.93 — full 21-field snapshot per pick. Was 10 fields, missed
    // projectile/knockback/phase/regen/etc — picks targeting those stats
    // silently appeared as "no-op" in before/after diffs.
    projectileCountBonus: r0(p.projectileCountBonus),
    projectileBounceBonus: r0(p.projectileBounceBonus),
    knockbackBonus: r2(p.knockbackBonus),
    pickupRange: r0(p.pickupRange),
    shield: r0(p.shield),
    maxShield: r0(p.maxShield),
    evasion: r2(p.evasion),
    thorns: r2(p.thorns),
    hpRegen: r2(p.hpRegen),
    shieldRegen: r2(p.shieldRegen),
    contactPhaseChance: r2(px.contactPhaseChance),
    xpMultiplier: r2(p.xpMultiplier),
    goldMultiplier: r2(p.goldMultiplier),
    attackAreaBonus: r2(p.attackAreaBonus),
    projectileSpeedBonus: r2(p.projectileSpeedBonus),
    overheal: r0(px.overheal),
    // BETA 21.42 — last 4 missing buffable stats. Picks targeting weapon range,
    // dash recharge/duration, or damage reduction previously diffed as "no-op"
    // because the before/after snapshot never captured them. damageReduction
    // lives on itemState.playerStats (not the player object), like its cap path.
    rangeBonus: r0(p.rangeBonus),
    dashRechargeBonus: r2(px.dashRechargeBonus),
    dashDurationBonus: r2(px.dashDurationBonus),
    damageReduction: r2(getItemState()?.playerStats?.damageReduction),
    speed: r0(p.speed),
  };
});

// BETA 14.55 — Kill-delta tracker for eventLog.
let _eventLogPrevKills = 0;
// BETA 14.55 — NearMiss detection state. Triggers when player HP drops
// below 30% then recovers above 60% within 5 seconds. recoverySource is
// just 'recovered' for now (could be split by heal vs regen vs lifesteal
// later). Reset on player death + per-build state reset.
let _nearMissNadirHp = -1;
let _nearMissDipTime = 0;
const _NEAR_MISS_LOW_FRAC = 0.3;
const _NEAR_MISS_RECOVER_FRAC = 0.6;
const _NEAR_MISS_WINDOW_SEC = 5.0;

// BETA 14.57 — DeathContext sliding window. Engine watches player.hp drops
// and records last 5 damage events. On death, the last 5 hits are emitted
// in deathContext along with hpAtDeath / iframeStateAtDeath / nearby enemy
// count. Reset on per-build state reset.
type DeathHit = { t: number; source: string; dmg: number };
let _recentHits: DeathHit[] = [];
let _lastHpForDeathTracking = -1;
let _deathClarityLogged = false; // BETA 22.48 — one-shot guard for the phantom-death console warn

// BETA 22.48 — DEATH CLARITY. Classify the fatal damage source into a plain-
// English "how" so Dylan can finally tell what killed him. Dylan: "i never see
// an enemy hit me when i die. its very confusing." Sources come from the
// _recentHits ledger (e.g. 'zombie', 'boss_slam', 'enemy_projectile').
function _deathHowFromSource(source: string): string {
  const s = (source || '').toLowerCase();
  if (!s || s === 'unknown') return 'unknown source';
  if (s.includes('slam')) return 'slam attack';
  if (s.includes('projectile') || s.includes('throw') || s.includes('shot')) return 'ranged shot';
  if (s.includes('gas') || s.includes('dot') || s.includes('burn') || s.includes('poison')) return 'damage-over-time';
  return 'contact';
}

// BETA 14.62 — Per-second sampling gate for passive synergy triggerCount.
// Without this, perc-perc combos would fire 60×/sec and inflate triggerCount
// vs trigger-event-based synergies. Sample once per game-time second.
let _lastSynergyFrameSec = -1;
function _recordHitForDeath(t: number, source: string, dmg: number): void {
  if (dmg <= 0) return;
  _recentHits.push({ t, source, dmg });
  if (_recentHits.length > 5) _recentHits.shift();
}
let _chatPrevPhase = ''; // BETA 22.49 — retained: drives overlay focus-screen transitions (live chat removed)

// ─── Image Cache ────────────────────────────────────────────────────────
const imageCache: Map<string, HTMLImageElement> = new Map();
const imageLoading: Set<string> = new Set();
let playerSprite: HTMLImageElement | null = null;

// BETA 22.81 — Dylan: "character 1.2x bigger." VISUAL-ONLY scale on the player
// sprite + its overlays (HP bar sits above the bigger head, spray/dash pips
// follow). We do NOT touch player.spriteSize itself — the enemy sprite-touch
// collision (enemySystem.ts) reads that field, so changing it would move the
// hitbox = a balance change. Scaling only the render = a slightly forgiving
// hitbox (visual extends ~20% past the hitbox), zero balance impact.
const PLAYER_RENDER_SCALE = 1.2;

// BETA 22.85 — directional walk-animation state (one player, so module-level;
// reset each run in initGame). _playerWalkFrames caches the loaded frame images
// for the current character; angle/moving/phase are updated in the movement
// tick and read by the player render (see playerWalkAnim.ts).
const _playerWalkFrames = new Map<string, HTMLImageElement>();
let _playerWalkAngle = Math.PI / 2; // default facing = down/front
let _playerWalkMoving = false;
let _playerWalkPhase = 0;
// BETA 22.71 — cosmetics system DELETED (Dylan: "delete all the cosmetic stuff,
// I'm not using it anymore"). Wearable overlays, silhouette model, /cosmetics
// editor, warp renderer and worn PNGs all removed.
function getCachedImage(url: string): HTMLImageElement | null {
  if (imageCache.has(url)) return imageCache.get(url)!;
  if (imageLoading.has(url)) return null;
  imageLoading.add(url);
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    // BETA 17.28 — Dylan's DevTools profile showed Image decode at 1604ms
    // (27.1% of frame!) because images decoded synchronously on first
    // drawImage. Pre-decode off-thread so the first frame that uses
    // each sprite doesn't stall. Mirrors loadImage path's behavior.
    img.decode?.().catch(() => { /* fallback to lazy decode */ });
    imageCache.set(url, img);
    imageLoading.delete(url);
  };
  img.onerror = () => { imageLoading.delete(url); };
  img.src = url;
  return null;
}

// BETA 13.88 — Cached "sprite with solid color outline" canvas. Returns an
// offscreen canvas containing: 8 directional offsets of a tinted sprite
// (creates the colored outline) + the original sprite drawn on top. Cached
// by (url + color + width). Computed ONCE per (sprite, color) combo, then
// drawn as a single drawImage per render (no per-frame outline cost).
//
// Used for chests + shrines per user request: "all chest are supposed to
// have a solid color border around the edge of the sprites a like 4 picles
// thing and then the glow."
const _outlinedSpriteCache: Map<string, HTMLCanvasElement> = new Map();
function getOutlinedSprite(
  spriteUrl: string,
  img: HTMLImageElement,
  outlineColor: string,
  outlineWidth: number = 4,
): HTMLCanvasElement | null {
  if (!img.naturalWidth) return null;
  const key = `${spriteUrl}|${outlineColor}|${outlineWidth}`;
  const cached = _outlinedSpriteCache.get(key);
  if (cached) return cached;

  // Step 1: render a tinted-sprite into a temp offscreen canvas.
  // (source-atop masks the fill to the sprite's alpha mask exactly.)
  const tmp = document.createElement('canvas');
  tmp.width = img.naturalWidth;
  tmp.height = img.naturalHeight;
  const tctx = tmp.getContext('2d');
  if (!tctx) return null;
  tctx.drawImage(img, 0, 0);
  tctx.globalCompositeOperation = 'source-atop';
  tctx.fillStyle = outlineColor;
  tctx.fillRect(0, 0, tmp.width, tmp.height);

  // Step 2: render the final canvas — bigger than the sprite by outlineWidth
  // on every side so the offsets fit. Stamp tinted-sprite at 8 offsets, then
  // draw the real sprite on top.
  const out = document.createElement('canvas');
  out.width = img.naturalWidth + outlineWidth * 2;
  out.height = img.naturalHeight + outlineWidth * 2;
  const octx = out.getContext('2d');
  if (!octx) return null;

  const o = outlineWidth;
  // 8 offsets: cardinal + diagonal — gives a clean outline that follows
  // the sprite's alpha edge in all directions.
  const offsets: Array<[number, number]> = [
    [-o, 0], [o, 0], [0, -o], [0, o],
    [-o, -o], [o, -o], [-o, o], [o, o],
  ];
  for (const [dx, dy] of offsets) {
    octx.drawImage(tmp, o + dx, o + dy);
  }
  octx.drawImage(img, o, o);

  _outlinedSpriteCache.set(key, out);
  return out;
}

// BETA 20.59 — Full-sprite colour tint (cached). Recolours a sprite's hue +
// saturation to `tintColor` while KEEPING its luminosity, so detail/shading
// survives (the chest still looks like a chest — just turned blue). Used to
// render the blue elite chest from the (red) ultra-chest art. Shares the
// outlined-sprite cache map (keyed with a 'tint|' prefix so no collision).
function getTintedSprite(spriteUrl: string, img: HTMLImageElement, tintColor: string): HTMLCanvasElement | null {
  if (!img.naturalWidth) return null;
  const key = `tint|${spriteUrl}|${tintColor}`;
  const cached = _outlinedSpriteCache.get(key);
  if (cached) return cached;
  const c = document.createElement('canvas');
  c.width = img.naturalWidth;
  c.height = img.naturalHeight;
  const tctx = c.getContext('2d');
  if (!tctx) return null;
  tctx.drawImage(img, 0, 0);
  // 'color' = source hue+sat over destination luminosity → a clean recolour.
  tctx.globalCompositeOperation = 'color';
  tctx.fillStyle = tintColor;
  tctx.fillRect(0, 0, c.width, c.height);
  // Clip the recolour fill back to the sprite's alpha mask.
  tctx.globalCompositeOperation = 'destination-in';
  tctx.drawImage(img, 0, 0);
  tctx.globalCompositeOperation = 'source-over';
  _outlinedSpriteCache.set(key, c);
  return c;
}

// ─── HUD Mouse Position (for pause hover tooltips) ───────────────────────────
let hudMouseX = 0;
let hudMouseY = 0;
export function setHudMousePos(x: number, y: number): void {
  hudMouseX = x;
  hudMouseY = y;
}

// BETA 22.74 — first-run ENTERING-THE-ZONE hang fix. loadImage had three
// never-settle paths (fetch with no timeout, resp.blob() on a stalled body
// stream, fallback Image() where neither onload nor onerror fires on a
// stalled request). One stalled request on a cold cache left initGame's
// preload await pending FOREVER with zero console errors; the second run
// always worked because everything else was cached and the stalled URL got
// a fresh connection. Every path is now bounded, and identical concurrent
// requests share one in-flight promise (boot preload + initGame used to
// double-fetch ~60 URLs).
const IMG_FETCH_TIMEOUT_MS = 8000;
const IMG_DECODE_TIMEOUT_MS = 3000;
const _inflightImages = new Map<string, Promise<HTMLImageElement>>();

async function loadImage(url: string, retries = 3): Promise<HTMLImageElement> {
  if (imageCache.has(url)) return imageCache.get(url)!;
  if (!url) return Promise.reject(new Error('Empty URL'));
  const inflight = _inflightImages.get(url);
  if (inflight) return inflight;
  const p = _loadImageUncached(url, retries);
  _inflightImages.set(url, p);
  try {
    return await p;
  } finally {
    _inflightImages.delete(url);
  }
}

async function _loadImageUncached(url: string, retries: number): Promise<HTMLImageElement> {
  // Use fetch+blob to bypass CORS/content-type issues with CDN
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const fetchUrl = attempt === 0 ? url : `${url}?retry=${attempt}&t=${Date.now()}`;
      // Bounded fetch: an aborted request REJECTS, which drops into the
      // existing cache-busting retry loop (the stall recovery that never
      // triggered before). The abort signal also covers a stalled blob().
      const ctrl = new AbortController();
      const abortTimer = setTimeout(() => ctrl.abort(), IMG_FETCH_TIMEOUT_MS);
      let blob: Blob;
      try {
        const resp = await fetch(fetchUrl, { mode: 'cors', signal: ctrl.signal });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        blob = await resp.blob();
      } finally {
        clearTimeout(abortTimer);
      }
      const objectUrl = URL.createObjectURL(blob);
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('Image decode failed'));
        el.src = objectUrl;
      });
      // BETA 13.92 FPS: force decode now (off-thread). Without this, decode
      // happens lazily on first drawImage during gameplay = mid-frame stutter.
      // BETA 22.74 — bounded: rare Chromium decode() never-settle bugs exist;
      // on timeout we proceed (decode falls back to lazy — cosmetic only).
      try {
        await Promise.race([
          img.decode(),
          new Promise<void>(r => setTimeout(r, IMG_DECODE_TIMEOUT_MS)),
        ]);
      } catch { /* older browsers — fall back to lazy decode */ }
      imageCache.set(url, img);
      return img;
    } catch (err) {
      if (attempt === retries - 1) {
        // Final fallback: try direct Image() load without crossOrigin —
        // bounded, because a stalled request fires NEITHER handler.
        return new Promise<HTMLImageElement>((resolve, reject) => {
          const img = new Image();
          const failTimer = setTimeout(
            () => reject(new Error(`Timed out after ${retries} attempts: ${url}`)),
            IMG_FETCH_TIMEOUT_MS,
          );
          img.onload = () => {
            clearTimeout(failTimer);
            imageCache.set(url, img);
            // best-effort decode (fire and forget — fallback path)
            img.decode?.().catch(() => {});
            resolve(img);
          };
          img.onerror = () => {
            clearTimeout(failTimer);
            reject(new Error(`Failed to load after ${retries} attempts: ${url}`));
          };
          img.src = url;
        });
      }
      await new Promise(r => setTimeout(r, 500 * (attempt + 1)));
    }
  }
  throw new Error(`Failed to load: ${url}`);
}

// ─── Room Helpers ───────────────────────────────────────────────────

function findCurrentRoom(rooms: PlacedRoom[], px: number, py: number): PlacedRoom | null {
  for (const room of rooms) {
    if (px >= room.x && px < room.x + room.size && py >= room.y && py < room.y + room.size) {
      return room;
    }
  }
  return null;
}

// ─── Preload Images Around Player ───────────────────────────────────

let lastPreloadTime = 0;
let lowHealthWarnTimer = 0; // Counts down; fires heartbeat sound when it hits 0
// BETA 15.92 — Tracks low-HP entry/exit transitions so we only call
// startSustainedDuck() once per entry and endSustainedDuck() once per exit.
// Without this latch the duck would re-trigger every frame while low-HP and
// effectively stay at the duck depth even after HP recovers (since each
// frame's startSustainedDuck call re-cancels the ramp-up). Reset to false on
// initGame so a fresh run doesn't start in a stuck-ducked state.
let _lowHpDucked = false;
// 🥜 pause/resume his voice across choice screens, fire the intro on the run-start
// transition, + throttle the elite/swarm danger scan.
// BETA 18.4 — Per-card focus scale (1.0 → 1.03 lerp) for the level-up /
// shrine / chest choice screens. Keyed by screen+index. Used to draw a
// smoothly-animated white focus outline that grows 3% larger on the card
// the player has selected (via mouse hover, gamepad d-pad, or keyboard
// arrows) and shrinks back when focus moves to a different card.
// Per Dylan: "i would love for a white box to be outlines on the one im
// on, and for the card to be lik 3 percent larger. but when i go to the
// next one cards my hit box is not on goes back to normal size in
// animation."
const _choiceCardScales = new Map<string, number>();
// ── BETA 22.87 — card BACKLIGHT: a blurred rarity-colored SILHOUETTE of the
// card itself (Dylan: "it should mask the edges perfectly and be a neon moving
// color of the card under it"). Cached per art+color; drawn scaled up +
// blurred + alpha-pulsed behind the card, brighter on the hovered card.
const _cardGlowCache = new Map<string, HTMLCanvasElement>();

// BETA 22.98 — Y-SORT (pack doc 03): machines whose BASE sits below the
// player draw AFTER him so he walks BEHIND them; machines above draw first
// so he walks in FRONT. Deferred list filled by the chest loop each frame,
// flushed in the player layer.
let _machinesOverPlayer: WildCardMachine[] = [];

// BETA 23.35 — latest state handle for the __devGoto console tool
let _devStateRef: GameState | null = null;

// BETA 23.13 — world props: same feet-line y-sort defer as machines.
let _propsOverPlayer: WorldProp[] = [];
// 23.49 — LAMP PERSONALITIES (Dylan: "should have like 50 different
// flickers and brightnesses... they are all pulsing the exact same").
// Seeded per lamp position: base brightness, radius, breath speed/phase,
// and a flicker STYLE — most steady, some uneasy, a few sputtering, and
// the occasional dying lamp that sags near-dark. The cone + bulb visuals
// read the same persona so what you SEE matches the pool on the ground.
interface LampPersona { base: number; radius: number; depth: number; bSpeed: number; phase: number; dying: boolean }
const _lampPersonas = new Map<string, LampPersona>();
function _lampPersona(x: number, y: number): LampPersona {
  const k = `${Math.round(x)},${Math.round(y)}`;
  let p = _lampPersonas.get(k);
  if (p) return p;
  if (_lampPersonas.size > 512) _lampPersonas.clear();
  let h = (Math.imul(Math.round(x), 0x9E3779B1) ^ Math.imul(Math.round(y), 0x85EBCA6B)) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0xC2B2AE35) >>> 0;
  const r1 = ((h >>> 8) & 1023) / 1023, r2 = ((h >>> 18) & 1023) / 1023, r3 = ((h >>> 2) & 255) / 255;
  const styleRoll = ((h >>> 5) & 99);
  const depth = styleRoll < 55 ? 0.03 + r3 * 0.05      // steady hum
              : styleRoll < 82 ? 0.10 + r3 * 0.12      // uneasy
              : styleRoll < 94 ? 0.32 + r3 * 0.22      // sputtering
              : 0.5 + r3 * 0.3;                        // dying
  p = {
    base: 0.52 + r1 * 0.46,
    radius: 160 + r2 * 60,
    depth,
    bSpeed: 0.9 + r1 * 1.8,
    phase: r2 * Math.PI * 2,
    dying: styleRoll >= 94,
  };
  _lampPersonas.set(k, p);
  return p;
}
/** live brightness 0..1 for a lamp this frame (breath × flicker × dying sag) */
function _lampLive(x: number, y: number, nowMs: number): number {
  const p = _lampPersona(x, y);
  let v = p.base * (0.93 + 0.07 * Math.sin(nowMs / (1400 / p.bSpeed) + p.phase));
  if (p.dying) v *= 0.3 + 0.7 * Math.max(0, Math.sin(nowMs / 1900 + p.phase));
  return v * flickerMul(x, y, p.depth, nowMs);
}

// 23.45 — tall foreground occluders that fade when they hide the player
const FADE_WHEN_OCCLUDING = new Set(['ftree_pine_01', 'ftree_oak_01', 'fcluster_02', 'fcluster_03', 'edge_cluster_01', 'edge_cluster_02', 'tree_lush_01', 'tree_lush_02', 'tree_lush_03', 'dead_tree']);

function _drawWorldProp(ctx: CanvasRenderingContext2D, p: WorldProp): void {
  const img = getCachedImage(p.def.url);
  if (!img || !(img as HTMLImageElement).naturalWidth) return;
  const el = img as HTMLImageElement;
  const aspect = el.naturalWidth / el.naturalHeight;
  notePropAspect(p.def.url, aspect);
  const h = p.def.h * (p.scale ?? 1), w = h * aspect;   // 23.45: per-instance scale
  if (p.def.kind === 'ground') {
    // painted at the ground angle already — lies flat, centered, no shadow
    ctx.drawImage(el, p.x - w / 2, p.y - h / 2, w, h);
    return;
  }
  // 23.32 — flips are allowed for PLACEMENT (Dylan) — everything inside the
  // mirror wrapper flips as a unit; the cast shadow mirrors its SHAPE only,
  // the shear (the sun) never changes.
  const _ax = p.def.anchorX ?? 0.5;
  if (p.flip) {
    ctx.save();
    ctx.translate(p.x, 0);
    ctx.scale(-1, 1);
    ctx.translate(-p.x, 0);
  }
  // 23.29/23.32 — shadow: sheared silhouette for chunky shapes, moonlight
  // ellipse for thin/leggy ones (Dylan's bench note); tall ones capped+soft.
  if (p.def.shadow === 'none') { /* 23.44: forest clusters — the mass IS the dark */ } else if (p.def.shadow === 'ellipse') {
    // 23.40 — ellipse sizes to the FOOT, not the sprite (Dylan: pole shadow
    // "terrible" — a tilted pole's sprite is mostly air, so the full-width
    // oval read as a giant blob under a toothpick)
    const _ew = p.def.foot ? Math.max(30, w * p.def.foot.halfWFrac * 2.2) : w * 0.6;
    drawEllipseShadow(ctx, p.x + (0.5 - _ax) * w, p.y - 2, _ew);
  }
  else drawCastShadow(ctx, p.def.url, p.x, p.y - 2, w, Math.min(h, 170), p.def.h > 180 ? 0.38 : 0.5);
  // 23.45 — OCCLUSION FADE (Dylan's 2.5D spec): when a FOREGROUND tree
  // (base south of Greg's feet) covers Greg, it fades to ~66% so he is
  // never unfairly hidden. Trunk-favoring split comes later if needed —
  // the spec blesses whole-sprite fade as the simple version. Lerped so
  // it breathes instead of popping.
  let _alpha = 1;
  if (FADE_WHEN_OCCLUDING.has(p.def.id)) {
    const _ls = _liveState;
    let target = 1;
    if (_ls) {
      const _plr = _ls.player;
      const _footY = _plr.y + (_plr.spriteSize || 40) * 0.6;
      if (p.y > _footY &&
          _plr.x > p.x - w * _ax - 8 && _plr.x < p.x + w * (1 - _ax) + 8 &&
          _plr.y > p.y - h && _plr.y < p.y + 6) target = 0.66;
    }
    p._fade = (p._fade ?? 1) + (target - (p._fade ?? 1)) * 0.14;
    if (p._fade < 0.995) _alpha = p._fade;
  }
  if (_alpha < 1) { ctx.save(); ctx.globalAlpha = _alpha; }
  ctx.drawImage(el, p.x - w * _ax, p.y - h, w, h);
  if (_alpha < 1) ctx.restore();

  // 23.32 — the lamp's GLASS DOME glows (measured: dome at 0.684 of sprite
  // width, 0.067 of height) and its light CONES DOWN toward the south
  // instead of a perfect circle (Dylan).
  if (p.def.id === 'street_lamp') {
    const domeX = p.x - w * _ax + w * 0.684;
    const domeY = p.y - h + h * 0.067;
    const cone = _getLampCone();
    const bulb = _getLampBulbGlow();
    // 23.49 — the visible cone + bulb read the SAME persona as the ground
    // pool, so a dying lamp LOOKS dying and a dim one looks dim.
    const _live = _lampLive(p.x, p.y, performance.now());
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    if (cone) {
      ctx.globalAlpha = 0.9 * _live;
      const cw = w * 1.7, ch = (p.y + 12) - domeY;
      ctx.drawImage(cone, domeX - cw / 2, domeY, cw, ch);
    }
    if (bulb) {
      ctx.globalAlpha = Math.min(1, 1.05 * _live);
      const bw = w * 0.85;
      ctx.drawImage(bulb, domeX - bw / 2, domeY - bw / 2, bw, bw);
    }
    ctx.restore();
  }

  // BETA 23.31 — DIGITAL BLINKING COP LIGHTS (Dylan: "cop cars with light
  // on it so you can add digital blinking cop lights"). The QA pass measured
  // the light bar's box in the sprite: normalized [0.4453,0.0154 → 0.7135,
  // 0.1734]. Red and blue halves alternate every 420ms with an additive glow.
  if (p.def.id === 'police_car_lightbar') {
    // 23.32 — lens boxes measured from the PIXELS (the bar sits diagonal on
    // the roof): BLUE lens upper-left, RED lens lower-right.
    // 23.33 — re-measured on the LEVELED (_v2) sprite by eyeballing the
    // actual lens pixels: BLUE upper-left, RED lower-right on the diagonal bar.
    const lx0 = p.x - w * _ax;
    const bluX = lx0 + w * 0.546, bluY = p.y - h + h * 0.021, bluW = w * (0.656 - 0.546), bluH = h * (0.117 - 0.021);
    const redX = lx0 + w * 0.660, redY = p.y - h + h * 0.174, redW = w * (0.7625 - 0.660), redH = h * (0.318 - 0.174);
    const phase = Math.floor(performance.now() / 420) % 2;
    const flick = 0.6 + 0.35 * Math.abs(Math.sin(performance.now() / 70));
    const blueBulb = _getBulbGlow('cop-blue', 'rgba(140,190,255,0.95)', 'rgba(64,128,255,0.55)', 'rgba(64,128,255,0)');
    const redBulb = _getBulbGlow('cop-red', 'rgba(255,150,150,0.95)', 'rgba(255,46,46,0.6)', 'rgba(255,46,46,0)');
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = phase === 1 ? flick : 0.08;
    ctx.fillStyle = 'rgb(64,128,255)';
    ctx.fillRect(bluX, bluY, bluW, bluH);
    if (blueBulb && phase === 1) { ctx.globalAlpha = flick * 0.9; ctx.drawImage(blueBulb, bluX + bluW / 2 - w * 0.14, bluY + bluH / 2 - w * 0.14, w * 0.28, w * 0.28); }
    ctx.globalAlpha = phase === 0 ? flick : 0.08;
    ctx.fillStyle = 'rgb(255,46,46)';
    ctx.fillRect(redX, redY, redW, redH);
    if (redBulb && phase === 0) { ctx.globalAlpha = flick * 0.9; ctx.drawImage(redBulb, redX + redW / 2 - w * 0.14, redY + redH / 2 - w * 0.14, w * 0.28, w * 0.28); }
    ctx.restore();
  }

  // BETA 23.21 — THE SURGE STATUE breathes: the active (fully-energized)
  // twin is pixel-registered over the idle base and pulses in and out —
  // Dylan: "it needs it going all the way through it, and it, like,
  // pulsing... a version when it's activated and a version whenever it's
  // stationary." Full-on 100% active is reserved for the future magnet-pull
  // mechanic; world statues idle-breathe between 30-80% energy.
  if (p.def.id === 'statue_surge') {
    const act = getCachedImage('/map/deadlot/props/statue_surge_active_v1.webp');
    if (act && (act as HTMLImageElement).naturalWidth) {
      const pulse = 0.3 + 0.5 * (0.5 + 0.5 * Math.sin(performance.now() / 640 + p.x * 0.01));
      ctx.save();
      // 23.39 — the statue's OWN purple is the light source now (Dylan
      // killed the ground pool): normal pass keeps the sprite readable,
      // then an additive pass makes the energy actually EMIT at night —
      // same recipe as the surge-vein glow.
      ctx.globalAlpha = pulse;
      ctx.drawImage(act as HTMLImageElement, p.x - w * _ax, p.y - h, w, h);
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = 0.25 + 0.45 * pulse;
      ctx.drawImage(act as HTMLImageElement, p.x - w * _ax, p.y - h, w, h);
      ctx.restore();
    }
  }
  if (p.flip) ctx.restore();   // 23.32 — close the mirror wrapper
}

// ── BETA 23.12 — machine sign text + SE cast shadows ────────────────────────
// Dylan's new angled machines have a blank black sign box on top: the token
// cost renders INTO that box, warped to the panel's angle so it reads as
// painted-on/backlit, never flat screen-space text. Quads = the inner black
// face corners (normalized to the sprite), measured off the actual pixels.
// Remeasure whenever the machine art changes.
const _MACHINE_SIGN_QUADS: Record<string, { tl: [number, number]; tr: [number, number]; bl: [number, number] }> = {
  right: { tl: [0.188, 0.031], tr: [0.843, 0.040], bl: [0.192, 0.188] },
  left:  { tl: [0.129, 0.061], tr: [0.774, 0.039], bl: [0.134, 0.215] },
};

// Baked warped sign-text overlays, one full-sprite-sized canvas per
// (facing, cost, affordable) — per frame it's a single drawImage on the same
// rect as the machine. Bake-time shadowBlur is fine; per-frame is the perf
// trap (playbook §5).
const _machineSignCache = new Map<string, HTMLCanvasElement>();
function _getMachineSignSprite(facing: string, cost: number, affordable: boolean, img: HTMLImageElement): HTMLCanvasElement | null {
  const key = `${facing}|${cost}|${affordable ? 1 : 0}`;
  const hit = _machineSignCache.get(key);
  if (hit) return hit;
  const quad = _MACHINE_SIGN_QUADS[facing] ?? _MACHINE_SIGN_QUADS.right;
  const W = img.naturalWidth, H = img.naturalHeight;
  if (!W || !H) return null;
  const cv = document.createElement('canvas');
  cv.width = W; cv.height = H;
  const c2 = cv.getContext('2d');
  if (!c2) return null;
  // Affine mapping a TWxTH virtual text space onto the sign face
  // parallelogram (TL + u·(TR−TL) + v·(BL−TL)).
  const TW = 400, TH = 120;
  const tl = [quad.tl[0] * W, quad.tl[1] * H];
  const tr = [quad.tr[0] * W, quad.tr[1] * H];
  const bl = [quad.bl[0] * W, quad.bl[1] * H];
  c2.setTransform(
    (tr[0] - tl[0]) / TW, (tr[1] - tl[1]) / TW,
    (bl[0] - tl[0]) / TH, (bl[1] - tl[1]) / TH,
    tl[0], tl[1],
  );
  // Fit the price as BIG as the box allows (margins keep it off the frame;
  // extra bottom margin absorbs the slight non-affine lean of the left panel).
  const label = String(cost);
  const maxW = TW * 0.84, maxH = TH * 0.7;   // 23.32: slightly smaller = sharper
  let fs = Math.floor(TH * 0.74);
  c2.textAlign = 'center'; c2.textBaseline = 'middle';
  c2.font = `400 ${fs}px SorryBossTitle, Anton, Impact, sans-serif`;
  while (fs > 10 && (c2.measureText(label).width > maxW || fs > maxH)) {
    fs--; c2.font = `400 ${fs}px SorryBossTitle, Anton, Impact, sans-serif`;
  }
  const cx = TW * 0.52, cy = TH * 0.55;   // 23.32: true optical center of the panel (Dylan: "not centered")
  if (affordable) {
    // 23.29 — ILLUMINATING YELLOW NEON (Dylan: the number was grey and
    // unreadable): the whole sign face gets a faint yellow wash, then hot
    // halo passes, then a near-white-yellow core.
    // 23.32 — SHARP neon (Dylan: "glowing too much makes it fuzzy"): one
    // moderate halo + a crisp core, plus the faint panel wash.
    c2.fillStyle = 'rgba(255,220,40,0.12)';
    c2.fillRect(0, 0, TW, TH);
    c2.shadowColor = 'rgba(255,230,40,0.9)'; c2.shadowBlur = fs * 0.3;
    c2.fillStyle = 'rgba(255,238,80,0.95)';
    c2.fillText(label, cx, cy); c2.fillText(label, cx, cy);
    c2.shadowBlur = 0;
    c2.fillStyle = '#fff9b0';
    c2.fillText(label, cx, cy);
  } else {
    // powered-down: dim, cold, no bloom — "you can't afford this"
    c2.shadowColor = 'rgba(255,120,90,0.25)'; c2.shadowBlur = fs * 0.25;
    c2.fillStyle = 'rgba(190,175,160,0.42)';
    c2.fillText(label, cx, cy);
  }
  c2.setTransform(1, 0, 0, 1, 0, 0);
  c2.shadowBlur = 0;
  // Don't cache until the game font is actually loaded — a bake made with the
  // fallback font would be frozen forever.
  let fontReady = true;
  try { fontReady = document.fonts.check('16px SorryBossTitle'); } catch { /* older browsers */ }
  if (fontReady) {
    if (_machineSignCache.size > 16) _machineSignCache.clear();
    _machineSignCache.set(key, cv);
  }
  return cv;
}

// 23.29/23.32 — baked glow blobs (lamp warm bulb, cop red/blue) + the lamp's
// light CONE (Dylan: "the light should be coned down pointing south, not a
// perfect circle"). All baked once.
const _bulbCache = new Map<string, HTMLCanvasElement>();
function _getBulbGlow(key: string, inner: string, mid: string, outer: string): HTMLCanvasElement | null {
  const hit = _bulbCache.get(key);
  if (hit) return hit;
  const cv = document.createElement('canvas');
  cv.width = 64; cv.height = 64;
  const c2 = cv.getContext('2d');
  if (!c2) return null;
  const g = c2.createRadialGradient(32, 32, 2, 32, 32, 30);
  g.addColorStop(0, inner);
  g.addColorStop(0.25, mid);
  g.addColorStop(1, outer);
  c2.fillStyle = g;
  c2.fillRect(0, 0, 64, 64);
  _bulbCache.set(key, cv);
  return cv;
}
function _getLampBulbGlow(): HTMLCanvasElement | null {
  return _getBulbGlow('lamp', 'rgba(255,244,200,0.95)', 'rgba(255,208,100,0.55)', 'rgba(255,184,77,0)');
}
let _lampCone: HTMLCanvasElement | null = null;
function _getLampCone(): HTMLCanvasElement | null {
  if (_lampCone) return _lampCone;
  const cv = document.createElement('canvas');
  cv.width = 128; cv.height = 256;
  const c2 = cv.getContext('2d');
  if (!c2) return null;
  // trapezoid light shaft: narrow at the dome, widening to the ground, fading
  const g = c2.createLinearGradient(0, 0, 0, 256);
  g.addColorStop(0, 'rgba(255,214,130,0.34)');
  g.addColorStop(0.75, 'rgba(255,196,100,0.13)');
  g.addColorStop(1, 'rgba(255,190,90,0)');
  c2.fillStyle = g;
  c2.beginPath();
  c2.moveTo(52, 0); c2.lineTo(76, 0);
  c2.lineTo(122, 246); c2.lineTo(6, 246);
  c2.closePath();
  c2.fill();
  _lampCone = cv;
  return cv;
}

// NE-pointing cast shadow (Dylan's law 23.12: "shadows have to be NE" — the
// sunset sun sits low in the SOUTH-WEST, so every collision object casts
// up-and-right, tucking the shadow BEHIND the object instead of over the
// walkable ground in front). Bake = vertically-flipped silhouette in
// Wesnoth's shadow color (#160034 — never pure black); runtime = one sheared
// drawImage from the feet line: rows fan up-right (x' = x + 0.55y, y' = −0.34y).
const _castShadowCache = new Map<string, HTMLCanvasElement>();
export function getCastShadowSprite(url: string, imgOverride?: HTMLImageElement): HTMLCanvasElement | null {
  const hit = _castShadowCache.get(url);
  if (hit) return hit;
  // 23.15 — imgOverride lets callers whose images live outside getCachedImage
  // (player walk frames) bake shadows too; the cache key is still the url.
  const img = imgOverride ?? getCachedImage(url);
  if (!img || !(img as HTMLImageElement).naturalWidth) return null;
  const el = img as HTMLImageElement;
  const cv = document.createElement('canvas');
  cv.width = el.naturalWidth; cv.height = el.naturalHeight;
  const c2 = cv.getContext('2d');
  if (!c2) return null;
  c2.translate(0, cv.height);
  c2.scale(1, -1);                       // row 0 of the bake = the FEET
  c2.drawImage(el, 0, 0);
  c2.setTransform(1, 0, 0, 1, 0, 0);
  c2.globalCompositeOperation = 'source-in';
  c2.fillStyle = '#160034';
  c2.fillRect(0, 0, cv.width, cv.height);
  // Soft-edge pass — a razor-edged silhouette reads as a painted decal, not
  // a shadow (first in-game look confirmed it). Quarter-res + blur, once.
  const sw = Math.max(64, Math.round(cv.width / 4));
  const sh = Math.max(64, Math.round(cv.height / 4));
  const pad = Math.round(sw * 0.10);
  const soft = document.createElement('canvas');
  soft.width = sw + pad * 2; soft.height = sh + pad * 2;
  const s2 = soft.getContext('2d');
  if (!s2) return null;
  s2.filter = `blur(${Math.max(2, Math.round(sw * 0.025))}px)`;
  s2.drawImage(cv, pad, pad, sw, sh);
  s2.filter = 'none';
  if (_castShadowCache.size > 96) _castShadowCache.clear();   // 23.15: player walk frames share this cache
  _castShadowCache.set(url, soft);
  return soft;
}
export function drawCastShadow(ctx: CanvasRenderingContext2D, url: string, feetX: number, feetY: number, w: number, h: number, alpha = 0.62, flip = false): void {
  const sil = getCastShadowSprite(url);
  if (!sil) return;
  // 'multiply' darkens the asphalt under the silhouette instead of painting
  // violet over it — the difference between "shadow" and "purple carpet".
  // The bake has a 10%-per-side pad for the blur skirt; over-draw to match.
  // 23.14 — ONE shadow angle everywhere (Dylan: "same exact angle as cop
  // cars. i like that shadow angle!"). The car look = short grounded hug
  // with a slight NE lean; the 0.8/−0.52 fan made TALL objects (machine,
  // lamp) read as a different angle than the wide-low cars. 0.5/−0.34
  // keeps every silhouette tucked like the cars.
  ctx.save();
  ctx.translate(feetX, feetY);
  ctx.transform(1, 0, 0.5, -0.34, 0, 0);    // rows lean UP-right = NE
  if (flip) ctx.scale(-1, 1);               // 23.32: mirror the SHAPE only — shear (=sun) unchanged
  ctx.globalAlpha = alpha;
  ctx.globalCompositeOperation = 'multiply';
  ctx.drawImage(sil, -w * 0.6, -h * 0.1, w * 1.2, h * 1.2);
  ctx.restore();
}

// 23.32 — MOONLIGHT contact ellipse for thin/leggy props (benches, poles,
// cones): a sheared leg-silhouette read wrong (Dylan). Soft, short, nudged NE.
export function drawEllipseShadow(ctx: CanvasRenderingContext2D, feetX: number, feetY: number, w: number): void {
  ctx.save();
  ctx.globalCompositeOperation = 'multiply';
  // 23.37 — soften with width: at bench size (~100wu) the 0.4 oval reads as
  // moonlight, but on wide frames (swing set 154wu) it read as a dark puddle
  ctx.globalAlpha = w > 110 ? 0.4 * (110 / w) : 0.4;
  ctx.fillStyle = '#160034';
  ctx.beginPath();
  ctx.ellipse(feetX + w * 0.08, feetY - 2, w * 0.52, w * 0.17, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}
// 23.15 — same NE shadow for sprites whose image lives outside getCachedImage
// (the player's walk frames). flip mirrors the SILHOUETTE only — the shear
// term is untouched, so the shadow angle stays NE no matter which way the
// sprite faces (one sun, one angle).
export function drawCastShadowForImage(ctx: CanvasRenderingContext2D, key: string, img: HTMLImageElement, feetX: number, feetY: number, w: number, h: number, alpha = 0.5, flip = false): void {
  const sil = getCastShadowSprite(key, img);
  if (!sil) return;
  ctx.save();
  ctx.translate(feetX, feetY);
  ctx.transform(1, 0, 0.5, -0.34, 0, 0);
  if (flip) ctx.scale(-1, 1);
  ctx.globalAlpha = alpha;
  ctx.globalCompositeOperation = 'multiply';
  ctx.drawImage(sil, -w * 0.6, -h * 0.1, w * 1.2, h * 1.2);
  ctx.restore();
}

function _drawWildCardMachine(ctx: CanvasRenderingContext2D, state: GameState, chest: WildCardMachine): void {
  const url = getMachineSprite(chest);
  if (!url) return;
  const img = getCachedImage(url);
  if (!img) return;
  const _mH = MACHINE_SPRITE_SIZE * 1.88;   // 23.29: ×0.8 — Dylan: "slightly too big... maybe a twenty percent decrease"
  const _mBottom = chest.y + MACHINE_SPRITE_SIZE / 2;
  const _mAsp = (img as HTMLImageElement).naturalWidth > 0
    ? (img as HTMLImageElement).naturalWidth / (img as HTMLImageElement).naturalHeight : 0.71;
  const _mW = _mH * _mAsp;

  // vend shake — decaying jitter, shadow stays planted (looks real)
  let _shX = 0, _shY = 0;
  if (chest.vendShakeAt != null) {
    const _vt = (performance.now() - chest.vendShakeAt) / 1000;
    if (_vt >= 0 && _vt < 0.7) {
      const _amp = 5.5 * (1 - _vt / 0.7);
      _shX = Math.sin(_vt * 55) * _amp;
      _shY = Math.cos(_vt * 43) * _amp * 0.55;
    }
  }

  // BETA 23.03 — AO GRIME PATCH under the machine (doc-09 ambient occlusion;
  // also kills the "white under the machine" read — the floor's brightness
  // layer could drop a LIGHT patch right under a cabinet, which looked like
  // a baked white shadow. The sprite itself is clean — verified pixel-level).
  ctx.save();
  const _aoG = ctx.createRadialGradient(chest.x, _mBottom - 8, 0, chest.x, _mBottom - 8, _mW * 0.85);
  _aoG.addColorStop(0, 'rgba(8,8,10,0.34)');
  _aoG.addColorStop(0.6, 'rgba(8,8,10,0.18)');
  _aoG.addColorStop(1, 'rgba(8,8,10,0)');
  ctx.fillStyle = _aoG;
  ctx.beginPath();
  ctx.ellipse(chest.x, _mBottom - 8, _mW * 0.85, _mW * 0.30, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // BETA 23.12 — directional CAST shadow to the SE (replaces the under-
  // ellipse; the AO patch above stays as contact grounding). Shadow stays
  // planted during vend shake.
  drawCastShadow(ctx, url, chest.x, _mBottom - 4, _mW, _mH);

  const _mX = chest.x - _mW / 2 + _shX;
  const _mY = _mBottom - _mH + _shY;
  ctx.drawImage(img, _mX, _mY, _mW, _mH);

  // BETA 23.12 — token cost baked INTO the top sign box, warped to the
  // panel's angle and backlit (Dylan: big as it can be, never poking out,
  // illuminating, matching the warp).
  const _cost = getCachedChestCost(state);
  const _affordable = state.player.gold >= _cost;
  const _sign = _getMachineSignSprite(chest.facing, _cost, _affordable, img as HTMLImageElement);
  if (_sign) ctx.drawImage(_sign, _mX, _mY, _mW, _mH);
}

function _getCardGlowSprite(artUrl: string, rgb: string): HTMLCanvasElement | null {
  const key = `${artUrl}|${rgb}`;
  const hit = _cardGlowCache.get(key);
  if (hit) return hit;
  const img = getCachedImage(artUrl);
  if (!img || !img.naturalWidth) return null;
  const cv = document.createElement('canvas');
  cv.width = img.naturalWidth; cv.height = img.naturalHeight;
  const c2 = cv.getContext('2d');
  if (!c2) return null;
  c2.drawImage(img, 0, 0);
  c2.globalCompositeOperation = 'source-in';
  c2.fillStyle = `rgb(${rgb})`;
  c2.fillRect(0, 0, cv.width, cv.height);
  if (_cardGlowCache.size > 24) _cardGlowCache.clear();   // tiny cap, menu-only
  _cardGlowCache.set(key, cv);
  return cv;
}

// ── BETA 22.87 — PERK CARD SYSTEM v1 animation state ────────────────────────
// Slam-in clock (reset on every fresh 3-card offer incl. rerolls), the exit
// beat (chosen card pulses + losers fade ~450ms before gameplay resumes), and
// the fly-to-HUD overlay (chosen card shrinks into the PERCS panel slot).
let _luOpenAt = 0;
let _luOptionsRef: unknown = null;
let _luSlamPlayed: boolean[] = [false, false, false];
interface _LuCardSnap { art: string | null; rarity: string; x: number; y: number; w: number; h: number }
let _luExit: { idx: number; start: number; snaps: _LuCardSnap[] } | null = null;
let _luFly: { art: string | null; rarity: string; x: number; y: number; w: number; h: number; start: number } | null = null;
let _luLastLayout: { startX: number; cardY: number; cardW: number; cardH: number; gap: number } | null = null;

// ── BETA 22.89 — WILD CARD machine screen animation state (mirror of _lu*).
// The offer key is the itemId+tier signature: getChestOptionDisplayData builds
// a fresh array every call, so identity (the _luOptionsRef trick) can't detect
// "same offer, next frame" here — the signature can.
let _wcOpenAt = 0;
let _wcOfferKey = '';
let _wcSlamPlayed: boolean[] = [false, false, false];

// BETA 22.89 — TRUE plate-text centering (Dylan: "the entire point… centered
// vertically and horizontally automatically, cause it's not"). The old
// renderer used textBaseline:'middle' + a fixed "+7% of font size" optical
// drop, which double-counted: middle already splits the em box, so the fudge
// pushed everything BELOW the boxes Dylan calibrates (the calibrator's DOM
// sample text flex-centers = true center; canvas must match it). Real fix:
// alphabetic baseline + the measured glyph box — drawing the baseline at
// cy + (ascent − descent)/2 puts the ink's visual center exactly on cy.
// Callers set textBaseline='alphabetic' before measuring AND drawing.
function _plateBaselineY(ctx: CanvasRenderingContext2D, text: string, cy: number): number {
  const m = ctx.measureText(text);
  return cy + (m.actualBoundingBoxAscent - m.actualBoundingBoxDescent) / 2;
}

export function isLevelUpExitActive(): boolean { return _luExit !== null; }

/** Called by every pick path (mouse/keyboard/gamepad) AFTER a successful
 *  handleLevelUpSelection. Freezes a snapshot of the 3 cards so the exit
 *  beat can render after the display cache clears. */
export function beginLevelUpExit(
  _state: GameState, idx: number,
  opts: Array<{ percId?: string; rarity: string; type: string; weaponId?: string; percIcon?: string }>,
): void {
  const L = _luLastLayout;
  if (!L) return;
  const snaps: _LuCardSnap[] = opts.map((o, i) => {
    let art: string | null;
    if (o.type === 'new_weapon' || o.type === 'weapon_upgrade') {
      // BETA 22.89 — the exit/fly snapshot carries ONE art url, so the live-
      // drawn weapon sprite used to vanish here. Baked shell+sprite keeps the
      // card solid; shell-only remains the modded/unloaded fallback.
      const baked = (o.weaponId && getWeaponDef(o.weaponId)?.spriteUrl === o.percIcon)
        ? weaponCardArtPath(o.weaponId, o.rarity) : null;
      art = (baked && getCachedImage(baked)) ? baked : cardShellPath(o.rarity);
    } else {
      art = cardArtPath(o.percId ?? '', o.rarity);
    }
    return {
      art,
      rarity: o.rarity,
      x: L.startX + i * (L.cardW + L.gap), y: L.cardY, w: L.cardW, h: L.cardH,
    };
  });
  _luExit = { idx, start: performance.now(), snaps };
  try { playCardSelectSfx(opts[idx]?.rarity ?? 'common'); } catch { /* sfx optional */ }
}

/** Chosen-card fly overlay — drawn during PLAY (after the HUD) so the card
 *  visibly lands in the right panel's PEANUT PERCS strip. */
function _renderLevelUpFly(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  if (!_luFly) return;
  const t = (performance.now() - _luFly.start) / 650;
  if (t >= 1) { _luFly = null; return; }
  const ease = t * t * (3 - 2 * t);
  // target ≈ the PEANUT PERCS icon strip in renderRightPanel (bottom-right stack)
  const tx = w - 150, ty = h - 190;
  const cx = _luFly.x + (_luFly.w / 2) + (tx - _luFly.x - _luFly.w / 2) * ease;
  const cy = _luFly.y + (_luFly.h / 2) + (ty - _luFly.y - _luFly.h / 2) * ease;
  const cw = _luFly.w * (1 - 0.92 * ease);
  const ch = _luFly.h * (1 - 0.92 * ease);
  const st = CARD_RARITY_STYLE[_luFly.rarity] ?? CARD_RARITY_STYLE.common;
  ctx.save();
  ctx.globalAlpha = t > 0.85 ? (1 - t) / 0.15 : 1;
  // sparkle trail for legendary (cheap: 6 fading glints along the path)
  if (st.sparkle) {
    for (let k = 0; k < 6; k++) {
      const tk = Math.max(0, ease - k * 0.06);
      const px = _luFly.x + _luFly.w / 2 + (tx - _luFly.x - _luFly.w / 2) * tk;
      const py = _luFly.y + _luFly.h / 2 + (ty - _luFly.y - _luFly.h / 2) * tk;
      ctx.fillStyle = `rgba(${st.glow},${0.5 - k * 0.07})`;
      ctx.beginPath();
      ctx.arc(px + Math.sin(k * 2.1) * 9, py + Math.cos(k * 1.7) * 9, 3.5 - k * 0.4, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  const img = _luFly.art ? getCachedImage(_luFly.art) : null;
  ctx.shadowColor = `rgba(${st.glow},0.8)`;
  ctx.shadowBlur = 16;
  if (img) ctx.drawImage(img, cx - cw / 2, cy - ch / 2, cw, ch);
  else { ctx.fillStyle = `rgba(${st.glow},0.6)`; ctx.fillRect(cx - cw / 2, cy - ch / 2, cw, ch); }
  ctx.restore();
}

function _choiceCardScaleTick(screen: string, idx: number, focused: boolean): number {
  const key = `${screen}_${idx}`;
  const cur = _choiceCardScales.get(key) ?? 1.0;
  const target = focused ? 1.03 : 1.0;
  // Smooth lerp — 12% toward target per frame ≈ ~50ms to reach within 1%.
  const next = cur + (target - cur) * 0.12;
  // Snap once within rounding margin so the value doesn't lerp forever.
  const out = Math.abs(target - next) < 0.0005 ? target : next;
  _choiceCardScales.set(key, out);
  return out;
}
let _shitTrailTimer = 0;    // BETA 13.98 — SHIT (item_shit) trail-drop interval timer
let _sheeeshRevivesUsed = 0; // BETA 20.87 — Sheeesh revive COUNT (Dylan: each copy = its own revive). Was a one-shot boolean.
// BETA 22.90 — N10 HEART THROB runtime state (was the Debt gold-shield vars —
// Dylan: "delete the old gold stuff"). _heartThrobReadyAt = game-time when the
// next shockwave may fire. Reset in startNewRun + resetReviveFlagsForBuild.
let _heartThrobReadyAt = 0;
// BETA 22.91 — trigger CHANGED per Dylan: "heart throb should be if your
// health gets too low — like whenever it causes the screen to flicker and go
// red. that is when heart throb activates. needs a solid cool down right?"
// Fires when HP drops below the low-health red-vignette threshold (35% —
// keep in sync with renderLowHealthVignette). Blast/force unchanged; the
// "solid" cooldown is 8s.
const HEART_THROB_HP_FRAC = 0.35;
const HEART_THROB_BLAST_R = 240;
const HEART_THROB_FORCE = 55;
const HEART_THROB_COOLDOWN = 8.0;
// BETA 14.45 — Exposed reset for WeaponLab applyBuild() so per-build builds get
// a fresh Sheeesh + Liar Deceiver revive budget. Without this, a build later in
// the session never sees Sheeesh fire (already used in a prior build), making
// per-build attribution wrong.
export function resetReviveFlagsForBuild(): void {
  _sheeeshRevivesUsed = 0;
  _heartThrobReadyAt = 0;
}
// BETA 14.04 — kill delta tracker. We compare totalKills frame-over-frame to
// fire on_kill hooks (Mom's Cooking heal, Mystery Meat random stat roll).
let _lastFrameTotalKills = 0;

// BETA 13.99 — Bungulators minion runtime state (follows player, alternates idle/punch frames)
interface BungulatorMinion {
  x: number;
  y: number;
  attackCooldown: number;     // s until next melee swing
  punchAnimTimer: number;     // s remaining showing punch frame (0 = idle frame)
  facing: 1 | -1;             // sprite flip
  /** Per-stack offset so multiple Bungulators don't overlap when idle */
  offsetAngle: number;
  // BETA 15.27 — angle-based ring movement (Dylan: "live on the invisible
  // white circle... turn and move freely on it"). Position is now derived
  // from `angle` each frame: x = player.x + cos(angle) * R, same for y.
  // This guarantees they STAY on the ring (never slide through the player
  // center). targetAngle lerps via shortest-arc, so a bungulator at 0°
  // moving to 180° goes the short way around the ring instead of straight
  // through the middle.
  angle: number;              // current angular position on orbit ring (radians)
}
let _bungulators: BungulatorMinion[] = [];

// BETA 13.99 — Iranian Bird minion runtime state (orbits player, auto-fires)
// BETA 14.78 — refactored to array so stacks + projectileCountBonus produce
// multiple birds. User: "i should be able to have as many iranian birds as i
// unlock. should be tied to increased projectile too." Count = stacks +
// projectileCountBonus. Each bird has its own angle / attack cooldown / facing.
interface IranianBird {
  angle: number;        // current orbital angle (radians)
  attackCd: number;     // s until next shot
  facing: number;       // sprite rotation toward last target
  /** Per-stack offset so multiple birds don't overlap; recomputed each frame
   *  to keep them evenly spaced as the count changes. */
  offsetAngle: number;
}
let _iranianBirds: IranianBird[] = [];
// BETA 13.102 — visual bullets fired by Iranian Bird. Pure VFX (damage is
// applied instantly on zap); these are tracers that travel from bird to
// last target so the user sees something "shooting" instead of just sparks.
interface IranianBirdBullet { x0: number; y0: number; x1: number; y1: number; age: number; lifetime: number; }
let _iranianBirdBullets: IranianBirdBullet[] = [];

// BETA 13.102 — Whoopie Cushion of Doom + Asmon's Desk Banana auto-drop timers
let _whoopieDropTimer = 0;
let _bananaDropTimer = 0;
function preloadImagesAround(state: GameState, px: number, py: number) {
  // Throttle: only run every 500ms to avoid per-frame overhead
  const now = performance.now();
  if (now - lastPreloadTime < 500) return;
  lastPreloadTime = now;

  const viewDist = 3000; // generous preload radius
  const { roomMap } = state.map;

  // Preload room tile images near the player
  for (const room of roomMap.rooms) {
    const cx = room.x + room.size / 2;
    const cy = room.y + room.size / 2;
    if (Math.abs(cx - px) < viewDist && Math.abs(cy - py) < viewDist) {
      getCachedImage(room.cdnUrl);
    }
  }

  // Preload shrine sprites near the player
  for (const shrine of roomMap.shrines) {
    if (Math.abs(shrine.x - px) < viewDist && Math.abs(shrine.y - py) < viewDist) {
      getCachedImage(getShrineSprite(shrine));
    }
  }

  // Preload chest sprites near the player
  for (const chest of roomMap.chests) {
    if (chest.state === 'gone') continue;
    if (Math.abs(chest.x - px) < viewDist && Math.abs(chest.y - py) < viewDist) {
      const url = getMachineSprite(chest);
      if (url) getCachedImage(url);
    }
  }

  // Preload barrel sprites (only 3 files — always preload regardless of distance)
  getCachedImage(BARREL_SPRITE_URL_XP);
  getCachedImage(BARREL_SPRITE_URL_GOLD);
  getCachedImage(BARREL_SPRITE_URL_BOTH);
  // BETA 16.22 — preload enemy death floor sprites + gas cloud sprites
  // (small, always-on assets so they never pop in on first death/gas-tick).
  for (const url of getDeathDecalUrls()) getCachedImage(url);
  for (const url of getGasOverlayUrls()) getCachedImage(url);
}

// ─── Initialize ─────────────────────────────────────────────────────

// BETA 16.88 — Per Dylan: "when i try and play a game now it takes a
// long time to load in. why?" Root cause: initGame awaits ALL game
// images (~60-70 PNGs: floor tiles, border walls, shrines, room PNGs,
// 26 Buncha Bullshiiii icons at up to 5MB each, 14 Peanut Perc icons)
// before the game becomes interactive. On a fresh page load with cold
// cache, that can take 5-30s depending on connection speed.
//
// 95% of the asset list is FIXED across runs (only the room PNGs vary
// per seed, but they pull from a fixed pool). Fix: kick off a static
// asset preload at app boot (Home.tsx mount) so by the time the player
// navigates auth → menu → character select → loadout → DEPLOY → 3-second
// countdown → initGame, the images are already in imageCache and
// initGame's await resolves instantly. No UI blocking, no awaiting on
// boot — fire and forget.
export async function preloadStaticGameAssets(): Promise<void> {
  const allUrls: string[] = [
    ...getFloorTileUrls(1),
    ...Object.values(BORDER_WALL_ASSETS),
    OUTSIDE_TEXTURE_URLS[1],
    ...Object.values(SHRINE_ASSETS),
    ...BUNCHA_BULLSHIIII.map(item => item.iconUrl),
    ...PEANUT_PERCS.map(perc => perc.iconUrl),
    // BETA 21.07 — character-select portraits. These were NOT preloaded, so on a
    // slow connection a tester hit Select Character with black tiles (the grid
    // <img> only started loading then, lazily). Now they download in the
    // background during auth/menu and are cached by the time the screen shows.
    // Paths match MenuRenderer's resolveAsset('/characters/<id>_peanut_portrait.png')
    // (resolveAsset is identity, so these are the exact same cache keys).
    ...['tactical', 'sponge', 'cowboy', 'killa', 'wizard', 'viking', 'mr_silly', 'lebron', 'chad', 'pirate', 'greg', 'suit_guy', 'cheerleader', 'bigfoot']
      .map(id => `/characters/${id}_peanut_portrait.png`),
  ];
  const uniqueUrls = Array.from(new Set(allUrls.filter(Boolean)));
  // Fire all loads in parallel. Cache hits are instant on later calls.
  await Promise.allSettled(uniqueUrls.map(url => loadImage(url)));
}

// BETA 23.62 — CO-OP PHASE 1: opts.seed lets a future co-op host hand every
// client the world seed (host broadcasts one number, all machines build the
// identical world). Omitted = exactly the old behavior (__setMapSeed pin, else
// fresh). No current caller passes it — single-player unchanged.
export async function initGame(character: CharacterDef, opts?: { seed?: number }): Promise<GameState> {
  // BETA 20.02 — Preload all enemy + weapon sprites BEFORE the game starts.
  // Per Dylan: "lag in the beginning, like a slight pause and then runs fine."
  // Was: each enemy variant + weapon loaded its sprite on first spawn/fire
  // (network fetch + GPU upload mid-frame). Now: kick off all loads at
  // initGame start; image.decode() runs off-thread, so by the time the
  // player can move, the sprite cache is warm. Skips the 1604ms image-decode
  // tail that BETA 17.28 only partially fixed (icons + chest sprites).
  // Fire-and-forget — image.decode() is async but doesn't block here.
  try { preloadAllEnemySprites(); } catch { /* defensive */ }
  try { preloadAllWeaponSprites(); } catch { /* defensive */ }

  // BETA 20.92 — seed the balance-roll RNG once per run (reproducible offers).
  // Fresh random seed each run, unless window.__setRunSeed(n) forced one for a
  // reproducibility test. Recorded into the run row so any run replays by seed.
  // Cosmetic randomness stays on Math.random (see rng.ts). Safe: only the
  // balance-roll call sites read this; an unseeded roll is still valid.
  try { seedRolls(); } catch { /* defensive */ }

  // BETA 15.91 — Reset live-play telemetry guard so the next death/win
  // submits a fresh row to Supabase. Previously-finalized run is already
  // submitted; this just clears the once-per-run dedupe flag.
  resetLivePlayTracker();
  // BETA 16.22 — fresh death-decal + gas-overlay state per run.
  clearDeathDecals();
  clearGroundDecals();
  clearGasOverlay();
  // BETA 16.24 — PSBOT cycle-25 P0.7: drop any leftover queued XP so
  // a new run starts at xp=0 cleanly.
  _resetQueuedXp();
  // BETA 16.25 — PSBOT cycle-26 P0.2: reset the prev-alive-HP tracker
  // for the new run.
  _prevAliveHp = 100;
  // BETA 16.26 — PSBOT cycle-27 P0.2: clear stat popups + prev-value
  // baselines so the new run starts with clean tracking.
  _resetStatPopups();
  // BETA 17.07 — PSBOT cycle-30w Protocol B: reset Karen Drone diagnostic
  // counter at run start. Will be surfaced in livePlayTracker + bot CSV
  // so PSBOT can see contact / cooldown-skip / radius-skip / los-skip
  // distribution per run before we attempt a fix.
  resetKarenDroneTrace();
  // BETA 17.08 — PSBOT cycle-30t Tier C remaining counters reset:
  // Anus Rocket fire count + Silly Juice peak stack. State-field resets
  // for voogoo/timmy/lastResort/dracsGasMask are in the run-init block
  // (~line 1635) alongside the existing _debt/_notXanax/_lastStand resets.
  _resetAnusRocketTrace();
  _resetSillyJuicePeakStack();
  // BETA 16.23 — Reset the GAME bgm chain (volume + filter) at run start
  // so any leftover duck/muffle from the previous run can't bleed into
  // the new one. Belt-and-suspenders alongside the setBgmContext
  // ('lobby') reset on canvas unmount — this path covers REPLAY (which
  // doesn't fire a context switch). Lobby chain is independent and
  // untouched.
  resetGameBgmChain();
  // BETA 15.92 — Reset low-HP duck latch + force-restore BGM volume in case
  // the previous run died while low-HP-ducked (we never got to fire the
  // exit transition). endSustainedDuck() is a no-op if BGM was already at
  // intended volume, so it's safe to call unconditionally.
  _lowHpDucked = false;
  endSustainedDuck();
  setBgmPlaybackRate(1.0, 0.2); // BETA — restore music speed in case we died while slowed
  // BETA 15.93 — Stop any in-progress death eulogy + clear cached star
  // field / trash talk lines so the next death screen gets fresh
  // selections. Safe to call unconditionally — both are idempotent.
  stopDeathEulogy();
  resetDeathScreenState();
  // BETA 13.99 — reset run-scoped flags + minion state
  _sheeeshRevivesUsed = 0;
  _heartThrobReadyAt = 0;        // BETA 22.90 — N10 Heart Throb cooldown reset
  // BETA 15.26 — character passive state reset (Sponge stacks, Suppression
  // timer, Silly Juice character stacks, Wizard Surge state, Cowboy Quick
  // Draw flag, all signature trackers). Without this, run 2 inherits run 1's
  // peak stacks / kill counters / etc.
  resetCharacterPassiveRunState();
  _shitTrailTimer = 0;
  _bungulators = [];
  // BETA 14.78 — Iranian Bird is now an array of birds (stacks + projCount → N birds)
  _iranianBirds = [];
  _iranianBirdBullets = [];
  _whoopieDropTimer = 0;
  _bananaDropTimer = 0;
  // BETA 16.71 — clear per-run input-mix totals so each run starts at 0s.
  resetInputTracker();
  // BETA 14.04 — reset run-scoped flags + accumulators
  _lastFrameTotalKills = 0;
  // Load player sprite — bounded await (BETA 22.74): on timeout we start
  // anyway and backfill when it lands; the render null-guards playerSprite.
  // A hard rejection still propagates → GameCanvas's ERROR screen.
  playerSprite = null;
  {
    const spriteLoad = loadImage(character.url);
    const winner = await Promise.race([
      spriteLoad,
      new Promise<null>(r => setTimeout(() => r(null), IMG_FETCH_TIMEOUT_MS)),
    ]);
    if (winner) {
      playerSprite = winner;
    } else {
      console.warn('[initGame] player sprite slow — starting without it, will backfill:', character.url);
      spriteLoad.then(img => { playerSprite = img; }).catch(() => {});
    }
  }

  // BETA 22.85 — preload this character's directional walk frames (if any) into
  // the module cache; the render null-guards missing frames, so a slow load
  // just shows the static sprite until they arrive.
  _playerWalkFrames.clear();
  _playerWalkAngle = Math.PI / 2; _playerWalkMoving = false; _playerWalkPhase = 0;
  if (hasWalkSet(character.id)) {
    for (const _wu of listWalkFrameUrls(character.id)) {
      loadImage(_wu).then(img => { _playerWalkFrames.set(_wu, img); }).catch(() => {});
    }
  }

  // Generate room-based map
  // 23.57 — __setMapSeed(n) pins the WORLD (map/trails/forest/places) for
  // reproducible debugging + world A/B tests (the 20.92 run seed only pins
  // balance rolls). null = fresh world every run, as always.
  const mapSeed = opts?.seed ?? _forcedMapSeed ?? (Date.now() % 1000000);
  const roomMap = generateRoomMap(mapSeed, 1);
  clearChunkCache();   // BETA 22.96 — fresh seeded ground per run
  resetSurgeCracks(mapSeed);   // G8 — fresh dynamic-crack store per run
  // BETA 23.08 — P2 POI dressing: ground bakes wear around machines/shrines
  setGroundPois([
    ...roomMap.chests.filter(c => c.type !== 'elite').map(c => ({ x: c.x, y: c.y, kind: 'machine' as const })),
    ...roomMap.shrines.filter(s => s.type !== 'boss').map(s => ({ x: s.x, y: s.y, kind: 'shrine' as const })),
  ]);
  // BETA 23.13 — props keep clear of the spawn area (POIs handled inside)
  setPropExclusions([{ x: roomMap.spawnPoint.x, y: roomMap.spawnPoint.y, r: 380 }]);
  // 23.41 G1 — forest clearings around the run-critical points
  setForestAnchors([
    { x: roomMap.spawnPoint.x, y: roomMap.spawnPoint.y },
    { x: roomMap.bossRoom.x + ROOM_SIZE / 2, y: roomMap.bossRoom.y + ROOM_SIZE / 2 },
  ]);
  // 23.56 fix-pack — the SAME points also get spur TRAILS to the network
  // (the portal used to have a clearing but no road: walled-off descend).
  setSpurAnchors([
    { x: roomMap.spawnPoint.x, y: roomMap.spawnPoint.y },
    { x: roomMap.bossRoom.x + ROOM_SIZE / 2, y: roomMap.bossRoom.y + ROOM_SIZE / 2 },
  ]);
  MAPDEV.on = mapDevRequested();   // BETA 23.00 — ?mapdev=1 freezes gameplay for map inspection

  // BETA 23.35 — DEV TELEPORT (Dylan: "use code to take you to whatever
  // sprite you want in game so you don't have to hunt"). From the console:
  //   __devGoto('graveyard') | __devGoto('town_square') | __devGoto('park')
  //   __devGoto('police_car_lightbar') | __devGoto('statue_surge') | any prop id
  //   __devGoto('machine') | __devGoto('shrine')
  // Moves the map-dev camera when it's on, otherwise teleports the player.
  try {
    (window as unknown as Record<string, unknown>).__devGoto = (what: string | number, yCoord?: number) => {
      const st = _devStateRef;
      if (!st) return 'no game state yet';
      // 23.52 — COORDINATE TELEPORT: __devGoto(x, y) jumps straight there
      // (mapdev camera, else the player). Numbers used to fall through into
      // the prop-id ring scan and burn ~21s scanning the world for a prop
      // named "-14000" — same trap as a typo'd id (capped below too).
      if (typeof what === 'number') {
        if (typeof yCoord !== 'number') return 'usage: __devGoto(x, y) or __devGoto("prop_id")';
        if (MAPDEV.on) { MAPDEV.cam.x = what; MAPDEV.cam.y = yCoord; }
        else { st.player.x = what; st.player.y = yCoord; }
        return `-> ${Math.round(what)},${Math.round(yCoord)}`;
      }
      const ox = MAPDEV.on ? MAPDEV.cam.x : st.player.x;
      const oy = MAPDEV.on ? MAPDEV.cam.y : st.player.y;
      const sd = st.map.roomMap.seed ?? 1;
      let best: { x: number; y: number } | null = null;
      let bestD = Infinity;
      const consider = (x: number, y: number) => {
        const d = Math.hypot(x - ox, y - oy);
        if (d > 4 && d < bestD) { bestD = d; best = { x, y }; }
      };
      if (what === 'park' || what === 'graveyard' || what === 'town_square') {
        for (const site of getParkSites(sd)) if (site.kind === what) consider(site.x, site.y);
      } else if (['camp','junkyard','crash_site','grave_patch','nest','arena','checkpoint','neighborhood'].includes(what)) {
        // 23.60 — teleport to the nearest PLACE of a kind (photo runs, tests)
        for (const pl of getPlaces(sd)) if (pl.kind === what) consider(pl.x, pl.y);
      } else if (what === 'machine') {
        for (const m of st.map.roomMap.chests) if (m.state !== 'gone') consider(m.x, m.y);
      } else if (what === 'shrine') {
        for (const sh of st.map.roomMap.shrines) consider(sh.x, sh.y);
      } else {
        // 23.52 — ring cap 48→16 (≈±20k wu): a typo'd prop id used to scan
        // ~25,000 chunk prop-placements before giving up (~21s main-thread hang)
        for (let ring = 0; ring < 16 && !best; ring += 4) {
          const span = (ring + 4) * 1024;
          for (const pr of propsInRect(ox - span, oy - span, ox + span, oy + span, sd)) {
            if (pr.def.id === what) consider(pr.x, pr.y);
          }
        }
      }
      if (!best) return `no '${what}' found nearby`;
      const t = best as { x: number; y: number };
      if (MAPDEV.on) { MAPDEV.cam.x = t.x; MAPDEV.cam.y = t.y; }
      else { st.player.x = t.x + 90; st.player.y = t.y + 60; }
      return `-> ${what} @ ${Math.round(t.x)},${Math.round(t.y)} (${Math.round(bestD)}wu away)`;
    };
    (window as unknown as Record<string, unknown>).__devForest = () => {
      MAPDEV.showForestDebug = !MAPDEV.showForestDebug;
      return 'forest debug ' + (MAPDEV.showForestDebug ? 'ON' : 'OFF');
    };
    // 23.60 — list all places with coords (photo runs, tests)
    (window as unknown as Record<string, unknown>).__devPlaces = () => {
      const st = _devStateRef;
      if (!st) return 'no game state yet';
      return getPlaces(st.map.roomMap.seed ?? 1).map(p => ({ kind: p.kind, x: Math.round(p.x), y: Math.round(p.y), rx: Math.round(p.rx) }));
    };
    (window as unknown as Record<string, unknown>).__devCorruption = () => {
      MAPDEV.showCorruption = !MAPDEV.showCorruption;
      return 'corruption debug ' + (MAPDEV.showCorruption ? 'ON' : 'OFF');
    };
    // G8 — dynamic-crack console probes (Bram's spawn tick uses the same
    // module API directly; these are the test-by-command handles):
    //   __devCrack()        → crack at the player's feet (gated by canCrackAt)
    //   __devCrack(x, y)    → crack at a point
    //   __devCracksNear(r?) → queryCracksNear around the player (default 600wu)
    (window as unknown as Record<string, unknown>).__devCrack = (cx3?: number, cy3?: number) => {
      const st = _devStateRef;
      if (!st) return 'no game state yet';
      const x = typeof cx3 === 'number' ? cx3 : st.player.x;
      const y = typeof cy3 === 'number' ? cy3 : st.player.y;
      if (!canCrackAt(x, y)) return `canCrackAt(${Math.round(x)},${Math.round(y)}) = false — ground won't open here`;
      const c = createSurgeCrack(x, y);
      return { id: c.id, x: Math.round(c.x), y: Math.round(c.y), radius: Math.round(c.radius), source: c.source, dynamicCount: dynamicCrackCount() };
    };
    (window as unknown as Record<string, unknown>).__devCracksNear = (r = 600) => {
      const st = _devStateRef;
      if (!st) return 'no game state yet';
      return queryCracksNear(st.player.x, st.player.y, r, { max: 12 })
        .map(c => ({ id: c.id, x: Math.round(c.x), y: Math.round(c.y), radius: Math.round(c.radius), source: c.source, dist: Math.round(Math.hypot(c.x - st.player.x, c.y - st.player.y)) }));
    };
    // G2 — biome console probe: __devBiome() at the player, __devBiome(x,y)
    // at a point. Returns the hard id + blend weights + the two live dials.
    (window as unknown as Record<string, unknown>).__devBiome = (bx?: number, by?: number) => {
      const st = _devStateRef;
      if (!st) return 'no game state yet';
      const x = typeof bx === 'number' ? bx : st.player.x;
      const y = typeof by === 'number' ? by : st.player.y;
      const s = st.map.roomMap.seed ?? 1;
      return {
        biome: biomeAt(x, y, s),
        weights: biomeWeightsAt(x, y, s).map(w => ({ id: w.id, w: Math.round(w.w * 100) / 100 })),
        carveMul: Math.round(biomeCarveMulAt(x, y, s) * 100) / 100,
        lampSpacingMul: Math.round(biomeLampSpacingMulAt(x, y, s) * 100) / 100,
      };
    };
    (window as unknown as Record<string, unknown>).__setMapSeed = (n?: number) => {
      _forcedMapSeed = typeof n === 'number' ? (n >>> 0) : null;
      return _forcedMapSeed === null ? 'map seed UNPINNED (fresh world per run)' : `map seed PINNED to ${_forcedMapSeed} (takes effect on next run start)`;
    };
    // 23.57-debug — probe the clearance field from the console
    (window as unknown as Record<string, unknown>).__devClearance = (cx2: number, cy2: number) => {
      const st = _devStateRef;
      if (!st) return 'no game state yet';
      return Math.round(forestClearance(cx2, cy2, st.map.roomMap.seed ?? 1) * 10) / 10;
    };
    // 23.56 fix-pack — drive the player through the REAL movement/collision
    // path from the console: __devWalk(1, 0, 3) walks east for 3 seconds.
    (window as unknown as Record<string, unknown>).__devWalk = (wdx: number, wdy: number, sec = 2) => {
      const st = _devStateRef;
      if (!st) return 'no game state yet';
      const l = Math.hypot(wdx, wdy) || 1;
      _devWalkDx = wdx / l; _devWalkDy = wdy / l;
      _devWalkUntil = (st.time ?? 0) + Math.max(0.1, Math.min(30, sec));
      return `walking (${_devWalkDx.toFixed(2)},${_devWalkDy.toFixed(2)}) for ${sec}s`;
    };
    // 23.56 fix-pack (docs 07+08) — procedural world validation: BFS the
    // walkable field from spawn, confirm every POI is reachable, count
    // walkable-but-unreachable islands (snag pockets) + tight squeezes.
    (window as unknown as Record<string, unknown>).__validateWorld = (radiusWu = 9000, stepWu = 64) => {
      const st = _devStateRef;
      if (!st) return 'no game state yet';
      const t0 = performance.now();
      const sd = st.map.roomMap.seed ?? 1;
      const sp = st.map.roomMap.spawnPoint;
      const R = Math.max(2000, Math.min(16000, radiusWu));
      const step = Math.max(32, Math.min(128, stepWu));
      const N = Math.floor((R * 2) / step);
      const x0 = sp.x - R, y0 = sp.y - R;
      const clr = new Float32Array(N * N);
      const walk = new Uint8Array(N * N);
      for (let gy = 0; gy < N; gy++) {
        for (let gx = 0; gx < N; gx++) {
          const c = forestClearance(x0 + gx * step, y0 + gy * step, sd);
          clr[gy * N + gx] = c;
          if (c > -16) walk[gy * N + gx] = 1;   // the movement law (isForestWalkBlockedAt)
        }
      }
      const reach = new Uint8Array(N * N);
      const q: number[] = [];
      const sgx = Math.round((sp.x - x0) / step), sgy = Math.round((sp.y - y0) / step);
      if (walk[sgy * N + sgx]) { reach[sgy * N + sgx] = 1; q.push(sgy * N + sgx); }
      while (q.length) {
        const i = q.pop() as number;
        const gx = i % N, gy = (i / N) | 0;
        for (let oy = -1; oy <= 1; oy++) for (let ox = -1; ox <= 1; ox++) {
          if (!ox && !oy) continue;
          const nx = gx + ox, ny = gy + oy;
          if (nx < 0 || ny < 0 || nx >= N || ny >= N) continue;
          const ni = ny * N + nx;
          if (walk[ni] && !reach[ni]) { reach[ni] = 1; q.push(ni); }
        }
      }
      // 23.57 — BORDER-AWARE verdicts: a walkable component that TOUCHES the
      // scan border may connect to spawn via routes OUTSIDE the box (trails
      // wander far). Those are 'offscan' (unknown-but-probably-fine), NOT
      // unreachable. Only border-free unreached components are true islands.
      const open = new Uint8Array(N * N);
      {
        const comp = new Int32Array(N * N);   // 0 = unlabeled
        let nextComp = 0;
        for (let i0 = 0; i0 < N * N; i0++) {
          if (!walk[i0] || reach[i0] || comp[i0]) continue;
          nextComp++;
          let touchesBorder = false;
          const cells: number[] = [i0];
          comp[i0] = nextComp;
          const stack = [i0];
          while (stack.length) {
            const i = stack.pop() as number;
            const gx = i % N, gy = (i / N) | 0;
            if (gx === 0 || gy === 0 || gx === N - 1 || gy === N - 1) touchesBorder = true;
            for (let oy = -1; oy <= 1; oy++) for (let ox = -1; ox <= 1; ox++) {
              if (!ox && !oy) continue;
              const nx = gx + ox, ny = gy + oy;
              if (nx < 0 || ny < 0 || nx >= N || ny >= N) continue;
              const ni = ny * N + nx;
              if (walk[ni] && !reach[ni] && !comp[ni]) { comp[ni] = nextComp; cells.push(ni); stack.push(ni); }
            }
          }
          if (touchesBorder) for (const c of cells) open[c] = 1;
        }
      }
      const probe = (wx: number, wy: number, radWu: number): 'reachable' | 'offscan' | 'isolated' => {
        const gx = Math.round((wx - x0) / step), gy = Math.round((wy - y0) / step);
        const rad = Math.ceil(radWu / step);
        let sawOpen = false;
        for (let oy = -rad; oy <= rad; oy++) for (let ox = -rad; ox <= rad; ox++) {
          const nx = gx + ox, ny = gy + oy;
          if (nx < 0 || ny < 0 || nx >= N || ny >= N) continue;
          if (reach[ny * N + nx]) return 'reachable';
          if (open[ny * N + nx]) sawOpen = true;
        }
        return sawOpen ? 'offscan' : 'isolated';
      };
      const inReach = (wx: number, wy: number, radWu: number): boolean => {
        const gx = Math.round((wx - x0) / step), gy = Math.round((wy - y0) / step);
        const rad = Math.ceil(radWu / step);
        for (let oy = -rad; oy <= rad; oy++) for (let ox = -rad; ox <= rad; ox++) {
          const nx = gx + ox, ny = gy + oy;
          if (nx >= 0 && ny >= 0 && nx < N && ny < N && reach[ny * N + nx]) return true;
        }
        return false;
      };
      const pois = getGroundPois()
        .filter(p => Math.abs(p.x - sp.x) < R - 300 && Math.abs(p.y - sp.y) < R - 300)
        .map(p => ({ x: Math.round(p.x), y: Math.round(p.y), kind: p.kind, verdict: probe(p.x, p.y, 280) }));
      const portalOk = st.portal ? probe(st.portal.x, st.portal.y, 300) !== 'isolated' : null;
      let walkable = 0, reachable = 0, islandCells = 0, offscanCells = 0, tight = 0;
      const islandSamples: Array<{ x: number; y: number }> = [];
      const tightSamples: Array<{ x: number; y: number }> = [];
      for (let i = 0; i < N * N; i++) {
        if (!walk[i]) continue;
        walkable++;
        if (reach[i]) {
          reachable++;
          if (clr[i] > -16 && clr[i] < 8 && tight < 1e9) {
            tight++;
            if (tightSamples.length < 5 && tight % 23 === 1) tightSamples.push({ x: Math.round(x0 + (i % N) * step), y: Math.round(y0 + ((i / N) | 0) * step) });
          }
        } else if (open[i]) {
          offscanCells++;   // border-touching — likely connects outside the scan
        } else {
          islandCells++;
          if (islandSamples.length < 5 && islandCells % 37 === 1) islandSamples.push({ x: Math.round(x0 + (i % N) * step), y: Math.round(y0 + ((i / N) | 0) * step) });
        }
      }
      const badPois = pois.filter(p => p.verdict === 'isolated');
      // 23.57 — places must be reachable too (intentional-world pack doc 09)
      const places = getPlaces(sd)
        .filter(p => Math.abs(p.x - sp.x) < R - 300 && Math.abs(p.y - sp.y) < R - 300)
        .map(p => ({ x: Math.round(p.x), y: Math.round(p.y), kind: p.kind, verdict: probe(p.x, p.y, 320) }));
      const badPlaces = places.filter(p => p.verdict === 'isolated');
      return {
        ok: badPois.length === 0 && portalOk !== false && badPlaces.length === 0,
        seed: sd,
        places: { total: places.length, unreachable: badPlaces },
        ms: Math.round(performance.now() - t0),
        scannedWu: R * 2, stepWu: step,
        walkablePct: Math.round((walkable / (N * N)) * 100),
        reachablePctOfWalkable: walkable ? Math.round((reachable / walkable) * 100) : 0,
        pois: { total: pois.length, unreachable: badPois },
        portalReachable: portalOk,
        offscanCells,
        snagIslands: { cells: islandCells, samples: islandSamples },
        tightSqueezes: { cells: tight, samples: tightSamples },
      };
    };
    (window as unknown as Record<string, unknown>).__devZoom = (z: number) => {
      MAPDEV.cam.zoom = Math.max(0.03, Math.min(2.5, z));
      return 'zoom ' + MAPDEV.cam.zoom;
    };
    (window as unknown as Record<string, unknown>).__devPos = () => {
      const st = _devStateRef;
      if (!st) return 'none';
      const fo = (st.player.spriteSize || 40) * 0.6;
      return JSON.stringify({
        pos: Math.round(st.player.x) + ',' + Math.round(st.player.y),
        phase: st.phase, mapdev: MAPDEV.on,
        keys: Array.from(st.keys as Set<string>).join('+') || '-',
        clr: Math.round(forestClearance(st.player.x, st.player.y + fo, st.map.roomMap.seed ?? 1)),
      });
    };
  } catch { /* non-browser */ }
  // BETA 13.25: corner/pinch triangle wiring removed — green-zone redirect now handles
  // all room-corner steering via classifyPoint + getGreenZoneRedirect.

  // Preload ALL images at game start — map is small enough (10000wu) to load everything upfront.
  // This eliminates room flickering/pop-in entirely.
  // BETA 13.92 FPS: extended to include item + perc icons. User's profile showed
  // 32.4% of frame was Image decode — the big PNGs (item icons at 5MB each)
  // were decoding mid-gameplay when chests opened. Now decoded at load time.
  const allUrls: string[] = [
    ...chunkAssetUrls(),   // BETA 22.96 — boundless ground + decals
    ...propAssetUrls(),    // BETA 23.13 — world props
    ...roomMap.floorTileUrls,
    ...Object.values(BORDER_WALL_ASSETS), // all 8 border wall tile PNGs
    OUTSIDE_TEXTURE_URLS[1], // outside texture for level 1
    ...roomMap.rooms.map(r => r.cdnUrl), // ALL room PNGs
    ...Object.values(SHRINE_ASSETS),       // ALL buff shrine sprites
    ...BUNCHA_BULLSHIIII.map(item => item.iconUrl), // ALL Buncha Bullshiiii item icons (26 PNGs)
    ...PEANUT_PERCS.map(perc => perc.iconUrl),       // ALL Peanut Perc icons (14 PNGs)
  ];
  // Deduplicate
  const uniqueUrls = Array.from(new Set(allUrls.filter(Boolean)));
  // Await all images — BETA 22.74: bounded. A single stalled request used to
  // leave this await pending forever (the first-run ENTERING-THE-ZONE hang).
  // Now: a 5s breadcrumb names exactly which URLs are still pending (the
  // next-real-repro instrumentation), and an 8s watchdog starts the game
  // anyway — getCachedImage null-guards + re-kicks its own loads, so missing
  // art pops in late instead of dead-ending the run.
  {
    const pending = new Set(uniqueUrls);
    const allDone = Promise.allSettled(
      uniqueUrls.map(url => loadImage(url).finally(() => pending.delete(url))),
    );
    const warnTimer = setTimeout(() => {
      if (pending.size > 0) {
        console.warn(`[initGame] preload slow — still waiting on ${pending.size}:`, Array.from(pending));
      }
    }, 5000);
    await Promise.race([
      allDone,
      new Promise<void>(r => setTimeout(r, IMG_FETCH_TIMEOUT_MS)),
    ]);
    clearTimeout(warnTimer);
    if (pending.size > 0) {
      console.warn(`[initGame] preload watchdog — starting anyway, ${pending.size} pending:`, Array.from(pending));
    }
  }

  // BETA 23.13 — seed prop aspect ratios from the preloaded images so the
  // footprint collider is live even for props that haven't rendered yet
  // (collision without aspect is skipped — see worldProps.propFootprintAt).
  for (const _pu of propAssetUrls()) {
    const _pimg = getCachedImage(_pu) as HTMLImageElement | null;
    if (_pimg && _pimg.naturalWidth > 0) notePropAspect(_pu, _pimg.naturalWidth / _pimg.naturalHeight);
  }

  // Build initial state
  // BETA 23.62 — CO-OP PHASE 1: the local player is created as roster slot
  // 'p1' in state.players; state.player is a LIVE accessor to that same
  // object (never a copy — the 22.02 knockback bug taught us copies eat
  // writes). Single-player = a roster of one.
  const localPlayer: Player = {
    id: 'p1',
    downed: false,
    reviveProgress: 0,
    x: roomMap.spawnPoint.x,
    y: roomMap.spawnPoint.y,
    speed: 100,
    // BETA 14.71 — BUG-2 from cycle-8 audit: hitbox was 30-50% smaller than
    // sprite, creating "visible touch but no damage" gap (28-39 px ghost
    // zone with zombie hordes). User: "if their bot sprite touched my
    // bot its a hit. enemy sprite hit/attack box is the exact shape of
    // the sprite". Player size now matches sprite half (32 = 64/2).
    // Combined with enemyAdapter.ts:70 fix (p.size instead of p.size * 0.7),
    // player hitbox = 32 px = full visible sprite radius.
    size: 32,
    spriteSize: 64,
    goopCount: 0,
    currentRoom: null,
    bobPhase: 0,
    facing: 1,
    hp: 100,                  // BETA 14.97 — user direction: base Max HP = 100. Reverts BETA 14.73 cycle-9 (150). Now paired with new enemy damage values (zombie 25 / timmy 40 / heavy 60).
    maxHp: 100,               // BETA 14.97 — user direction: base Max HP = 100. See above.
    xp: 0,
    level: 1,
    xpToNext: 100,
    invincibleTimer: 0,
    gold: 0,
    // BETA 21.11 — rechargeable dash: start a run with both charges full.
    dashCharges: 2,
    dashRechargeTimer: 0,
    dashActiveTimer: 0,
    dashHeldLastFrame: false,
    dashDirX: 0,
    dashDirY: 0,
    luck: 0,
    critChance: 1,            // BETA 14.97 — user direction: base Crit Chance = 1%. Was 5.
    critDamage: 1,            // BETA 14.97 — user direction: base Crit Damage = 2.0x → +1.0× multiplier on crit. Was 0.
    lifesteal: 0,
    shield: 0,
    maxShield: 0,
    // BETA 16.43 — base shield regen 2.0/sec (was 1.0 in 16.36). Per
    // Dylan "balance it" — shield should feel like a recurring buffer.
    shieldRegen: 2.0,
    armor: 0,
    evasion: 1,               // BETA 14.97 — user direction: base Evasion = 1%. Was 0.
    knockbackBonus: 0,
    projectileCountBonus: 0,
    rangeBonus: 0,
    pickupRange: 0,
    xpMultiplier: 1.0,
    goldMultiplier: 1.0,
    hpRegen: 10,              // BETA 14.97 — user direction: base HP Regen = 10/sec. Was 0.
    damageBonus: 0,
    attackSpeedBonus: 0,
    thorns: 0,
    // BETA 13.77 — newly exposed bonuses (zero-init; populated each frame by applyAllBonusesToPlayer).
    eliteDamageBonus: 0,
    projectileSpeedBonus: 0,
    attackAreaBonus: 0,
    // BETA 13.82 — wires the `+1 Projectile Bounce` shrine that was a silent no-op pre-13.82.
    projectileBounceBonus: 0,
  };

  const state: GameState = {
    phase: 'playing',
    players: { p1: localPlayer },
    localPlayerId: 'p1',
    hostPlayerId: 'p1',
    // BETA 23.62 — CO-OP PHASE 1 compat accessor. MUST stay a getter/setter
    // pair aliasing the live roster entry: 132 call sites across 5 files
    // read AND write through state.player, and every one keeps working
    // because this returns the SAME object as players[localPlayerId].
    get player(): Player { return (this as GameState).players[(this as GameState).localPlayerId]!; },
    set player(v: Player) { (this as GameState).players[(this as GameState).localPlayerId] = v; },
    // BETA 16.33 — Dylan: "zoom in 15 percent on FOV." 1.2 → 1.38.
    // BETA 16.70 — Per Dylan (iPhone): "look how zoomed in it is." Phones
    // have small viewports and the player can't see far enough into the
    // world. Zoom out (1.38 → 1.0) on touch devices so more of the
    // map / approaching enemies are visible. Desktop unchanged.
    camera: {
      x: roomMap.spawnPoint.x,
      y: roomMap.spawnPoint.y,
      // BETA 18.4 — Dylan (ROG Ally X): "i feel like i am zoomed too far
      // out and UI is a bit small on xbox rog ally x." Handheld gaming
      // devices (Steam Deck, ROG Ally, Switch) have small physical screens
      // but are CONTROLLER-driven (not finger-touch). They want MORE zoom
      // so the game feels appropriately sized for a handheld view, not
      // less zoom like a phone-touch device. Default 1.7× for handhelds —
      // tighter than desktop 1.38× and much tighter than touch 1.0×.
      zoom: isHandheldGamingDevice() ? 1.7 : isTouchDevice() ? 1.0 : 1.38,
    },
    // BETA 20.74 — start every run with the cinematic intro glide (wide map
    // reveal → ease into the character). See the camera block in updateGame.
    _introZoomT: 0,
    keys: new Set(),
    mouse: { x: 0, y: 0, worldX: 0, worldY: 0, down: false },
    debug: false,
    startTime: Date.now(),
    loadProgress: 0,
    interactTarget: null,
    interactPromptAlpha: 0,
    interactPromptText: '',
    extractionPort: null,
    extractionArmedAt: null,
    extracted: false,
    showMinimap: true,
    hudHidden: false,  // BETA 16.82 — Y button toggles chat + left stats panel
    time: 0,
    particles: [],
    goopBoxes: [],
    portal: null,
    map: {
      roomMap,
      seed: mapSeed,
      level: 1,
      WORLD_SIZE,
      ROOM_SIZE,
    },
    // BETA 13.29: corridorTriangles field removed from GameState.
    wave: 0,
    waveTimer: 0,
    killCount: 0,
    chestsOpenedThisRun: 0,
    shrinesActivatedThisRun: 0,  // BETA 16.90 — PSBOT cycle-30f Bug 6
    rerollsLeft: 3,
    hudScale: 1.0,
    viewportW: 0,
    viewportH: 0,
    screenShakeTimer: 0,
    screenShakeIntensity: 0,
    lastStandAvailable: true,
    lastStandFlashTimer: 0,
    character,
    portalDiscovered: false,
    minimapFogSize: 150,
    minimapFog: new Float32Array(150 * 150),  // all 0.0 = hidden
    minimapRevealRadius: 600,
    lootOrbs: createLootOrbState(),
    pickupNotifications: [],
    runUnlockNotifs: [],
    runUnlocksHistory: [],
    enemies: [],
    pendingEnemyDamage: [],
    ultraChest: null,
    finalBossActive: false,
    finalBossEnemyIds: [],
    gameOverStartTime: 0,
    damageTakenThisRun: 0,
    killedByType: undefined,
    killedByVariant: undefined,
    killedByName: undefined,
    // BETA 14.21 — Per-attack outcome counters reset to 0 each run.
    _enemyAttacksAttemptedThisRun: 0,
    _enemyAttacksHitThisRun: 0,
    _enemyAttacksMissedByEvasion: 0,
    _enemyAttacksMissedByIframe: 0,
    _enemyAttacksMissedByPhase: 0,
    // BETA 14.30 (BUG-05) — 5th outcome counter
    _enemyAttacksMissedByOther: 0,
    // BETA 14.30 Phase 1 — event counters
    _reviveEventsThisRun: 0,
    _healEventsThisRun: 0,
    _dodgeEventsThisRun: 0,
    _phaseEventsThisRun: 0,
    _knockbackEventsThisRun: 0,
    // BETA 14.30 Phase 1 — stat snapshots default 0; populated at finalizeRun()
    finalDamagePct: 0, finalCritChancePct: 0, finalCritDamagePct: 0,
    finalAttackSpeedPct: 0, finalMovementSpeedPct: 0, finalLifestealPct: 0,
    finalEvasionPct: 0, finalPhaseChancePct: 0, finalPickupRangePct: 0,
    finalLockOnRange: 0, finalKnockbackPct: 0, finalEliteDamagePct: 0,
    finalThornsPct: 0, finalXPGainPct: 0, finalGoldGainPct: 0,
    finalMaxHP: 0, finalHPRegen: 0, finalArmor: 0, finalShield: 0,
    finalOverheal: 0, finalProjectileCount: 0, finalProjectileBounce: 0,
    finalProjectilePierce: 0,
    // BETA 14.30 Phase 1 — stat-delta event log + source breakdown
    _statDeltaEventsThisRun: [],
    _finalStatsBySourceThisRun: {},
    // BETA 14.31 Phase 2 — per-perc / per-item / per-shrine trackers
    _percTrackerThisRun: {},
    _itemTrackerThisRun: {},
    _shrineTrackerThisRun: {},
    // BETA 14.32 Phase 3 — floor-effect tracker + per-frame sweeps
    _floorEffectTrackerThisRun: {},
    _playerHpBucketLowFrames: 0,
    _playerHpBucketMidFrames: 0,
    _playerHpBucketHighFrames: 0,
    _playerStationaryFrames: 0,
    _playerDiagonalMoveFrames: 0,
    _noEnemyNearbyFrames: 0,
    _5plusEnemiesIn150wuFrames: 0,
    _playerMSAvgSnapshot: 0,
    _phaseChanceCurrentAvgSnapshot: 0,
    _critChanceCurrentAvgSnapshot: 0,
    _kittenPlacementsCount: 0,
    _kittenActiveAtAnyTimeAvg: 0,
    _kittenAttacksFiredTotal: 0,
    _kittenDmgDealtTotal: 0,
    _kittenKillsTotal: 0,
    _kittenFreezeAppliedFrames: 0,
    _kittenLifetimeAvgMs: 0,
    _kittenDeathRate: 0,
    _kittenPlacementCoordsBucketed: { corner: 0, center: 0, scatter: 0 },
    // BETA 14.33 Phase 4 — weapon × enemy matrix + synergy
    _weaponEnemyMatrixThisRun: {},
    _topWeaponVsZombieThisRun: '',
    _topWeaponVsTimmyThisRun: '',
    _topWeaponVsCaseohThisRun: '',   // 23.39 — stays as an empty PSBOT-compat column (CaseOh purged)
    _topWeaponVsEliteThisRun: '',
    _weakestWeaponOverallThisRun: '',
    _synergyTrackerThisRun: {},
    // BETA 14.34 Phase 5 — event log + death context + near-miss
    _eventLogThisRun: [],
    _nearMissEventsCount: 0,
    _nearMissEventsLog: [],
    _firstBloodTime: 0,
    _firstDamageTakenTime: 0,
    _longestStretchBetweenHitsMs: 0,
    _engineFramePerfThisRun: { spikes: [], avgFrameTimeMs: 0, worstFrameTimeMs: 0 },
  };
  ultraChestPool.reset();
  _chatPrevPhase = 'playing'; // BETA 22.49 — focus-screen transition tracker (live chat removed)
  // BETA 14.55 — reset eventLog kill-delta + near-miss state on new run
  _eventLogPrevKills = 0;
  _nearMissNadirHp = -1;
  _nearMissDipTime = 0;
  // BETA 14.57 — reset deathContext sliding window on new run
  _recentHits = [];
  _lastHpForDeathTracking = -1;
  _deathClarityLogged = false; // BETA 22.48 — one-shot phantom-death console flag

  // Create portal in boss room
  // BETA 16.52 — Per Dylan: "you should be able to unlock the portal at
  // any time in the game." Portal starts ACTIVE from spawn so the player
  // can walk into the boss area and start the final fight whenever they
  // feel ready (instead of waiting for the floor-complete trigger).
  const bossRoom = roomMap.bossRoom;
  state.portal = {
    x: bossRoom.x + ROOM_SIZE / 2,
    y: bossRoom.y + ROOM_SIZE / 2,
    roomId: bossRoom.id,
    active: true,
    pulsePhase: 0,
  };

  // BETA 22.98 — Cloakzy NPC feature deleted (streamer likeness).

  // Set player current room
  state.player.currentRoom = findCurrentRoom(roomMap.rooms, state.player.x, state.player.y);

  // Preload images around spawn
  preloadImagesAround(state, state.player.x, state.player.y);

  // ── Initialize registered subsystems ──
  if (weaponsModule) weaponsModule.init(state);
  if (enemiesModule) enemiesModule.init(state);
  // BETA 23.02 — map-building mode: hand the player 4 SHOOTING weapons so
  // the N/E/S/W orbit + weapon-depth + muzzle work is testable on sight.
  if (MAP_BUILD_NO_ENEMIES && weaponsModule) {
    for (const _wid of ['wpn_squirter', 'wpn_frozen_darts', 'wpn_shitty_cannon', 'wpn_ex_girlfriend']) {
      try { addWeaponById(_wid); } catch { /* dev convenience only */ }
    }
  }
  if (itemsModule) itemsModule.init(state);
  resetItemEffects();
  preloadItemSprites();

  // ── Auto-playtester bot (disabled by default, F9/button to toggle) ──
  initBot({
    enabled: false,
    maxRuns: 0,          // 0 = loop forever until manually disabled
    logLevel: 'summary',
    autoRestart: true,
    kiteAggression: 0.6,
    fleeThreshold: 0.20,
    healSeekThreshold: 0.50,
  });

  // ── Auto-playtester v2 data collector (initialized alongside v1) ──
  initBotV2(state);

  // Expose bot console commands for DevTools
  (window as any).__botExport = exportBotData;
  (window as any).__botCurrentRun = getCurrentRunLog;
  (window as any).__botDownloadCSV = downloadFullCSV;
  // ── Flow Field Pathfinding ──
  initFlowField(roomMap);
  forceFlowFieldRebuild(state.player.x, state.player.y);
  return state;
}

// ─── Update ─────────────────────────────────────────────────────────

// BETA 13.57: trail-enemy reposition (fog-of-war teleport for chasing zombies).
// When 6+ regular zombies are trailing behind the player's movement vector, take
// the farthest-behind one and place it AHEAD of the player, off-screen. Distance
// scales with player speed so they never flicker into view. Player just sees more
// enemies up ahead instead of an infinite chase line. Skips elites + bosses (their
// threat is fixed-location). Skips when player is stationary (so trail catches up
// naturally if camping). Throttled to 1 teleport per frame so it never looks like
// a wave appearing.
function repositionTrailEnemies(state: GameState): void {
  const { player, enemies } = state;
  const pvx = player.vx ?? 0;
  const pvy = player.vy ?? 0;
  const speed = Math.hypot(pvx, pvy);
  if (speed < 50) return; // not moving — let trail catch up

  // Approx safe off-screen distance. Canvas typical viewport ~1200×700 wu @ zoom 1,
  // so anything beyond ~700 wu from the player is reliably off-camera.
  const VIEWPORT_SAFE_RADIUS = 700;
  const speedBuffer = (speed / 200) * 250; // base 200 → +250, at 400 → +500
  const placementDist = VIEWPORT_SAFE_RADIUS + 200 + speedBuffer;

  const ux = pvx / speed;
  const uy = pvy / speed;

  // Find regular zombies trailing in a cone behind player's heading
  const TRAIL_MIN_BEHIND = 600;
  const TRAIL_MAX_BEHIND = 2000;
  const TRAIL_LATERAL_TOL = 800;
  const trailers: { e: typeof enemies[number]; behindDist: number }[] = [];
  for (const e of enemies) {
    if (!e.alive) continue;
    if (e.type !== 'zombie') continue;
    const variant = (e as { variant?: string }).variant;
    if (variant === 'elite' || variant === 'boss') continue;
    const dx = e.x - player.x;
    const dy = e.y - player.y;
    const along = dx * ux + dy * uy;
    const lateralX = dx - along * ux;
    const lateralY = dy - along * uy;
    const lateral = Math.hypot(lateralX, lateralY);
    const behindDist = -along;
    if (behindDist > TRAIL_MIN_BEHIND && behindDist < TRAIL_MAX_BEHIND && lateral < TRAIL_LATERAL_TOL) {
      trailers.push({ e, behindDist });
    }
  }

  if (trailers.length < 6) return;

  // Pick the farthest-behind trailer (least useful where it is)
  trailers.sort((a, b) => b.behindDist - a.behindDist);
  const victim = trailers[0].e;

  // Try up to 6 candidate angles in a ±60° cone around forward
  const baseAngle = Math.atan2(uy, ux);
  const WS = state.map.WORLD_SIZE;
  for (let attempt = 0; attempt < 6; attempt++) {
    const angleSpread = (Math.random() - 0.5) * (Math.PI * 2 / 3);
    const angle = baseAngle + angleSpread;
    const distJitter = placementDist + (Math.random() - 0.5) * 150;
    const targetX = player.x + Math.cos(angle) * distJitter;
    const targetY = player.y + Math.sin(angle) * distJitter;
    if (targetX < 100 || targetX > WS - 100 || targetY < 100 || targetY > WS - 100) continue;
    if (canMoveTo(state, targetX, targetY, victim.size, true)) {
      victim.x = targetX;
      victim.y = targetY;
      noteTrailTeleport(); // BETA 13.59: track invisible behavior in CSV
      return;
    }
  }
  // All angles blocked — silently skip; will retry next frame
}

// BETA 14.63 — VooGoo Rager auto-heal cooldown. 45s cooldown, heals 40% of
// player.maxHp. BETA — the authoritative cooldown now lives on STATE
// (state._voogooHealCd) so the damage path (enemyAdapter clutch-save) and the
// per-frame poll share ONE timer. This module var is only a DISPLAY MIRROR the
// HUD tray reads (it has no `state` in scope).
let _voogooHealCdDisplay = 0;

// BETA 16.25 — PSBOT cycle-26 P0.2 + Dylan: "make sure i actually should
// have died." Track the player's last-known healthy HP each frame so the
// death replay can animate the HP bar visibly draining from that value
// → 0 over the first 1s of slow-mo. Without this, the bar already shows
// 0 by the time the replay starts (because the lethal damage has been
// applied), and Dylan can't SEE the death happen — he just sees the
// zoom + RIP screen with no payoff. Updated only when player.hp > 0
// so it preserves the pre-fatal-hit value across the death frame.
let _prevAliveHp = 100;

// BETA 16.24 — PSBOT cycle-25 P0.7: queued XP for per-frame cap. Overflow
// from a heavy collection event (chick magnet vacuum, big swarm wipe)
// drains into player.xp at XP_PER_FRAME_CAP per frame so multi-level
// cascades trigger one level-up at a time → audio sounds aren't crushed.
const XP_PER_FRAME_CAP = 200;
let _queuedXp = 0;
// BETA 20.27 — Dylan: show gold + XP gains as ONE consolidated popup above
// the player (not scattered per-enemy). Accumulate intake, flush on a short
// cooldown so a chick-magnet vacuum of hundreds of orbs = a single "+N XP".
let _xpGainAccum = 0;
let _goldGainAccum = 0;
let _gainPopupTimer = 0;
// BETA 19.3 — XP drain watchdog. When _queuedXp climbs above XP_STUCK_WARN
// AND fails to drain for XP_STUCK_GRACE_MS, fire a console warning so we
// can diagnose live why XP isn't flowing into player.xp. Complements the
// XP_STUCK anomaly detector (18.19) with a finer-grained signal that
// catches the case where orbs ARE being picked up but the drain logic is
// gated out (wrong phase, drain disabled, etc.).
const XP_STUCK_WARN = 500;
const XP_STUCK_GRACE_MS = 5000;
let _queuedXpStuckSinceMs: number | null = null;
let _queuedXpStuckLastWarnMs = 0;
function _resetQueuedXp(): void {
  _queuedXp = 0;
  _queuedXpStuckSinceMs = null;
  _queuedXpStuckLastWarnMs = 0;
}

// BETA 17.12 — PSBOT cycle-30ae carry-forward: wrapper around handleChestItemSelection
// that spawns a pickupNotification announcing which dimension was rolled (if any).
// Per Dylan cycle-30ae: dimensional items were silently auto-rolling without feedback.
// 17.11 changed card text to "random roll"; 17.12 adds the result popup.
// Full clickable choice UI (player picks which dimension) deferred to 17.13.
function _handleChestPickWithDimensionPopup(state: GameState, index: number): void {
  const result = handleChestItemSelection(index);
  // BETA 21.45 — satisfying confirm burst (gold). Single chokepoint for every
  // chest-pick input path (Enter / number / gamepad-A / mouse).
  pickConfirmCelebration(state, '#ffd700');
  if (result?.rolledDimensionLabel) {
    state.pickupNotifications = state.pickupNotifications || [];
    state.pickupNotifications.push({
      text: `Rolled: ${result.rolledDimensionLabel}`,
      color: '#FFD700', // gold
      timer: 2.5,
    });
  }
}

// BETA 22.91 — enter the WILD CARDS pick screen directly (the old machine
// SPIN SCREEN is deleted — Dylan: "this is still all in the game and pops up
// first... then my wild cards show after"). Every machine interact calls this
// at buy time; the slam-in of renderChestChoiceScreen IS the reveal now.
// Registration mirrors the old 18.13 spin→choice transition block so gamepad
// focus boxes exist the same frame the screen appears.
function _enterChestChoice(state: GameState): void {
  state.phase = 'chest_choice';
  try {
    const W = _lastRenderW;
    const H = _lastRenderH;
    const L = computeChoiceCardLayout(W, H, 3, !!state.hudHidden, 'chest');
    const btnY = L.cardY + L.cardH + 28;
    const _doRerollCh = (fn: () => void): void => {
      if ((state.rerollsLeft ?? 0) <= 0) return;
      state.rerollsLeft = (state.rerollsLeft ?? 0) - 1;
      fn();
      if (!state.pickupNotifications) state.pickupNotifications = [];
      state.pickupNotifications.push({ text: `REROLLED — ${state.rerollsLeft} left`, color: '#6699ff', timer: 1.5 });
    };
    const _pickCh = (i: number) => {
      _handleChestPickWithDimensionPopup(state, i);
      state.phase = 'playing';
      // BETA 18.14 — synchronous cleanup so tickGamepad treats input as
      // in-game the moment a card is picked (see the old transition block).
      unregisterFocusScreen('chest_cards');
      setActiveScreen(null);
    };
    unregisterFocusScreen('chest_cards');
    registerFocusScreen('chest_cards', [
      { id: 'card_0', rect: { x: L.startX, y: L.cardY, w: L.cardW, h: L.cardH }, onActivate: () => _pickCh(0) },
      { id: 'card_1', rect: { x: L.startX + L.cardW + L.gap, y: L.cardY, w: L.cardW, h: L.cardH }, onActivate: () => _pickCh(1) },
      { id: 'card_2', rect: { x: L.startX + 2 * (L.cardW + L.gap), y: L.cardY, w: L.cardW, h: L.cardH }, onActivate: () => _pickCh(2) },
      { id: 'skip',   rect: { x: W / 2 - 200, y: btnY, w: 180, h: 36 }, onActivate: () => skipChestChoice() },
      { id: 'reroll', rect: { x: W / 2 + 20,  y: btnY, w: 180, h: 36 }, onActivate: () => _doRerollCh(rerollChestOptions) },
    ], () => skipChestChoice(), 0);
    setActiveScreen('chest_cards');
    setChestHoverIndex(0);
  } catch { /* never crash the interact path */ }
}

// 23.56 fix-pack — __devWalk virtual input + slide-vector debug capture
let _devWalkDx = 0, _devWalkDy = 0, _devWalkUntil = -1;
let _forcedMapSeed: number | null = null;   // 23.57 — __setMapSeed(n): reproducible worlds
let _slideDbg: { x: number; y: number; sx: number; sy: number; t: number } | null = null;

let _perfUpdateMs = 0;   // BETA 23.06 — DT-A perf overlay
let _perfRenderMs = 0;
let _perfFrameT0 = 0;

export function updateGame(state: GameState, dt: number): GameState {
  _devStateRef = state;   // 23.35 — __devGoto handle
  _perfFrameT0 = performance.now();
  // BETA 15.34 — Update the live-state pointer once per frame so rare event
  // callbacks (perc pick auditor, etc.) can read state.player. Safe: this
  // is a single pointer write, NOT the 14-field state-sync block that
  // caused the 15.24 freeze.
  _liveState = state;

  // BETA 23.00 — P0 DEV MAP VIEWER (?mapdev=1): gameplay frozen, free camera.
  if (MAPDEV.on) {
    mapDevTick(state.keys, dt, {
      playerX: state.player.x, playerY: state.player.y,
      clearChunkCache,
      machines: state.map.roomMap.chests.filter(c => c.state !== 'gone' && c.type !== 'elite'),
      shrines: state.map.roomMap.shrines.filter(s => s.type !== 'boss'),
    });
    state.camera.x = MAPDEV.cam.x;
    state.camera.y = MAPDEV.cam.y;
    state.camera.zoom = MAPDEV.cam.zoom;
    return state;
  }

  // BETA 22.87 — Perk Card exit beat: after a pick, the screen holds for
  // ~450ms (chosen card pulses, losers fade) THEN gameplay resumes and the
  // chosen card flies into the PERCS strip. Runs above the phase gate.
  if (_luExit && state.phase === 'levelup') {
    if (performance.now() - _luExit.start > 450) {
      const snap = _luExit.snaps[_luExit.idx];
      if (snap) _luFly = { art: snap.art, rarity: snap.rarity, x: snap.x, y: snap.y, w: snap.w, h: snap.h, start: performance.now() };
      _luExit = null;
      state.phase = 'playing';
      try { playCardFlySfx(); } catch { /* sfx optional */ }
    }
  } else if (_luExit && state.phase !== 'levelup') {
    _luExit = null;   // defensive: phase changed under us (death, quit)
  }

  // BETA 16.90 — Phase time accumulator (PSBOT cycle-30o flagged
  // phaseTimeLevelup / phaseTimeChest / phaseTimeShrine all reading 0).
  // Was previously at ~line 1976 but the early return at ~line 1501
  // skipped non-playing phases entirely. Now at top of updateGame so it
  // ticks for every phase. Pointer-only writes, no perf concern.
  {
    const _sAnyPhase = state as unknown as Record<string, number | undefined>;
    const _phase = state.phase as string;
    if (_phase === 'playing' || _phase === 'death_replay') {
      _sAnyPhase._phaseTimePlaying = (_sAnyPhase._phaseTimePlaying ?? 0) + dt;
    } else if (_phase === 'levelup') {
      _sAnyPhase._phaseTimeLevelup = (_sAnyPhase._phaseTimeLevelup ?? 0) + dt;
    } else if (_phase === 'chest_choice' || _phase === 'chest_spin') {
      _sAnyPhase._phaseTimeChest = (_sAnyPhase._phaseTimeChest ?? 0) + dt;
    } else if (_phase === 'shrine_choice') {
      _sAnyPhase._phaseTimeShrine = (_sAnyPhase._phaseTimeShrine ?? 0) + dt;
    }
  }

  // BETA 16.58 — Wave 3: poll connected gamepad once per frame BEFORE
  // input is consumed. Translates analog stick / d-pad / buttons into
  // the same state.keys mutations that keyboard + touch already feed,
  // so movement + interact + pause + Q + R all "just work" on a
  // controller without engine-side branching. No-op if no controller.
  tickGamepad(state);
  // BETA 18.17 — Controller anomaly detector tick. Runs every frame,
  // compares input snapshots against current pipeline state to detect
  // PHANTOM_A_BUTTON / DPAD_NO_FOCUS_CHANGE / PAUSE_INPUT_NO_NAV /
  // ACTIVE_SCREEN_LINGERING / PHASE_STUCK_WITH_INPUT. Logs to console
  // with [CTRL-ANOMALY] prefix + dispatches a controller-anomaly
  // CustomEvent + appends to a ring buffer queryable via
  // window.__getControllerAnomalies(). Per Dylan: "how will you see if
  // i click something and then i cant move my character. you should be
  // geting some kind of error log and what it is in real time."
  try {
    const _gpName = getConnectedControllerName();
    tickControllerAnomalies({
      currentPhase: state.phase,
      currentFocusedId: getFocusedTarget()?.id ?? null,
      currentActiveScreen: getActiveScreen(),
      pauseSelectedIndex: state.phase === 'paused' ? getPauseSelectedIndex() : null,
      controllerConnected: _gpName !== null,
      gamepadActivityRecentMs: 99999, // TODO: thread real "ms since last gamepad" if needed
      // BETA 18.19 — XP_STUCK inputs for the Killa-stuck-at-lvl-1 pattern.
      playerLevel: state.player.level,
      playerXp: state.player.xp,
      killCount: state.killCount ?? 0,
    });
  } catch { /* never crash the loop */ }
  // BETA 16.71 — commit this frame's input-source flags into per-source
  // second totals. Cheap O(4) — runs even when no input fired (just
  // clears the flags). Reads dt directly. Only counts gameplay-phase
  // time so menu / pause / level-up overlays don't bloat the totals.
  if (state.phase === 'playing') {
    tickInputTracker(dt);
  }

  // BETA 16.21 — PSBOT cycle-24 P0.2: chat persistence during overlays.
  // Dylan: "When level-up / shrine / chest overlays open, chat in top-left
  // freezes/pauses. Game feels 'dead' because chat stops." Fix: tick chat
  // every frame regardless of game phase so messages keep fading + idle
  // messages keep spawning while the player picks. Uses unscaled dt so
  // chat ticks at real time even during the death_replay slow-mo. The
  // duplicate inner-block call is removed below.
  // BETA 16.33 — keep the slam-impact shake callback pointing at the
  // current state object (lazy ref, no import cycle).
  _engineStateForShake = state;

  // BETA 18.18 — route stats.difficulty + shrine difficulty buffs into the
  // wave system's difficultyMultiplier (Dylan: "increasing difficulty means
  // adding more enemies. btw. not harder enemies"). Spawn rate is the ONLY
  // thing waveState.difficultyMultiplier scales (waveSystem.ts:473).
  // BETA 22.49 — removed the chat-feature hooks (cursed-chat level + AI roast
  // context) that used to sit here; only the difficulty wiring remains.
  {
    const _itemSt = getItemState();

    // BETA 18.18 — DIFFICULTY WIRING FIX. Bug: this per-tick recompute
    // was SETTING ws.difficultyMultiplier from item + shrine sources only,
    // OVERWRITING the floor-advance multiplier (engine.ts ~4591) every
    // single tick. So Dylan reaching floor 2 (expected 2× harder) got
    // wiped back to 1× as soon as the next frame ran. Likewise the
    // wall-clock time scaling (+0.15× per minute, cap +2.5×) only applied
    // at floor advance and got nuked instantly.
    //
    // Fix: compute the FULL multiplier from all four sources every tick
    // and use it as the single source of truth. Removes the parallel
    // `*=` mutation at the floor-advance site (also updated below).
    //   floorBase  = 2^(currentFloor-1)   (floor 1 = 1×, floor 2 = 2×, floor 3 = 4×)
    //   timeBonus  = min(2.5, runMinutes × 0.15)
    //   itemMult   = playerStats.difficulty (cursed_chat = +25%/stack)
    //   shrineMult = 1 + shrineDiffPct/100 (+8% per shrine pick)
    //   total      = floorBase × (1 + timeBonus) × itemMult × shrineMult
    const ws = enemiesModule?.getWaveState?.();
    if (ws && _itemSt) {
      // BETA 20.35 — Dylan: "i need each floor linear." Was 2^(floor-1) EXPONENTIAL
      // (F2=2x, F3=4x, F4=8x) AND 0-indexed currentFloor made F2 actually 1x (an
      // off-by-one). Now LINEAR: F1=1x, F2=2x, F3=3x, F4=4x (currentFloor is
      // 0-indexed, 0 = Floor 1). Enemy HP floor-mult is already linear, so each
      // floor now ramps consistently instead of swarm-flooding on later floors.
      const _floorBase   = 1 + Math.max(0, (ws.currentFloor ?? 0));
      const _runMinutes  = (Date.now() - (state.startTime ?? Date.now())) / 60_000;
      const _timeBonus   = Math.min(2.5, Math.max(0, _runMinutes) * 0.15);
      const _itemMult    = _itemSt.playerStats.difficulty;
      const _shrinePct   = getShrineDifficultyPct();
      const _shrineMult  = 1 + Math.max(0, _shrinePct) / 100;
      const _combined    = _floorBase * (1 + _timeBonus) * _itemMult * _shrineMult;
      _setWaveDifficulty(ws, Math.max(0.1, _combined));
      // BETA 18.18 — push floor + run-minute context so shrine generation
      // can gate negative-trap shrines off Floor 1 / first 2 minutes.
      setShrineGatingContext(ws.currentFloor ?? 1, _runMinutes);
    }
  }

  // BETA 22.49 — chatSystem.update removed (live chat feature deleted).

  // BETA 16.22 — death floor decals + gas damage overlay tick. Both run
  // on real game time regardless of phase so decals continue fading
  // during overlays + the death replay (replay won't slow-mo decals out
  // of a 2-second display window unfairly).
  tickDeathDecals(state.time ?? 0);
  tickGasOverlay(state.time ?? 0);

  // BETA 16.25 — PSBOT cycle-26 P0.2: snapshot last-known healthy HP each
  // frame so the death replay can animate the bar draining from this
  // value → 0 over the first 1s. Only update while alive — preserves
  // the pre-fatal-hit value across the death frame.
  if (state.phase === 'playing' && state.player.hp > 0) {
    _prevAliveHp = state.player.hp;
  }

  // BETA 16.24 — PSBOT cycle-25 P0.7: drain queued XP at XP_PER_FRAME_CAP.
  // Overflow from heavy collections (chick magnet vacuum, swarm wipe)
  // drips in over multiple frames so cascading level-ups don't all fire
  // in a single tick. Only drains during active play (not gameover etc).
  if (_queuedXp > 0 && state.phase === 'playing') {
    const drain = Math.min(_queuedXp, XP_PER_FRAME_CAP);
    state.player.xp += drain;
    _queuedXp -= drain;
    // Drain happened — reset stuck timer.
    _queuedXpStuckSinceMs = null;
  } else if (_queuedXp > XP_STUCK_WARN) {
    // BETA 19.3 — Queue is big but no drain this frame. Track how long
    // we've been stuck and warn periodically so devs see it in console.
    const _now = performance.now();
    if (_queuedXpStuckSinceMs === null) _queuedXpStuckSinceMs = _now;
    if (_now - _queuedXpStuckSinceMs > XP_STUCK_GRACE_MS && _now - _queuedXpStuckLastWarnMs > 10000) {
      console.warn(
        `[XP-STUCK] _queuedXp=${_queuedXp} not draining for ${((_now - _queuedXpStuckSinceMs) / 1000).toFixed(1)}s. ` +
        `phase=${state.phase} (drain requires 'playing'). ` +
        `player.xp=${state.player.xp} player.xpToNext=${state.player.xpToNext} player.level=${state.player.level}`
      );
      _queuedXpStuckLastWarnMs = _now;
    }
  }

  // BETA 16.21 — PSBOT cycle-24 P0.3: death replay tick. While in
  // 'death_replay' phase, run the slow-motion cinematic — scale dt × 0.2,
  // smooth-zoom camera in, snap-center on player, increment timer using
  // REAL (unscaled) dt. After 5s of real time, transition to 'gameover'
  // and fire the existing audio swap + telemetry. The rest of updateGame
  // continues with the slowed dt so enemies, projectiles, particles all
  // play out in slow-motion.
  if (state.phase === 'death_replay') {
    const sAny = state as unknown as {
      _deathReplayElapsedSec?: number;
      _deathReplayInitialZoom?: number;
      _deathReplayTargetZoom?: number;
    };
    const realDt = dt;
    sAny._deathReplayElapsedSec = (sAny._deathReplayElapsedSec ?? 0) + realDt;
    const elapsed = sAny._deathReplayElapsedSec;
    const initialZoom = sAny._deathReplayInitialZoom ?? state.camera.zoom;
    const targetZoom = sAny._deathReplayTargetZoom ?? initialZoom * 1.7;
    // Smooth zoom over first 0.8s
    const zoomT = Math.min(1, elapsed / 0.8);
    const zoomEase = 1 - Math.pow(1 - zoomT, 3); // ease-out cubic
    state.camera.zoom = initialZoom + (targetZoom - initialZoom) * zoomEase;
    // Snap camera onto player so the zoom centers on the corpse
    state.camera.x = state.player.x;
    state.camera.y = state.player.y;
    // Light screen shake to sell the moment (fades out over duration)
    state.screenShakeTimer = 0.15;
    state.screenShakeIntensity = 6 * (1 - elapsed / 5.0);
    // After 5s of real time, transition to gameover (fires eulogy + telemetry).
    if (elapsed >= 5.0) {
      state.phase = 'gameover';
      state.gameOverStartTime = Date.now();
      // BETA 16.23 — Reset the game BGM chain BEFORE the eulogy starts
      // so the eulogy plays unmuffled at full volume (the chain was hard-
      // ducked at depth=0.35 during the replay). Without this the eulogy
      // would inherit the duck for its first ~400ms of play. resetGame
      // BgmChain instantly snaps gain → _gameVolume + filter → 22000 Hz.
      try { resetGameBgmChain(); } catch (e) { console.warn('[gameover] resetGameBgmChain threw', e); }
      try { playPlayerDeath(); } catch (e) { console.warn('[gameover] playPlayerDeath threw', e); }
      try { stopBgm(); } catch (e) { console.warn('[gameover] stopBgm threw', e); }
      try { void playDeathEulogy(); } catch (e) { console.warn('[gameover] playDeathEulogy threw', e); }
      try { captureRunSnapshot(state); } catch (e) { console.warn('[gameover] captureRunSnapshot threw', e); }
      try { finalizeLiveRun(state, 'gameover'); } catch (e) { console.warn('[gameover] finalizeLiveRun threw', e); }
      // EXTRACTION DEATH PENALTY (BETA 22.45) — dying WITHOUT extracting destroys the
      // mods socketed on the guns you brought into the run (the Tarkov risk: a kitted
      // gun "enters your run with its mods at risk"). Extraction (separate path) skips
      // this so you keep your modded guns. Backpack mods stay safe either way.
      // Only a REAL player death loses mods — NEVER bot / replay / lab runs, which read
      // the same persistent stash and would wipe Dylan's real mods.
      const _isRealDeath = !isBotEnabled() && !isLabMode()
        && !(window as Window & { __replaySessionActive?: boolean }).__replaySessionActive;
      if (!state.extracted && _isRealDeath) {
        try { const _lost = loseSocketedModsOnDeath(); if (_lost > 0) console.log(`[extract] death stripped ${_lost} socketed mod(s)`); }
        catch (e) { console.warn('[gameover] mod-strip threw', e); }
      }
      // 🥜 a resigned Peanut comment a beat after the death screen has settled
      console.log('[death_replay] complete — gameover transition fired');
      return state;
    }
    // Slow-motion: scale dt for the rest of this frame's update
    dt = realDt * 0.2;
  }

  // BETA 14.63 — push current run time so other modules (e.g. itemsAdapter
  // shrine-select callback) can timestamp eventLog entries with the actual
  // game-clock second instead of 0.
  setCurrentRunTime(state.time ?? 0);
  // BETA 15.26 — character passive per-frame tick. Sets the module-level
  // character ID first so weaponSystem hooks (Killa fire timestamp, Cowboy
  // Quick Draw, LeBron King's Court bounce mult) can read it. Then ticks
  // continuous Layer 2 effects (Suppression Fire timer, Surge active sec).
  setCurrentCharacterId(state.character?.id ?? '');
  tickCharacterPassiveRunState(state, dt);

  // BETA — Weapon LOOP hums: play continuously while the weapon is equipped AND
  // we're in active combat; stop otherwise (pick screens, gameover, no weapon).
  // NOTE: Chad Aura's loop was REMOVED per Dylan ("aura doesn't need a sound — I
  // just want the enemy grunt on hit"). Only Karen Drones keeps a hum.
  {
    const _wsLoop = getWeaponSystemState();
    const _inCombat = state.phase === 'playing';
    const _hasKaren = _inCombat && (_wsLoop?.weapons.some(w => w.definitionId === 'wpn_karen_drones') ?? false);
    if (_hasKaren) startKarenDronesLoop(); else stopKarenDronesLoop();
  }

  // BETA 19.93 — Signature damage credit (9 chars beyond LeBron). Each
  // tick, compute totalDamageDealt delta since last frame and attribute
  // `delta × signatureContribPct / 100` to the active character's signature
  // counter. PSBOT cycle-30bb ask: get per-character damage-credit math
  // working so analyst can see which signatures actually carry runs.
  // LeBron stays on its own bounce-chain tracker (more precise for chains).
  {
    const _sigAny = state as unknown as { _sigLastTotalDmg?: number };
    const wsForSig = getWeaponSystemState();
    if (wsForSig) {
      const curDmg = wsForSig.totalDamageDealt ?? 0;
      const prevDmg = _sigAny._sigLastTotalDmg ?? curDmg;
      const dDmg = Math.max(0, curDmg - prevDmg);
      if (dDmg > 0) attributeSignatureFrameDamage(dDmg);
      _sigAny._sigLastTotalDmg = curDmg;
    }
  }
  // BETA 14.34 Phase 5 — frame-perf sampler. Captures wall-clock at start,
  // measures total frame time at end, records spikes (>33ms = sub-30fps).
  const _frameStartMs = performance.now();
  // BETA 14.07 — Pause everything while the BarrelCalibrator modal is open.
  // User: "if i am in this calibrator the game should pause". Skip the entire
  // update loop so projectiles, enemies, timers, particles all freeze in place.
  // Calibrator UI is React-rendered with zIndex 9999 (above the canvas pause
  // menu) so it's always visible while paused.
  if (isLabCalibratorOpen()) {
    return state;
  }

  // ── v2 data collector runs FIRST (snapshots choice screens before v1 picks) ──
  updateBotV2(state, dt);
  // ── v1 bot handles movement, choice selection, brain learning ──
  // BETA 13.60: time the bot update so CSV shows how much frame budget the AI eats
  const _botT = performance.now();
  updateBot(state, dt);
  notePerf('bot', performance.now() - _botT);

  // ── Bot simulated key actions: process E (interact) and 1/2/3/Enter (card choices) ──
  // Real keypresses are handled in GameCanvas onKeyDown and immediately consumed.
  // Bot uses simulateKey() which writes to state.keys; we consume those here.
  if (state.keys.has('e') && state.interactTarget) {
    state.keys.delete('e');
    handleInteract(state);
  }

  // BETA 16.99 — Per Dylan: "remove all q items. make them all passives.
  // remove the press Q lingo and the on screen things that live on card."
  // VooGoo Rager was the last Q-press item. Now AUTO-TRIGGERS when HP
  // drops below 50% maxHP and cooldown is ready. Same 45s cooldown,
  // same 40% maxHP heal — just no Q-press required. Passive heal-on-low.
  // (Whoopie + Banana were already auto-drop passives.)
  {
    const voogooSlot = getItemState()?.inventory.find(s => s.item.id === 'item_voogoo_rager');
    if (voogooSlot) {
      const _vsa = state as unknown as Record<string, number | undefined>;
      _vsa._voogooHealCd = Math.max(0, (_vsa._voogooHealCd ?? 0) - dt);
      _voogooHealCdDisplay = _vsa._voogooHealCd ?? 0; // HUD tray mirror (state is authoritative)
      if ((_vsa._voogooHealCd ?? 0) <= 0) trackItemQReady('item_voogoo_rager');
      // Auto-trigger: HP < 50% maxHP + cooldown ready + alive + not full HP.
      // BETA 20.00 — UN-GATED from Debt (Dylan: healing keeps running, Debt is
      // a race). VooGoo's 45s cooldown + 40% maxHp restore is fine help during
      // Debt; without it, gold drains faster.
      // BETA 21.75 — threshold / heal% / cooldown editable via tuneOf (dashboard).
      const _voogooAutoTrigger = state.player.hp > 0
        && state.player.hp < state.player.maxHp * tuneOf('item_voogoo_rager', 'triggerHpThreshold', 0.5)
        && (_vsa._voogooHealCd ?? 0) <= 0;
      if (_voogooAutoTrigger) {
        const healAmt = Math.round(state.player.maxHp * tuneOf('item_voogoo_rager', 'healPctOfMaxHp', 0.4));
        const beforeHp = state.player.hp;
        state.player.hp = Math.min(state.player.maxHp, state.player.hp + healAmt);
        _vsa._voogooHealCd = tuneOf('item_voogoo_rager', 'cooldownSeconds', 45); // editable cooldown
        _voogooHealCdDisplay = _vsa._voogooHealCd ?? 0;
        state._healEventsThisRun = (state._healEventsThisRun ?? 0) + 1;
        // BETA 16.37 — track healing-received total (PSBOT cycle-30i §7E Bug C)
        const _sAnyHeal = state as unknown as Record<string, number | undefined>;
        _sAnyHeal._healingReceivedThisRun = (_sAnyHeal._healingReceivedThisRun ?? 0) + (state.player.hp - beforeHp);
        // BETA 17.07 — PSBOT cycle-30t Tier C counter #4: voogoo telemetry.
        _sAnyHeal._voogooHealsTriggeredThisRun = (_sAnyHeal._voogooHealsTriggeredThisRun ?? 0) + 1;
        _sAnyHeal._voogooHealAmountThisRun     = (_sAnyHeal._voogooHealAmountThisRun ?? 0) + (state.player.hp - beforeHp);
        // BETA 16.38 — flash the tray slot for VooGoo Rager activation
        flashActiveItem('item_voogoo_rager');
        trackItemQPress('item_voogoo_rager');
        trackItemProc('item_voogoo_rager');
        // Synergy: voogooRager_dracsGasMask fires on Q-heal when both equipped.
        // valueDelta = healAmt (extra HP from this Q activation).
        if (getItemState()?.inventory.some(s => s.item.id === 'item_dracs_gas_mask')) {
          trackSynergyTrigger('voogooRager_dracsGasMask', healAmt);
        }
        // BETA 20.69 — POWER moment (VFX audit: "a small green puff... feels
        // like a regular pickup, not a power moment"). Two-layer burst-wave
        // (white-hot core + green halo) + a thump.
        // BETA 20.70 — level-up = a POWER moment: white energy SPARK-rays burst
        // out + green STAR sparkles spin up. (Was two flat puffs.)
        spawnParticles(state.particles, state.player.x, state.player.y, '#ffffff', 24, { shape: 'spark', stretch: 6, speedMin: 120, speedMax: 360, lifeMin: 0.35, lifeMax: 0.6 });
        spawnParticles(state.particles, state.player.x, state.player.y, '#00ff88', 56, { shape: 'star', spin: 7, spinRandomSign: true, speedMin: 40, speedMax: 220, lifeMin: 0.5, lifeMax: 1.1 });
        state.screenShakeTimer = Math.max(state.screenShakeTimer, 0.25);
        state.screenShakeIntensity = Math.max(state.screenShakeIntensity, 10);
        state.pickupNotifications.push({ text: `VOOGOO RAGER! +${healAmt} HP`, color: '#00ff88', timer: 2.0 });
      }
    }
  }
  if (state.phase === 'levelup' || state.phase === 'shrine_choice' ||
      state.phase === 'chest_choice' || state.phase === 'ultra_chest') {
    for (const k of ['1', '2', '3'] as const) {
      if (state.keys.has(k)) {
        state.keys.delete(k);
        if (state.phase === 'levelup') {
          const idx = parseInt(k, 10) - 1;
          const opts = getLevelUpOptionDisplayData();
          if (idx < opts.length) setLevelUpSelectedIndex(idx);
        } else if (state.phase === 'shrine_choice') {
          const idx = parseInt(k, 10) - 1;
          setShrineSelectedIndex(idx);
        } else if (state.phase === 'chest_choice') {
          const idx = parseInt(k, 10) - 1;
          setChestHoverIndex(idx);
        } else if (state.phase === 'ultra_chest') {
          // Bot support: pick ultra chest item by number key.
          // BETA 20.64 — routes through pickUltraChestCard (single source).
          // The old inline collect here left the 'ultra_chest' focus screen
          // REGISTERED after the bot picked (phase flipped to playing but
          // activeScreen stayed) — the 18.14 input-eaten bug class. The
          // shared helper unregisters synchronously. (19.26's re-roll fix
          // lives inside resetUltraChestRoll, which the helper calls.)
          pickUltraChestCard(state, parseInt(k, 10) - 1);
        }
      }
    }
    if (state.keys.has('enter')) {
      state.keys.delete('enter');
      if (state.phase === 'levelup') {
        if (handleLevelUpSelection(getLevelUpSelectedIndex())) pickConfirmCelebration(state, '#4da6ff'); // BETA 21.45 — confirm burst (blue)
      }
      else if (state.phase === 'shrine_choice') {
        const _shR = handleShrineSelection(getShrineSelectedIndex());
        if (_shR) shrinePickCelebration(state); // BETA 20.69
      }
      else if (state.phase === 'chest_choice') {
        const items = getChestOptionDisplayData();
        if (items.length > 0) _handleChestPickWithDimensionPopup(state, getChestHoverIndex());
      }
      // BETA 20.64 — Enter parity for the ultra chest (was missing entirely:
      // Enter did nothing here). Activates the focused card, same as A.
      else if (state.phase === 'ultra_chest') {
        const _ucF = getFocusedTarget();
        if (_ucF && typeof _ucF.onActivate === 'function') _ucF.onActivate();
      }
    }
  }

  // BETA 22.91 — the machine SPIN SCREEN is DELETED (Dylan: "this is still
  // all in the game and pops up first and does its thing and then my wild
  // cards show after"). Machines call _enterChestChoice at interact time;
  // the wild-card slam-in is the only reveal. Defensive: if anything still
  // lands in chest_spin, fall straight through to the card screen.
  if (state.phase === 'chest_spin') {
    clearChestSpin();
    _enterChestChoice(state);
    return state;
  }

  // BETA 17.90 — If a per-build state reset is pending (applyBuild()
  // requested one and the consume block downstream hasn't fired yet),
  // force phase back to 'playing' BEFORE the phase-gated early-return
  // below. Without this hoist, a build ending in 'won' / 'gameover' /
  // 'levelup' caused the early-return at the next line to bail before
  // consumePerBuildStateReset() could run, so all subsequent builds
  // started already stuck in the prior build's terminal phase. 17.89
  // fixed levelup-lock for the consume block but not THIS gate — that's
  // why min-20 LUDICROUS test 1 immediately stuck on 'won' phase after
  // a clear+restart.
  if (isLabMode() && isPerBuildStateResetPending()) {
    state.phase = 'playing';
  }
  // BETA 18.10 — ROOT CAUSE FIX for "d-pad doesn't move outline on
  // level-up / shrine / chest." Dylan: "i see the white box around the
  // first box on a shrine. it did not make that box larger... and i can
  // not tab left or right or anything with d pad or analog stick."
  //
  // The chain: tickGamepad (line 1335) runs every frame BEFORE this
  // early-return + correctly advances _focusedId in gamepadFocus on
  // D-pad input. BUT the hover sync (engine.ts:~3973) that translates
  // _focusedId → setLevelUpHoverIndex (which drives the visible white
  // outline) lives WAY below the early-return — so during levelup/
  // shrine/chest phases the engine bailed at this line before reaching
  // the sync. _focusedId moved internally; the visible outline stayed
  // pinned to the 18.9 default seed on card 0 forever.
  //
  // Fix: run the focus → hover sync HERE, before the early-return.
  // Self-contained, cheap (~5 lines), independent of the larger
  // chatMessages block downstream.
  try {
    const _afsRT = getActiveScreen();
    if (_afsRT === 'levelup_cards' || _afsRT === 'shrine_cards' || _afsRT === 'chest_cards') {
      const _focusedRT = getFocusedTarget();
      if (_focusedRT) {
        // BETA 19.34 — Dylan: "the white box is around the first item
        // choice each time regardless if im hovering with my mouse...
        // for mouse and keyboard players the white box only shows if im
        // hovering over the item". Root cause: this sync ran every frame
        // and OVERWROTE the mouse-set hoverIdx with the default
        // _focusedId=card_0. Now: only sync when _focusedId actually
        // CHANGED since last sync (gamepad d-pad moved focus). Mouse
        // hover keeps full control between gamepad moves.
        const _lastSyncedAny = state as unknown as Record<string, string | undefined>;
        const _syncKey = `_lastFocusSyncId_${_afsRT}`;
        if (_lastSyncedAny[_syncKey] !== _focusedRT.id) {
          _lastSyncedAny[_syncKey] = _focusedRT.id;
          let _idxRT = -1;
          if (_focusedRT.id === 'card_0') _idxRT = 0;
          else if (_focusedRT.id === 'card_1') _idxRT = 1;
          else if (_focusedRT.id === 'card_2') _idxRT = 2;
          if (_idxRT >= 0) {
            if (_afsRT === 'levelup_cards') setLevelUpHoverIndex(_idxRT);
            else if (_afsRT === 'shrine_cards') setShrineHoverIndex(_idxRT);
            else if (_afsRT === 'chest_cards') setChestHoverIndex(_idxRT);
          }
        }
      }
    }
  } catch { /* never crash the loop on hover sync */ }
  // BETA 16.21 — PSBOT cycle-24 P0.3: allow updates to flow during
  // 'death_replay' so enemies, projectiles, particles all keep moving in
  // slow-motion. The lethal damage check (line ~1954) is gated on phase
  // === 'playing' so it won't re-fire.
  if (state.phase !== 'playing' && state.phase !== 'death_replay') return state;
  if (isBalancePanelVisible()) return state; // freeze everything while BALANCING is open

  state.time += dt;

  // ── Extraction port — opens at 120s; arm with X; survive 60s ANYWHERE → EXTRACT ──
  if (state.phase === 'playing') {
    if (!state.extractionPort && state.time >= EXTRACT_APPEAR_SEC && !isBotEnabled()) {
      const _sp = state.map.roomMap.spawnPoint; // guaranteed-open tile (where you spawned)
      state.extractionPort = { x: _sp.x, y: _sp.y, pulsePhase: 0 };
      addForestAnchor({ x: _sp.x, y: _sp.y, r: 620 });   // 23.41 G1 — the port carves its clearing
      addSpurAnchor({ x: _sp.x, y: _sp.y });   // 23.56 — and gets a road to it
      state.pickupNotifications.push({ text: '🛸 EXTRACTION OPEN — reach the port + press [E] to escape with your loot', color: '#48e0a0', timer: 6.0 });
      try { playExtractAppear(); } catch { /* audio not ready */ }
    }
    if (state.extractionPort) state.extractionPort.pulsePhase += dt * 2.4;
    if (state.extractionArmedAt != null) {
      const _remain = EXTRACT_COUNTDOWN_SEC - (state.time - state.extractionArmedAt);
      if (_remain <= 0) {
        // EXTRACTED — success. Scrap / cases / modded-gun mods auto-persist in
        // localStorage, so nothing to save here. Route through the gameover screen
        // but flagged `extracted` so it renders as a WIN (green), not a death.
        state.extracted = true;
        state.phase = 'gameover';
        state.gameOverStartTime = Date.now();
        try { resetGameBgmChain(); } catch { /* */ }
        try { stopBgm(); } catch { /* */ }
        try { playExtractSuccess(); } catch { /* */ }
        try { captureRunSnapshot(state); } catch (e) { console.warn('[extract] captureRunSnapshot threw', e); }
        try { finalizeLiveRun(state, 'extracted'); } catch (e) { console.warn('[extract] finalizeLiveRun threw', e); }
        return state;
      }
    }
  }

  // BETA 21.19 — GOD MODE per-frame escalation (after final-boss kill). LINEAR
  // ramps so they can NEVER overflow (the old exponential endless crashed at
  // ~6.69e+26). HP + damage climb +4%/s (doubles ~every 25s, forever); speed
  // ramps gently and is HARD-CAPPED at 1.6× so enemies stay collidable instead of
  // teleporting through walls. Applied to new spawns only (no per-frame re-scale).
  {
    const _sGodTick = state as unknown as Record<string, number | undefined>;
    if ((_sGodTick._godMode ?? 0) > 0) {
      const _gSec = Math.max(0, state.time - (_sGodTick._godModeStart ?? state.time));
      // BETA 21.46 — Dylan: GOD MODE must KILL you FAST (no infinite farm to LV 64).
      // Was +4%/s HP+dmg — far too weak vs an uncapped late build, so it never
      // killed. Now HP +40%/s (enemies out-tank your clear within ~30s so they
      // survive to REACH you), damage +80%/s (and the anti-one-shot caps are
      // LIFTED in god mode, enemyAdapter, so landed contacts actually drop you).
      // Speed still gently ramped + hard-capped 1.6× (wall-tunnel guard). LINEAR
      // (overflow-safe). Combined: a fully-uncapped build dies within ~30-45s.
      const _gHp = 1 + _gSec * 0.4;
      const _gDmg = 1 + _gSec * 0.8;
      setGodModeScaling(_gHp, _gDmg, Math.min(1 + _gSec * 0.01, 1.6));
      // BETA 21.46 — flood the screen in god mode (alive-cap ×3) so a high-level
      // player's AoE can't keep the perimeter clear → enemies leak through + reach you.
      setGodModeDensityMult(3);
    } else {
      setGodModeScaling(1, 1, 1);
      setGodModeDensityMult(1);
    }
  }
  // BETA 14.09 — Lab time-lock. When set, clamp state.time to the chosen
  // value each frame so wave-system difficulty + swarm spawns + enemy stat
  // ramps stay frozen at that minute. User: "i would like to lock a level
  // i am on. not really timer based. just allows me to control what wave
  // i am on". Lab-only — no effect outside isLabMode().
  if (isLabMode()) {
    const lockSec = getLabFrozenTimeSec();
    if (lockSec != null) state.time = lockSec;
    // BETA 14.39 — apply retreat-sim zoom override. Default sim zoom is 1.0
    // (no override). When user drags the zoom slider, lab camera updates
    // each frame so the user sees more of the map.
    const _simZoom = getRetreatSimZoom();
    if (_simZoom !== 1.0) {
      // Multiply against the default lab/run zoom (1.2) for consistency.
      state.camera.zoom = 1.2 * _simZoom;
    }
    // BETA 14.45 — consume per-build state reset request (set by weaponLab.
    // applyBuild). Fixes the RANDOMIZE leak: prior-build state accumulators
    // were carrying forward, causing 9755 HP / 25888% damage by the 11th
    // re-roll. Reset all per-run-cumulative state fields so each build
    // starts from clean baseline.
    if (consumePerBuildStateReset()) {
      // BETA 14.48 — LAG FIX. Per-build cleanup of accumulating arrays. By
      // 8 RANDOMIZE clicks, avg frame time had grown from 5ms → 144ms; some
      // frames spiked to 2.6 SECONDS. Root cause: transient state arrays
      // (particles, notifications, ground effects, enemies, projectiles)
      // weren't cleared on applyBuild, accumulating across builds until the
      // game choked. Now cleared explicitly:
      state.particles.length = 0;
      state.pickupNotifications.length = 0;
      state.runUnlockNotifs = [];
      state.pendingEnemyDamage.length = 0;
      // Clear all alive enemies — wave system will respawn fresh ones
      state.enemies.length = 0;
      // Clear weapon-system ground effects (SHIT trail puddles, banana traps,
      // whoopie cushions, weapon impact AOEs) so the per-tick effect-update
      // loop doesn't iterate hundreds of stale entries.
      const _wsForCleanup = getWeaponSystemState();
      if (_wsForCleanup) clearAllGroundEffects(_wsForCleanup);
      // Clear in-flight enemy projectiles
      clearEnemyProjectiles();
      state._mysteryMeatBonuses = undefined;
      state._runGoldSpent = 0;
      state._postDamageTimer = 0;
      state._liarUsedThisRun = false;
      _sheeeshRevivesUsed = 0; // module-level engine flag
      _heartThrobReadyAt = 0;      // BETA 22.90 — N10 Heart Throb cooldown reset
      // Debt CSV trackers reset (fields kept for CSV column compat — always 0
      // since 22.90 deleted the gold mechanic)
      state._debtActiveUntilSnapshot = 0;
      state._debtAccumulatedSnapshot = 0;
      state._debtSavesTriggeredThisRun = 0;
      state._debtGoldSpentThisRun = 0;
      state._debtHitsAbsorbedThisRun = 0;
      state._debtForceEndedThisRun = 0;
      // BETA 15.15 — N13 Not Xanax + N06 Let's Ride trackers reset
      state._notXanaxAccumulatedSnapshot = 0;
      state._notXanaxSlowsAppliedThisRun = 0;
      state._notXanaxTotalSlowSecondsThisRun = 0;
      state._perkBungulatorCountSnapshot = 0;
      state._perkBungulatorsSpawnedThisRun = 0;
      state._reviveEventsThisRun = 0;
      state._healEventsThisRun = 0;
      state._dodgeEventsThisRun = 0;
      state._phaseEventsThisRun = 0;
      // BETA 17.07 — PSBOT cycle-30t Tier C counters reset (5 of 12 this beta).
      const _sAnyReset = state as unknown as Record<string, number | boolean | undefined>;
      _sAnyReset._voogooHealsTriggeredThisRun = 0;
      _sAnyReset._voogooHealAmountThisRun = 0;
      _sAnyReset._timmyDamageReducedThisRun = 0;
      _sAnyReset._lastResortActivationsThisRun = 0;
      _sAnyReset._lastResortActiveLastFrame = false;
      // BETA 17.08 — PSBOT cycle-30t Tier C counters reset (final 6, closes batch).
      _sAnyReset._dracsGasMaskOverhealThisRun = 0;
      // BETA 17.11 — A3 + A7 telemetry proof counters (PSBOT cycle-30ad #3+#4
      // kept claiming these were unshipped because they couldn't see firing
      // in bot data. Now explicit counters surface in CSV.)
      _sAnyReset._a3InvulnRegenPausedFramesThisRun = 0;
      _sAnyReset._a7TimmySlowsAppliedThisRun = 0;
      // BETA 17.12 — Floor 1 progression diagnostic (PSBOT cycle-30ad Bundle 3).
      // Tracks WHERE bots/players get stuck on floor 1: never killed mega
      // (combat failure), killed mega but never reached portal (movement
      // failure), reached portal but didn't advance (interaction failure).
      _sAnyReset._megaBossKilledThisRun = 0;
      _sAnyReset._portalReachedThisRun = false;
      _sAnyReset._floorAdvancesThisRun = 0;
      state._knockbackEventsThisRun = 0;
      state._enemyAttacksAttemptedThisRun = 0;
      state._enemyAttacksHitThisRun = 0;
      state._enemyAttacksMissedByEvasion = 0;
      state._enemyAttacksMissedByIframe = 0;
      state._enemyAttacksMissedByPhase = 0;
      state._enemyAttacksMissedByOther = 0;
      // BETA 16.36 — shield regen telemetry counters (Dylan: "add all
      // shield regen tracking ad upgrades in bot runs and regular plays")
      const _stateInitAny = state as unknown as Record<string, number>;
      _stateInitAny._shieldDamageAbsorbedThisRun = 0;
      _stateInitAny._shieldRegeneratedThisRun = 0;
      _stateInitAny._shieldRegenDelayUntil = 0;
      // BETA 16.37 — PSBOT cycle-30i §7E Bug C: heal-received counter was
      // never being incremented anywhere despite lifesteal + Mom's Cooking
      // both healing. Reset baseline so each run starts at 0.
      _stateInitAny._healingReceivedThisRun = 0;
      _stateInitAny._goldCollectedThisRun = 0;   // BETA 19.99 — lifetime gold accumulator (vs current wallet)
      // BETA 16.37 — PSBOT cycle-30i §7E Bug F: phase time tracking
      // existed in bot autoPlaytester only — live_play always reported 0s.
      // Now: tick into one of these counters each frame based on
      // state.phase. Surfaced in livePlayTracker.
      _stateInitAny._phaseTimePlaying = 0;
      _stateInitAny._phaseTimeLevelup = 0;
      _stateInitAny._phaseTimeChest = 0;
      _stateInitAny._phaseTimeShrine = 0;
      // BETA 16.38 — reset active-item tray so each run/build starts clean.
      resetActiveItemTray();
      // BETA 16.42 — reset hit-marker fields used by the player render path.
      _stateInitAny._playerHitFlashTimer = 0;
      _stateInitAny._playerHitLastDamage = 0;
      _stateInitAny._playerHitLastWasShield = 0;
      // BETA 16.51 — Last Stand is split from item-revives (PSBOT cycle-30k
      // P1: "Every single run has 1+ revive event. ... a default/character-
      // passive/pity-revive mechanism"). Last Stand now has its own counter.
      _stateInitAny._lastStandTriggersThisRun = 0;
      // Phase 5 timing baselines (reset so each build captures its own first*)
      state._firstBloodTime = 0;
      state._firstDamageTakenTime = 0;
      state._longestStretchBetweenHitsMs = 0;
      // BETA 14.55 — Reset eventLog kill-delta tracker + near-miss state per
      // build. Without these, prior-build kills/HP-history bleed into the
      // first frames of the new build's telemetry.
      _eventLogPrevKills = state.killCount ?? 0;
      _nearMissNadirHp = -1;
      _nearMissDipTime = 0;
      // BETA 14.57 — Reset deathContext sliding window per build.
      _recentHits = [];
      _lastHpForDeathTracking = -1;
      _deathClarityLogged = false; // BETA 22.48
      // BETA 14.57 — Reset goldSpent per build (was already in 14.45 as
      // part of the broader reset; explicit here for clarity).
      state._runGoldSpent = 0;
      // BETA 14.63 — Reset VooGoo auto-heal cooldown per build so each build
      // starts with the save ready.
      (state as unknown as Record<string, number | undefined>)._voogooHealCd = 0;
      _voogooHealCdDisplay = 0;
      stopAllWeaponLoops();   // BETA — clear any leftover aura hum from a prior run
      // Last Stand: each build gets a fresh save
      state.lastStandAvailable = true;
      // Damage taken accumulator (per-run, but per-build delta is what matters)
      state.damageTakenThisRun = 0;
      // Reset killCount so firstBloodTime captures THIS build's first kill
      state.killCount = 0;
      // BETA 17.88 — Reset player level/XP per build. LUDICROUS bulk test
      // showed prior-build XP/level carrying into the next build (each row's
      // recorded `level` was the cumulative re-roll level, not the level
      // reached during *that* build). Each build must start at lvl 1 so the
      // CSV reflects in-build progression.
      state.player.level = 1;
      state.player.xp = 0;
      // BETA 18.19 — was hardcoded 100. Now uses xpRequiredForLevel so lab
      // builds match the new single-source-of-truth curve. (For level 1
      // the value is small — labs are for stat testing, not progression.)
      state.player.xpToNext = xpRequiredForLevel(1);
      // BETA 17.89 — Reset state.phase to 'playing'. The 17.88 LUDICROUS test
      // showed the player getting stuck in 'levelup' phase across builds:
      // build 1 leveled up, build 2's applyBuild() didn't touch state.phase,
      // so build 2 (and every subsequent build) started already frozen on
      // a leftover level-up card screen. Result: frozen player + zero combat
      // for 49 of 50 builds. Dylan: "the bot should have been running and not
      // standing still ... if that didnt happen then run it back." Unconditional
      // reset is safe here: this block runs ONLY when applyBuild() requests a
      // per-build state reset — the previous build's UI state is being
      // discarded by definition.
      state.phase = 'playing';
      // Per-frame sweep counters
      state._playerHpBucketLowFrames = 0;
      state._playerHpBucketMidFrames = 0;
      state._playerHpBucketHighFrames = 0;
      state._playerStationaryFrames = 0;
      state._playerDiagonalMoveFrames = 0;
      state._noEnemyNearbyFrames = 0;
      state._5plusEnemiesIn150wuFrames = 0;
      state._playerMSAvgSnapshot = 0;
      state._phaseChanceCurrentAvgSnapshot = 0;
      state._critChanceCurrentAvgSnapshot = 0;
      // Frame-perf samples (cleared so each build's perf isolates)
      state._engineFramePerfThisRun = { spikes: [], avgFrameTimeMs: 0, worstFrameTimeMs: 0 };
      // BETA 20.45 — reset the high-frequency human-play trace each run
      (state as unknown as { _humanTrace?: number[][]; _humanTraceNext?: number })._humanTrace = [];
      (state as unknown as { _humanTrace?: number[][]; _humanTraceNext?: number })._humanTraceNext = 0;
    }
  }
  const { player, keys, camera } = state;

  // BETA 14.12 — Snapshot player stat bonuses for enemySystem to read inside
  // damageEnemy on-hit hooks (Big Mac burn DoT, Stay Frosty slow duration).
  // Per-frame cache so item-on-hit scaling stays in sync with the live perc
  // state without touching damageEnemy's signature. Manus handoff Section A.
  setCurrentPlayerStats({
    damageBonus: player.damageBonus ?? 0,
    attackSpeedBonus: player.attackSpeedBonus ?? 0,
    attackAreaBonus: player.attackAreaBonus ?? 0,
    luck: player.luck ?? 0,
  });
  // BETA 14.27 — push live combat stat counters into weaponLab cache so
  // _buildRowSnapshot can compute per-build deltas at finalize time. These
  // are run-cumulative in state; weaponLab snapshots them at applyBuild()
  // and subtracts on finalize.
  setCurrentCombatStats({
    damageTaken: state.damageTakenThisRun ?? 0,
    attacksAttempted: state._enemyAttacksAttemptedThisRun ?? 0,
    attacksHit: state._enemyAttacksHitThisRun ?? 0,
    attacksMissedByEvasion: state._enemyAttacksMissedByEvasion ?? 0,
    attacksMissedByIframe: state._enemyAttacksMissedByIframe ?? 0,
    attacksMissedByPhase: state._enemyAttacksMissedByPhase ?? 0,
    // BETA 14.30
    attacksMissedByOther: state._enemyAttacksMissedByOther ?? 0,
    reviveEvents: state._reviveEventsThisRun ?? 0,
    healEvents: state._healEventsThisRun ?? 0,
    dodgeEvents: state._dodgeEventsThisRun ?? 0,
    phaseEvents: state._phaseEventsThisRun ?? 0,
    knockbackEvents: state._knockbackEventsThisRun ?? 0,
  });

  // BETA 14.30 Phase 1 — per-frame stat snapshot. Each frame, copy player's
  // current stat values into state.final* fields so the run-end value is
  // just whatever was last captured (no separate finalizeRun call needed).
  // These are READ from player object at this point (before the per-frame
  // applyAllBonusesToPlayer runs later in the tick) — so they reflect the
  // current end-of-frame state from the previous tick. Good enough for
  // bot CSV at finalize.
  state.finalDamagePct = player.damageBonus ?? 0;
  // BETA 14.36 — critChance reports % BONUS from base (5). With no buffs,
  // player.critChance is 5 (default). Subtract base so finalCritChancePct=0
  // when no crit buffs equipped.
  state.finalCritChancePct = (player.critChance ?? 5) - 5;
  state.finalCritDamagePct = player.critDamage ?? 0;
  state.finalAttackSpeedPct = player.attackSpeedBonus ?? 0;
  // movementSpeedBonus is encoded as `player.speed` directly (base 200).
  // Compute % above 200 for the snapshot.
  state.finalMovementSpeedPct = player.speed > 0 ? ((player.speed - 200) / 200) * 100 : 0;
  state.finalLifestealPct = player.lifesteal ?? 0;
  state.finalEvasionPct = player.evasion ?? 0;
  state.finalPhaseChancePct = (player.contactPhaseChance ?? 0) * 100;
  state.finalPickupRangePct = player.pickupRange ?? 0;
  state.finalLockOnRange = player.rangeBonus ?? 0;
  state.finalKnockbackPct = player.knockbackBonus ?? 0;
  state.finalEliteDamagePct = player.eliteDamageBonus ?? 0;
  state.finalThornsPct = player.thorns ?? 0;
  state.finalXPGainPct = ((player.xpMultiplier ?? 1) - 1) * 100;
  state.finalGoldGainPct = ((player.goldMultiplier ?? 1) - 1) * 100;
  state.finalMaxHP = player.maxHp ?? 0;
  state.finalHPRegen = player.hpRegen ?? 0;
  state.finalArmor = player.armor ?? 0;
  state.finalShield = player.maxShield ?? 0;
  state.finalOverheal = (player.maxHp ?? 0) > 100 ? (player.maxHp ?? 0) - 100 : 0;
  state.finalProjectileCount = player.projectileCountBonus ?? 0;
  state.finalProjectileBounce = player.projectileBounceBonus ?? 0;
  // BETA 15.18 — wire to lazy player field set by itemsAdapter when N08 picks apply.
  state.finalProjectilePierce = (player as unknown as Record<string, number>).projectilePierceBonus ?? 0;
  // BETA 14.30 Phase 1 — push to weaponLab cache for WL CSV consumption.
  setCurrentStatSnapshot({
    finalDamagePct: state.finalDamagePct ?? 0,
    finalCritChancePct: state.finalCritChancePct ?? 0,
    finalCritDamagePct: state.finalCritDamagePct ?? 0,
    finalAttackSpeedPct: state.finalAttackSpeedPct ?? 0,
    finalMovementSpeedPct: state.finalMovementSpeedPct ?? 0,
    finalLifestealPct: state.finalLifestealPct ?? 0,
    finalEvasionPct: state.finalEvasionPct ?? 0,
    finalPhaseChancePct: state.finalPhaseChancePct ?? 0,
    finalPickupRangePct: state.finalPickupRangePct ?? 0,
    finalLockOnRange: state.finalLockOnRange ?? 0,
    finalKnockbackPct: state.finalKnockbackPct ?? 0,
    finalEliteDamagePct: state.finalEliteDamagePct ?? 0,
    finalThornsPct: state.finalThornsPct ?? 0,
    finalXPGainPct: state.finalXPGainPct ?? 0,
    finalGoldGainPct: state.finalGoldGainPct ?? 0,
    finalMaxHP: state.finalMaxHP ?? 0,
    finalHPRegen: state.finalHPRegen ?? 0,
    finalArmor: state.finalArmor ?? 0,
    finalShield: state.finalShield ?? 0,
    finalOverheal: state.finalOverheal ?? 0,
    finalProjectileCount: state.finalProjectileCount ?? 0,
    finalProjectileBounce: state.finalProjectileBounce ?? 0,
    finalProjectilePierce: state.finalProjectilePierce ?? 0,
  });
  // BETA 14.36 — push timing baselines from state to weaponLab cache.
  if ((state._firstBloodTime ?? 0) > 0) setFirstBloodTime(state._firstBloodTime ?? 0);
  if ((state._firstDamageTakenTime ?? 0) > 0) setFirstDamageTakenTime(state._firstDamageTakenTime ?? 0);
  if ((state._longestStretchBetweenHitsMs ?? 0) > 0) setLongestStretchBetweenHitsMs(state._longestStretchBetweenHitsMs ?? 0);

  // BETA 14.32 Phase 3 — per-frame sweep counters. Increment based on
  // current frame state, then push to weaponLab for WL CSV.
  {
    const hpPct = player.maxHp > 0 ? player.hp / player.maxHp : 1;
    if (hpPct < 0.30) state._playerHpBucketLowFrames = (state._playerHpBucketLowFrames ?? 0) + 1;
    else if (hpPct < 0.70) state._playerHpBucketMidFrames = (state._playerHpBucketMidFrames ?? 0) + 1;
    else state._playerHpBucketHighFrames = (state._playerHpBucketHighFrames ?? 0) + 1;
    // Stationary check (no movement keys held this frame)
    const movingKeyHeld = keys.has('w') || keys.has('a') || keys.has('s') || keys.has('d') ||
                         keys.has('arrowup') || keys.has('arrowleft') || keys.has('arrowdown') || keys.has('arrowright');
    if (!movingKeyHeld) state._playerStationaryFrames = (state._playerStationaryFrames ?? 0) + 1;
    // Diagonal move check (>=2 cardinal keys held)
    let cardinalCount = 0;
    if (keys.has('w') || keys.has('arrowup')) cardinalCount++;
    if (keys.has('s') || keys.has('arrowdown')) cardinalCount++;
    if (keys.has('a') || keys.has('arrowleft')) cardinalCount++;
    if (keys.has('d') || keys.has('arrowright')) cardinalCount++;
    if (cardinalCount >= 2) state._playerDiagonalMoveFrames = (state._playerDiagonalMoveFrames ?? 0) + 1;
    // Enemy density buckets
    if (state.enemies?.length) {
      let nearbyCount = 0;
      let anyWithin300 = false;
      for (const e of state.enemies) {
        if (!e.alive) continue;
        const dx = e.x - player.x, dy = e.y - player.y;
        const d2 = dx*dx + dy*dy;
        if (d2 < 90000 /* 300^2 */) anyWithin300 = true;
        if (d2 < 22500 /* 150^2 */) nearbyCount++;
      }
      if (!anyWithin300) state._noEnemyNearbyFrames = (state._noEnemyNearbyFrames ?? 0) + 1;
      if (nearbyCount >= 5) state._5plusEnemiesIn150wuFrames = (state._5plusEnemiesIn150wuFrames ?? 0) + 1;
      // BETA 14.58 — per-perc conditionMetFrames sweep.
      // BETA 20.11 — N05 Bungulazine REMOVED as a perc (it's now the
      // item_bungula_chambers legendary Buncha Bullshit item, not a perc).
      // Its dead always-false sweep lines were deleted here.
      // N06 Let's Ride: condition met when no enemies within 300wu.
      // N12 Mr Silly Pills: condition met during diagonal movement (set above).
      const _percState = getPercState();
      if (_percState) {
        const equipsN06 = _percState.slots.some(s => s.perc?.id === 'N06' && s.level > 0);
        const equipsN12 = _percState.slots.some(s => s.perc?.id === 'N12' && s.level > 0);
        const equipsN08 = _percState.slots.some(s => s.perc?.id === 'N08' && s.level > 0);
        if (equipsN06 && !anyWithin300) trackPercCondition('N06');
        if (equipsN12 && cardinalCount >= 2) trackPercCondition('N12');
        // N08 Not Ritalin: first 60s of run = the buff phase
        if (equipsN08 && (state.time ?? 0) < 60) trackPercCondition('N08');
      }
    } else {
      state._noEnemyNearbyFrames = (state._noEnemyNearbyFrames ?? 0) + 1;
      const _percState = getPercState();
      if (_percState) {
        const equipsN06 = _percState.slots.some(s => s.perc?.id === 'N06' && s.level > 0);
        if (equipsN06) trackPercCondition('N06');
        const equipsN08 = _percState.slots.some(s => s.perc?.id === 'N08' && s.level > 0);
        if (equipsN08 && (state.time ?? 0) < 60) trackPercCondition('N08');
      }
    }
    // BETA 14.58 — currentEffectiveBonus snapshots for scaling percs.
    // N04 Not Blue Chew: 0.4% dmg per 1% missing HP per stack.
    // N07 Puberty Blockers: 0.3% all-stats per minute survived per stack.
    // N09 Greedy Tums: +0.5 dmg per 100g held per stack.
    // N10 Debt: +1 maxHp per 500g spent per stack.
    {
      const ps = getPercState();
      if (ps) {
        const find = (id: string) => ps.slots.find(s => s.perc?.id === id);
        const n04 = find('N04');
        if (n04 && n04.level > 0) {
          const missingFrac = player.maxHp > 0 ? Math.max(0, 1 - player.hp / player.maxHp) : 0;
          trackPercEffectiveBonus('N04', 0.4 * (missingFrac * 100) * n04.level);
        }
        const n07 = find('N07');
        if (n07 && n07.level > 0) {
          const minsSurvived = (state.time ?? 0) / 60;
          trackPercEffectiveBonus('N07', 0.3 * minsSurvived * n07.level);
        }
        const n09 = find('N09');
        if (n09 && n09.level > 0) {
          trackPercEffectiveBonus('N09', 0.5 * Math.floor((player.gold ?? 0) / 100) * n09.level);
        }
        // (N10 effective-bonus tracker removed in 22.90 — Heart Throb is a
        // binary proximity shockwave, not a gold-spent scaler.)
        // BETA 14.62 — Passive perc-perc + perc-item synergy combos. These
        // synergies don't have explicit boost code paths; their value is
        // "the build chose both at once." Track triggerCount as 1-per-frame
        // when both are equipped, so analyst sees uptime correlate with
        // run outcomes. Sampled once per second equivalent (every frame at
        // 60fps would inflate; gate by state.time integer rollover).
        const _stTime = Math.floor(state.time ?? 0);
        if (_stTime !== _lastSynergyFrameSec) {
          _lastSynergyFrameSec = _stTime;
          const has = (id: string) => ps.slots.some(s => s.perc?.id === id && s.level > 0);
          // notBlueChew_hutchsArmor (N04 + N02)
          if (has('N04') && has('N02')) trackSynergyTrigger('notBlueChew_hutchsArmor', 0);
          // greedyTums_stepmomsPills (N09 + N03)
          if (has('N09') && has('N03')) trackSynergyTrigger('greedyTums_stepmomsPills', 0);
          // notAcid_hutchsArmor (N14 + N02)
          if (has('N14') && has('N02')) trackSynergyTrigger('notAcid_hutchsArmor', 0);
          // letsRide_kiteBuilds (N06 + any kite-style equipment) — wildcard.
          // N06 condition was already checked above via noEnemyNearbyFrames;
          // simplest gate: N06 equipped AND noEnemyNearbyFrames bumped this frame.
          if (has('N06')) trackSynergyTrigger('letsRide_kiteBuilds', 0);
          // chadAura_dracsFang — both melee weapons. Check via weapon system.
          const _ws = getWeaponSystemState();
          const hasChad = _ws?.weapons?.some(w => w.definitionId === 'chad_aura');
          const hasDracs = _ws?.weapons?.some(w => w.definitionId === 'wpn_dracs_fang');
          if (hasChad && hasDracs) trackSynergyTrigger('chadAura_dracsFang', 0);
          // voogooRager_dracsGasMask — both items.
          const _inv = getItemState()?.inventory;
          const hasVoogoo = _inv?.some(s => s.item.id === 'item_voogoo_rager');
          const hasMask = _inv?.some(s => s.item.id === 'item_dracs_gas_mask');
          if (hasVoogoo && hasMask) trackSynergyTrigger('voogooRager_dracsGasMask', 0);
        }
      }
    }
    // Running averages (simple cumulative, weighted by frame count)
    const totalFrames = (state._playerHpBucketLowFrames ?? 0) + (state._playerHpBucketMidFrames ?? 0) + (state._playerHpBucketHighFrames ?? 0);
    if (totalFrames > 0) {
      state._playerMSAvgSnapshot = ((state._playerMSAvgSnapshot ?? 0) * (totalFrames - 1) + player.speed) / totalFrames;
      state._phaseChanceCurrentAvgSnapshot = ((state._phaseChanceCurrentAvgSnapshot ?? 0) * (totalFrames - 1) + (player.contactPhaseChance ?? 0) * 100) / totalFrames;
      state._critChanceCurrentAvgSnapshot = ((state._critChanceCurrentAvgSnapshot ?? 0) * (totalFrames - 1) + (player.critChance ?? 0)) / totalFrames;
    }
    setCurrentSweepCounters({
      playerHpBucketLowFrames: state._playerHpBucketLowFrames ?? 0,
      playerHpBucketMidFrames: state._playerHpBucketMidFrames ?? 0,
      playerHpBucketHighFrames: state._playerHpBucketHighFrames ?? 0,
      playerStationaryFrames: state._playerStationaryFrames ?? 0,
      playerDiagonalMoveFrames: state._playerDiagonalMoveFrames ?? 0,
      noEnemyNearbyFrames: state._noEnemyNearbyFrames ?? 0,
      fivePlusEnemiesIn150wuFrames: state._5plusEnemiesIn150wuFrames ?? 0,
      playerMSAvg: state._playerMSAvgSnapshot ?? 0,
      phaseChanceCurrentAvg: state._phaseChanceCurrentAvgSnapshot ?? 0,
      critChanceCurrentAvg: state._critChanceCurrentAvgSnapshot ?? 0,
      kittenPlacementsCount: state._kittenPlacementsCount ?? 0,
      kittenActiveAtAnyTimeAvg: state._kittenActiveAtAnyTimeAvg ?? 0,
      kittenAttacksFiredTotal: state._kittenAttacksFiredTotal ?? 0,
      kittenDmgDealtTotal: state._kittenDmgDealtTotal ?? 0,
      kittenKillsTotal: state._kittenKillsTotal ?? 0,
      kittenFreezeAppliedFrames: state._kittenFreezeAppliedFrames ?? 0,
      kittenLifetimeAvgMs: state._kittenLifetimeAvgMs ?? 0,
      kittenDeathRate: state._kittenDeathRate ?? 0,
      kittenPlacementCornerCount: state._kittenPlacementCoordsBucketed?.corner ?? 0,
      kittenPlacementCenterCount: state._kittenPlacementCoordsBucketed?.center ?? 0,
      kittenPlacementScatterCount: state._kittenPlacementCoordsBucketed?.scatter ?? 0,
    });
  }
  const WS = state.map.WORLD_SIZE;

  // ── Invincibility timer ──
  if (player.invincibleTimer > 0) {
    player.invincibleTimer = Math.max(0, player.invincibleTimer - dt);
  }

  // ── BETA 21.11 — Dash timers (burst window + charge recharge) ──
  if ((player.dashActiveTimer ?? 0) > 0) {
    player.dashActiveTimer = Math.max(0, (player.dashActiveTimer ?? 0) - dt);
  }
  if ((player.dashCharges ?? 0) < DASH_MAX_CHARGES) {
    player.dashRechargeTimer = Math.max(0, (player.dashRechargeTimer ?? 0) - dt);
    if ((player.dashRechargeTimer ?? 0) <= 0) {
      player.dashCharges = Math.min(DASH_MAX_CHARGES, (player.dashCharges ?? 0) + 1);
      // Arm the next refill only while still below the cap.
      player.dashRechargeTimer = (player.dashCharges ?? 0) < DASH_MAX_CHARGES ? _dashRechargeFor(player) : 0;
    }
  }

  // ── Screen shake timer ──
  if (state.screenShakeTimer > 0) {
    state.screenShakeTimer = Math.max(0, state.screenShakeTimer - dt);
  }
  // BETA 16.59 — Wave 4: shake toggle. If the user has shake disabled
  // in Settings, zero the timer + intensity every frame so no per-call-site
  // change is needed (dozens of effects set shake; they all funnel through
  // these two fields).
  if (!getScreenShakeEnabled()) {
    state.screenShakeTimer = 0;
    state.screenShakeIntensity = 0;
  }

  // BETA 16.39 — Player hit flash timer.
  // BETA 16.42 — Bumped window from 0.22 to 0.55 so the floating
  // "💥 -N" marker has time to rise + fade visibly.
  // Set by enemyAdapter at the moment damage actually lands. Read in
  // the player render path which overlays a red tint while > 0.
  {
    const _sAnyHitFlash = state as unknown as Record<string, number | undefined>;
    const t = _sAnyHitFlash._playerHitFlashTimer ?? 0;
    if (t > 0) _sAnyHitFlash._playerHitFlashTimer = Math.max(0, t - dt);
  }

  // BETA 16.38 — sync active-item tray. Tray ONLY shows TRUE Q-press
  // items (player input fires the activation).
  // BETA 16.47 — Per Dylan: "the new q item on screen for the card whoopie
  // coochins of doop does not need to be a Q activation. that is a
  // passive that auto depoloyed. ... this is not a legit Q item." Whoopie
  // and Banana auto-drop on a timer — they're passives. Filter to an
  // explicit allowlist of items where Q actually triggers something.
  // Passive auto-drop items don't render in the tray (no perf cost,
  // no misleading "Q" badge).
  {
    const inv = getItemState()?.inventory ?? [];
    const activeIds = inv
      .filter(slot => Q_ACTIVATED_ITEM_IDS.has(slot.item.id))
      .map(slot => slot.item.id);
    syncActiveItemSlots(activeIds, dt);
  }

  // BETA 16.90 — phase time accum block was at line 1976 BEFORE this
  // BETA 16.37 attempt — but updateGame's early return at ~line 1501
  // kicked out for any phase that wasn't 'playing' or 'death_replay'.
  // So phaseTimeLevelup / WildCardMachine / Shrine never accumulated and read 0
  // in cycle-30o telemetry. Block moved to the TOP of updateGame so
  // it ticks regardless of phase. Pure pointer write — no risk.

  // BETA 16.35 — Shield regen. Dylan: "does shield never regen?"
  // BETA 16.36 — regen RATE is now player.shieldRegen (base 1.0/sec,
  // items + shrines stack on top). Plus tracking: regen amount accumulates
  // into state._shieldRegeneratedThisRun so live_play + bot_run telemetry
  // can show how much shield was actually recovered per run.
  if (state.phase === 'playing') {
    const sAny = state as unknown as Record<string, number | undefined>;
    const delayUntil = sAny._shieldRegenDelayUntil ?? 0;
    const p = state.player;
    // BETA 17.03 — PSBOT cycle-30u A3: pause shield regen during invuln
    // windows (Last Stand / Sheeesh / Liar Deceiver). The 1.5s i-frame
    // after Last Stand was letting shield top off "for free" while the
    // player couldn't be hit, undermining the recovery-window design.
    // BETA 17.11 — PSBOT cycle-30v/aa/ad keeps claiming A3 isn't shipped
    // because they can't see it fire in bot data. Adding explicit counter:
    // every frame where regen would have fired but is gated by invuln
    // increments a3InvulnRegenPausedFrames. Surfaces in CSV so PSBOT can
    // see A3 fires when Last Stand/Sheeesh trigger.
    const inInvulnWindow = (p.invincibleTimer ?? 0) > 0;
    // BETA 19.5 — Defensive: clamp NaN/undefined shield BEFORE regen
    // check. Same root cause as the absorption bypass — NaN < maxShield
    // is false → regen skipped → shield stuck at NaN forever, displaying
    // as full while never doing anything.
    if (!Number.isFinite(p.shield)) p.shield = 0;
    if (!Number.isFinite(p.maxShield)) p.maxShield = 0;
    const wantsRegen =
      p.maxShield > 0 &&
      p.shield < p.maxShield &&
      state.time >= delayUntil &&
      (p.shieldRegen ?? 0) > 0;
    // BETA 20.00 — UN-GATED from Debt (Dylan: healing keeps running). Shield
    // regen recovers shield → Debt deactivates (shield = 0 condition fails) →
    // damage absorbs in shield first. That's intentional in the new design:
    // shield is your first-line defense, Debt is the safety net while shield
    // is depleted.
    if (wantsRegen && !inInvulnWindow) {
      const before = p.shield;
      p.shield = Math.min(p.maxShield, p.shield + p.shieldRegen * dt);
      // Shield ACTIVATE sound — it just came back online (crossed 0 → up).
      if (before <= 0.01 && p.shield > 0.01) playShieldActivate();
      const regenedThisFrame = p.shield - before;
      sAny._shieldRegeneratedThisRun = (sAny._shieldRegeneratedThisRun ?? 0) + regenedThisFrame;
    } else if (wantsRegen && inInvulnWindow) {
      // A3 in action — would have regen'd but invuln blocked it.
      sAny._a3InvulnRegenPausedFramesThisRun = (sAny._a3InvulnRegenPausedFramesThisRun ?? 0) + 1;
    }
  }

  // 23.39 — boss-shrine ramping shake DELETED with the boss shrine (CaseOh purge).

  // ── Last stand flash timer ──
  if (state.lastStandFlashTimer > 0) {
    state.lastStandFlashTimer = Math.max(0, state.lastStandFlashTimer - dt);
  }
  // BETA 16.59 — Wave 4: screen flash toggle. Suppresses ALL flash overlays
  // (last stand flash, boss flash, hit flash) at the source by clearing
  // the timer if the user has flashes disabled in Settings.
  if (!getScreenFlashEnabled()) {
    state.lastStandFlashTimer = 0;
    const _sAnyHF = state as unknown as Record<string, number | undefined>;
    _sAnyHF._playerHitFlashTimer = 0;
  }

  // 23.39 — final-boss spawn watcher DELETED (CaseOh purge). The portal
  // now charges directly; Phase C roster bosses bring a new gate.

    // BETA 22.90 — N10 HEART THROB (was the Debt gold-shield; Dylan: "change
  // the name and what it does. delete the old gold stuff").
  // BETA 22.91 — trigger CHANGED per Dylan: fires when HP drops into the
  // low-health RED-FLICKER state (below 35% — the renderLowHealthVignette
  // threshold), not on enemy proximity. While critical it re-fires every
  // HEART_THROB_COOLDOWN (8s): every enemy inside HEART_THROB_BLAST_R gets
  // shoved radially away (elites/bosses resist like regular knockback).
  // `_debtActiveUntilSnapshot` stays pinned to 0 — the field survives only
  // for CSV column compat; nothing reads it as a mechanic anymore.
  {
    const _ps = getPercState();
    const _htSlot = _ps?.slots.find(s => s.perc?.id === 'N10');
    const _htLowHp = player.hp > 0
      && (player.maxHp > 0 ? player.hp / player.maxHp : 1) < HEART_THROB_HP_FRAC;
    if (_htSlot && _htLowHp && (state.time ?? 0) >= _heartThrobReadyAt) {
      const _ess = getEnemySystemState();
      if (_ess) {
        {
          _heartThrobReadyAt = (state.time ?? 0) + HEART_THROB_COOLDOWN;
          for (const _en of _ess.enemies) {
            if (!_en.alive) continue;
            const _dx = _en.x - player.x, _dy = _en.y - player.y;
            const _d2 = _dx * _dx + _dy * _dy;
            if (_d2 > HEART_THROB_BLAST_R * HEART_THROB_BLAST_R) continue;
            const _d = Math.sqrt(_d2) || 1;
            // mirror the damage-path knockback resists so bosses don't fly
            const _res = _en.variant === 'boss' ? 0.15 : _en.variant === 'elite' ? 0.5 : 1;
            _en.knockbackX += (_dx / _d) * HEART_THROB_FORCE * _res;
            _en.knockbackY += (_dy / _d) * HEART_THROB_FORCE * _res;
          }
          // VFX — Heart Throb's OWN look (identity law): one thick crimson
          // heartbeat ring + pink pressure spray. (Staggered cyan ring pulses
          // stay the chick magnet's — this is a single ba-bump.)
          spawnParticles(state.particles, player.x, player.y, '#ff3366', 3, { shape: 'ring', speedMin: 0, speedMax: 0, sizeMin: 26, sizeMax: 34, lifeMin: 0.35, lifeMax: 0.45 });
          spawnParticles(state.particles, player.x, player.y, '#ff7799', 22, { shape: 'mote', speedMin: 160, speedMax: 320, lifeMin: 0.25, lifeMax: 0.5, sizeMin: 2, sizeMax: 4 });
          state.screenShakeTimer = 0.15;
          state.screenShakeIntensity = 5;
          try { playHeartThrobPulse(); } catch { /* sfx optional */ }
        }
      }
    }
    state._debtActiveUntilSnapshot = 0;
    state._debtAccumulatedSnapshot = 0;
    // BETA 15.15 — N13 Not Xanax accumulated snapshot (drives slow duration)
    const _xanaxSlot = _ps?.slots.find(s => s.perc?.id === 'N13');
    state._notXanaxAccumulatedSnapshot = _xanaxSlot?.accumulatedBonus ?? 0;
    // BETA 15.21 — N06 Let's Ride: every pick = +1 buddy, NO CAP.
    // Per Dylan: "N06 should give you 1+ bungulator every level up." +
    // "not max!!!! unlimited". The maxLevel=40 still bounds the perk
    // itself (so the absolute maximum is 40 perk-source buddies), but
    // we no longer impose a per-frame spawn cap of 5.
    //
    //   level 1:  1 buddy
    //   level 5:  5 buddies
    //   level 10: 10 buddies
    //   level 40: 40 buddies (perk maxLevel cap)
    const _rideSlot = _ps?.slots.find(s => s.perc?.id === 'N06');
    const _perkBungs = _rideSlot ? _rideSlot.level : 0;
    state._perkBungulatorCountSnapshot = _perkBungs;
  }
  // Initialize trackers if undefined (so CSV emit doesn't see undefined)
  if (state._debtSavesTriggeredThisRun === undefined) state._debtSavesTriggeredThisRun = 0;
  if (state._debtGoldSpentThisRun === undefined) state._debtGoldSpentThisRun = 0;
  if (state._debtHitsAbsorbedThisRun === undefined) state._debtHitsAbsorbedThisRun = 0;
  if (state._debtForceEndedThisRun === undefined) state._debtForceEndedThisRun = 0;
  if (state._notXanaxSlowsAppliedThisRun === undefined) state._notXanaxSlowsAppliedThisRun = 0;
  if (state._notXanaxTotalSlowSecondsThisRun === undefined) state._notXanaxTotalSlowSecondsThisRun = 0;
  if (state._perkBungulatorsSpawnedThisRun === undefined) state._perkBungulatorsSpawnedThisRun = 0;

  // ── HP regeneration from shrine buffs ──
  // BETA 15.14 — gated during Debt window. Per Dylan: "there has to be a
  // pause on anything healing them including life steal."
  // BETA 16.04 — ALSO gated when hp <= 0. Per Dylan: "i could not die
  // HUGE bug" + "all the way to lik 1hp had timmys and 100's of zombies
  // on me. 2 weapons. no items." Root cause traced to a 16.02 regression:
  // when I removed enemyAdapter's gameover-shortcut, the death detection
  // moved entirely to engine.ts line 1921. But regen runs BEFORE that
  // check — so each frame, damage drops hp to 0, then regen tops hp back
  // up before the death check sees it. Player gets stuck in 0/regen
  // ping-pong and never dies. Fix: regen does NOT apply to a dead
  // player. Once hp hits 0, regen pauses; engine.ts death check fires
  // next frame; rich death handler runs.
  // BETA 20.00 — HP regen UN-GATED during Debt per Dylan's new spec: healing
  // needs to bring HP back to 50% to exit Debt. Without healing, Debt would
  // be permanent until gold drains → guaranteed death. The new mechanic is
  // a RACE between heal-rate and gold-drain-rate.
  // BETA 20.77 — COMBAT-GATED REGEN (audit of Dylan's god-mode run: base
  // 10/s alone healed 83% of ALL incoming damage — the Charter's "heals ≤50%
  // of damage" was mathematically impossible). Regen now pauses while the
  // post-damage window is open (state._postDamageTimer, 3s per hit — the
  // same timer Not Xanax uses): it's RECOVERY between fights, not immortality
  // during them. Dylan's locked 10/s value unchanged; only WHEN it applies.
  // (BETA 22.90 — the old "Debt bypasses the combat gate" exception is gone
  // with the Debt mechanic itself; only the post-damage window gates now.)
  if (player.hpRegen > 0 && player.hp > 0 && player.hp < player.maxHp
      && (state._postDamageTimer ?? 0) <= 0) {
    const _hpBeforeRegen = player.hp;
    player.hp = Math.min(player.maxHp, player.hp + player.hpRegen * dt);
    // BETA 20.67 — count passive regen into the healing ledger (deep-dive
    // HIGH #4): runHealingReceived was blind to base regen (10 HP/sec ≈
    // ~6,000 HP per 10-min run) — every sustain-vs-damage analysis and the
    // Charter §2 "heals ≤ 50% of damage" law were off by the single biggest
    // heal source. Split out as runRegenHealed so passive vs active is visible.
    const _rgDelta = player.hp - _hpBeforeRegen;
    const _sAnyRg = state as unknown as Record<string, number | undefined>;
    _sAnyRg._healingReceivedThisRun = (_sAnyRg._healingReceivedThisRun ?? 0) + _rgDelta;
    _sAnyRg._regenHealedThisRun = (_sAnyRg._regenHealedThisRun ?? 0) + _rgDelta;
  }

  // ── Low-health heartbeat warning ──
  {
    const hpPct = player.hp / player.maxHp;
    const isLowHp = hpPct < 0.25 && state.phase === 'playing';
    // BETA 15.92 — Music ducks hard when low HP, heartbeat amplifies. Per
    // Dylan: "music ducks hard when low health and the heartbeat low health
    // is amplified. until you are no longer in the low health and the music
    // goes back up fading up rather quickly. in sync." Sustained duck is
    // entry/exit-paired (startSustainedDuck / endSustainedDuck) so it stays
    // ducked the whole time HP is low rather than auto-restoring after a
    // fixed window. _lowHpDucked tracks transitions so we only call once
    // per entry / exit.
    if (isLowHp && !_lowHpDucked) {
      _lowHpDucked = true;
      startSustainedDuck(0.15); // duck to 15% intended volume
      setBgmPlaybackRate(0.8, 0.6); // BETA — slow + pitch-drop the music while dying
    } else if (!isLowHp && _lowHpDucked) {
      _lowHpDucked = false;
      endSustainedDuck();       // ramp back up over 400ms
      setBgmPlaybackRate(1.0, 0.5); // back to normal speed/pitch in sync
    }
    // 23.42 — Peanut voice phase/idle/threat logic DELETED (all peanut
    // voice lines removed on Dylan's order; menu music only, in-game audio
    // is atmospheric + intentional).
    if (isLowHp) {
      lowHealthWarnTimer -= dt;
      if (lowHealthWarnTimer <= 0) {
        playLowHealthWarning();
        // Pulse faster the lower your HP: 1.5s at 25%, 0.7s at ~0%
        lowHealthWarnTimer = Math.max(0.7, 1.5 * (hpPct / 0.25));
      }
    } else {
      lowHealthWarnTimer = 0; // Reset so it fires immediately when HP drops
    }
  }

  // ── Player Movement ──
  let dx = 0, dy = 0;
  if (keys.has('w') || keys.has('arrowup')) dy -= 1;
  if (keys.has('s') || keys.has('arrowdown')) dy += 1;
  if (keys.has('a') || keys.has('arrowleft')) dx -= 1;
  if (keys.has('d') || keys.has('arrowright')) dx += 1;

  if (dx !== 0 && dy !== 0) {
    const len = Math.sqrt(dx * dx + dy * dy);
    dx /= len; dy /= len;
  }

  // BETA 21.11 — DASH FIRE (rising edge). Tap Shift / right-bumper to spend a
  // charge and blink a short COMMITTED burst with brief i-frames. The bumper is
  // mapped into state.keys as 'shift' by gamepadInput so the engine reads one
  // input source. Direction = current move dir, or facing when standing still.
  // BETA 22.47 — REMOVED the `!isBotEnabled()` gate (Dylan: "bots do not know
  // about dash yet... so they can use dash now"). The bot now presses 'shift'
  // itself via an autoPlaytester.ts hook when genuinely fleeing/swarmed —
  // this block just needs to stop hard-refusing that input.
  const _dashKeyDown = keys.has('shift') || keys.has('dash');
  if (
    _dashKeyDown && !player.dashHeldLastFrame &&
    (player.dashActiveTimer ?? 0) <= 0 &&
    (player.dashCharges ?? 0) > 0
  ) {
    let _ddx = dx, _ddy = dy;
    if (_ddx === 0 && _ddy === 0) { _ddx = player.facing; _ddy = 0; }
    player.dashDirX = _ddx;
    player.dashDirY = _ddy;
    player.dashActiveTimer = _dashDurationFor(player);   // BETA 21.18 — dash-distance buff
    // BETA 22.05 — a dash is a COMMITTED escape: clear any enemy knockback so the burst breaks
    // FREE instead of being cancelled by the shove. Root cause of Dylan's "my dash does not work":
    // BETA 22.02 made enemy knockback actually reach the player for the first time, and in a swarm
    // the accumulated shove (~25 wu/frame at the cap) out-muscled the dash burst (~15 wu/frame),
    // so the dash did nothing exactly when surrounded. Clearing + ignoring knockback during the
    // dash makes it a reliable get-out-of-the-pile tool again.
    player.knockbackX = 0;
    player.knockbackY = 0;
    player.dashCharges = (player.dashCharges ?? 0) - 1;
    // BETA 22.47 — run-scoped dash-usage counter (mirrors the existing
    // _shieldDamageAbsorbedThisRun-style accumulator pattern) so both the
    // bot CSV and human live-play telemetry can see dash adoption.
    const _dashAcc = state as unknown as Record<string, number | undefined>;
    _dashAcc._dashesUsedThisRun = (_dashAcc._dashesUsedThisRun ?? 0) + 1;
    // Arm the recharge timer only if it wasn't already counting toward a refill.
    if ((player.dashRechargeTimer ?? 0) <= 0) player.dashRechargeTimer = _dashRechargeFor(player);
    // Brief i-frames — reuses the field that already blocks damage + flashes the sprite.
    player.invincibleTimer = Math.max(player.invincibleTimer, DASH_IFRAME_SEC);
    playFootstep();
  }
  player.dashHeldLastFrame = _dashKeyDown;

  // 23.56 fix-pack — __devWalk(dx,dy,sec): command-driven virtual input so
  // collision/slide can be tested through the REAL movement path (synthetic
  // keyboard events are unreliable; test-by-command rule).
  if (_devWalkUntil > (state.time ?? 0)) { dx = _devWalkDx; dy = _devWalkDy; }
  const isMoving = dx !== 0 || dy !== 0;
  if (dx !== 0) player.facing = dx > 0 ? 1 : -1;

  // BETA 22.85 — feed the directional walk animation: movement angle +
  // whether moving + a phase accumulator that only advances while walking.
  _playerWalkMoving = isMoving;
  if (isMoving) { _playerWalkAngle = Math.atan2(dy, dx); _playerWalkPhase += dt; }

  if (isMoving) {
    player.bobPhase += dt * 10;
    if (Math.sin(player.bobPhase) > 0.9 && Math.sin(player.bobPhase - dt * 10) <= 0.9) {
      playFootstep();
    }
  } else {
    player.bobPhase *= 0.9;
  }

  // Sub-step collision (use smaller steps for accuracy)
  // BETA 21.11 — during a dash burst, move along the committed dash direction at
  // boosted speed (ignoring current input so the blink can't be cancelled). The
  // same sub-step canMoveTo loop below still runs, so a dash can't phase walls.
  let _moveDx = dx, _moveDy = dy, _speedMult = 1;
  if ((player.dashActiveTimer ?? 0) > 0) {
    _moveDx = player.dashDirX ?? dx;
    _moveDy = player.dashDirY ?? dy;
    _speedMult = DASH_SPEED_MULT;
    if (_moveDx !== 0) player.facing = _moveDx > 0 ? 1 : -1;
  }
  // BETA 21.33 — Timmy-type hits SLOW the player (half move speed until slowUntil).
  // Skipped during a dash so the dash escape always works at full burst.
  if ((player.dashActiveTimer ?? 0) <= 0 && (player.slowUntil ?? 0) > (state.time ?? 0)) {
    _speedMult *= 0.5;
  }
  const totalMoveX = _moveDx * player.speed * _speedMult * dt;
  const totalMoveY = _moveDy * player.speed * _speedMult * dt;
  const maxStep = 8;
  const maxMove = Math.max(Math.abs(totalMoveX), Math.abs(totalMoveY));
  const steps = Math.max(1, Math.ceil(maxMove / maxStep));
  const stepMoveX = totalMoveX / steps;
  const stepMoveY = totalMoveY / steps;

  let newX = player.x;
  let newY = player.y;

  // BETA 13.54: when bot is driving, treat the player as an entity so exterior
  // green curves block movement. Manual play keeps isEntity=false so humans can
  // still cut corners through exterior green.
  const playerIsEntity = isBotEnabled();
  for (let i = 0; i < steps; i++) {
    const _preX = newX, _preY = newY;
    if (canMoveTo(state, newX + stepMoveX, newY, player.size, playerIsEntity)) newX += stepMoveX;
    if (canMoveTo(state, newX, newY + stepMoveY, player.size, playerIsEntity)) newY += stepMoveY;
    // 23.56 fix-pack (doc 04 "better slide"): axis slide can deadlock in the
    // forest's concave noise pockets — both single-axis probes blocked while
    // a diagonal along the boundary is open. When a step made NO progress,
    // project the intent onto the clearance-field TANGENT (the field gives us
    // the boundary normal for free) and try that instead. Kills edge-snag.
    if (MAP_FOREST && BOUNDLESS_MAP && newX === _preX && newY === _preY && (stepMoveX !== 0 || stepMoveY !== 0)) {
      const _tsSeed = state.map.roomMap.seed ?? 1;
      const _tsFy = newY + (player.spriteSize || 40) * 0.6;
      const _tsEps = 12;
      const _tsGx = forestClearance(newX + _tsEps, _tsFy, _tsSeed) - forestClearance(newX - _tsEps, _tsFy, _tsSeed);
      const _tsGy = forestClearance(newX, _tsFy + _tsEps, _tsSeed) - forestClearance(newX, _tsFy - _tsEps, _tsSeed);
      const _tsGl = Math.hypot(_tsGx, _tsGy);
      if (_tsGl > 0.0001) {
        const _tsTx = -_tsGy / _tsGl, _tsTy = _tsGx / _tsGl;
        const _tsDot = stepMoveX * _tsTx + stepMoveY * _tsTy;
        const _tsSx = _tsTx * _tsDot, _tsSy = _tsTy * _tsDot;
        if ((Math.abs(_tsSx) > 0.01 || Math.abs(_tsSy) > 0.01)
            && canMoveTo(state, newX + _tsSx, newY + _tsSy, player.size, playerIsEntity)) {
          newX += _tsSx; newY += _tsSy;
          _slideDbg = { x: newX, y: newY, sx: _tsSx, sy: _tsSy, t: state.time ?? 0 };   // [t] overlay arrow
        }
      }
    }
  }

   player.x = newX;
  player.y = newY;
  player.vx = totalMoveX / dt;
  player.vy = totalMoveY / dt;

  // BETA 21.33 — apply ENEMY KNOCKBACK shove (Dylan: "enemies should hit you and you
  // go flying"). Wall-collision checked so it can't push you THROUGH walls; decays
  // ~fast so control returns in ~0.5s and a swarm can't permanently pin you.
  // BETA 22.05 — but NEVER during an active dash: the dash overrides knockback so it stays a
  // reliable escape (it already cleared knockback on fire above; this also blocks any new shove
  // that lands mid-dash from cancelling the burst).
  if ((player.dashActiveTimer ?? 0) <= 0) {
    const _kbx = player.knockbackX ?? 0, _kby = player.knockbackY ?? 0;
    if (_kbx !== 0 || _kby !== 0) {
      const _sx = _kbx * dt, _sy = _kby * dt;
      if (canMoveTo(state, player.x + _sx, player.y, player.size, playerIsEntity)) player.x += _sx;
      if (canMoveTo(state, player.x, player.y + _sy, player.size, playerIsEntity)) player.y += _sy;
      // BETA 21.87 — Dylan: "enemies still not knocking me back." The shove was always
      // applied (it's wired), but it decayed ~94%/frame so the fling was over in a blink
      // AND the big screen shake (now -80%) masked it. Slower decay → the shove plays out
      // as a readable arc you can SEE, while still returning control in ~0.6s (no pinning).
      const _kdecay = Math.pow(0.06, dt);
      player.knockbackX = Math.abs(_kbx) < 6 ? 0 : _kbx * _kdecay;
      player.knockbackY = Math.abs(_kby) < 6 ? 0 : _kby * _kdecay;
    }
  }

  // ── BETA 15.2 — STUCK DETECTION + UNSTICK ──────────────────────────────
  // Per Dylan: "the bots are getting stuck on boundaries... walls in rooms.
  // maybe you can set up some code that makes that impossible to get stuck."
  //
  // Mechanism: after movement is applied, check if the player WANTED to move
  // (input non-zero) but actually didn't (delta < threshold). After ~0.5s of
  // sustained "wants to move but isn't moving", scan radially for the
  // nearest open cell and teleport the player there. Works for both bot AND
  // human; harmless to human players because it only fires after 0.5s of
  // zero movement WITH active input — which means they're stuck on geometry.
  const _stuckTracker = (state as unknown as { _unstickTracker?: { lastX: number; lastY: number; stuckFrames: number; cooldownTimer: number } })._unstickTracker
    ?? ((state as unknown as { _unstickTracker?: object })._unstickTracker = { lastX: player.x, lastY: player.y, stuckFrames: 0, cooldownTimer: 0 });
  const _ut = _stuckTracker as { lastX: number; lastY: number; stuckFrames: number; cooldownTimer: number };
  _ut.cooldownTimer = Math.max(0, _ut.cooldownTimer - dt);
  if (isMoving && _ut.cooldownTimer <= 0) {
    const _moveDelta = Math.hypot(player.x - _ut.lastX, player.y - _ut.lastY);
    if (_moveDelta < 0.5) {
      _ut.stuckFrames++;
    } else {
      _ut.stuckFrames = 0;
      _ut.lastX = player.x;
      _ut.lastY = player.y;
    }
    // 30 frames @ 60fps = ~0.5s of "stuck" → trigger unstick
    if (_ut.stuckFrames > 30) {
      // Radial scan: 8 directions × 5 radii (16/32/48/64/96 wu). Pick the
      // first open spot that's biased toward where the player wanted to go
      // (so we move WITH their intent, not against it).
      const angles = [0, Math.PI / 4, Math.PI / 2, (3 * Math.PI) / 4, Math.PI, (5 * Math.PI) / 4, (3 * Math.PI) / 2, (7 * Math.PI) / 4];
      const radii = [16, 32, 48, 64, 96];
      // Score each angle by alignment with desired direction (dx, dy).
      const wantAng = Math.atan2(dy, dx);
      let bestX = player.x, bestY = player.y, bestScore = -Infinity, found = false;
      for (const r of radii) {
        for (const a of angles) {
          const nx = player.x + Math.cos(a) * r;
          const ny = player.y + Math.sin(a) * r;
          if (!canMoveTo(state, nx, ny, player.size, playerIsEntity)) continue;
          // Score = closer to desired direction is better, AND smaller radii preferred (don't teleport farther than needed)
          const angDiff = Math.abs(((a - wantAng + Math.PI * 3) % (Math.PI * 2)) - Math.PI);
          const score = (Math.PI - angDiff) * 100 - r * 0.5;
          if (score > bestScore) {
            bestScore = score;
            bestX = nx;
            bestY = ny;
            found = true;
          }
        }
        if (found && bestScore > 0) break; // Early-exit: good enough at small radius
      }
      if (found) {
        player.x = bestX;
        player.y = bestY;
        _ut.lastX = bestX;
        _ut.lastY = bestY;
      }
      _ut.stuckFrames = 0;
      _ut.cooldownTimer = 0.5; // Don't re-fire for 0.5s after an unstick
    }
  } else if (!isMoving) {
    _ut.stuckFrames = 0;
    _ut.lastX = player.x;
    _ut.lastY = player.y;
  }
  // ───────────────────────────────────────────────────────────────────────
  // ── Update Flow Field (rebuilds when player moves to new cell) ── (BETA 13.70 FPS: timed)
  {
    const _t = performance.now();
    updateFlowField(dt, player.x, player.y);
    notePerf('flowfield', performance.now() - _t);
  }
  // Camera smooth follow
  const camLerp = 1 - Math.pow(0.001, dt);
  // BETA 22.97 — 80° top-down FEEL, part 1 (Dylan: "im still 100 percent top
  // down"): Greg sits slightly BELOW screen center (~55% height, per the map
  // rebuild pack) so the player sees more battlefield ahead. The camera aims
  // a touch ABOVE the player in world space to push him down on screen.
  const _camAheadY = BOUNDLESS_MAP ? 1080 * 0.07 / (camera.zoom || 1) : 0;   // v7 doc 14: Greg at 0.56-0.62 screen height (→ ~0.57); 1080 = design height
  camera.x += (player.x - camera.x) * camLerp;
  camera.y += (player.y - _camAheadY - camera.y) * camLerp;
  // BETA 22.96 — no camera clamp in the boundless map (the world has no edge).
  if (!BOUNDLESS_MAP) {
    camera.x = Math.max(0, Math.min(WS, camera.x));
    camera.y = Math.max(0, Math.min(WS, camera.y));
  }

  // ── BETA 20.74 — CINEMATIC RUN INTRO ──
  // Dylan: "i would rather an awesome transition and a zoom out of the map
  // and it zoom in to my character. more of a vibe." Replaces the green
  // GENERATING DUNGEON box as the run-start moment: the camera opens WIDE
  // over the dungeon and glides into the player over ~2.2s (easeInOutCubic —
  // slow reveal, fast travel, soft landing). Gameplay runs underneath; the
  // glide tracks the LIVE player position so moving during the intro is fine.
  // Overrides the smooth-follow above only while active, then hands back.
  if (state._introZoomT !== undefined) {
    const INTRO_DUR = 2.2;
    const INTRO_ZOOM_OUT = 0.45; // wide dramatic reveal (not the whole world — keeps tile-draw cost sane)
    // Capture the device-correct base zoom on the first frame (desktop
    // 1.38 / touch 1.0 / handheld 1.7) so the glide lands exactly there.
    if (state._introEndZoom === undefined) state._introEndZoom = camera.zoom;
    state._introZoomT += dt;
    const _t01 = Math.min(1, state._introZoomT / INTRO_DUR);
    const _ease = _t01 < 0.5 ? 4 * _t01 * _t01 * _t01 : 1 - Math.pow(-2 * _t01 + 2, 3) / 2;
    const _mapMid = WS / 2;
    camera.x = _mapMid + (player.x - _mapMid) * _ease;
    camera.y = _mapMid + (player.y - _mapMid) * _ease;
    camera.zoom = INTRO_ZOOM_OUT + (state._introEndZoom - INTRO_ZOOM_OUT) * _ease;
    if (_t01 >= 1) {
      camera.zoom = state._introEndZoom;
      state._introZoomT = undefined;
      state._introEndZoom = undefined;
    }
  }

  // Update current room
  player.currentRoom = findCurrentRoom(state.map.roomMap.rooms, player.x, player.y);

  // ── BETA 21.96 — GREEN GAS room timer (Dylan: "linger in a room >10s and green gas fills
  // it, chips only YOU; the gas mask lets you last longer"). Per-room timer resets when you
  // change rooms / step into open ground. Gentle % maxHP ramp so it nudges you to keep
  // moving without one-shotting. Drives a green edge-vignette indicator (renderGasVignette).
  {
    const _sg = state as unknown as { _gasTimer?: number; _gasRoom?: PlacedRoom | null; _gasLevel?: number };
    const _room = player.currentRoom;
    if (!_room) { _sg._gasTimer = 0; _sg._gasLevel = 0; }
    else {
      if (_room !== _sg._gasRoom) { _sg._gasTimer = 0; _sg._gasRoom = _room; }
      _sg._gasTimer = (_sg._gasTimer ?? 0) + dt;
      const _hasMask = getItemState()?.inventory?.some(s => s.item.id === 'item_dracs_gas_mask') ?? false;
      const _grace = _hasMask ? 30 : 10;                 // seconds before the gas bites
      const _past = _sg._gasTimer - _grace;
      if (_past > 0 && player.hp > 0) {
        const _dps = Math.min(player.maxHp * 0.09, player.maxHp * (0.018 + _past * 0.010)); // gentle ramp, % maxHP (ignores armor — it's gas)
        player.hp = Math.max(0, player.hp - _dps * dt);
        _sg._gasLevel = Math.min(1, _past / 6);          // indicator intensity (full ~6s after it starts)
      } else {
        _sg._gasLevel = Math.max(0, Math.min(0.28, (_sg._gasTimer / _grace) * 0.28)); // faint creeping pre-warning tint
      }
    }
  }

  // ── Update shrines (buff shrines only — boss shrines now use E-key interact) ──
  const justActivated = updateShrines(state.map.roomMap.shrines, player.x, player.y, dt * 1000);
  for (const shrineId of justActivated) {
    const shrine = state.map.roomMap.shrines.find(s => s.id === shrineId);
    if (!shrine || shrine.type === 'boss') continue;

    // Buff shrine — show buff selection UI
    markShrineSpent(shrine);
    // BETA 22.94 — BLUE burst (Dylan: no green/orange on shrines; golden
    // shrines keep their gold identity burst).
    const particleColor = shrine.golden ? '#ffd700' : '#4db8ff';
    spawnParticles(state.particles, shrine.x, shrine.y, particleColor, 30);
    // BETA 20.77 — pass luck so shrine rarities + buff NUMBERS scale with it.
    const hasOptions = generateShrineOptions(shrine.golden === true, state.player.luck ?? 0);
    if (hasOptions) {
      state.phase = 'shrine_choice';
    } else {
      const percResult = handleShrineActivation();
      if (percResult) {
        state.pickupNotifications = state.pickupNotifications || [];
        state.pickupNotifications.push({
          text: `PERC: ${percResult.percName}`,
          color: '#00ffcc',
          timer: 2.5,
        });
      }
    }
  }

  // ── Update interact targets ──
  state.interactTarget = null;
  state.interactPromptText = '';

  // Check chests
  for (const chest of state.map.roomMap.chests) {
    if (isPlayerNearMachine(chest, player.x, player.y)) {
      state.interactTarget = 'chest';
      // 22.89 — chests are WILD CARD MACHINES now
      if (chest.type === 'elite') {
        state.interactPromptText = '[E] ELITE WILD CARD MACHINE (FREE — High-End Pull)';
      } else if (chest.type === 'gold') {
        state.interactPromptText = '[E] WILD CARD MACHINE (FREE PULL)';
      } else {
        const cost = getCachedChestCost(state);
        state.interactPromptText = `[E] BUY WILD CARD (${cost} TOKENS)`;
      }
      break;
    }
  }

  // Check barrels
  if (!state.interactTarget) {
    for (const barrel of state.map.roomMap.barrels) {
      if (isPlayerNearBarrel(barrel, player.x, player.y)) {
        state.interactTarget = 'barrel';
        state.interactPromptText = '[E] Smash Barrel';
        break;
      }
    }
  }

  // BETA 23.28 — CHICK MAGNET STATUES DELETED (Dylan: "mega caseoh statue... removed immediately"). Surge Statues are the replacement; the pull mechanic returns through them later.

  // Check ultra boss chest
  if (!state.interactTarget && state.ultraChest && !state.ultraChest.collected) {
    if (Math.hypot(player.x - state.ultraChest.x, player.y - state.ultraChest.y) < 90) {
      state.interactTarget = 'ultra_chest';
      state.interactPromptText = '[E] ULTRA BOSS CHEST';
    }
  }

  // 23.39 — boss-shrine interact prompt DELETED (no boss shrines generate).

    // Check portal
  if (!state.interactTarget && state.portal) {
    if (Math.hypot(player.x - state.portal.x, player.y - state.portal.y) < 60) {
      state.interactTarget = 'portal';
      // BETA 16.53 — three states now: floor-advance ready (mega just
      // killed), final-boss in progress, normal active (summon mega).
      const sAny = state as unknown as Record<string, boolean | undefined>;
      const ws = enemiesModule?.getWaveState?.();
      const nextFloor = (ws?.currentFloor ?? 0) + 2; // 1-indexed for player display
      state.interactPromptText = sAny._floorAdvanceReady
        ? `[E] DESCEND TO FLOOR ${nextFloor}`
        : state.portal.active
          ? '[E] CHARGE PORTAL'
          : 'PORTAL';
    }
  }

  // Check extraction port — only prompt while it's open and NOT yet armed
  // (once armed the 60s timer runs anywhere, so the prompt is no longer useful).
  if (!state.interactTarget && state.extractionPort && state.extractionArmedAt == null) {
    if (Math.hypot(player.x - state.extractionPort.x, player.y - state.extractionPort.y) < EXTRACT_RADIUS) {
      state.interactTarget = 'extraction_port';
      state.interactPromptText = '[E] EXTRACT — start the 60s escape';
    }
  }


  // Animate interact prompt
  if (state.interactTarget) {
    state.interactPromptAlpha = Math.min(1, state.interactPromptAlpha + dt * 4);
  } else {
    state.interactPromptAlpha = Math.max(0, state.interactPromptAlpha - dt * 4);
  }

  // Preload images
  preloadImagesAround(state, player.x, player.y);

  // Update portal pulse
  if (state.portal) state.portal.pulsePhase += dt * 2;

  // Portal discovery — only show arrow/minimap after player gets close enough
  if (state.portal && !state.portalDiscovered) {
    const portalDist = Math.hypot(player.x - state.portal.x, player.y - state.portal.y);
    if (portalDist < 600) {
      state.portalDiscovered = true;
    }
  }

  // Update particles
  for (let i = state.particles.length - 1; i >= 0; i--) {
    const p = state.particles[i];
    // BETA 20.70 — gravity (debris sinks / gas rises) + spin (shard/star
    // tumble) + per-particle drag. Undefined fields = exact legacy motion.
    if (p.gravity) p.vy += p.gravity * dt;
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    const _drag = p.drag ?? 0.95;
    p.vx *= _drag;
    p.vy *= _drag;
    if (p.spin) p.angle = (p.angle ?? 0) + p.spin * dt;
    p.life -= dt;
    if (p.life <= 0) state.particles.splice(i, 1);
  }

  // ── Update loot orbs (vacuum pickup) ──
  if (state.lootOrbs) {
    const lootResult = updateLootOrbs(state.lootOrbs, dt, {
      x: player.x,
      y: player.y,
      magnetBonus: (state.player.pickupRange ?? 0) / 100,
      // BETA 23.6x — CO-OP PHASE 2 part D: machine-local collector id for the
      // owned-orb guard (orbBelongsTo). SP: 'p1' — evaluates true for all orbs.
      playerId: state.localPlayerId,
    });
    // BETA 21.42 — accumulate orbs collected this run. updateLootOrbs has always
    // returned orbsCollected but nothing totaled it, so magnet / pickup-range
    // effectiveness was invisible in telemetry — exactly why "pickup range never
    // felt like it changed" had no data to confirm against. Surfaced in the run
    // payload (livePlayTracker) as run.orbsCollected.
    if (lootResult.orbsCollected > 0) {
      const _sOrbAcc = state as unknown as { _orbsCollectedThisRun?: number };
      _sOrbAcc._orbsCollectedThisRun = (_sOrbAcc._orbsCollectedThisRun ?? 0) + lootResult.orbsCollected;
    }
    // Apply collected loot with SFX
    if (lootResult.xpGained > 0) {
      // BETA 16.24 — PSBOT cycle-25 P0.7. Per Dylan + PSBOT: when chick
      // magnet vacuums hundreds of orbs at once, all XP collects in one
      // frame → cascade of level-ups in a single tick → audio cascade
      // stomps the chick magnet stinger. Cap XP intake at 200 per frame;
      // queue the overflow to the next frame. Smooths multi-level-ups
      // and gives the audio queue (P0.6) a chance to fire each level-up
      // sound at a hearable cadence.
      const incomingXp = Math.round(lootResult.xpGained * (player.xpMultiplier ?? 1.0));
      _queuedXp += incomingXp;
      _xpGainAccum += incomingXp; // BETA 20.27 — feed the above-player gain popup
      // BETA 19.3 — Opt-in orb pickup logging. Enable via
      // `window.__debugOrbPickup = true` in DevTools to trace every XP
      // pickup → queue event during a run. Off by default so it doesn't
      // spam the console for normal play.
      try {
        if ((window as unknown as { __debugOrbPickup?: boolean }).__debugOrbPickup) {
          console.log(`[ORB-PICKUP] +${incomingXp} XP (raw ${lootResult.xpGained}, mult ${(player.xpMultiplier ?? 1).toFixed(2)}). queue=${_queuedXp} player.xp=${state.player.xp}/${state.player.xpToNext} phase=${state.phase}`);
        }
      } catch { /* */ }
      playXPPickup();
      // BETA 14.56 — eventLog push (pickup event: xp)
      pushEventLog({ t: state.time ?? 0, type: 'pickup', data: { kind: 'xp', amount: lootResult.xpGained } });
      // BETA 20.57 — Peanut's Big Brain (R09) reworked. XP orbs no longer BURST
      // on pickup (that blue ring fired on every orb grab — visually spammy).
      // Instead, XP orbs sitting on the ground are hazards that damage enemies
      // who walk into them — handled in the per-frame tick in updateGame (search
      // _bigBrainTimer).
      // BETA 14.62 — WriteThatDown synergies fire on XP pickup when equipped:
      // writeThatDown_chestBuilds (XP race with chest builds)
      // pubertyBlockers_writeThatDown (long-run scaling stack)
      const _invXp = getItemState()?.inventory;
      if (_invXp?.some(s => s.item.id === 'item_write_that_down')) {
        const xpAmt = Math.round(lootResult.xpGained * (player.xpMultiplier ?? 1.0));
        trackItemProc('item_write_that_down');
        // chestBuilds is wildcard "*" — fires whenever WTD is equipped at all.
        trackSynergyTrigger('writeThatDown_chestBuilds', xpAmt);
        const _ps = getPercState();
        if (_ps?.slots.some(s => s.perc?.id === 'N07' && s.level > 0)) {
          trackSynergyTrigger('pubertyBlockers_writeThatDown', xpAmt);
        }
      }
    }
    if (lootResult.goldGained > 0) {
      {
        // BETA 19.99 — lifetime gold accumulator (livePlayTracker reads
        // _goldCollectedThisRun so payload reports total earned, not current wallet).
        const _gGain = Math.round(lootResult.goldGained * (player.goldMultiplier ?? 1.0));
        player.gold += _gGain;
        _goldGainAccum += _gGain; // BETA 20.27 — feed the above-player gain popup
        const _sAnyG = state as unknown as Record<string, number | undefined>;
        _sAnyG._goldCollectedThisRun = (_sAnyG._goldCollectedThisRun ?? 0) + _gGain;
      }
      playGoldPickup();
      // BETA 14.56 — eventLog push (pickup event: gold)
      pushEventLog({ t: state.time ?? 0, type: 'pickup', data: { kind: 'gold', amount: lootResult.goldGained } });
    }
    if (lootResult.healthGained > 0) {
      // BETA 20.00 — UN-GATED from Debt (Dylan: healing keeps running).
      // hp > 0 guard: a health orb must not revive an already-dead player
      // before the death check runs.
      // BETA 21.18 — Dylan's "just a nudge": health orbs no longer heal DURING
      // combat (same 5s post-hit gate as regen), so they're between-fight recovery
      // instead of an instant in-fight top-off. The orb is still collected
      // (XP/gold above) + pickup SFX still plays. (22.90: Debt bypass deleted.)
      if (player.hp > 0 && (state._postDamageTimer ?? 0) <= 0) {
        const _hpBeforeOrb = player.hp;
        player.hp = Math.min(player.maxHp, player.hp + lootResult.healthGained);
        // BETA 20.77 — audit fix: health-orb heals were INVISIBLE to the
        // healing ledger (runHealingReceived under-counted; the real ratio
        // in Dylan's run was above the recorded 96%).
        const _orbHealed = player.hp - _hpBeforeOrb;
        if (_orbHealed > 0) {
          const _sAnyOrb = state as unknown as Record<string, number | undefined>;
          _sAnyOrb._healingReceivedThisRun = (_sAnyOrb._healingReceivedThisRun ?? 0) + _orbHealed;
          state._healEventsThisRun = (state._healEventsThisRun ?? 0) + 1;
        }
      }
      playHealthPickup();
      // BETA 14.56 — eventLog push (pickup event: health)
      pushEventLog({ t: state.time ?? 0, type: 'pickup', data: { kind: 'health', amount: lootResult.healthGained } });
    }
  }

  // ── BETA 20.27 — Consolidated gold + XP gain popups above the player ──
  // Dylan: show gold/XP gains in real time above my character (not scattered
  // per-enemy). Damage/crit hit markers above enemies are untouched. We batch
  // the intake and flush at most ~2 popups/sec, so a chick-magnet vacuum of
  // hundreds of orbs in one frame becomes a single satisfying "+450 XP" —
  // zero per-orb cost, no clutter. Reuses the pooled damageNumbers renderer.
  _gainPopupTimer -= dt;
  if (_gainPopupTimer <= 0 && (_xpGainAccum > 0 || _goldGainAccum > 0)) {
    const _wsGain = getWeaponSystemState();
    if (_wsGain) {
      if (_xpGainAccum > 0) {
        spawnDamageNumber(_wsGain, {
          x: player.x, y: player.y - 56, value: 0, isBuff: true,
          label: `+${_xpGainAccum} XP`, color: '#5fd6ff', timer: 0.85, velocityY: -44,
        });
      }
      if (_goldGainAccum > 0) {
        spawnDamageNumber(_wsGain, {
          x: player.x, y: player.y - 38, value: 0, isBuff: true,
          label: `+${_goldGainAccum} GOLD`, color: '#ffc23f', timer: 0.85, velocityY: -44,
        });
      }
    }
    _xpGainAccum = 0;
    _goldGainAccum = 0;
    _gainPopupTimer = 0.5;
  }

  // ── Check level up ──
  // BETA 18.19 — Single source of truth: xpRequiredForLevel() from
  // peanutPercSystem (formula `15 * level^1.6`). Pre-18.19 the engine
  // computed this inline AND the perc system had its own shadow formula —
  // they didn't match. Curve unchanged from the engine's original math.
  if (player.xp >= player.xpToNext && state.phase === 'playing') {
    player.xp -= player.xpToNext;
    player.level++;
    player.xpToNext = xpRequiredForLevel(player.level);
    // BETA 21.39 — the per-level pickup growth used to live here as a one-off mutation,
    // but applyAllBonusesToPlayer resets player.pickupRange to 0 EVERY FRAME, so it was
    // wiped instantly (Dylan: "never felt any change"). Moved into that every-frame
    // rebuild (itemsAdapter ~line 1946) keyed off player.level so it actually sticks.
    // BETA 15.89 — Per Dylan: "chest should be pause too same with level
    // up. so we can here the audio for all those when it happens."
    // True pause (not duck). Resumes when player picks a card or skips
    // (handleLevelUpSelection / level-up skip handler in GameCanvas).
    startMuffledDuck(); // BETA 16.12 — was pauseBgm
    // BETA 14.55 — eventLog push (levelup event)
    pushEventLog({ t: state.time ?? 0, type: 'levelup', data: { level: player.level } });
    // Trigger level-up choice screen via itemsAdapter.
    // BETA 14.05 — In Weapon Lab with "skip level-ups" toggled on, skip the
    // choice screen entirely. Player still levels (XP / stat bonuses still
    // count) but the UI doesn't interrupt testing. User: "i want to toggle
    // off leveling up so i can just test the weapons."
    if (itemsModule && !(isLabMode() && isLabSkipLevelUp())) {
      state.phase = 'levelup';
    }
  }

  // ── Check player death ──
  if (player.hp <= 0 && state.phase === 'playing') {
    // BETA 16.24 — PSBOT cycle-25 P0.S2: snapshot hpAtDeath AT THIS
    // moment (before death replay slow-mo + regen/lifesteal can patch
    // HP back up). livePlayTracker.buildLiveRun reads the captured value
    // instead of state.player.hp at finalize time. Fixes the cycle-25
    // audit finding "hpAtDeath: 129 with a lying single-source deathCause".
    // Capture is idempotent on the FIRST lethal moment per run; revive
    // interceptors below don't overwrite it.
    captureLiveHpAtDeath(player.hp);
    // BETA 14.55 — eventLog push (death event). Fires once at the moment of
    // death; revive interceptors (Rage Quit, Sheeesh, Last Stand) run after
    // and may save the player but the event itself is recorded.
    pushEventLog({
      t: state.time ?? 0,
      type: 'death',
      data: { x: player.x, y: player.y, level: player.level },
    });
    // BETA 14.57 — Capture deathContext at the moment of death.
    // Last 5 hits come from the sliding window we've been recording every
    // HP drop into. Distance to nearest enemy + count in contact range read
    // from current state.enemies. iframe state from invincibleTimer.
    let nearestEnemyDist = Infinity;
    let inContactRange = 0;
    const enemies = enemiesModule?.getEnemies() ?? [];
    for (const e of enemies) {
      if (!e.alive) continue;
      const dx = e.x - player.x;
      const dy = e.y - player.y;
      const distSq = dx * dx + dy * dy;
      if (distSq < nearestEnemyDist) nearestEnemyDist = distSq;
      if (distSq < 64 * 64) inContactRange++; // 64wu = "contact range"
    }
    const lastHit = _recentHits.length > 0 ? _recentHits[_recentHits.length - 1] : null;
    // BETA 22.48 — DEATH CLARITY (Dylan: "i never see an enemy hit me when i
    // die. its very confusing. is it possible to have code to know if i was
    // actually supposed to die"). Measure the GAP between the last recorded
    // HP-drop and this death frame. A "phantom" death = no hit at all, OR the
    // last hit landed > 1.5s ago (so the killing blow wasn't a fresh enemy
    // strike — usually a DoT/gas tick or an off-screen projectile you never
    // saw, sometimes a bug). We build a plain-English explanation for the
    // death screen and tag the run so every death is auditable.
    const _now = state.time ?? 0;
    const _fatalAge = lastHit ? Math.max(0, _now - lastHit.t) : -1;
    const _phantom = !lastHit || _fatalAge > 1.5;
    setDeathContext({
      hpAtDeath: player.hp,
      fatalHitSource: lastHit?.source ?? 'unknown',
      fatalHitDmg: lastHit?.dmg ?? 0,
      last5HitsBeforeDeath: _recentHits.slice(),
      distanceToNearestEnemy: Math.sqrt(nearestEnemyDist === Infinity ? 0 : nearestEnemyDist),
      enemiesInContactRangeAtDeath: inContactRange,
      iframeStateAtDeath: player.invincibleTimer ?? 0,
      fatalHitAgeSec: _fatalAge,
      deathWasPhantom: _phantom,
    });
    state.fatalHitAgeSec = _fatalAge;
    state.deathWasPhantom = _phantom;
    // BETA 20.90 — PSBOT cycle-30bi: deathCause / killedBy* MUST agree with
    // fatalHitSource. Dylan died to a boss slam but the screen said "Timmy
    // (throw)": deathCause was set in enemyAdapter from the single global
    // peekLastDamageSource(), which goes stale across multi-attacker frames (4
    // Timmy throws, then the boss blow 3s later). The _recentHits ledger
    // ordered it correctly (boss last) — so derive killedBy* from the SAME
    // authoritative last hit here, overriding the global-based guess. Runs on
    // every hp<=0 (incl. saves), so the FINAL death's true killer wins.
    if (lastHit?.source) {
      const _trueKiller = _resolveKillerFromSource(lastHit.source, { enemies });
      if (_trueKiller) {
        state.killedByType = _trueKiller.type;
        state.killedByVariant = _trueKiller.variant;
        state.killedByName = _trueKiller.name;
      }
    }
    // BETA 22.48 — build the plain-English death explanation now that killedBy*
    // is resolved. This is what the death screen shows under "Killed by X".
    {
      const _who = state.killedByName || 'Something';
      const _how = lastHit ? _deathHowFromSource(lastHit.source) : 'unknown source';
      const _dmg = Math.round(lastHit?.dmg ?? 0);
      const _ageTxt = _fatalAge >= 0 ? `${_fatalAge.toFixed(1)}s ago` : 'never';
      if (!lastHit) {
        state.deathExplanation = 'No hit recorded — environmental or a tracking gap';
      } else if (_phantom) {
        // stale: the last damage was a while back → not a fresh on-screen strike
        state.deathExplanation = `No fresh hit — last was ${_who} (${_how}) ${_ageTxt}`;
      } else {
        state.deathExplanation = `${_who} — ${_how} for ${_dmg}`;
      }
      // one-shot console line so phantom deaths are visible while testing
      if (!_deathClarityLogged) {
        _deathClarityLogged = true;
        if (_phantom) {
          console.warn(`[death] PHANTOM? hp${Math.round(player.hp)} · ${state.deathExplanation} · lastHits=`, _recentHits.slice());
        } else {
          console.log(`[death] ${state.deathExplanation} · hp${Math.round(player.hp)}`);
        }
      }
    }
    // BETA 22.90 — N10 Debt on-death gold save DELETED (Dylan: "delete the
    // old gold stuff"). N10 is HEART THROB now — a proximity knockback
    // shockwave (per-frame block above) with no death-save component. Lethal
    // damage falls straight through to Rage Quit / Sheeesh / Liar Deceiver.

    // BETA 19.94 — Reverted 19.91 revive cap. Dylan: "we dont cap things."
    // Each revive item still has its own one-shot-per-run gate (Sheeesh,
    // Liar, Rage Quit, Last Stand are each individually gated). Stacking
    // all 3 collectible revive items + Last Stand = 4 saves, but the
    // PLAYER had to earn them (epic+ items + ultra chest). Power earned
    // through build commitment is the design intent.
    const _sAnyRev = state as unknown as { _totalRevivesThisRun?: number };
    const _revivesUsed = _sAnyRev._totalRevivesThisRun ?? 0;
    void _revivesUsed; // kept for telemetry parity
    // Rage Quit Aura: intercept death once per run
    if (player.ultraState?.rageQuit_available) {
      player.ultraState.rageQuit_available = false;
      player.ultraState.rageQuit_used = (player.ultraState.rageQuit_used ?? 0) + 1; // BETA 20.39 — telemetry
      _sAnyRev._totalRevivesThisRun = _revivesUsed + 1; // BETA 19.91 — count toward 2/run cap
      player.hp = Math.max(1, Math.round(player.maxHp * 0.3));
      // BETA 20.19 — Rage Quit Aura redesign (Dylan): WIPE the whole map
      // (20,000 dmg to EVERY enemy, no radius limit), 3s of blinking
      // invulnerability, and all XP/gold zooms to you (chick-magnet). The
      // rageQuit_vacuumTimer below keeps pulling for 3s so the loot dropped by
      // the wipe also vacuums in (it spawns after the kills resolve).
      player.invincibleTimer = 3.0;
      for (const e of (enemiesModule?.getEnemies() ?? [])) {
        enemiesModule?.damageEnemy?.(e.id, 20000, { x: 0, y: 0 });
      }
      player.ultraState.rageQuit_vacuumTimer = 3.0;
      if (state.lootOrbs) vacuumAllOrbs(state.lootOrbs, state.localPlayerId); // BETA 23.6x — CO-OP PHASE 2 part D: owned-orb guard (local magnet)
      state.screenShakeTimer = 1.0;
      state.screenShakeIntensity = 40;
      state.lastStandFlashTimer = 1.0;
      // BETA 20.72 — legacy 90-circle burst REMOVED: the map-wipe shockwave
      // (staggered white rings + red wash + 50 tumbling debris shards) now
      // fires from ultraItems.ts where the wipe is detected.
      state.pickupNotifications.push({ text: 'RAGE QUIT — MAP WIPED!', color: '#ff2200', timer: 3.0 });
    }
    // BETA 13.99 / 20.87 — Sheeesh: intercept death, revive at 25% HP. Dylan:
    // "Each Sheeesh = its own revive." Was a one-shot boolean (2 copies still
    // gave 1 revive). Now gated on how many you OWN vs how many have fired.
    else if (
      _sheeeshRevivesUsed < (getItemState()?.inventory.find(s => s.item.id === 'item_sheeesh')?.stacks ?? 0)
    ) {
      _sheeeshRevivesUsed++;
      _sAnyRev._totalRevivesThisRun = _revivesUsed + 1; // BETA 19.91 — count toward 2/run cap
      // BETA 14.30 Phase 1 — revive event counter
      state._reviveEventsThisRun = (state._reviveEventsThisRun ?? 0) + 1;
      // BETA 14.61 — item proc tracker for Sheeesh
      trackItemProc('item_sheeesh');
      player.hp = Math.max(1, Math.round(player.maxHp * tuneOf('item_sheeesh', 'reviveHpPct', 0.25))); // BETA 21.79 — editable
      player.invincibleTimer = tuneOf('item_sheeesh', 'invulnSeconds', 2); // BETA 21.79 — editable
      state.screenShakeTimer = 0.5;
      state.screenShakeIntensity = 20;
      state.lastStandFlashTimer = 1.0;
      spawnParticles(state.particles, player.x, player.y, '#ff66cc', 50);
      // BETA 16.55 — PSBOT cycle-30m. Dylan: "if sheeesh saved me i need a
      // popup says SHEEESH with a heart emoji or something and an audio
      // that it activated." Audio cue + screen-center pink heart popup.
      playSheeeshActivate();
      state.pickupNotifications.push({
        text: '❤️ SHEEESH! ❤️',
        color: '#ff66cc',
        timer: 5.0,
        initialTimer: 5.0,
        fontSize: 48,
        position: 'screen_center',
      });
    }
    // BETA 14.04 — Liar Deceiver False Prophet: second-revive intercept (epic
    // item). Stacks WITH Sheeesh — Sheeesh fires first if both equipped (it
    // gives 25% HP back); Liar fires only if Sheeesh has already been used
    // this run (or isn't equipped) and we'd otherwise die. Different from
    // Sheeesh: doesn't refill HP (sets to 1), repositions player 80wu in a
    // random direction (decoy fakeout), grants 5s invuln. Purple particle burst.
    else if (getItemState()?.inventory.find(s => s.item.id === 'item_liar_deceiver_false_prophet') && !state._liarUsedThisRun) {
      state._liarUsedThisRun = true;
      _sAnyRev._totalRevivesThisRun = _revivesUsed + 1; // BETA 19.91 — count toward 2/run cap
      // BETA 14.30 Phase 1 — revive event counter
      state._reviveEventsThisRun = (state._reviveEventsThisRun ?? 0) + 1;
      // BETA 14.61 — item proc tracker for Liar Deceiver
      trackItemProc('item_liar_deceiver_false_prophet');
      // BETA 14.61 — synergy trigger: sheeesh_liarDeceiver fires when BOTH
      // revives have been used this run (Liar fires after Sheeesh has been
      // consumed). valueDelta = 1 (one extra revive granted by the pair).
      if (_sheeeshRevivesUsed > 0) {
        trackSynergyTrigger('sheeesh_liarDeceiver', 1);
      }
      player.hp = 1;
      // BETA 22.06 — capture the DEATH spot before the decoy warp: the fake-you left behind
      // HERE is what DETONATES (the nuke), while real-you warps to safety below.
      const _liarBlastX = player.x, _liarBlastY = player.y;
      player.invincibleTimer = tuneOf('item_liar_deceiver_false_prophet', 'invulnSeconds', 5); // BETA 21.79 — editable
      // BETA 15.37 — wall-safe teleport. Per Dylan: "liar deciver false profit
      // is not working as well." Root cause: the original teleport just did
      // `player.x += cos(angle)*80` without a walkability check. If the random
      // angle pointed into a wall (~50% chance in tight rooms), the player got
      // stuck inside geometry — 5s invuln expired and they died next hit, so
      // the revive looked broken.
      //
      // Fix: try 8 cardinal+diagonal angles starting from a random offset.
      // Pick the first one that lands in a walkable spot. If none of the 8
      // work (player surrounded by walls, e.g. dead-center of a corner clip),
      // skip the teleport — the revive still fires (HP=1, invuln=5s) but
      // without repositioning. Better than getting stuck inside a wall.
      const _decoyDist = tuneOf('item_liar_deceiver_false_prophet', 'decoyDistance', 80); // BETA 21.79 — editable
      const _angleOffset = Math.random() * Math.PI * 2;   // random starting offset
      let _placed = false;
      for (let i = 0; i < 8; i++) {
        const ang = _angleOffset + (i * Math.PI / 4);     // cardinal+diagonal sweep
        const tx = player.x + Math.cos(ang) * _decoyDist;
        const ty = player.y + Math.sin(ang) * _decoyDist;
        if (canMoveTo(state, tx, ty, player.size, true)) {
          player.x = tx;
          player.y = ty;
          _placed = true;
          break;
        }
      }
      // If all 8 angles were walls, leave player in place — at least they
      // still have 1 HP + 5s invuln to escape the immediate threat.
      void _placed;
      // BETA 22.06 — THE NUKE (Dylan): the decoy you leave behind DETONATES. A huge bomb
      // centered on the death spot — massive AoE that KILLS anything it touches in the radius,
      // EXCEPT mega/final bosses (they take the hit but can't be KILLED by it, so you can't
      // suicide-cheese a boss). Damage + radius are dashboard-tunable. Kills drop loot via the
      // 22.03 damageEnemy wrapper.
      {
        const _blastDmg = tuneOf('item_liar_deceiver_false_prophet', 'blastDamage', 5000);
        const _blastR = tuneOf('item_liar_deceiver_false_prophet', 'blastRadius', 520);
        const _bR2 = _blastR * _blastR;
        for (const _be of (enemiesModule?.getEnemies() ?? [])) {
          const _bdx = _be.x - _liarBlastX, _bdy = _be.y - _liarBlastY;
          if (_bdx * _bdx + _bdy * _bdy > _bR2) continue;              // outside the blast
          const _isMegaBoss = _be.variant === 'boss';   // 23.39 — variant is the only mega signal now
          // mega/final boss: deal damage but NEVER the killing blow (clamp to leave >=1 HP).
          const _bDmg = _isMegaBoss ? Math.max(0, Math.min(_blastDmg, (_be.hp ?? 1) - 1)) : _blastDmg;
          if (_bDmg <= 0) continue;
          const _bd = Math.hypot(_bdx, _bdy) || 1;
          enemiesModule?.damageEnemy?.(_be.id, _bDmg, { x: (_bdx / _bd) * 80, y: (_bdy / _bd) * 80 }); // flung outward
        }
      }
      // ── NUKE VFX: shockwave rings → fireball → fire sparks → rising mushroom smoke → debris ──
      spawnParticles(state.particles, _liarBlastX, _liarBlastY, '#ffffff', 5, { shape: 'ring', speedMin: 0, speedMax: 0, sizeMin: 28, sizeMax: 96, lifeMin: 0.3, lifeMax: 0.6 });
      spawnParticles(state.particles, _liarBlastX, _liarBlastY, '#ffe066', 80, { shape: 'mote', speedMin: 60, speedMax: 560, lifeMin: 0.3, lifeMax: 0.85, sizeMin: 4, sizeMax: 13 });
      spawnParticles(state.particles, _liarBlastX, _liarBlastY, '#ff5a1f', 56, { shape: 'spark', stretch: 6, speedMin: 140, speedMax: 640, lifeMin: 0.25, lifeMax: 0.6 });
      spawnParticles(state.particles, _liarBlastX, _liarBlastY, '#9a9a9a', 64, { shape: 'bubble', gravity: -95, speedMin: 30, speedMax: 170, lifeMin: 1.1, lifeMax: 2.1, sizeMin: 12, sizeMax: 28 }); // rising stem/cloud
      spawnParticles(state.particles, _liarBlastX, _liarBlastY, '#4f4f4f', 44, { shape: 'bubble', gravity: -62, speedMin: 10, speedMax: 95, lifeMin: 1.3, lifeMax: 2.3, sizeMin: 16, sizeMax: 38 }); // billowing dark cap
      spawnParticles(state.particles, _liarBlastX, _liarBlastY, '#2e211a', 30, { shape: 'shard', spin: 8, spinRandomSign: true, gravity: 300, speedMin: 110, speedMax: 380, lifeMin: 0.5, lifeMax: 1.0, sizeMin: 3, sizeMax: 7 }); // debris
      state.screenShakeTimer = 1.0;
      state.screenShakeIntensity = 38;
      state.lastStandFlashTimer = 1.0;
      // BETA 16.55 — audio cue + screen-center popup. BETA 22.06 — + explosion sound.
      playLiarDeceiverActivate();
      playLiarNukeBlast(_liarBlastX, _liarBlastY);   // BETA — its own nuke blast (was the Anus Rocket sound)
      state.pickupNotifications.push({
        text: '☢️ LIAR DECEIVER — NUKE! ☢️',
        color: '#ff7733',
        timer: 4.0,
        initialTimer: 4.0,
        fontSize: 42,
        position: 'screen_center',
      });
    }
    // BETA 14.17 — Lab respawn instead of gameover. Logs the death event
    // (timestamp + cause) so WeaponLab CSV can track deaths per build, then
    // resets player to full HP with 1.5s invuln so they can keep testing
    // without losing the build/CSV row. User: "i should be able to take
    // damage from enemies... track deaths and how long i lasted, what i
    // died from etc."
    // BETA 19.86 — Dylan: "when the bot dies it just respons imediatly. you
    // track how many deaths but it goes to infinity with all things and
    // buffs still on character so you can run the fucking test!!!!!"
    // Same exact behavior as lab respawn — full HP, keep build, clear arena,
    // bump death counter — gated on `window.__replaySessionActive` set by
    // ReplayLauncherPanel. Bot never goes to gameover during a replay so
    // the auto-restart 'r' key path never fires (which was wiping the build).
    else if (isLabMode() || ((window as Window & { __replaySessionActive?: boolean }).__replaySessionActive === true)) {
      const cause = state.killedByName ?? state.killedByType ?? 'unknown';
      const deathNum = pushLabDeath({ atSec: state.time, cause });
      // Respawn at full HP + brief invuln + reset shield + clear killed-by
      player.hp = player.maxHp;
      player.invincibleTimer = 1.5;
      state.killedByName = undefined;
      state.killedByType = undefined;
      state.killedByVariant = undefined;
      state.lastStandFlashTimer = 0.6;
      spawnParticles(state.particles, player.x, player.y, '#ffaa00', 40);
      state.pickupNotifications.push({ text: `LAB RESPAWN (death #${deathNum} — ${cause})`, color: '#ffaa00', timer: 2.5 });
      playPlayerDeath();
      // BETA 17.78 — Dylan: "something is bugged" (boss pile-up). Lab
      // respawn was preserving ALL alive enemies including accumulated
      // bosses from prior life-cycles. Over a 39-min session that piled
      // up 7+ bosses. On respawn, kill every enemy so the arena resets
      // for the next life. Build / items / level / gold / state.time all
      // STAY (lab purpose). Just the enemy field is cleared.
      for (const e of state.enemies) {
        if (e.alive) {
          e.alive = false;
          e.hp = 0;
        }
      }
    }
    else {
      // BETA 16.21 — PSBOT cycle-24 P0.3: 5-second slow-motion death replay
      // before the RIP PEANUT screen. Sets phase='death_replay' instead of
      // 'gameover' on the lethal frame; the replay tick at the top of
      // updateGame slows dt × 0.2, smooth-zooms the camera 1.7×, and after
      // 5s of REAL time triggers the original gameover transition (audio
      // swap + telemetry + RIP screen). Bots skip the replay and go
      // straight to gameover so headless runs aren't slowed.
      const _botActive = isBotEnabled();
      if (_botActive) {
        // BETA 16.00 — REORDERED + DEFENSIVE WRAP. Per Dylan in 15.99
        // testing: "main game music did not stop when i died. death song
        // does not start. all i still see are the stars". Hypothesis:
        // captureRunSnapshot or finalizeLiveRun was throwing an unhandled
        // error BEFORE the stopBgm() / playDeathEulogy() calls could run,
        // so music stayed playing and eulogy never started. Stars still
        // rendered because renderDeathScreen runs in renderGame each frame
        // regardless of whether the gameover transition's later calls
        // succeeded. Now: phase set + audio-switch FIRST, then telemetry
        // + snapshot wrapped in try/catch so they can fail silently
        // without breaking the audio swap.
        state.phase = 'gameover';
        state.gameOverStartTime = Date.now();
        try { playPlayerDeath(); } catch (e) { console.warn('[gameover] playPlayerDeath threw', e); }
        console.log('[gameover-transition] bot mode — skipping eulogy + death screen');
        try { captureRunSnapshot(state); } catch (e) { console.warn('[gameover] captureRunSnapshot threw', e); }
        // BETA 20.67 — deep-dive CRITICAL #7: do NOT finalizeLiveRun here.
        // The bot's own pipeline (autoPlaytester finalizeRun → recordBotRunEvent)
        // posts the rich row; this call double-posted a second, thinner row for
        // EVERY bot run (and in a normal tab both landed as live_play,
        // contaminating human data). Bot pipeline owns bot rows now.
      } else {
        // Live player: enter death_replay phase. Replay tick runs at top of
        // updateGame; it transitions to 'gameover' after 5s and fires the
        // original audio + telemetry chain at that point.
        state.phase = 'death_replay';
        const sAny = state as unknown as {
          _deathReplayStartedAt?: number;
          _deathReplayInitialZoom?: number;
          _deathReplayTargetZoom?: number;
          _deathReplayElapsedSec?: number;
          _deathReplayHpStart?: number;
          _deathReplayKillerLabel?: string;
        };
        sAny._deathReplayStartedAt = state.time ?? 0;
        sAny._deathReplayInitialZoom = state.camera.zoom;
        sAny._deathReplayTargetZoom = state.camera.zoom * 1.7;
        sAny._deathReplayElapsedSec = 0;
        // BETA 16.25 — PSBOT cycle-26 P0.2: capture pre-fatal-hit HP so
        // the bar can visibly drain to 0 during the slow-mo (Dylan: "it
        // showed i was full health... so why did i die?"). _prevAliveHp
        // was last updated ON THE PREVIOUS FRAME with the player's HP
        // before the lethal damage applied this frame.
        sAny._deathReplayHpStart = _prevAliveHp;
        sAny._deathReplayKillerLabel = state.killedByName ?? state.killedByType ?? 'unknown';
        // Hard duck during replay (no synth death-thump until replay ends —
        // playPlayerDeath fires at the gameover transition for max impact).
        try { startMuffledDuck(0.35); } catch (e) { console.warn('[death_replay] duck threw', e); }
        // BETA 16.25 — Dylan: "make sure i actually should have died."
        // Diagnostic log at activation moment so we can verify post-hoc
        // that the death detection wasn't a false positive (HP <= 0
        // confirmed, lethal hit source captured). If a future report
        // shows death_replay firing with hpAtDeath > 0 we know there's a
        // detection bug to investigate.
        const _lastHit = _recentHits.length > 0 ? _recentHits[_recentHits.length - 1] : null;
        console.log('[death_replay] activated', {
          hpAtDeath: state.player.hp,
          hpBeforeFatalHit: _prevAliveHp,
          fatalSource: _lastHit?.source ?? 'unknown',
          fatalDmg: _lastHit?.dmg ?? 0,
          killerName: state.killedByName,
          killerType: state.killedByType,
          inviolableTimer: state.player.invincibleTimer,
        });
      }
    }
  }

  // ── Final boss win condition / Floor-2 transition ──
  // BETA 16.53 — Per Dylan: "if we kill this casoeh the boss portal allows
  // you to press E again while standing in it and then you go to level 2."
  // Mega kill no longer ends the run via 'won' — instead it flags the
  // portal for the floor-2 advance. Player presses E in the portal to
  // actually transition.
  if (state.finalBossActive && state.finalBossEnemyIds.length > 0) {
    // BETA 21.42 — Dylan: activating the portal gave the boss-KILL reward (orb vacuum +
    // ultra chest + XP/gold) BEFORE the mega was killed. Cause: state.enemies is a
    // per-frame COPY rebuilt later in the update (~line 4013), so on the mega's spawn
    // frame it isn't in state.enemies yet → `.every(not-alive)` is spuriously TRUE →
    // the kill reward fires the instant the boss appears. Fix: latch _finalBossSeenAlive
    // and only pay out once the mega has actually been observed alive at least once.
    const _sBoss = state as unknown as { _finalBossSeenAlive?: boolean };
    if (state.finalBossEnemyIds.some(id => state.enemies.find(e => e.id === id && e.alive))) {
      _sBoss._finalBossSeenAlive = true;
    }
    const allDead = state.finalBossEnemyIds.every(
      id => !state.enemies.find(e => e.id === id && e.alive)
    );
    if (allDead && _sBoss._finalBossSeenAlive) {
      _sBoss._finalBossSeenAlive = false;
      state.finalBossActive = false;
      const sAny = state as unknown as Record<string, boolean | undefined>;
      // BETA 17.12 — PSBOT cycle-30ad Bundle 3 Floor 1 diagnostic.
      // Increment mega-boss-killed counter so we can see in CSV whether
      // the floor progression bottleneck is "can't kill mega" or "killed
      // mega but didn't reach portal".
      const _sAnyFloor = state as unknown as Record<string, number | undefined>;
      _sAnyFloor._megaBossKilledThisRun = (_sAnyFloor._megaBossKilledThisRun ?? 0) + 1;
      const currentFloor = enemiesModule?.getWaveState?.()?.currentFloor ?? 0;
      // BETA 17.15 — PSBOT cycle-30af Dylan-direct: boss kill celebration.
      // "when i killed the main boss all xp and gold should have come to me."
      // Spawn a big XP+gold drop scaled to current floor. Plus particle
      // burst + screen flash + shake so the moment FEELS earned.
      // Floor 1 boss: 3000 XP + 1500 gold. Floor 2: 6000 + 3000. Floor 3: 12k + 6k.
      const _floorMult = Math.pow(2, currentFloor); // 1, 2, 4
      const _bossXp = 3000 * _floorMult;
      const _bossGold = 1500 * _floorMult;
      state.player.xp += _bossXp;
      state.player.gold += _bossGold;
      {
        // BETA 19.99 — also feed lifetime accumulator.
        const _sAnyBG = state as unknown as Record<string, number | undefined>;
        _sAnyBG._goldCollectedThisRun = (_sAnyBG._goldCollectedThisRun ?? 0) + _bossGold;
      }
      // Big celebration VFX at last-known mega position (use player as fallback)
      const _bossX = state.player.x;
      const _bossY = state.player.y;
      spawnParticles(state.particles, _bossX, _bossY, '#ffd700', 200, { shape: 'star', spin: 5, spinRandomSign: true, speedMin: 40, speedMax: 360, lifeMin: 0.6, lifeMax: 1.6, sizeMin: 3, sizeMax: 8 });  // BETA 20.70 — mega-boss death = a TREASURE explosion of gold stars
    spawnParticles(state.particles, _bossX, _bossY, '#ffffff', 4, { shape: 'ring', speedMin: 0, speedMax: 0, sizeMin: 18, sizeMax: 22, lifeMin: 0.5, lifeMax: 0.5 });  // + a white shockwave ring
      state.screenShakeTimer = Math.max(state.screenShakeTimer, 1.5);
      state.screenShakeIntensity = Math.max(state.screenShakeIntensity, 50);
      state.lastStandFlashTimer = 1.5;
      // Notifications
      state.pickupNotifications?.push({
        text: `🏆 FINAL BOSS DEFEATED — +${_bossXp} XP / +${_bossGold} GOLD`,
        color: '#ffd700',
        timer: 5.0,
      });
      playVictory();
      // Only treat as floor-advance for floors 0-1 (advancing to 2-3).
      // After floor 3 (currentFloor === 2), final mega kill = win.
      if (currentFloor < 2) {
        sAny._floorAdvanceReady = true;
        state.pickupNotifications?.push({
          text: '⚡ PRESS E IN PORTAL FOR NEXT FLOOR',
          color: '#ffcc00',
          timer: 6.0,
        });
        // Reactivate portal so player can interact with it again
        if (state.portal) state.portal.active = true;
        // BETA 19.76 — Floor-advance safety window. Dylan's LeBron run:
        // killed mega-boss, portal lit, but died to a regular zombie
        // walking back to the portal (deathCause: "enemy:Zombie",
        // floorAdvances: 0). With peakAlive=182 enemies still on the
        // map post-boss, the walk back is a death zone. Grant 10s
        // invincibility on mega kill + heal to full so player can
        // safely cross to the portal. Doesn't bypass damage during
        // the boss fight itself — only kicks in once boss is dead.
        // BETA 20.00 — UN-GATED from Debt (Dylan: healing keeps running). Mega
        // kill grants 10s invuln + full heal so walk-back to portal is safe.
        state.player.invincibleTimer = Math.max(state.player.invincibleTimer ?? 0, 10);
        state.player.hp = state.player.maxHp;
        // BETA 19.76 — Dylan: "all xp and gold should warp into player
        // like a chick magnets when final boss dies. if there are multiple
        // then it doesnt happen until the last one dies." Final boss death
        // is already gated on `state.finalBossEnemyIds.every(...)` at line
        // 3206 (== last of N), so this fires correctly for single OR
        // multi-boss spawns. Vacuums every XP / gold / health orb on the
        // map straight to the player as a victory reward.
        if (state.lootOrbs) {
          vacuumAllOrbs(state.lootOrbs, state.localPlayerId); // BETA 23.6x — CO-OP PHASE 2 part D: owned-orb guard (local magnet)
        }
        // BETA 20.28 — Mega boss death SPAWNS the ULTRA chest (the red one with
        // the 7 ultra items). The ultra-chest screen was fully built but
        // state.ultraChest was NEVER assigned anywhere, so the 7 ultra items
        // were unobtainable. Spawn it right on the player (they have 10s invuln
        // + full heal here, so grabbing it is safe) — walk over it, press E.
        state.ultraChest = { x: state.player.x, y: state.player.y, collected: false };
      } else if (isBotEnabled()) {
        // Bots keep the old 'won' end — the bot pipeline owns their win rows.
        state.phase = 'won';
        captureRunSnapshot(state);
      } else {
        // BETA 21.19 — Dylan: killing the FINAL boss NO LONGER ends the run (human
        // players). The game keeps running in escalating GOD MODE (enemies keep
        // scaling HP / damage / speed forever) until you DIE — death finalizes the
        // run like any other (no 'won' phase). Same safety reward as a floor advance
        // first: 10s invuln + full heal + orb vacuum + the ultra chest, THEN the storm.
        const _sGod = state as unknown as Record<string, number | undefined>;
        _sGod._godMode = 1;
        _sGod._godModeStart = state.time;
        state.player.invincibleTimer = Math.max(state.player.invincibleTimer ?? 0, 10);
        state.player.hp = state.player.maxHp;
        if (state.lootOrbs) vacuumAllOrbs(state.lootOrbs, state.localPlayerId); // BETA 23.6x — CO-OP PHASE 2 part D: owned-orb guard (local magnet)
        state.ultraChest = { x: state.player.x, y: state.player.y, collected: false };
        state.pickupNotifications?.push({
          text: '☠ GOD MODE — IT NEVER ENDS. SURVIVE. ☠',
          color: '#ff2a2a',
          timer: 6.0,
        });
      }
    }
  }

  // BETA 17.16 — PSBOT cycle-30af: tick floor transition overlay timer.
  {
    const _sAnyFtTick = state as unknown as Record<string, number | undefined>;
    if ((_sAnyFtTick._floorTransitionTimer ?? 0) > 0) {
      _sAnyFtTick._floorTransitionTimer = (_sAnyFtTick._floorTransitionTimer ?? 0) - dt;
      if ((_sAnyFtTick._floorTransitionTimer ?? 0) <= 0) {
        _sAnyFtTick._floorTransitionTimer = 0;
      }
    }
  }

  // ── Update pickup notifications ──
  if (state.pickupNotifications) {
    for (let i = state.pickupNotifications.length - 1; i >= 0; i--) {
      state.pickupNotifications[i].timer -= dt;
      if (state.pickupNotifications[i].timer <= 0) {
        state.pickupNotifications.splice(i, 1);
      }
    }
  }

  // ── Tick unlock banner notifications ──
  if (state.runUnlockNotifs?.length) {
    for (const n of state.runUnlockNotifs) {
      n.slideProgress = Math.min(1, n.slideProgress + dt * 8); // ~0.12s slide-in
      n.timer -= dt;
    }
    state.runUnlockNotifs = state.runUnlockNotifs.filter(n => n.timer > 0);
  }

  // ── BETA 19.39 / 19.40 — Time-series telemetry for live + bot play ──
  // Dylan: "go heavy on the tracking. you have done this before. work on
  // [killa] for now. but most importantly i want you to track enemies.
  // they are for sure not balanced on how many on screen at certain
  // times and such". Per-minute was too coarse — a 75s bot run only
  // emits 1-2 samples and tells us nothing about WHEN the death cascade
  // started. Bumped to 10-second granularity. Each sample captures the
  // 4 numbers we need to triage: alive enemies, player HP, cumulative
  // damage taken, and the run-time second. With ~600 ms decode budget,
  // even a 10-minute run = 60 samples = ~3 KB JSON — well under any cap.
  if (state.phase === 'playing') {
    const _alive = state.enemies?.filter(e => e.alive).length ?? 0;
    // BETA 19.93 — samples10s now captures EVERY player stat each 10s
    // so post-run analysis can detect mid-run bleeds ("damageBonus dropped
    // 50% → 5% at minute 7") instead of only seeing HP + alive count.
    type Sample = {
      t: number; alive: number; hp: number; dmgTaken: number; lvl: number;
      dmg: number; atkSpd: number; lifesteal: number; armor: number; shield: number;
      proj: number; bounce: number; knockback: number; phase: number;
      evasion: number; thorns: number; pickup: number; hpRegen: number;
      shieldRegen: number; xpMult: number; goldMult: number; attackArea: number;
      projSpeed: number; speed: number; luck: number; critC: number; critD: number;
      eliteDmg: number;
    };
    const _sAlive = state as unknown as {
      _peakAliveCount?: number;
      _samples10s?: Sample[];
      _samples10sNext?: number; // next 10-sec bucket index to record
      _samplesDmgTakenTotal?: number;
    };
    if (_alive > (_sAlive._peakAliveCount ?? 0)) _sAlive._peakAliveCount = _alive;
    const _run10s = Math.floor((state.time ?? 0) / 10);
    if (_sAlive._samples10sNext === undefined) _sAlive._samples10sNext = 0;
    if (!_sAlive._samples10s) _sAlive._samples10s = [];
    if (_run10s >= _sAlive._samples10sNext) {
      const p = state.player;
      const px = p as unknown as Record<string, number | undefined>;
      const r2 = (v: number | undefined) => Math.round((v ?? 0) * 100) / 100;
      const r0 = (v: number | undefined) => Math.round(v ?? 0);
      _sAlive._samples10s.push({
        t: Math.round((state.time ?? 0)),
        alive: _alive,
        hp: r0(p.hp),
        dmgTaken: r0(state.damageTakenThisRun),
        lvl: r0(p.level),
        dmg: r2(p.damageBonus),
        atkSpd: r2(p.attackSpeedBonus),
        lifesteal: r2(p.lifesteal),
        armor: r0(p.armor),
        shield: r0(p.shield),
        proj: r0(p.projectileCountBonus),
        bounce: r0(p.projectileBounceBonus),
        knockback: r2(p.knockbackBonus),
        phase: r2(px.contactPhaseChance),
        evasion: r2(p.evasion),
        thorns: r2(p.thorns),
        pickup: r0(p.pickupRange),
        hpRegen: r2(p.hpRegen),
        shieldRegen: r2(p.shieldRegen),
        xpMult: r2(p.xpMultiplier),
        goldMult: r2(p.goldMultiplier),
        attackArea: r2(p.attackAreaBonus),
        projSpeed: r2(p.projectileSpeedBonus),
        speed: r0(p.speed),
        luck: r2(p.luck),
        critC: r2(p.critChance),
        critD: r2(p.critDamage),
        eliteDmg: r2(p.eliteDamageBonus),
      });
      _sAlive._samples10sNext = _run10s + 1;
    }
  }

  // ── Delegate to subsystem modules ──
  // BETA 13.60: per-system frame-time tracking. Wraps each subsystem update with
  // performance.now() so the bot CSV can show ms/sec per system across a run —
  // makes invisible perf cost visible. Negligible overhead (~5 perf.now() calls/frame).
  updateItemEffectVisuals(dt);
  // BETA 16.69 — Build the kitten-lure assignment BEFORE enemy AI ticks
  // so steerEnemyAI can override targets in the same frame. Cheap O(K*E_in_radius)
  // scan; no-op when Ugly Kittens isn't equipped.
  {
    const _ws = getWeaponSystemState();
    if (_ws) tickKittenLure(_ws, state.enemies);
    // BETA 20.10 — trap lure (banana/whoopie monkey bombs). Same pre-AI tick.
    if (_ws) tickTrapLure(_ws, state.enemies);
  }
  // BETA 22.98 — MAP-BUILDING MODE (Dylan): enemy system fully off while the
  // map is under construction. One flag in mapChunks.ts turns them back on.
  if (enemiesModule && !MAP_BUILD_NO_ENEMIES) {
    const _t = performance.now();
    enemiesModule.update(state, dt);
    notePerf('enemies', performance.now() - _t);
  } else if (enemiesModule && MAP_BUILD_NO_ENEMIES && state.phase === 'playing') {
    // BETA 23.02 — TEST DUMMIES (Dylan): 4 stationary zombies stand in a
    // ring around spawn so weapons have targets. Enemy UPDATE stays off —
    // they render + take damage but never move or attack.
    maintainTestDummies(state, state.map.roomMap.spawnPoint.x, state.map.roomMap.spawnPoint.y, state.time ?? 0);
  }
  if (weaponsModule) {
    const _t = performance.now();
    // BETA 23.01 — feed nearby solid-prop zones to the weapon smart-slide
    // BEFORE the weapon tick so this frame's slide + shots use them.
    updateWeaponOcclusionZones(state);
    weaponsModule.update(state, dt);
    notePerf('weapons', performance.now() - _t);
  }
  if (itemsModule) {
    const _t = performance.now();
    itemsModule.update(state, dt);
    notePerf('items', performance.now() - _t);
  }
  if (audioModuleExt?.update) audioModuleExt.update(state, dt);

  // ── Ultra item system ──
  state.enemies = enemiesModule?.getEnemies() ?? [];

  // BETA 13.57: fog-of-war trail repositioning — keeps action front-loaded when
  // the player is moving fast through a long zombie chain.
  repositionTrailEnemies(state);

  // Feed live enemy positions to itemEffectSystem for homing rockets + T-Bag targeting
  setActiveEnemies(state.enemies.map(e => ({ id: e.id, x: e.x, y: e.y, alive: e.alive !== false })));

  // ── Nutty T-Bag (R16) — update orbit position each frame ──
  {
    const tbagSlot = getItemState()?.inventory.find(s => s.item.id === 'R16');
    if (tbagSlot) {
      const tbagDmg = tuneOf('R16', 'dmgPerStack', 20) * tbagSlot.stacks;
      updateTBagPosition(player.x, player.y, tbagSlot.stacks, tbagDmg);
      if (wasTBagJustSpawned()) playTBagSummon();
    }
  }

  // ── BETA 13.98 — SHIT (item_shit) — drop a slow trail puddle behind the player ──
  // BETA 14.26 — Card vs ground sprite split. The CARD icon (shown in the
  // pick screen / loadout) needs the rarity-tinted background; the GROUND
  // sprite (drawn on the world floor) needs a transparent background so it
  // doesn't carry a green square through the level. User: "this is the
  // bunchabulshit card. what it should look like when a player chooses the
  // item is this in the game? has to be right." So:
  //   - item_poop_deck.png        = card icon (green-BG common rarity)
  //   - item_poop_deck_ground.png = ground sprite stage 1 (transparent)
  //   - item_shit_puddle.png      = ground sprite stage 2 (post-step splash)
  // Lifetime stays at 8s so puddles linger long enough for enemies to step in.
  {
    const shitSlot = getItemState()?.inventory.find(s => s.item.id === 'item_shit');
    if (shitSlot) {
      // BETA 16.29 — PSBOT cycle-30e: per-dimension contributions read
      // from slot.pickedDimensions. First-stack picks are unchanged
      // (legacy stacks count drives base scaling). Each duplicate pick
      // commits to ONE dimension; the apply path adds:
      //   puddleRadius   → +4wu per dim-pick to spawn radius
      //   dropRate       → -5% interval per dim-pick (faster drops)
      //   puddleSlowMult → +5pp slow strength per dim-pick (0.5 → 0.55 etc)
      //   puddleDamage   → +2 damage tick per dim-pick (dotPerSec)
      const dims = shitSlot.pickedDimensions ?? {};
      const dimRadius = (dims.puddleRadius ?? 0) * 4;
      const dimDropRate = (dims.dropRate ?? 0) * 5; // % faster
      const dimSlow = (dims.puddleSlowMult ?? 0) * 5; // pp toward more slow
      const dimDmg = (dims.puddleDamage ?? 0) * 2;
      _shitTrailTimer -= dt;
      if (_shitTrailTimer <= 0) {
        const ws = getWeaponSystemState();
        if (ws) {
          // BETA 16.90 — PSBOT cycle-30e Part 3 §3 universal item-effect
          // scaling audit: SHIT puddle damage / radius previously did NOT
          // honor player.attackAreaBonus / player.bonusDamagePercent.
          // Every other weapon respects these stats; SHIT didn't, making
          // perc/item bonuses feel inert when SHIT was on the build. Now
          // radius multiplies by (1 + attackAreaBonus/100) and dotPerSec
          // multiplies by (1 + bonusDamagePercent/100).
          const _areaMult = 1 + (player.attackAreaBonus ?? 0) / 100;
          const _dmgMult = 1 + (player.damageBonus ?? 0) / 100;
          spawnGroundEffectAt(ws, player.x, player.y, {
            radius: (tuneOf('item_shit', 'puddleBaseRadius', 30) + tuneOf('item_shit', 'puddleRadiusPerStack', 4) * shitSlot.stacks + dimRadius) * _areaMult,
            lifetime: tuneOf('item_shit', 'puddleLifetime', 8.0),
            // slowMult is INVERSE: 0.5 = 50% speed (heavy slow). Subtract
            // dimSlow/100 so each pick increases slow strength.
            slowMult: Math.max(0.1, tuneOf('item_shit', 'puddleSlowMult', 0.5) - dimSlow / 100),
            dotPerSec: dimDmg * _dmgMult, // base 0; only damages if puddleDamage dim was picked
            color: 'rgba(0,0,0,0)',
            weaponId: 'item_shit',
            groundSpriteUrl: '/items/item_poop_deck_ground.png',
            groundSpriteUrlAfterStep: '/items/item_shit_puddle.png',
          });
        }
        // BETA 15.67 base interval, BETA 16.29 dropRate dimension scales it.
        const baseInterval = tuneOf('item_shit', 'dropInterval', 1.5) / Math.max(1, Math.sqrt(shitSlot.stacks));
        const dropRateMult = 1 - Math.min(0.85, dimDropRate / 100);
        _shitTrailTimer = baseInterval * dropRateMult;
      }
    }
  }

  // ── BETA 13.99 — Bungulators (item_bungulators) — melee brawler minion(s) ──
  {
    const bungSlot = getItemState()?.inventory.find(s => s.item.id === 'item_bungulators');
    // BETA 14.78 — count = stacks + projectileCountBonus (user: "same for
    // bungulators... connected to increased projectile buff"). Lets you stack
    // bungulators by picking the +1 Projectile Count shrines/percs too, not
    // just by picking up more item copies.
    // BETA 15.15 — ALSO add N06 Let's Ride XXX perk bungulators (shared pool
    // per PSBOT spec: "ONE pool = visually consistent, item's hit-harder buff
    // applies to ALL bungulators"). Perk count snapshot = min(5, floor(level/8)).
    // If only the perk is picked (no item), bungulators still spawn.
    const _itemBungs = bungSlot ? bungSlot.stacks + (player.projectileCountBonus ?? 0) : 0;
    const _perkBungs = state._perkBungulatorCountSnapshot ?? 0;
    // BETA 15.35 — defensive cap. Per Dylan: "killa peanut got the lock up bug
    // on bungulator upgrade screen from a chest." If projectileCountBonus or
    // _perkBungulatorCountSnapshot is corrupted (NaN, Infinity, or just very
    // large from many stacks), the unbounded `while` loop below would spawn
    // thousands of bungulators in one frame and freeze the page. 50 is plenty
    // visually (you can't see more than ~10 distinct fists at once anyway) and
    // matches the cap from BETA 15.26 (rolled back in 15.22; restoring here).
    const wantCount = Math.min(50, Math.max(0, (_itemBungs | 0) + (_perkBungs | 0)));
    // Track total perk-source bungulators ever spawned (for CSV)
    if (_perkBungs > (state._perkBungulatorsSpawnedThisRun ?? 0)) {
      state._perkBungulatorsSpawnedThisRun = _perkBungs;
    }
    // Spawn missing Bungulators (one per stack)
    while (_bungulators.length < wantCount) {
      const i = _bungulators.length;
      const initAngle = (i / Math.max(1, wantCount)) * Math.PI * 2;
      _bungulators.push({
        // BETA 16.35 — bungulators ride the INNER ring (Dylan: 3 rings).
        x: player.x + Math.cos(initAngle) * BUNGULATOR_ORBIT_RADIUS,
        y: player.y + Math.sin(initAngle) * BUNGULATOR_ORBIT_RADIUS,
        attackCooldown: 0,
        punchAnimTimer: 0,
        facing: 1,
        offsetAngle: initAngle,
        // BETA 15.27 — start angle = the idle slot so newly-spawned
        // bungulators don't pop in at the player center.
        angle: initAngle,
      });
    }
    // Despawn extras if stacks shrunk (e.g., item lost)
    if (_bungulators.length > wantCount) _bungulators.length = wantCount;

    // BETA 13.102 — Recompute angles every frame so they stay symmetrically
    // spaced as stacks grow/shrink. User: "if more than one stack, the
    // position around the circle symmetrically. and try not to touch".
    // Without this, an existing bungulator keeps its angle from the moment
    // it was spawned (denominator was the OLD stack count) — adding a 3rd
    // stack used to leave the 1st and 2nd at 0° and 180° while the 3rd
    // landed at 240°, three-clumped instead of evenly 120°-spaced.
    for (let i = 0; i < _bungulators.length; i++) {
      _bungulators[i].offsetAngle = (i / _bungulators.length) * Math.PI * 2;
    }

    if (_bungulators.length > 0) {
      const enemies = enemiesModule?.getEnemies?.() ?? [];
      // BETA 15.27 — REWRITE per Dylan: "make all added bungulators to live
      // on the invisible white circle around the bot. the bungulators should
      // be able to turn and move freely on it depending on if an enemy is
      // near so they can attack it. this has been bugging me."
      //
      // Two bugs in the 14.77 implementation:
      //   1. Cartesian X/Y lerp = bungulators slid THROUGH the player center
      //      when moving from one side of the ring to the other. Looked janky.
      //   2. Targeting picked the bi-th nearest enemy with fallback to the
      //      closest — so 5 bungulators on 1 enemy all converged to the
      //      EXACT same angle, visually overlapping into a single sprite.
      //
      // Fix: each bungulator stores `angle` (current angular position on the
      // ring). Position is DERIVED from angle every frame, guaranteeing they
      // stay on the ring. Lerp uses shortest-arc (handles the 0/2π wrap).
      // Targeting fans out — when M bungulators chase N enemies (M > N), the
      // overflow bungulators spread across a 60° arc centered on the closest
      // available target, so a swarm reads as a swarm instead of a clump.
      const enemyDistList = enemies
        .filter(e => e.alive)
        .map(e => ({ e, d: Math.sqrt((e.x - player.x) ** 2 + (e.y - player.y) ** 2) }))
        .sort((a, b) => a.d - b.d);

      const bungCount = _bungulators.length;
      for (let bi = 0; bi < bungCount; bi++) {
        const bung = _bungulators[bi];
        // Pick a target enemy. If we have at least bi+1 enemies, take the
        // bi-th nearest. Otherwise this bungulator overflows onto an existing
        // target — give it a small angular offset around that target's angle
        // so it doesn't stack on a sibling.
        let targetAngle: number;
        if (enemyDistList.length === 0) {
          // No enemies — drift to evenly-spaced idle slot
          targetAngle = (bi / bungCount) * Math.PI * 2;
        } else if (bi < enemyDistList.length) {
          // 1:1 — point straight at this enemy
          const e = enemyDistList[bi].e;
          targetAngle = Math.atan2(e.y - player.y, e.x - player.x);
        } else {
          // Overflow — assign to the closest enemy (index 0) but offset by an
          // angular slot so multiple overflow bungulators don't perfectly
          // overlap. Spread overflow across a 60° (~1.047 rad) arc centered
          // on the target's angle. The first overflow goes left, second
          // right, alternating — so 5 bungulators on 1 enemy get angles
          // [target, target-15°, target+15°, target-30°, target+30°].
          const e0 = enemyDistList[0].e;
          const baseAngle = Math.atan2(e0.y - player.y, e0.x - player.x);
          const overflowIdx = bi - enemyDistList.length + 1;     // 1, 2, 3...
          const sign = (overflowIdx % 2 === 0) ? 1 : -1;
          const slot = Math.ceil(overflowIdx / 2);
          // 15° per slot, capped at 30° (so 5 overflows = ±30°, 7 = ±30° max)
          const offsetRad = Math.min(0.524, slot * 0.262);  // 0.262 ≈ 15°
          targetAngle = baseAngle + sign * offsetRad;
        }

        // Shortest-arc lerp: compute signed angular delta, take it modulo 2π
        // mapped to [-π, π], then advance by dt × speed. Speed of 6 means a
        // bungulator on the opposite side of the ring (180° = π rad) reaches
        // the new target in roughly π/6 ≈ 0.5 seconds — fast enough to feel
        // responsive, slow enough to read as ring-travel rather than teleport.
        let delta = targetAngle - bung.angle;
        // Normalize delta to [-π, π] so we always take the short way around
        while (delta > Math.PI)  delta -= Math.PI * 2;
        while (delta < -Math.PI) delta += Math.PI * 2;
        const angularSpeed = 6;
        const step = delta * Math.min(1, dt * angularSpeed);
        bung.angle += step;
        // Keep bung.angle in a stable range [-π, π] so floating-point drift
        // over a long run doesn't accumulate huge values.
        if (bung.angle > Math.PI)  bung.angle -= Math.PI * 2;
        if (bung.angle < -Math.PI) bung.angle += Math.PI * 2;

        // DERIVE position from angle. This is the critical line — bungulators
        // are now LOCKED to the ring by construction. They cannot be off-ring.
        // BETA 16.35 — inner ring (closer than weapons; weapons no longer
        // hide bungulators when both render at the same radius).
        bung.x = player.x + Math.cos(bung.angle) * BUNGULATOR_ORBIT_RADIUS;
        bung.y = player.y + Math.sin(bung.angle) * BUNGULATOR_ORBIT_RADIUS;

        bung.attackCooldown -= dt;
        if (bung.punchAnimTimer > 0) bung.punchAnimTimer -= dt;

        // BETA 14.12 — Scaling: Bungulators now read player stat bonuses so
        // perks like Steroids (+damage), Adderall (+attack speed), and area-
        // bonus shrines stack onto their punches. Per Manus handoff: items
        // calling damageEnemy directly with hardcoded numbers were a major
        // disconnect — minions felt useless mid-game.
        const _bungDmgMult = 1 + (player.damageBonus ?? 0) / 100;
        const _bungAsMult  = 1 + (player.attackSpeedBonus ?? 0) / 100;
        const _bungAreaMult = 1 + (player.attackAreaBonus ?? 0) / 100;
        const _bungRange = tuneOf('item_bungulators', 'attackRange', 60) * _bungAreaMult;

        // Find nearest enemy within attack range
        if (bung.attackCooldown <= 0) {
          let nearest: { id: number; x: number; y: number; alive?: boolean } | null = null;
          let nearestDist = _bungRange;
          for (const e of enemies) {
            if (!e.alive) continue;
            const dx = e.x - bung.x;
            const dy = e.y - bung.y;
            const d = Math.sqrt(dx * dx + dy * dy);
            if (d < nearestDist) {
              nearestDist = d;
              nearest = e;
            }
          }
          if (nearest) {
            // Punch! Damage scales with damageBonus + crit chance / damage.
            const baseDmg = tuneOf('item_bungulators', 'punchDamage', 18);
            const critChance = Math.min(1.0, ((player.critChance ?? 0)) / 100);
            const isCrit = Math.random() < critChance;
            const totalCritDmg = (player.critDamage ?? 0) / 100;
            const finalDmg = isCrit
              ? Math.round(baseDmg * _bungDmgMult * (1 + totalCritDmg))
              : Math.round(baseDmg * _bungDmgMult);
            enemiesModule?.damageEnemy?.(nearest.id, finalDmg, { x: 0, y: 0 });
            // BETA 15.78 — Dylan-provided bungulator punch sound (spatial,
            // throttled 0.12s in playBungulatorPunch so multiple stacked
            // bungulators don't overlap into noise).
            playBungulatorPunch(nearest.x, nearest.y);
            // BETA 20.72 — punch IMPACT finally visible: gold metallic sparks
            // + a small glow ring at the struck enemy (brawler identity —
            // matches the ally's new purple-aura read). Punch already
            // throttled by its cooldown, counts kept tiny.
            spawnParticles(state.particles, nearest.x, nearest.y, '#ffd700', 3, { shape: 'spark', stretch: 5, speedMin: 100, speedMax: 220, lifeMin: 0.12, lifeMax: 0.22, sizeMin: 1.5, sizeMax: 2.5 });
            spawnParticles(state.particles, nearest.x, nearest.y, '#aa66ff', 1, { shape: 'ring', speedMin: 0, speedMax: 0, sizeMin: 6, sizeMax: 9, lifeMin: 0.2, lifeMax: 0.25 });
            // BETA 14.62 — Bungulators punch proc + dmgDealt tracker.
            trackItemProc('item_bungulators');
            trackItemDmg('item_bungulators', finalDmg);
            // BETA 14.62 — Synergy: karenDrones_bungulators fires on each
            // bungulator punch when Karen Drones equipped (weapon system).
            // valueDelta = punch damage dealt.
            const _wsForBung = getWeaponSystemState();
            if (_wsForBung?.weapons?.some(w => w.definitionId === 'wpn_karen_drones')) {
              trackSynergyTrigger('karenDrones_bungulators', finalDmg);
            }
            bung.attackCooldown = tuneOf('item_bungulators', 'punchCooldown', 0.7) / _bungAsMult;
            bung.punchAnimTimer = 0.25;
            bung.facing = nearest.x >= bung.x ? 1 : -1;
          }
        }
      }
    }
  }

  // ── BETA 13.99 / 14.78 — Iranian Bird (item_iranian_bird) — orbital quadcopter drones ──
  // BETA 14.78 — multi-bird support. Count = stacks + projectileCountBonus.
  // Each bird orbits at evenly-spaced offset, picks its OWN nearest enemy, and
  // attacks on its own cooldown so multiple birds zap multiple targets.
  {
    const birdSlot = getItemState()?.inventory.find(s => s.item.id === 'item_iranian_bird');
    // BETA 15.35 — defensive cap (see bungulator block above for rationale).
    // Iranian Bird spawn loop was unbounded; matches the cap from BETA 15.36.
    // BETA 21.41 — Dylan "no caps": Iranian Bird count cap 10 → 50 (matches Bungulators)
    // so projectileCountBonus actually keeps adding birds. 50 = perf safety ceiling, not balance.
    const wantBirdCount = birdSlot ? Math.min(50, (birdSlot.stacks | 0) + ((player.projectileCountBonus ?? 0) | 0)) : 0;
    // Spawn missing birds
    while (_iranianBirds.length < wantBirdCount) {
      const i = _iranianBirds.length;
      _iranianBirds.push({
        angle: 0,
        attackCd: 0,
        facing: 0,
        offsetAngle: (i / Math.max(1, wantBirdCount)) * Math.PI * 2,
      });
    }
    // Despawn extras if count shrank
    if (_iranianBirds.length > wantBirdCount) _iranianBirds.length = wantBirdCount;
    // Recompute offsets each frame for symmetric distribution as count changes
    for (let i = 0; i < _iranianBirds.length; i++) {
      _iranianBirds[i].offsetAngle = (i / _iranianBirds.length) * Math.PI * 2;
    }

    if (_iranianBirds.length > 0) {
      const _birdDmgMult = 1 + (player.damageBonus ?? 0) / 100;
      const _birdAsMult  = 1 + (player.attackSpeedBonus ?? 0) / 100;
      const _birdRangeMult = 1 + (player.rangeBonus ?? 0) / 100;
      const enemies = enemiesModule?.getEnemies?.() ?? [];

      for (const bird of _iranianBirds) {
        bird.angle += dt * tuneOf('item_iranian_bird', 'orbitSpeed', 1.6); // orbit speed (shared rotation rate)
        bird.attackCd -= dt;
        if (bird.attackCd <= 0) {
          // Each bird's actual world position = player + orbit-vector at
          // (bird.angle + bird.offsetAngle), so birds spread around the player
          // at evenly-spaced angles even though they share a rotation rate.
          const renderAngle = bird.angle + bird.offsetAngle;
          const birdX = player.x + Math.cos(renderAngle) * 110;
          const birdY = player.y + Math.sin(renderAngle) * 110;

          let nearest: { id: number; x: number; y: number; alive?: boolean } | null = null;
          let nearestDist = tuneOf('item_iranian_bird', 'scanRange', 300) * _birdRangeMult;
          for (const e of enemies) {
            if (!e.alive) continue;
            const dx = e.x - birdX;
            const dy = e.y - birdY;
            const d = Math.sqrt(dx * dx + dy * dy);
            if (d < nearestDist) { nearestDist = d; nearest = e; }
          }

          if (nearest) {
            const baseDmg = tuneOf('item_iranian_bird', 'shotDamage', 14);
            const critChance = Math.min(1.0, ((player.critChance ?? 0)) / 100);
            const isCrit = Math.random() < critChance;
            const totalCritDmg = (player.critDamage ?? 0) / 100;
            const finalDmg = isCrit
              ? Math.round(baseDmg * _birdDmgMult * (1 + totalCritDmg))
              : Math.round(baseDmg * _birdDmgMult);
            enemiesModule?.damageEnemy?.(nearest.id, finalDmg, { x: 0, y: 0 });
            trackItemProc('item_iranian_bird');
            trackItemDmg('item_iranian_bird', finalDmg);
            const _inv = getItemState()?.inventory;
            if (_inv?.some(s => s.item.id === 'item_bungulators')) {
              trackSynergyTrigger('iranianBird_bungulators', finalDmg);
            }
            bird.facing = Math.atan2(nearest.y - birdY, nearest.x - birdX);
            _iranianBirdBullets.push({
              x0: birdX, y0: birdY,
              x1: nearest.x, y1: nearest.y,
              age: 0,
              lifetime: 0.18,
            });
            playIranianBirdFire(birdX, birdY);   // BETA — drone zap sound (was silent)
            bird.attackCd = tuneOf('item_iranian_bird', 'fireCooldown', 0.6) / _birdAsMult;
          } else {
            bird.attackCd = tuneOf('item_iranian_bird', 'idleRescan', 0.15); // re-scan quickly when no target
          }
        }
      }

      // Always advance the bullet animation
      for (const b of _iranianBirdBullets) b.age += dt;
      _iranianBirdBullets = _iranianBirdBullets.filter(b => b.age < b.lifetime);
    }
  }

  // BETA 17.03 — PSBOT cycle-30u A7: Timmy slow-aura. Per item description:
  // "Timmy speed -25% within 400wu of player." Damage reduction was wired
  // in 16.90 but the slow-aura half was never implemented. Per-frame: if
  // player has Timmy Repellent equipped, find Timmy enemies within 400wu
  // and refresh their slowedUntil with a short 0.15s window + 0.75
  // multiplier. Slow expires automatically (slowedUntil -= dt path in
  // enemySystem) when a Timmy leaves the aura range. Stay Frosty +
  // existing slow sources still take priority (0.5 is stronger than 0.75)
  // via the min-multiplier on co-applied slows.
  {
    const _timmySprayEquipped = getItemState()?.inventory.some(s => s.item.id === 'item_timmy_repellent_spray');
    if (_timmySprayEquipped) {
      const _ess = getEnemySystemState();
      if (_ess) {
        const RADIUS_SQ = 400 * 400;
        // 23.55 — Timmy deleted: the aura now slows any 'brawler'-TAGGED
        // enemy (Bully Spray's card says "Brawlers"), so it wakes up with the
        // Phase C roster. _a7TimmySlowsAppliedThisRun keeps its name as a
        // PSBOT-compat CSV column.
        const _sAnyA7 = state as unknown as Record<string, number | undefined>;
        let _slowsAppliedThisFrame = 0;
        for (const enemy of _ess.enemies) {
          if (!enemy.alive || !(getEnemyDef(enemy.definitionId)?.tags?.includes('brawler'))) continue;
          const dx = enemy.x - player.x, dy = enemy.y - player.y;
          if (dx * dx + dy * dy > RADIUS_SQ) continue;
          // Refresh slow each frame while in aura. 0.15s > dt so the
          // slow doesn't flicker between frames.
          enemy.slowedUntil = Math.max(enemy.slowedUntil, 0.15);
          // Take the stronger slow if multiple sources active (lower = stronger).
          enemy.slowMultiplier = Math.min(enemy.slowMultiplier, 0.75);
          _slowsAppliedThisFrame++;
        }
        if (_slowsAppliedThisFrame > 0) {
          _sAnyA7._a7TimmySlowsAppliedThisRun = (_sAnyA7._a7TimmySlowsAppliedThisRun ?? 0) + _slowsAppliedThisFrame;
        }
      }
    }
  }

  // ── BETA 13.102 — Whoopie Cushion of Doom (auto-drop floor trap) ──
  // User: "should be dropped on the ground and live there until it gets stepped on
  // and then oblique brown floor vfx under enemies, players, bot and above floor,
  // then fade quickly". Cushion lives 30s waiting for an enemy. On first contact:
  // applies 60% slow (pseudo-stun for 2s via slowDurationOnTick refresh), spawns
  // brown VFX particles, and the cushion fades out within 0.6s.
  //
  // BETA 16.28 — PSBOT cycle-30f Phase 7 Bug 1 INVESTIGATED: PSBOT audit
  // assumed a 30s cooldown and reported "3.1× over expected." The actual
  // cadence is `6.0 / attackSpeedMult` (line ~2848 below). 30s is the
  // cushion LIFETIME (line 2824, how long it waits for an enemy), NOT
  // the drop cadence. Working as designed; PSBOT's expected baseline
  // was wrong. No fix needed.
  {
    const cushionSlot = getItemState()?.inventory.find(s => s.item.id === 'item_whoopie_cushion_of_doom');
    if (cushionSlot) {
      // BETA 16.30 — PSBOT cycle-30e Phase 2: Whoopie converted to stackable
      // + dimensions (whoopieRadius / whoopieStunDur / whoopieCdReduce /
      // whoopieDamage). Read pickedDimensions for per-dimension scaling.
      const _wDims = cushionSlot.pickedDimensions ?? {};
      const _wRadiusBonus = (_wDims.whoopieRadius ?? 0) * 25;
      const _wStunDurBonus = (_wDims.whoopieStunDur ?? 0) * 0.4;
      const _wCdReduce = (_wDims.whoopieCdReduce ?? 0) * 1.0;
      const _wDamageBonus = (_wDims.whoopieDamage ?? 0) * 5;
      _whoopieDropTimer -= dt;
      if (_whoopieDropTimer <= 0) {
        const ws = getWeaponSystemState();
        if (ws) {
          playWhoopieThrow(player.x, player.y);   // BETA — throw/lob sound on drop
          const _wAreaMult = 1 + (player.attackAreaBonus ?? 0) / 100;
          const _wDmgMult = 1 + (player.damageBonus ?? 0) / 100;
          // Radius: base 32 + whoopieRadius dim contributions × area, cap 60.
          const _wRadius = Math.min(60, (tuneOf('item_whoopie_cushion_of_doom', 'blastRadius', 32) + _wRadiusBonus) * _wAreaMult);
          // BETA 20.10 — LEGENDARY monkey bomb. Throw cushions in random
          // directions (not under the player). Each lures nearby enemies via
          // trapLure.ts, then on first contact AoE-FREEZES + damages the whole
          // lured group and auto-vacuums their loot to the player.
          // BETA 21.71 — Dylan: base ONE cushion per drop; +1 per projectile-count
          // buff (was hardcoded 3). Mirrors bungulators/birds (engine.ts:4152/4354).
          const _wThrowCount = Math.max(1, 1 + (player.projectileCountBonus ?? 0));
          for (let _ti = 0; _ti < _wThrowCount; _ti++) {
            const _ang = Math.random() * Math.PI * 2;
            const _dist = 70 + Math.random() * 70;
            const _tx = player.x + Math.cos(_ang) * _dist;
            const _ty = player.y + Math.sin(_ang) * _dist;
            spawnGroundEffectAt(ws, _tx, _ty, {
            radius: _wRadius,
            lifetime: tuneOf('item_whoopie_cushion_of_doom', 'trapLifeSec', 30.0),
            slowMult: tuneOf('item_whoopie_cushion_of_doom', 'enemySlowMult', 0.4),
            dotPerSec: 0,
            color: 'rgba(0,0,0,0)',
            weaponId: 'item_whoopie_cushion_of_doom',
            groundSpriteUrl: '/items/item_whoopie_cushion_of_doom_ground.png',
            groundSpriteUrlAfterStep: '/items/item_whoopie_cushion_of_doom_ground.png',
            onFirstStep: (effect) => {
              // BETA 20.72 — whoopie pop gets its identity: brown food-poof
              // BUBBLES (it's a cushion full of... something). Was a 30-circle
              // default burst — the exact shared look the VFX law bans.
              // BETA 20.78 — freeze half is now ICE-CRYSTAL SHARDS (cyan,
              // spinning, brief). The 20.72 cyan rings reused the staggered-
              // ring motif the chick magnet owns — identity-law dedup; the
              // magnet keeps sole ownership of ring pulses.
              spawnParticles(state.particles, effect.x, effect.y, '#8b6f47', 14, { shape: 'bubble', speedMin: 30, speedMax: 130, lifeMin: 0.35, lifeMax: 0.7, sizeMin: 3, sizeMax: 7, gravity: -50 });
              spawnParticles(state.particles, effect.x, effect.y, '#00E5FF', 10, { shape: 'shard', speedMin: 60, speedMax: 190, lifeMin: 0.2, lifeMax: 0.45, sizeMin: 2, sizeMax: 5, spin: 9, spinRandomSign: true });
              playWhoopiePopBurst(effect.x, effect.y);   // BETA — activate/pop sound on first enemy contact
              const _aoeR = effect.radius + tuneOf('item_whoopie_cushion_of_doom', 'aoeRadiusBonus', 30);
              const _ess = getEnemySystemState();
              if (_ess) {
                for (const _en of _ess.enemies) {
                  if (!_en.alive) continue;
                  const _ex = _en.x - effect.x, _ey = _en.y - effect.y;
                  if (_ex * _ex + _ey * _ey > _aoeR * _aoeR) continue;
                  _en.frozenUntil = Math.max(_en.frozenUntil, tuneOf('item_whoopie_cushion_of_doom', 'freezeSeconds', 2.0) + _wStunDurBonus);  // AoE stun
                  const _dmg = Math.round((tuneOf('item_whoopie_cushion_of_doom', 'hitDamage', 25) + _wDamageBonus) * _wDmgMult);
                  enemiesModule?.damageEnemy?.(_en.id, _dmg, { x: 0, y: 0 });
                  trackItemDmg('item_whoopie_cushion_of_doom', _dmg);
                }
              }
              // Auto-collect loot from the kills (Dylan: gold/xp comes to you).
              if (state.lootOrbs) vacuumOrbsNear(state.lootOrbs, effect.x, effect.y, _aoeR + tuneOf('item_whoopie_cushion_of_doom', 'lootGrabBonus', 50), state.localPlayerId); // BETA 23.6x — CO-OP PHASE 2 part D: owned-orb guard (local magnet)
              effect.lifetime = Math.min(effect.lifetime, tuneOf('item_whoopie_cushion_of_doom', 'popFadeSec', 0.6));
            },
            });
          }
          // BETA 14.59 — Whoopie Cushion auto-drop "use" tracker. Auto-drop
          // is the equivalent of a Q-press in the original spec. Tracks both
          // qPressCount (uses) and procCount (effective same metric here).
          trackItemQPress('item_whoopie_cushion_of_doom');
          trackItemProc('item_whoopie_cushion_of_doom');
          // BETA 16.38 — flash the tray slot when whoopie auto-drops
          flashActiveItem('item_whoopie_cushion_of_doom');
          // BETA 14.62 — Synergy: whoopieCushion_asmonsBanana fires on each
          // whoopie auto-drop when banana also equipped. valueDelta = 1.
          if (getItemState()?.inventory.some(s => s.item.id === 'item_asmons_desk_banana')) {
            trackSynergyTrigger('whoopieCushion_asmonsBanana', 1);
          }
        }
        const _wAsMult = 1 + (player.attackSpeedBonus ?? 0) / 100;
        // BETA 16.30 — Cooldown: 6s base − whoopieCdReduce dimension. Floor 1s.
        // BETA 17.35 — cycle-30aq audit: floor 1.0 → 2.0. Combined with
        // maxStacks 0 → 5 cap, the worst case becomes ~2s/drop instead
        // of every-frame stun pulse.
        _whoopieDropTimer = Math.max(tuneOf('item_whoopie_cushion_of_doom', 'minCooldownSec', 2.0), (tuneOf('item_whoopie_cushion_of_doom', 'dropCooldownSec', 6.0) - _wCdReduce) / _wAsMult);
      }
    }
  }

  // ── BETA 13.102 — Asmon's Desk Banana (auto-drop floor trap) ──
  // User: "it's floor sprite too. left on the floor, doesn't stay with bot or
  // character. if touch explodes vfx". Banana lives 30s waiting for an enemy.
  // On first contact: damages + knockback (high enemy.damage burst), spawns
  // yellow/brown explosion VFX, and the banana removes itself.
  {
    const bananaSlot = getItemState()?.inventory.find(s => s.item.id === 'item_asmons_desk_banana');
    if (bananaSlot) {
      // BETA 16.30 — PSBOT cycle-30e Phase 2: Banana converted to
      // stackable + dimensions (bananaRange / bananaCdReduce / bananaForce
      // / bananaDamage). Read pickedDimensions for per-dimension scaling.
      const _bDims = bananaSlot.pickedDimensions ?? {};
      const _bRangeBonus = (_bDims.bananaRange ?? 0) * 25;
      const _bCdReduce = (_bDims.bananaCdReduce ?? 0) * 1.5;
      const _bForceBonus = (_bDims.bananaForce ?? 0) * 12; // % bonus to knockback
      const _bDamageBonus = (_bDims.bananaDamage ?? 0) * 8;
      void _bForceBonus; // currently engine doesn't read knockback force on the banana effect — wire in cycle-31 if needed
      _bananaDropTimer -= dt;
      if (_bananaDropTimer <= 0) {
        const ws = getWeaponSystemState();
        if (ws) {
          playBananaThrow(player.x, player.y);   // BETA — throw/lob sound on drop
          const _bAreaMult = 1 + (player.attackAreaBonus ?? 0) / 100;
          const _bDmgMult  = 1 + (player.damageBonus ?? 0) / 100;
          const _bCritChance = Math.min(1.0, ((player.critChance ?? 0)) / 100);
          const _bCritDmg = (player.critDamage ?? 0) / 100;
          // Radius: base 36 + bananaRange dim contributions, then × area, capped 60.
          const _bRadius = Math.min(60, (tuneOf('item_asmons_desk_banana', 'blastRadius', 36) + _bRangeBonus) * _bAreaMult);
          // BETA 20.10 — LEGENDARY monkey bomb. Throw bananas in random
          // directions (not under the player). Each lures nearby enemies via
          // trapLure.ts, then on first contact AoE-damages (with crit) the whole
          // lured group and auto-vacuums their loot to the player. Banana =
          // burst damage / crit; Whoopie = AoE freeze. Two distinct legendaries.
          // BETA 21.71 — Dylan: base ONE banana per drop; +1 per projectile-count
          // buff (was hardcoded 3). Mirrors bungulators/birds (engine.ts:4152/4354).
          const _bThrowCount = Math.max(1, 1 + (player.projectileCountBonus ?? 0));
          for (let _ti = 0; _ti < _bThrowCount; _ti++) {
            const _ang = Math.random() * Math.PI * 2;
            const _dist = 70 + Math.random() * 70;
            const _tx = player.x + Math.cos(_ang) * _dist;
            const _ty = player.y + Math.sin(_ang) * _dist;
            spawnGroundEffectAt(ws, _tx, _ty, {
            radius: _bRadius,
            lifetime: tuneOf('item_asmons_desk_banana', 'trapLifeSec', 30.0),
            slowMult: tuneOf('item_asmons_desk_banana', 'enemySlowMult', 1.0),
            dotPerSec: 0,
            color: 'rgba(0,0,0,0)',
            weaponId: 'item_asmons_desk_banana',
            groundSpriteUrl: '/items/item_asmons_desk_banana_ground.png',
            groundSpriteUrlAfterStep: '/items/item_asmons_desk_banana_ground.png',
            onFirstStep: (effect) => {
              // BETA 20.72 — banana slip gets its identity: spinning yellow
              // PEEL CHUNKS arcing out under gravity + brown trailing motes —
              // matching the new whip-arc strokes in renderGroundEffects.
              // Was two default-circle bursts (the banned shared look).
              spawnParticles(state.particles, effect.x, effect.y, '#ffcc33', 14, { shape: 'shard', spin: 10, spinRandomSign: true, gravity: 300, speedMin: 60, speedMax: 220, lifeMin: 0.3, lifeMax: 0.6, sizeMin: 2.5, sizeMax: 5 });
              spawnParticles(state.particles, effect.x, effect.y, '#8b6f47', 10, { shape: 'mote', gravity: 120, speedMin: 30, speedMax: 120, lifeMin: 0.3, lifeMax: 0.55 });
              playBananaBombBurst(effect.x, effect.y);   // BETA — activate/burst sound on first enemy contact
              const _aoeR = effect.radius + tuneOf('item_asmons_desk_banana', 'aoeRadiusBonus', 30);
              const _ess = getEnemySystemState();
              if (_ess) {
                for (const _en of _ess.enemies) {
                  if (!_en.alive) continue;
                  const _ex = _en.x - effect.x, _ey = _en.y - effect.y;
                  if (_ex * _ex + _ey * _ey > _aoeR * _aoeR) continue;
                  // Base damage + dimension-bonus damage, scales with damageBonus + crit
                  const baseDmg = tuneOf('item_asmons_desk_banana', 'hitDamage', 35) + _bDamageBonus;
                  const isCrit = Math.random() < _bCritChance;
                  const finalDmg = isCrit
                    ? Math.round(baseDmg * _bDmgMult * (1 + _bCritDmg))
                    : Math.round(baseDmg * _bDmgMult);
                  enemiesModule?.damageEnemy?.(_en.id, finalDmg, { x: 0, y: 0 });
                  trackItemDmg('item_asmons_desk_banana', finalDmg);
                }
              }
              trackItemProc('item_asmons_desk_banana');
              // Auto-collect loot from the kills (Dylan: gold/xp comes to you).
              if (state.lootOrbs) vacuumOrbsNear(state.lootOrbs, effect.x, effect.y, _aoeR + tuneOf('item_asmons_desk_banana', 'lootGrabBonus', 50), state.localPlayerId); // BETA 23.6x — CO-OP PHASE 2 part D: owned-orb guard (local magnet)
              effect.lifetime = Math.min(effect.lifetime, tuneOf('item_asmons_desk_banana', 'popFadeSec', 0.4));
            },
            });
          }
          trackItemQPress('item_asmons_desk_banana');
          // BETA 16.38 — flash the tray slot when banana auto-drops
          flashActiveItem('item_asmons_desk_banana');
        }
        // Cooldown: 8s base − bananaCdReduce dimension picks. Floor at 1s.
        // BETA 17.35 — cycle-30aq audit: floor 1.0 → 2.0 (same reason
        // as whoopie above). Capped maxStacks 5 + 2s floor closes the
        // perma-knockback screen problem.
        const _bAsMult = 1 + (player.attackSpeedBonus ?? 0) / 100;
        _bananaDropTimer = Math.max(tuneOf('item_asmons_desk_banana', 'minCooldownSec', 2.0), (tuneOf('item_asmons_desk_banana', 'dropCooldownSec', 8.0) - _bCdReduce) / _bAsMult);
      }
    }
  }

  // ── BETA 14.04 — Post-damage timer tick (drives Not Xanax perc condition) ──
  if ((state._postDamageTimer ?? 0) > 0) {
    state._postDamageTimer = Math.max(0, (state._postDamageTimer ?? 0) - dt);
  }

  // ── BETA 14.04 — On-kill hooks (Mom's Cooking heal + Mystery Meat roll) ──
  // Track killCount delta since last frame. For each kill this frame, fire
  // the on_kill effects of items in the inventory. state.killCount is the
  // engine's running kill total maintained by enemySystem on each enemy death.
  {
    const curKills = state.killCount ?? 0;
    const killDelta = Math.max(0, curKills - _lastFrameTotalKills);
    _lastFrameTotalKills = curKills;
    if (killDelta > 0) {
      const inv = getItemState()?.inventory;
      if (inv) {
        // Mom's Cooking — heal +5 HP per stack on each kill (BETA 19.97: 2 → 5)
        // 23.39 — item KEPT in the purge (name/art show no likeness); the
        // internal item id still says caseoh — renaming needs a stash-data
        // migration, flagged to Dylan.
        // BETA 16.04 — gated when hp <= 0 so kill-heal doesn't resurrect a
        // dead player before engine's death check fires.
        // BETA 20.00 — UN-GATED from Debt (Dylan: healing keeps running).
        const momsSlot = inv.find(s => s.item.id === 'item_caseoh_moms_cooking');
        if (momsSlot && player.hp > 0) {
          // BETA 20.77 — kill-count taper (god-mode audit: at 8,636 kills the
          // flat 5/kill formula could supply 6.5× ALL damage taken; Mystery
          // Meat got this exact taper in 16.91, Mom's never did). Same tiers.
          const _momsAny = state as unknown as { _momsCookingTotalHeals?: number };
          const _momsTotal = _momsAny._momsCookingTotalHeals ?? 0;
          const _momsDim =
            _momsTotal < 100  ? 1.0  :
            _momsTotal < 500  ? 0.5  :
            _momsTotal < 1000 ? 0.25 :
            _momsTotal < 3000 ? 0.10 :
                                0.05;
          _momsAny._momsCookingTotalHeals = _momsTotal + killDelta;
          const healAmt = tuneOf('item_caseoh_moms_cooking', 'healPerKill', 5) * momsSlot.stacks * killDelta * _momsDim;
          const beforeHp = player.hp;
          player.hp = Math.min(player.maxHp, player.hp + healAmt);
          // BETA 16.37 — track Mom's Cooking healing in run-level counters
          // (PSBOT cycle-30i §7E Bug C — heal counters were always 0).
          const actualHealed = player.hp - beforeHp;
          if (actualHealed > 0) {
            const _sAnyMoms = state as unknown as Record<string, number | undefined>;
            _sAnyMoms._healingReceivedThisRun = (_sAnyMoms._healingReceivedThisRun ?? 0) + actualHealed;
            // BETA 19.99 — was += killDelta. 50 kills in one frame counted as
            // 50 heal events even though only ONE heal application happened.
            // healEventsThisRun is meant to count heal APPLICATIONS, not HP
            // healed. Use += 1 to match every other heal counter (lifesteal,
            // VooGoo, regen).
            state._healEventsThisRun = (state._healEventsThisRun ?? 0) + 1;
          }
          // BETA 14.62 — Mom's Cooking item proc + dmgDealt tracker (heal as
          // "damage dealt to nothing" doesn't apply; track applies as proc).
          for (let i = 0; i < killDelta; i++) trackItemProc('item_caseoh_moms_cooking');
          // BETA 14.62 — Synergy: caseohCooking_voogooRager fires when both
          // equipped. valueDelta = HP healed by Mom's (analyst can compare
          // vs voogooRager Q-heal usage to gauge sustained healing build).
          const hasVoogoo = inv.some(s => s.item.id === 'item_voogoo_rager');
          if (hasVoogoo) {
            trackSynergyTrigger('caseohCooking_voogooRager', healAmt);
          }
        }
        // Mystery Meat Stew — for each kill × stacks, roll one random tiny boost
        // and accumulate into state._mysteryMeatBonuses (applied via itemsAdapter
        // recompute path: maxHp += accum.maxHp, damageBonus += accum.damageBonus,
        // luck += accum.luck).
        const meatSlot = inv.find(s => s.item.id === 'item_mystery_meat_stew');
        if (meatSlot) {
          if (!state._mysteryMeatBonuses) state._mysteryMeatBonuses = { maxHp: 0, damageBonus: 0, luck: 0 };
          const rolls = meatSlot.stacks * killDelta;
          const _meatRollDelta = { maxHp: 0, damageBonus: 0, luck: 0 };
          // BETA 16.91 / 17.15 — PSBOT cycle-30af confirms MMS still runaway-
          // capable. Cowboy 3748 kills floor 2 = 3748 rolls. Pushing the taper
          // harder so Cowboy-scale runs flatten out around 1000-3000 rolls
          // instead of just slowing. Curve:
          //   0-100    rolls = 100% (early ramp feels rewarding)
          //   100-500  rolls = 50%  (mid-game scaling)
          //   500-1000 rolls = 25%  (start tapering)
          //   1000-3000 rolls = 10% (late-game soft cap)
          //   3000+    rolls = 5%   (effectively flat past 3k)
          const _meatStateAny = state as unknown as { _mysteryMeatTotalRolls?: number };
          const _baseMaxHp = 0.5;
          const _baseDmg = 0.5;
          const _baseLuck = 0.1;
          for (let i = 0; i < rolls; i++) {
            const totalRolls = _meatStateAny._mysteryMeatTotalRolls ?? 0;
            // BETA 20.77 — real tail tiers (god-mode audit: the flat 5% tail
            // was LINEAR forever — rolls 3,000-17,272 contributed MORE total
            // (+damage% than everything before them; meat alone = +223% of
            // Dylan's +430%).
            const dimMag =
              totalRolls < 100  ? 1.0  :
              totalRolls < 500  ? 0.5  :
              totalRolls < 1000 ? 0.25 :
              totalRolls < 3000 ? 0.10 :
              totalRolls < 6000 ? 0.02 :
                                  0.005;
            _meatStateAny._mysteryMeatTotalRolls = totalRolls + 1;
            const r = Math.random();
            if (r < 0.333) {
              const delta = _baseMaxHp * dimMag;
              state._mysteryMeatBonuses.maxHp += delta;
              _meatRollDelta.maxHp += delta;
            } else if (r < 0.666) {
              const delta = _baseDmg * dimMag;
              state._mysteryMeatBonuses.damageBonus += delta;
              _meatRollDelta.damageBonus += delta;
            } else {
              const delta = _baseLuck * dimMag;
              state._mysteryMeatBonuses.luck += delta;
              _meatRollDelta.luck += delta;
            }
          }
          // Floating popup near the player so Dylan sees what rolled this
          // frame. Skip if no rolls happened (rolls=0 means no kills).
          if (rolls > 0) {
            const _wsMeat = getWeaponSystemState();
            if (_wsMeat) {
              const parts: string[] = [];
              if (_meatRollDelta.maxHp > 0)        parts.push(`+${_meatRollDelta.maxHp.toFixed(1)} HP`);
              if (_meatRollDelta.damageBonus > 0)  parts.push(`+${_meatRollDelta.damageBonus.toFixed(1)}% DMG`);
              if (_meatRollDelta.luck > 0)         parts.push(`+${_meatRollDelta.luck.toFixed(1)} LUCK`);
              if (parts.length > 0) {
                spawnDamageNumber(_wsMeat, {
                  x: state.player.x + (Math.random() - 0.5) * 30,
                  y: state.player.y - 60,
                  value: 0,
                  isCrit: false,
                  isBuff: true,
                  label: `🍖 ${parts.join('  ')}`,
                  timer: 1.0,
                  velocityY: -40,
                });
              }
            }
          }
          // BETA 14.62 — Mystery Meat Stew proc tracker (one proc per roll).
          for (let i = 0; i < rolls; i++) trackItemProc('item_mystery_meat_stew');
          // BETA 14.62 — Synergy: mysteryMeatStew_kills fires on every kill
          // when MMS equipped (the synergy is "kill source agnostic" per pair
          // def using "*"). triggerCount accumulates per kill.
          for (let i = 0; i < killDelta; i++) trackSynergyTrigger('mysteryMeatStew_kills', 0);
        }
      }
    }
  }

  ultraChestPool.updateAll(state, dt);

  // ── BETA 20.49 — Blue Elite WildCardMachine: drain queued elite-death positions and
  // spawn a free blue legendary chest at each. Done engine-side because the
  // enemy-system death hook only has EnemyState, not the map/chests array. ──
  {
    const _ess = getEnemySystemState();
    const _eliteSpawns = _ess?.pendingEliteChestSpawns;
    if (_eliteSpawns && _eliteSpawns.length > 0) {
      for (const sp of _eliteSpawns) {
        state.map.roomMap.chests.push(createEliteMachine(sp.x, sp.y));
      }
      // BETA 20.67 — count chests DROPPED (deep-dive medium: only opens were
      // tracked, so the chest economy + Charter §4 reward beats were blind).
      const _sChDrop = state as unknown as Record<string, number | undefined>;
      _sChDrop._eliteChestsDroppedThisRun = (_sChDrop._eliteChestsDroppedThisRun ?? 0) + _eliteSpawns.length;
      _eliteSpawns.length = 0;
    }
  }

  // ── BETA 20.57 — Peanut's Big Brain (R09): XP orbs left on the ground damage
  // enemies that walk into them (the blue burst on XP PICKUP was removed — too
  // spammy). Throttled ~0.3s tick so it's a hazard zone, not an instant-delete;
  // one hit per enemy per tick. No burst VFX — just the enemy damage numbers. ──
  {
    const _bbSlot = getItemState()?.inventory.find(s => s.item.id === 'R09');
    if (_bbSlot && state.lootOrbs && state.enemies.length > 0) {
      const _bbT = state as unknown as { _bigBrainTimer?: number };
      _bbT._bigBrainTimer = (_bbT._bigBrainTimer ?? 0) + dt;
      if (_bbT._bigBrainTimer >= tuneOf('R09', 'tickSeconds', 0.45)) {  // BETA 21.81 — editable tick rate
        _bbT._bigBrainTimer = 0;
        const _bbDmg = Math.round(tuneOf('R09', 'dmgPerStack', 1) * _bbSlot.stacks * (1 + (state.player.damageBonus ?? 0) / 100)); // BETA 21.81 — editable per-copy chip damage
        const _xpOrbs = state.lootOrbs.orbs.filter(o => o.type === 'xp');
        if (_xpOrbs.length > 0) {
          for (const e of state.enemies) {
            const _e = e as unknown as { alive?: boolean; x: number; y: number; id: number; hitboxRadius?: number };
            if (!_e.alive) continue;
            const _r = (_e.hitboxRadius ?? 18) + tuneOf('R09', 'touchRange', 16); // BETA 21.81 — editable "walk into the orb" range
            const _r2 = _r * _r;
            for (const o of _xpOrbs) {
              const dx = _e.x - o.x, dy = _e.y - o.y;
              if (dx * dx + dy * dy < _r2) {
                state.pendingEnemyDamage.push({ id: _e.id, amount: _bbDmg });
                // BETA 20.72 — R09's signature finally visible: cyan
                // sparkle-rays at the orb that zapped (VISUAL ONLY, damage
                // unchanged). Tick is already throttled to 0.45s so this
                // can't spam.
                spawnBigBrainSparkle(o.x, o.y);
                break; // one hit per enemy per tick
              }
            }
          }
        }
      }
    }
  }

  // Drain pending enemy damage from ultra item effects
  if (state.pendingEnemyDamage.length > 0) {
    for (const hit of state.pendingEnemyDamage) {
      enemiesModule?.damageEnemy?.(hit.id, hit.amount,
        hit.knockbackX != null ? { x: hit.knockbackX, y: hit.knockbackY ?? 0 } : undefined);
    }
    state.pendingEnemyDamage = [];
  }

  // ── Rage Quit Aura: keep vacuuming loot for 3s after the map wipe ──
  // BETA 20.19 — the wipe's loot drops a frame or two after the kills resolve,
  // so we keep pulling everything to the player for 3s (chick-magnet).
  if ((state.player.ultraState?.rageQuit_vacuumTimer ?? 0) > 0) {
    state.player.ultraState!.rageQuit_vacuumTimer -= dt;
    if (state.lootOrbs) vacuumAllOrbs(state.lootOrbs, state.localPlayerId); // BETA 23.6x — CO-OP PHASE 2 part D: owned-orb guard (local magnet)
  }

  // ── Donation Alert: chat "donations" → temporary DAMAGE surge ──
  // BETA 20.19 — redesign (Dylan): no more gold drop. On a kill streak with no
  // recent damage ("chat thinks you're doing well"), chat donates → a temp
  // +50–100% damage surge for 8s. Luckier = donations come more often. Tunable
  // (Dylan: "can balance it if it is too OP"). Add/revert mirrors The Grind's
  // proven damageMultiplier pattern.
  if (state.player.activeUltraItems?.includes('ultra_donation_alert')) {
    const u = state.player.ultraState!;
    // tick + revert an active surge
    if ((u.donation_buffTimer ?? 0) > 0) {
      u.donation_buffTimer -= dt;
      if (u.donation_buffTimer <= 0 && u.donation_buffActive) {
        player.damageMultiplier = (player.damageMultiplier ?? 1) - (u.donation_buffDmg ?? 0);
        u.donation_buffActive = false;
      }
    }
    const newKills = state.killCount - (u.donation_prevKills ?? 0);
    if (newKills > 0) {
      u.donation_prevKills = state.killCount;
      u.donation_killCount = (u.donation_killCount ?? 0) + newKills;
      const _luck = player.luck ?? 0;
      const _threshold = Math.max(15, 40 - _luck * 0.15);    // luckier = donate sooner
      const _doingWell = (state._postDamageTimer ?? 0) <= 0; // no recent damage = chat happy
      if (u.donation_killCount >= _threshold && _doingWell && !u.donation_buffActive) {
        u.donation_killCount = 0;
        u.donation_procs = (u.donation_procs ?? 0) + 1; // BETA 20.39 — telemetry
        const _dmgBuff = 0.5 + Math.min(_luck, 100) * 0.005;  // +50%..+100% damage (luck-scaled)
        player.damageMultiplier = (player.damageMultiplier ?? 1) + _dmgBuff;
        u.donation_buffActive = true;
        u.donation_buffTimer = 8.0;
        u.donation_buffDmg = _dmgBuff;
        const msg = DONATION_MESSAGES[Math.floor(Math.random() * DONATION_MESSAGES.length)];
        state.pickupNotifications.push({ text: `💸 ${msg} (+${Math.round(_dmgBuff*100)}% DMG, 8s)`, color: '#ffd700', timer: 3.5 });
      }
    }
  }

  // BETA 14.55 — Kill events for eventLog. Same delta-from-last-frame pattern
  // as the chat onKill below. Cap at 200 kills logged (drop-oldest); for long
  // runs the recent kills are what the analyst cares about.
  {
    const newKillsForEvent = (state.killCount ?? 0) - _eventLogPrevKills;
    if (newKillsForEvent > 0) {
      for (let i = 0; i < newKillsForEvent; i++) {
        pushEventLog({ t: state.time ?? 0, type: 'kill', data: {} });
      }
      _eventLogPrevKills = state.killCount ?? 0;
    }
  }

  // BETA 14.57/14.59 — DeathContext: track HP drops to populate the recent-
  // hits sliding window. BETA 14.59 added source threading via
  // weaponLab.setLastDamageSource (called by enemySystem before player.damage)
  // + consumeLastDamageSource here. Source clears on each consume so multi-
  // hit-same-frame works (last source wins).
  if (_lastHpForDeathTracking < 0) _lastHpForDeathTracking = player.hp;
  if (player.hp < _lastHpForDeathTracking) {
    const dmgAmount = _lastHpForDeathTracking - player.hp;
    const source = consumeLastDamageSource();
    _recordHitForDeath(state.time ?? 0, source, dmgAmount);
  }
  // BETA 22.48 — if HP recovered above 0 (a Debt/Sheeesh/Liar save bumped it
  // back up), re-arm the one-shot death-clarity console flag so the NEXT death
  // attempt logs again (otherwise an early save would swallow the real death's log).
  if (player.hp > 0 && _deathClarityLogged) _deathClarityLogged = false;
  _lastHpForDeathTracking = player.hp;

  // BETA 14.55 — NearMiss detection. Track HP fraction across frames.
  // Trigger conditions: HP dipped below 30% → record nadir + dip time.
  // Recovery: HP returned above 60% within 5 seconds → push near-miss event.
  // Reset if window expires or player died.
  {
    const hpFrac = player.maxHp > 0 ? player.hp / player.maxHp : 0;
    if (player.hp <= 0) {
      // Death cancels any pending near-miss
      _nearMissNadirHp = -1;
      _nearMissDipTime = 0;
    } else if (_nearMissNadirHp < 0) {
      // Not currently tracking. Start tracking if we just dipped low.
      if (hpFrac < _NEAR_MISS_LOW_FRAC) {
        _nearMissNadirHp = player.hp;
        _nearMissDipTime = state.time ?? 0;
      }
    } else {
      // Currently tracking a dip. Check for recovery or expiry.
      if (player.hp < _nearMissNadirHp) {
        // HP went even lower — update nadir
        _nearMissNadirHp = player.hp;
      }
      const elapsed = (state.time ?? 0) - _nearMissDipTime;
      if (elapsed > _NEAR_MISS_WINDOW_SEC) {
        // Window expired without recovery — reset tracking
        _nearMissNadirHp = -1;
        _nearMissDipTime = 0;
      } else if (hpFrac > _NEAR_MISS_RECOVER_FRAC) {
        // Recovered! Push the near-miss event.
        pushNearMissEvent({
          t: _nearMissDipTime,
          hpAtNadir: _nearMissNadirHp,
          recoveryTime: elapsed,
          recoverySource: 'recovered',
        });
        _nearMissNadirHp = -1;
        _nearMissDipTime = 0;
      }
    }
  }

  // ── Overlay focus-screen registration (controller card navigation) ──
  // BETA 22.49 — the live-chat event detection that used to wrap this block
  // is gone; this now only drives gamepad focus + hover for the pick screens.
  {
    // BETA 16.77 — Per Dylan: "i still cant navigate which card or
    // upgradei want with analog sticks or directional pad using
    // controller." Register focus targets when an overlay screen
    // activates (level-up / shrine / chest choice). Cards are fixed at
    // 3 picks; SKIP + REROLL are also focusable. dpad-left/right
    // cycles between cards, dpad-down jumps to SKIP/REROLL row.
    // A button activates focused option; B button skips; Y button
    // (mapped to 'r' keypress in gamepadInput) rerolls.
    if (state.phase !== _chatPrevPhase) {
      // TS narrowed state.phase to 'playing'|'death_replay' due to the
      // early return at line ~1496 — but state.phase can still mutate to
      // 'levelup' / 'shrine_choice' / 'chest_choice' / 'paused' inside
      // this function (e.g. line ~2474). Use a string cast so the
      // dispatch below sees the actual runtime value.
      const phase = state.phase as string;
      // Tear down old focus screen on every transition
      unregisterFocusScreen('levelup_cards');
      unregisterFocusScreen('shrine_cards');
      unregisterFocusScreen('chest_cards');
      unregisterFocusScreen('ultra_chest');
      unregisterFocusScreen('gameover_screen');
      unregisterFocusScreen('won_screen');
      // BETA 16.82 — Per Dylan: "still no focus boxes on shrine
      // upgrades, chest upgrades, free and cost ones, and no focus
      // boxes on level ups in xbox rog ally x in controller mode."
      // Root cause: rects were hardcoded as { x: 200, y: 240, w: 280,
      // h: 380 } — a 1280-logical guess that didn't match the actual
      // responsive render math (cardW=229, startX=281 on a 1280px Ally
      // at 720p with HUD_MARGIN=280). The focus box was off-screen-ish
      // so even with the focus-to-hover sync, no visible feedback.
      // Now: rects mirror the EXACT math in renderLevelUpScreen /
      // renderShrineChoiceScreen / renderChestChoiceScreen using the
      // last-rendered canvas dimensions captured at the top of
      // renderGame (_lastRenderW / _lastRenderH).
      if (phase === 'levelup' || phase === 'shrine_choice' || phase === 'chest_choice') {
        const W = _lastRenderW;
        const H = _lastRenderH;
        // BETA 16.86 — Use the same computeChoiceCardLayout helper that
        // the render functions use, so focus rects ALWAYS match the
        // visible card positions regardless of HUD-visibility centering
        // adjustments.
        // BETA 18.6 — Per Dylan (ROG Ally X): "if i click A it will reroll
        // the items for free as many times as i want." Root cause: the
        // gamepad reroll handler called the reroll function directly with
        // no rerollsLeft check + no decrement. Mouse-click reroll path in
        // GameCanvas.tsx:1250 correctly gates `if (gs.rerollsLeft <= 0)
        // return; gs.rerollsLeft--;`. Gamepad path was missing both. Now
        // wrapped with the same gate so controller and mouse behave
        // identically — 3 rerolls per run, shared across screens.
        const _doReroll = (fn: () => void): void => {
          if ((state.rerollsLeft ?? 0) <= 0) return;
          state.rerollsLeft = (state.rerollsLeft ?? 0) - 1;
          fn();
          if (!state.pickupNotifications) state.pickupNotifications = [];
          state.pickupNotifications.push({ text: `REROLLED — ${state.rerollsLeft} left`, color: '#6699ff', timer: 1.5 });
        };
        if (phase === 'levelup') {
          const L = computeChoiceCardLayout(W, H, 3, !!state.hudHidden, 'levelup');
          const rerollY = L.cardY + L.cardH + 64;
          // BETA 18.11 — Wrap each card onActivate to also set state.phase
          // back to 'playing' on successful pick. Dylan: "i cant fucking
          // click A to chose anything. if i click A everything dissapears."
          // Root cause: handleLevelUpSelection internally clears the cached
          // display data (cachedLevelUpDisplay = null) but doesn't change
          // state.phase. So gamepad A pick → cards vanish (no data to
          // render) but state.phase stays 'levelup' → dark overlay with
          // nothing on it = "everything disappeared." The keyboard handler
          // (GameCanvas.tsx:908) correctly does `if (success) state.phase
          // = 'playing';`. Mirror that here for gamepad activation.
          const _pickLevelUp = (i: number) => {
            if (isLevelUpExitActive()) return;   // BETA 22.87 — exit beat playing, ignore double-picks
            const _snapOpts = getLevelUpOptionDisplayData();
            const success = handleLevelUpSelection(i);
            if (success) {
              // BETA 22.87 — Perk Card System v1: NO instant resume. The exit
              // beat holds the screen ~450ms (chosen card pulses, losers fade),
              // then updateGame flips to 'playing' + launches the fly-to-HUD.
              beginLevelUpExit(state, i, _snapOpts);
              pickConfirmCelebration(state, '#4da6ff'); // BETA 21.45 — confirm burst (blue)
              // BETA 18.14 — defensive synchronous cleanup, see chest _pickCh
              unregisterFocusScreen('levelup_cards');
              setActiveScreen(null);
            }
          };
          registerFocusScreen('levelup_cards', [
            { id: 'card_0', rect: { x: L.startX, y: L.cardY, w: L.cardW, h: L.cardH }, onActivate: () => _pickLevelUp(0) },
            { id: 'card_1', rect: { x: L.startX + L.cardW + L.gap, y: L.cardY, w: L.cardW, h: L.cardH }, onActivate: () => _pickLevelUp(1) },
            { id: 'card_2', rect: { x: L.startX + 2 * (L.cardW + L.gap), y: L.cardY, w: L.cardW, h: L.cardH }, onActivate: () => _pickLevelUp(2) },
            { id: 'reroll', rect: { x: W / 2 - 90, y: rerollY, w: 180, h: 36 }, onActivate: () => _doReroll(rerollLevelUpOptions) },
          ], () => { /* B = no-op on level-up; level must be picked */ }, 0);
          setActiveScreen('levelup_cards');
          // BETA 18.9 — Force default hover so the white outline is visible
          // IMMEDIATELY on level-up screen open, before any gamepad input.
          // Dylan: "i ask dont fucking see any white boxes outlinang anything
          // on upgrades, level ups or shrines." hoverIdx defaulted to -1 (no
          // card hovered) so the 18.6 outline never drew until input fired.
          // Now: card 0 is highlighted on entry regardless of input.
          setLevelUpHoverIndex(0);
        } else if (phase === 'shrine_choice') {
          const L = computeChoiceCardLayout(W, H, 3, !!state.hudHidden, 'shrine');
          const btnY = L.cardY + L.cardH + 28;
          // BETA 18.11 — see _pickLevelUp comment above for why state.phase
          // = 'playing' must happen here in the gamepad activation path.
          const _pickShrine = (i: number) => {
            const result = handleShrineSelection(i);
            if (result) {
              state.phase = 'playing';
              shrinePickCelebration(state);
              if (!state.pickupNotifications) state.pickupNotifications = [];
              state.pickupNotifications.push({ text: `${result.percName}`, color: '#00ffcc', timer: 2.5 });
              // BETA 18.14 — defensive synchronous cleanup, see chest _pickCh
              unregisterFocusScreen('shrine_cards');
              setActiveScreen(null);
            }
          };
          registerFocusScreen('shrine_cards', [
            { id: 'card_0', rect: { x: L.startX, y: L.cardY, w: L.cardW, h: L.cardH }, onActivate: () => _pickShrine(0) },
            { id: 'card_1', rect: { x: L.startX + L.cardW + L.gap, y: L.cardY, w: L.cardW, h: L.cardH }, onActivate: () => _pickShrine(1) },
            { id: 'card_2', rect: { x: L.startX + 2 * (L.cardW + L.gap), y: L.cardY, w: L.cardW, h: L.cardH }, onActivate: () => _pickShrine(2) },
            { id: 'skip',   rect: { x: W / 2 - 200, y: btnY, w: 180, h: 36 }, onActivate: () => skipShrineChoice() },
            { id: 'reroll', rect: { x: W / 2 + 20,  y: btnY, w: 180, h: 36 }, onActivate: () => _doReroll(rerollShrineOptions) },
          ], () => skipShrineChoice(), 0);
          setActiveScreen('shrine_cards');
          setShrineHoverIndex(0); // BETA 18.9 — see levelup comment above.
        } else if (phase === 'chest_choice') {
          const L = computeChoiceCardLayout(W, H, 3, !!state.hudHidden, 'chest');
          const btnY = L.cardY + L.cardH + 28;
          // BETA 18.11 — same state.phase transition fix as level-up/shrine.
          const _pickChest = (i: number) => {
            _handleChestPickWithDimensionPopup(state, i);
            // handleChestItemSelection returned true if the pick succeeded;
            // unconditionally transition to playing here since the pick
            // helper already cleared the cached display data.
            state.phase = 'playing';
            // BETA 18.14 — defensive synchronous cleanup, see chest _pickCh
            unregisterFocusScreen('chest_cards');
            setActiveScreen(null);
          };
          registerFocusScreen('chest_cards', [
            { id: 'card_0', rect: { x: L.startX, y: L.cardY, w: L.cardW, h: L.cardH }, onActivate: () => _pickChest(0) },
            { id: 'card_1', rect: { x: L.startX + L.cardW + L.gap, y: L.cardY, w: L.cardW, h: L.cardH }, onActivate: () => _pickChest(1) },
            { id: 'card_2', rect: { x: L.startX + 2 * (L.cardW + L.gap), y: L.cardY, w: L.cardW, h: L.cardH }, onActivate: () => _pickChest(2) },
            { id: 'skip',   rect: { x: W / 2 - 200, y: btnY, w: 180, h: 36 }, onActivate: () => skipChestChoice() },
            { id: 'reroll', rect: { x: W / 2 + 20,  y: btnY, w: 180, h: 36 }, onActivate: () => _doReroll(rerollChestOptions) },
          ], () => skipChestChoice(), 0);
          setActiveScreen('chest_cards');
          setChestHoverIndex(0); // BETA 18.9 — see levelup comment above.
        }
      } else if (phase === 'paused') {
        // Pause menu has its own React-side registration in PauseMenu.tsx.
      } else if (phase === 'gameover' || phase === 'won') {
        // BETA 16.93 — Controller phase 3: focus screens for post-run
        // end screens. Death screen has REPLAY/QUIT canvas buttons
        // (visible after 3s); win screen is keyboard-only (R restart,
        // Esc menu) so we synthesize an equivalent focus target.
        // onActivate dispatches a synthetic KeyboardEvent('keydown')
        // because GameCanvas's R/Esc handlers run on DOM keydown
        // events, not on state.keys reads.
        const _fireKey = (key: string) => {
          if (typeof window === 'undefined') return;
          window.dispatchEvent(new KeyboardEvent('keydown', { key, bubbles: true }));
        };
        const W = _lastRenderW;
        const H = _lastRenderH;
        if (phase === 'gameover') {
          const rects = getDeathScreenButtonRects(W, H, 999);
          registerFocusScreen('gameover_screen', [
            { id: 'replay', rect: rects.replay, onActivate: () => _fireKey('r') },
            { id: 'quit',   rect: rects.quit,   onActivate: () => _fireKey('Escape') },
          ], () => _fireKey('Escape'), 0);
          setActiveScreen('gameover_screen');
        } else {
          // Win screen — synthetic "restart" target where the
          // keyboard prompt renders.
          const btnW = Math.min(220, W * 0.22);
          const btnH = 56;
          const restartX = (W - btnW) / 2;
          const restartY = H * 0.86;
          registerFocusScreen('won_screen', [
            { id: 'restart', rect: { x: restartX, y: restartY, w: btnW, h: btnH }, onActivate: () => _fireKey('r') },
          ], () => _fireKey('Escape'), 0);
          setActiveScreen('won_screen');
        }
      } else {
        // playing → no focus screen
        setActiveScreen(null);
      }
    }
    _chatPrevPhase = state.phase;

    // BETA 16.79 — MIRROR GAMEPAD FOCUS → CANVAS HOVER for the in-game
    // overlay screens. Per Dylan: "i cant use anolog or directional pad
    // to chose what i want." Root cause: gamepadFocus tracked the
    // focused id internally but never told the canvas-rendered cards
    // (level-up / shrine / chest) which one was focused — so the
    // visual hover indicator stayed on whatever the mouse last touched.
    // Now: each frame, read the focused id and call the matching
    // setHoverIndex so the existing hover-bright render runs on the
    // gamepad-focused card.
    {
      const _activeFocusScreen = getActiveScreen();
      if (_activeFocusScreen === 'levelup_cards' || _activeFocusScreen === 'shrine_cards' || _activeFocusScreen === 'chest_cards') {
        const focused = getFocusedTarget();
        if (focused) {
          let cardIdx = -1;
          if (focused.id === 'card_0') cardIdx = 0;
          else if (focused.id === 'card_1') cardIdx = 1;
          else if (focused.id === 'card_2') cardIdx = 2;
          if (cardIdx >= 0) {
            if (_activeFocusScreen === 'levelup_cards') setLevelUpHoverIndex(cardIdx);
            else if (_activeFocusScreen === 'shrine_cards') setShrineHoverIndex(cardIdx);
            else if (_activeFocusScreen === 'chest_cards') setChestHoverIndex(cardIdx);
          }
        }
      }
    }

    // BETA 22.49 — removed the live-chat triggers (first-kill / boss roast)
    // and the hype-train shield + speed buffs (both were part of the chat
    // feature). The focus-screen registration above is the only logic kept.
  }

  // ── Minimap fog-of-war reveal ──
  updateMinimapFog(state);

  // BETA 14.34 Phase 5 — frame-perf sampler closeout. Measure total frame
  // time from start. If > 33ms (sub-30fps), record as a spike (cap 50 spikes
  // per run to bound memory). Update worst + running avg.
  {
    // BETA 20.46 — measure BOTH clocks: updateMs = engine logic only (this was the
    // misleading number — ~1-2ms even at 30fps); realMs = the FULL frame the player
    // actually feels (update + render + browser present), fed in from the game loop.
    // realMs is what "fps" means; the gap (realMs - updateMs) is render/browser cost.
    const updateMs = performance.now() - _frameStartMs;
    const realMs = (state as unknown as { _realFrameMs?: number })._realFrameMs || updateMs;
    const perf = state._engineFramePerfThisRun ?? { spikes: [], avgFrameTimeMs: 0, worstFrameTimeMs: 0 };
    // BETA 20.45 — on-screen load counts (cheap; reused for spike-context + the
    // human trace). reduce avoids the per-frame array alloc that filter() does.
    const _aliveN = state.enemies ? state.enemies.reduce((a, e) => a + (e.alive ? 1 : 0), 0) : 0;
    const _projN = getWeaponSystemState()?.projectiles?.length ?? 0;
    const _partN = state.particles?.length ?? 0;
    if (realMs > 33 && perf.spikes.length < 50) {
      // BETA 20.45/46 — a REAL <30fps frame, enriched with WHAT was on screen
      // (enemies / projectiles / particles) AND the update-vs-render split so a
      // spike points at its CAUSE (logic vs render) not just a timestamp.
      (perf.spikes as Array<Record<string, number>>).push({
        t: Math.round((state.time ?? 0) * 10) / 10, frameTimeMs: Math.round(realMs),
        updateMs: Math.round(updateMs * 10) / 10, alive: _aliveN, proj: _projN, particles: _partN,
      });
    }
    if (realMs > perf.worstFrameTimeMs) perf.worstFrameTimeMs = realMs;
    // Running avg via simple exponential moving average (REAL frame time)
    perf.avgFrameTimeMs = perf.avgFrameTimeMs === 0 ? realMs : perf.avgFrameTimeMs * 0.95 + realMs * 0.05;
    state._engineFramePerfThisRun = perf;
    setCurrentFramePerf(perf);

    // ── BETA 20.45 — HIGH-FREQUENCY HUMAN PLAY RECORDER ───────────────────────
    // Dylan: precise ground-truth data from REAL human play (bot training +
    // pinpointing "felt off" moments). Samples a compact behavioral snapshot ~3×/s
    // while playing. FLAT-ARRAY rows (no repeated JSON keys) keep the payload small;
    // a drop-oldest ring buffer bounds it to the last ~8 minutes.
    // Row schema = humanTraceCols in livePlayTracker:
    //   [t, px, py, vx, vy, hp, alive, nearestEnemyDist, playerProjAlive, frameMs, kills]
    if (state.phase === 'playing') {
      const _ht = state as unknown as { _humanTrace?: number[][]; _humanTraceNext?: number };
      const _now = state.time ?? 0;
      if (!_ht._humanTrace) _ht._humanTrace = [];
      if (_ht._humanTraceNext === undefined) _ht._humanTraceNext = 0;
      if (_now >= _ht._humanTraceNext) {
        _ht._humanTraceNext = _now + 0.34;   // ~3 Hz
        const p = state.player;
        let _nd = -1;   // nearest live-enemy distance (threat proximity); -1 = none
        if (state.enemies) {
          let best = Infinity;
          for (const e of state.enemies) {
            if (!e.alive) continue;
            const dx = e.x - p.x, dy = e.y - p.y; const d = dx * dx + dy * dy;
            if (d < best) best = d;
          }
          _nd = best === Infinity ? -1 : Math.round(Math.sqrt(best));
        }
        const R = Math.round;
        _ht._humanTrace.push([
          R(_now * 10) / 10, R(p.x), R(p.y), R(p.vx ?? 0), R(p.vy ?? 0),
          R(p.hp), _aliveN, _nd, _projN, R(realMs), state.killCount ?? 0, R(updateMs),
        ]);
        if (_ht._humanTrace.length > 1500) _ht._humanTrace.shift();   // ring buffer
      }
    }
  }

  return state;
}

// ─── Minimap Fog-of-War ────────────────────────────────────────────────────────

let _lastFogTime = 0;
function updateMinimapFog(state: GameState): void {
  // Reset if a new run started (state.time was reset to 0)
  if (_lastFogTime > state.time) _lastFogTime = 0;
  // 20 Hz is plenty for fog-of-war — no need to recalculate every frame
  if (state.time - _lastFogTime < 0.05) return;
  _lastFogTime = state.time;

  const { player, minimapFog, minimapFogSize } = state;
  const WS = state.map.WORLD_SIZE;
  const revealRadius = state.minimapRevealRadius;

  // Convert player world position to fog grid position
  const gridScale = minimapFogSize / WS;
  const pcx = player.x * gridScale;
  const pcy = player.y * gridScale;
  const gridRadius = revealRadius * gridScale;
  const gridRadiusSq = gridRadius * gridRadius;

  // Only update cells within the bounding box of the reveal circle
  const minGx = Math.max(0, Math.floor(pcx - gridRadius));
  const maxGx = Math.min(minimapFogSize - 1, Math.ceil(pcx + gridRadius));
  const minGy = Math.max(0, Math.floor(pcy - gridRadius));
  const maxGy = Math.min(minimapFogSize - 1, Math.ceil(pcy + gridRadius));

  for (let gy = minGy; gy <= maxGy; gy++) {
    for (let gx = minGx; gx <= maxGx; gx++) {
      const dx = gx - pcx;
      const dy = gy - pcy;
      const distSq = dx * dx + dy * dy;
      if (distSq <= gridRadiusSq) {
        const idx = gy * minimapFogSize + gx;
        // Soft edge: full reveal in inner 70%, fade to edge
        const dist = Math.sqrt(distSq);
        const t = dist / gridRadius;
        const reveal = t < 0.7 ? 1.0 : 1.0 - ((t - 0.7) / 0.3);
        if (reveal > minimapFog[idx]) {
          minimapFog[idx] = reveal;
          minimapFogDirty = true; // BETA 13.87 — flag for renderer to rebuild fog canvas only when fog actually changed
        }
      }
    }
  }
}

// ─── WildCardMachine Cost Escalation ──────────────────────────────────────────────────────

function getChestCost(chestsOpenedThisRun: number, _gameTime: number = 0, goldCollectedThisRun: number = 0): number {
  // BETA 16.20 — PSBOT cycle-23: 1.5^N → 1.7^N. Per Dylan + cycle-23 audit:
  // long Killa run had 27k gold + 14 chests opened, chest 14 cost 4.4k.
  // No real economic gate. New schedule below pushes the wall down hard.
  //
  // Tuning history:
  //   - BETA 15.41: 3^N  (too steep — chest 10 cost 295,245g)
  //   - BETA 15.67: 1.2^N (too soft — chest 15 only cost 230g)
  //   - BETA 15.72: 1.5^N (middle ground; cycle-23 found still too soft)
  //   - BETA 16.20: 1.7^N (per cycle-23)
  //
  // Schedule (MACHINE_BASE_COST = 15g, base 1.7):
  //   1st (N=0):    15g       6th (N=5):   213g      11th (N=10):  4,084g
  //   2nd (N=1):    26g       7th (N=6):   362g      12th (N=11):  6,943g
  //   3rd (N=2):    43g       8th (N=7):   616g      13th (N=12): 11,803g
  //   4th (N=3):    74g       9th (N=8):  1,047g     14th (N=13): 20,065g
  //   5th (N=4):   125g      10th (N=9):  1,780g     15th (N=14): 34,110g
  //
  // BETA 17.03 — PSBOT cycle-30u A2 tune: HYBRID curve. 1.7^N stays for
  // chests 1-10 (the existing wall the player has to climb). From chest
  // 11 onwards, the multiplier drops to 1.3^N starting from chest 10's
  // price as the base. Endless / very long runs no longer hit
  // exponentially-impossible costs (chest 20 was 1.3M gold under pure
  // 1.7^N). New tail keeps SOME pressure (chest 15 ~13k, chest 20 ~50k)
  // but doesn't lock out 30+ minute runs entirely.
  const N = chestsOpenedThisRun;
  // BETA 21.49 — Dylan: "chests cost a tad too much + the scaling is too steep,
  // feels pointless vs free shrines." Eased the base 1.7 → 1.4 (1.3 tail kept):
  // ~5th 58g (was 125), 8th 158g (was 616), 10th 310g (was 1,780). Still climbs,
  // just affordable enough to be worth buying instead of taking a free shrine.
  const ladder = N <= 10
    ? Math.round(MACHINE_BASE_COST * Math.pow(1.4, N))
    // Anchor at chest-10 price, then 1.3^(N-10) from there.
    : Math.round(MACHINE_BASE_COST * Math.pow(1.4, 10) * Math.pow(1.3, N - 10));
  // BETA 20.66 — "chest buys feel personal. and hard." (Dylan + Charter §6).
  // The ladder alone couldn't keep up with income (~9k gold/min late: chest
  // 6 at 213g = ~1.4 SECONDS of income — Dylan: "someone is just insanely
  // rich"). From the 4th chest on, the price can never fall below 60% of
  // YOUR average gold-per-minute this run — the richer the run, the harder
  // the buy (Megabonk-style income-proportional sink; Charter target:
  // chest #6+ ≈ 45-60s of income). First 3 chests stay ladder-cheap so the
  // early game still gets its toys.
  if (N >= 3 && _gameTime > 60 && goldCollectedThisRun > 0) {
    const avgGoldPerMin = goldCollectedThisRun / (_gameTime / 60);
    return Math.max(ladder, Math.round(0.6 * avgGoldPerMin));
  }
  return ladder;
}

// BETA 21.30 — Dylan: "why are chest prices fluctuating in real time... going up
// and down when i kill enemies." Root cause: the income-proportional floor above
// (0.6 × avg gold/min) was recomputed EVERY FRAME for the HUD — goldCollected jumps
// on each pickup (price up) while gameTime keeps growing between pickups (price
// drifts down). The income-scaling is intended, but the displayed number must be
// STABLE. Fix: lock the price per chest — compute it once when the chest count
// changes (i.e. right after you open one) and freeze it until the next open. All
// display + affordability + charge paths read this one cached value, so you always
// pay exactly what you see.
function getCachedChestCost(state: GameState): number {
  const s = state as unknown as { _chestCostCacheN?: number; _chestCostCacheVal?: number; _goldCollectedThisRun?: number };
  const n = state.chestsOpenedThisRun;
  if (s._chestCostCacheN !== n || s._chestCostCacheVal === undefined) {
    s._chestCostCacheN = n;
    s._chestCostCacheVal = getChestCost(n, state.time ?? 0, s._goldCollectedThisRun ?? 0);
  }
  return s._chestCostCacheVal;
}

// ─── Handle Interaction (E key) ─────────────────────────────────────

export function handleInteract(state: GameState): GameState {
  if (state.phase !== 'playing') return state;
  const { player } = state;

  // ── Extraction port — press [E]/[X] while standing on it to ARM the 60s escape.
  // Once armed the countdown runs no matter where you go (Dylan: "keeps going
  // anywhere"), so this only fires the first time, on the port. ──
  if (state.extractionPort && state.extractionArmedAt == null &&
      Math.hypot(player.x - state.extractionPort.x, player.y - state.extractionPort.y) < EXTRACT_RADIUS) {
    state.extractionArmedAt = state.time;
    state.interactTarget = null;
    state.pickupNotifications.push({ text: '⏱ EXTRACTION ARMED — survive 60 seconds to escape!', color: '#48e0a0', timer: 5.0 });
    try { playExtractArm(); } catch { /* audio not ready */ }
    return state;
  }

  // Check ultra boss chest
  if (state.ultraChest && !state.ultraChest.collected) {
    if (Math.hypot(player.x - state.ultraChest.x, player.y - state.ultraChest.y) < 90) {
      state.ultraChest.collected = true;
      state.phase = 'ultra_chest';
      // BETA 20.64 — roll NOW (render's lazy roll stays as a fallback) so the
      // controller focus targets can be registered AT the transition moment —
      // the 18.13 chest_cards lesson: register at transition or the focus
      // outline never seeds and the screen is gamepad-dead.
      if (!_ultraChestRollDone) {
        _ultraChestRolled = ultraChestPool.rollChest();
        _ultraChestRollDone = true;
      }
      try {
        const _ucW = _lastRenderW, _ucH = _lastRenderH;
        const _ucN = _ultraChestRolled.length;
        unregisterFocusScreen('ultra_chest');
        if (_ucN > 0) {
          const _ucL = getUltraChestLayout(_ucW, _ucH, _ucN);
          registerFocusScreen('ultra_chest',
            _ultraChestRolled.map((_it, i) => ({
              id: `ultra_${i}`,
              rect: { x: _ucL.startX + i * (_ucL.cardW + _ucL.gap), y: _ucL.cardY, w: _ucL.cardW, h: _ucL.cardH },
              onActivate: () => { pickUltraChestCard(state, i); },
            })),
            () => closeUltraChest(state), 0);
        } else {
          // POOL EXHAUSTED screen — still needs a controller-reachable close.
          registerFocusScreen('ultra_chest',
            [{ id: 'ultra_close', rect: { x: _ucW / 2 - 90, y: _ucH * 0.78, w: 180, h: 44 }, onActivate: () => closeUltraChest(state) }],
            () => closeUltraChest(state), 0);
        }
        setActiveScreen('ultra_chest');
      } catch { /* never crash interact */ }
      // BETA 15.68 — Ultra boss chest is FREE (drop reward, no cost)
      playChestOpenFree();
      // BETA 15.89 — Per Dylan: pause BGM (not duck) so the chest sound
      // is fully audible. Resumes when player picks an item or skips.
      startMuffledDuck(); // BETA 16.12 — was pauseBgm
      spawnParticles(state.particles, state.ultraChest.x, state.ultraChest.y, '#ff2200', 40, { shape: 'ring', speedMin: 30, speedMax: 140, sizeMin: 4, sizeMax: 8, lifeMin: 0.5, lifeMax: 0.9 });  // BETA 20.70 — ULTRA chest opens with a fiery shockwave
      return state;
    }
  }

  // Check chests
  for (const chest of state.map.roomMap.chests) {
    if (isPlayerNearMachine(chest, player.x, player.y)) {
      if (chest.type === 'elite') {
        // BETA 20.49 — Blue Elite WildCardMachine: FREE, guaranteed legendary normal item.
        (state as unknown as { _lastChestPaid?: boolean })._lastChestPaid = false;
        openMachine(chest);
        state.chestsOpenedThisRun++;
        startChestSpin('elite', state.player.luck ?? 0);
        spawnParticles(state.particles, chest.x, chest.y, '#00b4ff', 24, { shape: 'ring', speedMin: 25, speedMax: 110, sizeMin: 3, sizeMax: 6, lifeMin: 0.45, lifeMax: 0.75 });  // BETA 20.70 — elite chest open ring
        setTimeout(() => markMachineGone(chest), 500);
        playMachineVend();   // BETA 22.89 — chests are WILD CARD MACHINES now
        startMuffledDuck();
        clearChestSpin();          // BETA 22.91 — spin screen deleted; rolls stay
        _enterChestChoice(state);  // straight to the wild-card slam-in
        return state;
      } else if (chest.type === 'gold') {
        // Gold chests are free — slot machine spin then 3 rare/epic item choices
        // BETA 15.54 — flag for the pickAudit `source` field. Resolves the
        // BETA 15.34 TODO that hardcoded all chest item picks as 'chest_free'.
        // Per PSBOT cycle-20 audit: 98 of 145 runs had runGoldSpent > 0 matching
        // exact 3^N chest costs, but pickAuditPickCounts_chestPaid stayed at 0.
        // Tracker bug, not behavior — fixed here.
        (state as unknown as { _lastChestPaid?: boolean })._lastChestPaid = false;
        openMachine(chest);
        state.chestsOpenedThisRun++;
        startChestSpin('gold', state.player.luck ?? 0);
        spawnParticles(state.particles, chest.x, chest.y, '#ffd700', 20, { shape: 'ring', speedMin: 25, speedMax: 110, sizeMin: 3, sizeMax: 6, lifeMin: 0.45, lifeMax: 0.75 });  // BETA 20.70 — gold chest open ring
        setTimeout(() => markMachineGone(chest), 500);
        // BETA 22.89 — free gold machine: same vend clunk (chests are machines now).
        playMachineVend();
        // BETA 15.89 — Pause BGM so the chest sound is fully audible.
        startMuffledDuck(); // BETA 16.12 — was pauseBgm
        clearChestSpin();          // BETA 22.91 — spin screen deleted; rolls stay
        _enterChestChoice(state);  // straight to the wild-card slam-in
        return state;
      } else {
        // Regular chests cost gold
        const cost = getCachedChestCost(state);
        if (player.gold >= cost) {
          player.gold -= cost;
          // BETA 14.57 — accumulate goldSpentThisRun (used by N10 Debt scaling
          // perc + analyst's Phase 3 audit checklist).
          state._runGoldSpent = (state._runGoldSpent ?? 0) + cost;
          // BETA 15.54 — flag for pickAudit (chest_paid vs chest_free).
          (state as unknown as { _lastChestPaid?: boolean })._lastChestPaid = true;
          openMachine(chest);
          state.chestsOpenedThisRun++;
          startChestSpin('goop', state.player.luck ?? 0);
          spawnParticles(state.particles, chest.x, chest.y, '#00bfff', 20, { shape: 'ring', speedMin: 25, speedMax: 110, sizeMin: 3, sizeMax: 6, lifeMin: 0.45, lifeMax: 0.75 });  // BETA 20.70 — goop chest open ring
          // BETA 22.95 — the machine physically SHAKES for the vend beat
          // (Dylan), THEN the wild-card screen slams in 550ms later so the
          // player actually sees the machine react to their tokens. The
          // machine stays visible through the shake and vanishes after the
          // reveal opens (was: gone at 500ms + instant reveal same frame).
          chest.vendShakeAt = performance.now();
          setTimeout(() => markMachineGone(chest), 1200);
          // BETA 22.89 — paid machine: tokens feed the slot, then the vend clunk.
          playTokenSpend();
          playMachineVend();
          // BETA 15.89 — Pause BGM so the chest sound is fully audible.
          startMuffledDuck(); // BETA 16.12 — was pauseBgm
          clearChestSpin();          // BETA 22.91 — spin screen deleted; rolls stay
          setTimeout(() => {
            // guard: only reveal if the run is still in normal play (player
            // could die or pause inside the vend beat)
            if (state.phase === 'playing') _enterChestChoice(state);
          }, 550);
          return state;
        }
        // BETA 22.89 — not enough tokens: the machine says no.
        playMachineDenied();
        return state;
      }
    }
  }

  // Check barrels
  for (const barrel of state.map.roomMap.barrels) {
    if (isPlayerNearBarrel(barrel, player.x, player.y)) {
      barrel.state = 'broken';
      playBarrelBreak();

      // Scatter contents as loot orbs — player picks them up naturally
      const numOrbs = barrel.size === 'large' ? 6 : 4;
      if (barrel.goldValue > 0 && state.lootOrbs) {
        const perOrb = Math.ceil(barrel.goldValue / numOrbs);
        for (let i = 0; i < numOrbs; i++) spawnSingleOrb(state.lootOrbs, 'gold', perOrb, barrel.x, barrel.y);
      }
      if (barrel.xpValue > 0 && state.lootOrbs) {
        const perOrb = Math.ceil(barrel.xpValue / numOrbs);
        for (let i = 0; i < numOrbs; i++) spawnSingleOrb(state.lootOrbs, 'xp', perOrb, barrel.x, barrel.y);
      }

      // BETA 20.70 — barrel SMASH = wooden SHARDS that tumble + fall (gravity).
      spawnParticles(state.particles, barrel.x, barrel.y, '#7B4A1E', 14, { shape: 'shard', spin: 9, spinRandomSign: true, gravity: 380, speedMin: 70, speedMax: 250, lifeMin: 0.5, lifeMax: 1.0 });
      spawnParticles(state.particles, barrel.x, barrel.y, '#5C3410', 8, { shape: 'shard', spin: 11, spinRandomSign: true, gravity: 420, speedMin: 50, speedMax: 200, lifeMin: 0.5, lifeMax: 0.9 });
      return state;
    }
  }

  // BETA 23.28 — CHICK MAGNET STATUES DELETED (Dylan: "mega caseoh statue... removed immediately"). Surge Statues are the replacement; the pull mechanic returns through them later.

  // 23.39 — boss-shrine activation DELETED (no boss shrines generate).

    // BETA 16.84 — Per PSBOT cycle-30k §11B: "54/54 bot runs never reached
  // floor 2." Root cause: floor advance is gated on (a) walking into the
  // active portal, (b) pressing E, (c) the old final-boss gate, (d) killing
  // mega, (e) walking back to portal, (f) pressing E again. Bots don't
  // navigate to portal, so they never trigger this chain. Result: every
  // bot run dies on floor 1, PSBOT can't generate floor 2+ telemetry.
  // Fix: when bot mode is active AND the player has survived 240s on
  // the current floor AND we're below floor 2, fire the floor-advance
  // side effects directly (same as the regular portal walk-in path
  // below, just without the portal-proximity / E-press requirements).
  // The bot effectively gets a free advance after sustained survival
  // so PSBOT can capture floor-2 / floor-3 difficulty curves.
  {
    const sAnyBot = state as unknown as Record<string, number | boolean | undefined>;
    // BETA 20.77 — clock fix: state.time is monotonic now, so the 240s gate
    // must be FLOOR-LOCAL (time since this floor started) or the bot would
    // chain-advance floors on consecutive frames.
    const _wsBotPeek = enemiesModule?.getWaveState?.();
    // BETA 20.88 — PSBOT cycle-30bg P0: OR in isBotV2Active(). The farm runs on
    // the V2 brain, which never sets V1's isBotEnabled() flag, so this gate was
    // false all run → bots that broke the 240s survival wall (73/198 on 20.87)
    // were stuck on floor 1 forever → zero floor-2 difficulty data.
    if ((isBotEnabled() || isBotV2Active())
        && !state.finalBossActive
        && ((state.time ?? 0) - (_wsBotPeek?.floorStartTime ?? 0)) > 240) {
      const wsBot = _wsBotPeek;
      const curFloorBot = wsBot?.currentFloor ?? 0;
      if (curFloorBot < 2) {
        // Run the same reset block as the regular portal-advance path
        // (mirrors lines ~3982-4020 below).
        if (wsBot) {
          wsBot.currentFloor++;
          // BETA 20.88 — PSBOT cycle-30bg §1b: the bot advance reuses the SAME
          // map (no regen), so state.map.level never updated and floorReached
          // (autoPlaytester reads state.map.level) stayed 1 even after advancing.
          // Sync the floor label here (1-indexed = currentFloor+1). Difficulty
          // comes from wsBot.difficultyMultiplier, so this is label-only/safe.
          if (state.map) state.map.level = (wsBot.currentFloor + 1) as 1 | 2 | 3;
          wsBot.floorStartTime = state.time;
          wsBot.floorTimeElapsed = 0;
          wsBot.spawnAccumulator = 0;              // BETA 21.52 — no prime: floor starts EMPTY, builds up (grace in waveSystem)
          wsBot.nextBossSpawnTime = state.time + 120; // BETA 20.77 — re-arm recurring bosses
          wsBot.bossActive = false;
          wsBot.pendingBossCount = 0;
          wsBot.swarmTriggered = wsBot.swarmTriggered.map(() => false);
          wsBot.bossTriggered  = wsBot.bossTriggered.map(() => false);
          wsBot.hordeTriggered = wsBot.hordeTriggered.map(() => false);
          wsBot.activeSwarm = false;
          wsBot.inFinalSwarm = false;
          wsBot.repeatingSwarmMinute = 0;   // 23.55 (audit) — same miss as the human path
          wsBot.finalSwarmTime = 0;
          wsBot.activeRateBurstEnemyId = null;
          wsBot.activeRateBurstMultiplier = 1.0;
          wsBot.difficultyMultiplier = Math.max(0.1, wsBot.difficultyMultiplier * 2.0);
        }
        for (const e of state.enemies) {
          if (e.alive) e.alive = false;
        }
        clearEnemyProjectiles();
        // BETA 20.77 — clock fix: NO state.time reset (see the player-path
        // comment below). Monotonic clock; floorStartTime makes floor time local.
        if (state.portal) state.portal.active = false;
        sAnyBot._floorAdvanceReady = false;
        // Move bot back to spawn for fresh-floor positioning
        const sp = state.map.roomMap.spawnPoint;
        state.player.x = sp.x;
        state.player.y = sp.y;
        state.interactTarget = null;
        state.pickupNotifications.push({
          text: `⚡ FLOOR ${(wsBot?.currentFloor ?? 1) + 1} — DOUBLED DIFFICULTY (BOT AUTO)`,
          color: '#ff8800',
          timer: 5.0,
        });
        return state;
      }
    }
  }

  // Check portal — active portal starts the final boss fight OR advances
  // floor if mega is already dead (BETA 16.53).
  if (state.portal && state.portal.active && !state.finalBossActive) {
    if (Math.hypot(player.x - state.portal.x, player.y - state.portal.y) < 60) {
      // BETA 17.12 — PSBOT cycle-30ad Bundle 3 Floor 1 diagnostic: mark
      // that player reached portal at all. Combined with megaBossKilled
      // counter tells PSBOT where the progression bottleneck is per run.
      const _sAnyPortal = state as unknown as Record<string, boolean | number | undefined>;
      _sAnyPortal._portalReachedThisRun = true;
      const sAny = state as unknown as Record<string, boolean | undefined>;
      if (sAny._floorAdvanceReady) {
        // BETA 17.12 — count floor advances per run.
        _sAnyPortal._floorAdvancesThisRun = ((_sAnyPortal._floorAdvancesThisRun as number) ?? 0) + 1;
        // BETA 16.53 — Floor advance. Per Dylan: "press E again while
        // standing in it and then you go to level 2 ... same map and
        // rooms just randomized again. but the enemies are the same
        // strength that they were at the end of floor 1. but floor 2
        // wont start with 1,000's of enemies. its another build up
        // just like the first 1 just 2 times harder."
        sAny._floorAdvanceReady = false;
        const ws = enemiesModule?.getWaveState?.();
        if (ws) {
          ws.currentFloor++;
          ws.floorStartTime = state.time;
          ws.floorTimeElapsed = 0;
          // BETA 20.77 — opening sprinkle on the new floor too (a fresh run
          // primes 5.0; floor 2 used to start at 0 = emptier than minute 0).
          ws.spawnAccumulator = 0;  // BETA 21.52 — no prime: new floor starts EMPTY then builds up (Dylan). 4s grace in waveSystem.
          // BETA 20.77 — audit fix: nextBossSpawnTime held a stale floor-1
          // absolute and (with the old clock reset) never fired again — ZERO
          // recurring bosses on floor 2+. Re-arm for this floor.
          ws.nextBossSpawnTime = state.time + 120;
          ws.bossActive = false;
          ws.pendingBossCount = 0;
          ws.swarmTriggered = ws.swarmTriggered.map(() => false);
          ws.bossTriggered  = ws.bossTriggered.map(() => false);
          ws.hordeTriggered = ws.hordeTriggered.map(() => false);
          ws.activeSwarm = false;
          ws.inFinalSwarm = false;
          ws.finalSwarmTime = 0;
          ws.activeRateBurstEnemyId = null;
          ws.activeRateBurstMultiplier = 1.0;
          ws.repeatingSwarmMinute = 0;   // 23.55 (audit) — was never reset: floors 2/3 lost every post-7:00 repeating swarm
          // BETA 17.31 — wall-clock difficulty design.
          // BETA 18.18 — Floor multiplier and time bonus are now applied
          // every tick by the per-tick wiring (engine.ts ~1431) which reads
          // ws.currentFloor + state.startTime. The previous in-place
          // multiplication here was nuked every frame and DOUBLE-counted
          // when it survived (e.g. floor advance during the same frame as
          // the per-tick recompute would multiply twice). Single source of
          // truth: per-tick recompute. This block now only resets the
          // multiplier so the per-tick code starts clean for the new floor.
          ws.difficultyMultiplier = 1.0;
        }
        // Clear all currently-alive enemies — fresh slate for the next floor
        // (otherwise the "thousands of enemies" Dylan warned about carry over).
        for (const e of state.enemies) {
          if (e.alive) e.alive = false;
        }
        // BETA 21.36 — Dylan: "floor 2 had many enemies still there from floor 1."
        // state.enemies is a per-frame COPY rebuilt from the enemy SYSTEM each tick,
        // so clearing it alone let the real enemies reappear next frame near the new
        // spawn. Clear the REAL enemy-system array too (the system purges dead ones).
        const _essAdv = getEnemySystemState();
        if (_essAdv) {
          for (const _re of _essAdv.enemies) _re.alive = false;
          // BETA 21.42 — zero aliveCount immediately. Marking enemies dead here
          // bypasses the per-death decrements, so without this the counter stayed
          // inflated until the next 2s cleanupDead reconcile — and the spawn gate
          // (aliveCount >= maxAlive, waveSystem.ts:633) blocked the new floor's
          // spawns for those ~2s. Reset now so floor 2/3 fills from the first tick.
          _essAdv.aliveCount = 0;
        }
        // Reset throwables in flight
        clearEnemyProjectiles();
        // BETA 21.31 — Dylan: "floor 2,3,4 should look like nothing is in it yet —
        // death sprites and all." Clear floor-1 corpses/blood decals + any leftover
        // gold/XP loot orbs so the new floor is visually FRESH (the map itself
        // reseeds below). Without this, decals + orbs from the old floor carried over.
        clearDeathDecals();
        clearGroundDecals();
        if (state.lootOrbs) clearLootOrbs(state.lootOrbs);  // BETA 21.52 — wipe leftover gold/XP orbs (+ ring cursor/merge accum)
        // BETA 20.77 — THE CLOCK FIX (audit of Dylan's god-mode run). The old
        // `state.time = 0` reset here corrupted EVERYTHING on floor 2:
        // floorStartTime was captured pre-reset (~620s), so floorTimeElapsed
        // ran NEGATIVE all floor — no swarms, no hordes, no scheduled bosses,
        // portal unreachable (needed ~19 real minutes) — while spawn rate +
        // alive-cap + enemy stat ramps restarted at minute-0 (floor-2 enemies
        // WEAKER than late floor 1, breaking Dylan's in-code spec above).
        // It also corrupted survivalTimeSec telemetry (320s recorded vs ~950s
        // real). state.time is now MONOTONIC for the whole run; per-floor
        // build-up comes from floor-local minutes in waveSystem (floorStartTime
        // is set correctly above, so floorTimeElapsed is floor-local).
        // Lock portal until next mega kill on this new floor
        state.portal.active = false;
        // BETA 17.16 — PSBOT cycle-30af Dylan-direct: "all rooms will be in
        // different places, as if i was starting a new game." Procedural
        // map regen with a new seed. Minimap fog also reset to full
        // unexplored so player must re-discover the layout.
        const _newFloorLevel = ((ws?.currentFloor ?? 1) >= 2 ? 3 : ((ws?.currentFloor ?? 0) >= 1 ? 2 : 1)) as 1 | 2 | 3;
        const _newSeed = (state.map.roomMap.seed ^ ((ws?.currentFloor ?? 1) * 0xABCDEF)) >>> 0;
        const _newRoomMap = generateRoomMap(_newSeed, _newFloorLevel);
        clearChunkCache();   // BETA 22.96 — fresh ground per floor
        resetSurgeCracks(_newSeed);   // G8 — new floor, new wounds
        setGroundPois([
          ...(_newRoomMap.chests.filter(c => c.type !== 'elite').map(c => ({ x: c.x, y: c.y, kind: 'machine' as const }))),
          ...(_newRoomMap.shrines.filter(s => s.type !== 'boss').map(s => ({ x: s.x, y: s.y, kind: 'shrine' as const }))),
        ]);
        // BETA 23.13 — fresh prop layout each floor, clear of the new spawn
        setPropExclusions([{ x: _newRoomMap.spawnPoint.x, y: _newRoomMap.spawnPoint.y, r: 380 }]);
        setForestAnchors([
          { x: _newRoomMap.spawnPoint.x, y: _newRoomMap.spawnPoint.y },
          { x: _newRoomMap.bossRoom.x + ROOM_SIZE / 2, y: _newRoomMap.bossRoom.y + ROOM_SIZE / 2 },
        ]);   // 23.41 G1
        setSpurAnchors([
          { x: _newRoomMap.spawnPoint.x, y: _newRoomMap.spawnPoint.y },
          { x: _newRoomMap.bossRoom.x + ROOM_SIZE / 2, y: _newRoomMap.bossRoom.y + ROOM_SIZE / 2 },
        ]);   // 23.56 — new floor's portal gets its road too
        state.map.roomMap = _newRoomMap;
        // 23.55 (audit) — the human path never synced the floor label (bot
        // path did at ~6604): FL: overlay + floorReached telemetry stayed 1.
        state.map.level = _newFloorLevel;
        // 23.55 (audit) — the portal kept the OLD map's bossRoom position
        // after regen — the next floor's descend portal sat at a stale spot
        // (often deep in the new forest). Move it onto the new map.
        state.portal.x = _newRoomMap.bossRoom.x + ROOM_SIZE / 2;
        state.portal.y = _newRoomMap.bossRoom.y + ROOM_SIZE / 2;
        state.portal.roomId = _newRoomMap.bossRoom.id;
        // (its forest clearing is already anchored — setForestAnchors above
        // includes the new bossRoom center)
        // Reset minimap fog to full unexplored
        if (state.minimapFog) {
          for (let i = 0; i < state.minimapFog.length; i++) state.minimapFog[i] = 0;
        }
        // Move player to NEW spawn point
        const sp = state.map.roomMap.spawnPoint;
        state.player.x = sp.x;
        state.player.y = sp.y;
        // BETA 17.16 — PSBOT cycle-30af: floor transition overlay timer.
        // Per Dylan: "simple black screen showing my character and then
        // words on screen saying GOING TO LEVEL 2!!" Overlay fades black
        // for 2.5s. Render code at the end of the frame draws the overlay.
        // Game still ticks underneath but enemies are cleared + time is
        // reset so it's a quiet 2.5s of transition.
        const _sAnyFt = state as unknown as Record<string, number | undefined>;
        _sAnyFt._floorTransitionTimer = 2.5;
        _sAnyFt._floorTransitionTargetFloor = (ws?.currentFloor ?? 0) + 1;
        state.interactTarget = null;
        // BETA 17.31 — wall-clock time bonus (genre-correct, see ~line 4326).
        const _ntMinutes = (Date.now() - state.startTime) / 60_000;
        // BETA 20.40 — show the REAL per-floor multiplier. The engine applies
        // _floorBase = 1 + currentFloor (engine.ts ~1528), so the base mult equals
        // the DISPLAYED floor number (Floor 2 = 2×, Floor 3 = 3×, ...). Was hardcoded
        // 2.0× regardless of floor → under-reported difficulty on Floor 3/4.
        const _shownFloor = (ws?.currentFloor ?? 1) + 1;
        const _multStr = (ws
          ? (_shownFloor * (1 + Math.min(2.5, _ntMinutes * 0.15))).toFixed(1)
          : '2.0');
        state.pickupNotifications.push({
          text: `⚡ FLOOR ${_shownFloor} — ${_multStr}× HARDER`,
          color: '#ff8800',
          timer: 5.0,
        });
        playVictory();
        return state;
      }
      // 23.39 — no final boss to summon (CaseOh purge): first E charges the
      // portal directly; second E descends. Keeps the two-press ritual so
      // PSBOT's portal telemetry (_portalReachedThisRun etc.) is unchanged.
      sAny._floorAdvanceReady = true;
      state.portal.active = false;
      state.interactTarget = null;
      state.pickupNotifications.push({
        text: '⚡ PORTAL CHARGED — press E again to descend',
        color: '#ffe833',
        timer: 3.0,
        initialTimer: 3.0,
        fontSize: 40,
        position: 'screen_center',
      });
      return state;
    }
  }

  return state;
}

// ─── Render ─────────────────────────────────────────────────────────

// BETA 16.37 — PSBOT cycle-30i §2A: install a font-string cache on the
// canvas context so identical re-assignments (`ctx.font = 'bold 12px ...'`)
// are no-ops. Dylan's perf profile showed `set font` consuming 34% of
// frame time across ~100 ctx.font sites in this file. The cache wraps
// the existing ctx.font setter and skips assignments that match the
// current value — zero refactor across the call sites, big GC win when
// 100s of HP bars / damage numbers all set the same font in a row.
//
// BETA 16.49 — BUG FIX. The original 16.37 cache used a CLOSURE-scoped
// `lastFont` variable. ctx.save() / ctx.restore() reverts ctx.font as
// part of the canvas state stack, but my closure variable never learned
// — so after a drawBtn(START) → save → set font → restore loop, the
// closure still thought the most-recent font was X, and the next
// drawBtn(UNLOCKS) setting `ctx.font = X` got SKIPPED even though the
// real ctx.font was now back to the post-restore default. UNLOCKS +
// QUESTS rendered at the canvas default font (small), START rendered
// correctly (since it was the FIRST draw after the cache thought
// lastFont was empty).
//
// Fix: read the ACTUAL ctx.font value via realGet inside the setter.
// Now the cache compares against the canvas's true state and save/
// restore round-trips invalidate it implicitly. Slightly slower than
// the closure version (one extra getter call per set) but still much
// faster than re-tokenizing the font string in the canvas engine.
// BETA 16.50 — Font cache DISABLED. Per Dylan: "still not fixed. there
// must be other code that is on that text somehwere in code that is not
// allowing your fixes to work." 16.49's realGet-based fix wasn't enough,
// so the cache is now a no-op. If perf regresses on the previously-cited
// 34% font-tokenization cost, we re-enable a SAFER cache later (one
// that doesn't go through Object.defineProperty interception). For now,
// correctness > perf.
const _FONT_CACHE_INSTALLED = Symbol('engineFontCacheInstalled');
function installFontCache(_ctx: CanvasRenderingContext2D): void {
  void _FONT_CACHE_INSTALLED; // keep symbol referenced for re-enable
  // intentionally empty — see comment above
}

// BETA 16.82 — Cache the most recent render canvas size so updateGame
// (which runs BEFORE render every frame) can recompute the level-up /
// shrine / chest focus rects to match the actual rendered card positions.
// Was: hardcoded { x: 200, y: 240, w: 280, h: 380 } — those were a
// 1280-logical guess that did NOT match the responsive cardW / cardH /
// startX math in renderLevelUpScreen. So the focus box never landed on
// the cards on a 1280×720 ROG Ally screen — Dylan: "still no focus
// boxes on shrine upgrades, chest upgrades, free and cost ones."
let _lastRenderW = 1280;
let _lastRenderH = 720;
export function _getLastRenderSize(): { w: number; h: number } {
  return { w: _lastRenderW, h: _lastRenderH };
}

// BETA 20.78 — taco fire-trail flame-tongue layout (hoisted: this draws per
// trail cell per frame). ox / lean / len are fractions of the cell's TRUE
// damage radius; ph = per-tongue flicker phase so the tongues don't sway in
// unison. Tongue 1 is the tall hot-yellow one.
const _TACO_TONGUES = [
  { ox: -0.55, lean: -0.30, len: 0.85, w: 3.4, ph: 0.0, color: '#ff5500' },
  { ox: -0.12, lean:  0.10, len: 1.15, w: 4.2, ph: 2.1, color: '#ffcc33' },
  { ox:  0.28, lean: -0.14, len: 1.00, w: 3.8, ph: 4.2, color: '#ff7a00' },
  { ox:  0.62, lean:  0.26, len: 0.78, w: 3.0, ph: 1.3, color: '#ff5500' },
] as const;
// BETA 21.56 — Dylan loves the Tacos fire trail; reuse its EXACT ember-bed +
// licking-tongue look on anything that's on fire (Big Mac Special Sauce burn).
// World-space coords, deterministic flicker (no RNG in render).
function _drawEmberFire(ctx: CanvasRenderingContext2D, wx: number, wy: number, radius: number, alpha: number, time: number, seed: number): void {
  if (alpha < 0.03) return;
  ctx.lineCap = 'round';
  ctx.globalAlpha = alpha * 0.3;  ctx.fillStyle = '#7a1d00';
  ctx.beginPath(); ctx.ellipse(wx, wy, radius, radius * 0.72, 0, 0, Math.PI * 2); ctx.fill();
  ctx.globalAlpha = alpha * 0.5;  ctx.fillStyle = '#c23a00';
  ctx.beginPath(); ctx.ellipse(wx, wy, radius * 0.55, radius * 0.4, 0, 0, Math.PI * 2); ctx.fill();
  const flickBase = time * 9 + seed * 1.93;
  ctx.globalAlpha = alpha * 0.9;
  for (let j = 0; j < _TACO_TONGUES.length; j++) {
    const tg = _TACO_TONGUES[j];
    const flick = Math.sin(flickBase + tg.ph);
    const h = radius * tg.len * (0.72 + 0.28 * flick);
    const bx = wx + tg.ox * radius, by = wy + radius * 0.1;
    ctx.strokeStyle = tg.color; ctx.lineWidth = tg.w;
    ctx.beginPath();
    ctx.moveTo(bx, by);
    ctx.quadraticCurveTo(bx - flick * radius * 0.14, by - h * 0.55, bx + tg.lean * radius + flick * radius * 0.12, by - h);
    ctx.stroke();
  }
  ctx.lineCap = 'butt'; ctx.lineWidth = 1; ctx.globalAlpha = 1.0;
}
// BETA 20.78 — Kevin orbit crackle knobs (hoisted: render hot path).
const _KEVIN_BOLT_SEGS = 4;     // segments per mini-bolt polyline
const _KEVIN_BOLT_SPAN = 1.15;  // radians of orbit each bolt covers
const _KEVIN_BOLT_R = 25;       // crackle orbit radius (sprite spans ±18)

// BETA 20.95 — fake dynamic lighting (debug toggle, OFF by default). The standard
// low-risk 2D technique: fill an offscreen layer with darkness, "punch out" a soft
// circle of light around the player with destination-out, then composite the result
// over the screen. Cheap (one cached offscreen canvas + a radial gradient + a
// drawImage) and only runs when debugDynamicLighting is on — zero cost otherwise.
// Does NOT touch the existing renderer; it's a layer drawn on top.
let _lightLayer: HTMLCanvasElement | null = null;
function renderDynamicLighting(ctx: CanvasRenderingContext2D, state: GameState, width: number, height: number): void {
  if (!debugDynamicLighting) return;
  try {
    if (!_lightLayer) _lightLayer = document.createElement('canvas');
    const lc = _lightLayer;
    if (lc.width !== width || lc.height !== height) { lc.width = width; lc.height = height; }
    const lx = lc.getContext('2d');
    if (!lx) return;
    lx.globalCompositeOperation = 'source-over';
    lx.clearRect(0, 0, width, height);
    // 1) night darkness (dark blue tint over the whole world).
    lx.fillStyle = 'rgba(6, 8, 24, 0.6)';
    lx.fillRect(0, 0, width, height);
    // 2) carve a soft glow around the player (screen-space position from camera).
    const cam = state.camera;
    const scale = cam.zoom || 1;
    const px = width / 2 + (state.player.x - cam.x) * scale;
    const py = height / 2 + (state.player.y - cam.y) * scale;
    const radius = Math.max(140, 260 * scale);
    lx.globalCompositeOperation = 'destination-out';
    const g = lx.createRadialGradient(px, py, radius * 0.12, px, py, radius);
    g.addColorStop(0, 'rgba(0,0,0,1)');
    g.addColorStop(0.55, 'rgba(0,0,0,0.85)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    lx.fillStyle = g;
    lx.beginPath(); lx.arc(px, py, radius, 0, Math.PI * 2); lx.fill();
    lx.globalCompositeOperation = 'source-over';
    // 3) composite the darkness-with-a-hole over the screen.
    ctx.drawImage(lc, 0, 0);
  } catch { /* lighting is decorative — never let it break a frame */ }
}

export function renderGame(ctx: CanvasRenderingContext2D, state: GameState, width: number, height: number) {
  // BETA 23.06 — DT-A perf: update cost = time between updateGame start and
  // render start (they run back-to-back in the frame loop); render cost
  // measured across this function.
  if (_perfFrameT0 > 0) _perfUpdateMs = performance.now() - _perfFrameT0;
  const _perfRenderT0 = performance.now();
  _lastRenderW = width;
  _lastRenderH = height;
  installFontCache(ctx);
  const { player, camera } = state;
  const { roomMap, WORLD_SIZE: WS } = state.map;

  // Clear
  ctx.fillStyle = '#050508';
  ctx.fillRect(0, 0, width, height);

  // BETA 16.68 — GLOBAL SCREEN SHAKE. Per Dylan: "when shakes happen on
  // the map it needs to shaake everything. all enemies. the bot or main
  // player, structures. not things individually. that doesnt look real."
  // Was: shake offset baked into the camera transform at line ~3936,
  // which only translated the world tiles + walls + room art (the things
  // rendered inside the camera save/restore). Post-camera renderers
  // (enemies, ground effects, loot, player, weapons, projectiles, item
  // effects) computed their own world→screen via state.camera and DID
  // NOT see the shake — so during a mega-boss slam the rooms would
  // wobble while the enemies stood still, breaking the impact feel.
  // Now: a single canvas-level translate wraps the entire WORLD render
  // section. Every world renderer (camera-transform OR direct camera
  // math) gets the same shake offset uniformly. Restored just before
  // HUD render so screen-anchored UI stays still.
  const shakeFade = state.screenShakeTimer > 0 ? state.screenShakeTimer / 0.20 : 0;
  // BETA 20.69 — ease-out decay (VFX audit: raw linear jitter "feels like a
  // stutter, not a thump"). Squaring the fade front-loads the impact and
  // releases smoothly — same intensity numbers, way better feel.
  const shakeEase = shakeFade * shakeFade;
  // BETA 21.87 — Dylan: "dial the screen shake down 80%." The big contact shake was
  // masking the actual knockback shove (you felt the camera jolt instead of seeing
  // yourself fly). One global 0.2× here cuts EVERY shake source by 80% uniformly.
  const _SHAKE_SCALE = 0.20;
  const shakeX = shakeFade > 0 ? (Math.random() * 2 - 1) * state.screenShakeIntensity * shakeEase * _SHAKE_SCALE : 0;
  const shakeY = shakeFade > 0 ? (Math.random() * 2 - 1) * state.screenShakeIntensity * shakeEase * _SHAKE_SCALE : 0;
  ctx.save();              // <-- WORLD-SHAKE save (matched at line ~4730 before HUD)
  ctx.translate(shakeX, shakeY);

  // Camera transform — pure camera math (shake is now applied globally above).
  ctx.save();
  const scale = camera.zoom;
  const offsetX = width / 2 - camera.x * scale;
  const offsetY = height / 2 - camera.y * scale;
  ctx.translate(offsetX, offsetY);
  ctx.scale(scale, scale);

  // Visible area
  // BETA 17.01 — Per Dylan: "how do we keep from seeing shrines, room,
  // chest, barrels, chick magents. pop in on screen." Bumped pad
  // 200 → 400 so entities render BEFORE they enter the visible area
  // (camera moves at high move-speed builds; old 200wu margin wasn't
  // enough headroom). Combined with the overlap-test culling fix
  // (was fully-contained which culled at view edge), this should
  // eliminate the pop-in.
  const pad = 400;
  const viewLeft = camera.x - width / (2 * scale) - pad;
  const viewTop = camera.y - height / (2 * scale) - pad;
  const viewRight = camera.x + width / (2 * scale) + pad;
  const viewBottom = camera.y + height / (2 * scale) + pad;
  const viewBounds: ViewBounds = { left: viewLeft, top: viewTop, right: viewRight, bottom: viewBottom };

  // 23.52 — FAR-ZOOM GUARD (dev zoom): below 0.15 the view spans hundreds-to-
  // thousands of chunks, so the per-frame view scans (props, prop lights,
  // surge veins, debug tint) would each crawl the whole visible world — and
  // every prop is sub-pixel anyway. The LOD ground bake (mapChunks) carries
  // the far view alone. Normal gameplay zoom (≥1.0) never trips this.
  const FAR_LOD = scale < 0.15;

  // ── 1+2. GROUND ──
  // BETA 22.96 — BOUNDLESS MAP M1: the endless chunked Dead Lot replaces the
  // void + tiled concrete + border walls + graffiti rooms (Dylan: "i dont
  // like this map we have… this map is all new"). One flag restores the old
  // map wholesale (mapChunks.BOUNDLESS_MAP).
  if (BOUNDLESS_MAP) {
    drawChunkedGround(ctx, viewLeft, viewTop, viewRight, viewBottom, state.map.roomMap.seed ?? 1, getCachedImage, scale);
    // BETA 23.13 — GROUND props (flat tires, tipped cones, manholes): part of
    // the floor, drawn right on top of it, under every standing thing.
    if (MAP_PROPS && !FAR_LOD && !(MAPDEV.on && !MAPDEV.showProps)) {
      const _gpSeed = state.map.roomMap.seed ?? 1;
      for (const _gp of propsInRect(viewLeft - 200, viewTop - 200, viewRight + 200, viewBottom + 200, _gpSeed)) {
        if (_gp.def.kind === 'ground') _drawWorldProp(ctx, _gp);
      }
    }
    // BETA 23.26 — THE SURGE IN THE GROUND: pulsing purple vein glow (world
    // space, over the baked cracks, under standing objects).
    if (MAP_SURGE_VEINS && !FAR_LOD && !(MAPDEV.on && !MAPDEV.showProps)) {
      renderSurgeVeinGlow(ctx, viewLeft, viewTop, viewRight, viewBottom, state.map.roomMap.seed ?? 1, performance.now());
      // G8 — DYNAMIC cracks (emergence exit wounds): entities over the baked
      // ground, same visual stack, grow-in + birth flare. Never a chunk rebake.
      renderDynamicCracks(ctx, viewLeft, viewTop, viewRight, viewBottom, performance.now());

  // ── 23.45 · [t] FOREST DEBUG (Dylan's spec: "if a tree looks like it
  // floats, debug should make it obvious") — base dots, band colors,
  // walkable-mask tint, player foot point. Mapdev-only, zero cost off. ──
  if (MAPDEV.on && MAPDEV.showForestDebug) {
    const _fdSeed = state.map.roomMap.seed ?? 1;
    ctx.save();
    // 23.56 fix-pack (doc 09) — FOUR mask bands + the collision isoline:
    //   green  = walkable core (c>30)      yellow = walkable edge (0..30)
    //   orange = warning band (-16..0 — LOOKS brushy, still walkable)
    //   red    = blocked forest (c<=-16)   blue   = the exact collision line
    for (let gy = Math.floor(viewTop / 96) * 96; gy < viewBottom; gy += 96) {
      for (let gx = Math.floor(viewLeft / 96) * 96; gx < viewRight; gx += 96) {
        const c = forestClearance(gx, gy, _fdSeed);
        ctx.fillStyle = c > 30 ? 'rgba(60,255,120,0.10)'
          : c > 0 ? 'rgba(220,255,90,0.12)'
          : c > -16 ? 'rgba(255,165,50,0.15)'
          : 'rgba(255,60,60,0.10)';
        ctx.fillRect(gx - 48, gy - 48, 96, 96);
        if (Math.abs(c + 16) < 7) {   // the movement law's isoline
          ctx.fillStyle = 'rgba(70,150,255,0.45)';
          ctx.fillRect(gx - 48, gy - 48, 96, 96);
        }
        // G2 — biome border: cyan tick where the hard biome flips vs the
        // right/below neighbor cell (the ground wash itself crossfades —
        // this hard line exists only in debug)
        const bHere = biomeAt(gx, gy, _fdSeed);
        if (biomeAt(gx + 96, gy, _fdSeed) !== bHere) {
          ctx.fillStyle = 'rgba(0,229,255,0.85)';
          ctx.fillRect(gx + 44, gy - 48, 4, 96);
        }
        if (biomeAt(gx, gy + 96, _fdSeed) !== bHere) {
          ctx.fillStyle = 'rgba(0,229,255,0.85)';
          ctx.fillRect(gx - 48, gy + 44, 96, 4);
        }
      }
    }
    // prop base dots + band labels
    for (const _dp of propsInRect(viewLeft, viewTop, viewRight, viewBottom, _fdSeed)) {
      const isTree = FADE_WHEN_OCCLUDING.has(_dp.def.id);
      const isWall = _dp.def.id.startsWith('fcluster');
      ctx.fillStyle = isWall ? '#ff9d00' : isTree ? '#00e5ff' : '#ffffff';
      ctx.beginPath();
      ctx.arc(_dp.x, _dp.y, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.8)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      // sortY line (short horizontal tick through the base)
      ctx.strokeStyle = isWall ? 'rgba(255,157,0,0.5)' : 'rgba(0,229,255,0.5)';
      ctx.beginPath();
      ctx.moveTo(_dp.x - 26, _dp.y);
      ctx.lineTo(_dp.x + 26, _dp.y);
      ctx.stroke();
    }
    // 23.57 — PLACE labels (intentional-world pack doc 10: node types visible)
    {
      const _plPx = Math.min(160, Math.max(16, 16 / scale));
      ctx.font = `bold ${Math.round(_plPx)}px monospace`;
      for (const _pl of getPlaces(_fdSeed)) {
        if (_pl.x < viewLeft - _pl.rx || _pl.x > viewRight + _pl.rx || _pl.y < viewTop - _pl.ry || _pl.y > viewBottom + _pl.ry) continue;
        ctx.strokeStyle = 'rgba(255,220,90,0.55)';
        ctx.lineWidth = Math.max(2, 2 / scale);
        ctx.beginPath();
        ctx.ellipse(_pl.x, _pl.y, _pl.rx * 0.8, _pl.ry * 0.8, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillStyle = '#ffe27a';
        ctx.fillText(_pl.kind.toUpperCase(), _pl.x - _pl.rx * 0.3, _pl.y);
        // 23.60 — ground-kit label under the kind (ground-kits pack doc 09)
        ctx.fillStyle = 'rgba(150,220,150,0.85)';
        ctx.font = `${Math.round(_plPx * 0.6)}px monospace`;
        ctx.fillText('gk:' + groundKitDisplayName(_pl.groundKit), _pl.x - _pl.rx * 0.3, _pl.y + _plPx * 0.8);
        ctx.font = `bold ${Math.round(_plPx)}px monospace`;
      }
    }
    // player foot point + foot collider circle (fix-pack doc 09)
    const _pfY = player.y + (player.spriteSize || 40) * 0.6;
    ctx.fillStyle = '#ff2bd6';
    ctx.beginPath();
    ctx.arc(player.x, _pfY, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(player.x, _pfY, (player.size ?? 20) * 0.7, 0, Math.PI * 2);
    ctx.stroke();
    // slide vector (purple arrow, fades 1s after the last tangent slide)
    if (_slideDbg && (state.time ?? 0) - _slideDbg.t < 1.0) {
      const _sv = _slideDbg;
      const _svL = Math.hypot(_sv.sx, _sv.sy) || 1;
      ctx.strokeStyle = 'rgba(200,80,255,0.95)';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(_sv.x, _sv.y);
      ctx.lineTo(_sv.x + (_sv.sx / _svL) * 60, _sv.y + (_sv.sy / _svL) * 60);
      ctx.stroke();
    }
    ctx.restore();
  }

  // ── 23.54 · [c] CORRUPTION DEBUG (cracks pack doc 07): influence heatmap,
  // cluster bounds, family labels. Mapdev-only, zero cost off. ──
  if (MAPDEV.on && MAPDEV.showCorruption) {
    const _cdSeed = state.map.roomMap.seed ?? 1;
    ctx.save();
    for (let gy = Math.floor(viewTop / 128) * 128; gy < viewBottom; gy += 128) {
      for (let gx = Math.floor(viewLeft / 128) * 128; gx < viewRight; gx += 128) {
        const inf = corruptionInfluence(gx, gy, _cdSeed);
        if (inf > 0.08) {
          ctx.fillStyle = `rgba(170,60,255,${inf * 0.30})`;
          ctx.fillRect(gx - 64, gy - 64, 128, 128);
        }
      }
    }
    // labels/lines scale with 1/zoom so [c] stays readable zoomed way out
    const _cdPx = Math.min(120, Math.max(14, 14 / scale));
    ctx.font = `${Math.round(_cdPx)}px monospace`;
    const _cd0x = Math.floor(viewLeft / 1024), _cd1x = Math.floor(viewRight / 1024);
    const _cd0y = Math.floor(viewTop / 1024), _cd1y = Math.floor(viewBottom / 1024);
    for (let cyc = _cd0y; cyc <= _cd1y; cyc++) for (let cxc = _cd0x; cxc <= _cd1x; cxc++) {
      for (const _v of veinsForChunk(cxc, cyc, _cdSeed)) {
        ctx.strokeStyle = 'rgba(240,100,255,0.7)';
        ctx.lineWidth = Math.max(1.5, 1.5 / scale);
        ctx.strokeRect(_v.minX - 6, _v.minY - 6, (_v.maxX - _v.minX) + 12, (_v.maxY - _v.minY) + 12);
        ctx.fillStyle = '#f0e6ff';
        ctx.fillText(`${_v.family[0].toUpperCase()} ${_v.intensity.toFixed(2)}`, _v.minX - 4, _v.minY - 10);
      }
    }
    ctx.restore();
  }
    }
  } else {
    // Render outside texture (void beyond border walls)
    renderOutsideTexture(ctx, state, viewLeft, viewTop, viewRight, viewBottom);
    // Render tiled floor texture (inside world bounds)
    renderFloorTiles(ctx, state, viewLeft, viewTop, viewRight, viewBottom);
  }

  // BETA 16.53 — Floor 2+ tint overlay. Per Dylan: "i want to know what
  // the best way to make level 2 look like they can tell they are on
  // level 2. im thinkin floor color is different. darker, or something."
  // Floor 1 = no tint. Floor 2 = cool blue overlay (descending). Floor 3
  // = darker rust red. Layered AFTER floor tiles so it tints the floor
  // but BEFORE walls + rooms so they retain their colors. Layered with
  // soft alpha so it reads as a mood shift, not a recolor.
  {
    const ws = enemiesModule?.getWaveState?.();
    const floor = ws?.currentFloor ?? 0;
    if (floor > 0) {
      ctx.save();
      ctx.fillStyle = floor === 1
        ? 'rgba(40, 30, 80, 0.28)'    // floor 2 — cool blue/purple
        : 'rgba(80, 25, 20, 0.30)';    // floor 3+ — rust red danger
      ctx.fillRect(viewLeft, viewTop, viewRight - viewLeft, viewBottom - viewTop);
      ctx.restore();
    }
  }

  // ── 3. Render border walls ──
  // BETA 22.96 — no walls in the boundless map (roomMap.rooms is empty there
  // too, so the room/barricade loops below become free no-ops; kept for the
  // kill-switch path).
  if (!BOUNDLESS_MAP) renderBorderWalls(ctx, state, viewLeft, viewTop, viewRight, viewBottom);

  // ── 4. Render room tile PNGs (graffiti rooms drawn over the floor) ──
  // Draw room PNGs on top of the floor — instant, no fade.
  // BETA 21.29 — crushed rooms swap to Dylan's dedicated destroyed-room sprite
  // (/rooms/room_destroyed.png), drawn in the crushed branch below.
  for (const room of roomMap.rooms) {
    if (room.x + room.size < viewLeft || room.x > viewRight ||
        room.y + room.size < viewTop || room.y > viewBottom) continue;

    const img = getCachedImage(room.cdnUrl);
    if (img) {
      if (room.crushed) {
        // BETA 21.29 — Dylan's dedicated DESTROYED-ROOM SPRITE (cracked concrete +
        // central impact crater + collapsed rubble piles + toppled yellow
        // barricades). His hand-made art replaces 21.28's procedural rubble, which
        // read "too basic." Drawn at room bounds (collision + barricades are already
        // removed for crushed rooms). Falls back to dimmed room art for the frame or
        // two before the sprite finishes loading.
        const _destroyed = getCachedImage('/rooms/room_destroyed.png');
        if (_destroyed) {
          ctx.drawImage(_destroyed, room.x, room.y, room.size, room.size);
        } else {
          ctx.save();
          ctx.globalAlpha = 0.5;
          ctx.drawImage(img, room.x, room.y, room.size, room.size);
          ctx.restore();
        }
      } else {
        ctx.drawImage(img, room.x, room.y, room.size, room.size);
      }
    }
  }

  // ── 3. Render barricades (no debug outlines) ──
  // BETA 16.53 — Crushed rooms have NO barricades (collision was removed
  // in isWalkable). Skip rendering them too — visually consistent.
  ctx.fillStyle = 'rgba(80,60,40,0.7)';
  for (const room of roomMap.rooms) {
    if (room.crushed) continue;
    for (const bar of room.barricades) {
      ctx.fillRect(bar.x, bar.y, bar.w, bar.h);
    }
  }

  // ── 4b. Enemy death floor decals (BETA 21.58) ──
  // Dylan: "death sprite should never cover barrels and such. they should be
  // under." renderDeathDecals self-converts (world→screen via camera), so it
  // must run OUTSIDE the camera transform. Temporarily exit it here — at the
  // FLOOR layer, before shrines/chests/barrels/portal/enemies — so blood splats
  // sit under every object, then re-apply the transform. (Was rendered post-
  // restore at the very bottom of the world group → ON TOP of barrels/chests.)
  ctx.restore(); // exit camera transform → shake-only space
  renderDeathDecals(ctx, state.camera, width, height, state.time ?? 0, getCachedImage);
  renderGroundDecals(ctx, state.camera, width, height, state.time ?? 0);
  ctx.save();
  ctx.translate(offsetX, offsetY);
  ctx.scale(scale, scale);

  // ── 5. Render shrines ──
  if (!(MAPDEV.on && !MAPDEV.showProps))
  for (const shrine of roomMap.shrines) {
    // 23.39 — boss shrines deleted (CaseOh purge); every shrine is a buff shrine
    const sprSize  = SHRINE_SPRITE_SIZE;
    const actRadius = SHRINE_ACTIVATION_RADIUS;
    void actRadius;

    // BETA 17.01 — Per Dylan: "how do we keep from seeing shrines, room,
    // chest, barrels, chick magents pop in on screen. its like they are
    // not loaded in yet, but i can see them on my screen not be there
    // and then they all the sudden load in. this takes me out of the
    // game." Root cause: the cull test was "fully-contained or skip"
    // (left edge < viewLeft → skip means "if any edge is off-screen,
    // don't render"). Should be OVERLAP test: skip ONLY if the entity
    // is ENTIRELY outside the view. Same fix applied to chests, barrels,
    // chick magnets below. Rooms (line 4345) already used the correct
    // pattern.
    if (shrine.x + sprSize < viewLeft || shrine.x - sprSize > viewRight ||
        shrine.y + sprSize < viewTop  || shrine.y - sprSize > viewBottom) continue;

    // ── Buff shrine: idle base + lit overlay, pixel-registered pair ──
    const idleImg = getCachedImage(SHRINE_ASSETS.idle);
    const litImg  = getCachedImage(SHRINE_ASSETS.spentLit);
    // BETA 23.10 — the platform LIES ON THE GROUND, so it must obey the
    // world's measured foreshortening (~0.35-0.42 height ratio), not the
    // art's natural 0.89 (painted at a much flatter angle). Drawing it at
    // 0.42 makes it match every other ground ellipse (Dylan: "lots of
    // sprites still look top down flat"). Charge segments + ring derive
    // from dw/dh below, so they follow automatically.
    const dw = sprSize;
    const dh = sprSize * 0.42;
    const sxTL = shrine.x - dw / 2, syTL = shrine.y - dh / 2;
    const nowMs = performance.now();

    // Lit amount: 0 = dark, 1 = fully on. The ignition clock holds at zero
    // while the shrine choice screen covers the world, so the flicker plays
    // the moment gameplay resumes and the player is actually watching.
    let lit = 0;
    if (shrine.state === 'spent_lit') {
      if (shrine.litAtMs == null) {
        lit = 1; // pre-22.94 shrine state — just stay on
      } else if (state.phase === 'shrine_choice') {
        shrine.litAtMs = nowMs;
      } else {
        if (!shrine.igniteSfxPlayed) {
          shrine.igniteSfxPlayed = true;
          try { playShrineIgnite(); } catch { /* sfx optional */ }
        }
        const t = (nowMs - shrine.litAtMs) / 1000;
        // real-tube strike: dark → flick → dark → flick → hum up to full
        lit = t < 0.10 ? 0
            : t < 0.16 ? 0.75
            : t < 0.28 ? 0.06
            : t < 0.34 ? 0.9
            : t < 0.46 ? 0.12
            : t < 0.72 ? 0.12 + ((t - 0.46) / 0.26) * 0.88
            : 1;
      }
    }

    if (idleImg && lit < 1) ctx.drawImage(idleImg, sxTL, syTL, dw, dh);
    if (litImg && lit > 0) {
      ctx.save();
      ctx.globalAlpha = lit;
      ctx.drawImage(litImg, sxTL, syTL, dw, dh);
      // ignition bloom — brief additive over-glow right as it strikes on
      if (shrine.litAtMs != null) {
        const t = (nowMs - shrine.litAtMs) / 1000;
        if (t > 0.46 && t < 1.25) {
          ctx.globalCompositeOperation = 'lighter';
          ctx.globalAlpha = Math.max(0, 0.45 * (1 - (t - 0.46) / 0.79)) * lit;
          ctx.drawImage(litImg, sxTL, syTL, dw, dh);
        }
      }
      ctx.restore();
    }
    if (!idleImg && !litImg) {
      // neither loaded yet — nothing to draw this frame (preloader will land)
    }

    // Channeling: segmented BLUE charge meter along the platform's inside
    // edge (Dylan's concept art), replacing the old green arc + green hint.
    if (shrine.state === 'channeling') {
      drawShrineChargeSegments(ctx, shrine.x, shrine.y, dw / 2, dh / 2, shrine.progress, nowMs);
    }
  }

  _machinesOverPlayer = [];   // 22.98 — fresh y-sort defer list each frame
  _propsOverPlayer = [];
  if (!(MAPDEV.on && !MAPDEV.showProps)) {   // 23.06 [9]

  // ── 5b. BETA 23.13 — STANDING world props (cars, lamps, junk): y-sorted
  // against the player's feet, deferred past him when he stands in front.
  if (MAP_PROPS && BOUNDLESS_MAP && !FAR_LOD) {
    const _wpSeed = state.map.roomMap.seed ?? 1;
    const _wpFeetY = state.player.y + (state.player.spriteSize || 40) * 0.6;
    const _wpVis = propsInRect(viewLeft - 300, viewTop - 420, viewRight + 300, viewBottom + 240, _wpSeed)
      .filter(p => p.def.kind === 'standing');
    _wpVis.sort((a, b) => a.y - b.y);
    for (const _wp of _wpVis) {
      // 23.17 — 8wu hysteresis: standing on the flip line strobed the sprite
      // front/behind every frame (Dylan's video). In the dead zone the
      // previous frame's decision sticks (cached WorldProp objects persist).
      const _wpDiff = _wp.y - _wpFeetY;
      const _wpPrev = (_wp as { _ovp?: boolean })._ovp;
      const _wpOver = (Math.abs(_wpDiff) < 8 && _wpPrev !== undefined) ? _wpPrev : _wpDiff > 0;
      (_wp as { _ovp?: boolean })._ovp = _wpOver;
      if (_wpOver) _propsOverPlayer.push(_wp);
      else _drawWorldProp(ctx, _wp);
    }
  }
  // ── 6. Render chests (BETA 13.70 FPS: faux-glow disc replaces shadowBlur=28+14*pulse) ──
  const chestPulse = 0.5 + 0.5 * Math.sin(state.time * 1.4); // compute once
  for (const chest of roomMap.chests) {
    if (chest.state === 'gone') continue;
    // BETA 17.01 — overlap test (was fully-contained; pop-in fix)
    if (chest.x + MACHINE_SPRITE_SIZE * 2 < viewLeft || chest.x - MACHINE_SPRITE_SIZE * 2 > viewRight ||
        chest.y + MACHINE_SPRITE_SIZE * 2 < viewTop || chest.y - MACHINE_SPRITE_SIZE * 2 > viewBottom) continue;

    const isGold = chest.type === 'gold';
    const isElite = chest.type === 'elite';
    const glowFill = isGold ? '#ffd200' : '#00b4ff';

    void chestPulse;

    // BETA 20.59 — Dylan: the blue elite chest should be the ULTRA boss-chest
    // ART turned fully BLUE (not the goop chest + a blue outline + a word). Use
    // the ultra sprite, hue-tinted blue. Goop/gold chests render as before.
    const url = isElite ? '/props/ultra_boss_chest_v3.png' : getMachineSprite(chest);
    if (url) {
      const img = getCachedImage(url);
      if (img) {
        if (isElite) {
          const _tinted = getTintedSprite(url, img, '#2080ff') ?? img;
          const _es = MACHINE_SPRITE_SIZE * 1.15; // a touch bigger — it's the special drop
          // BETA 20.69 — elite chest gets the pulsing blue treasure halo too.
          const _ecPulse = 0.55 + 0.45 * Math.sin(state.time * 2.6 + chest.x * 0.013);
          ctx.save();
          ctx.shadowColor = '#2080ff';
          ctx.shadowBlur = 14 + 12 * _ecPulse;
          ctx.drawImage(_tinted, chest.x - _es / 2, chest.y - _es / 2, _es, _es);
          ctx.restore();
          // 22.95 — elite keeps its floating label (it's a loot drop, not a machine)
          ctx.font = 'bold 13px "Share Tech Mono", monospace';
          ctx.textAlign = 'center';
          ctx.fillStyle = 'rgba(15,19,24,0.85)';
          ctx.fillRect(chest.x - 24, chest.y - _es / 2 - 20, 48, 14);
          ctx.fillStyle = '#00e5ff';
          ctx.fillText('ELITE', chest.x, chest.y - _es / 2 - 9);
          ctx.textAlign = 'left';
        } else {
          // BETA 22.99 — y-sort by FEET vs BASE (v6 doc 13): compare the
          // machine's base line to the player's FEET, not his sprite center.
          // BETA 23.17 — 8wu hysteresis kills the front/behind strobe when
          // the player stands right on the flip line (Dylan's video).
          const _mDiff = (chest.y + MACHINE_SPRITE_SIZE / 2) - (state.player.y + (state.player.spriteSize || 40) * 0.6);
          const _mPrev = (chest as { _ovp?: boolean })._ovp;
          const _mOver = (Math.abs(_mDiff) < 8 && _mPrev !== undefined) ? _mPrev : _mDiff > 0;
          (chest as { _ovp?: boolean })._ovp = _mOver;
          if (_mOver) {
            _machinesOverPlayer.push(chest);
          } else {
            _drawWildCardMachine(ctx, state, chest);
          }
        }
      }
    }
  }

  }   // 23.06 — close the [9] props gate around the machine/chest loop

  // ── 6b. Render ultra boss chest (if present and not collected) ──
  if (state.ultraChest && !state.ultraChest.collected) {
    const uc = state.ultraChest;
    const ucSize = 120; // world units
    const ucImg = getCachedImage('/props/ultra_boss_chest_v3.png');
    const ucPulse = 0.5 + 0.5 * Math.sin(state.time * 2.0);
    // BETA 13.91 — User: "no circles on these ever". Disc removed; only the
    // 4px solid sprite-edge outline remains (rendered below).
    void ucPulse;
    if (ucImg) {
      const natW = (ucImg as HTMLImageElement).naturalWidth || ucSize;
      const natH = (ucImg as HTMLImageElement).naturalHeight || ucSize;
      const asp = natW / natH;
      const dw = asp >= 1 ? ucSize : ucSize * asp;
      const dh = asp >= 1 ? ucSize / asp : ucSize;
      // Outlined sprite (cached) — 4px solid red border around ultra chest
      const ucOutlined = getOutlinedSprite('/props/ultra_boss_chest_v3.png', ucImg, '#ff1e00', 4);
      if (ucOutlined) {
        const padW = (dw / natW) * 4;
        const padH = (dh / natH) * 4;
        ctx.drawImage(ucOutlined, uc.x - dw / 2 - padW, uc.y - dh / 2 - padH, dw + padW * 2, dh + padH * 2);
      } else {
        ctx.drawImage(ucImg, uc.x - dw / 2, uc.y - dh / 2, dw, dh);
      }
    } else {
      ctx.fillStyle = '#ff2200';
      ctx.beginPath();
      ctx.arc(uc.x, uc.y, 30, 0, Math.PI * 2);
      ctx.fill();
    }
    // Pulsing "ULTRA LOOT" label above chest (world space)
    // BETA 13.88 FPS: removed ctx.shadowBlur=8
    ctx.save();
    ctx.globalAlpha = 0.85 + 0.15 * ucPulse;
    ctx.font = 'bold 14px "Share Tech Mono", monospace';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#ff4400';
    ctx.fillText('⚠ ULTRA LOOT', uc.x, uc.y - 72);
    ctx.restore();
  }

  // ── 7. Render barrels (sprite-based) ──
  const barrelPulse = 0.5 + 0.5 * Math.sin(state.time * 2.2);
  for (const barrel of roomMap.barrels) {
    if (barrel.state === 'broken') continue;
    const sprSize = barrel.size === 'large' ? BARREL_SPRITE_LARGE : BARREL_SPRITE_SMALL;
    // BETA 17.01 — overlap test (was fully-contained; pop-in fix)
    if (barrel.x + sprSize < viewLeft || barrel.x - sprSize > viewRight ||
        barrel.y + sprSize < viewTop  || barrel.y - sprSize > viewBottom) continue;

    const sprUrl = barrel.type === 'xp' ? BARREL_SPRITE_URL_XP
                 : barrel.type === 'gold' ? BARREL_SPRITE_URL_GOLD
                 : BARREL_SPRITE_URL_BOTH;
    const img = getCachedImage(sprUrl);
    if (!img) continue;

    // BETA 13.91 — User: "no circles on these ever". Disc removed; only the
    // 4px solid sprite-edge outline remains (rendered below).
    void barrelPulse;
    const glowFill = barrel.type === 'xp'   ? '#28b4ff'
                   : barrel.type === 'gold'  ? '#ffbe1e'
                   :                           '#a078ff';

    // Outlined sprite (cached — 1 drawImage per barrel, no per-frame outline cost)
    const outlined = getOutlinedSprite(sprUrl, img, glowFill, 4);
    if (outlined) {
      ctx.drawImage(outlined, barrel.x - sprSize / 2 - 4, barrel.y - sprSize / 2 - 4, sprSize + 8, sprSize + 8);
    } else {
      ctx.drawImage(img, barrel.x - sprSize / 2, barrel.y - sprSize / 2, sprSize, sprSize);
    }
  }

  // ── 8. Render portal ──
  // BETA 17.37 — cycle-30aq audit: was point-in-rect with no sprite-radius
  // buffer. Portal sprite is ~70wu radius; without the buffer it popped
  // in abruptly at the viewport edge. Add 80wu margin so the portal
  // fades in smoothly with motion. (Note: viewLeft/viewRight already
  // include `pad=400` from the broader camera bound, but the portal's
  // check was using the un-padded camera, fixing here for consistency.)
  if (state.portal && state.portal.x >= viewLeft - 80 && state.portal.x <= viewRight + 80 &&
      state.portal.y >= viewTop - 80 && state.portal.y <= viewBottom + 80) {
    const p = state.portal;
    const pulse = 0.5 + 0.5 * Math.sin(p.pulsePhase);
    const size = 35 + 5 * Math.sin(p.pulsePhase * 1.5);

    ctx.save();
    // BETA 23.10 — portal is a GROUND marking: foreshorten like the world
    ctx.translate(p.x, p.y);
    ctx.scale(1, 0.42);
    ctx.translate(-p.x, -p.y);
    ctx.globalAlpha = 0.3 * pulse;
    const grd = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, size * 2);
    grd.addColorStop(0, '#ff4400');
    grd.addColorStop(0.5, '#ff8800');
    grd.addColorStop(1, 'transparent');
    ctx.fillStyle = grd;
    ctx.beginPath();
    ctx.arc(p.x, p.y, size * 2, 0, Math.PI * 2);
    ctx.fill();

    ctx.globalAlpha = 0.8;
    const inner = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, size);
    inner.addColorStop(0, '#ffcc00');
    inner.addColorStop(0.6, '#ff6600');
    inner.addColorStop(1, '#cc2200');
    ctx.fillStyle = inner;
    ctx.beginPath();
    ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
    ctx.fill();

    ctx.globalAlpha = 0.6;
    ctx.strokeStyle = '#ffee88';
    ctx.lineWidth = 2;
    for (let i = 0; i < 4; i++) {
      const angle = p.pulsePhase + (i * Math.PI / 2);
      ctx.beginPath();
      ctx.arc(p.x, p.y, size * 0.6, angle, angle + 1);
      ctx.stroke();
    }

    ctx.globalAlpha = 1;
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 14px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(p.active ? 'EXIT' : '🔒', p.x, p.y);
    if (!p.active) {
      ctx.font = '10px monospace';
      ctx.fillStyle = '#ff6600';
      ctx.fillText('BOSS PORTAL', p.x, p.y + 22);
    }
    ctx.restore();
  }

  // ── Extraction port (octagonal, teal → red while extracting) — the 120s escape
  // hatch. Black center holds the red countdown once armed (Dylan's spec). ──
  if (state.extractionPort &&
      state.extractionPort.x >= viewLeft - 80 && state.extractionPort.x <= viewRight + 80 &&
      state.extractionPort.y >= viewTop - 80 && state.extractionPort.y <= viewBottom + 80) {
    const ep = state.extractionPort;
    const armed = state.extractionArmedAt != null;
    const remain = armed ? Math.max(0, EXTRACT_COUNTDOWN_SEC - (state.time - (state.extractionArmedAt as number))) : 0;
    const pulse = 0.5 + 0.5 * Math.sin(ep.pulsePhase);
    // BETA 22.45 — pad sized so ~4 players fit comfortably (Dylan); uses the
    // octagonal stone-port sprite when present, procedural octagon as fallback.
    const R = 84;
    const ring = armed ? '#ff5a5a' : '#48e0a0';
    const glow = armed ? '#ff3030' : '#2ad19a';
    const octa = (rad: number) => {
      ctx.beginPath();
      for (let i = 0; i < 8; i++) {
        const a = ep.pulsePhase * 0.3 + i * Math.PI / 4 + Math.PI / 8;
        const px = ep.x + Math.cos(a) * rad, py = ep.y + Math.sin(a) * rad;
        if (i) ctx.lineTo(px, py); else ctx.moveTo(px, py);
      }
      ctx.closePath();
    };
    ctx.save();
    // outer glow halo (state color, breathes with pulse)
    ctx.globalAlpha = 0.26 * (0.6 + 0.4 * pulse);
    const halo = ctx.createRadialGradient(ep.x, ep.y, 0, ep.x, ep.y, R * 1.7);
    halo.addColorStop(0, glow); halo.addColorStop(1, 'transparent');
    ctx.fillStyle = halo;
    ctx.beginPath(); ctx.arc(ep.x, ep.y, R * 1.7, 0, Math.PI * 2); ctx.fill();
    ctx.globalAlpha = 1;
    const portImg = getCachedImage('/extraction/extraction_port.png');
    if (portImg && portImg.complete && portImg.naturalWidth > 0) {
      // Dylan's octagonal port sprite (has a black centre hole for the countdown).
      if (armed) { ctx.save(); ctx.shadowColor = '#ff3030'; ctx.shadowBlur = 22; }
      ctx.drawImage(portImg, ep.x - R, ep.y - R, R * 2, R * 2);
      if (armed) ctx.restore();
      // thin state-ring on the rim so idle/armed still reads at a glance
      ctx.lineWidth = 4; ctx.strokeStyle = ring; ctx.globalAlpha = 0.5 + 0.35 * pulse; octa(R * 0.99); ctx.stroke();
      ctx.globalAlpha = 1;
    } else {
      // procedural fallback: octagon plate + rings + black centre disc
      ctx.globalAlpha = 0.9; ctx.fillStyle = '#0c1a16'; octa(R); ctx.fill();
      ctx.lineWidth = 5; ctx.strokeStyle = ring; ctx.globalAlpha = 0.7 + 0.3 * pulse; octa(R); ctx.stroke();
      ctx.lineWidth = 2; ctx.globalAlpha = 0.4; octa(R * 0.74); ctx.stroke();
      ctx.globalAlpha = 1; ctx.fillStyle = '#05080a';
      ctx.beginPath(); ctx.arc(ep.x, ep.y, R * 0.5, 0, Math.PI * 2); ctx.fill();
    }
    // countdown / prompt in the centre (lands in the sprite's black hole or the fallback disc)
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    if (armed) {
      ctx.fillStyle = '#ff6a6a'; ctx.font = 'bold 40px monospace';
      ctx.fillText(String(Math.ceil(remain)), ep.x, ep.y + 1);
    } else {
      ctx.fillStyle = ring; ctx.font = 'bold 34px monospace';
      ctx.fillText('⤓', ep.x, ep.y - 6);
      ctx.font = 'bold 12px monospace'; ctx.fillStyle = '#bfeede';
      ctx.fillText('EXTRACT', ep.x, ep.y + 22);
    }
    ctx.restore();
  }


  // ── 8. Render enemies — rendered after camera restore (enemySystem does its own world→screen) ──
  // (moved below ctx.restore)

  // ── 9. Render particles ──
  // BETA 17.37 — cycle-30aq audit: was point-check with zero margin so
  // particles popped in at the exact viewport edge. Particles are small
  // and short-lived so a small buffer is enough — 40wu (~2 frames of
  // typical particle motion) gives smooth fade-in. (Bigger world objects
  // get pad=400; particles only need enough for one frame of motion.)
  // BETA 17.96 — VFX toggle: skip the entire particle render pass when
  // user has VFX disabled in the lab. Simulation still ticks (particles
  // still spawn + age + die) so combat math is unaffected — only the
  // visual draw is suppressed.
  for (const p of (isVfxEnabled() ? state.particles : [])) {
    if (p.x < viewLeft - 40 || p.x > viewRight + 40 || p.y < viewTop - 40 || p.y > viewBottom + 40) continue;
    // BETA 20.70 — SHAPE-AWARE renderer (VFX overhaul Batch A). One site, six
    // looks: the single circle every effect shared is gone. Ease-out alpha
    // (20.69) kept as the baseline curve. Pure 2D paths, no shadowBlur.
    const _lifeT = Math.max(0, p.life / p.maxLife);
    const _fade = Math.pow(_lifeT, p.fadePow ?? 2);
    // BETA 20.77 — PERF (Dylan's run: render-bound spikes with particles
    // pegged at the 250 cap). (1) skip near-invisible particles — the last
    // chunk of every particle's life paid full path+fill at alpha <3%;
    // (2) per-particle ctx.save/restore REMOVED — only shard/star transform
    // (they save/restore locally); everything else just sets alpha/styles.
    // State is reset once after the loop.
    if (_fade < 0.03) continue;
    const _sz = Math.max(0.5, p.size * (0.4 + 0.75 * _lifeT));
    if (_sz < 0.75 && p.shape !== 'ring') continue;
    ctx.fillStyle = p.color;
    ctx.strokeStyle = p.color;
    switch (p.shape) {
      case 'spark': {
        // thin bright streak along travel — raw energy / collection
        const a = p.angle ?? Math.atan2(p.vy, p.vx);
        const len = _sz * (p.stretch ?? 4);
        ctx.globalAlpha = _fade;
        ctx.lineWidth = Math.max(1, _sz * 0.55);
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(p.x - Math.cos(a) * len, p.y - Math.sin(a) * len);
        ctx.lineTo(p.x + Math.cos(a) * len * 0.25, p.y + Math.sin(a) * len * 0.25);
        ctx.stroke();
        break;
      }
      case 'shard': {
        // spinning angular splinter — destruction / debris / death
        ctx.globalAlpha = _fade;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.angle ?? 0);
        ctx.beginPath();
        ctx.moveTo(0, -_sz * 1.5);
        ctx.lineTo(_sz * 0.72, _sz);
        ctx.lineTo(-_sz * 0.72, _sz);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
        break;
      }
      case 'ring': {
        // expanding hollow outline — shockwave / "value unlocked"
        const r = _sz * (1 + (1 - _lifeT) * 3.6);
        ctx.globalAlpha = _fade * 0.9;
        ctx.lineWidth = Math.max(1, _sz * 0.5 * _lifeT);
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.stroke();
        break;
      }
      case 'mote': {
        // soft flickering dot, barely shrinks — gas / embers / spores / coins
        const _flick = 0.62 + 0.38 * Math.sin(p.life * 28 + p.x * 0.5);
        ctx.globalAlpha = _fade * 0.85 * _flick;
        ctx.beginPath();
        ctx.arc(p.x, p.y, Math.max(0.5, p.size * (0.6 + 0.4 * _lifeT)), 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case 'bubble': {
        // hollow bubble + highlight dot — reflect / pop
        ctx.globalAlpha = _fade * 0.85;
        ctx.lineWidth = Math.max(1, _sz * 0.32);
        ctx.beginPath();
        ctx.arc(p.x, p.y, _sz, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = _fade;
        ctx.beginPath();
        ctx.arc(p.x - _sz * 0.32, p.y - _sz * 0.32, Math.max(0.5, _sz * 0.22), 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case 'star': {
        // 4-point sparkle — magic / treasure / crit
        ctx.globalAlpha = _fade;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.angle ?? 0);
        const _sl = _sz * 1.9, _sw = _sz * 0.32;
        ctx.beginPath();
        ctx.moveTo(0, -_sl); ctx.lineTo(_sw, 0); ctx.lineTo(0, _sl); ctx.lineTo(-_sw, 0); ctx.closePath();
        ctx.moveTo(-_sl, 0); ctx.lineTo(0, _sw); ctx.lineTo(_sl, 0); ctx.lineTo(0, -_sw); ctx.closePath();
        ctx.fill();
        ctx.restore();
        break;
      }
      default: {
        // legacy circle
        ctx.globalAlpha = _fade;
        ctx.beginPath();
        ctx.arc(p.x, p.y, _sz, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }
  // BETA 20.77 — single state reset for the whole particle pass (replaces
  // up to 250 save/restore pairs per frame).
  ctx.globalAlpha = 1;
  ctx.lineCap = 'butt';
  ctx.lineWidth = 1;

  // BETA 23.28 — CHICK MAGNET STATUES DELETED (Dylan: "mega caseoh statue... removed immediately"). Surge Statues are the replacement; the pull mechanic returns through them later.

  // ── 9b. Render aura BELOW the player so it doesn't cover the character ──
  if (weaponsModule) renderAuraWeaponsBelow(ctx, state);

  // ── 9b-fire. BETA 21.56 — Dylan: burning enemies (Big Mac Special Sauce DoT)
  // get the SAME Tacos-fire look while they take damage. World space; ember fire
  // at the enemy's feet, fading as the burn timer runs out. Off-screen culled. ──
  {
    const _essFire = getEnemySystemState();
    if (_essFire) {
      for (const _be of _essFire.enemies) {
        if (!_be.alive || (_be.burnUntil ?? 0) <= 0) continue;
        if (_be.x + 60 < viewLeft || _be.x - 60 > viewRight || _be.y + 60 < viewTop || _be.y - 60 > viewBottom) continue;
        const _fr = Math.max(14, (_be.hitboxRadius ?? 24) * 1.5);
        const _fa = Math.min(0.95, _be.burnUntil * 1.6);  // fade out in the last ~0.6s
        _drawEmberFire(ctx, _be.x, _be.y + _fr * 0.25, _fr, _fa, state.time, _be.id);
      }
    }
  }

  // ── 9c. Render ultra item effects (taco fire trails, Kevin orbit) ──
  const u = player.ultraState;
  if (u) {
    // Taco fire trails (world space) — BETA 20.78: LICKING FLAME TONGUES over
    // a dim ember bed (VFX identity law — was two flat alpha discs, the shared
    // circle look). Ember footprint stays = TRUE damage radius (readability).
    // Tongues flicker deterministically from time + cell index — no RNG here.
    if (u.taco_fireTrails?.length) {
      const trails = u.taco_fireTrails as Array<{x:number;y:number;radius:number;lifetime:number}>;
      ctx.lineCap = 'round';
      for (let i = 0; i < trails.length; i++) {
        const t = trails[i];
        const ta = Math.min(1, t.lifetime * 0.6);
        if (ta < 0.03) continue;  // perf: skip near-invisible cells
        // Ember bed — dim ellipse at the TRUE damage radius.
        ctx.globalAlpha = ta * 0.3;
        ctx.fillStyle = '#7a1d00';
        ctx.beginPath();
        ctx.ellipse(t.x, t.y, t.radius, t.radius * 0.72, 0, 0, Math.PI * 2);
        ctx.fill();
        // Hot ember core at the bed's center.
        ctx.globalAlpha = ta * 0.5;
        ctx.fillStyle = '#c23a00';
        ctx.beginPath();
        ctx.ellipse(t.x, t.y, t.radius * 0.55, t.radius * 0.4, 0, 0, Math.PI * 2);
        ctx.fill();
        // 4 licking tongues — short upward streaks whose height + lean flicker
        // via sin(time + cell index + per-tongue phase). Layout consts hoisted
        // to _TACO_TONGUES (module level) per the perf law.
        const flickBase = state.time * 9 + i * 1.93;
        ctx.globalAlpha = ta * 0.9;
        for (let j = 0; j < _TACO_TONGUES.length; j++) {
          const tg = _TACO_TONGUES[j];
          const flick = Math.sin(flickBase + tg.ph);  // -1..1
          const h = t.radius * tg.len * (0.72 + 0.28 * flick);
          const bx = t.x + tg.ox * t.radius;
          const by = t.y + t.radius * 0.1;
          ctx.strokeStyle = tg.color;
          ctx.lineWidth = tg.w;
          ctx.beginPath();
          ctx.moveTo(bx, by);
          ctx.quadraticCurveTo(
            bx - flick * t.radius * 0.14, by - h * 0.55,
            bx + tg.lean * t.radius + flick * t.radius * 0.12, by - h,
          );
          ctx.stroke();
        }
      }
      ctx.lineCap = 'butt';
      ctx.lineWidth = 1;
      ctx.globalAlpha = 1.0;
    }
    // Kevin orbit (world space) — BETA 20.78: ELECTRIC-BLUE CRACKLE — two
    // jagged mini-bolts orbiting the sprite on opposite sides (lightning is
    // the bar: wide glow pass + white-hot core pass, irregular, alive). Was a
    // flat low-alpha disc. Jag is deterministic from time + segment + bolt
    // index — no RNG in render. Kevin's sprite itself is untouched.
    if (u.kevin_active && u.kevin_x != null) {
      const kevinImg = getCachedImage('/enemies/kevin_projectile.png');
      const kt = state.time;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      for (let j = 0; j < 2; j++) {
        const a0 = kt * 3.4 + j * Math.PI;  // opposite sides, slow orbit
        for (let pass = 0; pass < 2; pass++) {
          ctx.strokeStyle = pass === 0 ? 'rgba(40,150,255,0.5)' : 'rgba(216,240,255,0.9)';
          ctx.lineWidth = pass === 0 ? 4 : 1.5;
          ctx.beginPath();
          for (let k = 0; k <= _KEVIN_BOLT_SEGS; k++) {
            const ang = a0 + (k / _KEVIN_BOLT_SEGS) * _KEVIN_BOLT_SPAN;
            const r = _KEVIN_BOLT_R + Math.sin(kt * 13.7 + k * 5.3 + j * 2.6) * 4.5;
            const px = u.kevin_x + Math.cos(ang) * r;
            const py = u.kevin_y + Math.sin(ang) * r;
            if (k === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
          }
          ctx.stroke();
        }
      }
      ctx.lineCap = 'butt';
      ctx.lineJoin = 'miter';
      ctx.lineWidth = 1;
      if (kevinImg) {
        ctx.drawImage(kevinImg, u.kevin_x - 18, u.kevin_y - 18, 36, 36);
      } else {
        ctx.fillStyle = '#00aaff';
        ctx.beginPath();
        ctx.arc(u.kevin_x, u.kevin_y, 14, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    // BETA 20.28 — Gained a Follower allies (world space). Friendly green goons
    // that hunt enemies + idle near the player. Green = clearly an ally.
    // BETA 20.78 — tiny GOON silhouettes: shard-triangle body + 2 white eye
    // dots (was 5 stacked plain circles — the banned shared look). Cheap
    // paths only; bob sways the hood tip so they read alive, no transforms.
    if (u.follower_list?.length) {
      for (const f of u.follower_list as Array<{ x: number; y: number; bob: number }>) {
        const bob = f.bob ?? 0;
        const fy = f.y + Math.sin(bob) * 3;
        const lean = Math.sin(bob * 0.5) * 2;  // hood-tip waddle sway
        // grounding shadow
        ctx.globalAlpha = 0.18;
        ctx.fillStyle = '#0a2a06';
        ctx.beginPath();
        ctx.ellipse(f.x, f.y + 9, 10, 3.5, 0, 0, Math.PI * 2);
        ctx.fill();
        // shard-triangle body (hooded-goon silhouette), ally green
        ctx.globalAlpha = 1.0;
        ctx.fillStyle = '#2fbf12';
        ctx.beginPath();
        ctx.moveTo(f.x + lean, fy - 14);
        ctx.lineTo(f.x + 9, fy + 8);
        ctx.lineTo(f.x - 9, fy + 8);
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = '#0a2a06';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        // lighter inner shard = chest highlight
        ctx.fillStyle = '#7dff5a';
        ctx.beginPath();
        ctx.moveTo(f.x + lean * 0.6, fy - 8);
        ctx.lineTo(f.x + 4.5, fy + 6);
        ctx.lineTo(f.x - 4.5, fy + 6);
        ctx.closePath();
        ctx.fill();
        // 2 white eye dots
        ctx.fillStyle = '#ffffff';
        ctx.beginPath(); ctx.arc(f.x - 3, fy - 0.5, 1.8, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(f.x + 3, fy - 0.5, 1.8, 0, Math.PI * 2); ctx.fill();
      }
      ctx.lineWidth = 1;
    }
  }

  // BETA 13.100b — render-layer reorder per user request:
  //   "bot health bar above character and armor when it is on should never be
  //    under walls rooms, character, weapons, enemies. always on top.
  //    gold and xp should be under character but over enemies and an floor
  //    sprites and rooms"
  // The player sprite + minions + chick-magnet-front used to render here, ABOVE
  // the items/pickups layer but BEFORE enemies/weapons/loot orbs (which all run
  // outside this camera transform). That meant loot orbs covered the player and
  // enemies could clip the world-space HP bar. Moved both blocks to AFTER the
  // outside-camera passes — see "BETA 13.100b player layer" + "BETA 13.100b HP
  // bar layer" sections below.
  // BETA 13.102 — Iranian Bird visual improvements (rotation, 4 propellers,
  // tracer bullets) live in the "BETA 13.100b Player layer" block at the new
  // location below, NOT here. See line ~2246 area for the Iranian Bird block.

  // ── 11. Render items/pickups (delegated — uses camera transform) ──
  if (itemsModule) itemsModule.render(ctx, state, viewBounds);

  ctx.restore(); // End camera transform

  // BETA 21.58 — death floor decals moved UP to the floor layer (section 4b,
  // before shrines/chests/barrels) so they render UNDER all objects per Dylan.
  // (Was here at the bottom of the world group, which drew them OVER barrels.)

  // ── BETA 13.99 — Render ground effects (smoke clouds, slow puddles) BEFORE
  // enemies so they appear underneath enemy + player sprites (visually correct
  // — enemies and the player should walk OVER the puddle, not under it).
  // Called outside the camera transform; renderGroundEffects does its own
  // world→screen conversion via the camera struct passed in.
  {
    const _ws = getWeaponSystemState();
    if (_ws) {
      const cam = {
        x: state.camera.x,
        y: state.camera.y,
        zoom: state.camera.zoom,
        viewportWidth: width,
        viewportHeight: height,
      };
      renderGroundEffects(_ws, ctx, cam);
    }
  }

  // BETA 13.102 — Render floor-level item effects (Diarrhea Gas clouds) UNDER
  // enemies. User: "diarea gas... under enemies, under bot characters and
  // regular character enemies, but above rooms".
  renderItemEffectsBelow(ctx, camera.x, camera.y, width, height, camera.zoom);

  // BETA 17.82 — Loot orbs (XP / gold / health) render ABOVE floor
  // sprites (rooms, tiles, walls, death decals, ground effects, gas
  // clouds) but BELOW enemies / weapons / projectiles / player. Dylan:
  // "xp should never cover enemies or wepoons. always below. but above
  // floor sprites." Was: orbs drew AFTER enemies (BETA 13.100b). With
  // pile merging in 17.82 the cluttered-pile problem is solved at the
  // data layer, so loot now sits in the natural floor-loot z-layer.
  if (state.lootOrbs) {
    renderLootOrbs(ctx, state.lootOrbs, camera.x, camera.y, width, height, camera.zoom);
  }

  // BETA 20.77 — Dylan: "blue chest are being hidden by gold and coins."
  // The world-pass chest draw happens BEFORE loot orbs, so a dense gold
  // pile buries the elite chest. RE-DRAW unopened elite chests ON TOP of
  // the loot layer (screen-space, mirrors renderLootOrbs' transform).
  // Sparing shadow halo = the allowed exception, one chest at a time.
  for (const _ec of state.map.roomMap.chests) {
    if (_ec.state === 'gone' || _ec.type !== 'elite') continue;
    const _esx = (_ec.x - camera.x) * camera.zoom + width / 2;
    const _esy = (_ec.y - camera.y) * camera.zoom + height / 2;
    if (_esx < -80 || _esx > width + 80 || _esy < -80 || _esy > height + 80) continue;
    const _eImg = getCachedImage('/props/ultra_boss_chest_v3.png');
    if (!_eImg) continue;
    const _eTinted = getTintedSprite('/props/ultra_boss_chest_v3.png', _eImg, '#2080ff') ?? _eImg;
    const _eSz = MACHINE_SPRITE_SIZE * 1.15 * camera.zoom;
    const _ePulse = 0.55 + 0.45 * Math.sin(state.time * 2.6 + _ec.x * 0.013);
    ctx.save();
    ctx.shadowColor = '#2080ff';
    ctx.shadowBlur = 14 + 12 * _ePulse;
    ctx.drawImage(_eTinted, _esx - _eSz / 2, _esy - _eSz / 2, _eSz, _eSz);
    ctx.restore();
  }

  // ── 12. Render enemies (delegated) — uses its own world→screen conversion ──
  if (enemiesModule && !(MAPDEV.on && !MAPDEV.showEntities)) enemiesModule.render(ctx, state, viewBounds);   // 23.06 [7]

  // BETA 16.22 — Per Dylan: per-enemy gas damage overlay. Renders AFTER
  // enemies (so the cloud sits "over enemies") but before loot orbs +
  // player + weapons + projectiles (so it's "under bot/character/weapons
  // /projectiles"). 25% opacity, sized to enemy hitbox, fades 1.2s after
  // the last gas tick.
  {
    const _es = getEnemySystemState();
    if (_es) {
      const enemyView = _es.enemies.map(e => ({
        id: e.id,
        x: e.x,
        y: e.y,
        hitboxRadius: e.hitboxRadius,
        spriteSize: e.spriteSize,
        alive: e.alive,
      }));
      renderGasOverlayBelow(
        ctx,
        state.camera,
        width,
        height,
        state.time ?? 0,
        enemyView,
        getCachedImage,
      );
    }
  }

  // ── 13. Debug nav overlay (drawn on top of everything, in screen space) ──
  if (debugNavOverlay || debugShowWaypoints) {
    _renderDebugNavOverlay(ctx, state, width, height);
  }
  if (debugFlowField) {
    _renderFlowFieldOverlay(ctx, state, width, height);
  }

  // ── BETA 17.82 — renderLootOrbs moved to BEFORE renderEnemies (search
  // "Loot orbs (XP / gold / health) render UNDER" above). Was here from
  // BETA 13.100b; now floor-level per Dylan: "xp should never cover
  // enemies or wepoons. always below." ──

  // ── BETA 15.49 — Arc weapon idle preview (Spatula + Drac's Fang).
  // Renders the persistent attack-zone sector + weapon sprite hovering in
  // the player's facing direction. Drawn BEFORE the player so the player
  // sprite is on top. The sector is faded so it doesn't dominate gameplay
  // but the player can always see (a) where the swing hits, (b) which way
  // the weapon is pointing, (c) what weapon they have. Per Dylan: "they
  // are not locking on the circle around the character. we can not see
  // the attack box on either weapon. the weapon should point wherever
  // direction the player is running."
  renderWeaponArcIdleOverlay(ctx, state);

  // ── BETA 13.100b — Player layer. Re-enters the camera transform briefly to
  // render player sprite + Bungulators + Iranian Bird + chick-magnet-front.
  // BETA 16.68 — shake now applied at the canvas level (outer save above
  // line 3935) so this re-entry uses pure camera math; no per-call shake
  // baked in or it'd double-shake. ──
  // BETA 23.01 — WEAPON DEPTH: attached weapons whose slot sits ABOVE Greg's
  // center draw BEHIND his sprite (self-converts world→screen; runs outside
  // the camera transform).
  renderOrbitWeaponSpritesPass(ctx, state, 'behind');
  ctx.save();
  ctx.translate(offsetX, offsetY);
  ctx.scale(scale, scale);
  if (playerSprite) {
    // BETA 22.85 — directional WALK animation (Greg etc.): pick the frame for
    // the current move direction + walk phase; falls back to the single static
    // sprite for characters with no walk set (zero regression). _pflip carries
    // the mirror (left/SW/NW) so it replaces the facing-based flip below.
    let _activePlayerImg: HTMLImageElement = playerSprite;
    let _pflip = player.facing < 0;
    {
      const _cid = state.character?.id;
      if (_cid && hasWalkSet(_cid)) {
        const _wf = resolveWalkFrame(_cid, _playerWalkAngle, _playerWalkMoving, _playerWalkPhase);
        if (_wf) {
          const _wimg = _playerWalkFrames.get(_wf.url);
          if (_wimg) { _activePlayerImg = _wimg; _pflip = _wf.flip; }
        }
      }
    }
    const sprH = player.spriteSize * PLAYER_RENDER_SCALE;
    const aspect = _activePlayerImg.naturalWidth / _activePlayerImg.naturalHeight;
    const sprW = sprH * aspect;
    const bobY = Math.sin(player.bobPhase) * 2;
    const isInvincible = player.invincibleTimer > 0;
    const flashAlpha = isInvincible
      ? 0.6 + 0.4 * Math.abs(Math.sin(player.invincibleTimer * Math.PI * 4))
      : 1.0;
    // BETA 23.15 — the player casts the SAME NE shadow as everything else
    // (Dylan: "player shadow needs to be cast correct NE as well"). Keyed by
    // the active frame's src so each walk frame gets its own silhouette.
    drawCastShadowForImage(
      ctx,
      _activePlayerImg.src || 'player-static',
      _activePlayerImg,
      player.x, player.y + sprH / 2 - 4,
      sprW, sprH, 0.45, _pflip,
    );
    // BETA 22.48 — REMOVED the armor-keyed blue character ring (was BETA 17.12).
    // Dylan's design changed: "armour creates a blue BAR. shield OUTLINES my
    // character. something is backwards." Armor now renders ONLY as the blue bar
    // under HP (renderTopHealthStrip); the character OUTLINE belongs to SHIELD only
    // (purple pass below). Armor no longer draws a ring around the player.
    // BETA 16.43 — Shield outline pass. Per Dylan: "if i have shield i
    // sould get a solid line outline on my character for shield."
    // BETA 17.12 cycle-30ae — Dylan: "shield should be a purple outline."
    // Color moved from blue (#00bfff) to royal purple (#9d4eff). Blue moved
    // to armor (above). Both rings can layer when player has BOTH stats.
    const shieldRatio = player.maxShield > 0 ? Math.max(0, Math.min(1, player.shield / player.maxShield)) : 0;
    if (shieldRatio > 0) {
      ctx.save();
      ctx.globalAlpha = flashAlpha * (0.4 + 0.6 * shieldRatio);
      ctx.translate(player.x, player.y + bobY);
      if (_pflip) ctx.scale(-1, 1);
      // Tight blur (6px) reads as a solid line on the shield-side, sits
      // inside the armor ring (8px) when both are present.
      ctx.shadowColor = '#9d4eff';   // BETA 17.12 — purple per cycle-30ae
      ctx.shadowBlur = 6;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      ctx.drawImage(_activePlayerImg, -sprW / 2, -sprH / 2, sprW, sprH);
      ctx.drawImage(_activePlayerImg, -sprW / 2, -sprH / 2, sprW, sprH);
      ctx.restore();
    }

    // BETA 21.11 — DASH TRAIL: while mid-dash, draw a few faded ghost copies
    // trailing BEHIND the blink direction so the dash reads as a fast motion
    // streak. Purely cosmetic; sits behind the main sprite. The i-frame flash
    // (flashAlpha, driven by invincibleTimer above) already pulses the sprite.
    if ((player.dashActiveTimer ?? 0) > 0) {
      const _ddx = player.dashDirX ?? 0;
      const _ddy = player.dashDirY ?? 0;
      const _dlen = Math.hypot(_ddx, _ddy) || 1;
      const _ux = _ddx / _dlen, _uy = _ddy / _dlen;
      for (let g = 1; g <= 3; g++) {
        const _back = g * (sprW * 0.28);
        ctx.save();
        ctx.globalAlpha = 0.22 * (1 - (g - 1) / 3);
        ctx.translate(player.x - _ux * _back, player.y + bobY - _uy * _back);
        if (_pflip) ctx.scale(-1, 1);
        ctx.drawImage(_activePlayerImg, -sprW / 2, -sprH / 2, sprW, sprH);
        ctx.restore();
      }
    }

    ctx.save();
    ctx.globalAlpha = flashAlpha;
    ctx.translate(player.x, player.y + bobY);
    if (_pflip) ctx.scale(-1, 1);
    ctx.drawImage(_activePlayerImg, -sprW / 2, -sprH / 2, sprW, sprH);
    ctx.restore();

    // BETA 21.84 — TIMMY REPELLENT SPRAY VFX. Dylan: "no animation to show this is
    // even happening." The 20.65 slow (35% on Timmys within 400wu) was invisible.
    // Now the can floats beside the peanut and continuously sprays a green repellent
    // mist, pulsing stronger so you can SEE the aura that's slowing the Timmys.
    {
      const _ownsSpray = getItemState()?.inventory?.some(s => s.item.id === 'item_timmy_repellent_spray');
      if (_ownsSpray) {
        const _now = performance.now();
        // BETA 22.71 — cosmetics deleted: the floating worn CAN is gone, but the
        // green repellent mist stays (Dylan 21.84: the slow needs visible feedback).
        const _nozX = player.x + sprW * 0.55;
        const _nozY = player.y + bobY - sprH * 0.30;
        // procedural mist: a looping cone of droplets streaming out + fading
        const _dirX = 0.84, _dirY = -0.54;                 // up-right
        const _perpX = -_dirY, _perpY = _dirX;
        const _len = sprW * 0.95;
        const _pulse = 0.6 + 0.4 * Math.max(0, Math.sin(_now * 0.006)); // breathing burst
        const _N = 12;
        ctx.save();
        ctx.globalAlpha = flashAlpha;
        for (let i = 0; i < _N; i++) {
          const _prog = ((_now * 0.0012) + i / _N) % 1;
          const _spread = (((i * 37) % 11) / 11 - 0.5) * _len * 0.5 * _prog; // fans out
          const _mx = _nozX + _dirX * _len * _prog + _perpX * _spread;
          const _my = _nozY + _dirY * _len * _prog + _perpY * _spread;
          const _mr = (2 + _prog * sprW * 0.07) * (0.7 + 0.6 * _pulse);
          const _ma = 0.55 * (1 - _prog) * _pulse;
          ctx.beginPath();
          ctx.arc(_mx, _my, _mr, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(150,230,95,${_ma.toFixed(3)})`;
          ctx.fill();
        }
        ctx.restore();
      }
    }

    // BETA 21.11 / 21.17 — DASH PIPS + RECHARGE METER: diamonds under the player.
    // BETA 21.73 — Dylan: "i can't tell if a dash is active or not — should be two
    // OPPOSING colors, one for ready/available and one for used/not available."
    // GREEN = ready (go), RED = used/unavailable. The recharging pip stays RED but
    // GROWS + brightens as it fills (the "coming back" read), then snaps to GREEN
    // the instant the charge lands. Two-color contrast you can read at a glance.
    {
      const _DASH_READY = '#39ff5e';  // green = available
      const _DASH_USED = '#ff3b3b';   // red = used / not available
      const _charges = player.dashCharges ?? 0;
      const _pipY = player.y + sprH / 2 + 7;
      const _pipGap = 11;
      const _startX = player.x - ((DASH_MAX_CHARGES - 1) * _pipGap) / 2;
      const _rechT = player.dashRechargeTimer ?? 0;
      const _prog = Math.max(0, Math.min(1, 1 - _rechT / _dashRechargeFor(player)));
      for (let i = 0; i < DASH_MAX_CHARGES; i++) {
        const _px = _startX + i * _pipGap;
        const _ready = i < _charges;
        const _charging = i === _charges && _charges < DASH_MAX_CHARGES;
        const _a = _ready ? 0.98 : _charging ? 0.4 + 0.55 * _prog : 0.32;
        const _sz = _ready ? 3.4 : _charging ? 1.8 + 1.4 * _prog : 2.0;
        ctx.save();
        ctx.globalAlpha = _a;
        ctx.fillStyle = _ready ? _DASH_READY : _DASH_USED;
        ctx.beginPath();
        ctx.moveTo(_px, _pipY - _sz);
        ctx.lineTo(_px + _sz, _pipY);
        ctx.lineTo(_px, _pipY + _sz);
        ctx.lineTo(_px - _sz, _pipY);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      }
    }

    // BETA 16.42 — Per Dylan: "when i get attacked it is just a red square
    // on my charaacter. just do the impact hit emoji in red on my
    // character. with a negative sign beside it and the ammount of
    // health or armour i lost in that hit." Replaced the source-atop red
    // overlay (16.39) with a floating "💥 -N" damage marker that pops at
    // player position and rises + fades over the hit-flash window. Reads
    // _playerHitFlashTimer + _playerHitLastDamage (latter set alongside
    // the timer in enemyAdapter).
    {
      const _sAnyPF = state as unknown as Record<string, number | undefined>;
      const flashT = _sAnyPF._playerHitFlashTimer ?? 0;
      const lastDmg = _sAnyPF._playerHitLastDamage ?? 0;
      const lastWasShield = (_sAnyPF._playerHitLastWasShield ?? 0) > 0;
      if (flashT > 0 && lastDmg > 0) {
        const flashAmt = Math.min(1, flashT / 0.55);
        // Float up + fade out as flashT decays from 1 → 0
        const liftY = -16 - (1 - flashAmt) * 22;
        ctx.save();
        ctx.globalAlpha = 0.85 * flashAmt + 0.15;
        ctx.font = 'bold 22px "Share Tech Mono", monospace';
        ctx.fillStyle = '#ff2a2a';
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 3;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const label = lastWasShield ? `💥 -${Math.round(lastDmg)} ◈` : `💥 -${Math.round(lastDmg)}`;
        ctx.strokeText(label, player.x, player.y + liftY);
        ctx.fillText(label, player.x, player.y + liftY);
        ctx.restore();
      }
    }
  }
  // BETA 23.01 — WEAPON DEPTH: weapons at/below Greg draw in FRONT of his
  // sprite but BEFORE the over-player machines (so a machine that hides
  // Greg hides his gun too — Dylan rules 1/3). Pass self-converts, so exit
  // the camera transform for it, then re-enter for the machine flush.
  ctx.restore();
  renderOrbitWeaponSpritesPass(ctx, state, 'front');
  ctx.save();
  ctx.translate(offsetX, offsetY);
  ctx.scale(scale, scale);
  // BETA 22.98 — y-sort flush: machines whose base is below the player draw
  // OVER him now (sorted by base y so stacked machines layer correctly).
  if (_propsOverPlayer.length > 0) {
    // 23.13 — props in front of the player flush first (machines keep 300wu
    // clearance from props, so the two lists can't visually interleave)
    _propsOverPlayer.sort((a, b) => a.y - b.y);
    for (const _wp of _propsOverPlayer) _drawWorldProp(ctx, _wp);
    _propsOverPlayer = [];
  }
  if (_machinesOverPlayer.length > 0) {
    _machinesOverPlayer.sort((a, b) => a.y - b.y);
    for (const _mch of _machinesOverPlayer) _drawWildCardMachine(ctx, state, _mch);
    _machinesOverPlayer = [];
  }
  if (_bungulators.length > 0) {
    const bungIdleImg = getCachedImage('/items/item_bungulators_idle.png');
    const bungPunchImg = getCachedImage('/items/item_bungulators_punch.png');
    for (const bung of _bungulators) {
      const img = (bung.punchAnimTimer > 0 ? bungPunchImg : bungIdleImg) ?? bungIdleImg;
      if (img) {
        const sprH = 56;
        const aspect = img.naturalWidth / img.naturalHeight;
        const sprW = sprH * aspect;
        ctx.save();
        ctx.translate(bung.x, bung.y);
        if (bung.facing === -1) ctx.scale(-1, 1);
        ctx.drawImage(img, -sprW / 2, -sprH / 2, sprW, sprH);
        ctx.restore();
      }
    }
  }
  // BETA 13.102 — Iranian Bird visual improvements (rotation to face enemy,
  // 4 spinning propellers on wing tips, tracer bullets). User: "iranian bird
  // should point at the enemies. needs spinning 4 propellers on the 4
  // propellers. bullet should be circle white dots. sharp red pulse around it
  // just a tiny bit bigger that the white circle. little to no fade in the red".
  {
    const birdSlot = getItemState()?.inventory.find(s => s.item.id === 'item_iranian_bird');
    if (birdSlot && _iranianBirds.length > 0) {
      const birdImg = getCachedImage('/items/item_iranian_bird_sprite.png');
      if (birdImg) {
        // BETA 14.78 — render every bird in the array. Each bird's world
        // position uses its own offsetAngle so they spread evenly around the
        // player even though they share a rotation rate.
        for (const bird of _iranianBirds) {
          const renderAngle = bird.angle + bird.offsetAngle;
          const birdX = player.x + Math.cos(renderAngle) * 110;
          const birdY = player.y + Math.sin(renderAngle) * 110;
          const sprH = 48;
          const aspect = birdImg.naturalWidth / birdImg.naturalHeight;
          const sprW = sprH * aspect;
          ctx.save();
          ctx.translate(birdX, birdY);
          ctx.rotate(bird.facing); // face the most recent zap target (per-bird)
          ctx.drawImage(birdImg, -sprW / 2, -sprH / 2, sprW, sprH);

          // 4 spinning propeller plus-shapes on the 4 corners (wing tips).
          const propPositions: Array<[number, number]> = [
            [-sprW * 0.40, -sprH * 0.30],
            [+sprW * 0.40, -sprH * 0.30],
            [-sprW * 0.40, +sprH * 0.30],
            [+sprW * 0.40, +sprH * 0.30],
          ];
          const propSpinAngle = state.time * 28;
          const propSize = sprH * 0.13;
          for (const [px, py] of propPositions) {
            ctx.save();
            ctx.translate(px, py);
            ctx.rotate(propSpinAngle);
            ctx.fillStyle = 'rgba(255,255,255,0.92)';
            ctx.strokeStyle = 'rgba(60,60,90,0.95)';
            ctx.lineWidth = Math.max(1, sprH * 0.012);
            ctx.fillRect(-propSize, -propSize * 0.18, propSize * 2, propSize * 0.36);
            ctx.fillRect(-propSize * 0.18, -propSize, propSize * 0.36, propSize * 2);
            ctx.strokeRect(-propSize, -propSize * 0.18, propSize * 2, propSize * 0.36);
            ctx.strokeRect(-propSize * 0.18, -propSize, propSize * 0.36, propSize * 2);
            ctx.beginPath();
            ctx.arc(0, 0, propSize * 0.22, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(40,40,60,0.95)';
            ctx.fill();
            ctx.restore();
          }
          ctx.restore();
        }

        // Bullet tracers: white circle with rotating black dots + sharp red
        // pulse outline (no shadowBlur, no fade alpha — sharp per user).
        for (const b of _iranianBirdBullets) {
          const t = b.age / b.lifetime;
          const bx = b.x0 + (b.x1 - b.x0) * t;
          const by = b.y0 + (b.y1 - b.y0) * t;
          ctx.save();
          ctx.translate(bx, by);
          ctx.strokeStyle = '#ff2233';
          ctx.lineWidth = 2.5;
          ctx.beginPath();
          ctx.arc(0, 0, 6.5, 0, Math.PI * 2);
          ctx.stroke();
          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          ctx.arc(0, 0, 5, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#222';
          for (let d = 0; d < 3; d++) {
            const da = (d / 3) * Math.PI * 2 + b.age * 8;
            ctx.beginPath();
            ctx.arc(Math.cos(da) * 1.8, Math.sin(da) * 1.8, 0.9, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.restore();
        }
      }
    }
  }
  // BETA 23.28 — CHICK MAGNET STATUES DELETED (Dylan: "mega caseoh statue... removed immediately"). Surge Statues are the replacement; the pull mechanic returns through them later.
  ctx.restore();

  // ── 12b. Render arc slash visuals ON TOP of enemies + player (melee swing flash) ──
  renderWeaponArcSlashOverlay(ctx, state);

  // ── 13. Render weapons/projectiles (delegated) — uses its own world→screen conversion ──
  if (weaponsModule) weaponsModule.render(ctx, state, viewBounds);

  // ── 14b. Render item effects (poison clouds, burst explosions, Bubble Wrap flash) ──
  renderItemEffects(ctx, camera.x, camera.y, width, height, camera.zoom);

  // ── BETA 13.100b — HP / shield bar layer. ALWAYS ON TOP per user request:
  // "bot health bar above character and armor when it is on should never be
  // under walls rooms, character, weapons, enemies. always on top." Renders
  // last in the world layer, before low-health vignette and HUD. Re-enters
  // camera transform.
  // BETA 16.68 — shake now applied at outer canvas save so this re-entry
  // is pure camera math. Player HP bar shakes WITH the player.
  ctx.save();
  ctx.translate(offsetX, offsetY);
  ctx.scale(scale, scale);
  {
    const sprH = player.spriteSize * PLAYER_RENDER_SCALE;
    const barW = 52;
    const barH = 5;
    const barX = player.x - barW / 2;
    const barGap = 3;
    const hpBarY = player.y - sprH / 2 - 10;
    const shBarY = hpBarY - barH - barGap;
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(barX - 1, hpBarY - 1, barW + 2, barH + 2);
    // BETA 16.25 — PSBOT cycle-26 P0.2: drain visualization during
    // death_replay (mirrors top-strip logic).
    let _drainHp = player.hp;
    if (state.phase === 'death_replay') {
      const _sAny = state as unknown as {
        _deathReplayHpStart?: number;
        _deathReplayElapsedSec?: number;
      };
      const startHp = _sAny._deathReplayHpStart ?? player.hp;
      const elapsed = _sAny._deathReplayElapsedSec ?? 0;
      const drainT = Math.min(1, elapsed / 1.0);
      _drainHp = startHp * (1 - drainT);
    }
    const hpPctHead = Math.max(0, _drainHp / player.maxHp);
    ctx.fillStyle = '#39ff14';
    ctx.fillRect(barX, hpBarY, barW * hpPctHead, barH);
    if (player.maxShield > 0) {
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fillRect(barX - 1, shBarY - 1, barW + 2, barH + 2);
      const shPct = Math.max(0, player.shield / player.maxShield);
      ctx.fillStyle = '#44aaff';
      ctx.fillRect(barX, shBarY, barW * shPct, barH);
    }
  }
  ctx.restore();

  // BETA 16.68 — END world-shake region. Everything rendered ABOVE this
  // line (rooms, enemies, player, projectiles, ground effects, loot,
  // particles, item effects) inherits the canvas-level shake translate
  // so they all jolt together. HUD renders below WITHOUT shake so
  // screen-anchored UI (HP bar, FPS, chat, stats panel, etc.) stays put.
  ctx.restore();           // <-- WORLD-SHAKE restore (matched at line ~3935)

  // ── BETA 20.95 — Dynamic lighting (OFF by default; debug-panel toggle). Drops
  // a dark "night" layer over the WORLD with a soft glow around the player, in
  // screen space (after the world restore) so the vignette + HUD below stay
  // bright/readable. No-op for real players unless debugDynamicLighting is on.
  renderDynamicLighting(ctx, state, width, height);

  // ── BETA 23.19 — THE LIGHTING PASS (plan P3) ─────────────────────────────
  // Near-night ambient + real pools: lamps sodium-warm, fire barrels
  // flickering orange, machine signs warm spill, lit shrines blue, a soft
  // neutral pool around Greg for readability. Drawn over the world, UNDER
  // the vignettes/HUD (those are postlight — CrossCode plane 101).
  if (MAP_LIGHTING && BOUNDLESS_MAP && !(MAPDEV.on && !MAPDEV.showProps)) {
    const _liSeed = state.map.roomMap.seed ?? 1;
    const _lights: LightSource[] = [];
    // player readability pool
    // 23.29 — noGlow: the additive pass was WASHING GREG OUT (Dylan: "bright
    // and faded"); his pool now only lifts the darkness, adds no glow.
    _lights.push({ x: player.x, y: player.y + 20, radius: 190, r: 235, g: 228, b: 215, intensity: 0.4, noGlow: true });
    if (!FAR_LOD)   // 23.52 — prop lights are sub-pixel at far zoom; skip the world scan
    for (const _lp of propsInRect(viewLeft - 400, viewTop - 400, viewRight + 400, viewBottom + 400, _liSeed)) {
      if (_lp.def.id === 'street_lamp') {
        // sodium head overhangs the pole arm — pool sits ahead of the base
        // 23.32 — pool under the GLASS DOME (0.684 of width from the pole
        // anchor, mirrored when the lamp is flipped), biased a touch south.
        const _domeOff = 50 * (_lp.flip ? -1 : 1);
        // 23.49 — per-lamp persona: brightness/radius/flicker all individual
        const _lpp = _lampPersona(_lp.x, _lp.y);
        const _live = _lampLive(_lp.x, _lp.y, performance.now());
        _lights.push({ x: _lp.x + _domeOff, y: _lp.y + 4, radius: _lpp.radius, r: 255, g: 184, b: 77, intensity: 1.05 * _live, flicker: 0 });
      } else if (_lp.def.id === 'fire_barrel') {
        _lights.push({ x: _lp.x, y: _lp.y - 20, radius: 130, r: 255, g: 140, b: 50, intensity: 0.85, flicker: 0.4 });
      } else if (_lp.def.id === 'campfire_ring') {
        // 23.36 — embers still hot in the ring: lower + redder than the
        // barrel (coals, not open flame), lazier flicker
        _lights.push({ x: _lp.x, y: _lp.y - 8, radius: 150, r: 255, g: 118, b: 42, intensity: 0.75, flicker: 0.3 });
      } else if (_lp.def.id === 'cp_lantern') {
        // 23.68 §3b L3 — the lantern's glow was a BAKED ellipse in the v1
        // sprite (cropped off); this is its real pool: small, warm, lazy wick
        _lights.push({ x: _lp.x, y: _lp.y + 6, radius: 95, r: 255, g: 196, b: 110, intensity: 0.6, flicker: 0.22 });
      } else if (_lp.def.id === 'surge_nest_mound') {
        // 23.31 — the nest's crater seethes (steady ominous purple)
        _lights.push({ x: _lp.x, y: _lp.y - 40, radius: 210, r: 170, g: 92, b: 207, intensity: 0.55, flicker: 0.18 });
      } else if (_lp.def.id === 'police_car_lightbar') {
        // 23.31 — alternating red/blue pool synced to the bar (420ms)
        const _cop = Math.floor(performance.now() / 420) % 2 === 0;
        _lights.push({ x: _lp.x, y: _lp.y - 10, radius: 170, r: _cop ? 255 : 64, g: _cop ? 46 : 128, b: _cop ? 46 : 255, intensity: 0.5 });
      }
      // 23.39 — statue_surge light pool REMOVED (Dylan: "you don't need to
      // add a light on him — he should be illuminating plenty with the
      // purple on the statue, just illuminate that"). The active overlay
      // now draws additively in _drawWorldProp instead.
    }
    for (const _lc of roomMap.chests) {
      if (_lc.state === 'gone' || _lc.type === 'elite') continue;
      if (_lc.x < viewLeft - 300 || _lc.x > viewRight + 300 || _lc.y < viewTop - 300 || _lc.y > viewBottom + 300) continue;
      _lights.push({ x: _lc.x, y: _lc.y + 26, radius: 130, r: 255, g: 214, b: 140, intensity: 0.45 });
    }
    for (const _ls of roomMap.shrines) {
      if (_ls.type === 'boss' || _ls.state !== 'spent_lit') continue;
      if (_ls.x < viewLeft - 300 || _ls.x > viewRight + 300 || _ls.y < viewTop - 300 || _ls.y > viewBottom + 300) continue;
      _lights.push({ x: _ls.x, y: _ls.y, radius: 170, r: 90, g: 160, b: 255, intensity: 0.6 });
    }
    renderLighting(ctx, _lights, camera, width, height, performance.now());
  }

  // ── Low-health vignette (CoD-style red edge bleed) ──
  renderLowHealthVignette(ctx, state, width, height);
  renderGasVignette(ctx, state, width, height);

  // ── HUD ──
  // BETA 23.00 — MAPDEV passes (horizon depth test + debug overlays), drawn
  // over the world, under the HUD. Horizon preview = exactly what P4 ships.
  if (MAPDEV.on) {
    if (MAPDEV.showHorizon) drawHorizonPreview(ctx, width, height, camera.x);
    drawMapDevOverlays(ctx, width, height, {
      toScreenX: (wx: number) => (wx - camera.x) * camera.zoom + width / 2,
      toScreenY: (wy: number) => (wy - camera.y) * camera.zoom + height / 2,
      scale: camera.zoom,
      chunkWu: 1024,
      machines: roomMap.chests.map(c => ({ x: c.x, y: c.y, gone: c.state === 'gone', elite: c.type === 'elite' })),
      shrines: roomMap.shrines.filter(s => s.type !== 'boss').map(s => ({ x: s.x, y: s.y })),
      player: { x: player.x, y: player.y, footOff: (player.spriteSize || 40) * 0.6, size: player.size || 14 },
      interactRadius: MACHINE_INTERACT_RADIUS,
      viewLeft, viewTop, viewRight, viewBottom,
    });
  }

  if (!(MAPDEV.on && !MAPDEV.showUI)) renderHUD(ctx, state, width, height);   // 23.06 [8]
  // BETA 23.06 — DT-A: perf overlay + one-shot screenshot (drawn above HUD)
  if (MAPDEV.on && MAPDEV.showPerf) {
    let _alive = 0;
    const _ess = getEnemySystemState();
    if (_ess?.enemies) for (const e of _ess.enemies) if (e.alive) _alive++;
    let _projs = 0;
    const _wss = getWeaponSystemState();
    if (_wss?.projectiles) for (const pr of _wss.projectiles) if (pr.alive) _projs++;
    let _orbs = 0;
    if (state.lootOrbs?.orbs) for (const o of state.lootOrbs.orbs) if (o.alive) _orbs++;
    _perfRenderMs = performance.now() - _perfRenderT0;
    drawPerfOverlay(ctx, width, {
      updateMs: _perfUpdateMs, renderMs: _perfRenderMs,
      enemies: _alive, projectiles: _projs, orbs: _orbs,
      chunks: chunkCacheSize(),
    });
  }
  if (MAPDEV.on && MAPDEV.wantScreenshot) {
    MAPDEV.wantScreenshot = false;
    takeMapDevScreenshot(ctx.canvas as HTMLCanvasElement, GAME_VERSION);
  }

  // BETA 16.99 — Per Dylan: "Q ITEM COVERING UI. WHYYYYYYYYYYYYYYYY...
  // its causing major fps drop too. remove all q items. make them all
  // passives. remove the press Q lingo and the on screen things that
  // live on card." Tray render at bottom-left was overlapping the
  // FL/T HUD on small screens AND adding per-frame cost (cooldown
  // sweep + glow + tooltip hit-test). VooGoo Rager auto-triggers
  // now (HP < 50% + cd ready); Whoopie + Banana were already auto-
  // drop passives. Tray no longer needed. Function kept for code
  // archaeology / future feature; just not called.
  void renderActiveItemTray;

  // ── Compass arrows (screen-edge indicators for chests + boss portal) ──
  renderCompassArrows(ctx, state, width, height);

  if (state.debug) renderDebug(ctx, state, width, height);
  if (state.interactPromptAlpha > 0.01) renderInteractPrompt(ctx, state, width, height);

  // Extraction countdown HUD — always on once armed (the timer runs anywhere).
  if (state.extractionArmedAt != null && state.phase === 'playing') {
    const _remain = Math.max(0, EXTRACT_COUNTDOWN_SEC - (state.time - state.extractionArmedAt));
    const _frac = _remain / EXTRACT_COUNTDOWN_SEC;
    const _urgent = _remain <= 10;
    const _blink = _urgent ? (0.55 + 0.45 * Math.sin(state.time * 12)) : 1;
    const _bx = width / 2, _by = 54, _bw = 300, _bh = 14;
    ctx.save();
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.font = 'bold 22px "Share Tech Mono", monospace';
    ctx.shadowColor = 'rgba(0,0,0,0.85)'; ctx.shadowBlur = 6;
    ctx.fillStyle = _urgent ? `rgba(255,84,84,${_blink})` : '#48e0a0';
    ctx.fillText(`🛸 EXTRACTING — ${Math.ceil(_remain)}s`, _bx, _by);
    ctx.shadowBlur = 0;
    ctx.fillStyle = 'rgba(0,0,0,0.6)'; ctx.fillRect(_bx - _bw / 2, _by + 16, _bw, _bh);
    ctx.fillStyle = _urgent ? `rgba(255,70,70,${_blink})` : '#2ad19a'; ctx.fillRect(_bx - _bw / 2, _by + 16, _bw * _frac, _bh);
    ctx.strokeStyle = 'rgba(255,255,255,0.4)'; ctx.lineWidth = 1; ctx.strokeRect(_bx - _bw / 2, _by + 16, _bw, _bh);
    ctx.restore();
  }


  if (state.phase === 'won') renderWinScreen(ctx, state, width, height);
  if (state.phase === 'paused') {
    renderPauseMenu(ctx, state, width, height);
    // Balance panel renders on top of the pause menu when open
    if (isBalancePanelVisible()) renderBalancePanel(ctx, state, width, height);
  }
  if (state.phase === 'levelup') renderLevelUpScreen(ctx, state, width, height);
  if (state.phase === 'shrine_choice') renderShrineChoiceScreen(ctx, state, width, height);
  if (state.phase === 'chest_spin') renderChestSpinScreen(ctx, state, width, height);
  if (state.phase === 'chest_choice') renderChestChoiceScreen(ctx, state, width, height);
  if (state.phase === 'ultra_chest') renderUltraChestScreen(ctx, state, width, height);

  // BETA 22.09 — the big hold-to-inspect map renders LAST so it's ON TOP of the shrine/chest/
  // level-up pick screens (Dylan: "when i open the map in a shrine/chest/level-up it's behind").
  renderBigMapOverlayIfHeld(ctx, state, width, height);

  // BETA 16.21 — PSBOT cycle-24 P0.2: re-render the chat panel ON TOP of
  // the overlay dim layer during level-up / shrine / chest screens so it
  // stays the brightest UI element. Dylan: "lift the chat panel out of
  // the dimming layer applied to the rest of the game, give it a slight
  // glow or brighter background so it pops." Achieved by painting a
  // semi-opaque dark backdrop behind the chat (cuts through the dim
  // layer) + a subtle glow border + the chat text on top. The earlier
  if (state.phase === 'levelup' || state.phase === 'shrine_choice' ||
      state.phase === 'chest_choice' || state.phase === 'ultra_chest' ||
      state.phase === 'chest_spin') {
    // BETA 22.49 — the live-chat re-render pass was removed here; only the
    // inventory-panel pop-over below remains.
    // BETA 16.24 — PSBOT cycle-25 P0.3: same lift treatment for the
    // bottom-right inventory panel (weapons + percs + bullshii items).
    // Per Dylan: "they dont need to be faded or hidden when i level up,
    // shrine, or chest screens. and i should be able to hover over the
    // items and click them to see my items and what they do." renderRight
    // Panel is called inside renderHUD (gets painted under the overlay
    // dim). This second call paints it AGAIN on top of the dim with
    // matching glow halo so it pops as the brightest UI element. Hover
    // tooltips inside renderRightPanel still work because they paint
    // last in this pass.
    const padH = 16;
    // BETA 22.62 — the right inventory panel is NO LONGER re-lifted above the
    // dim during pick screens (Dylan's art brief: "do not let the weapon
    // slots or bottom-right inventory compete with the cards"). It still
    // renders dimmed under the overlay like the rest of the HUD.
    // BETA 16.24 — PSBOT cycle-25 P0.4: same lift for the new left-side
    // STATS panel.
    // BETA 16.82 — gated on hudHidden so the Y-button HUD toggle clears
    // the left-side space for cards on 720p Ally screens.
    // BETA 20.26 — Dylan: the stats panel must get BIG on the pick screens AND
    // show, in real time, what the card under the cursor/focus would do to my
    // stats. Gather the hovered card's structured stat delta(s) per phase, then
    // pass desiredScale 1.5 + the projection (in-game HUD call below stays 1.0).
    let _statPreview: { stat: string; value: number; isPercent: boolean }[] | undefined;
    let _statPreviewNote: string | undefined;
    try {
      if (state.phase === 'levelup') {
        const idx = getLevelUpHoverIndex();
        if (idx >= 0) _statPreview = getLevelUpOptionDisplayData()[idx]?.previewMods;
      } else if (state.phase === 'shrine_choice') {
        const idx = getShrineHoverIndex();
        if (idx >= 0) _statPreview = getShrineBuffDisplayOptions()[idx]?.previewMods;
      } else if (state.phase === 'chest_choice') {
        const idx = getChestHoverIndex();
        if (idx >= 0) {
          const _chestItem = getChestOptionDisplayData()[idx];
          _statPreview = _chestItem?.previewMods;
          // BETA 20.34 — show a note whenever there's nothing to project: no
          // statModifiers (effect items), OR none of them map to a panel stat
          // (e.g. the obscure gold/damageReduction items). Every active item
          // DOES something, so this beats a blank panel.
          const _hasProjectable = !!_statPreview && _statPreview.some(m => !!_PREVIEW_SRC_TO_PANEL[m.stat]);
          if (_chestItem && !_hasProjectable) {
            _statPreviewNote = '✦ Triggers an effect';
          }
        }
      }
    } catch { /* projection is best-effort; never block the panel */ }
    // BETA 20.36 — Dylan: the enlarged panel was COVERING the choice cards. At
    // 1.85× its right edge reached ~504px and overlapped the first card. Capped
    // to 1.45× (right edge ~399px) so it clears the cards while still reading BIG.
    // BETA 22.62 v2 — the taped paper STATS NOTE replaces the panel on pick
    // screens (Dylan: physical prop, visually secondary to the cards).
    void _statPreviewNote;
    if (!state.hudHidden) _drawPickStatsNote(ctx, state, width, height, _statPreview);
    // BETA 22.64 — the survivor pose + speech bubble paint LAST so the
    // character stands IN FRONT of the cards and the stats note (Dylan:
    // "the character needs to not be behind cards... his head is being cut
    // off from the stats card").
    if (state.phase === 'levelup' || state.phase === 'shrine_choice' || state.phase === 'chest_choice') {
      const _quipSeed = state.phase === 'levelup' ? state.player.level
        : state.phase === 'shrine_choice' ? state.player.level + 3
        : state.killCount + 1;
      _drawPickPose(ctx, state, width, height);
      _drawPickQuip(ctx, state, width, height, _quipSeed);
    }
  }
  // BETA 15.93 — RIP PEANUT death screen replaces the legacy
  // renderGameOverScreen. Same activation phase; different visuals +
  // grade badge + trash talk cycler + REPLAY/QUIT buttons.
  // BETA 15.99 — Bot mode skips the death screen entirely (the
  // 2-second restart watchdog kills it anyway, leaves only stars
  // visible). Bot mode renders a simple text overlay so the bot
  // farm dashboard / Dylan can see what's happening without the
  // full eulogy experience.
  if (state.phase === 'gameover') {
    if (isBotEnabled()) {
      // Simple bot-mode death overlay — black background + small status text.
      ctx.save();
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = '#888888';
      ctx.font = `bold 28px "Share Tech Mono", monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('BOT DEAD — RESTARTING', width / 2, height / 2);
      ctx.restore();
    } else {
      renderDeathScreen(ctx, state, width, height, playerSprite);
    }
  }

  // Pickup notifications (floating text near top-center) — BETA 22.06 not on the death screen
  // (no in-game UI bleed onto the RIP screen).
  if (state.phase !== 'gameover') renderPickupNotifications(ctx, state, width, height);

  // BETA 21.16 — START-OF-RUN OBJECTIVE CARD (cinematic). Per Dylan: the old
  // briefing was "too much jargon" — cut to the objective and "make it look cool."
  // Big glowing slam-in title + HUD corner brackets + scanline warning band, fades
  // out by ~8.5s. Gated on phase 'playing' + early time (monotonic clock → only at
  // the very start, never on floor advance) + not bot mode. Wrapped in try/catch
  // so a render hiccup can never crash the gameplay frame.
  try {
    const _introTotal = 8.5;
    const _it = state.time ?? 0;
    if (state.phase === 'playing' && _it < _introTotal && !isBotEnabled()) {
      let _ia = 1;
      if (_it < 0.5) _ia = _it / 0.5;                       // fade in
      else if (_it > 6.5) _ia = (_introTotal - _it) / 2.0;   // fade out over last 2s
      _ia = Math.max(0, Math.min(1, _ia));
      if (_ia > 0.01) {
        const _md = Math.min(width, height);
        const _l1 = 'FIND THE BOSS PORTAL.';
        const _l2 = 'KILL THE BOSS.';
        const _tag = 'GOOD LUCK.';
        // Fit the big lines to ~84% width so even the slam-in peak can't clip.
        let _bigF = Math.min(Math.round(_md * 0.058), 50);
        let _tagF = Math.min(Math.round(_md * 0.026), 22);
        ctx.font = `bold ${_bigF}px "Share Tech Mono", monospace`;
        let _wmax = Math.max(ctx.measureText(_l1).width, ctx.measureText(_l2).width);
        const _fitW = width * 0.84;
        if (_wmax > _fitW) {
          const _sc = _fitW / _wmax;
          _bigF = Math.max(12, Math.floor(_bigF * _sc));
          _tagF = Math.max(11, Math.floor(_tagF * _sc));
          ctx.font = `bold ${_bigF}px "Share Tech Mono", monospace`;
          _wmax = Math.max(ctx.measureText(_l1).width, ctx.measureText(_l2).width);
        }
        // Gentle slam-in: 1.12x -> 1.0x over the first 0.4s (ease-out cubic).
        const _slamT = Math.min(1, _it / 0.4);
        const _slam = 1.12 - 0.12 * (1 - Math.pow(1 - _slamT, 3));
        const _pulse = 0.82 + 0.18 * Math.sin(_it * 4.2);
        // Upper third, clamped so the warning band clears the top HUD row.
        const _half = Math.min(108, Math.round(height * 0.17));
        const _cy = Math.max(Math.round(height * 0.26), 70 + _half);

        // ── dark warning band (untransformed, behind the text) ──
        ctx.save();
        ctx.globalAlpha = _ia * 0.62;
        ctx.fillStyle = '#05060a';
        ctx.fillRect(0, _cy - _half, width, _half * 2);
        ctx.globalAlpha = _ia * 0.55;
        ctx.strokeStyle = '#ff3b3b';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(0, _cy - _half); ctx.lineTo(width, _cy - _half);
        ctx.moveTo(0, _cy + _half); ctx.lineTo(width, _cy + _half);
        ctx.stroke();
        ctx.globalAlpha = _ia * 0.05;                         // faint scanlines
        ctx.fillStyle = '#ffffff';
        for (let _sy = _cy - _half + 2; _sy < _cy + _half; _sy += 4) ctx.fillRect(0, _sy, width, 1);
        ctx.restore();

        // ── slam-in glowing title + HUD corner brackets (transformed) ──
        ctx.save();
        ctx.globalAlpha = _ia;
        ctx.translate(width / 2, _cy);
        ctx.scale(_slam, _slam);
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const _y1 = -_bigF * 0.66;
        const _y2 = _bigF * 0.42;
        const _yTag = _y2 + _bigF * 0.62 + _tagF * 0.7;
        ctx.shadowColor = 'rgba(255,40,40,0.95)';
        ctx.shadowBlur = 26 * _pulse;
        ctx.font = `bold ${_bigF}px "Share Tech Mono", monospace`;
        ctx.fillStyle = '#ffffff';
        ctx.fillText(_l1, 0, _y1);
        ctx.fillStyle = '#ff4040';
        ctx.fillText(_l2, 0, _y2);
        ctx.shadowBlur = 0;
        ctx.font = `bold ${_tagF}px "Share Tech Mono", monospace`;
        ctx.fillStyle = '#ffd24a';
        ctx.fillText(_tag, 0, _yTag);
        const _bw = _wmax / 2 + 30;
        const _bh = _bigF * 1.55;
        const _bl = Math.max(16, _bigF * 0.4);
        ctx.strokeStyle = 'rgba(255,59,59,0.9)';
        ctx.lineWidth = 3;
        ctx.shadowColor = 'rgba(255,40,40,0.8)';
        ctx.shadowBlur = 12 * _pulse;
        ctx.beginPath();
        ctx.moveTo(-_bw, -_bh + _bl); ctx.lineTo(-_bw, -_bh); ctx.lineTo(-_bw + _bl, -_bh);
        ctx.moveTo(_bw - _bl, -_bh); ctx.lineTo(_bw, -_bh); ctx.lineTo(_bw, -_bh + _bl);
        ctx.moveTo(-_bw, _bh - _bl); ctx.lineTo(-_bw, _bh); ctx.lineTo(-_bw + _bl, _bh);
        ctx.moveTo(_bw - _bl, _bh); ctx.lineTo(_bw, _bh); ctx.lineTo(_bw, _bh - _bl);
        ctx.stroke();
        ctx.shadowBlur = 0;
        ctx.restore();
      }
    }
  } catch { /* never let the intro card crash the frame */ }

  // BETA 22.06 — Dylan: "when i die i don't want left-over in-game UI bleeding over the death
  // screen." These HUD overlays draw AFTER renderDeathScreen, so on gameover they painted on top
  // of the RIP screen (the "⚠ INCOMING HORDE / TIMMY ENTRY" banner was the culprit). Skip all
  // in-game HUD once we're on the death screen.
  if (state.phase !== 'gameover') {
    // Last-stand full-screen flash
    renderLastStandFlash(ctx, state, width, height);

    // Quest/unlock slide-up banners (bottom-center, always on top of everything)
    renderUnlockBanners(ctx, state, width, height);
  }

  // BETA 17.16 — PSBOT cycle-30af floor transition overlay. Per Dylan:
  // "simple black screen showing my character and then words on screen
  // saying GOING TO LEVEL 2!!" Fires after portal interact on floor
  // advance. Black overlay with player character + big text + difficulty
  // preview for 2.5s. Game ticks underneath (enemies cleared, time reset)
  // so it's a quiet visual masking of the regen.
  {
    const _sAnyFt = state as unknown as Record<string, number | undefined>;
    const _ftTimer = _sAnyFt._floorTransitionTimer ?? 0;
    if (_ftTimer > 0) {
      const targetFloor = _sAnyFt._floorTransitionTargetFloor ?? 2;
      const elapsed = 2.5 - _ftTimer;
      // Fade curve: 0.0-0.5s fade in, 0.5-2.0s hold, 2.0-2.5s fade out
      let alpha = 1.0;
      if (elapsed < 0.5) alpha = elapsed / 0.5;
      else if (elapsed > 2.0) alpha = (2.5 - elapsed) / 0.5;
      alpha = Math.max(0, Math.min(1, alpha));
      // Black overlay
      ctx.save();
      ctx.fillStyle = `rgba(0, 0, 0, ${alpha})`;
      ctx.fillRect(0, 0, width, height);
      // Text + character only during hold phase
      if (elapsed > 0.4 && elapsed < 2.1 && alpha > 0.85) {
        // Big "GOING TO LEVEL N!!" text
        ctx.fillStyle = '#ffd700';
        ctx.font = 'bold 72px "Share Tech Mono", monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.shadowColor = '#ff8800';
        ctx.shadowBlur = 24;
        ctx.fillText(`GOING TO LEVEL ${targetFloor}!!`, width / 2, height / 2);
        ctx.shadowBlur = 0;
        // Difficulty preview underneath. BETA 17.31 — wall-clock time bonus.
        const _olMinutes = (Date.now() - state.startTime) / 60_000;
        const _mult = (2.0 + Math.min(2.5, _olMinutes * 0.15)).toFixed(1);
        ctx.fillStyle = '#ff4444';
        ctx.font = 'bold 28px "Share Tech Mono", monospace';
        ctx.fillText(`DIFFICULTY ${_mult}× HARDER`, width / 2, height / 2 + 60);
        // Subtitle
        ctx.fillStyle = '#888888';
        ctx.font = '20px "Share Tech Mono", monospace';
        ctx.fillText('Fresh map · new rooms · same loadout', width / 2, height / 2 + 100);
      }
      ctx.restore();
    }
  }
}

// ─── Floor Tile Rendering ───────────────────────────────────────────
// One grass tile image, rendered via createPattern for seamless tiling (no grid lines)

let floorPattern: CanvasPattern | null = null;
let floorPatternUrl = '';

function renderFloorTiles(
  ctx: CanvasRenderingContext2D, state: GameState,
  viewLeft: number, viewTop: number, viewRight: number, viewBottom: number
) {
  const WS = state.map.WORLD_SIZE;
  const { roomMap } = state.map;

  // Use the single grass tile URL
  const url = roomMap.floorTileUrls[0];
  const img = url ? getCachedImage(url) : null;

  // Floor renders across the full world — wall images include their own floor portion
  // that blends seamlessly with this tile, so no clamping needed.
  const x0 = Math.max(0, viewLeft);
  const y0 = Math.max(0, viewTop);
  const x1 = Math.min(WS, viewRight);
  const y1 = Math.min(WS, viewBottom);
  if (x1 <= x0 || y1 <= y0) return;

  if (img) {
    // Build or reuse a CanvasPattern — seamless, no grid lines
    if (!floorPattern || floorPatternUrl !== url) {
      // Scale the source image to 512×512 so the pattern repeat distance is large enough to be invisible
      const tileCanvas = document.createElement('canvas');
      tileCanvas.width = 512;
      tileCanvas.height = 512;
      const tileCtx = tileCanvas.getContext('2d')!;
      tileCtx.drawImage(img, 0, 0, 512, 512);
      floorPattern = ctx.createPattern(tileCanvas, 'repeat');
      floorPatternUrl = url;
    }
    if (floorPattern) {
      ctx.fillStyle = floorPattern;
      ctx.fillRect(x0, y0, x1 - x0, y1 - y0);
    }
  } else {
    // Fallback solid dark charcoal while the concrete tile loads
    ctx.fillStyle = '#2d2d30';
    ctx.fillRect(x0, y0, x1 - x0, y1 - y0);
  }
}

// ─── Outside Texture (void beyond border walls) ───────────────────────────────

// Dark industrial abyss outside the border — code-based, no texture needed.
// Features: deep void base, fog gradient near walls, animated fog wisps,
// floating dust/ember particles, and subtle depth noise for visual interest.

// Pre-computed void dust particles (seeded once, animated via time)
interface VoidDust {
  x: number; y: number; // offset from world center (range -4096..+4096)
  size: number;         // 1..4 wu
  speed: number;        // drift speed
  angle: number;        // drift direction
  brightness: number;   // 0..1
  phase: number;        // twinkle phase offset
}
const VOID_DUST: VoidDust[] = [];
const VOID_DUST_COUNT = 120;
for (let i = 0; i < VOID_DUST_COUNT; i++) {
  VOID_DUST.push({
    x: (Math.random() - 0.5) * 10000,
    y: (Math.random() - 0.5) * 10000,
    size: 1 + Math.random() * 3,
    speed: 2 + Math.random() * 8,
    angle: Math.random() * Math.PI * 2,
    brightness: 0.15 + Math.random() * 0.35,
    phase: Math.random() * Math.PI * 2,
  });
}

// Pre-computed fog wisp paths
interface FogWisp {
  cx: number; cy: number; // center offset from world edge
  rx: number; ry: number; // ellipse radii
  speed: number;
  phase: number;
  opacity: number;
  width: number;
}
const FOG_WISPS: FogWisp[] = [];
// BETA 13.70 FPS: halved from 24. Each wisp does a per-frame createRadialGradient — too expensive.
const FOG_WISP_COUNT = 12;
for (let i = 0; i < FOG_WISP_COUNT; i++) {
  FOG_WISPS.push({
    cx: (Math.random() - 0.5) * 8000,
    cy: -100 - Math.random() * 600,
    rx: 200 + Math.random() * 600,
    ry: 40 + Math.random() * 120,
    speed: 0.15 + Math.random() * 0.4,
    phase: Math.random() * Math.PI * 2,
    opacity: 0.03 + Math.random() * 0.06,
    width: 60 + Math.random() * 200,
  });
}

function renderOutsideTexture(
  ctx: CanvasRenderingContext2D, state: GameState,
  viewLeft: number, viewTop: number, viewRight: number, viewBottom: number
) {
  const WS = state.map.WORLD_SIZE;
  // BETA 13.70 FPS: early-exit when camera is fully inside the world. Outside is only
  // visible at borders. Saves up to ~12 createRadialGradient/frame the rest of the time.
  const BORDER_THRESHOLD = 200;
  const nearBorder = viewLeft < BORDER_THRESHOLD || viewRight > WS - BORDER_THRESHOLD ||
                     viewTop  < BORDER_THRESHOLD || viewBottom > WS - BORDER_THRESHOLD;
  if (!nearBorder) return;
  const ext = OUTSIDE_TEXTURE_EXTENT;
  const t = state.time;
  const fogDepth = 500; // how far the fog gradient extends from the wall edge

  // ── 1. Base dark void fill for all outside areas ──
  ctx.fillStyle = '#030306';
  // Top strip
  if (viewTop < 0) {
    ctx.fillRect(
      Math.max(-ext, viewLeft), Math.max(-ext, viewTop),
      Math.min(WS + ext, viewRight) - Math.max(-ext, viewLeft),
      0 - Math.max(-ext, viewTop)
    );
  }
  // Bottom strip
  if (viewBottom > WS) {
    ctx.fillRect(
      Math.max(-ext, viewLeft), WS,
      Math.min(WS + ext, viewRight) - Math.max(-ext, viewLeft),
      Math.min(ext, viewBottom - WS)
    );
  }
  // Left strip
  if (viewLeft < 0) {
    ctx.fillRect(
      Math.max(-ext, viewLeft), Math.max(0, viewTop),
      0 - Math.max(-ext, viewLeft),
      Math.min(WS, viewBottom) - Math.max(0, viewTop)
    );
  }
  // Right strip
  if (viewRight > WS) {
    ctx.fillRect(
      WS, Math.max(0, viewTop),
      Math.min(ext, viewRight - WS),
      Math.min(WS, viewBottom) - Math.max(0, viewTop)
    );
  }
  // Corner fills
  if (viewTop < 0 && viewLeft < 0)
    ctx.fillRect(Math.max(-ext, viewLeft), Math.max(-ext, viewTop), 0 - Math.max(-ext, viewLeft), 0 - Math.max(-ext, viewTop));
  if (viewTop < 0 && viewRight > WS)
    ctx.fillRect(WS, Math.max(-ext, viewTop), Math.min(ext, viewRight - WS), 0 - Math.max(-ext, viewTop));
  if (viewBottom > WS && viewLeft < 0)
    ctx.fillRect(Math.max(-ext, viewLeft), WS, 0 - Math.max(-ext, viewLeft), Math.min(ext, viewBottom - WS));
  if (viewBottom > WS && viewRight > WS)
    ctx.fillRect(WS, WS, Math.min(ext, viewRight - WS), Math.min(ext, viewBottom - WS));

  // ── 2. Fog gradients near each wall edge ──
  // These create a subtle atmospheric glow where the void meets the wall
  const drawFogGradient = (
    x: number, y: number, w: number, h: number,
    side: 'top' | 'bottom' | 'left' | 'right'
  ) => {
    let grad: CanvasGradient;
    const d = Math.min(fogDepth, side === 'top' || side === 'bottom' ? h : w);
    if (side === 'top') {
      grad = ctx.createLinearGradient(x, y + h - d, x, y + h);
    } else if (side === 'bottom') {
      grad = ctx.createLinearGradient(x, y + d, x, y);
    } else if (side === 'left') {
      grad = ctx.createLinearGradient(x + w - d, y, x + w, y);
    } else {
      grad = ctx.createLinearGradient(x + d, y, x, y);
    }
    // Warm industrial fog tint
    grad.addColorStop(0, 'rgba(8, 10, 15, 0)');
    grad.addColorStop(0.3, 'rgba(15, 18, 25, 0.15)');
    grad.addColorStop(0.6, 'rgba(25, 22, 18, 0.12)');
    grad.addColorStop(0.85, 'rgba(40, 35, 28, 0.08)');
    grad.addColorStop(1, 'rgba(50, 42, 30, 0.04)');
    ctx.fillStyle = grad;
    ctx.fillRect(x, y, w, h);
  };

  if (viewTop < 0) {
    drawFogGradient(
      Math.max(-ext, viewLeft), Math.max(-ext, viewTop),
      Math.min(WS + ext, viewRight) - Math.max(-ext, viewLeft),
      0 - Math.max(-ext, viewTop), 'top'
    );
  }
  if (viewBottom > WS) {
    drawFogGradient(
      Math.max(-ext, viewLeft), WS,
      Math.min(WS + ext, viewRight) - Math.max(-ext, viewLeft),
      Math.min(ext, viewBottom - WS), 'bottom'
    );
  }
  if (viewLeft < 0) {
    drawFogGradient(
      Math.max(-ext, viewLeft), Math.max(0, viewTop),
      0 - Math.max(-ext, viewLeft),
      Math.min(WS, viewBottom) - Math.max(0, viewTop), 'left'
    );
  }
  if (viewRight > WS) {
    drawFogGradient(
      WS, Math.max(0, viewTop),
      Math.min(ext, viewRight - WS),
      Math.min(WS, viewBottom) - Math.max(0, viewTop), 'right'
    );
  }

  // ── 3. Animated fog wisps (elliptical blobs drifting near the walls) ──
  ctx.save();
  for (const wisp of FOG_WISPS) {
    const angle = wisp.phase + t * wisp.speed;
    // Wisps drift near all 4 edges — position them relative to the world center
    const wcx = WS / 2 + wisp.cx;
    const wcy = WS / 2 + wisp.cy;
    // Create 4 instances — one near each wall
    const positions = [
      { x: wcx + Math.cos(angle) * wisp.rx, y: Math.sin(angle) * wisp.ry - 200 },                    // near north
      { x: wcx + Math.cos(angle + 1) * wisp.rx, y: WS - Math.sin(angle + 1) * wisp.ry + 200 },      // near south
      { x: Math.sin(angle + 2) * wisp.ry - 200, y: wcy + Math.cos(angle + 2) * wisp.rx },            // near west
      { x: WS - Math.sin(angle + 3) * wisp.ry + 200, y: wcy + Math.cos(angle + 3) * wisp.rx },      // near east
    ];
    for (const pos of positions) {
      if (pos.x + wisp.width < viewLeft || pos.x - wisp.width > viewRight ||
          pos.y + wisp.width < viewTop || pos.y - wisp.width > viewBottom) continue;
      // Only draw if actually outside the world
      if (pos.x > 0 && pos.x < WS && pos.y > 0 && pos.y < WS) continue;

      const pulseFactor = 0.7 + 0.3 * Math.sin(t * 0.8 + wisp.phase);
      const grad = ctx.createRadialGradient(
        pos.x, pos.y, 0,
        pos.x, pos.y, wisp.width * pulseFactor
      );
      grad.addColorStop(0, `rgba(30, 28, 22, ${wisp.opacity * pulseFactor})`);
      grad.addColorStop(0.5, `rgba(20, 18, 15, ${wisp.opacity * 0.5 * pulseFactor})`);
      grad.addColorStop(1, 'rgba(10, 10, 10, 0)');
      ctx.fillStyle = grad;
      ctx.fillRect(
        pos.x - wisp.width * pulseFactor,
        pos.y - wisp.width * pulseFactor,
        wisp.width * 2 * pulseFactor,
        wisp.width * 2 * pulseFactor
      );
    }
  }
  ctx.restore();

  // ── 4. Floating dust / ember particles ──
  ctx.save();
  for (const dust of VOID_DUST) {
    // Animate position with slow drift
    const dx = Math.cos(dust.angle) * dust.speed * t;
    const dy = Math.sin(dust.angle) * dust.speed * t;
    // Wrap around a large area
    const range = 5000;
    let px = WS / 2 + ((dust.x + dx) % range + range) % range - range / 2;
    let py = WS / 2 + ((dust.y + dy) % range + range) % range - range / 2;
    // Shift to be outside the world bounds
    // Place particles in the void area (outside 0..WS)
    const side = Math.floor((dust.phase * 4) / (Math.PI * 2)) % 4;
    if (side === 0) py = -50 - Math.abs(py % 800);        // north void
    else if (side === 1) py = WS + 50 + Math.abs(py % 800); // south void
    else if (side === 2) px = -50 - Math.abs(px % 800);     // west void
    else px = WS + 50 + Math.abs(px % 800);                 // east void

    if (px < viewLeft - 50 || px > viewRight + 50 ||
        py < viewTop - 50 || py > viewBottom + 50) continue;

    // Twinkle effect
    const twinkle = 0.3 + 0.7 * Math.abs(Math.sin(t * 0.5 + dust.phase));
    const alpha = dust.brightness * twinkle;
    // Warm ember color
    const r = 180 + Math.floor(dust.brightness * 60);
    const g = 140 + Math.floor(dust.brightness * 40);
    const b = 80 + Math.floor(dust.brightness * 30);
    ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(2)})`;
    ctx.beginPath();
    ctx.arc(px, py, dust.size * twinkle, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();

  // ── 5. Subtle vignette darkening at the far edges ──
  // Makes the void feel like it fades into infinite darkness
  const vignetteDepth = 1500;
  const drawVignette = (x: number, y: number, w: number, h: number, side: 'top' | 'bottom' | 'left' | 'right') => {
    if (w <= 0 || h <= 0) return;
    const d = Math.min(vignetteDepth, side === 'top' || side === 'bottom' ? h : w);
    let grad: CanvasGradient;
    if (side === 'top') {
      grad = ctx.createLinearGradient(x, y + h, x, y + h - d);
    } else if (side === 'bottom') {
      grad = ctx.createLinearGradient(x, y, x, y + d);
    } else if (side === 'left') {
      grad = ctx.createLinearGradient(x + w, y, x + w - d, y);
    } else {
      grad = ctx.createLinearGradient(x, y, x + d, y);
    }
    grad.addColorStop(0, 'rgba(0, 0, 0, 0)');
    grad.addColorStop(1, 'rgba(0, 0, 0, 0.6)');
    ctx.fillStyle = grad;
    ctx.fillRect(x, y, w, h);
  };
  if (viewTop < -200) {
    drawVignette(
      Math.max(-ext, viewLeft), Math.max(-ext, viewTop),
      Math.min(WS + ext, viewRight) - Math.max(-ext, viewLeft),
      0 - Math.max(-ext, viewTop), 'top'
    );
  }
  if (viewBottom > WS + 200) {
    drawVignette(
      Math.max(-ext, viewLeft), WS,
      Math.min(WS + ext, viewRight) - Math.max(-ext, viewLeft),
      Math.min(ext, viewBottom - WS), 'bottom'
    );
  }
  if (viewLeft < -200) {
    drawVignette(
      Math.max(-ext, viewLeft), Math.max(0, viewTop),
      0 - Math.max(-ext, viewLeft),
      Math.min(WS, viewBottom) - Math.max(0, viewTop), 'left'
    );
  }
  if (viewRight > WS + 200) {
    drawVignette(
      WS, Math.max(0, viewTop),
      Math.min(ext, viewRight - WS),
      Math.min(WS, viewBottom) - Math.max(0, viewTop), 'right'
    );
  }
}

// ─── Border Walls ──────────────────────────────────────────────────────────

function renderBorderWalls(
  ctx: CanvasRenderingContext2D, state: GameState,
  viewLeft: number, viewTop: number, viewRight: number, viewBottom: number
) {
  const WS = state.map.WORLD_SIZE;
  const CS = BORDER_CORNER_SIZE;   // 400wu — corner square size
  const WD = BORDER_WALL_DEPTH;    // 400wu — wall strip depth (same as corner)
  const TL = BORDER_WALL_TILE_LENGTH; // ~717wu — length of one wall tile

  // Helper: draw a cached image tile at world coordinates
  const drawTile = (url: string, x: number, y: number, w: number, h: number) => {
    const img = imageCache.get(url);
    if (img) {
      ctx.drawImage(img, x, y, w, h);
    }
  };

  // ══════════════════════════════════════════════════════════════════════
  // 4 CORNERS (square tiles, one at each corner of the world)
  // ══════════════════════════════════════════════════════════════════════
  // Top-left corner
  if (viewLeft < CS && viewTop < CS) {
    drawTile(BORDER_WALL_ASSETS.cornerTL, 0, 0, CS, CS);
  }
  // Top-right corner
  if (viewRight > WS - CS && viewTop < CS) {
    drawTile(BORDER_WALL_ASSETS.cornerTR, WS - CS, 0, CS, CS);
  }
  // Bottom-left corner
  if (viewLeft < CS && viewBottom > WS - CS) {
    drawTile(BORDER_WALL_ASSETS.cornerBL, 0, WS - CS, CS, CS);
  }
  // Bottom-right corner
  if (viewRight > WS - CS && viewBottom > WS - CS) {
    drawTile(BORDER_WALL_ASSETS.cornerBR, WS - CS, WS - CS, CS, CS);
  }

  // ══════════════════════════════════════════════════════════════════════
  // NORTH WALL — tiles between TL and TR corners
  // ══════════════════════════════════════════════════════════════════════
  if (viewTop < WD) {
    const wallStart = CS;           // starts after TL corner
    const wallEnd = WS - CS;        // ends before TR corner
    const totalLength = wallEnd - wallStart;
    const tileCount = Math.ceil(totalLength / TL);
    for (let i = 0; i < tileCount; i++) {
      const tx = wallStart + i * TL;
      const tw = Math.min(TL, wallEnd - tx);
      if (tx + tw > viewLeft && tx < viewRight) {
        drawTile(BORDER_WALL_ASSETS.top, tx, 0, tw, WD);
      }
    }
  }

  // ══════════════════════════════════════════════════════════════════════
  // SOUTH WALL — tiles between BL and BR corners
  // ══════════════════════════════════════════════════════════════════════
  if (viewBottom > WS - WD) {
    const wallStart = CS;
    const wallEnd = WS - CS;
    const totalLength = wallEnd - wallStart;
    const tileCount = Math.ceil(totalLength / TL);
    for (let i = 0; i < tileCount; i++) {
      const tx = wallStart + i * TL;
      const tw = Math.min(TL, wallEnd - tx);
      if (tx + tw > viewLeft && tx < viewRight) {
        drawTile(BORDER_WALL_ASSETS.bottom, tx, WS - WD, tw, WD);
      }
    }
  }

  // ══════════════════════════════════════════════════════════════════════
  // WEST WALL — tiles between TL and BL corners
  // ══════════════════════════════════════════════════════════════════════
  if (viewLeft < WD) {
    const wallStart = CS;
    const wallEnd = WS - CS;
    const totalLength = wallEnd - wallStart;
    const tileCount = Math.ceil(totalLength / TL);
    for (let i = 0; i < tileCount; i++) {
      const ty = wallStart + i * TL;
      const th = Math.min(TL, wallEnd - ty);
      if (ty + th > viewTop && ty < viewBottom) {
        drawTile(BORDER_WALL_ASSETS.left, 0, ty, WD, th);
      }
    }
  }

  // ══════════════════════════════════════════════════════════════════════
  // EAST WALL — tiles between TR and BR corners
  // ══════════════════════════════════════════════════════════════════════
  if (viewRight > WS - WD) {
    const wallStart = CS;
    const wallEnd = WS - CS;
    const totalLength = wallEnd - wallStart;
    const tileCount = Math.ceil(totalLength / TL);
    for (let i = 0; i < tileCount; i++) {
      const ty = wallStart + i * TL;
      const th = Math.min(TL, wallEnd - ty);
      if (ty + th > viewTop && ty < viewBottom) {
        drawTile(BORDER_WALL_ASSETS.right, WS - WD, ty, WD, th);
      }
    }
  }
}

// ─── Low-Health Vignette ─────────────────────────────────────────────────────────

/**
 * CoD-style red edge vignette that pulses when the player is low on health.
 * Kicks in below 35% HP. Gets more intense and pulses faster toward 0%.
 * Uses a double-beat heartbeat rhythm (main beat + faint echo) to feel urgent.
 */
function renderLowHealthVignette(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  w: number,
  h: number,
): void {
  // BETA 17.96 — VFX toggle.
  if (!isVfxEnabled()) return;
  if (state.phase !== 'playing') return;

  const { player } = state;
  const hpPct = player.maxHp > 0 ? player.hp / player.maxHp : 1;

  // No effect when healthy
  if (hpPct >= 0.35) return;

  // dangerT: 0 at 35% HP → 1 at 5% HP (fully critical below 5%)
  const dangerT = Math.max(0, Math.min(1, (0.35 - hpPct) / 0.30));

  // Pulse speed ramps from 2.5 rad/s (early warning) → 8 rad/s (nearly dead)
  const pulseSpeed = 2.5 + dangerT * 5.5;

  // Double-beat heartbeat: strong beat + faint echo half-period later
  const beat  = Math.sin(state.time * pulseSpeed);
  const echo  = Math.sin(state.time * pulseSpeed * 2) * 0.22;
  // Remap -1..1 to 0..1
  const pulse = Math.max(0, (beat + echo + 1.22) / 2.44);

  // Vignette edge opacity: 0.25 at first warning → 0.88 when nearly dead
  const maxAlpha = 0.25 + dangerT * 0.63;
  const edgeAlpha = pulse * maxAlpha;

  // Inner radius starts large (just a faint rim) and shrinks inward at low HP
  const cx   = w / 2;
  const cy   = h / 2;
  const diag = Math.sqrt(cx * cx + cy * cy);
  const innerR = diag * (0.55 - dangerT * 0.25); // shrinks from 55%→30% of diag

  // Radial gradient: transparent center → dark crimson edges
  const grad = ctx.createRadialGradient(cx, cy, innerR, cx, cy, diag * 1.1);
  grad.addColorStop(0,   'rgba(160, 0, 0, 0)');
  grad.addColorStop(0.5, `rgba(170, 0, 0, ${edgeAlpha * 0.35})`);
  grad.addColorStop(1,   `rgba(200, 0, 0, ${edgeAlpha})`);

  ctx.save();
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  // At critical HP (< 15%) add a thin bright-red border line right at the edge
  if (hpPct < 0.15) {
    const rimAlpha = pulse * 0.7 * ((0.15 - hpPct) / 0.15);
    ctx.strokeStyle = `rgba(255, 30, 30, ${rimAlpha})`;
    ctx.lineWidth = 4 + dangerT * 8;
    ctx.strokeRect(0, 0, w, h);
  }

  ctx.restore();
}

// BETA 21.96 — GREEN GAS room indicator. A green haze creeps in from the screen EDGES as the
// room's gas timer climbs (faint while it's filling, then thicker + drifting once it bites),
// so you can read "get out of this room" without an obnoxious full-screen fog. Driven by
// state._gasLevel (0..1) set in the gas update.
function renderGasVignette(ctx: CanvasRenderingContext2D, state: GameState, w: number, h: number): void {
  if (!isVfxEnabled() || state.phase !== 'playing') return;
  const level = (state as unknown as { _gasLevel?: number })._gasLevel ?? 0;
  if (level <= 0.01) return;
  const cx = w / 2, cy = h / 2, diag = Math.sqrt(cx * cx + cy * cy);
  // gentle drift so the haze looks alive once it's active
  const drift = level >= 0.5 ? (0.85 + 0.15 * Math.sin(state.time * 2.2)) : 1;
  const edge = Math.min(0.5, level * 0.5) * drift;          // edge opacity caps ~0.5
  const innerR = diag * (0.62 - level * 0.22);              // haze creeps inward as it builds
  const grad = ctx.createRadialGradient(cx, cy, innerR, cx, cy, diag * 1.1);
  grad.addColorStop(0, 'rgba(120, 220, 70, 0)');
  grad.addColorStop(0.55, `rgba(120, 210, 60, ${edge * 0.4})`);
  grad.addColorStop(1, `rgba(95, 190, 45, ${edge})`);
  ctx.save();
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);
  // once the gas is actually biting, a soft green rim + a small "☣ GAS" tag bottom-center.
  if (level >= 0.45) {
    ctx.strokeStyle = `rgba(150, 240, 90, ${0.5 * level})`;
    ctx.lineWidth = 5;
    ctx.strokeRect(0, 0, w, h);
    ctx.globalAlpha = Math.min(1, level);
    ctx.font = 'bold 18px "Share Tech Mono", monospace';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#aaff66';
    ctx.fillText('☣ GAS — MOVE!', w / 2, h - 28);
  }
  ctx.restore();
}

// ─── HUD ────────────────────────────────────────────────────────────────────────

/**
 * Full-width HP / shield / armor strip pinned to the very top of the screen.
 * Returns the total pixel height consumed so callers can push other HUD elements below it.
 */
function renderTopHealthStrip(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  w: number,
): number {
  const { player } = state;
  const HP_H     = 22;  // slimmer bar
  // BETA 21.03 — shield strip grown 5 → 15 so the blue bar + its text are
  // actually readable on the handheld (was a 6px label in a 5px strip =
  // invisible). totalH is returned + propagated to the rest of the HUD via
  // state._hudTopStripH, so chat/etc. auto-reflow down — no manual offsets.
  const SHIELD_H = player.maxShield > 0 ? 15 : 0;
  // BETA — Armor gets its OWN labeled blue bar under HP (Dylan: "armor is the
  // blue bar... its hard to tell how much armor i have"). Shown only when > 0.
  const ARMOR_H  = player.armor > 0 ? 15 : 0;
  const totalH   = HP_H + ARMOR_H + SHIELD_H;

  // BETA 16.25 — PSBOT cycle-26 P0.2: during death_replay phase, render
  // the bar draining from pre-fatal-hit HP → 0 over the first 1.0s so
  // Dylan can visibly SEE the death happen. Without this the bar shows
  // 0 instantly because the lethal damage already applied. Per Dylan:
  // "it showed i was full health... so why did i die?"
  let displayHp = player.hp;
  if (state.phase === 'death_replay') {
    const sAny = state as unknown as {
      _deathReplayHpStart?: number;
      _deathReplayElapsedSec?: number;
    };
    const startHp = sAny._deathReplayHpStart ?? player.hp;
    const elapsed = sAny._deathReplayElapsedSec ?? 0;
    const drainT = Math.min(1, elapsed / 1.0); // 1-second drain
    displayHp = startHp * (1 - drainT);
  }

  // HP color: green > 50%, orange 25–50%, red < 25%
  const hpPct   = Math.max(0, Math.min(1, player.maxHp > 0 ? displayHp / player.maxHp : 1));
  const hpColor = hpPct > 0.50 ? '#39ff14' : hpPct > 0.25 ? '#ffaa00' : '#ff2222';
  const hpGlow  = hpPct > 0.50 ? 'rgba(57,255,20,0.5)' : hpPct > 0.25 ? 'rgba(255,170,0,0.5)' : 'rgba(255,40,40,0.7)';

  // Strip background — full width but slim
  ctx.fillStyle = 'rgba(5,5,10,0.92)';
  ctx.fillRect(0, 0, w, HP_H);
  ctx.strokeStyle = hpColor;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, HP_H);
  ctx.lineTo(w, HP_H);
  ctx.stroke();

  // Level box — left side
  const LV_W = 68;
  ctx.fillStyle = 'rgba(0,0,0,0.45)';
  ctx.fillRect(0, 0, LV_W, HP_H);
  ctx.strokeStyle = hpColor;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(LV_W, 0);
  ctx.lineTo(LV_W, HP_H);
  ctx.stroke();
  ctx.font = 'bold 13px "Share Tech Mono", monospace';
  ctx.fillStyle = hpColor;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // BETA 13.88 FPS: removed ctx.shadowBlur=6 (was firing every frame on the HUD)
  ctx.fillText(`LV ${player.level}`, LV_W / 2, HP_H / 2);

  // BETA — Armor moved OUT of the HP bar into its own blue bar below (see the
  // Armor strip further down). HP bar now reclaims the full width (ARM_W = 0).
  const ARM_W = 0;

  // HP bar fill area
  const barPad = 5;
  const barX = LV_W + barPad;
  const barW = w - LV_W - ARM_W - barPad * 2;
  const barY = barPad;
  const barH = HP_H - barPad * 2;

  // Dark track
  ctx.fillStyle = 'rgba(15,15,25,0.85)';
  roundRect(ctx, barX, barY, barW, barH, 3);
  ctx.fill();

  // Colored fill — gradient per health zone
  if (hpPct > 0) {
    const fillW = barW * hpPct;
    const grad = ctx.createLinearGradient(barX, 0, barX + fillW, 0);
    if (hpPct > 0.50) {
      grad.addColorStop(0, '#22aa00');
      grad.addColorStop(1, '#39ff14');
    } else if (hpPct > 0.25) {
      grad.addColorStop(0, '#aa6600');
      grad.addColorStop(1, '#ffaa00');
    } else {
      grad.addColorStop(0, '#aa0000');
      grad.addColorStop(1, '#ff2222');
    }
    ctx.fillStyle = grad;
    roundRect(ctx, barX, barY, fillW, barH, 3);
    ctx.fill();
    // Shine
    ctx.fillStyle = 'rgba(255,255,255,0.10)';
    roundRect(ctx, barX, barY, fillW, barH * 0.45, 3);
    ctx.fill();
  }

  // Bar border
  ctx.strokeStyle = hpColor;
  ctx.lineWidth = 1;
  roundRect(ctx, barX, barY, barW, barH, 3);
  ctx.stroke();

  // HP text — centered in bar area. BETA 16.25 — uses displayHp during
  // death_replay so the number visibly counts down from pre-fatal HP → 0.
  const pctNum  = Math.round(hpPct * 100);
  const hpLabel = `HP ${Math.ceil(displayHp)} / ${player.maxHp}   ${pctNum}%`;
  ctx.font = 'bold 12px "Share Tech Mono", monospace';
  ctx.fillStyle = hpPct < 0.25 ? 'rgba(255,210,210,0.95)' : 'rgba(255,255,255,0.88)';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(hpLabel, barX + barW / 2, HP_H / 2 + 1);

  // Last Stand indicator — golden star on the right edge of the HP bar
  if (state.lastStandAvailable) {
    ctx.font = 'bold 13px "Share Tech Mono", monospace';
    ctx.textAlign = 'right';
    ctx.fillStyle = '#ffd700';
    // BETA 13.88 FPS: removed ctx.shadowBlur=6 from LAST STAND HUD label
    ctx.fillText('\u2605 LAST STAND', barX + barW - 4, HP_H / 2 + 1);
  }

  // Armor strip — blue bar directly below the HP bar (Dylan: "armor is the blue
  // bar"). Armor is flat damage reduction with no hard max, so the fill is
  // scaled against a 100-armor reference; the NUMBER is the source of truth.
  if (player.armor > 0) {
    const ay = HP_H;
    const arPct = Math.max(0, Math.min(1, player.armor / 100));
    ctx.fillStyle = 'rgba(5,8,22,0.88)';
    ctx.fillRect(0, ay, w, ARMOR_H);
    if (arPct > 0) {
      const ag = ctx.createLinearGradient(0, ay, Math.max(w * arPct, 2), ay);
      ag.addColorStop(0, '#1a3acc');
      ag.addColorStop(1, '#3a78ff');
      ctx.fillStyle = ag;
      ctx.fillRect(0, ay, Math.max(w * arPct, 2), ARMOR_H);
    }
    ctx.strokeStyle = 'rgba(80,140,255,0.5)';
    ctx.lineWidth = 0.5;
    ctx.strokeRect(0, ay, w, ARMOR_H);
    ctx.font = 'bold 12px "Share Tech Mono", monospace';
    ctx.fillStyle = 'rgba(214,228,255,0.98)';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(0,0,0,0.85)';
    ctx.shadowBlur = 2;
    ctx.fillText(`🛡 ARMOR  ${Math.round(player.armor)}`, LV_W + barPad, ay + ARMOR_H / 2 + 0.5);
    ctx.shadowBlur = 0;
  }

  // Shield strip — thin bar just below the armor strip
  if (player.maxShield > 0) {
    const sy    = HP_H + ARMOR_H;
    const shPct = Math.max(0, Math.min(1, player.shield / player.maxShield));

    ctx.fillStyle = 'rgba(5,8,22,0.88)';
    ctx.fillRect(0, sy, w, SHIELD_H);

    if (shPct > 0) {
      const sg = ctx.createLinearGradient(0, sy, w * shPct, sy);
      sg.addColorStop(0, '#1155cc');
      sg.addColorStop(1, '#44aaff');
      ctx.fillStyle = sg;
      ctx.fillRect(0, sy, w * shPct, SHIELD_H);
    }

    ctx.strokeStyle = 'rgba(68,170,255,0.5)';
    ctx.lineWidth = 0.5;
    ctx.strokeRect(0, sy, w, SHIELD_H);

    // BETA 21.03 — readable: 6px → 12px, brighter ice-blue with a dark shadow
    // for contrast over the blue fill.
    ctx.font = 'bold 12px "Share Tech Mono", monospace';
    ctx.fillStyle = 'rgba(224,242,255,0.98)';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(0,0,0,0.85)';
    ctx.shadowBlur = 2;
    ctx.fillText(`◈ SHIELD  ${Math.ceil(player.shield)} / ${player.maxShield}`, LV_W + barPad, sy + SHIELD_H / 2 + 0.5);
    ctx.shadowBlur = 0;
  }

  return totalH;
}

// BETA 16.38 — Active-item tray (bottom-left). Renders a 76px box per
// Q-active item in the player's inventory with cooldown sweep, key
// indicator, hover tooltip, pick animation, and activation flash.
//
// Cooldown source-of-truth lives on engine.ts module timers
// (_voogooHealCd / _whoopieDropTimer / _bananaDropTimer); this renderer
// reads them directly via the per-item helper.
function _activeItemCooldownRatio(itemId: string, atkSpeedMult: number): { remaining: number; max: number } {
  // Returns the remaining + max cooldown in seconds. ratio = remaining/max
  // (0 = ready, 1 = just activated). Falls back to ready if itemId unknown.
  switch (itemId) {
    case 'item_voogoo_rager':
      return { remaining: _voogooHealCdDisplay, max: 45.0 };
    case 'item_whoopie_cushion_of_doom':
      return { remaining: _whoopieDropTimer, max: Math.max(0.001, 6.0 / atkSpeedMult) };
    case 'item_asmons_desk_banana':
      return { remaining: _bananaDropTimer, max: Math.max(0.001, 8.0 / atkSpeedMult) };
    default:
      return { remaining: 0, max: 1 };
  }
}

function renderActiveItemTray(ctx: CanvasRenderingContext2D, state: GameState, _w: number, h: number): void {
  const slots = getActiveItemSlots();
  if (slots.length === 0) return;
  const inv = getItemState()?.inventory ?? [];

  // Mouse position — only check hover when phase is 'playing' so tooltips
  // don't shadow chest/level-up cards which use the same screen real estate.
  const showHoverTip = state.phase === 'playing';
  const mx = hudMouseX;
  const my = hudMouseY;

  const atkSpeedMult = 1 + (state.player.attackSpeedBonus ?? 0) / 100;

  ctx.save();
  let hoverIdx = -1;
  let hoverInv: typeof inv[number] | null = null;

  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i];
    const invSlot = inv.find(s => s.item.id === slot.itemId);
    if (!invSlot) continue;
    const def = invSlot.item;
    const bbox = getTraySlotBbox(i, h);
    const isHovered = showHoverTip && mx >= bbox.x && mx < bbox.x + bbox.w && my >= bbox.y && my < bbox.y + bbox.h;
    if (isHovered) { hoverIdx = i; hoverInv = invSlot; }

    // Cooldown ratio (1 = just activated, 0 = ready)
    const cdInfo = _activeItemCooldownRatio(slot.itemId, atkSpeedMult);
    const cdRatio = Math.max(0, Math.min(1, cdInfo.remaining / cdInfo.max));
    const ready = cdInfo.remaining <= 0.001;

    // Pick + activate animation values
    const pickT = slot.pickAnimT;        // 1 → 0 over 1.2s
    const flashT = slot.activateFlashT;  // 1 → 0 over 0.6s

    // Pick spin: 0..2π × pickT (one full rotation as it lands)
    const pickRot = pickT * Math.PI * 2;
    // Pick scale: 1.6 → 1.0 (zooms down into slot)
    const pickScale = 1.0 + 0.6 * pickT;
    // Activation shake: small jitter while flashT > 0
    const shakeX = flashT > 0 ? (Math.random() - 0.5) * 6 * flashT : 0;
    const shakeY = flashT > 0 ? (Math.random() - 0.5) * 6 * flashT : 0;

    const cx = bbox.x + bbox.w / 2 + shakeX;
    const cy = bbox.y + bbox.h / 2 + shakeY;

    // Get rarity color
    const itemColor = (def.rarity === 'common'    ? '#7ddf3a'
                    : def.rarity === 'uncommon'  ? '#3adfd0'
                    : def.rarity === 'rare'      ? '#b07ddf'
                    : def.rarity === 'epic'      ? '#df7d7d'
                    : def.rarity === 'legendary' ? '#dfcf7d'
                    : '#7ddf3a');

    // Box background
    ctx.fillStyle = ready ? 'rgba(10,10,15,0.92)' : 'rgba(10,10,15,0.78)';
    roundRect(ctx, bbox.x, bbox.y, bbox.w, bbox.h, 8);
    ctx.fill();

    // Border — bright when ready, dim when on cooldown, glowing when flashing
    const borderAlpha = ready ? 1.0 : 0.55;
    ctx.strokeStyle = itemColor;
    ctx.globalAlpha = borderAlpha;
    ctx.lineWidth = ready ? 2.5 : 1.5;
    ctx.shadowColor = itemColor;
    ctx.shadowBlur = ready ? 10 : 4;
    if (flashT > 0) ctx.shadowBlur = 10 + 18 * flashT;
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.globalAlpha = 1.0;

    // Icon — with pick spin/scale + activation shake/dissolve
    const img = getCachedImage(def.iconUrl);
    if (img) {
      ctx.save();
      ctx.translate(cx, cy);
      if (pickT > 0) {
        ctx.rotate(pickRot);
        ctx.scale(pickScale, pickScale);
      }
      // Dissolve effect on activation: fade slightly + pop
      if (flashT > 0) {
        ctx.globalAlpha = 1.0 - 0.35 * flashT;
        const popScale = 1.0 + 0.35 * flashT;
        ctx.scale(popScale, popScale);
      }
      // Cooldown desaturates
      if (!ready) ctx.globalAlpha *= 0.78;
      const iconSize = 56;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(img, -iconSize / 2, -iconSize / 2, iconSize, iconSize);
      ctx.imageSmoothingEnabled = true;
      ctx.restore();
    }

    // Cooldown sweep overlay (radial wipe from 12 o'clock clockwise)
    if (!ready && cdRatio > 0) {
      ctx.save();
      ctx.fillStyle = 'rgba(0,0,0,0.58)';
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, bbox.w * 0.72, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * cdRatio, false);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    // Cooldown timer text (ready / Xs)
    ctx.font = 'bold 11px "Share Tech Mono", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    if (ready) {
      ctx.fillStyle = itemColor;
      ctx.shadowColor = itemColor;
      ctx.shadowBlur = 6;
      ctx.fillText('READY', cx, bbox.y + bbox.h - 9);
      ctx.shadowBlur = 0;
    } else {
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.fillText(`${cdInfo.remaining.toFixed(1)}s`, cx, bbox.y + bbox.h - 9);
    }

    // Q key indicator — VooGoo only (Banana + Whoopie auto-drop)
    if (slot.itemId === 'item_voogoo_rager') {
      ctx.font = 'bold 10px "Share Tech Mono", monospace';
      ctx.fillStyle = ready ? '#ffd700' : 'rgba(180,180,180,0.6)';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillText('Q', bbox.x + 4, bbox.y + 4);
    } else {
      // Auto-drop badge
      ctx.font = 'bold 8px "Share Tech Mono", monospace';
      ctx.fillStyle = 'rgba(140,180,255,0.7)';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillText('AUTO', bbox.x + 4, bbox.y + 4);
    }
  }

  // Hover tooltip — render last so it overlays neighbors
  if (hoverInv && hoverIdx >= 0) {
    const def = hoverInv.item;
    const bbox = getTraySlotBbox(hoverIdx, h);
    const tipX = bbox.x;
    const tipY = bbox.y - 14;
    ctx.font = 'bold 13px "Share Tech Mono", monospace';
    const desc = def.description;
    // Word-wrap to ~280px
    const tipMaxW = 280;
    const words = desc.split(' ');
    const lines: string[] = [];
    let line = '';
    for (const w of words) {
      const test = line + (line ? ' ' : '') + w;
      if (ctx.measureText(test).width > tipMaxW && line) {
        lines.push(line);
        line = w;
      } else { line = test; }
    }
    if (line) lines.push(line);
    const lineH = 16;
    const tipH = 22 + lines.length * lineH + 6;
    const tipW = tipMaxW + 14;

    ctx.save();
    ctx.fillStyle = 'rgba(8,8,12,0.95)';
    ctx.strokeStyle = '#7ddf3a';
    ctx.lineWidth = 1.5;
    roundRect(ctx, tipX, tipY - tipH, tipW, tipH, 6);
    ctx.fill();
    ctx.shadowColor = '#7ddf3a';
    ctx.shadowBlur = 6;
    ctx.stroke();
    ctx.shadowBlur = 0;

    // Item name
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.font = 'bold 13px "Share Tech Mono", monospace';
    ctx.fillText(def.name, tipX + 8, tipY - tipH + 6);
    // Description lines
    ctx.font = '12px "Share Tech Mono", monospace';
    ctx.fillStyle = 'rgba(220,220,220,0.92)';
    for (let li = 0; li < lines.length; li++) {
      ctx.fillText(lines[li], tipX + 8, tipY - tipH + 24 + li * lineH);
    }
    ctx.restore();
  }

  ctx.restore();
}

function renderHUD(ctx: CanvasRenderingContext2D, state: GameState, w: number, h: number) {
  const { player } = state;
  const { roomMap } = state.map;
  const padH = 16;

  // BETA 14.39 — Retreat sim FOV box. White rectangle outline showing
  // what WOULD be visible at the default zoom (1.2). When the sim zoom-
  // out slider has been dragged, this rect appears smaller than the
  // canvas — anything outside the rect is "off-screen" from the player's
  // normal POV. User: "there should be a rectangle white outline only
  // showing the usual players POV."
  if (isLabMode() && getRetreatSimShowFovBox()) {
    const DEFAULT_ZOOM = 1.2;
    const ratio = state.camera.zoom / DEFAULT_ZOOM;  // ≤1 when zoomed out
    const rectW = w * ratio;
    const rectH = h * ratio;
    const rectX = (w - rectW) / 2;
    const rectY = (h - rectH) / 2;
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.85)';
    ctx.lineWidth = 2;
    ctx.setLineDash([8, 4]);
    ctx.strokeRect(rectX, rectY, rectW, rectH);
    // Label
    ctx.setLineDash([]);
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.font = '11px monospace';
    ctx.fillText('NORMAL FOV', rectX + 4, rectY + 14);
    ctx.restore();
  }

  // Render top health strip first — returns the height it consumed
  const topStripH = renderTopHealthStrip(ctx, state, w);
  // BETA 19.18 — Stash measured strip height on state so the post-overlay
  // chat re-render pass (level-up / shrine / chest screens) can position
  // the chat panel at the SAME y as this main HUD render. Without this,
  // the post-overlay path used a hardcoded `topY = 60` that mismatched
  // the live measurement (which varies 22 / 27 depending on shield bar),
  // causing the chat to jump 17–22px down when an overlay opened.
  state._hudTopStripH = topStripH;
  const topY = topStripH + padH; // push all other top elements below the strip

  ctx.save();

  // BETA 22.49 — live-chat overlay + hype-train indicator removed; the left
  // stats panel now always uses its default top (no chat panel to dock under).
  state._hudChatBottomY = 0;

  // ── Top-center: HUD Row (LV, XP | TIMER | GOLD, NEXT CHEST) ──────────
  // BETA 15.41 — Per Dylan: "level you are, gold and xp to be on lefht and
  // right of the timer in the top middle. timer stays centered. same size
  // and style. xp is blue and gold is gold. and then next cchest price on
  // the right of the gold you actuallly have. we ahve a lot of this data
  // on screen but its hard to see. down on the botom left. but its small
  // and doesnt look cool"
  // Layout (centered as a single row, timer pinned to screen center):
  //   [ LV {N} ] [ XP {x}/{x} ] [ TIMER ] [ G {gold}g ] [ NEXT {price}g ]
  const ROUND_DURATION = 600; // 10 minutes
  const remaining = Math.max(0, ROUND_DURATION - state.time);
  const remMins = Math.floor(remaining / 60);
  const remSecs = Math.floor(remaining % 60);
  // BETA 15.45 — endless mode: when the 10:00 timer hits 0, flip the
  // display to show "ENDLESS x{N}" where N is the current enemy multiplier.
  // Doubles every 20s. By 10:20 = x2, 10:40 = x4, 11:00 = x8, etc.
  const _endlessActive = state.time >= ROUND_DURATION;
  // BETA 21.19 — GOD MODE (post-final-boss) overrides the timer with the live
  // escalation multiplier so you can watch the storm climbing.
  const _sGodHud = state as unknown as Record<string, number | undefined>;
  const _godActive = (_sGodHud._godMode ?? 0) > 0;
  let timerStr: string;
  let timerColor: string;
  if (_godActive) {
    // BETA 21.24 — timer is now a CLOCK + state label; the live enemy multiplier
    // lives in the AGGRO bar below (Dylan). Shows time-in-god-mode.
    const _gSec = Math.max(0, state.time - (_sGodHud._godModeStart ?? state.time));
    const _gm = Math.floor(_gSec / 60);
    const _gs = Math.floor(_gSec % 60);
    timerStr = `☠ GOD MODE ${_gm}:${_gs.toString().padStart(2, '0')}`;
    timerColor = Math.floor(state.time * 3) % 2 === 0 ? '#ff1133' : '#ff5500';
  } else if (_endlessActive) {
    // BETA 21.24 — time-in-endless clock; multiplier moved to the AGGRO bar.
    const elapsedEndless = state.time - ROUND_DURATION;
    const _em = Math.floor(elapsedEndless / 60);
    const _es = Math.floor(elapsedEndless % 60);
    timerStr = `⚠ ENDLESS ${_em}:${_es.toString().padStart(2, '0')}`;
    // Pulse red ↔ orange every 0.5s for visual urgency
    const pulse = Math.floor(state.time * 2) % 2 === 0;
    timerColor = pulse ? '#ff2233' : '#ff8800';
  } else {
    // BETA 21.14 — Dylan likes the countdown's "what happens when it hits 0?"
    // tension — the real problem was that a bare "4:32" read as a BUGGED survival
    // timer ticking down. Fix = LABEL it so it clearly reads as a countdown TO
    // ENDLESS, not a broken survival clock. (Total survival time still counts up
    // on the death screen.) Pulses orange→red as ENDLESS nears (color keyed off
    // `remaining`); flips to "ENDLESS x{N}" at 0 in the branch above — the payoff.
    timerStr = `ENDLESS IN ${remMins}:${remSecs.toString().padStart(2, '0')}`;
    timerColor = remaining < 60 ? '#ff3333' : remaining < 120 ? '#ffaa00' : '#ffffff';
  }
  // Build the four side-stats. Each renders in the SAME 30px bold style as
  // the timer, with its own colored backplate, sized to the text.
  const lvStr   = `LV ${player.level}`;
  const xpStr   = `XP ${player.xp}/${player.xpToNext}`;
  // BETA 22.89 — TOKENS, not gold (Dylan: "we are not collecting gold
  // anymore. TOKENS."). Same internal field; a drawn token disc replaces the
  // "G …g" text identity. CARD = next Wild Card Machine price.
  const goldStr = `  ${player.gold}`;          // leading pad = room for the disc
  const chestPriceVal = getCachedChestCost(state);
  const chestStr = `  CARD ${chestPriceVal}`;
  const _canAffordNext = player.gold >= chestPriceVal;

  ctx.font = 'bold 30px "Share Tech Mono", monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';

  // Measure each box (text width + 32px horizontal padding)
  const _measure = (s: string) => ctx.measureText(s).width + 32;
  const timerW = _measure(timerStr);
  const lvW    = _measure(lvStr);
  const xpW    = _measure(xpStr);
  const goldW  = _measure(goldStr);
  const chestW = _measure(chestStr);

  const GAP = 8; // spacing between boxes
  const BOX_H = 44;

  // Timer is anchored to screen center; left/right groups fan out from there.
  const timerX = w / 2 - timerW / 2;
  // Left group (right-to-left from timer): XP, then LV
  const xpX = timerX - GAP - xpW;
  const lvX = xpX - GAP - lvW;
  // Right group (left-to-right from timer end): GOLD, then NEXT CHEST
  const goldX  = timerX + timerW + GAP;
  const chestX = goldX + goldW + GAP;

  // Helper to render one stat box at (boxX, topY) with width boxW, given fg/border colors and the text.
  const _drawStatBox = (boxX: number, boxW: number, label: string, fg: string, border: string) => {
    ctx.fillStyle = 'rgba(5,5,8,0.82)';
    ctx.strokeStyle = border;
    ctx.lineWidth = 1.5;
    roundRect(ctx, boxX, topY, boxW, BOX_H, 6);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = fg;
    ctx.fillText(label, boxX + boxW / 2, topY + 7);
  };

  // LEFT cluster
  _drawStatBox(lvX, lvW, lvStr, '#ffffff', 'rgba(255,255,255,0.45)');
  _drawStatBox(xpX, xpW, xpStr, '#5a8cff', '#5a8cff');                    // XP blue per Dylan
  // CENTER: timer (matches original style)
  _drawStatBox(timerX, timerW, timerStr, timerColor, timerColor);
  // RIGHT cluster — tokens (22.89): drawn flat ridged token disc + count
  _drawStatBox(goldX, goldW, goldStr, '#ffd700', '#ffd700');
  _drawStatBox(chestX, chestW, chestStr,
    _canAffordNext ? '#ffd700' : '#9a9a9a',
    _canAffordNext ? '#ffd700' : 'rgba(154,154,154,0.6)');
  drawTokenDisc(ctx, goldX + 22, topY + BOX_H / 2, 11);
  drawTokenDisc(ctx, chestX + 22, topY + BOX_H / 2, 11, !_canAffordNext);

  // BETA 21.24 — AGGRO BAR (Dylan): once ENDLESS (10:00) or GOD MODE (post-boss)
  // is live, enemies scale up AGGRESSIVELY (getEndlessMultiplier + god ramp). This
  // bar pops up under the timer with the live ENEMY multiplier so you can watch
  // survivability collapse — guaranteed death, and you see it coming.
  if (_endlessActive || _godActive) {
    let aggroMult = 1;
    if (_endlessActive) {
      const _el = state.time - ROUND_DURATION;
      const _expM = Math.min(Math.pow(2, _el / 20), 32);
      aggroMult *= _expM + Math.max(0, _el - 100) * 0.6; // mirrors getEndlessMultiplier
    }
    if (_godActive) {
      const _gs = Math.max(0, state.time - (_sGodHud._godModeStart ?? state.time));
      aggroMult *= 1 + _gs * 0.04; // mirrors the GOD MODE per-frame ramp
    }
    const _barW = 360, _barH = 26;
    const _barX = w / 2 - _barW / 2;
    const _barY = topY + BOX_H + 8;
    // log-scale fill: x1 empty → x256 full, so it keeps climbing through the
    // whole aggressive range instead of pinning at the cap.
    const _fill = Math.max(0, Math.min(1, Math.log2(Math.max(1, aggroMult)) / 8));
    const _pulseHz = 1.5 + _fill * 4; // pulses faster as it fills
    const _pulse = 0.55 + 0.45 * Math.abs(Math.sin(state.time * _pulseHz * Math.PI));
    // backplate
    ctx.fillStyle = 'rgba(5,5,8,0.85)';
    ctx.strokeStyle = `rgba(255,40,50,${0.5 + 0.5 * _fill})`;
    ctx.lineWidth = 2;
    roundRect(ctx, _barX, _barY, _barW, _barH, 6);
    ctx.fill();
    ctx.stroke();
    // fill — red→orange, brightness pulsing
    const _grad = ctx.createLinearGradient(_barX, 0, _barX + _barW, 0);
    _grad.addColorStop(0, `rgba(255,${Math.round(70 * (1 - _fill))},25,${_pulse})`);
    _grad.addColorStop(1, `rgba(255,25,40,${_pulse})`);
    ctx.fillStyle = _grad;
    roundRect(ctx, _barX + 2, _barY + 2, Math.max(0, (_barW - 4) * _fill), _barH - 4, 5);
    ctx.fill();
    // label
    const _mulTxt = aggroMult >= 10 ? Math.round(aggroMult).toString() : aggroMult.toFixed(1);
    ctx.font = 'bold 16px "Share Tech Mono", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = '#ff0022';
    ctx.shadowBlur = 8 * _pulse;
    ctx.fillStyle = '#ffffff';
    ctx.fillText(`⚠ AGGRO x${_mulTxt}`, w / 2, _barY + _barH / 2 + 1);
    ctx.shadowBlur = 0;
    ctx.textBaseline = 'top'; // restore for subsequent HUD draws
  }

  // Top-right: Room info
  if (player.currentRoom) {
    const roomText = `[${player.currentRoom.displayName}]`;
    ctx.font = '14px "Share Tech Mono", monospace';
    const tw = ctx.measureText(roomText).width;
    ctx.fillStyle = 'rgba(5,5,8,0.88)';
    ctx.strokeStyle = '#39ff14';
    roundRect(ctx, w - padH - tw - 24, topY, tw + 24, 32, 4);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#39ff14';
    ctx.textAlign = 'right';
    ctx.fillText(roomText, w - padH - 12, topY + 9);
  }

  // XP bar — full width at the very bottom of the screen
  if (itemsModule || player.xp > 0) {
    const barH = 7;
    const barY = h - barH;
    const xpPct = Math.min(1, player.xp / player.xpToNext);

    // Dark backing track
    ctx.fillStyle = 'rgba(10,10,30,0.75)';
    ctx.fillRect(0, barY, w, barH);

    // Filled XP portion
    const fillW = w * xpPct;
    const xpGrad = ctx.createLinearGradient(0, barY, fillW, barY);
    xpGrad.addColorStop(0, '#3355ff');
    xpGrad.addColorStop(1, '#66aaff');
    ctx.fillStyle = xpGrad;
    ctx.fillRect(0, barY, fillW, barH);

    // Subtle top border glow line
    ctx.fillStyle = 'rgba(120,160,255,0.35)';
    ctx.fillRect(0, barY, fillW, 1);

    // Level label — left side just above the bar
    ctx.font = 'bold 11px "Share Tech Mono", monospace';
    ctx.fillStyle = 'rgba(140,170,255,0.85)';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'bottom';
    ctx.fillText(`LV ${player.level}`, 8, barY - 2);

    // XP numbers — right side
    ctx.textAlign = 'right';
    ctx.fillText(`${player.xp} / ${player.xpToNext} XP`, w - 8, barY - 2);
    ctx.textBaseline = 'alphabetic';
  }

  // Weapon HUD (bottom-center: ammo + reload bar)
  if (weaponsModule) {
    const ammoText = getWeaponAmmoDisplay();
    const reloadPct = getWeaponReloadProgress();
    const isThrowable = getWeaponIsThrowable();
    const wBarW = 140;
    const wBarH = 6;
    const wBarX = (w - wBarW) / 2;
    const wBarY = h - padH - 36;

    // Background panel
    ctx.fillStyle = 'rgba(5,5,8,0.85)';
    ctx.strokeStyle = reloadPct < 1 ? '#ff6600' : '#39ff14';
    ctx.lineWidth = 1;
    roundRect(ctx, wBarX - 10, wBarY - 24, wBarW + 20, 42, 4);
    ctx.fill();
    ctx.stroke();

    // Ammo / throw-state text
    ctx.font = 'bold 14px "Share Tech Mono", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillStyle = reloadPct < 1 ? '#ff6600' : '#ffd700';
    let hudLabel: string;
    if (isThrowable) {
      hudLabel = reloadPct < 1 ? '⏳ CHARGING...' : '🪃 THROW READY';
    } else {
      hudLabel = reloadPct < 1 ? 'RELOADING...' : `🔫 ${ammoText}`;
    }
    ctx.fillText(hudLabel, w / 2, wBarY - 20);

    // Reload bar
    ctx.fillStyle = 'rgba(30,30,30,0.8)';
    ctx.fillRect(wBarX, wBarY, wBarW, wBarH);
    const barColor = reloadPct < 1 ? '#ff6600' : '#39ff14';
    ctx.fillStyle = barColor;
    ctx.fillRect(wBarX, wBarY, wBarW * reloadPct, wBarH);
    ctx.strokeStyle = 'rgba(255,255,255,0.2)';
    ctx.lineWidth = 0.5;
    ctx.strokeRect(wBarX, wBarY, wBarW, wBarH);
  }

  // Bottom-left: Stats panel
  // BETA 15.41 — Trimmed: gold and chest count moved to the new top-center
  // HUD row. Bottom-left now only shows the kill/floor/elapsed-time data
  // that isn't already in the top row.
  {
    const panelW = 200;
    const panelH = 32;
    const px = padH;
    const py = h - padH - panelH;
    const elapsed2 = Math.floor(state.time);
    const mins2 = Math.floor(elapsed2 / 60);
    const secs2 = elapsed2 % 60;
    const timeStr2 = `${mins2}:${secs2.toString().padStart(2, '0')}`;

    ctx.fillStyle = 'rgba(5,5,8,0.82)';
    ctx.strokeStyle = 'rgba(255,215,0,0.25)';
    ctx.lineWidth = 1;
    roundRect(ctx, px, py, panelW, panelH, 4);
    ctx.fill();
    ctx.stroke();

    ctx.font = 'bold 12px "Share Tech Mono", monospace';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillStyle = '#ff6644';
    ctx.fillText(`KILLS: ${state.killCount}`, px + 10, py + 9);
    ctx.fillStyle = '#44aaff';
    ctx.fillText(`FL: ${state.map.level ?? 1}`, px + 90, py + 9);
    ctx.fillStyle = 'rgba(57,255,20,0.7)';
    ctx.fillText(`T: ${timeStr2}`, px + 140, py + 9);
  }

  // Bottom-right: Version number
  ctx.font = '11px "Share Tech Mono", monospace';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'bottom';
  ctx.fillStyle = 'rgba(57,255,20,0.45)';
  ctx.fillText(GAME_VERSION, w - padH, h - padH);

  // BETA 18.4 / 18.7 — Gamepad debug HUD. Always renders so Dylan can
  // tell if the API sees the Ally X at all + see whether the focus
  // screen is registered + which target is focused + which direction
  // was last detected.
  try {
    const _pads = typeof navigator !== 'undefined' && navigator.getGamepads ? navigator.getGamepads() : [];
    let _gp: Gamepad | null = null;
    let _padCount = 0;
    for (const _p of _pads) { if (_p && _p.connected) { if (!_gp) _gp = _p; _padCount++; } }
    const _hudX = w - padH;
    let _hudY = h - padH - 16;
    ctx.font = '10px "Share Tech Mono", monospace';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    const _line = (txt: string, color: string): void => {
      ctx.fillStyle = color;
      ctx.fillText(txt, _hudX, _hudY);
      _hudY -= 14;
    };
    // Active focus screen + focused target (BETA 18.7) — tells us if the
    // pipeline is wired even when no gamepad input fires.
    const _af = getActiveScreen();
    const _ft = getFocusedTarget();
    if (_af) {
      _line(`FOCUS: ${_af} → ${_ft?.id ?? '(none)'}`, '#7fff9f');
    } else {
      _line(`FOCUS: (no active screen)`, 'rgba(127,127,127,0.6)');
    }
    if (_gp) {
      const _pressed: string[] = [];
      for (let _i = 0; _i < _gp.buttons.length; _i++) {
        if (_gp.buttons[_i]?.pressed) _pressed.push(String(_i));
      }
      const _activeAxes: string[] = [];
      for (let _i = 0; _i < _gp.axes.length; _i++) {
        const _v = _gp.axes[_i] ?? 0;
        if (Math.abs(_v) > 0.15) _activeAxes.push(`${_i}:${_v.toFixed(2)}`);
      }
      _line(_activeAxes.length ? `AX[${_activeAxes.join(' ')}]` : 'AX[—]', _activeAxes.length ? '#ffaa55' : 'rgba(127,220,255,0.45)');
      _line(_pressed.length ? `BTN[${_pressed.join(',')}]` : 'BTN[—]', _pressed.length ? '#ffaa55' : 'rgba(127,220,255,0.45)');
      const _padName = (_gp.id || 'Controller').slice(0, 36);
      _line(`🎮 ${_padName}${_padCount > 1 ? ` (+${_padCount - 1})` : ''}`, 'rgba(127,220,255,0.85)');
    } else {
      _line('AX[—]', 'rgba(127,127,127,0.45)');
      _line('BTN[—]', 'rgba(127,127,127,0.45)');
      _line('🎮 NO PAD — press any controller button on this page first', 'rgba(255,170,85,0.85)');
    }
  } catch { /* never crash the HUD */ }

  // ── Right-side panel: Weapon Slot + Percs + Items ──
  // BETA 20.77 — Dylan: "tab should allow me to hide stats, and items so i
  // can see my screen." The HUD toggle (TAB key / Y button) now hides the
  // right items panel too, not just chat + left stats.
  if (!state.hudHidden) renderRightPanel(ctx, state, w, h, padH);

  // BETA 22.87 — chosen perk card flying into the PERCS strip (post-pick).
  _renderLevelUpFly(ctx, w, h);

  // ── BETA 16.24 — PSBOT cycle-25 P0.4: Left-side STATS PANEL ──
  // Per Dylan: "i should have visable real stats on the left showing
  // my actual stats. like damage, speed, luck. all the main base things
  // and any special ones like gas damage. but only show what i actually
  // have. put it in a box and make it accruate. hoverable with
  // explanation on each thing it is." Renders below the chat panel.
  // Only shows stats with non-zero / non-base values.
  // BETA 16.82 — gated on hudHidden (Y button toggle).
  // BETA 22.95 — ALSO gated off the pick screens (Dylan: "we are showing
  // two stats menues at the same time"). The pick screens draw their own
  // stats note (_drawPickStatsNote); the HUD copy underneath was the dupe.
  const _onPickScreen = state.phase === 'levelup' || state.phase === 'shrine_choice'
    || state.phase === 'chest_choice' || state.phase === 'chest_spin' || state.phase === 'ultra_chest';
  if (!state.hudHidden && !_onPickScreen) renderLeftStatsPanel(ctx, state, w, h, padH);

  // BETA 20.74 — HOLD-TO-INSPECT FULL MAP. While TAB (keyboard) or RT
  // (controller) is held during play, the corner minimap swaps for a big
  // centered version over a dim wash; release snaps straight back. Reuses
  // renderMinimap's override params (same path the pause menu uses), so
  // fog, rooms, player dot, and portal marker all come along for free.
  // BETA 22.09 — the BIG (held) map now draws AFTER the pick screens, in renderGame, so it sits
  // ON TOP of the shrine/chest/level-up cards (Dylan: "the map is behind them"). Here in the HUD
  // we only draw the CORNER minimap, and only when the big map is NOT being held.
  if (state.showMinimap && !_isBigMapHeld(state)) {
    renderMinimap(ctx, state, w, h);
  }
  ctx.restore();
}

// ─── BETA 16.26 — PSBOT cycle-27 P0.2: stats panel animation ─────────────
// Per Dylan: "i would love an animation in the stats panel if its new or
// the ammount goes up for me to see it happen. something like a plus
// sign and green and it being added. right now stats go up so fast that
// i cant tell if it added."
//
// Track per-stat previous values across frames. When a value goes up,
// spawn a floating "+N" green text near the row that floats upward and
// fades over ~1.2s. Same pattern as in-world damageNumbers but anchored
// to the stats panel layout.
interface StatDeltaPopup {
  statKey: string;       // identifier of which stat row this belongs to
  delta: string;         // pre-formatted "+1.0%" etc.
  spawnedAt: number;     // wall-clock ms (Date.now)
}
const _statPopups: StatDeltaPopup[] = [];
const _statPrevValues: Record<string, number> = {};
const STAT_POPUP_DURATION_MS = 1200;

function _trackStatChange(key: string, newValue: number, formatDelta: (d: number) => string): void {
  const prev = _statPrevValues[key];
  if (prev === undefined) {
    _statPrevValues[key] = newValue;
    return;
  }
  const diff = newValue - prev;
  // Threshold to avoid micro-flicker on float math; only popup if delta
  // is meaningfully positive (>= 0.05 of unit). Negative changes don't
  // popup (no "you lost X" feedback for now).
  if (diff >= 0.05) {
    _statPopups.push({
      statKey: key,
      delta: formatDelta(diff),
      spawnedAt: Date.now(),
    });
    if (_statPopups.length > 30) _statPopups.shift(); // bound memory
  }
  _statPrevValues[key] = newValue;
}

function _resetStatPopups(): void {
  _statPopups.length = 0;
  for (const k of Object.keys(_statPrevValues)) delete _statPrevValues[k];
}

// ─── BETA 16.24 — Left-side Stats Panel (PSBOT cycle-25 P0.4) ──────────
// ─── BETA 20.26 — Live hover stat projection (pick screens) ────────────────
// A "preview mod" is one raw per-pick stat delta carried by a hovered card
// (level-up perc, shrine buff, or chest item). renderLeftStatsPanel projects
// it onto the matching stat row so the player sees, in real time, what the
// card would do to their stats. Numbers are NEVER fabricated: additive stats
// (every one _applyPercStatBonus does `field += amount` on) animate current →
// current+delta; multiplicative/special stats only highlight the row.
type PanelPreviewMod = { stat: string; value: number; isPercent: boolean };

// SOURCE stat key (perc.stat / shrine buff.stat / item statModifier.stat) →
// panel row statKey. The three source vocabularies overlap; this unifies them.
const _PREVIEW_SRC_TO_PANEL: Record<string, string> = {
  damage: 'damage', attackSpeed: 'atkSpeed', critChance: 'critChance',
  critDamage: 'critDmg', lifesteal: 'lifesteal', armor: 'armor', shield: 'shield',
  luck: 'luck', dodgeChance: 'evasion', evasion: 'evasion', thorns: 'thorns',
  eliteDamage: 'eliteDmg', projectileBounce: 'bounces', projectileCount: 'projectiles',
  hpRegen: 'hpRegen', knockback: 'knockback', attackSize: 'attackArea',
  pickupRange: 'pickupRange', projectileSpeed: 'projSpeed', attackRange: 'range',
  maxHp: 'hp', moveSpeed: 'moveSpeed', xpBonus: 'xpGain', xpGain: 'xpGain',
  goldBonus: 'goldGain', goldGain: 'goldGain', gold: 'goldGain',
  // BETA 20.37 — Dylan: shrine hover didn't project the buff. Shrine buffs use the
  // player-FIELD key names (damageBonus, attackSpeedBonus, maxShield, ...), which
  // differ from the perc/item keys above — so the projection silently skipped them.
  // Alias the shrine-style keys to their panel rows.
  damageBonus: 'damage', attackSpeedBonus: 'atkSpeed', eliteDamageBonus: 'eliteDmg',
  knockbackBonus: 'knockback', projectileBounces: 'bounces', projectileCountBonus: 'projectiles',
  rangeBonus: 'range', maxShield: 'shield', shieldRegen: 'shieldRegen',
  goldMultiplier: 'goldGain', xpMultiplier: 'xpGain',
  // BETA 20.39 — wire the 5 buffs that previously projected NOTHING on hover
  // (Dylan deep-audit). overheal/pierce/phase = additive player fields;
  // damageReduction lives on itemState.playerStats; maxShieldMult is a
  // %-multiplier converted to a flat Shield delta in the projection loop below.
  overheal: 'overheal', projectilePierce: 'pierce', phaseChance: 'phase',
  damageReduction: 'dmgReduction', maxShieldMult: 'shield',
};

interface PanelStatMeta {
  icon: string; label: string;
  cur: (p: Player) => number;   // current raw value (same field the row reads)
  fmt: (n: number) => string;   // same formatting the row uses
  additive: boolean;            // projected = current + delta in the same unit
}
const _PANEL_STAT_META: Record<string, PanelStatMeta> = {
  damage:      { icon: '⚔', label: 'Damage',        cur: p => p.damageBonus ?? 0,           fmt: n => `+${n.toFixed(0)}%`,  additive: true },
  atkSpeed:    { icon: '⚡', label: 'Atk Speed',     cur: p => p.attackSpeedBonus ?? 0,      fmt: n => `+${n.toFixed(1)}%`,  additive: true },
  critChance:  { icon: '✸', label: 'Crit Chance',   cur: p => p.critChance ?? 0,            fmt: n => `${n.toFixed(1)}%`,   additive: true },
  critDmg:     { icon: '💥', label: 'Crit Dmg',      cur: p => p.critDamage ?? 0,            fmt: n => `+${n.toFixed(0)}%`,  additive: true },
  lifesteal:   { icon: '🩸', label: 'Lifesteal',     cur: p => p.lifesteal ?? 0,             fmt: n => `${n.toFixed(0)}%`,   additive: true },
  armor:       { icon: '🛡', label: 'Armor',         cur: p => p.armor ?? 0,                 fmt: n => `${Math.round(n)}`,   additive: true },
  shieldRegen: { icon: '◈⚡', label: 'Shield Regen',  cur: p => (p as unknown as { shieldRegen?: number }).shieldRegen ?? 0, fmt: n => `${n.toFixed(1)}/s`, additive: true }, // BETA 20.37 — shrine shieldRegen buffs project here
  shield:      { icon: '◈', label: 'Shield',        cur: p => p.maxShield ?? 0,             fmt: n => `${Math.round(n)}`,   additive: true },
  luck:        { icon: '🍀', label: 'Luck',          cur: p => p.luck ?? 0,                  fmt: n => `+${n.toFixed(0)}%`,  additive: true },
  evasion:     { icon: '💨', label: 'Evasion',       cur: p => p.evasion ?? 0,               fmt: n => `${n.toFixed(0)}%`,   additive: true },
  thorns:      { icon: '🌵', label: 'Thorns',        cur: p => p.thorns ?? 0,                fmt: n => `${n.toFixed(0)}%`,   additive: true },
  eliteDmg:    { icon: '👑', label: 'Elite Dmg',     cur: p => p.eliteDamageBonus ?? 0,      fmt: n => `+${n.toFixed(0)}%`,  additive: true },
  bounces:     { icon: '↺', label: 'Bounces',       cur: p => p.projectileBounceBonus ?? 0, fmt: n => `+${Math.round(n)}`,  additive: true },
  projectiles: { icon: '⋙', label: 'Projectiles',   cur: p => p.projectileCountBonus ?? 0,  fmt: n => `+${Math.round(n)}`,  additive: true },
  hpRegen:     { icon: '✚', label: 'HP Regen',      cur: p => p.hpRegen ?? 0,               fmt: n => `+${n.toFixed(1)}/s`, additive: true },
  knockback:   { icon: '↗', label: 'Knockback',     cur: p => p.knockbackBonus ?? 0,        fmt: n => `+${n.toFixed(0)}%`,  additive: true },
  attackArea:  { icon: '⊙', label: 'Size',          cur: p => p.attackAreaBonus ?? 0,       fmt: n => `+${n.toFixed(0)}%`,  additive: true },
  pickupRange: { icon: '◎', label: 'Pickup Range',  cur: p => p.pickupRange ?? 0,           fmt: n => `+${n.toFixed(0)}%`,  additive: true },
  projSpeed:   { icon: '➤', label: 'Proj Speed',    cur: p => (p as unknown as { projectileSpeedBonus?: number }).projectileSpeedBonus ?? 0, fmt: n => `+${n.toFixed(0)}%`, additive: true },
  range:       { icon: '⤢', label: 'Lock-On Range', cur: p => (p as unknown as { rangeBonus?: number }).rangeBonus ?? 0, fmt: n => `+${n.toFixed(0)}wu`, additive: true },
  // Non-additive (multiplicative / special) — highlight the row, never invent a number.
  hp:          { icon: '❤', label: 'Max HP',        cur: p => p.maxHp ?? 0,                 fmt: n => `${Math.round(n)}`,   additive: false },
  moveSpeed:   { icon: '👟', label: 'Move Speed',    cur: p => p.speed ?? 100,               fmt: n => `${Math.round(n)}%`,  additive: false },
  xpGain:      { icon: '★', label: 'XP Gain',       cur: p => p.xpMultiplier ?? 1,          fmt: n => `${n.toFixed(2)}×`,   additive: false },
  goldGain:    { icon: '◇', label: 'Gold Gain',     cur: p => p.goldMultiplier ?? 1,        fmt: n => `${n.toFixed(2)}×`,   additive: false },
  // BETA 20.39 — 4 buffs that used to highlight nothing on hover now project a number.
  overheal:    { icon: '❤', label: 'Overheal',      cur: p => (p as unknown as { overheal?: number }).overheal ?? 0,                              fmt: n => `+${Math.round(n)}`, additive: true },
  pierce:      { icon: '➹', label: 'Pierce',        cur: p => (p as unknown as { projectilePierceBonus?: number }).projectilePierceBonus ?? 0,    fmt: n => `+${Math.round(n)}`, additive: true },
  phase:       { icon: '👻', label: 'Phase',         cur: p => (p.contactPhaseChance ?? 0) * 100,                                                  fmt: n => `${n.toFixed(0)}%`,  additive: true }, // BETA 20.40 — read the REAL phase chance (contactPhaseChance, fraction→%), not the stale phaseChanceBonus field
  dmgReduction:{ icon: '🩹', label: 'Dmg Reduction', cur: () => (getItemState()?.playerStats as { damageReduction?: number } | undefined)?.damageReduction ?? 0, fmt: n => `${n.toFixed(0)}%`, additive: true },
};

// Eased tick animation for the projected number (reset whenever the hovered
// card's effect changes).
let _statPreviewHash = '';
let _statPreviewStart = 0;

function renderLeftStatsPanel(
  ctx: CanvasRenderingContext2D, state: GameState, _w: number, _h: number, padH: number,
  // BETA 20.26 — Dylan: "stats box doesn't get bigger on any level up or shrine,
  // or chest unlock screen." On the 3 pick screens we render the panel ENLARGED
  // (desiredScale > 1). The actual scale used is clamped to fit the remaining
  // vertical space so a stat-heavy late-game panel can't overflow the viewport.
  desiredScale = 1,
  // BETA 20.26 — when the player hovers/focuses a card on a pick screen, its
  // stat delta(s) are passed here so the panel projects them live.
  preview?: PanelPreviewMod[],
  // BETA 20.34 — Dylan: effect-only chest items (Anus Rocket, Diarrhea Gas,
  // Nutty T-Bag, Adrenaline Pod) have no stat number to project. Show this note
  // so hovering them still gives panel feedback instead of nothing.
  previewNote?: string,
  // BETA 22.62 — Dylan's pick-screen art brief: "replace the blue stats box
  // with a torn notebook page taped to the board." Same panel, same rows,
  // same live preview — paper palette + parchment backdrop when true.
  paperStyle = false,
): void {
  const p = state.player;
  if (!p) return;
  const _pc = paperStyle ? {
    glow: 'rgba(0,0,0,0.35)', bg: 'rgba(214,199,168,0.96)', border: 'rgba(0,0,0,0)',
    header: '#7a1f16', line: 'rgba(36,29,20,0.28)', hover: 'rgba(122,31,22,0.07)',
    label: 'rgba(36,29,20,0.78)', value: '#241d14', delta: '#2e7d1f', preview: '#2e7d1f',
    flashRGB: '212,178,52',
  } : {
    glow: 'rgba(108, 200, 255, 0.45)', bg: 'rgba(8, 12, 22, 0.92)', border: 'rgba(108, 200, 255, 0.55)',
    header: '#6cc8ff', line: 'rgba(108, 200, 255, 0.25)', hover: 'rgba(108, 200, 255, 0.10)',
    label: '#bbeaff', value: '#ffffff', delta: '#7dff5a', preview: '#a8ffb8', flashRGB: '80, 220, 100',
  };
  // Position: below the chat panel.
  // BETA 19.16 — Was hardcoded `panelY = 200`, assuming chat was always
  // 124px tall (6 single-line messages). Since BETA 17.89, chat wraps
  // long messages into multiple lines, making the panel 200px+. The
  // hardcoded panelY caused chat text to overlap the stats panel header.
  // HUD pass now writes the measured chat bottom into state._hudChatBottomY
  // (or 0 when chat isn't drawn) — dock 16px below it, or fall back to
  // the historic 200px when chat is off.
  const panelX = padH;
  // BETA 20.08 — LOCK the panel position. Was docking at `_chatBottom + 16`,
  // which moved the ENTIRE stats box up/down every time a chat message
  // appeared or expired. Per Dylan: "it needs to be locked. right now the
  // stats box moves up and down due to the ai chat coming in." Now a FIXED Y
  // below the chat's reserved height (clears ~6 lines of chat). The chat
  // floats above; the stats panel never bounces.
  const _topY = (state._hudTopStripH ?? 22) + padH;
  const panelY = _topY + 168;
  // BETA 20.08 — Dylan: "the text is too small. hard to read. increase the
  // size. this may increase the entire box some but that is fine." Wider box
  // (220 → 264) + taller rows (16 → 21) to fit the larger fonts below.
  const panelW = 264;
  const ROW_H = 21;
  const PAD = 10;

  // BETA 16.26 — PSBOT cycle-27 P0.2: track stat changes per frame for
  // floating "+X" popup feedback. Each tracked stat has a stable key.
  // Popups display next to the row that owns them, animated up + fade
  // over 1.2s. Only positive changes spawn popups.
  _trackStatChange('hp', p.hp, d => `+${Math.round(d)}`);
  _trackStatChange('damage', p.damageBonus ?? 0, d => `+${d.toFixed(1)}%`);
  _trackStatChange('atkSpeed', p.attackSpeedBonus ?? 0, d => `+${d.toFixed(1)}%`);
  _trackStatChange('critChance', p.critChance ?? 0, d => `+${d.toFixed(1)}%`);
  _trackStatChange('critDmg', p.critDamage ?? 0, d => `+${d.toFixed(1)}%`);
  _trackStatChange('lifesteal', p.lifesteal ?? 0, d => `+${d.toFixed(0)}%`);
  _trackStatChange('armor', p.armor ?? 0, d => `+${Math.round(d)}`);
  _trackStatChange('shield', p.maxShield ?? 0, d => `+${Math.round(d)}`);
  _trackStatChange('moveSpeed', p.speed ?? 100, d => `+${Math.round(d)}`);
  _trackStatChange('luck', p.luck ?? 0, d => `+${d.toFixed(0)}%`);
  _trackStatChange('evasion', p.evasion ?? 0, d => `+${d.toFixed(0)}%`);
  _trackStatChange('thorns', p.thorns ?? 0, d => `+${d.toFixed(0)}%`);
  _trackStatChange('eliteDmg', p.eliteDamageBonus ?? 0, d => `+${d.toFixed(0)}%`);
  _trackStatChange('bounces', p.projectileBounceBonus ?? 0, d => `+${Math.round(d)}`);
  _trackStatChange('projectiles', p.projectileCountBonus ?? 0, d => `+${Math.round(d)}`);
  // BETA 16.28 — PSBOT cycle-30d Ask 1: track new Megabonk-style stat rows
  // for popup feedback.
  _trackStatChange('hpRegen', p.hpRegen ?? 0, d => `+${d.toFixed(1)}/s`);
  _trackStatChange('knockback', p.knockbackBonus ?? 0, d => `+${d.toFixed(0)}%`);
  _trackStatChange('attackArea', p.attackAreaBonus ?? 0, d => `+${d.toFixed(0)}%`);
  _trackStatChange('pickupRange', p.pickupRange ?? 0, d => `+${d.toFixed(0)}%`);
  _trackStatChange('xpGain', (p.xpMultiplier ?? 1) * 100, d => `+${d.toFixed(0)}%`);
  _trackStatChange('goldGain', (p.goldMultiplier ?? 1) * 100, d => `+${d.toFixed(0)}%`);

  // Build a stat list, filtering out zero/base values so the panel only
  // shows what the player actually has. statKey lets popups attach to rows.
  // BETA 16.92 — PSBOT cycle-30d Ask 1: sectioned grouping. Sections
  // mirror Megabonk's panel: DEFENSE / OFFENSE / MODIFIERS / ECONOMY /
  // SPECIAL. Rows are tagged at push time, sorted by section before
  // render, and a divider + label renders whenever the section flips.
  type StatSection = 'def' | 'off' | 'mod' | 'eco' | 'sp';
  type Row = { statKey: string; icon: string; label: string; value: string; tip: string; section?: StatSection };
  const _SECTION_ORDER: StatSection[] = ['def', 'off', 'mod', 'eco', 'sp'];
  const _SECTION_LABEL: Record<StatSection, string> = {
    def: 'DEFENSE', off: 'OFFENSE', mod: 'MODIFIERS', eco: 'ECONOMY', sp: 'SPECIAL',
  };
  // Map statKey → section. Anything not listed defaults to 'mod'.
  const _STAT_SECTION: Record<string, StatSection> = {
    hp: 'def', shield: 'def', shieldRegen: 'def', armor: 'def', evasion: 'def', thorns: 'def', hpRegen: 'def',
    damage: 'off', atkSpeed: 'off', critChance: 'off', critDmg: 'off', lifesteal: 'off',
    eliteDmg: 'off', bounces: 'off', projectiles: 'off', projSpeed: 'off', range: 'off', knockback: 'off', attackArea: 'off',
    moveSpeed: 'mod', luck: 'mod', pickupRange: 'mod',
    xpGain: 'eco', goldGain: 'eco',
    gasDmg: 'sp', difficulty: 'sp',
  };
  const rows: Row[] = [];

  // HP — always show (alive player has HP). Format "current/max".
  rows.push({
    statKey: 'hp',
    icon: '❤',
    label: 'HP',
    value: `${Math.round(p.hp)}/${Math.round(p.maxHp)}`,
    tip: 'Health. Take damage from enemies → drops. Heals from items + perks.',
  });

  // Damage bonus
  if ((p.damageBonus ?? 0) !== 0) {
    rows.push({
      statKey: 'damage',
      icon: '⚔',
      label: 'Damage',
      value: `+${(p.damageBonus ?? 0).toFixed(0)}%`,
      tip: 'Bonus damage on every hit. Sums perks (T01 Steroids) + shrine buffs + items.',
    });
  }
  // Attack speed
  if ((p.attackSpeedBonus ?? 0) !== 0) {
    rows.push({
      statKey: 'atkSpeed',
      icon: '⚡',
      label: 'Atk Speed',
      value: `+${(p.attackSpeedBonus ?? 0).toFixed(1)}%`,
      tip: 'Faster reload + spin (Karen Drones). Sums T02 Adderall + items.',
    });
  }
  // Crit chance
  if ((p.critChance ?? 0) > 5) {  // base is 5 — only show if buffed
    rows.push({
      statKey: 'critChance',
      icon: '✸',
      label: 'Crit Chance',
      value: `${(p.critChance ?? 0).toFixed(1)}%`,
      tip: 'Chance each hit crits. Base 5%. Boosted by T06, shrine crit buffs, items.',
    });
  }
  // Crit damage
  if ((p.critDamage ?? 0) !== 0) {
    rows.push({
      statKey: 'critDmg',
      icon: '💥',
      label: 'Crit Dmg',
      value: `+${(p.critDamage ?? 0).toFixed(0)}%`,
      tip: 'Extra damage on crit hits. Cowboy Quick Draw passive contributes.',
    });
  }
  // Lifesteal
  if ((p.lifesteal ?? 0) !== 0) {
    rows.push({
      statKey: 'lifesteal',
      icon: '🩸',
      label: 'Lifesteal',
      value: `${(p.lifesteal ?? 0).toFixed(0)}%`,
      tip: 'HP healed per damage dealt. From shrines + Drac\'s Fang weapon.',
    });
  }
  // Armor
  if ((p.armor ?? 0) > 0) {
    rows.push({
      statKey: 'armor',
      icon: '🛡',
      label: 'Armor',
      value: String(Math.round(p.armor ?? 0)),
      tip: 'Reduces incoming damage by flat amount per hit.',
    });
  }
  // Shield
  if ((p.maxShield ?? 0) > 0) {
    rows.push({
      statKey: 'shield',
      icon: '◈',
      label: 'Shield',
      value: String(Math.round(p.maxShield ?? 0)),
      tip: 'Absorbs damage before HP. Regenerates after 3.5s of no shield damage. Purple outline on player when active.',
    });
  }
  // BETA 16.36 — Shield regen rate row. Only shown if non-zero (base 1.0
  // means the row appears as soon as shield exists). Items + shrines
  // bump this above 1 → row reflects the bonus.
  const _shRegen = (p as unknown as { shieldRegen?: number }).shieldRegen ?? 0;
  if ((p.maxShield ?? 0) > 0 && _shRegen > 0) {
    rows.push({
      statKey: 'shieldRegen',
      icon: '◈⚡',
      label: 'Shield Regen',
      value: `${_shRegen.toFixed(1)}/s`,
      tip: 'Shield regen rate after 3.5s of no shield damage. Stack with shrines + items. Base 2/sec.',
    });
  }
  // Move speed
  if ((p.speed ?? 100) !== 100) {
    rows.push({
      statKey: 'moveSpeed',
      icon: '👟',
      label: 'Move Speed',
      value: `${Math.round(p.speed ?? 100)}%`,
      tip: 'How fast you move. Base 100%.',
    });
  }
  // Luck
  if ((p.luck ?? 0) !== 0) {
    rows.push({
      statKey: 'luck',
      icon: '🍀',
      label: 'Luck',
      value: `+${(p.luck ?? 0).toFixed(0)}%`,
      tip: 'Better rolls on level-up rarities + shrine offers.',
    });
  }
  // Evasion
  if ((p.evasion ?? 0) > 0) {
    rows.push({
      statKey: 'evasion',
      icon: '💨',
      label: 'Evasion',
      value: `${(p.evasion ?? 0).toFixed(0)}%`,
      tip: 'Chance to dodge incoming damage entirely.',
    });
  }
  // Thorns
  if ((p.thorns ?? 0) > 0) {
    rows.push({
      statKey: 'thorns',
      icon: '🌵',
      label: 'Thorns',
      value: `${(p.thorns ?? 0).toFixed(0)}%`,
      tip: 'Reflect % of damage taken back to attacker.',
    });
  }
  // Elite damage
  if ((p.eliteDamageBonus ?? 0) > 0) {
    rows.push({
      statKey: 'eliteDmg',
      icon: '👑',
      label: 'Elite Dmg',
      value: `+${(p.eliteDamageBonus ?? 0).toFixed(0)}%`,
      tip: 'Bonus damage vs boss/elite enemies (crowned mobs, brawlers).',
    });
  }
  // Projectile bounce
  if ((p.projectileBounceBonus ?? 0) > 0) {
    rows.push({
      statKey: 'bounces',
      icon: '↺',
      label: 'Bounces',
      value: `+${p.projectileBounceBonus ?? 0}`,
      tip: 'Projectiles bounce off walls + chain to extra enemies.',
    });
  }
  // Projectile count
  if ((p.projectileCountBonus ?? 0) > 0) {
    rows.push({
      statKey: 'projectiles',
      icon: '⋙',
      label: 'Projectiles',
      value: `+${p.projectileCountBonus ?? 0}`,
      tip: 'Extra projectiles per shot. Adds Karen Drones too.',
    });
  }

  // BETA 16.28 — PSBOT cycle-30d Ask 1: expanded Megabonk-style stat
  // coverage. Adds rows for HP Regen / Knockback / Size / Pickup Range /
  // XP Gain / Gold Gain so the panel reflects the full attribute set
  // Megabonk shows. Sectioned dividers + reorganization deferred to a
  // follow-up cycle (current panel renders flat).
  if ((p.hpRegen ?? 0) > 0) {
    rows.push({
      statKey: 'hpRegen',
      icon: '✚',
      label: 'HP Regen',
      value: `+${(p.hpRegen ?? 0).toFixed(1)}/s`,
      tip: 'Health regenerated per second.',
    });
  }
  if ((p.knockbackBonus ?? 0) > 0) {
    rows.push({
      statKey: 'knockback',
      icon: '↗',
      label: 'Knockback',
      value: `+${(p.knockbackBonus ?? 0).toFixed(0)}%`,
      tip: 'Stronger push on enemies when hit.',
    });
  }
  if ((p.attackAreaBonus ?? 0) > 0) {
    rows.push({
      statKey: 'attackArea',
      icon: '⊙',
      label: 'Size',
      value: `+${(p.attackAreaBonus ?? 0).toFixed(0)}%`,
      tip: 'Larger arc swings, AoE radius, and projectile size.',
    });
  }
  if ((p.pickupRange ?? 0) > 0) {
    rows.push({
      statKey: 'pickupRange',
      icon: '◎',
      label: 'Pickup Range',
      value: `+${(p.pickupRange ?? 0).toFixed(0)}%`,
      tip: 'Larger radius for auto-collecting XP/gold/health orbs.',
    });
  }
  if ((p.xpMultiplier ?? 1) !== 1) {
    rows.push({
      statKey: 'xpGain',
      icon: '★',
      label: 'XP Gain',
      value: `${((p.xpMultiplier ?? 1)).toFixed(2)}×`,
      tip: 'Multiplier on XP orb pickups.',
    });
  }
  if ((p.goldMultiplier ?? 1) !== 1) {
    rows.push({
      statKey: 'goldGain',
      icon: '◇',
      label: 'Gold Gain',
      value: `${((p.goldMultiplier ?? 1)).toFixed(2)}×`,
      tip: 'Multiplier on gold drops.',
    });
  }
  // BETA 16.92 — PSBOT cycle-30d Ask 1 stats panel parity fill-in.
  // Added: Projectile Speed, Range, Difficulty (negative shrine effect).
  if ((p.projectileSpeedBonus ?? 0) > 0) {
    rows.push({
      statKey: 'projSpeed',
      icon: '➤',
      label: 'Projectile Speed',
      value: `+${(p.projectileSpeedBonus ?? 0).toFixed(0)}%`,
      tip: 'Faster projectile travel speed (snipers / arc weapons).',
    });
  }
  if ((p.rangeBonus ?? 0) > 0) {
    rows.push({
      statKey: 'range',
      icon: '⤢',
      label: 'Lock-On Range',
      value: `+${(p.rangeBonus ?? 0).toFixed(0)}wu`,
      tip: 'Extends weapon target-acquisition range.',
    });
  }
  // BETA 20.40 — owned overheal/pierce/phase/dmg-reduction now show PERMANENTLY
  // (deep-audit gap: they only flashed during hover-projection, invisible once owned).
  {
    const _ovh = (p as unknown as { overheal?: number }).overheal ?? 0;
    if (_ovh > 0) rows.push({ statKey: 'overheal', icon: '❤', label: 'Overheal', value: `+${Math.round(_ovh)}`, tip: 'Extra HP buffer above max (N07 Puberty Blockers). Persists until chipped away.' });
    const _prc = (p as unknown as { projectilePierceBonus?: number }).projectilePierceBonus ?? 0;
    if (_prc > 0) rows.push({ statKey: 'pierce', icon: '➹', label: 'Pierce', value: `+${Math.round(_prc)}`, tip: 'Extra enemies each shot passes through (N08 Not Ritalin).' });
    const _phs = (p.contactPhaseChance ?? 0) * 100;
    if (_phs > 0) rows.push({ statKey: 'phase', icon: '👻', label: 'Phase', value: `${_phs.toFixed(0)}%`, tip: 'Chance to phase through enemy contact damage (N14 Not Acid).' });
    const _dr = (getItemState()?.playerStats as { damageReduction?: number } | undefined)?.damageReduction ?? 0;
    if (_dr > 0) rows.push({ statKey: 'dmgReduction', icon: '🩹', label: 'Dmg Reduction', value: `${_dr.toFixed(0)}%`, tip: 'Flat % less damage taken (Mystery Meat Stew).' });
  }
  // Difficulty shrine — negative-tier "+8% difficulty" buffs accumulate
  // here. Showing it as a stat so the player knows when they\'ve picked
  // them. Only shows if difficulty has been actively pumped via shrines.
  {
    const _diffPct = getShrineDifficultyPct();
    if (_diffPct > 0) {
      rows.push({
        statKey: 'difficulty',
        icon: '⚠',
        label: 'Difficulty',
        value: `+${_diffPct.toFixed(0)}%`,
        tip: 'Spawn-rate multiplier from negative shrines (more enemies, no stat changes).',
      });
    }
  }

  // Special: gas damage active (from R11 Diarrhea Gas in inventory).
  const inv = getItemState()?.inventory ?? [];
  if (inv.some(s => s.item.id === 'R11')) {
    rows.push({
      statKey: 'gasDmg',
      icon: '☣',
      label: 'Gas Damage',
      value: 'ACTIVE',
      tip: 'Diarrhea Gas spawns poison clouds on damage taken.',
    });
  }

  if (rows.length === 0) return;

  // BETA 16.92 — Tag each row with its section + sort by section order.
  // Rows without a mapped section default to 'mod'.
  for (const r of rows) {
    r.section = _STAT_SECTION[r.statKey] ?? 'mod';
  }
  rows.sort((a, b) => {
    const aIdx = _SECTION_ORDER.indexOf(a.section ?? 'mod');
    const bIdx = _SECTION_ORDER.indexOf(b.section ?? 'mod');
    return aIdx - bIdx;
  });
  // BETA — Pin the SURVIVAL CORE (HP, Armor, Shield) to the very top of the
  // panel — Dylan: "the most important stats like this should be at the top."
  // Stable post-pass: core stats lead in HP→Armor→Shield order; everything
  // else keeps its section order. (All three are DEFENSE, so the section
  // header still renders correctly above them.)
  const _CORE_ORDER = ['hp', 'armor', 'shield'];
  rows.sort((a, b) => {
    const ai = _CORE_ORDER.indexOf(a.statKey);
    const bi = _CORE_ORDER.indexOf(b.statKey);
    if (ai === -1 && bi === -1) return 0;
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
  // Count distinct sections present so we can size the panel correctly
  // (each section gets a 14px label + 2px divider line above its first row).
  const _sectionsPresent = new Set<StatSection>();
  for (const r of rows) _sectionsPresent.add(r.section ?? 'mod');
  const SECTION_HEADER_H = 21;   // BETA 20.08 — bumped 16 → 21 to match larger section-label font

  // BETA 20.26 — Aggregate the hovered card's projection deltas per panel row.
  const renderedKeys = new Set(rows.map(r => r.statKey));
  const previewDeltaByKey = new Map<string, { delta: number; additive: boolean }>();
  if (preview && preview.length > 0) {
    for (const mod of preview) {
      const pk = _PREVIEW_SRC_TO_PANEL[mod.stat];
      if (!pk || !_PANEL_STAT_META[pk]) continue;
      const flatHp = pk === 'hp' && !mod.isPercent;   // flat +HP adds to maxHp
      const additive = _PANEL_STAT_META[pk].additive || flatHp;
      // BETA 21.60 — maxShieldMult is ADDITIVE on the flat base (all picked %s sum,
      // then multiply the flat base ONCE), NOT compounding. The old preview used
      // (current shield × %), which grew every pick and read as compounding to Dylan
      // ("+50% of 150, then of 225..."). Recover the flat base = current / (1 + sumPct/100)
      // so the projected gain is the REAL constant additive delta (e.g. always +50 on a
      // 100 base), matching what the shield actually does.
      const _shieldMultPct = (p as { _shieldMultPct?: number })._shieldMultPct ?? 0;
      const _shieldFlatBase = (p.maxShield ?? 0) / (1 + _shieldMultPct / 100);
      const _modVal = mod.stat === 'maxShieldMult'
        ? Math.round(_shieldFlatBase * (mod.value / 100))
        : mod.value;
      const prev2 = previewDeltaByKey.get(pk);
      previewDeltaByKey.set(pk, {
        delta: (prev2?.delta ?? 0) + _modVal,
        additive: (prev2?.additive ?? true) && additive,
      });
    }
  }
  // Projection entries for stats the player has no row for yet → append below.
  // (forEach instead of spread — the build target predates Map-iterator spread.)
  const newPreviewKeys: string[] = [];
  const _pvHashParts: string[] = [];
  previewDeltaByKey.forEach((v, k) => {
    _pvHashParts.push(`${k}:${v.delta}`);
    if (!renderedKeys.has(k) && v.additive) newPreviewKeys.push(k);
  });
  // Eased animation clock — restarts whenever the projected set changes.
  const _pvNow = performance.now();
  const _pvHash = _pvHashParts.join(',');
  // BETA 20.31 — only (re)start the ease clock for a REAL, CHANGED preview.
  // The panel renders TWICE per frame on pick screens (renderHUD with no
  // preview + the scaled overlay WITH preview). The old unguarded reset let the
  // empty-preview call zero _statPreviewStart every frame → _pvEase stuck at 0 →
  // the projected number was frozen at the CURRENT value (highlighted green but
  // never counted up to current+delta). Guarding on a non-empty hash lets the
  // overlay call's clock survive the HUD call, so the projection animates.
  if (_pvHash && _pvHash !== _statPreviewHash) { _statPreviewHash = _pvHash; _statPreviewStart = _pvNow; }
  const _pvT = _pvHash ? Math.min(1, (_pvNow - _statPreviewStart) / 240) : 1;
  const _pvEase = 1 - Math.pow(1 - _pvT, 3);   // easeOutCubic

  // BETA — Character PASSIVE band pinned at the very top of the panel so the
  // player always remembers their character's signature buff. Reads live from
  // CHARACTER_PASSIVES so it stays correct no matter how passives are tuned.
  const _passiveDef = state.character?.id ? CHARACTER_PASSIVES[state.character.id] : undefined;
  const PASSIVE_H = _passiveDef ? 38 : 0;

  const panelH = PAD * 2 + (rows.length + newPreviewKeys.length) * ROW_H + 24 + PASSIVE_H + _sectionsPresent.size * SECTION_HEADER_H + (previewNote ? ROW_H : 0); // +24 STATS header, +PASSIVE_H passive band, per-section dividers, effect-note row (20.34)

  // BETA 20.26 — Enlarge the whole panel on pick screens. We pivot-scale about
  // the panel's top-left corner (panelX,panelY) so every internal coordinate
  // stays valid without rewriting the layout math. The scale is clamped so a
  // tall panel can't spill past the bottom edge of the viewport.
  const _availH = _h - panelY - 12;
  const _panelScale = Math.max(1, Math.min(desiredScale, panelH > 0 ? _availH / panelH : desiredScale));
  ctx.save();
  if (_panelScale !== 1) {
    ctx.translate(panelX, panelY);
    ctx.scale(_panelScale, _panelScale);
    ctx.translate(-panelX, -panelY);
  }

  // Backdrop + border (matches chat panel style)
  ctx.save();
  ctx.shadowColor = _pc.glow;
  ctx.shadowBlur = 14;
  ctx.fillStyle = _pc.bg;
  ctx.fillRect(panelX, panelY, panelW, panelH);
  ctx.shadowBlur = 0;
  if (paperStyle) {
    const _paperImg = getCachedImage('/pick-kit/panel_parchment.png');
    if (_paperImg) ctx.drawImage(_paperImg, panelX - panelW * 0.05, panelY - panelH * 0.03, panelW * 1.10, panelH * 1.06);
  }
  ctx.strokeStyle = _pc.border;
  ctx.lineWidth = 1;
  ctx.strokeRect(panelX, panelY, panelW, panelH);

  // Header
  // BETA 20.08 — bigger fonts for readability (Dylan). Header 13 → 17.
  ctx.font = 'bold 17px "Share Tech Mono", monospace';
  ctx.fillStyle = _pc.header;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText('STATS', panelX + PAD, panelY + 5);

  // BETA — PASSIVE band: pinned gold strip directly under the STATS header so
  // the player always remembers their character's signature + growth passive.
  if (_passiveDef) {
    const bandX = panelX + 5, bandY = panelY + 25, bandW = panelW - 10, bandH = PASSIVE_H - 5;
    ctx.fillStyle = 'rgba(255, 200, 60, 0.10)';
    ctx.fillRect(bandX, bandY, bandW, bandH);
    ctx.strokeStyle = 'rgba(255, 200, 60, 0.45)';
    ctx.lineWidth = 1;
    ctx.strokeRect(bandX, bandY, bandW, bandH);
    // Line 1 — signature name (gold, the identity hook)
    ctx.font = 'bold 13px "Share Tech Mono", monospace';
    ctx.fillStyle = '#ffd24a';
    ctx.fillText(`✦ ${_passiveDef.signatureName}`, panelX + PAD, panelY + 28);
    // Line 2 — per-level growth stat (dim, the secondary passive)
    ctx.font = '11px "Share Tech Mono", monospace';
    ctx.fillStyle = 'rgba(255, 220, 150, 0.75)';
    ctx.fillText(_passiveDef.layer1Label, panelX + PAD, panelY + 45);
  }

  // Rows
  // BETA 20.08 — row font 12 → 15 for readability.
  ctx.font = '15px "Share Tech Mono", monospace';
  let curY = panelY + 28 + PASSIVE_H;
  const mx = state.mouse?.x ?? -9999;
  const my = state.mouse?.y ?? -9999;
  let hoverTip: string | null = null;
  // Map of statKey → rowY for popup anchoring (BETA 16.26).
  const rowYByStatKey = new Map<string, number>();
  // BETA 16.92 — Track previous section so we know when to draw a header.
  let _prevSection: StatSection | null = null;
  for (const row of rows) {
    // Draw section header + divider if section changed
    const _curSection = row.section ?? 'mod';
    if (_curSection !== _prevSection) {
      ctx.save();
      // Faint divider line above the header
      ctx.strokeStyle = _pc.line;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(panelX + PAD, curY + 4);
      ctx.lineTo(panelX + panelW - PAD, curY + 4);
      ctx.stroke();
      // Section label
      // BETA 20.08 — section label 10 → 13.
      ctx.font = 'bold 13px "Share Tech Mono", monospace';
      ctx.fillStyle = _pc.header;
      ctx.textAlign = 'left';
      ctx.fillText(_SECTION_LABEL[_curSection], panelX + PAD, curY + 8);
      ctx.restore();
      ctx.font = '15px "Share Tech Mono", monospace';
      curY += SECTION_HEADER_H;
      _prevSection = _curSection;
    }
    const rowY = curY;
    rowYByStatKey.set(row.statKey, rowY);
    // BETA 20.26 — internal row-hover uses unscaled mouse coords, so disable it
    // when the panel is pivot-scaled (pick screens). The player hovers CARDS
    // there, not stat rows; the projection preview handles that interaction.
    const isHover = _panelScale === 1 && mx >= panelX && mx < panelX + panelW && my >= rowY && my < rowY + ROW_H;
    if (isHover) {
      ctx.fillStyle = _pc.hover;
      ctx.fillRect(panelX + 2, rowY, panelW - 4, ROW_H);
      hoverTip = row.tip;
    }
    // BETA 16.26 — PSBOT cycle-27 P0.2: row flash for stat increases.
    // Briefly highlight the row in green when a popup just spawned for it.
    const popupForRow = _statPopups.find(pop => {
      if (pop.statKey !== row.statKey) return false;
      const age = Date.now() - pop.spawnedAt;
      return age < 500; // first 500ms = flash window
    });
    if (popupForRow) {
      const flashAge = Date.now() - popupForRow.spawnedAt;
      const flashAlpha = (1 - flashAge / 500) * 0.35;
      ctx.fillStyle = `rgba(${_pc.flashRGB}, ${flashAlpha.toFixed(3)})`;
      ctx.fillRect(panelX + 2, rowY, panelW - 4, ROW_H);
    }
    // BETA 20.26 — live projection overlay for the hovered card's stat(s).
    const _pv = previewDeltaByKey.get(row.statKey);
    if (_pv) {
      ctx.fillStyle = `rgba(${_pc.flashRGB}, ${(0.10 + 0.12 * _pvEase).toFixed(3)})`;
      ctx.fillRect(panelX + 2, rowY, panelW - 4, ROW_H);
    }
    ctx.fillStyle = _pv ? _pc.preview : _pc.label;
    ctx.textAlign = 'left';
    ctx.fillText(`${row.icon}  ${row.label}`, panelX + PAD, rowY + 2);
    ctx.textAlign = 'right';
    if (_pv && _pv.additive) {
      // Animate the projected value current → current+delta (never fabricated:
      // these are the stats _applyPercStatBonus adds linearly).
      const meta = _PANEL_STAT_META[row.statKey];
      const from = meta.cur(p);
      const to = from + _pv.delta;
      const animVal = from + (to - from) * _pvEase;
      const projStr = row.statKey === 'hp'
        ? `${Math.round(p.hp)}/${Math.round(animVal)}`
        : meta.fmt(animVal);
      ctx.fillStyle = _pc.delta;
      ctx.fillText(projStr, panelX + panelW - PAD, rowY + 2);
    } else if (_pv) {
      // Multiplicative / special stat — keep the true value, flag it climbing.
      ctx.fillStyle = _pc.value;
      ctx.fillText(row.value, panelX + panelW - PAD, rowY + 2);
      const vw = ctx.measureText(row.value).width;
      ctx.fillStyle = _pc.delta;
      ctx.textAlign = 'left';
      ctx.fillText('▲', panelX + panelW - PAD - vw - 16, rowY + 2);
    } else {
      ctx.fillStyle = _pc.value;
      ctx.fillText(row.value, panelX + panelW - PAD, rowY + 2);
    }
    curY += ROW_H;
  }

  // BETA 20.26 — Append preview rows for stats the player doesn't have yet
  // (e.g. hovering a "+10% Lifesteal" card when Lifesteal isn't on the panel).
  // Projected from a base of 0 → always correct for additive-from-zero stats.
  for (const pk of newPreviewKeys) {
    const meta = _PANEL_STAT_META[pk];
    const entry = previewDeltaByKey.get(pk)!;
    const rowY = curY;
    ctx.fillStyle = `rgba(${_pc.flashRGB}, ${(0.12 + 0.14 * _pvEase).toFixed(3)})`;
    ctx.fillRect(panelX + 2, rowY, panelW - 4, ROW_H);
    ctx.fillStyle = '#a8ffb8';
    ctx.textAlign = 'left';
    ctx.fillText(`${meta.icon}  ${meta.label}`, panelX + PAD, rowY + 2);
    ctx.fillStyle = _pc.delta;
    ctx.textAlign = 'right';
    ctx.fillText(meta.fmt(entry.delta * _pvEase), panelX + panelW - PAD, rowY + 2);
    curY += ROW_H;
  }

  // BETA 20.34 — effect-item note: a chest item with no stat to project (Anus
  // Rocket, Diarrhea Gas, Nutty T-Bag, Adrenaline Pod) still gets panel feedback.
  if (previewNote) {
    const _nY = curY;
    ctx.fillStyle = `rgba(${_pc.flashRGB}, ${(0.12 + 0.14 * _pvEase).toFixed(3)})`;
    ctx.fillRect(panelX + 2, _nY, panelW - 4, ROW_H);
    ctx.font = 'bold 14px "Share Tech Mono", monospace';
    ctx.fillStyle = _pc.delta;
    ctx.textAlign = 'left';
    ctx.fillText(previewNote, panelX + PAD, _nY + 3);
    ctx.font = '15px "Share Tech Mono", monospace';
    curY += ROW_H;
  }

  // BETA 16.26 — PSBOT cycle-27 P0.2: render floating "+X%" green popups
  // for stat changes. Each popup floats up + fades over 1.2s. Anchored
  // to its row's right edge using rowYByStatKey.
  ctx.font = 'bold 12px "Share Tech Mono", monospace';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'right';
  const now = Date.now();
  for (let i = _statPopups.length - 1; i >= 0; i--) {
    const pop = _statPopups[i];
    const age = now - pop.spawnedAt;
    if (age >= STAT_POPUP_DURATION_MS) {
      _statPopups.splice(i, 1);
      continue;
    }
    const rowY = rowYByStatKey.get(pop.statKey);
    if (rowY === undefined) continue; // row not visible (filtered out)
    const t = age / STAT_POPUP_DURATION_MS;
    const alpha = 1 - t;
    const yOffset = -t * 18; // float up 18px over duration
    const popupX = panelX + panelW - PAD - 50;
    const popupY = rowY + ROW_H / 2 + yOffset;
    // Glow halo
    ctx.shadowColor = 'rgba(80, 220, 100, 0.7)';
    ctx.shadowBlur = 8;
    ctx.fillStyle = `rgba(120, 255, 140, ${alpha.toFixed(3)})`;
    ctx.fillText(pop.delta, popupX, popupY);
    ctx.shadowBlur = 0;
  }
  ctx.restore();

  // Hover tooltip — render last so it floats above the panel + overlay.
  // BETA 20.26 — suppressed while pivot-scaled (mouse coords don't match the
  // scaled rows, and the tooltip would render at the wrong place).
  if (hoverTip && _panelScale === 1) {
    ctx.save();
    ctx.font = '11px "Share Tech Mono", monospace';
    const lines: string[] = [];
    // Word-wrap at ~46 chars
    const words = hoverTip.split(' ');
    let line = '';
    for (const w of words) {
      if ((line + ' ' + w).length > 46) {
        lines.push(line);
        line = w;
      } else {
        line = line ? `${line} ${w}` : w;
      }
    }
    if (line) lines.push(line);
    const tipW = 280;
    const tipH = 8 + lines.length * 14 + 8;
    const tipX = Math.min(panelX + panelW + 8, _w - tipW - 8);
    const tipY = my + 16;
    ctx.fillStyle = 'rgba(8, 12, 22, 0.95)';
    ctx.strokeStyle = 'rgba(108, 200, 255, 0.7)';
    ctx.lineWidth = 1;
    ctx.fillRect(tipX, tipY, tipW, tipH);
    ctx.strokeRect(tipX, tipY, tipW, tipH);
    ctx.fillStyle = '#bbeaff';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    for (let i = 0; i < lines.length; i++) {
      ctx.fillText(lines[i], tipX + 8, tipY + 8 + i * 14);
    }
    ctx.restore();
  }

  // BETA 20.26 — close the pivot-scale transform opened before the backdrop.
  ctx.restore();
}

// BETA 16.40 — exported + parameterized so the pause menu can render its
// own copy of the minimap inside the chalkboard. Default values match
// the in-game top-right HUD position/size when args are omitted.
// BETA 22.09 — is the player HOLDING the big "inspect map" key (Space/RT) during a phase where
// we show it? Shared by the HUD (which then skips the corner map) and renderBigMapOverlayIfHeld.
function _isBigMapHeld(state: GameState): boolean {
  return !!state.showMinimap
    && (!!state._bigMapKeyHeld || !!state._bigMapPadHeld)
    && (state.phase === 'playing' || state.phase === 'levelup' || state.phase === 'shrine_choice'
        || state.phase === 'chest_choice' || state.phase === 'chest_spin' || state.phase === 'ultra_chest');
}

// BETA 22.09 — the big "hold to inspect" map. Drawn LAST in renderGame (AFTER the pick screens)
// so it sits ON TOP of the shrine/chest/level-up cards instead of behind them (Dylan).
function renderBigMapOverlayIfHeld(ctx: CanvasRenderingContext2D, state: GameState, w: number, h: number): void {
  if (!_isBigMapHeld(state)) return;
  ctx.fillStyle = 'rgba(0,0,0,0.62)';
  ctx.fillRect(0, 0, w, h);
  const _bigSize = Math.min(w, h) * 0.82;
  const _bigX = (w - _bigSize) / 2;
  const _bigY = (h - _bigSize) / 2;
  renderMinimap(ctx, state, w, h, _bigX, _bigY, _bigSize);
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.font = '13px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('release SPACE / RT to close map', w / 2, Math.min(h - 10, _bigY + _bigSize + 24));
  ctx.textAlign = 'left';
}

export function renderMinimap(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  w: number,
  _h: number,
  overrideX?: number,
  overrideY?: number,
  overrideSize?: number,
): void {
  const WS = state.map.WORLD_SIZE;
  const { roomMap } = state.map;
  const size = overrideSize ?? 150;
  const padM = 16;
  const mx = overrideX ?? (w - padM - size);
  const my = overrideY ?? 80;
  const sc = size / WS;
  // BETA 22.09 — marker scale: dots/chests/arrow grow with the map size, so the BIG (held)
  // map shows big, readable icons instead of the tiny corner-minimap dots (Dylan: "chests too
  // small, clickable items too small, red arrow needs to be prominent").
  const mk = Math.max(1, size / 240);

  // BETA 22.95 — FOG-OF-WAR DELETED (Dylan: "because the map will be
  // infinity we dont have to black it out anymore"). Everything is always
  // revealed; the corner minimap is a CIRCLE now. isRevealed kept as a
  // constant so the call sites below stay untouched.
  const isRevealed = (_wx: number, _wy: number): number => 1;
  const isCircle = overrideSize == null;   // corner map = circle; big TAB map stays square
  const mcx = mx + size / 2, mcy = my + size / 2;

  ctx.save();
  if (isCircle) {
    ctx.beginPath();
    ctx.arc(mcx, mcy, size / 2, 0, Math.PI * 2);
    ctx.clip();
  }

  // Draw background
  ctx.fillStyle = '#a19f9d';
  ctx.fillRect(mx, my, size, size);

  // Rooms — only draw if revealed
  for (const room of roomMap.rooms) {
    const rcx = room.x + room.size / 2;
    const rcy = room.y + room.size / 2;
    const rev = isRevealed(rcx, rcy);
    if (rev <= 0) continue;

    const rx = mx + room.x * sc;
    const ry = my + room.y * sc;
    const rw = room.size * sc;
    const rh = room.size * sc;

    if (room.isBoss) ctx.fillStyle = `rgba(200,50,0,${0.7 * rev})`;
    else ctx.fillStyle = `rgba(70,65,60,${0.6 * rev})`;
    ctx.fillRect(rx, ry, Math.max(rw, 2), Math.max(rh, 2));
  }

  // Shrines — circles, bright colors, dark outline for visibility
  for (const shrine of roomMap.shrines) {
    const rev = isRevealed(shrine.x, shrine.y);
    if (rev <= 0) continue;
    const sx = mx + shrine.x * sc;
    const sy = my + shrine.y * sc;
    // BETA 22.10 — actual shrine sprite on the BIG map; colored dot fallback on the corner map.
    const _sUrl = mk >= 1.6 ? getShrineSprite(shrine) : null;
    const _sImg = _sUrl ? getCachedImage(_sUrl) : null;
    if (_sImg && _sImg.complete && _sImg.naturalWidth > 0) {
      const _isz = 15 * mk;
      ctx.globalAlpha = rev;
      ctx.drawImage(_sImg, sx - _isz / 2, sy - _isz / 2, _isz, _isz);
      ctx.globalAlpha = 1;
    } else {
      // BETA 22.94 — shrines are BLUE on the minimap too (were green).
      const baseColor = shrine.golden
        ? (shrine.state === 'spent_lit' ? [255,215,0] : shrine.state === 'channeling' ? [255,230,80] : [200,160,0])
        : (shrine.state === 'spent_lit' ? [95,201,255] : shrine.state === 'channeling' ? [140,225,255] : [180,180,180]);
      ctx.fillStyle = `rgba(0,0,0,${rev})`;
      ctx.beginPath();
      ctx.arc(sx, sy, 3.5 * mk, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = `rgba(${baseColor[0]},${baseColor[1]},${baseColor[2]},${rev})`;
      ctx.beginPath();
      ctx.arc(sx, sy, 2.5 * mk, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // Chests — larger squares, bright saturated colors, dark outline
  for (const chest of roomMap.chests) {
    if (chest.state === 'gone') continue;
    const rev = isRevealed(chest.x, chest.y);
    if (rev <= 0) continue;
    const cx = mx + chest.x * sc;
    const cy = my + chest.y * sc;
    // BETA 22.10 — Dylan: "use the same sprites for them." On the BIG (held) map draw the actual
    // chest sprite; fall back to a bright colored square on the corner map (or while it loads).
    const _cUrl = mk >= 1.6 ? getMachineSprite(chest) : null;
    const _cImg = _cUrl ? getCachedImage(_cUrl) : null;
    if (_cImg && _cImg.complete && _cImg.naturalWidth > 0) {
      const _isz = 15 * mk;
      ctx.globalAlpha = rev;
      ctx.drawImage(_cImg, cx - _isz / 2, cy - _isz / 2, _isz, _isz);
      ctx.globalAlpha = 1;
    } else {
      const chestSize = 3.2 * mk;   // BETA 22.09 — scale with map size
      const _co = Math.max(1, mk);
      ctx.fillStyle = `rgba(0,0,0,${rev})`;
      ctx.fillRect(cx - chestSize / 2 - _co, cy - chestSize / 2 - _co, chestSize + _co * 2, chestSize + _co * 2);
      if (chest.type === 'gold') ctx.fillStyle = `rgba(255,220,0,${rev})`;
      else if (chest.type === 'elite') ctx.fillStyle = `rgba(0,229,255,${rev})`;
      else ctx.fillStyle = `rgba(50,200,255,${rev})`;
      ctx.fillRect(cx - chestSize / 2, cy - chestSize / 2, chestSize, chestSize);
    }
  }

  // Portal — only show if discovered AND revealed
  if (state.portal && state.portalDiscovered) {
    const rev = isRevealed(state.portal.x, state.portal.y);
    if (rev > 0) {
      ctx.fillStyle = `rgba(255,102,0,${rev})`;
      ctx.beginPath();
      ctx.arc(mx + state.portal.x * sc, my + state.portal.y * sc, 3, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // BETA 22.95 — fog overlay DELETED (infinite-map direction; nothing to
  // black out). The cached fog canvas machinery is gone with it.

  // BETA 22.95 — Dylan: "minimap … showing enemies". EVERY alive enemy gets
  // a dot now: regular = small red, elite = gold, boss = big red. (22.09
  // only showed elites/bosses.) Scaled with the map; off-map points clipped.
  {
    const _ess = getEnemySystemState();
    if (_ess?.enemies) {
      for (const _en of _ess.enemies) {
        if (!_en.alive) continue;
        const _ex = mx + _en.x * sc, _ey = my + _en.y * sc;
        if (_ex < mx || _ex > mx + size || _ey < my || _ey > my + size) continue;
        const _boss = _en.variant === 'boss';
        const _elite = _en.variant === 'elite';
        if (_boss || _elite) {
          const _r = (_boss ? 3.4 : 2.4) * mk;
          ctx.fillStyle = '#000';
          ctx.beginPath(); ctx.arc(_ex, _ey, _r + 1.3 * mk, 0, Math.PI * 2); ctx.fill();
          ctx.fillStyle = _boss ? '#ff2a2a' : '#ffd11a';
          ctx.beginPath(); ctx.arc(_ex, _ey, _r, 0, Math.PI * 2); ctx.fill();
        } else {
          ctx.fillStyle = 'rgba(255,64,52,0.85)';
          ctx.beginPath(); ctx.arc(_ex, _ey, 1.3 * mk, 0, Math.PI * 2); ctx.fill();
        }
      }
    }
  }

  // Extraction port marker (BETA 22.43) — a MAGENTA diamond, shown ONLY once the
  // port has opened AND its tile is revealed on the fog (Dylan: "if the extraction
  // appears anywhere on the map where I've uncovered, it should show on the map").
  // Magenta (#ff2bd6) is used nowhere else on the HUD, so it reads instantly; the
  // diamond shape sets it apart from chest squares and shrine/threat circles.
  // Drawn AFTER the fog overlay so it stays crisp; turns red when armed (matches
  // the in-world port). The port spawns on your start tile, so it's effectively
  // always revealed — this is the "find your way back" beacon.
  if (state.extractionPort) {
    const _epRev = isRevealed(state.extractionPort.x, state.extractionPort.y);
    if (_epRev > 0) {
      const _epx = mx + state.extractionPort.x * sc;
      const _epy = my + state.extractionPort.y * sc;
      const _epArmed = state.extractionArmedAt != null;
      const _epR = 4 * mk;
      const _epOut = 1.4 * mk;
      ctx.save();
      ctx.translate(_epx, _epy);
      ctx.rotate(Math.PI / 4); // 45° → diamond
      ctx.fillStyle = '#000';
      ctx.fillRect(-_epR - _epOut, -_epR - _epOut, (_epR + _epOut) * 2, (_epR + _epOut) * 2);
      ctx.fillStyle = _epArmed ? '#ff5a5a' : '#ff2bd6';
      ctx.fillRect(-_epR, -_epR, _epR * 2, _epR * 2);
      ctx.restore();
    }
  }

  // BETA 16.33 — Player marker = RED ARROW pointing the direction the
  // player is facing. Per Dylan: "we should be a red arrow on the mni
  // map in the top right corner pointingin the direction we are looking
  // not a little green dot or whatever." Was: white-ringed green dot.
  const px = mx + state.player.x * sc;
  const py = my + state.player.y * sc;
  const aimAngle = getLastFacingAngle();
  // BETA 22.09 — Dylan: "i want the red arrow on me to be prominent." Bigger arrow (scaled with
  // the map) + a soft white halo so it pops on any fog tone, especially on the big held map.
  const _a = mk * 1.35;
  ctx.save();
  ctx.translate(px, py);
  ctx.fillStyle = 'rgba(255,255,255,0.40)';
  ctx.beginPath();
  ctx.arc(0, 0, 5 * mk, 0, Math.PI * 2);
  ctx.fill();
  ctx.rotate(aimAngle);
  // Arrow shape: triangle pointing along +X (facing direction, post-rotate).
  ctx.beginPath();
  ctx.moveTo(7 * _a, 0);        // tip
  ctx.lineTo(-4 * _a, -4 * _a); // back-left
  ctx.lineTo(-2 * _a, 0);       // notch
  ctx.lineTo(-4 * _a, 4 * _a);  // back-right
  ctx.closePath();
  // Black outline so the arrow stays visible on any fog tone
  ctx.fillStyle = '#ff2222';
  ctx.strokeStyle = '#000';
  ctx.lineWidth = Math.max(1.2, 1.2 * mk);
  ctx.fill();
  ctx.stroke();
  ctx.restore();

  // BETA 22.95 — close the circular clip + draw the round rim (corner map).
  ctx.restore();
  if (isCircle) {
    ctx.strokeStyle = '#39ff14';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(mcx, mcy, size / 2, 0, Math.PI * 2);
    ctx.stroke();
  } else {
    ctx.strokeStyle = '#39ff14';
    ctx.lineWidth = 1;
    ctx.strokeRect(mx, my, size, size);
  }
}

// Offscreen canvas for minimap fog overlay (cached)
// BETA 13.87 FPS: also cache ImageData buffer + dirty flag so we only rebuild
// when fog actually changed (set by minimapFog mutation site near line 1089).
// BETA 22.95 — minimap fog canvas machinery deleted with the fog itself.
let minimapFogDirty = true; // still written by updateMinimapFog; render ignores it

function renderDebug(ctx: CanvasRenderingContext2D, state: GameState, _w: number, _h: number) {
  const { player } = state;
  const { roomMap } = state.map;
  const elapsed = Math.floor(state.time);
  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;

  const lines = [
    `SEED: ${state.map.seed}`,
    `ROOMS: ${roomMap.rooms.length}`,
    `SHRINES: ${roomMap.shrines.length}`,
    `CHESTS: ${roomMap.chestCount} (${roomMap.goldChestCount} gold)`,
    `TIME: ${mins}:${secs.toString().padStart(2, '0')}`,
    `POS: (${Math.floor(player.x)}, ${Math.floor(player.y)})`,
    `ROOM: ${player.currentRoom ? player.currentRoom.displayName : 'OPEN GROUND'}`,
    `GOLD: ${player.gold}`,
    `ZOOM: ${state.camera.zoom.toFixed(1)}x`,
    `PARTICLES: ${state.particles.length}`,
    `HP: ${player.hp}/${player.maxHp}`,
    `LV: ${player.level} XP: ${player.xp}/${player.xpToNext}`,
    `WAVE: ${state.wave} KILLS: ${state.killCount}`,
    `IMAGES: ${imageCache.size} loaded, ${imageLoading.size} loading`,
  ];

  ctx.save();
  ctx.font = '13px "Share Tech Mono", monospace';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  const lh = 18;
  const pw = 320;
  const ph = lines.length * lh + 16;
  const px = 16;
  const py = 100;

  ctx.fillStyle = 'rgba(5,5,8,0.92)';
  ctx.strokeStyle = '#39ff14';
  ctx.lineWidth = 1;
  roundRect(ctx, px, py, pw, ph, 4);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = '#39ff14';
  lines.forEach((line, i) => {
    ctx.fillText(line, px + 8, py + 8 + i * lh);
  });
  ctx.restore();
}

function renderInteractPrompt(ctx: CanvasRenderingContext2D, state: GameState, w: number, h: number) {
  ctx.save();
  ctx.globalAlpha = state.interactPromptAlpha;

  // BETA 16.80 — Per Dylan: "still says E to smash when using controller.
  // or E to activate chest, or E to activate chick magnet." When a
  // controller is connected, replace the keyboard "[E]" prefix in any
  // interact prompt with "[A]" (Xbox primary action button). Done at
  // RENDER time so we don't have to thread input mode through every
  // call site that builds prompt text.
  const _rawText = state.interactPromptText || '[E] Interact';
  // BETA 16.82 — Per Dylan: "X is the action button to activate anything
  // while playing the game. why does it say press A to activate chick
  // magent?" The in-game interact button moved to X in this version, so
  // the prompt now rewrites [E] → [X], not [A].
  const text = _hasGamepadConnected() ? _rawText.replace(/^\[E\]/, '[X]') : _rawText;
  ctx.font = 'bold 20px "Share Tech Mono", monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const tw = ctx.measureText(text).width;
  const px = w / 2;
  const py = h - 120;

  const promptColor = state.interactTarget === 'chest' ? '#00bfff'
    : state.interactTarget === 'boss_shrine' ? '#ff4400'
    : '#ff6600';

  // BETA 13.87 FPS: faux-glow rect replaces ctx.shadowBlur (was firing every
  // frame the prompt was visible — significant cost on lower-end GPUs).
  ctx.globalAlpha = 0.3 * state.interactPromptAlpha;
  ctx.fillStyle = promptColor;
  roundRect(ctx, px - tw / 2 - 30, py - 26, tw + 60, 52, 9);
  ctx.fill();
  ctx.globalAlpha = state.interactPromptAlpha;

  ctx.fillStyle = 'rgba(5,5,8,0.9)';
  ctx.strokeStyle = promptColor;
  ctx.lineWidth = 2;
  roundRect(ctx, px - tw / 2 - 24, py - 20, tw + 48, 40, 6);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = promptColor;
  ctx.fillText(text, px, py);
  ctx.restore();
}

// ─── Debug Nav Overlay ────────────────────────────────────────────────────────
function _renderDebugNavOverlay(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  _w: number,
  _h: number,
): void {
  const { camera } = state;
  const rooms = state.map?.roomMap?.rooms ?? [];
  const zoom  = camera.zoom;

  // world-to-screen helpers
  const sx = (wx: number) => (wx - camera.x) * zoom + _w / 2;
  const sy = (wy: number) => (wy - camera.y) * zoom + _h / 2;
  const sp = (wu: number) => wu * zoom;

  ctx.save();
  ctx.textAlign   = 'center';
  ctx.textBaseline = 'middle';

  if (debugNavOverlay) {
    for (const room of rooms) {
      const rx = room.x, ry = room.y, rs = _RS;

      // ── Room outline
      ctx.strokeStyle = 'rgba(0,200,255,0.4)';
      ctx.lineWidth   = 1;
      ctx.strokeRect(sx(rx), sy(ry), sp(rs), sp(rs));

      // ── 3-Zone Collision Overlay (RED / BLUE / GREEN) ──
      // Vector-drawn paths for perfectly smooth curves at any zoom level.
      // Uses the exact same geometry as classifyPoint() in roomCollisionGrid.ts.

      const W = _WALL, O = _OUTER, CL = CROSS_LEFT, CR = CROSS_RIGHT, CT = CROSS_TOP, CB = CROSS_BOTTOM;
      const CLP = _CLIP;
      const rxW = CL - W;  // 204 — ellipse x-radius
      const ryH = CT - W;  // 220 — ellipse y-radius
      const rxE = O - CR;  // 204
      const ryS = O - CB;  // 220

      // ── RED: Full room background (walls) ──
      ctx.fillStyle = 'rgba(255,40,40,0.55)';
      ctx.fillRect(sx(rx), sy(ry), sp(768), sp(768));

      // ── BLUE: Center cross (overwrites red in the interior) ──
      ctx.fillStyle = 'rgba(40,120,255,0.35)';
      // Horizontal bar
      ctx.fillRect(sx(rx + W), sy(ry + CT), sp(O - W), sp(CB - CT));
      // Vertical bar
      ctx.fillRect(sx(rx + CL), sy(ry + W), sp(CR - CL), sp(O - W));

      // ── BLUE: 4 corner quadrants (the rounded blue area outside the ellipse arcs) ──
      // NW blue corner: rectangle minus the green ellipse
      // Draw the full rectangle as blue, then the green ellipse on top
      ctx.fillStyle = 'rgba(40,120,255,0.35)';
      ctx.fillRect(sx(rx + W), sy(ry + W), sp(CL - W), sp(CT - W));
      ctx.fillRect(sx(rx + CR), sy(ry + W), sp(O - CR), sp(CT - W));
      ctx.fillRect(sx(rx + CR), sy(ry + CB), sp(O - CR), sp(O - CB));
      ctx.fillRect(sx(rx + W), sy(ry + CB), sp(CL - W), sp(O - CB));

      // ── GREEN INTERIOR ELLIPSES — REMOVED IN BETA 15.39 ──
      // The 4 quarter-ellipse arc render blocks were removed when 15.38
      // deleted the underlying classifyPoint zones. No more green interior
      // overlay in the dev/debug 3-zone view. Exterior green curves still
      // render below via the exteriorGreenCurves loop (untouched).

      // ── RED CORNER CLIP TRIANGLES — REMOVED IN BETA 15.40 ──
      // The 4 fill blocks (NW/NE/SE/SW 48wu right-triangles) were removed
      // when classifyPoint stopped marking these as 'red'. Room walls are
      // now ONLY the rectangular wall band + doorway gaps.

      // ── BLUE: Doorway trapezoids (drawn on top of red walls) ──
      ctx.fillStyle = 'rgba(40,120,255,0.35)';
      // North doorway
      ctx.beginPath();
      ctx.moveTo(sx(rx + N_OUT_L), sy(ry));
      ctx.lineTo(sx(rx + N_OUT_R), sy(ry));
      ctx.lineTo(sx(rx + N_IN_R), sy(ry + W));
      ctx.lineTo(sx(rx + N_IN_L), sy(ry + W));
      ctx.closePath();
      ctx.fill();
      // South doorway
      ctx.beginPath();
      ctx.moveTo(sx(rx + S_IN_L), sy(ry + O));
      ctx.lineTo(sx(rx + S_IN_R), sy(ry + O));
      ctx.lineTo(sx(rx + S_OUT_R), sy(ry + 768));
      ctx.lineTo(sx(rx + S_OUT_L), sy(ry + 768));
      ctx.closePath();
      ctx.fill();
      // West doorway
      ctx.beginPath();
      ctx.moveTo(sx(rx), sy(ry + W_OUT_T));
      ctx.lineTo(sx(rx + W), sy(ry + W_IN_T));
      ctx.lineTo(sx(rx + W), sy(ry + W_IN_B));
      ctx.lineTo(sx(rx), sy(ry + W_OUT_B));
      ctx.closePath();
      ctx.fill();
      // East doorway
      ctx.beginPath();
      ctx.moveTo(sx(rx + O), sy(ry + E_IN_T));
      ctx.lineTo(sx(rx + 768), sy(ry + E_OUT_T));
      ctx.lineTo(sx(rx + 768), sy(ry + E_OUT_B));
      ctx.lineTo(sx(rx + O), sy(ry + E_IN_B));
      ctx.closePath();
      ctx.fill();
      // Draw zone boundary outlines for clarity
      // Wall inner edge rectangle (RED/interior boundary)
      ctx.strokeStyle = 'rgba(255,60,60,0.7)';
      ctx.lineWidth = 1.5;
      ctx.strokeRect(sx(rx + _WALL), sy(ry + _WALL), sp(_OUTER - _WALL), sp(_OUTER - _WALL));
      // Blue cross outline
      ctx.strokeStyle = 'rgba(60,140,255,0.5)';
      ctx.lineWidth = 1;
      // Horizontal bar
      ctx.strokeRect(sx(rx + _WALL), sy(ry + CROSS_TOP), sp(_OUTER - _WALL), sp(CROSS_BOTTOM - CROSS_TOP));
      // Vertical bar
      ctx.strokeRect(sx(rx + CROSS_LEFT), sy(ry + _WALL), sp(CROSS_RIGHT - CROSS_LEFT), sp(_OUTER - _WALL));
      // Corner clip triangles outline — REMOVED IN BETA 15.40
      // (matches the fill removal above; corners no longer classify as red)
      // Doorway trapezoid outlines (cyan)
      // North
      ctx.beginPath();
      ctx.moveTo(sx(rx + N_OUT_L), sy(ry));
      ctx.lineTo(sx(rx + N_OUT_R), sy(ry));
      ctx.lineTo(sx(rx + N_IN_R), sy(ry + _WALL));
      ctx.lineTo(sx(rx + N_IN_L), sy(ry + _WALL));
      ctx.closePath();
      ctx.strokeStyle = 'rgba(0,255,255,0.7)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      // South
      ctx.beginPath();
      ctx.moveTo(sx(rx + S_IN_L), sy(ry + _OUTER));
      ctx.lineTo(sx(rx + S_IN_R), sy(ry + _OUTER));
      ctx.lineTo(sx(rx + S_OUT_R), sy(ry + 768));
      ctx.lineTo(sx(rx + S_OUT_L), sy(ry + 768));
      ctx.closePath();
      ctx.strokeStyle = 'rgba(0,255,255,0.7)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      // West
      ctx.beginPath();
      ctx.moveTo(sx(rx), sy(ry + W_OUT_T));
      ctx.lineTo(sx(rx + _WALL), sy(ry + W_IN_T));
      ctx.lineTo(sx(rx + _WALL), sy(ry + W_IN_B));
      ctx.lineTo(sx(rx), sy(ry + W_OUT_B));
      ctx.closePath();
      ctx.strokeStyle = 'rgba(0,255,255,0.7)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      // East
      ctx.beginPath();
      ctx.moveTo(sx(rx + _OUTER), sy(ry + E_IN_T));
      ctx.lineTo(sx(rx + 768), sy(ry + E_OUT_T));
      ctx.lineTo(sx(rx + 768), sy(ry + E_OUT_B));
      ctx.lineTo(sx(rx + _OUTER), sy(ry + E_IN_B));
      ctx.closePath();
      ctx.strokeStyle = 'rgba(0,255,255,0.7)';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // ── Gap dots — actual gap centers from room.gaps[], shown as filled blue dots
      // These are the exact points the doorway magnet snaps the bot toward.
      for (const g of (room.gaps ?? [])) {
        const gcx = g.x + g.w / 2;
        const gcy = g.y + g.h / 2;
        ctx.beginPath();
        ctx.arc(sx(gcx), sy(gcy), sp(10), 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(30, 140, 255, 0.9)';
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.7)';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        // Label with direction
        const label = g.dir ? g.dir[0].toUpperCase() : '?';
        ctx.fillStyle = '#fff';
        ctx.font = `bold ${Math.max(7, sp(8))}px monospace`;
        ctx.fillText(label, sx(gcx), sy(gcy));
      }

      // ── Doorway magnet dots (orange) — inner waypoints where entities steer through
      for (const mid of DOORWAY_INNER_MIDPOINTS) {
        const mx = rx + mid.x;
        const my = ry + mid.y;
        ctx.beginPath();
        ctx.arc(sx(mx), sy(my), sp(12), 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,160,0,0.85)';
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.8)';
        ctx.lineWidth = 2;
        ctx.stroke();
        // Label
        ctx.fillStyle = '#fff';
        ctx.font = `bold ${Math.max(7, sp(8))}px monospace`;
        ctx.fillText(mid.dir[0].toUpperCase(), sx(mx), sy(my));
      }

    }
  }

  // ── Exterior green curves (convex, concave, straight, diagonal bridges) ──
  if (debugNavOverlay) {
    for (const curve of state.map.roomMap.exteriorGreenCurves ?? []) {
      const poly = curve.polygon;
      if (poly.length < 3) continue;
      // Fill
      ctx.beginPath();
      ctx.moveTo(sx(poly[0].x), sy(poly[0].y));
      for (let pi = 1; pi < poly.length; pi++) {
        ctx.lineTo(sx(poly[pi].x), sy(poly[pi].y));
      }
      ctx.closePath();
      ctx.fillStyle = curve.type === 'diagonal_bridge'
        ? 'rgba(0,200,255,0.40)'
        : 'rgba(40,255,40,0.45)';
      ctx.fill();
      // Stroke
      ctx.beginPath();
      ctx.moveTo(sx(poly[0].x), sy(poly[0].y));
      for (let pi = 1; pi < poly.length; pi++) {
        ctx.lineTo(sx(poly[pi].x), sy(poly[pi].y));
      }
      ctx.closePath();
      ctx.strokeStyle = curve.type === 'diagonal_bridge'
        ? 'rgba(0,200,255,0.7)'
        : 'rgba(40,220,40,0.7)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  }

  // ── Enemy waypoint arrows (green)
  if (debugShowWaypoints && enemiesModule) {
    const enemies = enemiesModule.getEnemies?.() ?? [];
    const debugEns = (enemiesModule as any).getDebugEnemyNav?.() as Array<{ x: number; y: number; wpX: number; wpY: number }> | undefined;
    if (debugEns) {
      for (const e of debugEns) {
        if (e.wpX === 0 && e.wpY === 0) continue;
        ctx.beginPath();
        ctx.moveTo(sx(e.x), sy(e.y));
        ctx.lineTo(sx(e.wpX), sy(e.wpY));
        ctx.strokeStyle = 'rgba(0,255,100,0.7)';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(sx(e.wpX), sy(e.wpY), sp(10), 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0,255,100,0.5)';
        ctx.fill();
      }
    } else {
      // Fallback: just draw dots at enemy positions
      for (const e of enemies) {
        ctx.beginPath();
        ctx.arc(sx(e.x), sy(e.y), sp(8), 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0,255,100,0.3)';
        ctx.fill();
      }
    }
  }

  ctx.restore();
}

// ─── Flow Field Debug Overlay ──────────────────────────────────────────────────
function _renderFlowFieldOverlay(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  w: number,
  h: number,
): void {
  if (!isFlowFieldReady()) return;
  const { camera } = state;
  const zoom = camera.zoom;
  const sx = (wx: number) => (wx - camera.x) * zoom + w / 2;
  const sy = (wy: number) => (wy - camera.y) * zoom + h / 2;
  const sp = (wu: number) => wu * zoom;

  // Only render cells visible on screen (performance)
  const halfW = w / (2 * zoom);
  const halfH = h / (2 * zoom);
  const minCX = Math.max(0, Math.floor((camera.x - halfW) / FF_CELL));
  const minCY = Math.max(0, Math.floor((camera.y - halfH) / FF_CELL));
  const maxCX = Math.min(FF_GW - 1, Math.ceil((camera.x + halfW) / FF_CELL));
  const maxCY = Math.min(FF_GH - 1, Math.ceil((camera.y + halfH) / FF_CELL));

  ctx.save();

  if (debugFlowFieldMode === 'cost') {
    // ── Cost Field: show blocked (red), high-cost (yellow), walkable (transparent)
    const cost = getCostField();
    if (!cost) { ctx.restore(); return; }
    for (let cy = minCY; cy <= maxCY; cy++) {
      for (let cx = minCX; cx <= maxCX; cx++) {
        const idx = cy * FF_GW + cx;
        const c = cost[idx];
        if (c === 1) continue; // walkable — skip
        const screenX = sx(cx * FF_CELL);
        const screenY = sy(cy * FF_CELL);
        const size = sp(FF_CELL);
        if (c === 255) {
          ctx.fillStyle = 'rgba(255,0,0,0.35)';
        } else if (c >= 128) {
          ctx.fillStyle = 'rgba(255,200,0,0.3)';
        } else {
          ctx.fillStyle = 'rgba(100,100,255,0.2)';
        }
        ctx.fillRect(screenX, screenY, size, size);
      }
    }
  } else if (debugFlowFieldMode === 'heat') {
    // ── Heat Map: color by distance to player (green=close, red=far)
    const intField = getIntegrationField();
    if (!intField) { ctx.restore(); return; }
    // Find max distance in view for normalization
    let maxDist = 1;
    for (let cy = minCY; cy <= maxCY; cy++) {
      for (let cx = minCX; cx <= maxCX; cx++) {
        const d = intField[cy * FF_GW + cx];
        if (d < 1e8 && d > maxDist) maxDist = d;
      }
    }
    for (let cy = minCY; cy <= maxCY; cy++) {
      for (let cx = minCX; cx <= maxCX; cx++) {
        const idx = cy * FF_GW + cx;
        const d = intField[idx];
        if (d >= 1e8) continue; // unreachable
        const t = Math.min(1, d / maxDist); // 0=close, 1=far
        const r = Math.round(t * 255);
        const g = Math.round((1 - t) * 255);
        const screenX = sx(cx * FF_CELL);
        const screenY = sy(cy * FF_CELL);
        const size = sp(FF_CELL);
        ctx.fillStyle = `rgba(${r},${g},0,0.3)`;
        ctx.fillRect(screenX, screenY, size, size);
      }
    }
  } else {
    // ── Arrows mode: draw direction arrows on every Nth cell
    const vectors = getFlowVectors();
    if (!vectors) { ctx.restore(); return; }
    // Skip cells for performance — draw every 2nd or 3rd cell depending on zoom
    const skip = zoom > 0.6 ? 2 : zoom > 0.3 ? 4 : 8;
    const arrowLen = sp(FF_CELL * 0.7);
    ctx.strokeStyle = 'rgba(0,200,255,0.7)';
    ctx.fillStyle = 'rgba(0,200,255,0.7)';
    ctx.lineWidth = 1.5;
    for (let cy = minCY; cy <= maxCY; cy += skip) {
      for (let cx = minCX; cx <= maxCX; cx += skip) {
        const idx = cy * FF_GW + cx;
        const dx = vectors.x[idx];
        const dy = vectors.y[idx];
        if (dx === 0 && dy === 0) continue; // no direction
        // Normalize (they're already -127 to 127 int8)
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        const ndx = dx / len;
        const ndy = dy / len;
        const centerX = sx((cx + 0.5) * FF_CELL);
        const centerY = sy((cy + 0.5) * FF_CELL);
        const tipX = centerX + ndx * arrowLen * 0.5;
        const tipY = centerY + ndy * arrowLen * 0.5;
        const tailX = centerX - ndx * arrowLen * 0.5;
        const tailY = centerY - ndy * arrowLen * 0.5;
        // Draw arrow line
        ctx.beginPath();
        ctx.moveTo(tailX, tailY);
        ctx.lineTo(tipX, tipY);
        ctx.stroke();
        // Draw arrowhead
        const headLen = arrowLen * 0.3;
        const angle = Math.atan2(ndy, ndx);
        ctx.beginPath();
        ctx.moveTo(tipX, tipY);
        ctx.lineTo(tipX - headLen * Math.cos(angle - 0.5), tipY - headLen * Math.sin(angle - 0.5));
        ctx.lineTo(tipX - headLen * Math.cos(angle + 0.5), tipY - headLen * Math.sin(angle + 0.5));
        ctx.closePath();
        ctx.fill();
      }
    }
  }

  // ── BETA 14.87 — Enemy heading lines use ACTUAL velocity (smooth) instead of
  // cell-snapped flow-field direction (jumpy). User: "red lines are still
  // flickering. find it!!!!! no guessing." Root cause: flow field is on a
  // cell grid that rebuilds ~every 0.6s; getFlowDirection() returns the cell-
  // aligned vector at the enemy's current position, so as the enemy moves
  // across cells (or the field rebuilds), the angle jumps. Velocity is the
  // enemy's smooth per-frame movement → no flicker.
  // ALSO: show hitbox circle + sprite outline + contactCooldown countdown per
  // enemy so user can SEE the touch range vs cooldown gate. Color-coded:
  //   GREEN ring  = in touchDist with cooldown <=0 (will deal damage NOW)
  //   RED   ring  = in touchDist with cooldown >0 (cooldown gating attack)
  //   WHITE ring  = not in touchDist (no damage possible)
  // We grab the FULL EnemyInstance via getEnemySystemState() so we have access
  // to velocityX/velocityY/contactCooldownRemaining/spriteSize/hitboxRadius —
  // the simplified `Enemy` shape from getEnemies() omits those fields.
  {
    const enemyState = getEnemySystemState();
    const enemies = enemyState?.enemies ?? [];
    const playerHB = state.player.size; // hitbox radius (=32 for player)
    ctx.lineWidth = 2;
    for (const e of enemies) {
      if (!e.alive) continue;
      const esx = sx(e.x);
      const esy = sy(e.y);
      const enemyHB = e.hitboxRadius;

      // BETA 14.88 — Red line always points AT THE BOT (direction from enemy to
      // player). User: "all red lines on eemies should be pointing at the bot
      // at all times right?" Yes — the line shows "this enemy is targeting me",
      // smooth and accurate every frame regardless of flow field cell-snap or
      // velocity (which can deviate from target direction due to separation/
      // walls/retreating). Was velocity-based in 14.87; before that, flow-cell-
      // snapped (jumpy). Now: pure direction-to-player.
      const dxToBot = state.player.x - e.x;
      const dyToBot = state.player.y - e.y;
      const distToBot = Math.hypot(dxToBot, dyToBot) || 1;
      const dirX = dxToBot / distToBot;
      const dirY = dyToBot / distToBot;
      {
        const lineLen = sp(60);
        ctx.beginPath();
        ctx.moveTo(esx, esy);
        ctx.lineTo(esx + dirX * lineLen, esy + dirY * lineLen);
        ctx.strokeStyle = 'rgba(255,100,0,0.85)';
        ctx.stroke();
      }

      // Hitbox + cooldown ring
      const dxToPlayer = state.player.x - e.x;
      const dyToPlayer = state.player.y - e.y;
      const distToPlayer = Math.hypot(dxToPlayer, dyToPlayer);
      const touchDist = enemyHB + playerHB;
      const cd = e.contactCooldownRemaining;
      const inTouch = distToPlayer < touchDist;
      let ringColor: string;
      if (inTouch && cd <= 0) ringColor = 'rgba(80, 255, 80, 0.95)';   // ready to hit
      else if (inTouch)         ringColor = 'rgba(255, 80, 80, 0.95)';   // gated by cooldown
      else                      ringColor = 'rgba(180, 180, 180, 0.45)'; // idle
      ctx.strokeStyle = ringColor;
      ctx.lineWidth = inTouch ? 2 : 1;
      ctx.beginPath();
      ctx.arc(esx, esy, sp(enemyHB), 0, Math.PI * 2);
      ctx.stroke();

      // Sprite-edge dashed outline (helps user see sprite vs hitbox match)
      ctx.strokeStyle = 'rgba(255, 200, 0, 0.4)';
      ctx.setLineDash([3, 3]);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(esx, esy, sp(e.spriteSize / 2), 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);

      // If in touch range, show the cooldown countdown above the enemy
      if (inTouch) {
        ctx.fillStyle = ringColor;
        ctx.font = `bold ${sp(11)}px "Share Tech Mono", monospace`;
        ctx.textAlign = 'center';
        const label = cd <= 0 ? 'READY' : `CD ${cd.toFixed(2)}s`;
        ctx.fillText(label, esx, esy - sp(enemyHB) - sp(6));
      }

      // Small dot at enemy center
      ctx.beginPath();
      ctx.arc(esx, esy, sp(3), 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,100,0,0.7)';
      ctx.fill();
    }

    // Player hitbox (cyan circle = touch boundary from player center)
    const psx = sx(state.player.x);
    const psy = sy(state.player.y);
    ctx.strokeStyle = 'rgba(0, 220, 255, 0.85)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(psx, psy, sp(playerHB), 0, Math.PI * 2);
    ctx.stroke();
    // Player sprite outline (cyan dashed) for sprite-vs-hitbox comparison
    ctx.strokeStyle = 'rgba(0, 220, 255, 0.35)';
    ctx.setLineDash([4, 4]);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(psx, psy, sp((state.player.spriteSize ?? 64) / 2), 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // ── BETA 14.88 — Bot CURRENT MOVEMENT direction (cyan line). User asked
  // "what is the blue line on the bot?" — was showing the FLOW FIELD vector
  // at the bot's cell (which is meaningless info since the bot uses direct
  // steering primary, not flow field). Now shows the bot's actual velocity
  // direction (where it's heading THIS frame). Symmetrical with the red enemy
  // lines (which point at the bot — "enemies targeting me"); cyan line shows
  // "where I'm going". When the bot is stationary, line vanishes.
  {
    const pvx = (state.player as { vx?: number }).vx ?? 0;
    const pvy = (state.player as { vy?: number }).vy ?? 0;
    const pmag = Math.hypot(pvx, pvy);
    if (pmag > 1) {
      const psx = sx(state.player.x);
      const psy = sy(state.player.y);
      const lineLen = sp(80);
      ctx.beginPath();
      ctx.moveTo(psx, psy);
      ctx.lineTo(psx + (pvx / pmag) * lineLen, psy + (pvy / pmag) * lineLen);
      ctx.strokeStyle = 'rgba(0,255,255,0.9)';
      ctx.lineWidth = 3;
      ctx.stroke();
    }
  }

  // ── BETA 14.88 — Legend (top-right of canvas) explaining the lines. ──
  {
    ctx.save();
    ctx.font = `${sp(11)}px "Share Tech Mono", monospace`;
    ctx.textAlign = 'left';
    const lx = w - 220;
    let ly = 14;
    void h; // unused — placement is fixed top-right
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.fillRect(lx - 6, 6, 210, 90);
    ctx.fillStyle = 'rgba(255, 100, 0, 0.85)';
    ctx.fillText('━ red line  = enemy → bot', lx, ly += 14);
    ctx.fillStyle = 'rgba(0, 255, 255, 0.9)';
    ctx.fillText('━ cyan line = bot velocity', lx, ly += 14);
    ctx.fillStyle = 'rgba(80, 255, 80, 0.95)';
    ctx.fillText('● green ring = will hit NOW', lx, ly += 14);
    ctx.fillStyle = 'rgba(255, 80, 80, 0.95)';
    ctx.fillText('● red ring = cooldown gated', lx, ly += 14);
    ctx.fillStyle = 'rgba(255, 200, 0, 0.6)';
    ctx.fillText('… yellow dashed = sprite radius', lx, ly += 14);
    ctx.restore();
  }

  ctx.restore();
}

function renderWinScreen(ctx: CanvasRenderingContext2D, state: GameState, w: number, h: number) {
  ctx.save();
  ctx.fillStyle = 'rgba(0,0,0,0.88)';
  ctx.fillRect(0, 0, w, h);

  const runSecs = Math.floor(state.time);
  const mins    = Math.floor(runSecs / 60);
  const secs    = runSecs % 60;
  const t       = (Date.now() % 3000) / 3000; // looping 0-1 for glow pulse

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Title
  // BETA 13.87 FPS: pulsing alpha replaces ctx.shadowBlur (win screen now
  // pulses by drawing the title twice — once at low alpha as a glow halo,
  // once at full alpha for the sharp text — instead of the GPU-killing blur).
  const titleFontSize = Math.round(w * 0.052);
  ctx.font = `bold ${titleFontSize}px "Share Tech Mono", monospace`;
  const winPulse = 0.4 + 0.4 * Math.sin(t * Math.PI * 2);
  ctx.fillStyle = '#39ff14';
  // Glow halo: render title at 5px-larger size + low alpha (cheap glow effect)
  ctx.globalAlpha = 0.25 * winPulse;
  ctx.font = `bold ${titleFontSize + 6}px "Share Tech Mono", monospace`;
  ctx.fillText('FINAL BOSS DEFEATED', w / 2, h * 0.28);
  // Sharp title on top
  ctx.globalAlpha = 1.0;
  ctx.font = `bold ${titleFontSize}px "Share Tech Mono", monospace`;
  ctx.fillText('FINAL BOSS DEFEATED', w / 2, h * 0.28);

  ctx.font = `${Math.round(w * 0.022)}px "Share Tech Mono", monospace`;
  ctx.fillStyle = '#aaa';
  ctx.fillText('The boss has been defeated. The dungeon is clear.', w / 2, h * 0.38);

  // Stats
  const statColor = '#ccc';
  ctx.fillStyle = statColor;
  ctx.font = '20px "Share Tech Mono", monospace';
  const lineH  = h * 0.063;
  const startY = h * 0.50;
  const statRows = [
    [`TIME`,    `${mins}:${String(secs).padStart(2, '0')}`],
    [`LEVEL`,   String(state.player.level)],
    [`KILLS`,   String(state.killCount)],
    [`DAMAGE`,  Math.round(getRunDamageDealt()).toLocaleString()],
    [`CHESTS`,  String(state.chestsOpenedThisRun)],
  ];
  for (let i = 0; i < statRows.length; i++) {
    ctx.textAlign = 'right'; ctx.fillStyle = '#555';
    ctx.fillText(statRows[i][0] + ' :', w / 2 - 10, startY + i * lineH);
    ctx.textAlign = 'left';  ctx.fillStyle = '#fff';
    ctx.fillText(statRows[i][1], w / 2 + 10, startY + i * lineH);
  }

  ctx.textAlign = 'center';
  ctx.font = '16px "Share Tech Mono", monospace';
  ctx.fillStyle = `rgba(57,255,20,${0.4 + 0.3 * Math.sin(t * Math.PI * 2)})`;
  ctx.fillText('Press R to play again  |  Esc for menu', w / 2, h * 0.90);
  ctx.restore();
}

// ─── Compass Arrows (screen-edge indicators) ────────────────────────────
// Draws colored arrows at the edge of the screen pointing toward:
//   - Nearby chests (blue for regular, gold for gold chests)
//   - Boss portal (red, always visible, never goes away)
// Arrows orbit the screen edge in a circle, staying out of the player's way.

function renderCompassArrows(
  ctx: CanvasRenderingContext2D, state: GameState, w: number, h: number
) {
  const { player, camera } = state;
  const { roomMap } = state.map;
  const scale = camera.zoom;
  const offsetX = w / 2 - camera.x * scale;
  const offsetY = h / 2 - camera.y * scale;

  // Convert world position to screen position
  const toScreen = (wx: number, wy: number) => ({
    sx: wx * scale + offsetX,
    sy: wy * scale + offsetY,
  });

  // Check if a screen position is visible (with margin)
  const margin = 60;
  const isOnScreen = (sx: number, sy: number) =>
    sx >= -margin && sx <= w + margin && sy >= -margin && sy <= h + margin;

  // Arrow rendering config
  const arrowSize = 14;
  const edgePad = 30; // distance from screen edge

  // Collect targets: { wx, wy, color, priority }
  type ArrowTarget = { wx: number; wy: number; color: string; label?: string; size?: number };
  const targets: ArrowTarget[] = [];

  // Boss portal arrow (only after player has discovered the portal)
  if (state.portal && state.portalDiscovered) {
    targets.push({
      wx: state.portal.x,
      wy: state.portal.y,
      color: '#ff3300',
      label: 'BOSS',
    });
  }

  // Extraction port arrow (BETA 22.43) — MAGENTA, unlimited range. The moment the
  // escape hatch opens it points back to it from anywhere off-screen, so you can
  // always find your way to extract (Dylan: "an arrow that is always showing and
  // pointing to it, in a color we don't use yet"). #ff2bd6 is unused by every other
  // HUD arrow (boss red, chest gold/cyan/blue, threat red, throw orange).
  if (state.extractionPort) {
    targets.push({
      wx: state.extractionPort.x,
      wy: state.extractionPort.y,
      color: '#ff2bd6',
      label: 'EXTRACT',
      size: 22,
    });
  }

  // WildCardMachine arrows (only for chests within ~1200 world units of player)
  const CHEST_ARROW_RANGE = 1200;
  for (const chest of roomMap.chests) {
    if (chest.state === 'gone') continue;
    const dist = Math.hypot(chest.x - player.x, chest.y - player.y);
    // BETA 20.77 — Dylan: "blue chest... no large arrow for it." Elite
    // chests drop mid-fight and the player drifts far away before looking —
    // their arrow now has UNLIMITED range (like the boss portal) + a label.
    if (dist > CHEST_ARROW_RANGE && chest.type !== 'elite') continue;

    const { sx, sy } = toScreen(chest.x, chest.y);
    // Only show arrow if chest is NOT on screen (or barely off-screen)
    if (!isOnScreen(sx, sy)) {
      targets.push({
        wx: chest.x,
        wy: chest.y,
        color: chest.type === 'gold' ? '#ffd700' : (chest.type === 'elite' ? '#00e5ff' : '#00ccff'),
        size: chest.type === 'elite' ? 32 : undefined, // BETA 20.58 big blue → 20.77 bigger + labeled
        label: chest.type === 'elite' ? 'ELITE' : undefined,
      });
    }
  }

  // BETA 20.58 — BIG RED arrow for the ultra (mega-boss) chest. It lives on
  // state.ultraChest (NOT roomMap.chests), so it needs its own arrow target.
  if (state.ultraChest && !state.ultraChest.collected) {
    const _uc = toScreen(state.ultraChest.x, state.ultraChest.y);
    if (!isOnScreen(_uc.sx, _uc.sy)) {
      targets.push({ wx: state.ultraChest.x, wy: state.ultraChest.y, color: '#ff1e00', size: 32 });
    }
  }

  // BETA 16.84 — Off-screen incoming-threat red arrows (PSBOT cycle-30o
  // carry-forward). Per spec: when an enemy is actively attacking the
  // player from off-screen, point a red arrow at the screen edge so the
  // player has a chance to dodge. "Actively attacking" = winding up a
  // ranged throw OR close enough for direct contact damage. Range cap
  // 700wu — beyond that the threat is too distant to be considered
  // imminent. Cap arrows at 6 to avoid HUD clutter when hordes spawn.
  // Reads from getEnemySystemState() (full internal enemies with
  // throwWindupTimer + hitboxRadius) since state.enemies is the
  // simplified getEnemies() snapshot that omits those fields.
  {
    const _enemySys = getEnemySystemState();
    const _enemies = _enemySys?.enemies ?? [];
    const THREAT_RANGE = 700;
    const THREAT_RANGE_SQ = THREAT_RANGE * THREAT_RANGE;
    const THREAT_LIMIT = 6;
    let threatCount = 0;
    const playerHB = (player as unknown as { hitboxRadius?: number }).hitboxRadius ?? player.size ?? 32;
    for (let ei = 0; ei < _enemies.length && threatCount < THREAT_LIMIT; ei++) {
      const enemy = _enemies[ei];
      if (!enemy || !enemy.alive || enemy.currentHP <= 0) continue;
      const ex = enemy.x - player.x, ey = enemy.y - player.y;
      const distSq = ex * ex + ey * ey;
      if (distSq > THREAT_RANGE_SQ) continue;
      const isWindingUp = (enemy.throwWindupTimer ?? 0) > 0;
      const dist = Math.sqrt(distSq);
      const touchRange = (enemy.hitboxRadius ?? 18) + playerHB + 30;
      const isCloseAttacker = dist < touchRange;
      if (!isWindingUp && !isCloseAttacker) continue;
      const { sx, sy } = toScreen(enemy.x, enemy.y);
      if (isOnScreen(sx, sy)) continue;  // visible — no arrow needed
      targets.push({
        wx: enemy.x,
        wy: enemy.y,
        color: '#ff2222',  // bright red for incoming threat
      });
      threatCount++;
    }
  }

  // BETA 21.12 — Off-screen INCOMING-PROJECTILE arrows. THE fix for Dylan's
  // "demolished from things nowhere near me" (confirmed by run data: boss/Timmy
  // throws of 100-204 dmg were the killer). Diagnosis: throwers fire on distance
  // ALONE (maxRange 560-580 > the ~261wu visible half-height), so they throw from
  // OFF-SCREEN, and the projectile's red outline is suppressed while off-screen —
  // the throw is invisible until it's already on top of you. Point an orange arrow
  // at every off-screen enemy projectile heading toward you so you can SEE it and
  // dash/move. Pure feedback — zero damage change. Pairs with the new dash.
  {
    const _projs = getActiveEnemyProjectiles();
    const PROJ_ARROW_RANGE_SQ = 1600 * 1600;  // within max throw reach
    const PROJ_ARROW_LIMIT = 5;
    let projArrowCount = 0;
    for (let pi = 0; pi < _projs.length && projArrowCount < PROJ_ARROW_LIMIT; pi++) {
      const pr = _projs[pi];
      const pdx = player.x - pr.x, pdy = player.y - pr.y;
      const pdistSq = pdx * pdx + pdy * pdy;
      if (pdistSq > PROJ_ARROW_RANGE_SQ) continue;
      // Only flag projectiles actually APPROACHING (velocity points toward player).
      if (pr.vx * pdx + pr.vy * pdy <= 0) continue;
      const { sx, sy } = toScreen(pr.x, pr.y);
      if (isOnScreen(sx, sy)) continue;  // already visible — its on-screen outline covers it
      targets.push({
        wx: pr.x,
        wy: pr.y,
        color: '#ff8a1e',  // orange = incoming THROW (distinct from red enemy threat)
      });
      projArrowCount++;
    }
  }

  // Draw each arrow
  for (const target of targets) {
    const { sx, sy } = toScreen(target.wx, target.wy);

    // If target is on screen, skip arrow (except boss portal always shows)
    if (isOnScreen(sx, sy) && target.color !== '#ff3300') continue;
    // Boss portal: if on screen, don't draw arrow either
    if (isOnScreen(sx, sy) && target.color === '#ff3300') continue;

    // Calculate angle from screen center to target
    const cx = w / 2;
    const cy = h / 2;
    const angle = Math.atan2(sy - cy, sx - cx);

    // Find edge intersection point
    const halfW = w / 2 - edgePad;
    const halfH = h / 2 - edgePad;

    // Ray-box intersection
    let t = Infinity;
    const cosA = Math.cos(angle);
    const sinA = Math.sin(angle);
    if (cosA !== 0) t = Math.min(t, Math.abs(halfW / cosA));
    if (sinA !== 0) t = Math.min(t, Math.abs(halfH / sinA));

    const ax = cx + cosA * t;
    const ay = cy + sinA * t;

    // Clamp to screen bounds
    const px = Math.max(edgePad, Math.min(w - edgePad, ax));
    const py = Math.max(edgePad, Math.min(h - edgePad, ay));

    // Draw arrow triangle pointing toward target
    ctx.save();
    ctx.translate(px, py);
    ctx.rotate(angle);

    // BETA 13.88 FPS: removed ctx.shadowBlur=12 (target arrow fires every frame
    // when there's a chest/shrine target — was constantly active). Solid fill +
    // dark outline still gives good visibility.
    ctx.fillStyle = target.color;
    const _as = target.size ?? arrowSize; // BETA 20.58 — per-target arrow size (big blue/red chest arrows)
    ctx.beginPath();
    ctx.moveTo(_as, 0);                  // tip
    ctx.lineTo(-_as * 0.6, -_as * 0.6);  // top-left
    ctx.lineTo(-_as * 0.3, 0);           // notch
    ctx.lineTo(-_as * 0.6, _as * 0.6);   // bottom-left
    ctx.closePath();
    ctx.fill();

    // Outline for visibility
    ctx.strokeStyle = 'rgba(0,0,0,0.5)';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.restore();

    // Optional label for boss portal
    if (target.label) {
      ctx.save();
      ctx.font = 'bold 10px "Share Tech Mono", monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = target.color;
      // BETA 13.88 FPS: removed ctx.shadowBlur=6 from target label
      // Offset label slightly away from arrow
      const labelDist = 18;
      const lx = px - Math.cos(angle) * labelDist;
      const ly = py - Math.sin(angle) * labelDist;
      ctx.fillText(target.label, lx, ly);
      ctx.restore();
    }
  }
}

// ─── Utility ────────────────────────────────────────────────────────────

function drawContainCenter(ctx: CanvasRenderingContext2D, img: HTMLImageElement, boxX: number, boxY: number, boxW: number, boxH: number) {
  const scale = Math.min(boxW / img.naturalWidth, boxH / img.naturalHeight);
  const dw = img.naturalWidth * scale;
  const dh = img.naturalHeight * scale;
  ctx.drawImage(img, boxX + (boxW - dw) / 2, boxY + (boxH - dh) / 2, dw, dh);
}

/** BETA 22.89 — the TOKEN disc (Dylan: "a gold coin but with the flatness and
 *  ridges around the edges"). Drawn wherever the currency shows in UI. */
function drawTokenDisc(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, dim = false) {
  ctx.save();
  ctx.fillStyle = dim ? '#8a8a8a' : '#ffd700';
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill();
  ctx.strokeStyle = dim ? '#5f5f5f' : '#aa7700';
  ctx.lineWidth = Math.max(1.5, r * 0.16);
  ctx.beginPath(); ctx.arc(cx, cy, r * 0.94, 0, Math.PI * 2); ctx.stroke();
  ctx.beginPath();
  for (let k = 0; k < 10; k++) {
    const a = (k / 10) * Math.PI * 2 + 0.31;
    ctx.moveTo(cx + Math.cos(a) * r * 0.74, cy + Math.sin(a) * r * 0.74);
    ctx.lineTo(cx + Math.cos(a) * r * 0.94, cy + Math.sin(a) * r * 0.94);
  }
  ctx.lineWidth = Math.max(1, r * 0.10);
  ctx.stroke();
  ctx.beginPath(); ctx.arc(cx, cy, r * 0.5, 0, Math.PI * 2); ctx.stroke();
  ctx.restore();
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

// BETA 16.82 — Per Dylan: "still no focus boxes on shrine upgrades,
// chest upgrades, free and cost ones, and no focus boxes on level ups
// in xbox rog ally x in controller mode." Draws a clear, unmistakable
// FOCUS BOX (white outline + glow) AROUND a card so controller users
// know which card the dpad/stick is targeting. Distinct from the
// rarity glow that ALL cards already have. Only drawn on the focused
// card and only when a controller is connected.
function drawFocusBox(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number = 12) {
  // BETA 19.68 — PSBOT cycle-30ba Bug #3: Dylan: "it also hard to see the
  // white hit box due to common cards being white." Was white outer + amber
  // inner — invisible against common (white) cards AND conflicted with
  // legendary (gold/amber) glow. Switched to CYAN outer + white inner.
  // Cyan is never used as a rarity accent → pops against ALL card types
  // (common/uncommon/rare/epic/legendary).
  ctx.save();
  // Outer cyan halo — large blur so it reads as a glowing frame
  ctx.shadowColor = 'rgba(0, 255, 255, 0.95)';
  ctx.shadowBlur = 26;
  ctx.strokeStyle = '#00ffff';
  ctx.lineWidth = 5;
  roundRect(ctx, x, y, w, h, r);
  ctx.stroke();
  // Inner accent — thin definition line. BETA: was white (#ffffff) which
  // re-introduced a white line on the focus box; reuse the outer cyan focus
  // color so the box has no white at all.
  ctx.shadowBlur = 0;
  ctx.strokeStyle = '#00ffff';
  ctx.lineWidth = 2;
  roundRect(ctx, x + 5, y + 5, w - 10, h - 10, Math.max(2, r - 4));
  ctx.stroke();
  ctx.restore();
}

// BETA 16.86 → 16.89 — Per Dylan: "why is this not centered vertically
// or horrizontally????? i am using an ultra wide. not centered either
// way on 16:9 monitor. all chrines, level ups and chest upgrades need
// to be looked at." Two-step regression:
//   16.86 — Tried to center cards in the "play area" (right of chat
//           panel) by adding a HUD_LEFT=308 offset to startX. This
//           helped narrow Ally screens (where cards overlapped chat)
//           but pushed cards to the right of true center on every
//           wider monitor — most notably ultra-wide where 308px
//           shifts the whole row visibly off-center, AND cardY math
//           pinned cards to titleY+60 which puts them at the TOP of
//           the screen (not vertically centered).
//   16.89 — TRUE absolute centering, both axes:
//             startX = (w - totalW) / 2
//             cardY  = (h - cardH) / 2
//           Title moves with the cards (60px above the card top) so
//           the whole header + cards + footer block stays visually
//           connected and dead-center on any screen size / aspect
//           ratio (1280×720 ROG Ally → 3440×1440 ultra-wide).
// On narrow screens (<1400) the chat panel may briefly overlap the
// top of card 1, but the player has the Y button to hide HUD if
// needed. That's the right tradeoff vs. having the cards visibly
// off-center for every PC user.
// BETA 22.73 — computePickStatsScale DELETED (orphaned since the 22.62 stats-note rebuild).

export function computeChoiceCardLayout(
  w: number, h: number, n: number,
  hudHidden: boolean,
  type: 'levelup' | 'shrine' | 'chest'
): { cardW: number; cardH: number; gap: number; startX: number; cardY: number; titleY: number } {
  // BETA 22.60 — the card row RESERVES the columns that draw on top of the
  // pick screens: the enlarged stats panel on the left (Dylan's 20.26 live
  // stat-preview feature) and the inventory HUD on the right. The panel and
  // this layout derive from ONE formula (computePickStatsScale): on narrow
  // windows the PANEL shrinks first, and the cards shrink second — nothing
  // ever slides under anything (20.36's fixed 1.45x cap only worked ≥1920).
  // BETA 22.62 v2 — the compact stats NOTE (not the old panel) owns a fixed
  // 250px column; the right HUD keeps its column; cards fill the rest.
  let leftReserve = hudHidden ? 0 : Math.min(250, w * 0.18);
  let rightReserve = hudHidden ? 0 : Math.min(190, w * 0.13);
  // BETA 22.87 r6 (Dylan): the MIDDLE card sits at the exact center of the
  // screen. Symmetric reserves guarantee w/2 centering while still clearing
  // the stats note column on narrow windows.
  if (type === 'levelup') { const sym = Math.max(leftReserve, rightReserve); leftReserve = sym; rightReserve = sym; }
  const avail = w - leftReserve - rightReserve;
  // BETA 22.87 r6 (Dylan): level-up cards breathe further apart; the other
  // pick screens keep the tighter row.
  // BETA 22.93 — shrine joins the wide-gap branch (Dylan: tiles "too big and
  // too close together" — the square tiles need the same air as level-up).
  let gap = (type === 'levelup' || type === 'shrine') ? (w < 1400 ? 30 : 48) : (w < 1400 ? 16 : 24);
  // BETA 22.89 (Dylan): wild-card art overhangs its card body (the WILD CARD
  // badge sticks out top-left), so the machine's card row breathes wider —
  // gap grows by 22% of the card width. Lives HERE (not in the renderer) so
  // every focus/hover/click hit-rect that calls this helper stays in sync
  // with the visible cards (the 16.82 lesson).
  const gapFrac = type === 'chest' ? 0.22 : 0;
  const idealW = (avail - (n - 1) * gap) / (n + gapFrac * (n - 1));
  const minW = 210;
  const maxW = 368;
  let cardW = Math.max(minW, Math.min(maxW, idealW));
  gap += cardW * gapFrac;
  if (cardW * n + (n - 1) * gap > avail) {
    cardW = Math.max(120, (avail - (n - 1) * gap) / n);
  }
  // v2 poster cards are tall (art ~1.45-1.62 h/w) — one uniform ratio for
  // every pick screen so the same card art serves all three.
  const ratio = 1.5;
  const cardH = Math.round(cardW * ratio);
  const totalW = n * cardW + (n - 1) * gap;
  // Center the row inside the FREE band (between the two reserved columns).
  const startX = leftReserve + (avail - totalW) / 2;
  // ABSOLUTE vertical centering — cards in the middle of the canvas.
  // Title rendered 60px above the card top, footer (skip/reroll) below.
  const cardY = Math.round((h - cardH) / 2);
  // Title sits 60px above cards. Clamp to >= 32 so it never clips off
  // the top edge on very small screens.
  const titleY = Math.max(32, cardY - 60);
  return { cardW, cardH, gap, startX, cardY, titleY };
}

// ─── Right-side HUD Panel: Weapon Slot + Percs + Items ──────────────────────

function renderRightPanel(
  ctx: CanvasRenderingContext2D, state: GameState, w: number, h: number, padH: number
) {
  const scale = state.hudScale ?? 1.0;
  const ICON = Math.round(36 * scale);
  const GAP  = Math.round(4  * scale);
  const PAD  = Math.round(10 * scale);
  const HDR  = Math.round(16 * scale);  // section header height
  const COLS = 4;
  const panelW = COLS * ICON + (COLS - 1) * GAP + PAD * 2;
  const panelX = w - padH - panelW;
  let curY = h - padH;
  const isPaused = state.phase === 'paused';

  // Tooltip data collected during icon rendering
  type TooltipEntry = { lines: string[]; color: string };
  let tooltip: TooltipEntry | null = null;

  function drawSlot(ix: number, iy: number, filled: boolean, borderColor: string) {
    ctx.fillStyle = filled ? 'rgba(20,20,35,0.9)' : 'rgba(10,10,20,0.6)';
    ctx.strokeStyle = filled ? borderColor : 'rgba(60,60,80,0.5)';
    ctx.lineWidth = filled ? 1.5 : 0.5;
    roundRect(ctx, ix, iy, ICON, ICON, Math.round(4 * scale));
    ctx.fill();
    ctx.stroke();
  }

  // BETA 17.28 — Dylan's DevTools profile showed drawBadge at 511ms self-time
  // (8.6% of frame) because it set ctx.font + textAlign + textBaseline on
  // EVERY call. Called 30-40× per frame across items/percs/weapons inventory
  // loops. Now: caller sets font/align ONCE before the loop (see _BADGE_FONT
  // hoist below); drawBadge only does the two fillText (shadow + gold).
  const _BADGE_FONT = `bold ${Math.round(9 * scale)}px "Share Tech Mono", monospace`;
  function drawBadge(ix: number, iy: number, text: string) {
    ctx.fillStyle = '#000';
    ctx.fillText(text, ix + ICON - 1, iy + ICON);
    ctx.fillStyle = '#ffd700';
    ctx.fillText(text, ix + ICON - 2, iy + ICON - 1);
  }

  function checkHover(ix: number, iy: number, lines: string[], color: string) {
    if (!isPaused) return;
    if (hudMouseX >= ix && hudMouseX <= ix + ICON && hudMouseY >= iy && hudMouseY <= iy + ICON) {
      tooltip = { lines, color };
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.strokeRect(ix + 1, iy + 1, ICON - 2, ICON - 2);
    }
  }

  function sectionHeader(label: string, borderColor: string, sectionH: number) {
    curY -= sectionH;
    ctx.fillStyle = 'rgba(5,5,10,0.88)';
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = 1;
    roundRect(ctx, panelX, curY, panelW, sectionH, Math.round(5 * scale));
    ctx.fill();
    ctx.stroke();
    ctx.font = `bold ${Math.round(8 * scale)}px "Share Tech Mono", monospace`;
    ctx.fillStyle = borderColor;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(label, panelX + PAD, curY + Math.round(4 * scale));
  }

  // ── Buncha Bullshiiii (icon grid, bottom) ──
  const items = getItemInventoryDisplayData();
  if (items.length > 0) {
    const rows = Math.ceil(items.length / COLS);
    const gridH = rows * (ICON + GAP) - GAP;
    const sectionH = HDR + gridH + PAD;
    sectionHeader('WILD CARDS', '#ff6600', sectionH);   // BETA 22.89 — buncha era over
    // BETA 17.28 — set badge font + align ONCE for whole items loop.
    ctx.font = _BADGE_FONT;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';

    for (let i = 0; i < items.length; i++) {
      const col = i % COLS;
      const row = Math.floor(i / COLS);
      const ix = panelX + PAD + col * (ICON + GAP);
      const iy = curY + HDR + row * (ICON + GAP);
      const item = items[i];
      const itemColor = ITEM_RARITY_COLORS[item.rarity]?.text || '#ccc';

      drawSlot(ix, iy, true, itemColor);
      const img = getCachedImage(item.iconUrl);
      if (img) ctx.drawImage(img, ix + 2, iy + 2, ICON - 4, ICON - 4);
      if (item.stacks > 1) drawBadge(ix, iy, `x${item.stacks}`);
      checkHover(ix, iy, [
        item.name,
        item.description || item.flavorText || '',
        `Stacks: ${item.stacks}${item.maxStacks > 0 ? ' / ' + item.maxStacks : ''}`,
      ].filter(Boolean), itemColor);
    }
    curY -= Math.round(6 * scale);
  }

  // ── Peanut Percs (4 icon slots) ──
  const percSectionH = HDR + ICON + PAD;
  sectionHeader('PEANUT PERCS', '#00ffcc', percSectionH);
  // BETA 17.28 — set badge font + align ONCE for percs loop.
  ctx.font = _BADGE_FONT;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'bottom';
  const allPercs = getAllPercSlotDisplayData();
  for (let i = 0; i < 4; i++) {
    const perc = allPercs[i];
    const ix = panelX + PAD + i * (ICON + GAP);
    const iy = curY + HDR;
    const filled = perc && !perc.isEmpty;
    const percColor = filled ? (perc.accentColor || '#00ffcc') : '#00ffcc';

    drawSlot(ix, iy, !!filled, percColor);
    if (filled) {
      const img = getCachedImage(perc.percIcon);
      if (img) ctx.drawImage(img, ix + 2, iy + 2, ICON - 4, ICON - 4);
      // BETA 16.28 — PSBOT cycle-30d Ask 3: show Lvl badge ALWAYS (was
      // gated `> 1`). Megabonk pattern: every weapon/perc shows its
      // level even at Lvl 1 so the player can see all levels at a
      // glance instead of only seeing badges on stacked items.
      if (perc.level >= 1) drawBadge(ix, iy, `${perc.level}`);
      checkHover(ix, iy, [
        perc.percName,
        perc.description || '',
        perc.totalBonusDisplay || '',
        `Level ${perc.level} / ${perc.maxLevel}`,
      ].filter(Boolean), percColor);
    }
  }
  curY -= Math.round(6 * scale);

  // ── Weapons (4 icon slots) ──
  const ws = weaponsModule ? getWeaponSystemState() : null;
  const weapSectionH = HDR + ICON + PAD;
  sectionHeader('WEAPONS', '#ffd700', weapSectionH);
  // BETA 17.28 — set badge font + align ONCE for weapons loop.
  ctx.font = _BADGE_FONT;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'bottom';
  for (let i = 0; i < 4; i++) {
    const weapon = ws?.weapons[i];
    const ix = panelX + PAD + i * (ICON + GAP);
    const iy = curY + HDR;
    const filled = !!weapon;
    const isActive = i === 0 && filled;
    const wColor = isActive ? '#ffd700' : filled ? 'rgba(255,215,0,0.5)' : '#ffd700';

    drawSlot(ix, iy, filled, wColor);
    if (filled) {
      const def = getWeaponDef(weapon!.definitionId);
      let sprUrl = def?.spriteUrl;
      // BETA 22.45 — if this gun went into the run as its MODDED combo, show that
      // kitted sprite in the HUD slot (was always the base art).
      try {
        const _wid = weapon!.definitionId;
        if (isArmed(_wid)) { const _c = equippedCodes(_wid); if (_c.length) { const _u = modComboImage(_wid, _c); if (_u) sprUrl = _u; } }
      } catch { /* mod resolver not ready */ }
      if (sprUrl) {
        const img = getCachedImage(sprUrl);
        // BETA 22.47 — was raw drawImage into a SQUARE box, which horizontally
        // SQUISHED wide gun sprites (Dylan: "the moded wepon looks squished from
        // left to right"). drawContainCenter letterboxes to preserve aspect.
        if (img) drawContainCenter(ctx, img, ix + 2, iy + 2, ICON - 4, ICON - 4);
      }
      // BETA 16.28 — PSBOT cycle-30d Ask 3: weapon level badge always.
      if (weapon!.level >= 1) drawBadge(ix, iy, `${weapon!.level}`);
      checkHover(ix, iy, [
        def?.name || weapon!.definitionId,
        `Damage: ${def?.baseDamage ?? '?'}`,
        `Level ${weapon!.level}`,
      ].filter(Boolean), '#ffd700');
    }
  }

  // ── Pause-mode tooltip ──
  if (tooltip) {
    const t = tooltip as TooltipEntry;
    const lineH = 16;
    const tPad = 10;
    const tW = 200;
    const tH = t.lines.length * lineH + tPad * 2;
    let tx = panelX - tW - 8;
    let ty = hudMouseY - 20;
    if (tx < 4) tx = 4;
    if (ty + tH > h - 4) ty = h - 4 - tH;
    if (ty < 4) ty = 4;

    ctx.fillStyle = 'rgba(5,5,15,0.96)';
    ctx.strokeStyle = t.color;
    ctx.lineWidth = 1;
    roundRect(ctx, tx, ty, tW, tH, 6);
    ctx.fill();
    ctx.stroke();

    ctx.font = 'bold 12px "Share Tech Mono", monospace';
    ctx.fillStyle = t.color;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(t.lines[0], tx + tPad, ty + tPad);

    ctx.font = '11px "Share Tech Mono", monospace';
    ctx.fillStyle = '#bbb';
    for (let i = 1; i < t.lines.length; i++) {
      ctx.fillText(t.lines[i], tx + tPad, ty + tPad + i * lineH);
    }
  }
}

// ─── Skip / Reroll button helpers ───────────────────────────────────────────

/** Width and height of each button — used by both renderer and hit tester. */
// BETA 22.63 — Dylan (2.17 video): SKIP and REROLL labels must be the SAME
// size with the same typography. One shared size for both.
// BETA 22.64 — shrunk so both taped labels FIT ON the baked wooden plank
// (Dylan: "oversized and not fitting on the wooden plank behind it").
const SKIP_BTN_W = 150;
const SKIP_BTN_H = 34;
const REROLL_BTN_W = 150;
const REROLL_BTN_H = 34;
const CHOICE_BTN_GAP = 20;

/**
 * Returns { skipX, skipY, rerollX, rerollY } for hit testing.
 * Centered horizontally, at the given `btnY`.
 */
export function getSkipRerollButtonRects(canvasW: number, btnY: number) {
  const totalBtnW = SKIP_BTN_W + CHOICE_BTN_GAP + REROLL_BTN_W;
  const startBtnX = (canvasW - totalBtnW) / 2;
  return {
    skipX: startBtnX, skipY: btnY, skipW: SKIP_BTN_W, skipH: SKIP_BTN_H,
    rerollX: startBtnX + SKIP_BTN_W + CHOICE_BTN_GAP, rerollY: btnY,
    rerollW: REROLL_BTN_W, rerollH: REROLL_BTN_H,
  };
}

function _drawSkipRerollButtons(ctx: CanvasRenderingContext2D, canvasW: number, btnY: number, rerollsLeft: number) {
  const r = getSkipRerollButtonRects(canvasW, btnY);

  // BETA 22.62 — physical torn-paper labels (Dylan's art brief: "buttons
  // should look like taped labels, not clean UI rectangles"). Hit rects
  // (getSkipRerollButtonRects) unchanged — only the paint differs.
  const _skipImg = getCachedImage('/pick-kit/label_red.png');
  ctx.save();
  if (_skipImg) {
    ctx.translate(r.skipX + r.skipW / 2, r.skipY + r.skipH / 2);
    ctx.rotate(-0.015);
    ctx.drawImage(_skipImg, -r.skipW / 2 - 6, -r.skipH / 2 - 5, r.skipW + 12, r.skipH + 10);
    ctx.rotate(0.015);
    ctx.translate(-(r.skipX + r.skipW / 2), -(r.skipY + r.skipH / 2));
  } else {
    ctx.fillStyle = 'rgba(120,26,20,0.94)';
    roundRect(ctx, r.skipX, r.skipY, r.skipW, r.skipH, 3); ctx.fill();
  }
  ctx.restore();

  const hasRerolls = rerollsLeft > 0;
  const _reImg = getCachedImage('/pick-kit/label_blue.png');
  ctx.save();
  if (!hasRerolls) ctx.globalAlpha = 0.45;
  if (_reImg) {
    ctx.translate(r.rerollX + r.rerollW / 2, r.rerollY + r.rerollH / 2);
    ctx.rotate(0.012);
    ctx.drawImage(_reImg, -r.rerollW / 2 - 6, -r.rerollH / 2 - 5, r.rerollW + 12, r.rerollH + 10);
    ctx.rotate(-0.012);
    ctx.translate(-(r.rerollX + r.rerollW / 2), -(r.rerollY + r.rerollH / 2));
  } else {
    ctx.fillStyle = 'rgba(40,40,110,0.94)';
    roundRect(ctx, r.rerollX, r.rerollY, r.rerollW, r.rerollH, 3); ctx.fill();
  }
  ctx.restore();

  // BETA 22.64 — Dylan: "SKIP typography is good; make REROLL the same font."
  // ONE font statement, two fillTexts — impossible for them to diverge.
  ctx.save();
  ctx.font = 'bold 16px Anton, "Share Tech Mono", monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#f3e9da';
  ctx.fillText('SKIP', r.skipX + r.skipW / 2, r.skipY + r.skipH / 2 + 1);
  if (!hasRerolls) ctx.globalAlpha = 0.45;
  ctx.fillStyle = '#ece7f5';
  ctx.fillText(hasRerolls ? `REROLL · ${rerollsLeft} LEFT` : 'REROLL · NONE', r.rerollX + r.rerollW / 2, r.rerollY + r.rerollH / 2 + 1);
  ctx.restore();
}

// ─── Level-Up Choice Screen ─────────────────────────────────────────────────

// Convert a '#rrggbb' rarity color into an 'r,g,b' triplet so it can be
// dropped into rgba(`${triplet},${alpha}`) for the pulsing hover ring.
// Falls back to common-green if the input isn't a 6-digit hex.
function _hexToRgbTriplet(hex: string): string {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex);
  if (!m) return '57,255,20';
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
}

// ─── PICK-KIT v2 (BETA 22.62) — built like the pause menu: complete art
// pieces (one background per screen, one complete card per rarity), code only
// writes the dynamic text into measured zones. Dylan: "design it like a prop."
const PICK_INK = '#34302a';
const PICK_INK_DIM = 'rgba(60,48,36,0.72)';
const _PICK_CARD_ART: Record<string, string> = {
  common: '/pick-kit/v2_card_common.png',
  uncommon: '/pick-kit/v2_card_common.png',
  rare: '/pick-kit/v2_card_rare.png',
  epic: '/pick-kit/v2_card_rare.png',
  legendary: '/pick-kit/v2_card_legendary.png',
  negative: '/pick-kit/v2_card_common.png',
};
// zone fractions of each card art (measured offline from the final PNGs)
// BETA 22.64 — rare/legendary PNGs re-cropped so all 3 parchment bodies sit at
// IDENTICAL canvas fractions (Dylan: "all 3 main cards are different sizes").
// Zone fractions remapped through the same crops.
const _PICK_ZONES: Record<string, { bannerC: number; barC: number }> = {
  common: { bannerC: 0.51, barC: 0.607 },
  uncommon: { bannerC: 0.51, barC: 0.607 },
  rare: { bannerC: 0.496, barC: 0.599 },
  epic: { bannerC: 0.496, barC: 0.599 },
  legendary: { bannerC: 0.513, barC: 0.618 },
  negative: { bannerC: 0.51, barC: 0.607 },
};
const _PICK_ACCENT: Record<string, string> = {
  common: '#3f7028', uncommon: '#3f7028', rare: '#6b3f8f', epic: '#6b3f8f',
  legendary: '#9a7414', negative: '#8f1f16',
};
// BETA 22.67 — the selection backlight speaks the card's RARITY (Dylan):
// spray ring art (hue-shifted from ring_green) + halo glow triplet per tier.
const _PICK_RING: Record<string, string> = {
  common: '/pick-kit/ring_green.png', uncommon: '/pick-kit/ring_green.png',
  rare: '/pick-kit/ring_purple.png', epic: '/pick-kit/ring_purple.png',
  legendary: '/pick-kit/ring_gold.png', negative: '/pick-kit/ring_red.png',
};
const _PICK_GLOW: Record<string, string> = {
  common: '94,235,52', uncommon: '94,235,52',
  rare: '186,106,255', epic: '186,106,255',
  legendary: '255,198,64', negative: '255,72,48',
};

/** Background board art (baked title + blank banner + blank plank), cover-fit. */
function _drawPickBg(ctx: CanvasRenderingContext2D, w: number, h: number, url: string, cropTopFrac = 0): void {
  const img = getCachedImage(url);
  if (!img) { ctx.fillStyle = 'rgba(10,8,6,0.95)'; ctx.fillRect(0, 0, w, h); return; }
  // BETA 22.87 — cropTopFrac cuts the top slice of the SOURCE art before
  // cover-fitting (kills baked-in titles/banners: Dylan "remove the sprite").
  const sy = img.naturalHeight * cropTopFrac;
  const sh = img.naturalHeight - sy;
  const sc = Math.max(w / img.naturalWidth, h / sh);
  const dw = img.naturalWidth * sc, dh = sh * sc;
  ctx.drawImage(img, 0, sy, img.naturalWidth, sh, (w - dw) / 2, (h - dh) / 2, dw, dh);
}

/** Banner + plank text (zones baked blank into every v2 background). */
function _drawPickHeaderText(ctx: CanvasRenderingContext2D, w: number, h: number, banner: string, taunt: string): void {
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `${Math.max(15, Math.round(h * 0.031))}px Anton, "Share Tech Mono", monospace`;
  ctx.fillStyle = '#f3ead9';
  ctx.fillText(banner, w * 0.495, h * 0.166);
  void taunt;   // drawn separately by _drawPickTaunt (needs the button row Y)
}

/** Taunt text — centered on the plank when the buttons sit above it,
 *  otherwise tucked just above the button row (Dylan: no collisions). */
function _drawPickTaunt(ctx: CanvasRenderingContext2D, w: number, h: number, btnY: number, taunt: string): void {
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `${Math.max(13, Math.round(h * 0.024))}px Anton, "Share Tech Mono", monospace`;
  ctx.fillStyle = '#f0e6d2';
  const plankCenter = h * 0.905;
  const y = btnY > h * 0.85 ? btnY - 30 : plankCenter;
  ctx.fillText(taunt, w * 0.5, y);
}

/** One complete card: per-rarity art + number + hover paint ring. Returns zone Ys. */
function _drawPickCardFrame(
  ctx: CanvasRenderingContext2D,
  cx: number, cardY: number, cardW: number, cardH: number,
  rarity: string, idx: number, isHovered: boolean, focusScale: number,
): { bannerY: number; barY: number; textTop: number; splatC: number } {
  const art = getCachedImage(_PICK_CARD_ART[rarity] ?? _PICK_CARD_ART.common);
  if (isHovered) {
    // BETA 22.65 — Dylan: the selection paint goes BEHIND the card, slightly
    // smaller so it just pokes past the edges, reading as AMBIENT LIGHT (never
    // covering the number tab or the header banner). Two layers under the
    // card: a soft green halo (shadow-blur bleed) + the spray ring with its
    // hole mapped INSIDE the card bounds so only the band's outer edge peeks.
    void focusScale;
    const glow = _PICK_GLOW[rarity] ?? _PICK_GLOW.common;
    ctx.save();
    ctx.shadowColor = `rgba(${glow},0.85)`;
    ctx.shadowBlur = Math.max(24, cardW * 0.13);
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.fillStyle = `rgba(${glow},0.55)`;
    ctx.fillRect(cx + 4, cardY + 4, cardW - 8, cardH - 8);   // fill hides under the card; only the halo shows
    ctx.restore();
    const ring = getCachedImage(_PICK_RING[rarity] ?? _PICK_RING.common);
    if (ring) {
      // ring hole = x .146-.857 / y .089-.822 of its art (drips hang below);
      // map the hole to the card rect with PER-AXIS insets tuned to the band's
      // uneven thickness -> peeks ~10% sides, ~6% top, ~17%+drips bottom
      // (Dylan 22.65 note: "a bit short, not really poking out the bottom").
      const inX = cx + cardW * 0.075, inW = cardW * 0.85;
      const inY = cardY + cardH * 0.05, inH = cardH * 0.90;
      const rw = inW / 0.711, rh = inH / 0.733;
      ctx.save();
      ctx.globalAlpha = 0.9;
      ctx.drawImage(ring, inX - rw * 0.146, inY - rh * 0.089, rw, rh);
      ctx.restore();
    }
  }
  // Dylan (2.17 video): cards must cast a DROP SHADOW ("they're taped to it,
  // not floating") and NOTHING on the cards pulses.
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.55)';
  ctx.shadowBlur = 18;
  ctx.shadowOffsetX = 10;
  ctx.shadowOffsetY = 9;
  if (art) ctx.drawImage(art, cx, cardY, cardW, cardH);
  else { ctx.fillStyle = '#c9b18c'; ctx.fillRect(cx, cardY, cardW, cardH); }
  ctx.restore();
  // number on the baked blue tab
  ctx.font = `bold ${Math.round(cardH * 0.042)}px Anton, "Share Tech Mono", monospace`;
  ctx.fillStyle = '#f0ece0';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(`${idx + 1}`, cx + cardW * 0.5, cardY + cardH * 0.038);
  // negative shrine rolls get a red CURSED label over the banner zone
  const z = _PICK_ZONES[rarity] ?? _PICK_ZONES.common;
  if (rarity === 'negative') {
    const lab = getCachedImage('/pick-kit/label_red.png');
    // BETA 22.74 — label at NATURAL aspect. The old *0.55 squash compensated
    // for the pre-22.63 PNG's dead padding (557×300, blob only ~31% of rows);
    // after the crop to the dense blob (307×94) the squash made the red label
    // cover only half the baked COMMON banner (green peeked above/below).
    // Measured: banner ≈ 0.46·cardW × 0.094·cardH — the cropped label at
    // natural aspect and this width covers it exactly.
    const lw = cardW * 0.46, lh = lab ? lw * (lab.naturalHeight / lab.naturalWidth) : cardH * 0.094;
    if (lab) ctx.drawImage(lab, cx + cardW / 2 - lw / 2, cardY + cardH * z.bannerC - lh / 2, lw, lh);
    ctx.font = `bold ${Math.round(cardH * 0.03)}px Anton, sans-serif`;
    ctx.fillStyle = '#f3ead9';
    ctx.fillText('CURSED', cx + cardW / 2, cardY + cardH * z.bannerC + 1);
  }
  return {
    bannerY: cardY + cardH * z.bannerC,
    barY: cardY + cardH * z.barC,
    textTop: cardY + cardH * (z.barC + 0.075),
    splatC: cardY + cardH * 0.275,
  };
}

/** Icon on the baked splat — center-cropped + round-clipped so the icon
 *  library's baked dark frames (every item PNG ships pre-framed) disappear. */
function _drawPickIcon(ctx: CanvasRenderingContext2D, url: string, cxPx: number, cyPx: number, size: number): void {
  const img = getCachedImage(url);
  if (!img) return;
  ctx.save();
  ctx.beginPath();
  ctx.arc(cxPx, cyPx, size / 2, 0, Math.PI * 2);
  ctx.clip();
  const crop = 0.74;
  const sw = img.naturalWidth * crop, sh = img.naturalHeight * crop;
  ctx.drawImage(img, (img.naturalWidth - sw) / 2, (img.naturalHeight - sh) / 2, sw, sh,
    cxPx - size / 2, cyPx - size / 2, size, size);
  ctx.restore();
}

/** Greg's commentary bubble (deterministic per seed — no flicker). */
const _PICK_QUIPS = ['PICK ONE, IDIOT.', 'THIS BETTER HELP.', "DON'T EMBARRASS ME.", 'ANY DAY NOW.', 'THE BOSS IS WATCHING.'];
function _drawPickQuip(ctx: CanvasRenderingContext2D, state: GameState, w: number, h: number, seed: number): void {
  const id = state.character?.id;
  if (!id || !getCachedImage(`/characters/poses/${id}.png`)) return;
  const bub = getCachedImage('/pick-kit/bubble.png');
  const text = _PICK_QUIPS[Math.abs(seed) % _PICK_QUIPS.length];
  const poseH = h * 0.34;
  // Clamped left of the card column and dropped to the survivor's torso so it
  // never covers a card or the stats note — comic-style, over the speaker.
  const bw = Math.min(200, w * 0.15), bh = bub ? bw * (bub.naturalHeight / bub.naturalWidth) : 70;
  const bx = Math.max(6, Math.min(250, w * 0.18) - bw - 6);
  const by = h - poseH * 0.62;
  if (bub) {
    // Mirror so the tail (top-left in the art) points up-right at the speaker.
    ctx.save();
    ctx.translate(bx + bw / 2, by + bh / 2);
    ctx.scale(-1, 1);
    ctx.drawImage(bub, -bw / 2, -bh / 2, bw, bh);
    ctx.restore();
  } else { ctx.fillStyle = 'rgba(245,240,228,0.95)'; ctx.fillRect(bx, by, bw, bh * 0.7); }
  // The balloon body sits in rows 40-83% / cols 45-98% of the art (the rest is
  // the tail) — mirrored, the body spans cols 2-55%. Center text in the BODY.
  const bodyX = bx + bw * 0.015, bodyW = bw * 0.535;
  const bodyCY = by + bh * 0.615;
  ctx.fillStyle = '#1c1813';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  let qfs = Math.max(11, Math.round(bw * 0.078));
  ctx.font = `bold ${qfs}px Anton, "Share Tech Mono", monospace`;
  if (ctx.measureText(text).width > bodyW * 0.92 && text.includes(' ')) {
    const words = text.split(' ');
    let l1 = '', l2 = '';
    for (const wd of words) {
      if (!l2 && (l1 + ' ' + wd).trim().length <= Math.ceil(text.length / 2)) l1 = (l1 + ' ' + wd).trim();
      else l2 = (l2 + ' ' + wd).trim();
    }
    while (qfs > 8 && Math.max(ctx.measureText(l1).width, ctx.measureText(l2).width) > bodyW * 0.94) {
      qfs--; ctx.font = `bold ${qfs}px Anton, "Share Tech Mono", monospace`;
    }
    ctx.fillText(l1, bodyX + bodyW / 2, bodyCY - qfs * 0.62);
    ctx.fillText(l2, bodyX + bodyW / 2, bodyCY + qfs * 0.62);
  } else {
    while (qfs > 8 && ctx.measureText(text).width > bodyW * 0.94) {
      qfs--; ctx.font = `bold ${qfs}px Anton, "Share Tech Mono", monospace`;
    }
    ctx.fillText(text, bodyX + bodyW / 2, bodyCY);
  }
}

/** The survivor's pose (top half) rises into the bottom-left corner. */
function _drawPickPose(ctx: CanvasRenderingContext2D, state: GameState, w: number, h: number): void {
  const id = state.character?.id;
  if (!id) return;
  const img = getCachedImage(`/characters/poses/${id}.png`);
  if (!img) return;
  const srcH = img.naturalHeight * 0.62;          // top half incl. the thumbs-up
  const drawH = h * 0.34;
  const drawW = drawH * (img.naturalWidth / srcH);
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.55)';
  ctx.shadowBlur = 22;
  ctx.shadowOffsetX = 14;
  ctx.shadowOffsetY = 8;
  ctx.drawImage(img, 0, 0, img.naturalWidth, srcH, Math.max(10, w * 0.014), h - drawH, drawW, drawH);
  ctx.restore();
}

/** Compact taped stats note (replaces the blue panel on pick screens).
 *  Highlights previewed/changed stats with a dirty yellow marker stripe. */
function _drawPickStatsNote(
  ctx: CanvasRenderingContext2D, state: GameState, w: number, h: number,
  preview?: PanelPreviewMod[],
): void {
  // BETA 22.92 — Dylan: "i want a better looking stats page. keep it simple."
  // Retired the taped parchment note → a clean dark-glass panel that matches
  // the HUD chrome: rounded slate body, gold STATS header strip, amber section
  // dividers, monospace rows, white values (gold when a hovered card previews
  // that stat). Auto-heights to content.
  const p = state.player;
  if (!p) return;
  const prevKeys = new Set((preview ?? []).map(m => _PREVIEW_SRC_TO_PANEL[m.stat] ?? m.stat));

  const rows: Array<[string, string, string] | [string]> = [
    ['DEFENSE'],
    ['hp', 'HP', `${Math.ceil(p.hp)}/${Math.round(p.maxHp)}`],
    ['hpRegen', 'HP REGEN', `+${(p.hpRegen ?? 0).toFixed(1)}/s`],
    ...((p.armor ?? 0) > 0 ? [['armor', 'ARMOR', `${Math.round(p.armor ?? 0)}`] as [string, string, string]] : []),
    ...((p.maxShield ?? 0) > 0 ? [['shield', 'SHIELD', `${Math.round(p.maxShield ?? 0)}`] as [string, string, string]] : []),
    ['OFFENSE'],
    ['damage', 'DAMAGE', `+${(p.damageBonus ?? 0).toFixed(0)}%`],
    ['atkSpeed', 'ATK SPD', `+${(p.attackSpeedBonus ?? 0).toFixed(0)}%`],
    ['critChance', 'CRIT', `${(p.critChance ?? 0).toFixed(0)}%`],
    ['MODIFIERS'],
    ['moveSpeed', 'MOVE SPD', `${Math.round(p.speed ?? 100)}%`],
    ...((p.luck ?? 0) > 0 ? [['luck', 'LUCK', `+${(p.luck ?? 0).toFixed(0)}%`] as [string, string, string]] : []),
  ];

  const nw = Math.min(230, w * 0.165);
  const PAD = Math.round(nw * 0.075);
  const fs = Math.max(12, Math.round(nw * 0.065));
  const HEADER_H = Math.round(fs * 2.4);
  const rowH = Math.round(fs * 1.7);
  const secH = Math.round(rowH * 1.15);
  let bodyH = 0;
  for (const r of rows) bodyH += r.length === 1 ? secH : rowH;
  const nh = HEADER_H + bodyH + PAD * 1.4;
  const nx = Math.max(10, w * 0.02);
  const ny = Math.max(h * 0.18, h * 0.5 - nh / 2);

  ctx.save();
  ctx.textBaseline = 'middle';
  // body
  ctx.shadowColor = 'rgba(0,0,0,0.6)'; ctx.shadowBlur = 20; ctx.shadowOffsetY = 8;
  const grad = ctx.createLinearGradient(nx, ny, nx, ny + nh);
  grad.addColorStop(0, 'rgba(20,20,28,0.94)');
  grad.addColorStop(1, 'rgba(10,10,15,0.94)');
  ctx.fillStyle = grad;
  roundRect(ctx, nx, ny, nw, nh, 10); ctx.fill();
  ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;
  ctx.lineWidth = 1.5; ctx.strokeStyle = 'rgba(255,215,120,0.35)';
  roundRect(ctx, nx, ny, nw, nh, 10); ctx.stroke();

  // header strip
  ctx.fillStyle = 'rgba(255,215,90,0.14)';
  roundRect(ctx, nx, ny, nw, HEADER_H, 10); ctx.fill();
  ctx.fillStyle = 'rgba(255,215,90,0.5)';
  ctx.fillRect(nx + PAD, ny + HEADER_H - 1.5, nw - PAD * 2, 1.5);
  ctx.textAlign = 'center';
  ctx.font = `700 ${Math.round(fs * 1.4)}px Anton, "Share Tech Mono", sans-serif`;
  ctx.fillStyle = '#ffd75a';
  ctx.fillText('STATS', nx + nw / 2, ny + HEADER_H / 2 + 1);

  const lx = nx + PAD;
  const rx = nx + nw - PAD;
  let sy = ny + HEADER_H + rowH * 0.7;
  for (const row of rows) {
    if (row.length === 1) {
      ctx.textAlign = 'left';
      ctx.font = `700 ${Math.round(fs * 0.92)}px Anton, sans-serif`;
      ctx.fillStyle = 'rgba(255,180,90,0.85)';
      ctx.fillText(row[0], lx, sy + secH * 0.15);
      ctx.strokeStyle = 'rgba(255,180,90,0.2)'; ctx.lineWidth = 1;
      const tw = ctx.measureText(row[0]).width;
      ctx.beginPath(); ctx.moveTo(lx + tw + 8, sy + secH * 0.15); ctx.lineTo(rx, sy + secH * 0.15); ctx.stroke();
      sy += secH;
      continue;
    }
    const [key, label, val] = row;
    if (prevKeys.has(key)) {
      ctx.fillStyle = 'rgba(255,215,90,0.16)';
      roundRect(ctx, lx - 5, sy - rowH * 0.45, nw - PAD * 2 + 10, rowH * 0.9, 4); ctx.fill();
    }
    ctx.font = `600 ${fs}px "Share Tech Mono", monospace`;
    ctx.textAlign = 'left';
    ctx.fillStyle = 'rgba(210,214,224,0.9)';
    ctx.fillText(label, lx, sy);
    ctx.textAlign = 'right';
    ctx.fillStyle = prevKeys.has(key) ? '#ffe27a' : '#ffffff';
    ctx.fillText(val, rx, sy);
    sy += rowH;
  }
  ctx.restore();
}

// ── ?cardcal — per-tier text-zone calibration overlay (22.87 r8 dev tool) ──
// Draws every per-tier zone box (getCardTierBoxes — override-aware) as a
// labeled colored outline ON the live cards + a 0.05-fraction grid. The
// numbers themselves are edited in the DevPanel CARD CAL tool; this overlay
// verifies the REAL renderer. Dev-only, ships dark unless the flag is present.
//   window.__cardcalRarity('epic')  → force all 3 cards to render as that
//   tier (art + zones) so every tier is checkable without reroll grinding;
//   window.__cardcalRarity(null) restores the real rolls.
//   window.__cardcalLevelUp()       → fill the XP bar to pop the screen.
const _CARDCAL = (() => {
  try { return new URLSearchParams(window.location.search).has('cardcal'); }
  catch { return false; }
})();
let _cardcalRarity: string | null = null;
const _cardcalRects: { x: number; y: number; w: number; h: number }[] = [];
if (_CARDCAL) {
  try {
    (window as any).__cardcalRarity = (r: string | null) => { _cardcalRarity = r; };
    (window as any).__cardcalLevelUp = () => {
      if (_liveState) _liveState.player.xp = _liveState.player.xpToNext;
    };
    (window as any).__cardcalRects = _cardcalRects;   // live card rects (canvas px) for zoom crops
  } catch { /* SSR / tests: no window */ }
}

// BETA 22.92 — dev shrine trigger (?cardcal): pop the shrine choice screen on
// demand so the new tile UI can be verified without walking onto a shrine.
if (_CARDCAL) {
  try {
    (window as any).__devShrine = (golden = false) => {
      if (!_liveState) return 'no state';
      generateShrineOptions(!!golden, _liveState.player.luck ?? 0);
      _liveState.phase = 'shrine_choice';
      return 'shrine_choice';
    };
  } catch { /* no window */ }
}

function _drawCardCalOverlay(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, cw: number, ch: number,
  tier: string, B: CardTierBoxes,
) {
  ctx.save();
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  // 0.05-fraction grid (brighter at 0.1 lines), fraction labels each 0.1
  for (let f = 5; f < 100; f += 5) {
    const major = f % 10 === 0;
    ctx.strokeStyle = major ? 'rgba(255,255,255,0.38)' : 'rgba(255,255,255,0.14)';
    ctx.lineWidth = 1;
    const gy = y + ch * f / 100, gx = x + cw * f / 100;
    ctx.beginPath(); ctx.moveTo(x, gy); ctx.lineTo(x + cw, gy); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(gx, y); ctx.lineTo(gx, y + ch); ctx.stroke();
    if (major) {
      ctx.font = '9px "Share Tech Mono", monospace';
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.shadowColor = 'rgba(0,0,0,0.9)'; ctx.shadowBlur = 3;
      ctx.fillText(`.${f / 10}`, x + 2, gy);           // y fractions, left rail
      ctx.fillText(`.${f / 10}`, gx + 1, y + ch - 7);  // x fractions, bottom rail
      ctx.shadowBlur = 0;
    }
  }
  // one zone box: outline + 12%-alpha fill + tiny "label .val" tag
  const box = (bx: number, by: number, bw: number, bh: number, col: string, tag: string) => {
    ctx.strokeStyle = col; ctx.lineWidth = 1.5;
    ctx.fillStyle = col.replace('rgb', 'rgba').replace(')', ',0.12)');
    ctx.fillRect(bx, by, bw, bh);
    ctx.strokeRect(bx, by, bw, bh);
    ctx.font = '10px "Share Tech Mono", monospace';
    ctx.shadowColor = 'rgba(0,0,0,0.95)'; ctx.shadowBlur = 3;
    ctx.fillStyle = col;
    ctx.fillText(tag, bx + 2, by - 6);
    ctx.shadowBlur = 0;
  };
  // one box per zone rect, straight from the (override-aware) tier table
  const zr = (r: [number, number, number, number], col: string, tag: string) =>
    box(x + cw * r[0], y + ch * r[1], cw * (r[2] - r[0]), ch * (r[3] - r[1]), col, tag);
  zr(B.name, 'rgb(0,229,255)', 'name');
  zr(B.lv, 'rgb(255,224,0)', 'lv');
  zr(B.win, 'rgb(0,255,102)', 'win');
  zr(B.eff, 'rgb(255,64,255)', 'eff');
  zr(B.cat, 'rgb(255,149,0)', 'cat');
  // tier tag, bottom-left inside the card
  ctx.font = '11px "Share Tech Mono", monospace';
  ctx.shadowColor = 'rgba(0,0,0,0.95)'; ctx.shadowBlur = 4;
  ctx.fillStyle = '#ffffff';
  ctx.fillText(`CARDCAL ${tier.toUpperCase()}`, x + 4, y + ch + 12);
  ctx.restore();
}

function renderLevelUpScreen(
  ctx: CanvasRenderingContext2D, state: GameState, w: number, h: number
) {
  // BETA 22.87 — PERK CARD SYSTEM v1 (Dylan's rebuild). The card IS the art:
  // one baked frame per family × rarity (/perk-cards/{tier}/{family}.webp),
  // code writes text ONLY into the card's own plates (top name / right LV /
  // bottom effect / bottom category). Rarity = frame color + colored label
  // ABOVE the card + matching backlight. Cards slam in staggered, the chosen
  // card pulses then flies to the PERCS strip, losers fade out.
  ctx.save();
  ctx.fillStyle = 'rgba(0,0,0,0.74)';   // r6 (Dylan): see-through darkened screen — the world stays visible behind the cards
  ctx.fillRect(0, 0, w, h);
  drawLiquidNeon(ctx, w, h, NEON_BLUE);

  const options = getLevelUpOptionDisplayData();
  const hoverIdx = getLevelUpHoverIndex();
  const cardCount = options.length;

  // fresh offer (or reroll) → restart the slam-in + level-anim clock.
  // (Must run BEFORE the header block: the gold LEVEL animation keys off it.)
  if (cardCount > 0 && _luOptionsRef !== options) {
    _luOptionsRef = options;
    _luOpenAt = performance.now();
    _luSlamPlayed = [false, false, false];
  }

  const L = computeChoiceCardLayout(w, h, Math.max(cardCount, 3), !!state.hudHidden, 'levelup');
  _luLastLayout = { startX: L.startX, cardY: L.cardY, cardW: L.cardW, cardH: L.cardH, gap: L.gap };
  // BETA 22.87 r5 (Dylan): the baked BOARD art is GONE entirely ("the big
  // brown things need to be deleted") — the level-up screen is the dark
  // backdrop + lightning + the cards. All text is screen-anchored UI.
  {
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // r6 (Dylan): the whole title block sits LOWER — anchored just above the
    // card row (and the bigger rarity labels), not pinned to the screen top.
    const _rarityF = Math.max(16, Math.round(L.cardH * 0.058));
    const lvRowY = L.cardY - _rarityF * 2.9;
    const chooseRowY = lvRowY - Math.max(22, h * 0.042);
    const titleRowY = chooseRowY - Math.max(30, h * 0.055);
    const tf = Math.max(30, Math.round(h * 0.062));
    ctx.font = `400 ${tf}px SorryBossTitle, Anton, Impact, sans-serif`;
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.95)';
    ctx.shadowBlur = 10;
    ctx.shadowOffsetY = 4;
    ctx.lineWidth = Math.max(3, tf * 0.09);
    ctx.strokeStyle = 'rgba(0,0,0,0.85)';
    ctx.strokeText('LEVEL UP!', w / 2, titleRowY);
    ctx.fillStyle = '#f3ead9';
    ctx.fillText('LEVEL UP!', w / 2, titleRowY);
    ctx.restore();
    ctx.font = `400 ${Math.max(15, Math.round(h * 0.026))}px SorryBossTitle, Anton, Impact, sans-serif`;
    ctx.fillStyle = '#e9dfc8';
    ctx.fillText('CHOOSE YOUR UPGRADE', w / 2, chooseRowY);
    // player level: its OWN gold element (Dylan: not beside the header, a
    // different color, and it ANIMATES old level -> new level on open)
    const newLv = state.player.level;
    const prevLv = Math.max(1, newLv - 1);
    const lt = (performance.now() - _luOpenAt) / 1000;
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.9)';
    ctx.shadowBlur = 6;
    ctx.shadowOffsetY = 2;
    const lvf = Math.max(17, Math.round(h * 0.032));
    if (lt < 0.35) {
      const k = lt / 0.35;
      ctx.globalAlpha = 1 - k;
      ctx.font = `400 ${Math.round(lvf * (1 - 0.35 * k))}px SorryBossTitle, Anton, Impact, sans-serif`;
      ctx.fillStyle = '#c9b06a';
      ctx.fillText(`LEVEL ${prevLv}`, w / 2, lvRowY);
    } else {
      const k = Math.min(1, (lt - 0.35) / 0.30);
      const pop = 1 + 0.55 * (1 - k) * (1 - k);
      const pulse = k >= 1 ? 1 + 0.03 * Math.sin(performance.now() / 260) : 1;
      ctx.font = `400 ${Math.round(lvf * pop * pulse)}px SorryBossTitle, Anton, Impact, sans-serif`;
      ctx.fillStyle = '#ffd700';
      ctx.fillText(`LEVEL ${newLv}`, w / 2, lvRowY);
    }
    ctx.restore();
  }

  // ── EXIT BEAT: post-pick freeze — chosen pulses, losers fade ──
  if (_luExit) {
    const et = (performance.now() - _luExit.start) / 1000;
    for (let i = 0; i < _luExit.snaps.length; i++) {
      const snap = _luExit.snaps[i];
      const img = snap.art ? getCachedImage(snap.art) : null;
      ctx.save();
      if (i === _luExit.idx) {
        const pulse = 1 + 0.10 * Math.sin(Math.min(et / 0.45, 1) * Math.PI);
        const st = CARD_RARITY_STYLE[snap.rarity] ?? CARD_RARITY_STYLE.common;
        ctx.shadowColor = `rgba(${st.glow},0.95)`;
        ctx.shadowBlur = 40;
        const pw = snap.w * pulse, ph = snap.h * pulse;
        if (img) ctx.drawImage(img, snap.x + (snap.w - pw) / 2, snap.y + (snap.h - ph) / 2, pw, ph);
      } else {
        const fade = Math.max(0, 1 - et / 0.3);
        if (fade > 0 && img) {
          ctx.globalAlpha = fade;
          ctx.drawImage(img, snap.x + (1 - fade) * 24 * (i < _luExit.idx ? -1 : 1), snap.y + (1 - fade) * 18, snap.w, snap.h);
        }
      }
      ctx.restore();
    }
    ctx.restore();
    return;
  }

  if (cardCount === 0) { ctx.restore(); return; }
  const tNow = (performance.now() - _luOpenAt) / 1000;

  for (let i = 0; i < cardCount; i++) {
    const opt = options[i];
    const cx = L.startX + i * (L.cardW + L.gap);
    const isHovered = i === hoverIdx;
    const isWeapon = opt.type === 'new_weapon' || opt.type === 'weapon_upgrade';
    // ?cardcal can force a display tier so all 4 shells are checkable at once
    // (art + zones only — the actual rolled rarity/effect is untouched)
    const dispRarity = (_CARDCAL && _cardcalRarity) ? _cardcalRarity : opt.rarity;
    const st = CARD_RARITY_STYLE[dispRarity] ?? CARD_RARITY_STYLE.common;
    // r8 — per-tier BOX geometry (Dylan's calibrator model): every zone is a
    // rect; text centers in it and the font fits BOTH width and height.
    const B = getCardTierBoxes(dispRarity);
    const bcx = (r: [number, number, number, number]) => (r[0] + r[2]) / 2;
    const bcy = (r: [number, number, number, number]) => (r[1] + r[3]) / 2;
    const bw = (r: [number, number, number, number]) => r[2] - r[0];
    const bh = (r: [number, number, number, number]) => r[3] - r[1];
    // BETA 22.89 — weapon cards prefer the BAKED shell+sprite (solid through
    // the exit/fly beat — live-drawn sprites vanished there). Falls back to
    // shell + live sprite while the bake loads, and permanently for MODDED
    // weapons (kitted combo icons aren't baked; percIcon !== base spriteUrl).
    const bakedWeaponUrl = (isWeapon && opt.weaponId
      && getWeaponDef(opt.weaponId)?.spriteUrl === opt.percIcon)
      ? weaponCardArtPath(opt.weaponId, dispRarity) : null;
    const bakedWeaponImg = bakedWeaponUrl ? getCachedImage(bakedWeaponUrl) : null;
    const artUrl = isWeapon
      ? (bakedWeaponImg ? bakedWeaponUrl : cardShellPath(dispRarity))
      : cardArtPath(opt.percId ?? '', dispRarity);
    const img = artUrl ? getCachedImage(artUrl) : null;

    // ── slam-in: overlay 150ms, then card i lands over 250ms (80ms stagger) ──
    const delay = 0.15 + i * 0.08;
    const prog = Math.max(0, Math.min(1, (tNow - delay) / 0.25));
    if (prog <= 0) continue;
    if (prog >= 1 && !_luSlamPlayed[i]) {
      _luSlamPlayed[i] = true;
      try { playCardSlamSfx(opt.rarity); } catch { /* sfx optional */ }
    }
    // ease-out-back: overshoot then settle = the impact bounce
    const eb = 1 + 2.2 * Math.pow(prog - 1, 3) + 1.2 * Math.pow(prog - 1, 2);
    const hoverScale = _choiceCardScaleTick('levelup', i, isHovered);
    const scale = (1.55 - 0.55 * eb) * (prog >= 1 ? hoverScale : 1);
    const yDrop = (1 - eb) * -70;
    const cw = L.cardW * scale, chh = L.cardH * scale;
    const ccx = cx + (L.cardW - cw) / 2, ccy = L.cardY + (L.cardH - chh) / 2 + yDrop;

    ctx.save();
    ctx.globalAlpha = Math.min(1, prog * 1.6);

    // ── rarity BACKLIGHT — blurred colored SILHOUETTE of the card itself
    // (perfect edge mask, neon pulse, brighter on the hovered card) ──
    if (artUrl) {
      const glowSpr = _getCardGlowSprite(artUrl, st.glow);
      if (glowSpr) {
        // BETA 22.95 — liquid neon: glow more SOLID + tighter (Dylan: "a
        // little less see through. shrink some so the light behind each card
        // isnt touching the light behind the card next to it").
        const pulse = isHovered
          ? 0.92 + 0.08 * Math.sin(performance.now() / 190 + i * 1.7)
          : 0.60 + 0.12 * Math.sin(performance.now() / 430 + i * 1.7);
        const gs = isHovered ? 1.05 : 1.034;
        const gw = cw * gs, gh = chh * gs;
        ctx.save();
        ctx.globalAlpha = Math.max(0, pulse) * Math.min(1, prog * 1.6);
        ctx.filter = `blur(${Math.max(4, cw * (isHovered ? 0.022 : 0.016))}px)`;   // 22.95 tighter
        ctx.drawImage(glowSpr, ccx - (gw - cw) / 2, ccy - (gh - chh) / 2, gw, gh);
        ctx.filter = 'none';
        ctx.restore();
      }
    }

    // weapon cards ride the EMPTY shells — their window is a real transparent
    // hole, so back it with near-black FIRST or the rarity glow bleeds through
    // as a flat colored box (Dylan: "weird solid square color boxes").
    // (baked weapon cards carry the backing inside the bake)
    if (isWeapon && !bakedWeaponImg) {
      ctx.fillStyle = 'rgba(16,13,10,0.97)';
      ctx.fillRect(ccx + cw * 0.05, ccy + chh * 0.04, cw * 0.90, chh * 0.92);
    }
    // ── the card (drop shadow so it sits ON the board, never floats) ──
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.55)';
    ctx.shadowBlur = 18;
    ctx.shadowOffsetX = 9;
    ctx.shadowOffsetY = 8;
    if (img) ctx.drawImage(img, ccx, ccy, cw, chh);
    else { ctx.fillStyle = '#1c1813'; ctx.fillRect(ccx, ccy, cw, chh); }
    ctx.restore();

    // impact dust — rarity-colored specks kicked up right after the slam
    if (prog >= 1 && tNow < delay + 0.6) {
      const dt2 = (tNow - delay - 0.25) / 0.35;
      for (let k = 0; k < 7; k++) {
        const ang = (k / 7) * Math.PI + Math.PI;   // upward fan
        const dist = (12 + (k * 37 % 26)) * (0.3 + dt2);
        ctx.fillStyle = `rgba(${st.glow},${Math.max(0, 0.7 - dt2)})`;
        ctx.beginPath();
        ctx.arc(ccx + cw / 2 + Math.cos(ang + k) * dist * 2.2, ccy + chh - 8 + Math.sin(ang) * dist * 0.5, 2.2 - dt2, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // ── RARITY LABEL — above the card, outside it, in the tier color ──
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const rf = Math.max(18, Math.round(L.cardH * 0.058));   // r6 — MUCH larger
    ctx.font = `400 ${rf}px SorryBossTitle, Anton, Impact, sans-serif`;
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.95)';
    ctx.shadowBlur = 7;
    ctx.shadowOffsetY = 4;
    ctx.lineWidth = Math.max(3, rf * 0.11);
    ctx.strokeStyle = 'rgba(0,0,0,0.9)';
    ctx.strokeText(st.label, ccx + cw / 2, ccy - rf * 0.95);
    ctx.fillStyle = st.text;
    ctx.fillText(st.label, ccx + cw / 2, ccy - rf * 0.95);
    // (no colored backlight on the label — Dylan r6: black drop shadow only)
    ctx.restore();
    if (st.sparkle) {
      // legendary: little star glints orbiting the label
      const lw2 = ctx.measureText(st.label).width;
      for (let k = 0; k < 3; k++) {
        const ph = performance.now() / 1000 * 2 + k * 2.1;
        const sx = ccx + cw / 2 + Math.sin(ph) * (lw2 / 2 + 14);
        const sy = ccy - rf * 0.9 + Math.cos(ph * 1.3) * 8;
        const sa = 0.5 + 0.5 * Math.sin(ph * 3.1);
        ctx.fillStyle = `rgba(255,235,150,${0.35 + 0.5 * sa})`;
        ctx.font = `${Math.round(rf * 0.55)}px sans-serif`;
        ctx.fillText('\u2726', sx, sy);
      }
    }

    // ── TEXT — only the card's own plates. ──
    const famName = (opt.percName || '').toUpperCase();
    // top long box: NAME
    let nf = Math.round(Math.min(chh * 0.068, chh * bh(B.name) * 0.9));   // fits the box height too
    ctx.font = `400 ${nf}px SorryBossTitle, Anton, Impact, sans-serif`;
    while (ctx.measureText(famName).width > cw * bw(B.name) && nf > 11) {
      nf--; ctx.font = `400 ${nf}px SorryBossTitle, Anton, Impact, sans-serif`;
    }
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,1)';
    ctx.shadowBlur = 8;
    ctx.shadowOffsetY = 3;
    ctx.textBaseline = 'alphabetic';
    const nameX = ccx + cw * bcx(B.name);
    const nameY = _plateBaselineY(ctx, famName, ccy + chh * bcy(B.name));
    ctx.lineWidth = Math.max(3, nf * 0.14);
    ctx.strokeStyle = 'rgba(0,0,0,1)';
    ctx.strokeText(famName, nameX, nameY);
    ctx.fillStyle = '#f3ead9';
    ctx.fillText(famName, nameX, nameY);
    ctx.restore();

    // right small plate: LV only (rarity NEVER goes here) — centered + fitted
    const lvText = `LV ${Math.max(1, opt.resultLevel || 1)}`;
    let lf = Math.round(Math.min(chh * 0.036, chh * bh(B.lv) * 0.86));   // dead-center + fits the badge box
    ctx.font = `400 ${lf}px SorryBossTitle, Anton, Impact, sans-serif`;
    while (ctx.measureText(lvText).width > cw * bw(B.lv) && lf > 10) {
      lf--; ctx.font = `400 ${lf}px SorryBossTitle, Anton, Impact, sans-serif`;
    }
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,1)';
    ctx.shadowBlur = 8;
    ctx.shadowOffsetY = 3;
    ctx.textBaseline = 'alphabetic';
    const lvX = ccx + cw * bcx(B.lv), lvY = _plateBaselineY(ctx, lvText, ccy + chh * bcy(B.lv));
    ctx.lineWidth = Math.max(3, lf * 0.14);
    ctx.strokeStyle = 'rgba(0,0,0,1)';
    ctx.strokeText(lvText, lvX, lvY);
    ctx.fillStyle = '#ffd700';
    ctx.fillText(lvText, lvX, lvY);
    ctx.restore();

    // weapon cards: shell window gets the weapon art (percs have art baked in).
    // Only the live-draw fallback path — baked cards already carry the sprite.
    if (isWeapon && !bakedWeaponImg && opt.percIcon) {
      const wimg = getCachedImage(opt.percIcon);
      if (wimg) {
        // r7 (Dylan): the weapon fills the tier's EXACT window rect as large
        // as possible — uniform scale only (never stretched), centered both
        // axes, with a drop shadow. 4% inner padding so it never kisses the rails.
        const winW = cw * bw(B.win) * 0.92;
        const winH = chh * bh(B.win) * 0.92;
        const wcx = ccx + cw * bcx(B.win);
        const wcy = ccy + chh * bcy(B.win);
        const sc2 = Math.min(winW / Math.max(1, wimg.naturalWidth), winH / Math.max(1, wimg.naturalHeight));
        const ww2 = wimg.naturalWidth * sc2, wh2 = wimg.naturalHeight * sc2;
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.65)';
        ctx.shadowBlur = 14;
        ctx.shadowOffsetX = 7;
        ctx.shadowOffsetY = 8;
        ctx.drawImage(wimg, wcx - ww2 / 2, wcy - wh2 / 2, ww2, wh2);
        ctx.restore();
      }
    }

    // bottom large box: ONE effect line (value colored so the eye finds it)
    const effect = (opt.effectText || opt.percDescription || '').trim();
    if (effect) {
      let ef = Math.max(13, Math.round(Math.min(chh * 0.040, chh * bh(B.eff) * 0.42)));
      ctx.font = `600 ${ef}px SorryBossBody, "Barlow Condensed", sans-serif`;
      const maxW = cw * bw(B.eff);
      // wrap to at most 2 lines
      const words = effect.split(/\s+/);
      let lines: string[] = [];
      const buildLines = () => {
        lines = []; let cur = '';
        for (const wd of words) {
          const t2 = cur ? `${cur} ${wd}` : wd;
          if (ctx.measureText(t2).width <= maxW) cur = t2;
          else { if (cur) lines.push(cur); cur = wd; }
        }
        if (cur) lines.push(cur);
      };
      // r8 (Dylan: "what about when it is a lot of text — very hard to read"):
      // never shrink into dust. The font has a READABLE floor; long blurbs get
      // a 3rd line (tighter spacing), and anything still longer is cut with an
      // ellipsis. Short text renders exactly as before.
      const MAX_LINES = 3;
      const efMin = Math.max(12, Math.round(chh * 0.028));
      buildLines();
      while (lines.length > 2 && ef > efMin) {
        ef--; ctx.font = `600 ${ef}px SorryBossBody, "Barlow Condensed", sans-serif`;
        buildLines();
      }
      if (lines.length > MAX_LINES) {
        let last = lines[MAX_LINES - 1] + '…';
        while (ctx.measureText(last).width > maxW && last.length > 4) {
          last = last.slice(0, -2).trimEnd() + '…';
        }
        lines = lines.slice(0, MAX_LINES);
        lines[MAX_LINES - 1] = last;
      }
      const nLines = Math.min(lines.length, MAX_LINES);
      const lineHF = bh(B.eff) * (nLines >= 3 ? 0.34 : 0.5);   // 3 lines pack tighter, still inside the plate
      const numRe = /([+\-]\d[\d.,]*%?(?:\/s)?)/;   // sign REQUIRED so '.50 Cal Sniper' stays white
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,1)';
      ctx.shadowBlur = 7;
      ctx.shadowOffsetY = 3;
      // true-center the line BLOCK on the box center: first line's measured
      // glyph box sets the baseline offset, uniform line spacing below it
      ctx.textBaseline = 'alphabetic';
      let ey = _plateBaselineY(ctx, lines[0] ?? '', ccy + chh * bcy(B.eff))
        - (nLines - 1) * chh * lineHF * 0.5;
      for (const line of lines.slice(0, MAX_LINES)) {
        const m2 = line.match(numRe);
        if (m2 && m2.index !== undefined) {
          // Dylan's font spec: prose in Barlow (SorryBossBody), the BUFF
          // NUMBER in Anton (SorryBossTitle) so the eye snaps to it.
          const before = line.slice(0, m2.index);
          const num = m2[1];
          const after = line.slice(m2.index + num.length);
          const bodyFont = `600 ${ef}px SorryBossBody, "Barlow Condensed", sans-serif`;
          const numFont = `400 ${ef}px SorryBossTitle, Anton, Impact, sans-serif`;
          ctx.font = bodyFont;
          const wB = ctx.measureText(before).width;
          const wA = ctx.measureText(after).width;
          ctx.font = numFont;
          const wN = ctx.measureText(num).width;
          let x0 = ccx + cw / 2 - (wB + wN + wA) / 2;
          ctx.textAlign = 'left';
          ctx.font = bodyFont;
          ctx.fillStyle = '#efe9da';
          ctx.fillText(before, x0, ey); x0 += wB;
          ctx.font = numFont;
          ctx.fillStyle = '#4dd8ff';
          ctx.fillText(num, x0, ey); x0 += wN;
          ctx.font = bodyFont;
          ctx.fillStyle = '#efe9da';
          ctx.fillText(after, x0, ey);
          ctx.textAlign = 'center';
        } else {
          ctx.fillStyle = '#efe9da';
          ctx.fillText(line, ccx + cw / 2, ey);
        }
        ey += chh * lineHF;
      }
      ctx.restore();
      if (opt.luckBoosted) {
        ctx.font = `${Math.round(ef * 0.9)}px "Share Tech Mono", monospace`;
        ctx.fillText('\ud83c\udf40', ccx + cw * 0.88, ccy + chh * (bcy(B.eff) - 0.05));
      }
    }

    // bottom small plate: CATEGORY — perc cards always say "<CAT> PERK"
    // (Dylan r8: "always the word PERK after it if it is a perk")
    const cat = isWeapon ? 'WEAPON' : `${opt.category || 'UTILITY'} PERK`;
    // 22.87 polish (Dylan): "damage, economy, weapon should be white and much larger"
    let cf = Math.round(Math.min(chh * 0.047, chh * bh(B.cat) * 0.9));   // r3 — larger; fits the box
    ctx.font = `400 ${cf}px SorryBossTitle, Anton, Impact, sans-serif`;
    while (ctx.measureText(cat).width > cw * bw(B.cat) && cf > 10) {
      cf--; ctx.font = `400 ${cf}px SorryBossTitle, Anton, Impact, sans-serif`;
    }
    ctx.save();
    // r8 (Dylan): "hard to read the white text on the gold" — heavier, fully
    // opaque shadow + thicker stroke on the plate texts
    ctx.shadowColor = 'rgba(0,0,0,1)';
    ctx.shadowBlur = 8;
    ctx.shadowOffsetY = 3;
    ctx.textBaseline = 'alphabetic';
    const catX = ccx + cw * bcx(B.cat);
    const catY = _plateBaselineY(ctx, cat, ccy + chh * bcy(B.cat));
    ctx.lineWidth = Math.max(3, cf * 0.16);
    ctx.strokeStyle = 'rgba(0,0,0,1)';
    ctx.strokeText(cat, catX, catY);
    ctx.fillStyle = '#f3ead9';
    ctx.fillText(cat, catX, catY);
    ctx.restore();

    // tiny 1/2/3 keyboard hint, top-left corner (subtle, not a new plate)
    ctx.font = `400 ${Math.round(chh * 0.022)}px SorryBossTitle, Anton, Impact, sans-serif`;
    ctx.fillStyle = 'rgba(240,236,224,0.5)';
    ctx.fillText(`${i + 1}`, ccx + cw * 0.075, ccy + chh * 0.045);

    if (_CARDCAL && prog >= 1) {
      _drawCardCalOverlay(ctx, ccx, ccy, cw, chh, dispRarity, B);
      _cardcalRects[i] = { x: ccx, y: ccy, w: cw, h: chh };
    }

    ctx.restore();
  }

  if (hoverIdx >= 0 && hoverIdx < cardCount && _gamepadIsActiveInput()) {
    const fcx = L.startX + hoverIdx * (L.cardW + L.gap);
    drawFocusBox(ctx, fcx - 6, L.cardY - 6, L.cardW + 12, L.cardH + 12, 14);
  }

  _drawSkipRerollButtons(ctx, w, L.cardY + L.cardH + 64, state.rerollsLeft);
  _drawPickTaunt(ctx, w, h, L.cardY + L.cardH + 64, 'CHOOSE WISELY, LOSER.');
  // pose + quip draw in the post-HUD pass so the survivor stands IN FRONT of
  // the cards and the stats note (Dylan: "his head is being cut off").
  ctx.restore();
}

// ─── Shrine Choice Screen ────────────────────────────────────────────────

// BETA 22.92 — SHRINE tile slam clock (mirrors the level-up / wild-card clocks)
let _shOptionsRef: unknown = null;
let _shOpenAt = 0;
let _shSlamPlayed: boolean[] = [false, false, false];

function renderShrineChoiceScreen(
  ctx: CanvasRenderingContext2D, state: GameState, w: number, h: number
) {
  // BETA 22.92 — SHRINE rebuilt on Dylan's 26 hand-made sigil TILES (icon +
  // baked stat label + a clean value zone). Same slam-in presentation as the
  // perk / wild-card screens, just simpler: one square tile per blessing, the
  // code draws the buff value into the tile's value zone. Old parchment-frame
  // shrine screen + v2_bg_shrine deleted. Corner survivor + quip still draw in
  // the post-HUD pass (Dylan: "i like the guy still popping up in the corner").
  ctx.save();
  ctx.fillStyle = 'rgba(0,0,0,0.74)';
  ctx.fillRect(0, 0, w, h);
  drawLiquidNeon(ctx, w, h, NEON_PURPLE);

  const buffs = getShrineBuffDisplayOptions();
  const hoverIdx = getShrineHoverIndex();
  const n = buffs.length;
  if (n === 0) { ctx.restore(); return; }

  // fresh offer (or reroll) restarts the slam-in clock
  const _ref = buffs.map(b => b.stat + b.valueText).join('|');
  if (_shOptionsRef !== _ref) {
    _shOptionsRef = _ref;
    _shOpenAt = performance.now();
    _shSlamPlayed = [false, false, false];
  }

  const L = computeChoiceCardLayout(w, h, n, !!state.hudHidden, 'shrine');
  const isGoldenShrine = getShrineIsGolden();
  // BETA 22.93 — tiles FIT their slot (Dylan: "too big and too close
  // together"). 22.92's ts = cardW * 1.28 drew each square WIDER than its
  // slot pitch (cardW + gap), so the three tiles physically overlapped.
  const ts = Math.min(L.cardH * 0.80, L.cardW * 0.96);
  const rowCY = L.cardY + L.cardH / 2;

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const tf = Math.max(30, Math.round(h * 0.062));
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.95)'; ctx.shadowBlur = 10; ctx.shadowOffsetY = 4;
  ctx.font = `400 ${tf}px SorryBossTitle, Anton, Impact, sans-serif`;
  ctx.lineWidth = Math.max(3, tf * 0.09); ctx.strokeStyle = 'rgba(0,0,0,0.85)';
  const _title = isGoldenShrine ? 'GOLDEN SHRINE' : 'SHRINE';
  ctx.strokeText(_title, w / 2, rowCY - ts * 0.62 - Math.max(58, h * 0.086));
  ctx.fillStyle = isGoldenShrine ? '#ffd700' : '#e7c6ff';
  ctx.fillText(_title, w / 2, rowCY - ts * 0.62 - Math.max(58, h * 0.086));
  ctx.font = `400 ${Math.max(15, Math.round(h * 0.026))}px SorryBossTitle, Anton, Impact, sans-serif`;
  ctx.fillStyle = '#e9dfc8';
  ctx.fillText(isGoldenShrine ? 'ALL LEGENDARY · THEY ALL STACK' : 'CHOOSE A BLESSING · THEY ALL STACK',
    w / 2, rowCY - ts * 0.62 - Math.max(28, h * 0.045));
  ctx.restore();

  const tNow = (performance.now() - _shOpenAt) / 1000;

  for (let i = 0; i < n; i++) {
    const buff = buffs[i];
    const isHovered = i === hoverIdx;
    const isNeg = buff.rarity === 'negative';
    const st = CARD_RARITY_STYLE[buff.rarity] ?? CARD_RARITY_STYLE.common;
    const label = isNeg ? 'CURSE' : st.label;
    const labelColor = isNeg ? '#ff5b47' : st.text;
    const slotCX = L.startX + i * (L.cardW + L.gap) + L.cardW / 2;
    const tile = shrineTilePath(buff.stat);
    const img = tile ? getCachedImage(tile) : null;

    const delay = 0.15 + i * 0.08;
    const prog = Math.max(0, Math.min(1, (tNow - delay) / 0.25));
    if (prog <= 0) continue;
    if (prog >= 1 && !_shSlamPlayed[i]) {
      _shSlamPlayed[i] = true;
      // BETA 22.93 — stone slab slam, not the paper card slap (Dylan:
      // "these are stone blocks").
      try { playShrineTileSlamSfx(buff.rarity); } catch { /* sfx optional */ }
    }
    const eb = 1 + 2.2 * Math.pow(prog - 1, 3) + 1.2 * Math.pow(prog - 1, 2);
    const hoverScale = _choiceCardScaleTick('shrine', i, isHovered);
    const scale = (1.55 - 0.55 * eb) * (prog >= 1 ? hoverScale : 1);
    const yDrop = (1 - eb) * -70;
    const size = ts * scale;
    const tx = slotCX - size / 2;
    const ty = rowCY - size / 2 + yDrop;

    ctx.save();
    ctx.globalAlpha = Math.min(1, prog * 1.6);

    if (tile) {
      const glowSpr = _getCardGlowSprite(tile, isNeg ? '255,80,60' : st.glow);
      if (glowSpr) {
        // BETA 22.95 — liquid neon: solid + tight (see level-up note).
        const pulse = isHovered
          ? 0.92 + 0.08 * Math.sin(performance.now() / 190 + i * 1.7)
          : 0.60 + 0.12 * Math.sin(performance.now() / 430 + i * 1.7);
        const gs = isHovered ? 1.05 : 1.034;
        ctx.save();
        ctx.globalAlpha = Math.max(0, pulse) * Math.min(1, prog * 1.6);
        ctx.filter = `blur(${Math.max(4, size * 0.018)}px)`;   // 22.95 tighter
        ctx.drawImage(glowSpr, tx - size * (gs - 1) / 2, ty - size * (gs - 1) / 2, size * gs, size * gs);
        ctx.filter = 'none';
        ctx.restore();
      }
    }

    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.55)';
    ctx.shadowBlur = 18; ctx.shadowOffsetX = 8; ctx.shadowOffsetY = 8;
    if (img) ctx.drawImage(img, tx, ty, size, size);
    else { ctx.fillStyle = '#141018'; ctx.fillRect(tx, ty, size, size); }
    ctx.restore();

    // BETA 22.93 — the tile's OWN colors glow (Dylan: "would be cool if the
    // color on the shrine sprite themselves glowed a little"). A blurred
    // additive re-draw of the tile makes its bright areas (the sigil + the
    // colored inner rim) bloom in their own color; the dark stone stays dark.
    if (img) {
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      const selfPulse = 0.72 + 0.28 * Math.sin(performance.now() / 480 + i * 2.1);
      ctx.globalAlpha = (isHovered ? 0.34 : 0.20) * selfPulse * Math.min(1, prog * 1.6);
      ctx.filter = `blur(${Math.max(3, size * 0.018)}px)`;
      ctx.drawImage(img, tx, ty, size, size);
      ctx.filter = 'none';
      ctx.restore();
    }

    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    const rf = Math.max(17, Math.round(ts * 0.10));
    ctx.font = `400 ${rf}px SorryBossTitle, Anton, Impact, sans-serif`;
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.95)'; ctx.shadowBlur = 7; ctx.shadowOffsetY = 4;
    ctx.lineWidth = Math.max(3, rf * 0.11); ctx.strokeStyle = 'rgba(0,0,0,0.9)';
    ctx.strokeText(label, slotCX, ty - rf * 0.85);
    ctx.fillStyle = labelColor;
    ctx.fillText(label, slotCX, ty - rf * 0.85);
    ctx.restore();

    const Z = TILE_VALUE_ZONE;
    const vcx = tx + size * Z.cx, vcy = ty + size * Z.cy;
    let vf = Math.round(size * 0.155);
    ctx.font = `400 ${vf}px SorryBossTitle, Anton, Impact, sans-serif`;
    while ((ctx.measureText(buff.valueText).width > size * Z.maxW || vf > size * Z.maxH * 1.15) && vf > 12) {
      vf--; ctx.font = `400 ${vf}px SorryBossTitle, Anton, Impact, sans-serif`;
    }
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,1)'; ctx.shadowBlur = 8; ctx.shadowOffsetY = 3;
    ctx.lineWidth = Math.max(3, vf * 0.14); ctx.strokeStyle = 'rgba(0,0,0,1)';
    ctx.strokeText(buff.valueText, vcx, vcy + vf * 0.05);
    ctx.fillStyle = isNeg ? '#ff5b47' : '#ffffff';
    ctx.fillText(buff.valueText, vcx, vcy + vf * 0.05);
    if (buff.luckBoosted && !isNeg) {
      ctx.font = `${Math.round(vf * 0.72)}px "Share Tech Mono", monospace`;
      ctx.fillText('🍀', vcx + size * Z.maxW * 0.5 + vf * 0.4, vcy);
    }
    ctx.restore();

    ctx.font = `400 ${Math.max(11, Math.round(ts * 0.05))}px SorryBossTitle, Anton, Impact, sans-serif`;
    ctx.fillStyle = 'rgba(230,224,208,0.6)';
    ctx.fillText(isNeg ? 'RISK IT FOR MORE ENEMIES' : 'STACKS INFINITELY', slotCX, ty + size + rf * 0.6);

    ctx.font = `400 ${Math.round(size * 0.05)}px SorryBossTitle, Anton, Impact, sans-serif`;
    ctx.fillStyle = 'rgba(240,236,224,0.5)';
    ctx.fillText(`${i + 1}`, tx + size * 0.08, ty + size * 0.07);

    ctx.restore();
  }

  if (hoverIdx >= 0 && hoverIdx < n && _gamepadIsActiveInput()) {
    const fcx = L.startX + hoverIdx * (L.cardW + L.gap) + L.cardW / 2;
    drawFocusBox(ctx, fcx - ts / 2 - 6, rowCY - ts / 2 - 6, ts + 12, ts + 12, 14);
  }

  // BETA 22.93 — buttons draw at the SAME y as their registered hit rects
  // (L.cardY + L.cardH + 28, see registerFocusScreen 'shrine_cards'). The old
  // rowCY + ts/2 + 64 only lined up when ts ≈ cardH*0.86; with the smaller
  // tiles the paint would sit 60px above the clickable rect (the 16.82 bug).
  _drawSkipRerollButtons(ctx, w, L.cardY + L.cardH + 28, state.rerollsLeft);
  _drawPickTaunt(ctx, w, h, rowCY + ts / 2 + 64, 'TOUCH THE SHRINE, TAKE THE DEAL.');
  ctx.restore();
}

// ─── WildCardMachine Spin Screen (3-slot slot-machine reveal) ──────────────────────────

let _chestSpinFlourishPlayed = false;
const _slotLandTimes: [number, number, number] = [0, 0, 0];  // performance.now() when each slot landed

// BETA 17.32 — chest_spin perf cache. Chrome DevTools MCP measured FPS
// dropping 60 → 51 during chest_spin overlay. Root cause: for every
// LANDED card, every frame we ran (a) a name-shrink loop that called
// ctx.measureText() up to 12× while toggling ctx.font each iteration,
// and (b) a description word-wrap loop that called ctx.measureText()
// once per word per frame. 3 cards × ~40 measureText calls/frame =
// ~120 redundant measurements. Item text doesn't change once landed;
// memoize the wrapped lines + shrunk name-font-size by (item.id, cardW).
// Cleared on phase exit (see _clearChestSpinTextCache call below).
interface _ChestCardText { name: string; nameFontSize: number; descLines: string[]; }
const _chestCardTextCache = new Map<string, _ChestCardText>();
function _chestCacheKey(itemId: string, cardW: number): string {
  // Round cardW to 8px buckets so minor responsive resize doesn't blow cache.
  return `${itemId}|${Math.round(cardW / 8) * 8}`;
}
function _clearChestSpinTextCache(): void { _chestCardTextCache.clear(); }
// BETA 21.75 — expose to the Balance Dashboard (via window, no import/cycle) so a
// live tuning edit repaints the gold-spin card's wrapped text too.
if (typeof window !== 'undefined') {
  (window as unknown as { __clearChestCardCache?: () => void }).__clearChestCardCache = _clearChestSpinTextCache;
}

// Rarity border colors per the design spec
// BETA 20.21 — 3-tier (Dylan): common GREEN, rare PURPLE, legendary GOLD. No
// grey/blue. uncommon/epic mirror their collapsed tier (no longer rolled).
const _SPIN_RARITY_COLORS: Record<string, string> = {
  common:    '#39ff14',
  uncommon:  '#39ff14',
  rare:      '#b15cff',
  epic:      '#b15cff',
  legendary: '#ffd700',
};

function renderChestSpinScreen(
  ctx: CanvasRenderingContext2D, state: GameState, w: number, h: number
) {
  const spin = getChestSpinDisplay();
  if (!spin) return;

  // BETA 15.71 — Per Dylan: "chest are doing 2 sounds each? i just for now
  // want it to trigger the open chest sound. there is no sound for final
  // showing of what the items landed on."
  // Was: per-slot playGoldPickup() (up to 3 plays during reveal) + a
  // playLevelUp() "final flourish" when all 3 had landed. That meant
  // chest_open.wav AND level_up.wav both played for one chest open —
  // two of Dylan's user-provided samples colliding.
  // Now: ONLY the playChestOpenFree/Paid call at the engine.ts handleInteract
  // chest paths fires audio. The spin reveal is silent.
  // The _chestSpinFlourishPlayed flag is kept (still reset on chest-spin
  // start in handleInteract:1085) for future re-introduction of a reveal
  // sound if Dylan provides one.

  ctx.save();
  ctx.fillStyle = 'rgba(0,0,0,0.88)';
  ctx.fillRect(0, 0, w, h);
  // BETA 20.20 — gold lightning from the INSTANT the chest opens (the spin/
  // reveal animation), not just once the cards land. Same effect as the
  // chest_choice screen so it's continuous through the whole chest sequence.
  drawLiquidNeon(ctx, w, h, NEON_GOLD);

  const isGold = spin.chestType === 'gold';
  const accentColor = spin.color;

  // BETA 16.89 — Title position now derived from layout helper so it
  // sits 60px above the (vertically centered) card row. Was h*0.12 = a
  // floating title at the top with cards way below — visually
  // disconnected on tall screens.
  const _spinLayoutTitle = computeChoiceCardLayout(w, h, 3, !!state.hudHidden, 'chest');
  // BETA 22.62 v2 — same background art as chest_choice so the spin ->
  // choice handoff doesn't restyle the screen mid-reveal.
  void accentColor;
  void _spinLayoutTitle.titleY;
  _drawPickBg(ctx, w, h, '/pick-kit/v2_bg_loot.png');
  // BETA 22.89 \u2014 buncha era over: the spin is the MACHINE working the vend.
  // (Spin visuals themselves still ride the old pick-kit \u2014 follow-up pass.)
  _drawPickHeaderText(ctx, w, h,
    isGold ? 'GOLD WILD CARD MACHINE' : 'WILD CARD MACHINE', '');

  // BETA 16.89 — reuse the title-time layout (same 3-card chest layout)
  // so cards lock at identical x/y/w/h through spin → choice handoff.
  const cardW = _spinLayoutTitle.cardW;
  const cardH = _spinLayoutTitle.cardH;
  const gap = _spinLayoutTitle.gap;
  const startX = _spinLayoutTitle.startX;
  const cardY = _spinLayoutTitle.cardY;
  const now = performance.now();

  for (let i = 0; i < spin.slots.length; i++) {
    const slot = spin.slots[i];
    const cx = startX + i * (cardW + gap);

    // BETA 16.42 — [N] key indicator now drawn INSIDE the card body (at
    // KEY_Y) below to match chest_choice. The above-card rendering left
    // a visible vertical jump when the choice phase moved it inside.

    const rarityCol = _SPIN_RARITY_COLORS[slot.item.rarity] ?? '#9e9e9e';
    const borderCol = rarityCol; // always full rarity color; flickerAlpha handles pulse

    // Track land time for glow pulse
    if (slot.justLanded) _slotLandTimes[i] = now;
    const landAge = _slotLandTimes[i] > 0 ? (now - _slotLandTimes[i]) : (slot.landed ? 9999 : 0);
    // Glow fades from 48px (on land) down to 18px over 400ms — no scale distortion
    const glowBlur = slot.landed
      ? Math.round(18 + Math.max(0, 30 * (1 - landAge / 400)))
      : 4;

    ctx.save();

    // Flicker alpha: spinning cards pulse rapidly between 0.45 and 0.85
    const flickerAlpha = slot.landed
      ? 1.0
      : 0.45 + 0.4 * Math.abs(Math.sin(now / 80 + i * 1.4));
    ctx.globalAlpha = flickerAlpha;

    // BETA 16.31 — fill bg, then fire-glow once landed.
    // While spinning, keep the simple-stroke approach so the flicker
    // animation doesn't fight the spin's own random flicker.
    // v2 — the card IS the per-rarity art (flickers with the spin's item,
    // then locks on the landed rarity — matches chest_choice exactly).
    void glowBlur;
    void borderCol;
    _drawPickCardFrame(ctx, cx, cardY, cardW, cardH, slot.item?.rarity ?? 'common', i, false, 1);

    // BETA 16.42 — Per Dylan: "on chest its still doin some kind of size
    // change at the very end. the position and size should be the exact
    // same for the slot machine effect. its better. the inside of the
    // card moves." Aligned spin layout to chest_choice EXACTLY (same
    // PAD_X / ICON_PANEL_Y / ICON_PANEL_H / DIVIDER_Y / RARITY_Y /
    // NAME_Y / DESC_Y constants). No more snap on the inside.
    const PAD_X         = 18;
    const KEY_Y         = cardY + 30;
    const ICON_PANEL_Y  = cardY + 54;
    const ICON_PANEL_H  = 198;
    const ICON_PANEL_W  = cardW - PAD_X * 2;
    const ICON_PANEL_X  = cx + PAD_X;
    const DIVIDER_Y     = ICON_PANEL_Y + ICON_PANEL_H + 14;
    const RARITY_Y      = DIVIDER_Y + 22;
    const NAME_Y        = DIVIDER_Y + 56;
    const DESC_Y        = DIVIDER_Y + 92;

    // [N] key indicator (matches choice screen)
    ctx.save();
    ctx.globalAlpha = slot.landed ? 1.0 : flickerAlpha;
    ctx.font = 'bold 24px "Share Tech Mono", monospace';
    ctx.fillStyle = slot.landed ? '#ffd700' : 'rgba(255,255,255,0.6)';
    ctx.textAlign = 'center';
    ctx.fillText(`[${i + 1}]`, cx + cardW / 2, KEY_Y);
    ctx.restore();

    // Icon panel — inset darker box (same as choice)
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    roundRect(ctx, ICON_PANEL_X, ICON_PANEL_Y, ICON_PANEL_W, ICON_PANEL_H, 4);
    ctx.fill();

    // Icon — centered inside the panel (matches choice screen sizing)
    const iconSize = Math.min(ICON_PANEL_H - 24, ICON_PANEL_W - 24);
    const iconX = ICON_PANEL_X + (ICON_PANEL_W - iconSize) / 2;
    const iconY = ICON_PANEL_Y + (ICON_PANEL_H - iconSize) / 2;
    const img = getCachedImage(slot.iconUrl);
    if (img) {
      ctx.imageSmoothingEnabled = false;
      if (slot.justLanded) {
        ctx.filter = 'brightness(2.2)';
        drawContainCenter(ctx, img, iconX, iconY, iconSize, iconSize);
        ctx.filter = 'none';
      } else {
        drawContainCenter(ctx, img, iconX, iconY, iconSize, iconSize);
      }
      ctx.imageSmoothingEnabled = true;
    } else if (!slot.landed) {
      // Spinning placeholder centered in the panel.
      const spinPhase = (now / 300 + i * 0.8) % 1;
      ctx.save();
      ctx.globalAlpha *= 0.55;
      ctx.fillStyle = rarityCol + '33';
      ctx.fillRect(iconX + 20, iconY + 20, iconSize - 40, iconSize - 40);
      ctx.globalAlpha = flickerAlpha * 0.9;
      ctx.font = `bold ${Math.round(60 + 8 * Math.abs(Math.sin(spinPhase * Math.PI)))}px "Share Tech Mono", monospace`;
      ctx.textAlign = 'center';
      ctx.fillStyle = rarityCol;
      ctx.shadowColor = rarityCol;
      ctx.shadowBlur = 12;
      ctx.textBaseline = 'middle';
      ctx.fillText('?', iconX + iconSize / 2, iconY + iconSize / 2);
      ctx.shadowBlur = 0;
      ctx.textBaseline = 'middle';
      ctx.restore();
    } else {
      ctx.fillStyle = rarityCol + '22';
      ctx.fillRect(iconX, iconY, iconSize, iconSize);
    }

    ctx.textAlign = 'center';

    if (slot.landed) {
      // Use the SAME constants as chest_choice — all values aliased so
      // the snap from spin → choice is invisible.
      const SPIN_DIVIDER_Y = DIVIDER_Y;
      const SPIN_RARITY_Y  = RARITY_Y;
      const SPIN_NAME_Y    = NAME_Y;
      const SPIN_DESC_Y    = DESC_Y;

      // Divider — thick rarity-colored
      ctx.strokeStyle = rarityCol;
      ctx.lineWidth = 2.5;
      ctx.shadowColor = rarityCol;
      ctx.shadowBlur = 8;
      ctx.beginPath();
      ctx.moveTo(cx + 18, SPIN_DIVIDER_Y);
      ctx.lineTo(cx + cardW - 18, SPIN_DIVIDER_Y);
      ctx.stroke();
      ctx.shadowBlur = 0;

      // Rarity tag
      ctx.font = 'bold 15px "Share Tech Mono", monospace';
      ctx.fillStyle = rarityCol;
      ctx.fillText(slot.item.rarity.toUpperCase(), cx + cardW / 2, SPIN_RARITY_Y);

      // BETA 17.32 — perf cache. Compute name-shrink + description
      // word-wrap ONCE per landed card per cardW; reuse across frames.
      // Was: 12-iteration measureText loop for name + N-word measureText
      // loop for description, every frame, for every card.
      const _cacheKey = _chestCacheKey(slot.item.id, cardW);
      let _cached = _chestCardTextCache.get(_cacheKey);
      if (!_cached) {
        // Name shrink (measureText loop — runs once, then cached)
        let nfs = 26;
        ctx.font = `bold ${nfs}px "Share Tech Mono", monospace`;
        while (ctx.measureText(slot.item.name).width > cardW - 28 && nfs > 14) {
          nfs--;
          ctx.font = `bold ${nfs}px "Share Tech Mono", monospace`;
        }
        // Description word-wrap (measureText per word — runs once, then cached)
        ctx.font = 'bold 17px "Share Tech Mono", monospace';
        const dWords = injectStatEmojis(slot.item.description).split(' ');
        const descLines: string[] = [];
        let dLine = '';
        const maxLines = 4;
        for (const word of dWords) {
          const test = dLine + (dLine ? ' ' : '') + word;
          if (ctx.measureText(test).width > cardW - 28 && dLine) {
            if (descLines.length < maxLines) descLines.push(dLine);
            dLine = word;
          } else { dLine = test; }
        }
        if (dLine && descLines.length < maxLines) descLines.push(dLine);
        _cached = { name: slot.item.name, nameFontSize: nfs, descLines };
        _chestCardTextCache.set(_cacheKey, _cached);
      }

      // Name on the black grungy bar (pick-kit, matches chest_choice)
      {
        const bar = getCachedImage('/pick-kit/bar_black.png');
        const bw2 = cardW - 30, bh2 = 34;
        if (bar) ctx.drawImage(bar, cx + 15, SPIN_NAME_Y - bh2 / 2 - 2, bw2, bh2);
        else { ctx.fillStyle = 'rgba(10,8,6,0.85)'; ctx.fillRect(cx + 15, SPIN_NAME_Y - 18, bw2, 34); }
      }
      void rarityCol;
      ctx.font = `bold ${_cached.nameFontSize}px "Share Tech Mono", monospace`;
      ctx.fillStyle = '#ffffff';
      ctx.fillText(slot.item.name, cx + cardW / 2, SPIN_NAME_Y - 1);

      // Description — typewriter ink on parchment
      ctx.font = 'bold 15px "Share Tech Mono", monospace';
      ctx.fillStyle = PICK_INK;
      let dY = SPIN_DESC_Y;
      for (const line of _cached.descLines) {
        ctx.fillText(line, cx + cardW / 2, dY);
        dY += 20;
      }
    }

    ctx.globalAlpha = 1.0;

    ctx.restore(); // end bounce scale transform
  }

  // Bottom status line
  ctx.textAlign = 'center';
  ctx.font = 'bold 18px "Share Tech Mono", monospace';
  if (spin.allLanded) {
    ctx.fillStyle = accentColor;
    ctx.shadowColor = accentColor;
    ctx.shadowBlur = 16;
    ctx.fillText('! SELECTING YOUR LOOT !', w / 2, cardY + cardH + 36);
    ctx.shadowBlur = 0;
  } else {
    ctx.fillStyle = 'rgba(255,255,255,0.35)';
    ctx.fillText('OPENING CHEST...', w / 2, cardY + cardH + 36);
  }

  // SKIP / REROLL buttons — grayed out during spin, full opacity when settled
  ctx.globalAlpha = spin.allLanded ? 1.0 : 0.28;
  _drawSkipRerollButtons(ctx, w, cardY + cardH + 58, state.rerollsLeft);
  ctx.globalAlpha = 1.0;

  ctx.restore();
}

// ─── WildCardMachine Choice Screen ────────────────────────────────────────────────

function renderChestChoiceScreen(
  ctx: CanvasRenderingContext2D, state: GameState, w: number, h: number
) {
  // BETA 22.89 — WILD CARDS: the machine's vend screen rides the perk-card
  // presentation (mirrors renderLevelUpScreen: see-through dark backdrop,
  // staggered slam-in, rarity backlight, live text in Dylan's calibrated
  // tier boxes). Card art is the BAKED wild card whose badge overhangs the
  // card body — WILD_CARD_IMG_BODY maps the body region onto the card rect.
  ctx.save();
  ctx.fillStyle = 'rgba(0,0,0,0.74)';
  ctx.fillRect(0, 0, w, h);
  drawLiquidNeon(ctx, w, h, NEON_GOLD);

  const items = getChestOptionDisplayData();
  const hoverIdx = getChestHoverIndex();
  const n = items.length;
  if (n === 0) { ctx.restore(); return; }

  // fresh offer (or reroll) → restart the slam-in clock. The display array is
  // rebuilt every call, so a signature stands in for identity here.
  const offerKey = items.map(it => `${it.itemId}:${it.tier}`).join('|');
  if (offerKey !== _wcOfferKey) {
    _wcOfferKey = offerKey;
    _wcOpenAt = performance.now();
    _wcSlamPlayed = [false, false, false];
  }

  const L = computeChoiceCardLayout(w, h, n, !!state.hudHidden, 'chest');

  // header stack — same geometry as the level-up title block
  {
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const _rarityF = Math.max(16, Math.round(L.cardH * 0.058));
    const subRowY = L.cardY - _rarityF * 2.9;
    const titleRowY = subRowY - Math.max(30, h * 0.055);
    const tf = Math.max(30, Math.round(h * 0.062));
    ctx.font = `400 ${tf}px SorryBossTitle, Anton, Impact, sans-serif`;
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.95)';
    ctx.shadowBlur = 10;
    ctx.shadowOffsetY = 4;
    ctx.lineWidth = Math.max(3, tf * 0.09);
    ctx.strokeStyle = 'rgba(0,0,0,0.85)';
    ctx.strokeText('WILD CARDS', w / 2, titleRowY);
    ctx.fillStyle = '#ffd700';
    ctx.fillText('WILD CARDS', w / 2, titleRowY);
    ctx.restore();
    ctx.font = `400 ${Math.max(15, Math.round(h * 0.026))}px SorryBossTitle, Anton, Impact, sans-serif`;
    ctx.fillStyle = '#e9dfc8';
    ctx.fillText('PICK ONE', w / 2, subRowY);
  }

  const tNow = (performance.now() - _wcOpenAt) / 1000;
  const [ibx0, iby0, ibx1, iby1] = WILD_CARD_IMG_BODY;

  for (let i = 0; i < n; i++) {
    const item = items[i];
    const cx = L.startX + i * (L.cardW + L.gap);
    const isHovered = i === hoverIdx;
    const st = CARD_RARITY_STYLE[item.tier] ?? CARD_RARITY_STYLE.common;
    const B = getCardTierBoxes(item.tier);
    const bcx = (r: [number, number, number, number]) => (r[0] + r[2]) / 2;
    const bcy = (r: [number, number, number, number]) => (r[1] + r[3]) / 2;
    const bw = (r: [number, number, number, number]) => r[2] - r[0];
    const bh = (r: [number, number, number, number]) => r[3] - r[1];
    const isBaked = !!item.cardArt;
    const artUrl = item.cardArt ?? cardShellPath(item.tier);
    const img = getCachedImage(artUrl);

    // ── slam-in: overlay 150ms, then card i lands over 250ms (80ms stagger) ──
    const delay = 0.15 + i * 0.08;
    const prog = Math.max(0, Math.min(1, (tNow - delay) / 0.25));
    if (prog <= 0) continue;
    if (prog >= 1 && !_wcSlamPlayed[i]) {
      _wcSlamPlayed[i] = true;
      try { playCardSlamSfx(item.tier); } catch { /* sfx optional */ }
    }
    // ease-out-back: overshoot then settle = the impact bounce
    const eb = 1 + 2.2 * Math.pow(prog - 1, 3) + 1.2 * Math.pow(prog - 1, 2);
    const hoverScale = _choiceCardScaleTick('chest', i, isHovered);
    const scale = (1.55 - 0.55 * eb) * (prog >= 1 ? hoverScale : 1);
    const yDrop = (1 - eb) * -70;
    const cw = L.cardW * scale, chh = L.cardH * scale;
    const ccx = cx + (L.cardW - cw) / 2, ccy = L.cardY + (L.cardH - chh) / 2 + yDrop;

    // baked wild-card art carries a transparent pad (badge overhang) — scale
    // it so the body region fills the card rect; legacy shells draw 1:1
    const dw = isBaked ? cw / (ibx1 - ibx0) : cw;
    const dh = isBaked ? chh / (iby1 - iby0) : chh;
    const dx2 = isBaked ? ccx - dw * ibx0 : ccx;
    const dy2 = isBaked ? ccy - dh * iby0 : ccy;

    ctx.save();
    ctx.globalAlpha = Math.min(1, prog * 1.6);

    // ── tier BACKLIGHT — blurred colored silhouette of the card art itself ──
    {
      const glowSpr = _getCardGlowSprite(artUrl, st.glow);
      if (glowSpr) {
        // BETA 22.95 — liquid neon: glow more SOLID + tighter (Dylan: "a
        // little less see through. shrink some so the light behind each card
        // isnt touching the light behind the card next to it").
        const pulse = isHovered
          ? 0.92 + 0.08 * Math.sin(performance.now() / 190 + i * 1.7)
          : 0.60 + 0.12 * Math.sin(performance.now() / 430 + i * 1.7);
        const gs = isHovered ? 1.05 : 1.034;
        const gw = dw * gs, gh = dh * gs;
        ctx.save();
        ctx.globalAlpha = Math.max(0, pulse) * Math.min(1, prog * 1.6);
        ctx.filter = `blur(${Math.max(4, cw * (isHovered ? 0.022 : 0.016))}px)`;   // 22.95 tighter
        ctx.drawImage(glowSpr, dx2 - (gw - dw) / 2, dy2 - (gh - dh) / 2, gw, gh);
        ctx.filter = 'none';
        ctx.restore();
      }
    }

    // ── the card (drop shadow so it sits on the dark, never floats) ──
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.55)';
    ctx.shadowBlur = 18;
    ctx.shadowOffsetX = 9;
    ctx.shadowOffsetY = 8;
    if (img) ctx.drawImage(img, dx2, dy2, dw, dh);
    else { ctx.fillStyle = '#1c1813'; ctx.fillRect(ccx, ccy, cw, chh); }
    ctx.restore();

    // legacy fallback (item without baked wild-card art): icon in the window
    if (!isBaked && item.iconUrl) {
      const wimg = getCachedImage(item.iconUrl);
      if (wimg) {
        const winW = cw * bw(B.win) * 0.92, winH = chh * bh(B.win) * 0.92;
        const sc2 = Math.min(winW / Math.max(1, wimg.naturalWidth), winH / Math.max(1, wimg.naturalHeight));
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.65)';
        ctx.shadowBlur = 14;
        ctx.shadowOffsetX = 7;
        ctx.shadowOffsetY = 8;
        ctx.drawImage(wimg,
          ccx + cw * bcx(B.win) - wimg.naturalWidth * sc2 / 2,
          ccy + chh * bcy(B.win) - wimg.naturalHeight * sc2 / 2,
          wimg.naturalWidth * sc2, wimg.naturalHeight * sc2);
        ctx.restore();
      }
    }

    // impact dust — tier-colored specks kicked up right after the slam
    if (prog >= 1 && tNow < delay + 0.6) {
      const dt2 = (tNow - delay - 0.25) / 0.35;
      for (let k = 0; k < 7; k++) {
        const ang = (k / 7) * Math.PI + Math.PI;   // upward fan
        const dist = (12 + (k * 37 % 26)) * (0.3 + dt2);
        ctx.fillStyle = `rgba(${st.glow},${Math.max(0, 0.7 - dt2)})`;
        ctx.beginPath();
        ctx.arc(ccx + cw / 2 + Math.cos(ang + k) * dist * 2.2, ccy + chh - 8 + Math.sin(ang) * dist * 0.5, 2.2 - dt2, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // ── TIER LABEL — above the card, outside it, in the tier color ──
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const rf = Math.max(18, Math.round(L.cardH * 0.058));
    ctx.font = `400 ${rf}px SorryBossTitle, Anton, Impact, sans-serif`;
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.95)';
    ctx.shadowBlur = 7;
    ctx.shadowOffsetY = 4;
    ctx.lineWidth = Math.max(3, rf * 0.11);
    ctx.strokeStyle = 'rgba(0,0,0,0.9)';
    ctx.strokeText(st.label, ccx + cw / 2, ccy - rf * 0.95);
    ctx.fillStyle = st.text;
    ctx.fillText(st.label, ccx + cw / 2, ccy - rf * 0.95);
    ctx.restore();
    if (st.sparkle) {
      // legendary: little star glints orbiting the label
      const lw2 = ctx.measureText(st.label).width;
      for (let k = 0; k < 3; k++) {
        const ph = performance.now() / 1000 * 2 + k * 2.1;
        const sx = ccx + cw / 2 + Math.sin(ph) * (lw2 / 2 + 14);
        const sy = ccy - rf * 0.9 + Math.cos(ph * 1.3) * 8;
        const sa = 0.5 + 0.5 * Math.sin(ph * 3.1);
        ctx.fillStyle = `rgba(255,235,150,${0.35 + 0.5 * sa})`;
        ctx.font = `${Math.round(rf * 0.55)}px sans-serif`;
        ctx.fillText('✦', sx, sy);
      }
    }

    // ── TEXT — only the card's own plates (true-centered, no fudge) ──
    const cardName = (item.name || '').toUpperCase();
    let nf = Math.round(Math.min(chh * 0.068, chh * bh(B.name) * 0.9));
    ctx.font = `400 ${nf}px SorryBossTitle, Anton, Impact, sans-serif`;
    while (ctx.measureText(cardName).width > cw * bw(B.name) && nf > 11) {
      nf--; ctx.font = `400 ${nf}px SorryBossTitle, Anton, Impact, sans-serif`;
    }
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,1)';
    ctx.shadowBlur = 8;
    ctx.shadowOffsetY = 3;
    ctx.textBaseline = 'alphabetic';
    const nameX = ccx + cw * bcx(B.name);
    const nameY = _plateBaselineY(ctx, cardName, ccy + chh * bcy(B.name));
    ctx.lineWidth = Math.max(3, nf * 0.14);
    ctx.strokeStyle = 'rgba(0,0,0,1)';
    ctx.strokeText(cardName, nameX, nameY);
    ctx.fillStyle = '#f3ead9';
    ctx.fillText(cardName, nameX, nameY);
    ctx.restore();

    // right small plate: STACK multiplier when the tier grants more than ×1
    if (item.tierStacks > 1) {
      const stText = `x${item.tierStacks}`;
      let lf = Math.round(Math.min(chh * 0.036, chh * bh(B.lv) * 0.86));
      ctx.font = `400 ${lf}px SorryBossTitle, Anton, Impact, sans-serif`;
      while (ctx.measureText(stText).width > cw * bw(B.lv) && lf > 10) {
        lf--; ctx.font = `400 ${lf}px SorryBossTitle, Anton, Impact, sans-serif`;
      }
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,1)';
      ctx.shadowBlur = 8;
      ctx.shadowOffsetY = 3;
      ctx.textBaseline = 'alphabetic';
      const lvX = ccx + cw * bcx(B.lv), lvY = _plateBaselineY(ctx, stText, ccy + chh * bcy(B.lv));
      ctx.lineWidth = Math.max(3, lf * 0.14);
      ctx.strokeStyle = 'rgba(0,0,0,1)';
      ctx.strokeText(stText, lvX, lvY);
      ctx.fillStyle = '#ffd700';
      ctx.fillText(stText, lvX, lvY);
      ctx.restore();
    }

    // bottom large box: effect text (3-line wrap / readable floor / ellipsis —
    // the level-up rules), buff numbers popped in Anton blue
    const effect = (item.description || '').trim();
    if (effect) {
      let ef = Math.max(13, Math.round(Math.min(chh * 0.040, chh * bh(B.eff) * 0.42)));
      ctx.font = `600 ${ef}px SorryBossBody, "Barlow Condensed", sans-serif`;
      const maxW = cw * bw(B.eff);
      const words = effect.split(/\s+/);
      let lines: string[] = [];
      const buildLines = () => {
        lines = []; let cur = '';
        for (const wd of words) {
          const t2 = cur ? `${cur} ${wd}` : wd;
          if (ctx.measureText(t2).width <= maxW) cur = t2;
          else { if (cur) lines.push(cur); cur = wd; }
        }
        if (cur) lines.push(cur);
      };
      const MAX_LINES = 3;
      const efMin = Math.max(12, Math.round(chh * 0.028));
      buildLines();
      while (lines.length > 2 && ef > efMin) {
        ef--; ctx.font = `600 ${ef}px SorryBossBody, "Barlow Condensed", sans-serif`;
        buildLines();
      }
      if (lines.length > MAX_LINES) {
        let last = lines[MAX_LINES - 1] + '…';
        while (ctx.measureText(last).width > maxW && last.length > 4) {
          last = last.slice(0, -2).trimEnd() + '…';
        }
        lines = lines.slice(0, MAX_LINES);
        lines[MAX_LINES - 1] = last;
      }
      const nLines = Math.min(lines.length, MAX_LINES);
      const lineHF = bh(B.eff) * (nLines >= 3 ? 0.34 : 0.5);
      const numRe = /([+\-]\d[\d.,]*%?(?:\/s)?)/;   // sign REQUIRED so plain numbers stay white
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,1)';
      ctx.shadowBlur = 7;
      ctx.shadowOffsetY = 3;
      ctx.textBaseline = 'alphabetic';
      let ey = _plateBaselineY(ctx, lines[0] ?? '', ccy + chh * bcy(B.eff))
        - (nLines - 1) * chh * lineHF * 0.5;
      for (const line of lines.slice(0, MAX_LINES)) {
        const m2 = line.match(numRe);
        if (m2 && m2.index !== undefined) {
          const before = line.slice(0, m2.index);
          const num = m2[1];
          const after = line.slice(m2.index + num.length);
          const bodyFont = `600 ${ef}px SorryBossBody, "Barlow Condensed", sans-serif`;
          const numFont = `400 ${ef}px SorryBossTitle, Anton, Impact, sans-serif`;
          ctx.font = bodyFont;
          const wB = ctx.measureText(before).width;
          const wA = ctx.measureText(after).width;
          ctx.font = numFont;
          const wN = ctx.measureText(num).width;
          let x0 = ccx + cw / 2 - (wB + wN + wA) / 2;
          ctx.textAlign = 'left';
          ctx.font = bodyFont;
          ctx.fillStyle = '#efe9da';
          ctx.fillText(before, x0, ey); x0 += wB;
          ctx.font = numFont;
          ctx.fillStyle = '#4dd8ff';
          ctx.fillText(num, x0, ey); x0 += wN;
          ctx.font = bodyFont;
          ctx.fillStyle = '#efe9da';
          ctx.fillText(after, x0, ey);
          ctx.textAlign = 'center';
        } else {
          ctx.fillStyle = '#efe9da';
          ctx.fillText(line, ccx + cw / 2, ey);
        }
        ey += chh * lineHF;
      }
      ctx.restore();
      if (item.luckBoosted) {
        ctx.font = `${Math.round(ef * 0.9)}px "Share Tech Mono", monospace`;
        ctx.fillText('🍀', ccx + cw * 0.88, ccy + chh * (bcy(B.eff) - 0.05));
      }
    }

    // bottom small plate: ONE-word category (Dylan spec — no suffix here)
    const cat = item.category || 'UTILITY';
    let cf = Math.round(Math.min(chh * 0.047, chh * bh(B.cat) * 0.9));
    ctx.font = `400 ${cf}px SorryBossTitle, Anton, Impact, sans-serif`;
    while (ctx.measureText(cat).width > cw * bw(B.cat) && cf > 10) {
      cf--; ctx.font = `400 ${cf}px SorryBossTitle, Anton, Impact, sans-serif`;
    }
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,1)';
    ctx.shadowBlur = 8;
    ctx.shadowOffsetY = 3;
    ctx.textBaseline = 'alphabetic';
    const catX = ccx + cw * bcx(B.cat);
    const catY = _plateBaselineY(ctx, cat, ccy + chh * bcy(B.cat));
    ctx.lineWidth = Math.max(3, cf * 0.16);
    ctx.strokeStyle = 'rgba(0,0,0,1)';
    ctx.strokeText(cat, catX, catY);
    ctx.fillStyle = '#f3ead9';
    ctx.fillText(cat, catX, catY);
    ctx.restore();

    // tiny 1/2/3 keyboard hint, top-left corner (subtle, not a new plate)
    ctx.font = `400 ${Math.round(chh * 0.022)}px SorryBossTitle, Anton, Impact, sans-serif`;
    ctx.fillStyle = 'rgba(240,236,224,0.5)';
    ctx.fillText(`${i + 1}`, ccx + cw * 0.075, ccy + chh * 0.045);

    ctx.restore();
  }

  if (hoverIdx >= 0 && hoverIdx < n && _gamepadIsActiveInput()) {
    const fcx = L.startX + hoverIdx * (L.cardW + L.gap);
    drawFocusBox(ctx, fcx - 6, L.cardY - 6, L.cardW + 12, L.cardH + 12, 14);
  }

  _drawSkipRerollButtons(ctx, w, L.cardY + L.cardH + 64, state.rerollsLeft);
  _drawPickTaunt(ctx, w, h, L.cardY + L.cardH + 64, "TAKE YOUR CUT. DON'T TELL THE BOSS.");
  // pose + quip draw in the post-HUD pass (in front of cards + stats note)
  ctx.restore();
}

// ─── Ultra WildCardMachine Screen ─────────────────────────────────────────────────

let _ultraChestRolled: UltraItem[] = [];
let _ultraChestRollDone = false;

export function getUltraChestRolled(): UltraItem[] { return _ultraChestRolled; }
export function resetUltraChestRoll(): void {
  _ultraChestRolled = [];
  _ultraChestRollDone = false;
}

// BETA 20.64 — Ultra chest was KEYBOARD-ONLY ('1'/'2'/'e') → soft-lock on the
// ROG Ally (no gamepad targets, no mouse). Single-source layout + pick/close
// used by the gamepad focus targets, the GameCanvas key branch, AND the new
// click branch — one code path no matter the input device.
// BETA 20.69 — shrine blessing celebration (Dylan: "shrine buff when i chose
// doesnt look cool"). Burst + flash + thump ON COMMIT so it feels EARNED —
// called from every pick path (gamepad onActivate, Enter, mouse click).
// BETA 21.45 — Dylan: "i want an animation once a choice is completed so it's
// satisfying — so we can tell it happened." A bright radial burst FROM the
// player (the buff flowing into you): two expanding shockwave rings + a 360°
// spark fan + a white-hot core + rising stars + a light punch. Color-coded by
// pick type (level-up blue / shrine teal / chest gold) so it reads at a glance.
export function pickConfirmCelebration(state: GameState, color: string = '#ffd700'): void {
  try {
    const px = state.player.x, py = state.player.y;
    spawnParticles(state.particles, px, py, color, 1, { shape: 'ring', speedMin: 0, speedMax: 8, sizeMin: 26, sizeMax: 34, lifeMin: 0.32, lifeMax: 0.42 });
    spawnParticles(state.particles, px, py, color, 1, { shape: 'ring', speedMin: 0, speedMax: 8, sizeMin: 13, sizeMax: 17, lifeMin: 0.42, lifeMax: 0.52 });
    spawnParticles(state.particles, px, py, color, 24, { shape: 'spark', speedMin: 220, speedMax: 430, stretch: 5, lifeMin: 0.24, lifeMax: 0.44, fadePow: 1.4 });
    spawnParticles(state.particles, px, py, '#ffffff', 12, { shape: 'spark', speedMin: 120, speedMax: 300, lifeMin: 0.18, lifeMax: 0.32 });
    spawnParticles(state.particles, px, py, color, 6, { shape: 'star', speedMin: 70, speedMax: 190, gravity: -70, spin: 6, spinRandomSign: true, lifeMin: 0.5, lifeMax: 0.8, sizeMin: 3, sizeMax: 5 });
    state.screenShakeTimer = Math.max(state.screenShakeTimer, 0.2);
    state.screenShakeIntensity = Math.max(state.screenShakeIntensity, 11);
    // 🥜 BETA 22.37 — Dylan: voice on item picks (level-up / chest / shrine) was
    // annoying + overused. Removed. The Peanut now talks for combat REASONS (kills,
    // hits, danger), not for every menu choice.
  } catch { /* celebration must never crash a pick */ }
}

// Back-compat wrapper — shrine picks keep their teal flavor.
export function shrinePickCelebration(state: GameState): void {
  pickConfirmCelebration(state, '#00ffcc');
}

export function getUltraChestLayout(w: number, h: number, count: number) {
  // MUST mirror renderUltraChestScreen's card math exactly.
  const cardW = 300, cardH = 380, gap = 40;
  const totalW = count * cardW + (count - 1) * gap;
  return { cardW, cardH, gap, startX: w / 2 - totalW / 2, cardY: h * 0.24 };
}

export function pickUltraChestCard(state: GameState, idx: number): UltraItem | null {
  if (state.phase !== 'ultra_chest') return null;
  if (idx < 0 || idx >= _ultraChestRolled.length) return null;
  const chosen = _ultraChestRolled[idx];
  ultraChestPool.collect(chosen.id, state);
  resetUltraChestRoll();
  state.phase = 'playing';
  // BETA 20.67 — ultra chest counts as an opened chest (deep-dive medium:
  // chestsOpened skipped it, so chest-economy math missed the boss reward).
  state.chestsOpenedThisRun = (state.chestsOpenedThisRun ?? 0) + 1;
  if (!state.pickupNotifications) state.pickupNotifications = [];
  state.pickupNotifications.push({ text: `✦ ${chosen.name.toUpperCase()}`, color: '#ff4400', timer: 4.0 });
  try { _logSessionEvent('ultra_item_picked', { item: chosen.id }); } catch { /* tracker optional */ }
  // Synchronous focus cleanup (the 18.14 lesson: phase change alone leaves
  // the focus screen active → next frame's gamepad tick eats movement input).
  try { unregisterFocusScreen('ultra_chest'); setActiveScreen(null); } catch { /* defensive */ }
  return chosen;
}

export function closeUltraChest(state: GameState): void {
  if (state.phase !== 'ultra_chest') return;
  resetUltraChestRoll();
  state.phase = 'playing';
  try { unregisterFocusScreen('ultra_chest'); setActiveScreen(null); } catch { /* defensive */ }
}

function renderUltraChestScreen(
  ctx: CanvasRenderingContext2D, state: GameState, w: number, h: number
) {
  // Roll once when screen first opens
  if (!_ultraChestRollDone) {
    _ultraChestRolled = ultraChestPool.rollChest();
    _ultraChestRollDone = true;
  }

  ctx.save();
  ctx.fillStyle = 'rgba(0,0,0,0.93)';
  ctx.fillRect(0, 0, w, h);

  // Header
  ctx.textAlign = 'center';
  ctx.fillStyle = '#ff2200';
  ctx.shadowColor = '#ff0000';
  ctx.shadowBlur = 24;
  ctx.font = `bold ${Math.round(h * 0.055)}px "Share Tech Mono", monospace`;
  ctx.fillText('⚡ ULTRA LOOT ⚡', w / 2, h * 0.13);
  ctx.shadowBlur = 0;

  ctx.fillStyle = '#ffaa44';
  ctx.font = `${Math.round(h * 0.028)}px "Share Tech Mono", monospace`;
  ctx.fillText('THE FINAL GIFT', w / 2, h * 0.19);

  if (_ultraChestRolled.length === 0) {
    // Pool exhausted
    ctx.fillStyle = '#888';
    ctx.font = `bold ${Math.round(h * 0.04)}px "Share Tech Mono", monospace`;
    ctx.fillText('POOL EXHAUSTED', w / 2, h / 2);
    ctx.fillStyle = '#555';
    ctx.font = `${Math.round(h * 0.025)}px "Share Tech Mono", monospace`;
    ctx.fillText('You already have every ultra item.', w / 2, h / 2 + 48);
    ctx.fillStyle = '#aaa';
    ctx.font = `${Math.round(h * 0.022)}px "Share Tech Mono", monospace`;
    ctx.fillText('[E] Close', w / 2, h * 0.82);
    ctx.restore();
    return;
  }

  // Cards — 2 max, centered
  const cardW = 300;
  const cardH = 380;
  const gap = 40;
  const totalW = _ultraChestRolled.length * cardW + (_ultraChestRolled.length - 1) * gap;
  const startX = w / 2 - totalW / 2;
  const cardY = h * 0.24;

  const borderColor = '#ff4400';
  const glowColor   = 'rgba(255,68,0,0.6)';

  for (let i = 0; i < _ultraChestRolled.length; i++) {
    const item = _ultraChestRolled[i];
    const cx = startX + i * (cardW + gap);

    ctx.save();

    // Card background
    ctx.fillStyle = 'rgba(18,4,0,0.97)';
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = 2.5;
    ctx.shadowColor = glowColor;
    ctx.shadowBlur = 22;
    roundRect(ctx, cx, cardY, cardW, cardH, 8);
    ctx.fill();
    ctx.stroke();
    ctx.shadowBlur = 0;

    // BETA 20.64 — controller focus outline (white, matches the other pick
    // screens) so gamepad users can see which ultra card the d-pad is on.
    try {
      if (getActiveScreen() === 'ultra_chest' && getFocusedTarget()?.id === `ultra_${i}`) {
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 3;
        roundRect(ctx, cx - 6, cardY - 6, cardW + 12, cardH + 12, 10);
        ctx.stroke();
      }
    } catch { /* defensive */ }

    // LEGENDARY badge
    ctx.fillStyle = borderColor;
    ctx.font = `bold 11px "Share Tech Mono", monospace`;
    ctx.textAlign = 'center';
    ctx.fillText('✦ LEGENDARY ✦', cx + cardW / 2, cardY + 22);

    // Sprite / emoji
    if (item.spriteIsEmoji) {
      ctx.font = '72px serif';
      ctx.fillText(item.sprite, cx + cardW / 2, cardY + 130);
    } else {
      const img = getCachedImage(item.sprite);
      if (img) {
        const natW = (img as HTMLImageElement).naturalWidth || 96;
        const natH = (img as HTMLImageElement).naturalHeight || 96;
        const maxS = 96;
        const asp = natW / natH;
        const dw = asp >= 1 ? maxS : maxS * asp;
        const dh = asp >= 1 ? maxS / asp : maxS;
        ctx.drawImage(img, cx + cardW / 2 - dw / 2, cardY + 38, dw, dh);
      } else {
        ctx.fillStyle = '#ff4400';
        ctx.font = '64px serif';
        ctx.fillText('⚡', cx + cardW / 2, cardY + 120);
      }
    }

    // Name
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold 15px "Share Tech Mono", monospace`;
    ctx.fillText(item.name.toUpperCase(), cx + cardW / 2, cardY + 160);

    // Description (word-wrap)
    ctx.fillStyle = '#cccccc';
    ctx.font = `12px "Share Tech Mono", monospace`;
    const words = item.description.split(' ');
    let line = '';
    let lineY = cardY + 188;
    const maxLineW = cardW - 28;
    for (const word of words) {
      const test = line ? `${line} ${word}` : word;
      if (ctx.measureText(test).width > maxLineW && line) {
        ctx.fillText(line, cx + cardW / 2, lineY);
        line = word;
        lineY += 18;
      } else {
        line = test;
      }
    }
    if (line) ctx.fillText(line, cx + cardW / 2, lineY);

    // Flavor text
    ctx.fillStyle = '#886644';
    ctx.font = `italic 11px "Share Tech Mono", monospace`;
    const fWords = item.flavorText.split(' ');
    let fLine = '';
    let fY = cardY + cardH - 52;
    for (const w2 of fWords) {
      const test = fLine ? `${fLine} ${w2}` : w2;
      if (ctx.measureText(test).width > maxLineW && fLine) {
        ctx.fillText(fLine, cx + cardW / 2, fY);
        fLine = w2;
        fY += 16;
      } else {
        fLine = test;
      }
    }
    if (fLine) ctx.fillText(fLine, cx + cardW / 2, fY);

    // Pick hint
    ctx.fillStyle = '#ff6633';
    ctx.font = `bold 12px "Share Tech Mono", monospace`;
    ctx.fillText(`[${i + 1}] Pick This`, cx + cardW / 2, cardY + cardH - 16);

    ctx.restore();

    // Preload sprite
    if (!item.spriteIsEmoji) getCachedImage(item.sprite);
  }

  ctx.textAlign = 'center';
  ctx.fillStyle = '#666';
  ctx.font = `13px "Share Tech Mono", monospace`;
  ctx.fillText('Press 1 or 2 to pick an item', w / 2, cardY + cardH + 36);

  ctx.restore();
}

// ─── Game Over Screen ───────────────────────────────────────────────────
// BETA 15.94 — Legacy renderGameOverScreen function REMOVED. Per Dylan:
// "we build a death screen a long time ago. but it wasnt really good.
// make sure that isnt still live." Replaced in 15.93 by renderDeathScreen
// (deathScreen.ts) which handles the RIP PEANUT moment. This block is
// intentionally left empty as a tombstone so future grep finds it and
// understands the history.

// ─── Last Stand Flash ──────────────────────────────────────────────────────

function renderLastStandFlash(
  ctx: CanvasRenderingContext2D, state: GameState, w: number, h: number,
) {
  // BETA 17.96 — VFX toggle.
  if (!isVfxEnabled()) return;
  const t = state.lastStandFlashTimer;
  if (t <= 0) return;
  // BETA 19.17 — BUG #12 from local audit. The flash is painted in
  // renderGame AFTER renderDeathScreen, so if Last Stand triggered the
  // same frame the player died (or its 1.5s timer was still ticking when
  // death fired), the red "LAST STAND — GET OUT — NEXT HIT KILLS YOU"
  // banner sat on top of the RIP PEANUT screen / death-replay overlay.
  // Skip the flash when those screens own the canvas — the player is
  // already dead, the warning is no longer actionable, and the death
  // grade + trash talk + REPLAY/QUIT buttons should be the focal point.
  if (state.phase === 'gameover' || state.phase === 'death_replay') return;

  ctx.save();

  // BETA 17.94 — clamp timer at 1.0 visually so repeat-trigger spam (which
  // happens during broken-math STRESS runs) can't blanket the screen.
  // Also lower max alpha 0.55 → 0.30 so the overlay is informative, not
  // blocking. Dylan: "what is super orange and covering the screen. that
  // sucks." Was: full-screen rgba(180,0,0,0.55) reset every frame on each
  // re-trigger → permanent orange wash.
  const safeT = Math.min(1, Math.max(0, t));
  const alpha = safeT * 0.30; // was 0.55 — softer, never blocks the game
  ctx.fillStyle = `rgba(180, 0, 0, ${alpha})`;
  ctx.fillRect(0, 0, w, h);

  // Only show text in first 0.7s
  if (t > 0.3) {
    const textAlpha = Math.min(1, (t - 0.3) / 0.4);
    ctx.globalAlpha = textAlpha;

    // Big warning line
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = 'bold 52px "Orbitron", "Share Tech Mono", monospace';
    ctx.fillStyle = '#ffffff';
    ctx.shadowColor = '#ff0000';
    ctx.shadowBlur = 28;
    ctx.fillText('LAST STAND', w / 2, h / 2 - 28);
    ctx.shadowBlur = 0;

    ctx.font = 'bold 20px "Share Tech Mono", monospace';
    ctx.fillStyle = 'rgba(255,200,200,0.95)';
    ctx.fillText('GET OUT  —  NEXT HIT KILLS YOU', w / 2, h / 2 + 22);
  }

  ctx.restore();
}

// ─── Quest / Unlock Banners (slide-up from bottom-center) ──────────────────

const BANNER_W  = 400;
const BANNER_H  = 68;
const BANNER_BOTTOM_PAD = 90;  // pixels above bottom edge
const BANNER_GAP = 10;

function renderUnlockBanners(
  ctx: CanvasRenderingContext2D, state: GameState, w: number, h: number,
) {
  const notifs = state.runUnlockNotifs;
  if (!notifs?.length) return;
  ctx.save();

  for (let i = 0; i < notifs.length; i++) {
    const n = notifs[i];
    // Each successive banner sits above the previous one
    const targetY = h - BANNER_BOTTOM_PAD - (i + 1) * (BANNER_H + BANNER_GAP);
    // Slides up from below the screen
    const slideY = targetY + (1 - n.slideProgress) * (BANNER_H + 40);
    const x = w / 2 - BANNER_W / 2;

    // Fade in (first ~0.12s via slideProgress) and fade out in final 0.6s
    const fadeIn  = Math.min(1, n.slideProgress * 10);
    const fadeOut = n.timer < 0.6 ? n.timer / 0.6 : 1;
    const alpha   = fadeIn * fadeOut;
    if (alpha <= 0) continue;

    ctx.globalAlpha = alpha;

    const isHorde   = n.type === 'horde';
    const accentClr = isHorde ? '#ff4422' : '#00ffcc';
    const bgClr     = isHorde ? 'rgba(30,5,5,0.95)' : 'rgba(5,18,12,0.93)';

    // Panel background
    ctx.fillStyle = bgClr;
    roundRect(ctx, x, slideY, BANNER_W, BANNER_H, 8);
    ctx.fill();

    // Border
    ctx.strokeStyle = accentClr;
    ctx.lineWidth = 2;
    roundRect(ctx, x, slideY, BANNER_W, BANNER_H, 8);
    ctx.stroke();

    // Left accent stripe
    ctx.fillStyle = accentClr;
    roundRect(ctx, x, slideY, 5, BANNER_H, 8);
    ctx.fill();

    // Header label
    ctx.fillStyle = accentClr;
    ctx.font = 'bold 10px "Share Tech Mono", monospace';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    const headerText = isHorde
      ? '\u26A0  INCOMING HORDE'
      : '\u2726  QUEST COMPLETE \u2014 UNLOCKED';
    ctx.fillText(headerText, x + 18, slideY + 20);

    // Perc / horde name (large)
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 20px "Orbitron", "Share Tech Mono", monospace';
    ctx.fillText(n.label, x + 18, slideY + 46);

    // Timer drain bar at bottom
    const barW = BANNER_W - 10;
    ctx.fillStyle = isHorde ? 'rgba(255,68,34,0.15)' : 'rgba(0,255,200,0.15)';
    ctx.fillRect(x + 5, slideY + BANNER_H - 5, barW, 3);
    ctx.fillStyle = accentClr;
    const timerMax = isHorde ? 3.5 : 5;
    ctx.fillRect(x + 5, slideY + BANNER_H - 5, barW * Math.max(0, n.timer / timerMax), 3);
  }

  ctx.restore();
}

// ─── Pickup Notifications (floating text) ──────────────────────────────────

function renderPickupNotifications(
  ctx: CanvasRenderingContext2D, state: GameState, w: number, _h: number
) {
  if (!state.pickupNotifications || state.pickupNotifications.length === 0) return;

  // BETA 16.55 — PSBOT cycle-30m: split into two pass piles —
  // (1) top_right pickups (default): right-aligned 13px, vertical stack
  // (2) screen_center bigs (Sheeesh/Last Stand/Liar): centered, large
  //     font, with subtle drop shadow so they read against any background.
  // Splitting prevents the centered bigs from shifting normal pickup positions.
  const topRight: typeof state.pickupNotifications = [];
  const center: typeof state.pickupNotifications = [];
  for (const n of state.pickupNotifications) {
    if (n.position === 'screen_center') center.push(n);
    else topRight.push(n);
  }

  // ── Pass 1: top-right pickups (existing behavior) ──
  // BETA 19.15 — Cap visible stack to the viewport height so dense events
  // (chest open + magnet field sweep + several perc procs firing the same
  // frame) don't push the newest notifications below the visible canvas.
  // The notifications still tick down naturally (lifecycle is unchanged);
  // we just stop DRAWING the oldest ones once the stack would overflow.
  // The newest pickup is always visible because it sits at the BOTTOM of
  // the rendered stack and is the most relevant to the player right now.
  // Cap math: each slot is 22px tall, starting at y=80, leaving a 20px
  // bottom margin so the last slot doesn't kiss the canvas edge. Min cap
  // of 8 so very short viewports still show something.
  if (topRight.length > 0) {
    ctx.save();
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.font = 'bold 13px "Share Tech Mono", monospace';
    const _maxVisible = Math.max(8, Math.floor((_h - 100) / 22));
    const _firstIdx = Math.max(0, topRight.length - _maxVisible);
    // Render only the most recent `_maxVisible` notifications. They keep
    // their stack-relative position via (i - _firstIdx) so the bottom
    // of the rendered list is still the newest, and the visible top is
    // the oldest that fits on-screen.
    for (let i = _firstIdx; i < topRight.length; i++) {
      const notif = topRight[i];
      const alpha = Math.min(1, notif.timer / 0.4);
      const yOffset = (2.5 - notif.timer) * 22;
      const ny = 80 + (i - _firstIdx) * 22 - yOffset;
      let r = 255, g = 255, b = 255;
      if (notif.color.startsWith('#') && notif.color.length >= 7) {
        r = parseInt(notif.color.slice(1, 3), 16);
        g = parseInt(notif.color.slice(3, 5), 16);
        b = parseInt(notif.color.slice(5, 7), 16);
      }
      ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`;
      ctx.fillText(notif.text, w - 12, ny);
    }
    ctx.restore();
  }

  // ── Pass 2: screen-center bigs (revive events) ──
  // BETA 16.86 — Per Dylan: "last stand forzen in front of level up
  // screen. should be behind the scren not in front if this happens."
  // Root cause: pickupNotifications render AFTER all the choice
  // overlays (level-up / shrine / chest_spin / chest_choice / ultra_chest).
  // A 4-second 💀 LAST STAND 💀 popup that triggers ~the same frame
  // as a level-up sat ON TOP of the level-up cards. Fix: skip the
  // screen-center pass during any card-phase. The popup timer keeps
  // ticking so when cards dismiss, it may be done — that's the right
  // tradeoff vs a popup blocking gameplay decisions.
  const _suppressCenter = state.phase === 'levelup'
    || state.phase === 'shrine_choice'
    || state.phase === 'chest_spin'
    || state.phase === 'chest_choice'
    || state.phase === 'ultra_chest';
  if (center.length > 0 && !_suppressCenter) {
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (let i = 0; i < center.length; i++) {
      const notif = center[i];
      const initial = notif.initialTimer ?? notif.timer;
      // Slight pulse — text scales up briefly on first frame, then settles
      const elapsed = Math.max(0, initial - notif.timer);
      const pulse = elapsed < 0.3 ? 1 + (0.3 - elapsed) * 0.5 : 1.0;
      // Fade in last 0.5s
      const alpha = Math.min(1, notif.timer / 0.5);
      const fontSize = Math.round((notif.fontSize ?? 36) * pulse);
      // Stack centered notifications vertically (rare to have >1)
      const cy = (_h * 0.32) + i * (fontSize + 12);
      let r = 255, g = 255, b = 255;
      if (notif.color.startsWith('#') && notif.color.length >= 7) {
        r = parseInt(notif.color.slice(1, 3), 16);
        g = parseInt(notif.color.slice(3, 5), 16);
        b = parseInt(notif.color.slice(5, 7), 16);
      }
      ctx.font = `bold ${fontSize}px "Share Tech Mono", monospace`;
      // Drop shadow for legibility
      ctx.fillStyle = `rgba(0,0,0,${alpha * 0.7})`;
      ctx.fillText(notif.text, w / 2 + 3, cy + 3);
      // Main text
      ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`;
      ctx.fillText(notif.text, w / 2, cy);
    }
    ctx.restore();
  }
}
